import { useState, useMemo, useEffect, useRef, createContext, useContext, useDeferredValue } from "react";
import * as React from "react";
import * as XLSX from 'xlsx';
import { initializeApp } from "firebase/app";
import { getDatabase, ref, set, onValue, get, update, query, orderByChild, equalTo, limitToLast, endBefore, startAfter, startAt, push } from "firebase/database";
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, getMultiFactorResolver, multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import QRCode from "qrcode";
import PreventivoModal, { generatePDF, generatePDFBlob, recapitiConsulente } from "./PreventivoModal";
import ContrattoModal, { generaPacchettoPDF, generaPacchettoBlob } from "./ContrattoModal";

// Firebase Realtime Database serializza un array come OGGETTO (chiavi
// numeriche) invece che come vero array, se ha degli elementi mancanti (es.
// un elemento cancellato singolarmente lasciando un "buco", o un campo come
// "permessi" impostato a mano su Firebase in modo non standard). Chiamare
// .some()/.map() direttamente su un oggetto lancia "X.some is not a
// function" — da qui questo helper, usato ovunque leggiamo array che
// potrebbero provenire da Firebase in questa forma "a rischio".
const toArraySafe = v => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) return [v.trim()]; // stringa singola inserita a mano su Firebase Console, es. "consulente_nlt" invece di un vero array
  if (v && typeof v === 'object') return Object.values(v);
  return [];
};

// Somma tutti i campi "costo*" di un contratto per il conto economico,
// escludendo SEMPRE l'estinzione anticipata del finanziamento — sia quando
// è impostata con la checkbox dedicata, sia quando un consulente l'ha
// scritta come testo libero abbreviato in un altro campo (es. "Altro
// Accessorio": "cont est fin", "est fin anticipata"), sia quando è stato
// creato un campo apposta a mano con un nome tipo "Cont Est Ant" — in
// quest'ultimo caso il nome del campo stesso viene controllato, non solo
// un'eventuale descrizione abbinata.
const RX_ESTINZIONE_TESTO = /estinzion|\best\.?\s*(fin|ant)\b|\b(fin|ant)\.?\s*est\b/i;
const MAPPA_DESCRIZIONE_SERVIZI = { costoAltroAccessorio: 'altroAccessorio', costoAltroAntifurto: 'altroAntifurto', costoAltroInstallazione: 'altroInstallazione' };
function sommaCostiServiziNoEstinzione(servizi) {
  const sv = servizi || {};
  return Object.entries(sv).filter(([k]) => {
    if (!k.startsWith('costo')) return false;
    if (k === 'costoEstinzioneAnticipata') return false;
    const campoDescrizione = MAPPA_DESCRIZIONE_SERVIZI[k];
    const nomeCampoLeggibile = k.replace('costo', '').replace(/([A-Z])/g, ' $1').trim();
    const testoDaControllare = `${campoDescrizione ? (sv[campoDescrizione] || '') : ''} ${nomeCampoLeggibile}`;
    if (RX_ESTINZIONE_TESTO.test(testoDaControllare)) return false;
    return true;
  }).reduce((t, [, v]) => t + (parseFloat(v) || 0), 0);
}

// Divide una riga CSV rispettando i campi tra virgolette — a differenza di
// un semplice line.split(sep), non si spezza se il separatore compare
// ANCHE dentro un campo (es. un punto e virgola nel testo degli optional),
// che altrimenti sfaserebbe tutte le colonne successive di quella riga.
function parseCSVLine(line, sep) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (char === sep && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

// ─── ERROR BOUNDARY ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('RadaR Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'16px', padding:'20px' }}>
          <div style={{ fontSize:'48px' }}>⚠️</div>
          <div style={{ fontSize:'18px', fontWeight:'900', color:'#fff' }}>Si è verificato un errore</div>
          <div style={{ fontSize:'12px', color:'#64748b', maxWidth:'400px', textAlign:'center' }}>{this.state.error?.message}</div>
          <button onClick={()=>window.location.reload()} style={{ background:'#6366f1', border:'none', borderRadius:'10px', padding:'12px 24px', color:'#fff', cursor:'pointer', fontWeight:'700', fontSize:'13px' }}>
            🔄 Ricarica applicazione
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const GAS_RENT_LOGO = "data:image/png;base64,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";

// ─── TEMA ─────────────────────────────────────────────────────────────────────
const ThemeContext = createContext('dark');
const useTheme = () => useContext(ThemeContext);

// Hook debounce — evita ricerche ad ogni tasto
function useDebounce(value, delay = 250) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// Consulenti per sede — formato "nome cognome" esatto come salvato in venditoreNome
const NOLA_CONSULENTI    = ['giovanni santaniello', 'francesco montuori', 'claudio filosa', 'pasquale del villano', 'antonio castaldo'];
const SALERNO_CONSULENTI = ['claudio gerardi', 'simone bruno', 'ciro imperato'];   // Ciro Imperato: da Nola a Salerno (ott 2026)

const isNolaNome = (nome = '') => {
  const n = nome.toLowerCase().trim();
  return NOLA_CONSULENTI.includes(n) || NOLA_CONSULENTI.some(c => n === c);
};
const isSalernoNome = (nome = '') => {
  const n = nome.toLowerCase().trim();
  return SALERNO_CONSULENTI.includes(n) || SALERNO_CONSULENTI.some(c => n === c);
};
const getSede = (nome = '') => {
  if (isNolaNome(nome)) return 'nola';
  if (isSalernoNome(nome)) return 'salerno';
  return 'agnano';
};

// Per ruolo front: marianna.vitrano → solo Nola; simona.lagrutta → solo
// Salerno; altri front → tutte le sedi
const getFrontSedeFilter = (user) => {
  if ((user?.ruolo||'').toLowerCase() !== 'front') return null;
  const email = (user?.email || '').toLowerCase();
  if (email.includes('marianna') || email.includes('vitrano')) return 'nola';
  if (email.includes('simona') || email.includes('lagrutta')) return 'salerno';
  return null;
};

// Mappa finanziaria → file PDF privacy
const PRIVACY_FINANZIARIE = {
  agos:      { match: ['agos','agos ducato'],             file: 'https://drive.google.com/file/d/1haemOWjdCTg2C2IdlDga0iTtBeRtlzUp/view', label: 'Agos Ducato' },
  compass:   { match: ['compass','compass banca'],        file: 'https://drive.google.com/file/d/1nrWaXRzE_xkEFBodHN_507pjtveeTbd9/view', label: 'Compass Banca' },
  santander: { match: ['santander','santander consumer'], file: 'https://drive.google.com/file/d/1hwIT756nRGqSkVgRSahEzQMRYKLf8_YK/view', label: 'Santander Consumer Bank' },
  deutsche:  { match: ['deutsche','deutsche bank'],       file: 'https://drive.google.com/file/d/1rImcCTKAOcVlDsLh24APeOlgQXfYW8g4/view', label: 'Deutsche Bank' },
};

function getPrivacyFinanziaria(nomeFinanziaria) {
  if (!nomeFinanziaria) return null;
  const n = nomeFinanziaria.toLowerCase().trim();
  for (const p of Object.values(PRIVACY_FINANZIARIE)) {
    if (p.match.some(m => n.includes(m))) return p;
  }
  return null;
}
const COLONNE_EXPORT_CONTRATTI = [
  { gruppo: 'Contratto', colonne: ['Stato', 'N. Contratto', 'Data Firma', 'Data Creazione', 'Data Consegna', 'Consulente', 'Sede'] },
  { gruppo: 'Cliente', colonne: ['Cliente Nome', 'Cliente Cognome', 'Ragione Sociale', 'Codice Fiscale', 'Telefono', 'Email', 'Città', 'CAP', 'Provenienza', 'Già Cliente'] },
  { gruppo: 'Veicolo', colonne: ['Marca', 'Modello', 'Versione', 'Tipo', 'Targa', 'VIN', 'Immatricolazione', 'Carburante', 'KM', 'Colore', 'Data Ingresso Stock', 'GG Giacenza'] },
  { gruppo: 'Prezzi', colonne: ['Prezzo Listino', 'Sconto', 'IPT', 'Servizi', 'Caparra', 'Saldo'] },
  { gruppo: 'Permuta e Finanziamento', colonne: ['Permuta', 'Valore Permuta', 'Permuta Marca', 'Permuta Modello', 'Permuta Targa', 'Finanziamento', 'Importo Finanziato', 'Banca', 'Tasso'] },
  { gruppo: 'Servizi Extra', colonne: ['LoJack', 'Mawdy', 'Blockshaft', 'GPL', 'Tagliando', 'Retrocamera', '2a Chiave', 'PP', 'FIR', 'GAP'] },
];

// Applica i formati NUMERICI VERI di Excel (non testo formattato) alle
// colonne indicate — valuta (€, due decimali) e data (dd/mm/yyyy). I valori
// nelle righe devono già essere numeri puri (per la valuta) o oggetti Date
// (per le date): questa funzione si limita a impostare il formato di
// visualizzazione della cella, permettendo somme, ordinamenti e filtri
// corretti in Excel — cosa che il semplice testo formattato non permette.
function applicaFormatiExcel(ws, headers, nRighe, colonneValuta = [], colonneData = []) {
  const idxValuta = colonneValuta.map(h => headers.indexOf(h)).filter(i => i !== -1);
  const idxData = colonneData.map(h => headers.indexOf(h)).filter(i => i !== -1);
  for (let r = 1; r <= nRighe; r++) {
    idxValuta.forEach(c => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (ws[addr] && typeof ws[addr].v === 'number') ws[addr].z = '#.##0,00 €';
    });
    idxData.forEach(c => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (ws[addr] && ws[addr].v instanceof Date) ws[addr].z = 'dd/mm/yyyy';
    });
  }
}

// Veicoli dello stock (cache in memoria o nel browser): servono all'export
// contratti per la data d'ingresso in stock. Se non c'è cache, una sola lettura.
async function veicoliStockPerExport() {
  const toArr = d => (d ? (Array.isArray(d) ? d.filter(Boolean) : Object.values(d)) : []);
  if (window._radarStockCache?.vehicles) return toArr(window._radarStockCache.vehicles);
  try { const ls = JSON.parse(localStorage.getItem('radar_stock_cache')); if (ls?.vehicles) return toArr(ls.vehicles); } catch (e) { /* nessuna cache */ }
  try { const snap = await get(ref(db, 'stock_data/vehicles')); return toArr(snap.val()); } catch (e) { return []; }
}

// ─── UTENTI SENZA VISIBILITÀ SUI MARGINI (regola ad hoc) ───────────────────
// Anche se admin o super admin, non vedono marginalità, bottom price / prezzo
// d'acquisto e conto economico, e negli Excel quelle colonne vengono tolte.
const UTENTI_SENZA_MARGINI = ['emanuele.amendola@radargas.it', 'federica.perna@radargas.it', 'martina.grasso@radargas.it'];
const puoVedereMargini = (u) => !UTENTI_SENZA_MARGINI.includes(String(u?.email ?? window._radarEmailUtente ?? '').toLowerCase().trim());
// Eccezione: possono vedere e inserire il prezzo d'acquisto sulle card dello
// stock anche se non vedono i margini (marginalità, conto economico restano nascosti)
const ECCEZIONI_PREZZO_ACQUISTO = ['federica.perna@radargas.it'];
const puoInserirePrezzoAcquisto = (u) => ECCEZIONI_PREZZO_ACQUISTO.includes(String(u?.email ?? window._radarEmailUtente ?? '').toLowerCase().trim());
// Conto economico: escluso anche il ruolo back office
const puoVedereContoEconomico = (u) => puoVedereMargini(u) && String(u?.ruolo ?? window._radarRuoloUtente ?? '').toLowerCase().trim() !== 'back_office';
// Colonne da togliere dagli Excel per chi non vede i margini
const COLONNE_RISERVATE_MARGINI = /bottom|prezzo\s*d.?acquisto|costo\s*d.?acquisto|margin|ricarico|prezzo\s*ferro/i;
function scriviExcel(wb, nomeFile) {
  if (!puoVedereMargini()) {
    wb.SheetNames.forEach(nome => {
      const righe = XLSX.utils.sheet_to_json(wb.Sheets[nome], { header: 1, defval: '' });
      if (!righe.length) return;
      const iH = righe.findIndex(r => r.some(c => typeof c === 'string' && c.trim()));
      if (iH < 0) return;
      const tieni = righe[iH].map(h => !(typeof h === 'string' && COLONNE_RISERVATE_MARGINI.test(h)));
      if (tieni.every(Boolean)) return;
      const pulite = righe.map(r => r.filter((_, i) => tieni[i] !== false));
      const ws = XLSX.utils.aoa_to_sheet(pulite);
      if (wb.Sheets[nome]['!cols']) ws['!cols'] = wb.Sheets[nome]['!cols'].filter((_, i) => tieni[i] !== false);
      wb.Sheets[nome] = ws;
    });
    // fogli dedicati ai margini / conto economico: tolti del tutto
    wb.SheetNames = wb.SheetNames.filter(n => { const ok = !COLONNE_RISERVATE_MARGINI.test(n) && !/conto\s*economico/i.test(n); if (!ok) delete wb.Sheets[n]; return ok; });
    if (!wb.SheetNames.length) { alert('Questo file contiene solo dati riservati sui margini.'); return; }
  }
  XLSX.writeFile(wb, nomeFile);
}

// ─── EXPORT EXCEL: solo admin, super admin e finance (per RUOLO) ────────────
const RUOLI_EXPORT_EXCEL = ['admin', 'super_admin', 'finance'];
const puoEsportareExcel = (u) => RUOLI_EXPORT_EXCEL.includes(String(u?.ruolo ?? window._radarRuoloUtente ?? '').toLowerCase().trim());
const bloccaExportExcel = () => {
  if (puoEsportareExcel()) return false;
  alert("L'esportazione in Excel è riservata ad admin, super admin e finance.");
  return true;
};

async function exportToExcel(preventiviFiltrati, contrattiFiltrati, nomeFile = 'RadaR_Export', colonneSelezionateContratti) {
  if (bloccaExportExcel()) return;
  // Data ingresso in stock per ogni contratto: dal veicolo in stock (id, telaio
  // o targa) oppure dalla copia del veicolo salvata nel contratto
  const normVT = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const stockVeicoli = (contrattiFiltrati || []).length ? await veicoliStockPerExport() : [];
  const perId = new Map(), perVin = new Map(), perTarga = new Map();
  stockVeicoli.forEach(sv0 => {
    if (sv0.id) perId.set(String(sv0.id), sv0);
    if (sv0.vin) perVin.set(normVT(sv0.vin), sv0);
    if (sv0.targa) perTarga.set(normVT(sv0.targa), sv0);
  });
  const ingressoStock = c => {
    const v = c.veicolo || {}, de = c.datiExtra || {};
    const sv0 = (v.id && perId.get(String(v.id))) || perVin.get(normVT(de.vin || v.vin)) || perTarga.get(normVT(v.targa));
    const raw = sv0?.enteredInStock || v.enteredInStock || '';
    const d = raw ? new Date(raw) : null;
    return d && !isNaN(d) ? d : '';
  };
  // Giorni in stock: dall'ingresso alla vendita (data firma del contratto,
  // in mancanza la data di creazione)
  const giorniGiacenza = (ingresso, c) => {
    if (!ingresso) return '';
    const fine = new Date((c.datiExtra || {}).dataFirma || c.dataCreazione || '');
    if (isNaN(fine)) return '';
    const gg = Math.round((Date.UTC(fine.getFullYear(), fine.getMonth(), fine.getDate()) - Date.UTC(ingresso.getFullYear(), ingresso.getMonth(), ingresso.getDate())) / 864e5);
    return gg >= 0 ? gg : '';
  };
  const fd = iso => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d; }; // vera Date, non testo
  const yn = v => v ? 'Sì' : 'No';
  const sv = v => (v === null || v === undefined) ? '' : String(v).trim();

  const headersP = [
    'Data','Consulente','Sede',
    'Cliente Nome','Cliente Cognome','Ragione Sociale','Codice Fiscale','Telefono','Email','Città','CAP','Provenienza','Già Cliente',
    'Marca','Modello','Versione','Tipo','Targa','Carburante','KM',
    'Prezzo Listino','Sconto','IPT','Caparra','Saldo',
    'Permuta','Valore Permuta','Permuta Marca','Permuta Modello','Permuta Targa',
    'Finanziamento','Importo Finanziato','Banca',
    'LoJack','Mawdy','Blockshaft','GPL','Tagliando','Retrocamera','2a Chiave',
  ];

  const righeP = (preventiviFiltrati || []).map(p => {
    const cl = p.cliente || {}; const v = p.veicolo || {}; const pr = p.prezzo || {}; const sv2 = p.servizi || {};
    const pL = parseFloat(pr.prezzoListino || v.prezzo || 0);
    const sc = parseFloat(pr.sconto || 0); const ipt = parseFloat(pr.ipt || 0); const cap = parseFloat(pr.caparra || 0);
    const perm = valorePermuteTot(pr); const fin = parseFloat(pr.importoFinanziato || 0);
    const totS = Object.entries(sv2).filter(([k]) => k.startsWith('costo')).reduce((t,[,val]) => t+(parseFloat(val)||0), 0);
    const saldo = pL - sc + ipt + totS - perm - fin - cap;
    const sede = getSede((p.venditoreNome||'').toLowerCase());
    return [
      fd(p.dataCreazione), sv(p.venditoreNome), sede,
      sv(cl.nome), sv(cl.cognome), sv(cl.ragioneSociale), sv(cl.codiceFiscale || cl.piva), sv(cl.telefono||cl.cellulare), sv(cl.email), sv(cl.citta), sv(cl.cap), sv(cl.provenienza), cl.giaCliente ? 'Sì' : 'No',
      sv(v.marca), sv(v.modello), sv(v.versione), sv(v.tipo), sv(v.targa), sv(v.carburante), parseFloat(v.km) || 0,
      pL, sc, ipt, cap, saldo,
      yn(pr.hasPermuta), perm, sv(pr.permutaMarca), sv(pr.permutaModello), sv(pr.permutaTarga),
      yn(pr.hasFinanziamento), fin, sv(pr.bancaFinanziamento||pr.finanziariaSocieta),
      yn(sv2.lojack), yn(sv2.mawdy36 || sv2.mawdy), yn(sv2.blockshaft), yn(sv2.gpl), yn(sv2.tagliando), yn(sv2.retrocamera), yn(sv2.secondaChiave),
    ];
  });

  const headersC = [
    'Stato','Avanzamento','N. Contratto','Data Firma','Data Creazione','Data Consegna','Consulente','Sede',
    'Cliente Nome','Cliente Cognome','Ragione Sociale','Codice Fiscale','Telefono','Email','Città','CAP','Provenienza','Già Cliente',
    'Marca','Modello','Versione','Tipo','Targa','VIN','Immatricolazione','Carburante','KM','Colore','Data Ingresso Stock','GG Giacenza',
    'Prezzo Listino','Sconto','IPT','Servizi','Caparra','Modalità Incasso Caparra','Saldo',
    'Permuta','Valore Permuta','Permuta Marca','Permuta Modello','Permuta Targa',
    'Finanziamento','Importo Finanziato','Banca','Tasso',
    'LoJack','Mawdy','Blockshaft','GPL','Tagliando','Retrocamera','2a Chiave','PP','FIR','GAP',
  ];

  const righeC = (contrattiFiltrati || [])
    // Ordinamento esplicito qui dentro, indipendente da come arriva
    // l'array in ingresso — così l'export mostra sempre i più recenti
    // per primi, senza dipendere da presupposti su chi lo chiama.
    .slice()
    .sort((a, b) => new Date(b.dataCreazione || 0) - new Date(a.dataCreazione || 0))
    .map(c => {
    const cl = c.cliente || {}; const v = c.veicolo || {}; const pr = c.prezzo || {}; const sv2 = c.servizi || {}; const de = c.datiExtra || {};
    const pL = parseFloat(pr.prezzoListino || v.prezzo || 0);
    const sc = parseFloat(pr.sconto || 0); const ipt = parseFloat(pr.ipt || 0); const cap = parseFloat(pr.caparra || 0);
    const perm = valorePermuteTot(pr); const fin = parseFloat(pr.importoFinanziato || 0);
    const totS = Object.entries(sv2).filter(([k]) => k.startsWith('costo')).reduce((t,[,val]) => t+(parseFloat(val)||0), 0);
    const saldo = pL - sc + ipt + totS - perm - fin - cap;
    // La prima riga di "incassi" corrisponde sempre alla caparra — è
    // posizionata subito sotto il suo campo nel form del preventivo, da
    // cui questo dato arriva quando il preventivo viene convertito in
    // contratto (in ContrattoModal.jsx non è più modificabile).
    const modalitaCaparra = sv((pr.incassi && pr.incassi[0]) ? pr.incassi[0].modalita : '');
    const sede = getSede((c.venditoreNome||'').toLowerCase());
    const statoConsegnaLabels = { da_preparare: 'Da preparare', in_lavorazione: 'In lavorazione', pronto: 'Pronto', consegnato: 'Consegnato' };
    const avanzamento = statoConsegnaLabels[c.backoffice?.statoConsegna] || 'Da preparare';
    const ingresso = ingressoStock(c);
    return [
      sv(c.stato), avanzamento, sv(de.numeroContratto), fd(de.dataFirma), fd(c.dataCreazione), (() => { const ic = infoConsegna(c); return ic.consegnato && ic.data ? fd(ic.data) : ''; })(), sv(c.venditoreNome), sede,
      sv(cl.nome), sv(cl.cognome), sv(cl.ragioneSociale), sv(cl.codiceFiscale || cl.piva), sv(cl.telefono||cl.cellulare), sv(cl.email), sv(cl.citta), sv(cl.cap), sv(cl.provenienza), cl.giaCliente ? 'Sì' : 'No',
      sv(v.marca), sv(v.modello), sv(v.versione), sv(v.tipo), sv(v.targa), sv(de.vin||v.vin), sv(de.immatricolazione||v.registrazione), sv(v.carburante), parseFloat(v.km) || 0, sv(de.coloreVeicolo||v.colore), ingresso, giorniGiacenza(ingresso, c),
      pL, sc, ipt, totS, cap, modalitaCaparra, saldo,
      yn(pr.hasPermuta), perm, sv(pr.permutaMarca), sv(pr.permutaModello), sv(pr.permutaTarga),
      yn(pr.hasFinanziamento), fin, sv(pr.bancaFinanziamento||pr.finanziariaSocieta), sv(pr.tassoFinanziamento||pr.tabellaFinanziamento),
      yn(sv2.lojack), yn(sv2.mawdy36 || sv2.mawdy), yn(sv2.blockshaft), yn(sv2.gpl), yn(sv2.tagliando), yn(sv2.retrocamera), yn(sv2.secondaChiave), yn(sv2.assPP), yn(sv2.assFIR), yn(sv2.assGAP),
    ];
  });

  const COLONNE_VALUTA_P = ['Prezzo Listino','Sconto','IPT','Caparra','Saldo','Valore Permuta','Importo Finanziato'];

  const wb = XLSX.utils.book_new();
  const wsP = XLSX.utils.aoa_to_sheet([headersP, ...righeP], { cellDates: true });
  wsP['!cols'] = headersP.map((_, i) => ({ wch: i < 3 ? 18 : i < 10 ? 20 : 14 }));
  applicaFormatiExcel(wsP, headersP, righeP.length, COLONNE_VALUTA_P, ['Data']);
  XLSX.utils.book_append_sheet(wb, wsP, 'Preventivi');

  let headersCFinali = headersC, righeCFinali = righeC;
  if (Array.isArray(colonneSelezionateContratti) && colonneSelezionateContratti.length > 0) {
    const indiciTenuti = headersC.map((h, i) => colonneSelezionateContratti.includes(h) ? i : -1).filter(i => i !== -1);
    headersCFinali = indiciTenuti.map(i => headersC[i]);
    righeCFinali = righeC.map(riga => indiciTenuti.map(i => riga[i]));
  }
  const COLONNE_VALUTA_C = ['Prezzo Listino','Sconto','IPT','Servizi','Caparra','Saldo','Valore Permuta','Importo Finanziato'];
  const wsC = XLSX.utils.aoa_to_sheet([headersCFinali, ...righeCFinali], { cellDates: true });
  wsC['!cols'] = headersCFinali.map(() => ({ wch: 18 }));
  applicaFormatiExcel(wsC, headersCFinali, righeCFinali.length, COLONNE_VALUTA_C, ['Data Firma', 'Data Creazione', 'Data Consegna', 'Data Ingresso Stock']);
  XLSX.utils.book_append_sheet(wb, wsC, 'Contratti');
  const oggi = new Date();
  const tag = `${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}`;
  scriviExcel(wb, `${nomeFile}_${tag}.xlsx`);
}
// ─────────────────────────────────────────────────────────────────────────────

const THEMES = {
  dark: {
    bg:       '#0a0c10',
    surface:  '#141722',
    surface2: '#0a0c10',
    border:   '#1e2230',
    border2:  '#2d334a',
    text:     '#e5e7eb',
    textSub:  '#9ca3af',
    textMuted:'#475569',
    navBg:    '#141722',
    cardBg:   '#141722',
    inputBg:  '#0a0c10',
    chartBg:  '#0a0c10',
    dropBg:   '#1a1d29',
  },
  light: {
    bg:       '#f1f5f9',
    surface:  '#ffffff',
    surface2: '#f8fafc',
    border:   '#e2e8f0',
    border2:  '#cbd5e1',
    text:     '#0f172a',
    textSub:  '#475569',
    textMuted:'#94a3b8',
    navBg:    '#ffffff',
    cardBg:   '#ffffff',
    inputBg:  '#f8fafc',
    chartBg:  '#f1f5f9',
    dropBg:   '#ffffff',
  }
};

// ─── BREAKPOINT ───────────────────────────────────────────────────────────────
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => { const fn = () => setW(window.innerWidth); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024 };
}

// ─── DRAWER FILTRI ────────────────────────────────────────────────────────────
function FilterDrawer({ open, onClose, children, activeCount, T }) {
  return (
    <>
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400, backdropFilter: 'blur(3px)' }} />}
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(320px, 88vw)', background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 401, transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.28s ease', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, background: T.surface, zIndex: 10 }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '900', color: T.text, letterSpacing: '1px' }}>FILTRI</div>
            {activeCount > 0 && <div style={{ fontSize: '10px', color: '#3b82f6', fontWeight: '700', marginTop: '2px' }}>{activeCount} attivi</div>}
          </div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontWeight: '900', fontSize: '18px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>{children}</div>
      </div>
    </>
  );
}

// Piccolo componente switch luna/sole
function ThemeSwitch({ theme, onToggle }) {
  const isDark = theme === 'dark';
  return (
    <button
      onClick={onToggle}
      title={isDark ? 'Passa al tema chiaro' : 'Passa al tema scuro'}
      style={{
        width: '38px', height: '22px',
        borderRadius: '11px',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        background: isDark ? '#2d334a' : '#e2e8f0',
        transition: 'background 0.25s',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        padding: '0 3px',
      }}
    >
      <div style={{
        width: '16px', height: '16px',
        borderRadius: '50%',
        background: isDark ? '#3b82f6' : '#f59e0b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '9px',
        transform: isDark ? 'translateX(0)' : 'translateX(16px)',
        transition: 'transform 0.25s, background 0.25s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }}>
        {isDark ? '🌙' : '☀️'}
      </div>
    </button>
  );
}

// Aggiorna questa data/ora ad ogni build+deploy, insieme allo stesso
// valore su Firebase (app_meta/version) — chi ha ancora la versione
// vecchia caricata vedrà comparire il banner "Nuova versione disponibile".
const APP_VERSION = '2026-08-28-02';

const firebaseConfig = {
  apiKey: "AIzaSyC1FvCRpy9uuLuwvfC4C7GgTIh5h2AYvi8",
  authDomain: "gas---bi.firebaseapp.com",
  databaseURL: "https://gas---bi-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "gas---bi",
  storageBucket: "gas---bi.firebasestorage.app",
  messagingSenderId: "932397728005",
  appId: "1:932397728005:web:01b347e28a93d6af34776c"
};

const app  = initializeApp(firebaseConfig);

// ─── DOCUMENTI (PDF/foto) — upload verso Cloudflare R2 tramite Worker ──────
// RadaR salva SOLO l'URL su Firebase, mai il file — resta leggero.
// Da configurare dopo aver creato il Worker e il bucket R2 (vedi istruzioni).
const DOCUMENTI_WORKER_URL = 'https://radar-documenti.easydigitale.workers.dev';
const DOCUMENTI_UPLOAD_TOKEN = '1nt3RN010@';

async function caricaDocumento(file, cartella) {
  const res = await fetch(`${DOCUMENTI_WORKER_URL}/upload?cartella=${encodeURIComponent(cartella)}`, {
    method: 'POST',
    headers: {
      'X-Upload-Token': DOCUMENTI_UPLOAD_TOKEN,
      'X-File-Name': file.name,
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Errore upload (HTTP ${res.status})`);
  }
  return res.json(); // { ok, url, chiave, nome }
}

async function eliminaDocumentoR2(chiave) {
  const res = await fetch(`${DOCUMENTI_WORKER_URL}/elimina?chiave=${encodeURIComponent(chiave)}`, {
    method: 'DELETE',
    headers: { 'X-Upload-Token': DOCUMENTI_UPLOAD_TOKEN },
  });
  if (!res.ok) throw new Error('Errore eliminazione file su R2');
  return res.json();
}

// Componente riusabile — mostra e gestisce i documenti collegati a un
// contratto o veicolo. "percorsoFirebase" è dove si salvano i metadati
// (es. "documenti_contratti/{id}" o "documenti_veicoli/{id}"), "cartellaR2"
// è la cartella dentro il bucket (es. "contratti/{id}").
function DocumentiSection({ db, user, percorsoFirebase, cartellaR2, T }) {
  const [documenti, setDocumenti] = useState([]);
  const [caricando, setCaricando] = useState(false);
  const [errore, setErrore] = useState('');
  const inputRef = useRef(null);
  const puoGestire = ['admin', 'super_admin', 'back_office'].includes((user?.ruolo || '').toLowerCase().trim());

  useEffect(() => {
    const unsub = onValue(ref(db, percorsoFirebase), snap => {
      const data = snap.val() || {};
      setDocumenti(Object.entries(data).map(([id, d]) => ({ id, ...d })).sort((a, b) => new Date(b.caricatoAt) - new Date(a.caricatoAt)));
    });
    return () => unsub();
  }, [percorsoFirebase]);

  const handleFile = async (file) => {
    if (!file) return;
    setErrore('');
    setCaricando(true);
    try {
      const risultato = await caricaDocumento(file, cartellaR2);
      const docId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await set(ref(db, `${percorsoFirebase}/${docId}`), {
        nome: risultato.nome,
        url: risultato.url,
        chiave: risultato.chiave,
        tipo: file.type?.startsWith('image/') ? 'foto' : 'pdf',
        caricatoAt: new Date().toISOString(),
        caricatoDa: `${user?.nome || ''} ${user?.cognome || ''}`.trim() || 'N.D.',
      });
    } catch (e) {
      setErrore('Errore nel caricamento: ' + e.message);
    }
    setCaricando(false);
  };

  const handleElimina = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.nome}"? Non è recuperabile.`)) return;
    try {
      await eliminaDocumentoR2(doc.chiave);
      await set(ref(db, `${percorsoFirebase}/${doc.id}`), null);
    } catch (e) {
      alert('Errore nella rimozione: ' + e.message);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      {puoGestire ? (
        <>
          <input ref={inputRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
          <button onClick={() => inputRef.current?.click()} disabled={caricando}
            style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: '10px', color: '#fff', cursor: caricando ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', marginBottom: '14px', opacity: caricando ? 0.6 : 1 }}>
            {caricando ? '⏳ Caricamento...' : '📎 Carica PDF o Foto'}
          </button>
          {errore && <div style={{ color: '#ef4444', fontSize: '11px', marginBottom: '10px' }}>{errore}</div>}
        </>
      ) : (
        <div style={{ fontSize: '11px', color: T.textMuted, marginBottom: '14px' }}>🔒 Solo admin e super admin possono caricare o eliminare documenti — puoi comunque aprire quelli già presenti.</div>
      )}

      {documenti.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px', color: T.textMuted, fontSize: '12px' }}>Nessun documento caricato.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {documenti.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: '18px' }}>{doc.tipo === 'foto' ? '🖼️' : '📄'}</div>
              <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '12px', color: '#3b82f6', textDecoration: 'none', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {doc.nome}
              </a>
              <div style={{ fontSize: '9px', color: T.textMuted, whiteSpace: 'nowrap' }}>
                {doc.caricatoDa} · {new Date(doc.caricatoAt).toLocaleDateString('it-IT')}
              </div>
              <button onClick={() => handleElimina(doc)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '2px 6px', visibility: puoGestire ? 'visible' : 'hidden' }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const db   = getDatabase(app);
const auth = getAuth(app);

// ─── AUTENTICAZIONE A DUE FATTORI (Authenticator / TOTP) ───────────────────
// Obbligatoria per tutti: chi non l'ha ancora attivata deve configurarla al
// primo accesso. Esenti solo gli account tecnici usati dagli script
// automatici (sync stock, export…), che non possono inserire un codice.
const MFA_OBBLIGATORIA = true;

// ─── LOG ACCESSI ─────────────────────────────────────────────────────────────
// Un record per ogni login riuscito (non per i semplici ricaricamenti di
// pagina): chi, quando, con quale metodo e da quale dispositivo/browser.
function descriviDispositivo() {
  const ua = navigator.userAgent || '';
  const so = /Windows/i.test(ua) ? 'Windows' : /Android/i.test(ua) ? 'Android' : /iPhone|iPad|iPod/i.test(ua) ? 'iOS' : /Mac OS/i.test(ua) ? 'macOS' : /Linux/i.test(ua) ? 'Linux' : 'Altro';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Altro';
  const tipo = /Mobi|Android|iPhone/i.test(ua) ? 'Smartphone' : /iPad|Tablet/i.test(ua) ? 'Tablet' : 'Computer';
  return { so, browser, tipo, ua: ua.slice(0, 300) };
}
// ─── SESSIONE UNICA: un utente può essere connesso da un solo dispositivo ───
// Ogni login "prende" la sessione (sessioni_attive/{uid}); gli altri
// dispositivi dello stesso utente vedono cambiare l'id e vengono disconnessi.
// Esenti le utenze condivise usate su più postazioni.
const SESSIONE_UNICA_ESENTI = ['officina.salerno@radargas.it', 'service@radargas.it', 'demo@radardms.it', 'servizi@radargas.it'];
const chiaveSessioneLocale = uid => `radar_sessione_${uid}`;
const leggiSessioneLocale = uid => { try { return JSON.parse(localStorage.getItem(chiaveSessioneLocale(uid)) || 'null'); } catch (e) { return null; } };
async function prendiSessione(fbUser) {
  if (!fbUser || SESSIONE_UNICA_ESENTI.includes(String(fbUser.email || '').toLowerCase())) return;
  const sess = { id: `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`, at: new Date().toISOString() };
  try { localStorage.setItem(chiaveSessioneLocale(fbUser.uid), JSON.stringify(sess)); } catch (e) { /* ok */ }
  try {
    const d = descriviDispositivo();
    await set(ref(db, `sessioni_attive/${fbUser.uid}`), { ...sess, dispositivo: `${d.tipo} · ${d.so} · ${d.browser}` });
  } catch (e) { /* senza regole aggiornate la sessione unica resta inattiva */ }
}

// "AGGIORNA DB" in RADAR (import CSV delle trattative): admin, super admin
// e, come eccezione, queste persone
const ECCEZIONI_AGGIORNA_DB = ['emanuele.amendola@radargas.it'];
const puoAggiornareDbRadar = (u) => ['admin', 'super_admin'].includes(String(u?.ruolo || '').toLowerCase().trim())
  || ECCEZIONI_AGGIORNA_DB.includes(String(u?.email ?? window._radarEmailUtente ?? '').toLowerCase().trim());

// Tab ANALISI: visibile solo a queste persone
const UTENTI_ANALISI = ['rosario.scala@radargas.it', 'daniele.fiore@radargas.it', 'gianluca.scala@radargas.it', 'elvira.scala@radargas.it'];
const puoVedereAnalisi = (u) => UTENTI_ANALISI.includes(String(u?.email ?? window._radarEmailUtente ?? '').toLowerCase().trim());

// Il registro accessi è visibile solo a questo utente (anche nelle regole del database)
const UTENTI_LOG_ACCESSI = ['daniele.fiore@radargas.it'];
const puoVedereLogAccessi = () => UTENTI_LOG_ACCESSI.includes(String(window._radarEmailUtente || '').toLowerCase());
async function registraAccesso(fbUser, metodo) {
  if (!fbUser) return;
  try {
    await push(ref(db, 'log_accessi'), {
      uid: fbUser.uid, email: fbUser.email || '', at: new Date().toISOString(), metodo, ...descriviDispositivo(),
    });
  } catch (e) { /* il log non deve mai impedire l'accesso */ }
}
const MFA_ESENTI = ['servizi@radargas.it', 'daniele.fiore@radargas.it'];

// ─── CACHE PERSISTENTE — IndexedDB ───────────────────────────────────────
// A differenza di localStorage (limite ~5-10MB, già rivelatosi insufficiente
// per contratti+preventivi insieme, ~7MB), IndexedDB non ha questo limite
// stretto — pensato apposta per salvare grandi quantità di dati nel
// browser, persistenti tra un refresh/riavvio e l'altro. Ogni funzione ha
// un fallback silenzioso: se IndexedDB non è disponibile (es. modalità
// privata di alcuni browser) o fallisce per qualsiasi motivo, l'app
// continua a funzionare esattamente come se la cache non ci fosse —
// nessun errore visibile, nessuna funzionalità persa, solo niente
// persistenza tra un refresh e l'altro (esattamente il comportamento di
// oggi prima di questa modifica).
function idbApri() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) { reject(new Error('IndexedDB non disponibile')); return; }
    const req = window.indexedDB.open('radar_cache', 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains('dati')) req.result.createObjectStore('dati');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbLeggi(chiave) {
  try {
    const database = await idbApri();
    return await new Promise((resolve, reject) => {
      const tx = database.transaction('dati', 'readonly');
      const req = tx.objectStore('dati').get(chiave);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch (e) { return null; } // fallback silenzioso: nessuna cache, comportamento come prima di oggi
}
async function idbScrivi(chiave, valore) {
  try {
    const database = await idbApri();
    await new Promise((resolve, reject) => {
      const tx = database.transaction('dati', 'readwrite');
      tx.objectStore('dati').put(valore, chiave);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) { /* fallback silenzioso: scrittura persa, ma la sessione corrente resta comunque aggiornata in memoria */ }
}

const ALERT_DAYS = 90;

// ─── Asset immagine per la stampa documenti Service (logo + auto stilizzate
// per lo schema danni) — caricati come base64, incorporati direttamente nel
// documento stampato, nessuna richiesta di rete aggiuntiva al momento della
// stampa.
const LOGO_GAS_BASE64 = "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";
const AUTO_FL_BASE64 = "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";
const AUTO_ALTO_BASE64 = "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";


function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const header = lines[0].split(';').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
  return lines.slice(1).map(line => {
    const cols = []; let inQ = false; let cur = '';
    for (let c of line) { if (c === '"') inQ = !inQ; else if (c === ';' && !inQ) { cols.push(cur); cur = ''; } else cur += c; }
    cols.push(cur);
    const obj = { _raw: cols };
    header.forEach((h, i) => { obj[h] = (cols[i] || '').replace(/^"|"$/g, '').trim(); });
    return mapRow(obj);
  });
}

function mapRow(raw) {
  const find = (...c) => { for (let k of c) if (raw[k]) return raw[k]; return ''; };
  let rawVal = String(find('importo', 'valore')).replace(/[^0-9,.]/g, '');
  if (rawVal.includes(',') && rawVal.includes('.')) rawVal = rawVal.replace('.', '').replace(',', '.');
  else if (rawVal.includes(',')) rawVal = rawVal.replace(',', '.');
  const dataChiusura = find('data chiusura') || '';
  const meseAnnoChiusura = dataChiusura.split('/').length >= 3 ? dataChiusura.split('/').slice(1, 3).join('/') : 'N.D.';
  return {
    data: find('data trattativa'),
    meseAnno: find('data trattativa').split('/').length >= 3 ? find('data trattativa').split('/').slice(1, 3).join('/') : 'N.D.',
    dataChiusura, meseAnnoChiusura,
    stato: find('stato trattativa') || 'In corso',
    venditore: find('venditore') || 'Non assegnato',
    cliente: `${find('nome cliente')} ${find('cognome cliente')}`.trim() || find('rag. sociale cliente') || 'Cliente Ignoto',
    marca: find('marca') || 'Generica',
    modello: find('modello') || '',
    importo: parseFloat(rawVal) || 0,
    origine: find('origine trattativa') || 'Sconosciuta',
    citta: find('citt\u00e0', 'citta') || 'N.D.',
    sede: find('sede') || raw._raw[6] || 'Sede Centrale'
  };
}

const fmt  = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtN = n => new Intl.NumberFormat('it-IT').format(n);

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
const SIDEBAR_WIDTH = 212;
const SIDEBAR_WIDTH_COLLAPSED = 68;
const BOTTOM_BAR_HEIGHT = 64;

// ─── Informativa Privacy Utenti RadaR (Art. 13 GDPR) ──────────────────────
function PrivacyInfoModal({ T, onClose }) {
  const sezioni = [
    { titolo: '1. Chi tratta i tuoi dati? (Titolare e Responsabile)', voci: [
      'Titolare del Trattamento: Gruppo Autoscala Srl.',
      'Responsabile del Trattamento: Daniele Fiore, in qualità di fornitore dell\'infrastruttura tecnologica e manutentore del software.',
    ]},
    { titolo: '2. Quali dati raccoglie la piattaforma?', intro: 'Durante l\'utilizzo di RadaR®, il sistema raccoglie ed elabora le seguenti informazioni dell\'utente aziendale:', voci: [
      'Dati di Profilo: Nome, cognome, indirizzo email aziendale, ruolo assegnato (es. Admin, Venditore, Back Office).',
      'Dati di Autenticazione: Credenziali di accesso (la password è memorizzata in modalità crittografata e non è visibile al gestore).',
      'Dati di Utilizzo e Log: Indirizzo IP di connessione, data e ora degli accessi, tracciamento delle operazioni effettuate all\'interno del software (es. inserimento di un nuovo contratto, modifica di un lead, aggiornamento dello stock).',
    ]},
    { titolo: '3. Perché vengono trattati questi dati? (Finalità e Base Giuridica)', intro: 'I dati degli utenti vengono trattati per le seguenti finalità:', voci: [
      'Esecuzione del rapporto lavorativo/collaborativo: Consentire l\'utilizzo dello strumento informatico per lo svolgimento delle mansioni quotidiane (Base giuridica: Esecuzione del contratto di lavoro).',
      'Sicurezza informatica: Monitorare gli accessi per prevenire frodi, accessi non autorizzati o data breach (Base giuridica: Legittimo interesse del Titolare a proteggere il proprio patrimonio informativo).',
      'Ottimizzazione aziendale: Monitorare le metriche aggregate di performance commerciali (es. lead gestiti, vendite concluse) per scopi di coordinamento aziendale.',
    ]},
    { titolo: '4. Come vengono protetti i dati?', intro: 'I tuoi dati sono ospitati su server sicuri all\'interno dell\'Unione Europea tramite l\'infrastruttura Cloud di Google Firebase. Sono protetti da:', voci: [
      'Crittografia dei dati in transito (HTTPS) e a riposo.',
      'Separazione logica dei database (i dati di una concessionaria non sono accessibili da altre strutture).',
      'Regole rigide di autorizzazione basate sul ruolo aziendale.',
    ]},
    { titolo: '5. Per quanto tempo vengono conservati?', intro: 'I dati dell\'utente aziendale vengono conservati per tutta la durata del rapporto di lavoro o collaborazione con il Titolare. In caso di cessazione del rapporto lavorativo (es. licenziamento o dimissioni del venditore), l\'account verrà disattivato tempestivamente dal Titolare e i relativi log di tracciamento interni saranno conservati per il periodo necessario a garantire la tutela legale dell\'azienda (di norma non oltre i termini di prescrizione di legge).' },
    { titolo: '6. Quali sono i tuoi diritti?', intro: 'In ogni momento, l\'utente può esercitare nei confronti del Titolare (la propria azienda) i diritti previsti dagli artt. 15 e seguenti del GDPR, tra cui:', voci: [
      'Diritto di accedere ai propri dati e conoscerne il trattamento.',
      'Diritto di rettifica in caso di dati inesatti.',
      'Diritto alla cancellazione o limitazione del trattamento (ove applicabile).',
      'Diritto di proporre reclamo all\'Autorità Garante per la Protezione dei Dati Personali.',
    ]},
  ];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: '18px', maxWidth: '640px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>🔒 Informativa Privacy per gli Utenti della Piattaforma RadaR®</div>
            <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>Art. 13 GDPR — Informativa sul trattamento dei dati personali degli utenti aziendali</div>
          </div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px', flexShrink: 0 }}>×</button>
        </div>
        <div style={{ padding: '20px 22px', overflowY: 'auto', fontSize: '12.5px', lineHeight: 1.6, color: T.textSub }}>
          <p style={{ marginTop: 0 }}>La presente informativa è resa ai sensi dell'Art. 13 del Regolamento UE 2016/679 (GDPR) agli utenti autorizzati all'accesso e all'utilizzo della piattaforma software RadaR®.</p>
          {sezioni.map((sez, i) => (
            <div key={i} style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: '900', color: T.text, marginBottom: '6px' }}>{sez.titolo}</div>
              {sez.intro && <p style={{ margin: '0 0 6px 0' }}>{sez.intro}</p>}
              {sez.voci ? (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {sez.voci.map((v, j) => <li key={j} style={{ marginBottom: '5px' }}>{v}</li>)}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, textAlign: 'right' }}>
          <button onClick={onClose} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: '10px', padding: '9px 20px', cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Ho capito</button>
        </div>
      </div>
    </div>
  );
}

function NavBar({ T, theme, toggleTheme, activeTab, setActiveTab, user, onLogout, collapsed, onToggleCollapsed }) {
  const [contrattiBozza, setContrattiBozza] = useState(0);
  const puoVedere = user?.ruolo === 'admin' || user?.ruolo === 'super_admin';
  useEffect(() => {
    if (!puoVedere) return;
    // Prima scaricava l'INTERO nodo contratti solo per contare le bozze,
    // riattivandosi ad ogni scrittura di qualsiasi contratto da parte di
    // chiunque — il badge NavBar è sempre montato per tutta la sessione di
    // ogni admin. La query filtrata scarica solo i record 'bozza'.
    const bozzeQuery = query(ref(db, 'contratti'), orderByChild('stato'), equalTo('bozza'));
    return onValue(bozzeQuery, snap => {
      const data = snap.val();
      setContrattiBozza(data ? Object.keys(data).length : 0);
    });
  }, [puoVedere]);
  const { isMobile } = useBreakpoint();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const ruolo = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();

  const TABS_ALL = [
    ['radar',      '📊', 'RADAR',      '#3b82f6', ['super_admin','admin','consulente','brand_manager_se','bdc','admin_nlt','consulente_nlt','logistica']],
    ['stock',      '🚗', 'STOCK',      '#10b981', ['super_admin','admin','consulente','brand_manager_se','back_office','bdc','logistica','front','admin_nlt','consulente_nlt','partner','finance','osservatore','service']],
    ['preventivi', '📋', 'PREV.',      '#f59e0b', ['super_admin','admin','consulente','brand_manager_se','bdc','back_office','logistica','front','partner']],
    ['contratti',  '📄', 'CONTR.',     '#10b981', ['super_admin','admin','consulente','brand_manager_se','back_office','finance','bdc','logistica','front','partner']],
    ['backoffice', '🔧', 'BACK OFF.',  '#8b5cf6', ['super_admin','admin','back_office','consulente','brand_manager_se','logistica','service','front']],
    ['finance',    '💰', 'FINANCE',    '#10b981', ['super_admin','admin','finance','back_office']],
    ['service',    '🔧', 'SERVICE',    '#6366f1', ['super_admin','admin','service','logistica','meccanico']],
    ['noleggio',   '🚐', 'NOLEGGIO',   '#8b5cf6', ['super_admin','admin','admin_nlt','consulente_nlt']],
    ['logistica',  '🚚', 'LOGISTICA',  '#0ea5e9', ['super_admin','admin','logistica','service','osservatore']],
    ['analisi',    '📈', 'ANALISI',    '#f59e0b', ['super_admin','admin']],
  ];
  const tabs = TABS_ALL.filter(([id,,,,ruoli]) => {
    // ANALISI: solo le persone indicate, qualunque sia il ruolo
    if (id === 'analisi') return puoVedereAnalisi(user);
    const r = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
    if (ruoli.includes(r)) return true;
    const permessi = toArraySafe(user?.permessi);
    return permessi.some(p => ruoli.includes(p));
  });

  // ═══════════════════════════ MOBILE: top bar + bottom tab bar ═══════════════════════════
  if (isMobile) {
    return (
      <>
        {/* TOP BAR compatta — solo brand, tema, utente */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: T.navBg, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 200 }}>
          <img src={theme === 'light' ? '/logo-blue.png' : '/logo.png'} alt="Logo" style={{ height: theme === 'light' ? '28px' : '20px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ThemeSwitch theme={theme} onToggle={toggleTheme} />
            <button onClick={() => setUserMenuOpen(o => !o)} style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#3b82f6', border: 'none', color: '#fff', fontWeight: '900', fontSize: '12px', cursor: 'pointer' }}>
              {(user?.nome || '?').charAt(0).toUpperCase()}
            </button>
          </div>
        </div>

        {/* Menu utente a tendina */}
        {userMenuOpen && (
          <>
            <div onClick={() => setUserMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 300 }} />
            <div style={{ position: 'fixed', top: '46px', right: '12px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', padding: '12px', zIndex: 301, minWidth: '160px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: '12px', fontWeight: '800', color: T.text, marginBottom: '10px' }}>{user.nome} {user.cognome}</div>
              <button onClick={onLogout} style={{ width: '100%', background: '#ef444415', padding: '8px 12px', borderRadius: '9px', color: '#ef4444', border: '1px solid #ef444430', cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>ESCI</button>
            </div>
          </>
        )}

        {/* BOTTOM TAB BAR — max 4 tab principali + "Menu" per il resto. Con
            più di 4-5 tab, stiparli tutti come icone rende ognuno troppo
            piccolo per essere toccato comodamente. */}
        {(() => {
          const MAX_BAR = 4;
          const inBarra = tabs.length > MAX_BAR ? tabs.slice(0, MAX_BAR) : tabs;
          const inMenu = tabs.length > MAX_BAR ? tabs.slice(MAX_BAR) : [];
          const menuAttivo = inMenu.some(([tab]) => tab === activeTab);
          return (
            <>
              <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: `${BOTTOM_BAR_HEIGHT}px`, background: T.navBg, borderTop: `1px solid ${T.border}`, display: 'flex', zIndex: 200, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                {inBarra.map(([tab, icon, label, color]) => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 2px' }}>
                    <span style={{ fontSize: '17px', filter: activeTab === tab ? 'none' : 'grayscale(40%)', opacity: activeTab === tab ? 1 : 0.55 }}>{icon}</span>
                    <span style={{ fontSize: '8px', fontWeight: '900', color: activeTab === tab ? color : T.textMuted, letterSpacing: '0.2px' }}>{label}</span>
                    {activeTab === tab && <div style={{ position: 'absolute', top: 0, left: '30%', right: '30%', height: '2.5px', background: color, borderRadius: '0 0 3px 3px' }} />}
                    {tab === 'contratti' && contrattiBozza > 0 && (
                      <span style={{ position: 'absolute', top: '2px', right: '18%', background: '#ef4444', color: '#fff', borderRadius: '8px', fontSize: '7px', fontWeight: '900', padding: '1px 4px', lineHeight: '11px' }}>{contrattiBozza}</span>
                    )}
                  </button>
                ))}
                {inMenu.length > 0 && (
                  <button onClick={() => setBottomMenuOpen(o => !o)}
                    style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 2px' }}>
                    <span style={{ fontSize: '17px', opacity: menuAttivo || bottomMenuOpen ? 1 : 0.55, filter: menuAttivo || bottomMenuOpen ? 'none' : 'grayscale(40%)' }}>☰</span>
                    <span style={{ fontSize: '8px', fontWeight: '900', color: menuAttivo || bottomMenuOpen ? '#64748b' : T.textMuted, letterSpacing: '0.2px' }}>ALTRO</span>
                    {(menuAttivo || bottomMenuOpen) && <div style={{ position: 'absolute', top: 0, left: '30%', right: '30%', height: '2.5px', background: '#64748b', borderRadius: '0 0 3px 3px' }} />}
                  </button>
                )}
              </div>

              {/* Pannello "Altro" — scorre dal basso, elenca le voci restanti per esteso */}
              {bottomMenuOpen && inMenu.length > 0 && (
                <>
                  <div onClick={() => setBottomMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 250 }} />
                  <div style={{ position: 'fixed', bottom: `${BOTTOM_BAR_HEIGHT}px`, left: 0, right: 0, background: T.surface, borderRadius: '18px 18px 0 0', border: `1px solid ${T.border}`, borderBottom: 'none', padding: '10px 8px', zIndex: 251, boxShadow: '0 -8px 24px rgba(0,0,0,0.25)', paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}>
                    <div style={{ width: '36px', height: '4px', background: T.border, borderRadius: '3px', margin: '0 auto 10px' }} />
                    {inMenu.map(([tab, icon, label, color]) => (
                      <button key={tab} onClick={() => { setActiveTab(tab); setBottomMenuOpen(false); }}
                        style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', border: 'none', borderRadius: '12px', cursor: 'pointer', background: activeTab === tab ? `${color}18` : 'transparent', color: activeTab === tab ? color : T.text, fontWeight: '800', fontSize: '13px', textAlign: 'left', marginBottom: '2px' }}>
                        <span style={{ fontSize: '18px' }}>{icon}</span>
                        <span>{label}</span>
                        {tab === 'contratti' && contrattiBozza > 0 && (
                          <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', borderRadius: '10px', fontSize: '10px', fontWeight: '900', padding: '2px 7px' }}>{contrattiBozza}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          );
        })()}
      </>
    );
  }

  // ═══════════════════════════ DESKTOP: sidebar laterale (collassabile) ═══════════════════════════
  const sbWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: `${sbWidth}px`, background: T.navBg, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', zIndex: 200, transition: 'width 0.18s ease' }}>
      {/* LOGO + TOGGLE */}
      <div style={{ padding: collapsed ? '16px 10px' : '18px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', gap: '6px' }}>
        {!collapsed && <img src={theme === 'light' ? '/logo-blue.png' : '/logo.png'} alt="Logo" style={{ height: theme === 'light' ? '36px' : '26px', maxWidth: '140px', objectFit: 'contain' }} />}
        <button onClick={onToggleCollapsed} title={collapsed ? 'Espandi menu' : 'Riduci menu'}
          style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.textSub, fontSize: '13px', flexShrink: 0 }}>
          {collapsed ? '»' : '«'}
        </button>
      </div>

      {/* NAV */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: collapsed ? '12px 8px' : '12px 10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {tabs.map(([tab, icon, label, color]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} title={collapsed ? label : undefined}
            style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: '10px', padding: collapsed ? '11px 8px' : '10px 12px', border: 'none', borderRadius: '10px', cursor: 'pointer', background: activeTab === tab ? color : 'transparent', color: activeTab === tab ? '#fff' : T.textSub, fontWeight: '800', fontSize: '12px', letterSpacing: '0.3px', textAlign: 'left', transition: 'background 0.15s' }}
            onMouseEnter={e => { if (activeTab !== tab) e.currentTarget.style.background = T.bg; }}
            onMouseLeave={e => { if (activeTab !== tab) e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ fontSize: '15px' }}>{icon}</span>
            {!collapsed && <span>{label}</span>}
            {tab === 'contratti' && contrattiBozza > 0 && (
              <span style={{ position: collapsed ? 'absolute' : 'static', top: collapsed ? '4px' : 'auto', right: collapsed ? '4px' : 'auto', marginLeft: collapsed ? 0 : 'auto', background: activeTab === tab ? 'rgba(255,255,255,0.3)' : '#ef4444', color: '#fff', borderRadius: '10px', fontSize: collapsed ? '8px' : '9px', fontWeight: '900', padding: collapsed ? '1px 4px' : '2px 6px', minWidth: '16px', textAlign: 'center', lineHeight: '14px' }}>{contrattiBozza}</span>
            )}
          </button>
        ))}
      </div>

      {/* UTENTE + TEMA + LOGOUT */}
      <div style={{ padding: '12px', borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: '10px', alignItems: collapsed ? 'center' : 'stretch' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', gap: '8px', width: '100%' }}>
          {!collapsed && <span style={{ fontSize: '11px', fontWeight: '700', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.nome} {user.cognome}</span>}
          <ThemeSwitch theme={theme} onToggle={toggleTheme} />
        </div>
        <button onClick={onLogout} title={collapsed ? 'Esci' : undefined} style={{ background: T.bg, padding: '8px 12px', borderRadius: '9px', color: T.text, border: `1px solid ${T.border}`, cursor: 'pointer', fontWeight: '800', fontSize: collapsed ? '13px' : '10px', width: collapsed ? '36px' : '100%' }}>{collapsed ? '⏻' : 'ESCI'}</button>
      </div>
    </div>
  );
}

function AppInner() {
  const [user, setUser]             = useState(null);   // { uid, nome, cognome, ruolo, email }
  const [authLoading, setAuthLoading] = useState(true);
  const [loginCreds, setLoginCreds] = useState({ user: '', pass: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  // 2FA: verifica del codice al login e configurazione al primo accesso
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaCodice, setMfaCodice] = useState('');
  const [mfaDaConfigurare, setMfaDaConfigurare] = useState(false);
  const [mfaSegreto, setMfaSegreto] = useState(null);     // { secret, qr, chiave }
  const [mfaErrore, setMfaErrore] = useState('');
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('radar');
  const [theme, setTheme] = useState(() => localStorage.getItem('radar_theme') || 'dark');
  const { isMobile } = useBreakpoint();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('radar_sidebar_collapsed') === '1');
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(c => {
      localStorage.setItem('radar_sidebar_collapsed', !c ? '1' : '0');
      return !c;
    });
  };
  const [contrattiGlobali, setContrattiGlobali] = useState([]);
  // Versione SENZA il filtro NLT (filtraCrm) — quel filtro esclude i
  // contratti noleggio-lungo-termine dalle statistiche di Radar/Finance,
  // ma la lista Contratti deve mostrare TUTTO, NLT incluso.
  const [contrattiGlobaliSenzaFiltro, setContrattiGlobaliSenzaFiltro] = useState([]);
  const [preventiviGlobali, setPreventiviGlobali] = useState([]);
  const [nuovaVersioneDisponibile, setNuovaVersioneDisponibile] = useState(false);

  // ─── CANALE LIVE — stati_veicoli / stati_contratti ───────────────────────
  // Due nodi piccoli e dedicati (solo status/sede per veicolo, solo stato
  // per contratto — non l'intero record), con onValue permanente: sicuro
  // da tenere sempre acceso perché quando cambia qualcosa manda solo quel
  // piccolo aggiornamento, non l'intero blocco di 7MB/3.6MB. Permette a
  // Stock/Contratti/Nuovo Preventivo di mostrare stato/ubicazione sempre
  // aggiornati anche con la cache principale che dura ore.
  const [statiVeicoliLive, setStatiVeicoliLive] = useState({});
  const [statiContrattiLive, setStatiContrattiLive] = useState({});
  useEffect(() => {
    if (!user) return;
    const unsub1 = onValue(ref(db, 'stati_veicoli'), snap => {
      const dati = snap.val() || {};
      setStatiVeicoliLive(dati);
      window._statiVeicoliLive = dati;
    });
    const unsub2 = onValue(ref(db, 'stati_contratti'), snap => {
      const dati = snap.val() || {};
      setStatiContrattiLive(dati);
      window._statiContrattiLive = dati;
    });
    return () => { unsub1(); unsub2(); };
  }, [user]);
  // Applica gli stati live sopra un array di veicoli — usata ovunque un
  // elenco veicoli venga mostrato, per correggere status/sede anche se il
  // resto del veicolo arriva da una cache vecchia di ore.
  const applicaStatiLiveVeicoli = (vehicles) => {
    if (!vehicles?.length) return vehicles;
    return vehicles.map(v => {
      const live = statiVeicoliLive[v.id];
      if (!live) return v;
      return { ...v, status: live.status ?? v.status, sede: live.sede ?? v.sede, bloccoAdmin: live.bloccoAdmin || null };
    });
  };
  // Stessa cosa per i contratti: applica lo stato live sopra l'elenco.
  const applicaStatiLiveContratti = (lista) => {
    if (!lista?.length) return lista;
    return lista.map(c => {
      const live = statiContrattiLive[c.id];
      if (!live) return c;
      return { ...c, stato: live };
    });
  };

  // ─── MONITOR RICONNESSIONI (temporaneo, per diagnosi banda) ─────────────
  // Traccia ogni volta che la connessione Firebase si interrompe e si
  // riconnette (WiFi che scatta, PC in sospensione, tab in background su
  // mobile) — ogni riconnessione forza Firebase a risincronizzare per
  // intero tutti i listener attivi (contratti, preventivi = ~7MB), a
  // differenza delle scritture normali che mandano solo la patch del
  // singolo record modificato. Scrive un contatore in
  // `debug_riconnessioni/{data}` così è visibile da Firebase Console senza
  // dover controllare ogni singolo PC. RIMUOVERE dopo la diagnosi.
  useEffect(() => {
    if (!user) return;
    let primaVolta = true;
    const unsubConn = onValue(ref(db, '.info/connected'), (snap) => {
      if (primaVolta) { primaVolta = false; return; } // non contare la connessione iniziale
      if (snap.val() === true) {
        const oggi = new Date().toISOString().slice(0, 10);
        const percorso = `debug_riconnessioni/${oggi}/${user.uid || 'anon'}`;
        get(ref(db, percorso)).then(s => {
          const attuale = s.val() || 0;
          set(ref(db, percorso), attuale + 1);
        });
        console.log(`🔌 Riconnessione Firebase rilevata alle ${new Date().toLocaleTimeString('it-IT')}`);
      }
    });
    return () => unsubConn();
  }, [user]);
  // ─── FINE MONITOR RICONNESSIONI ──────────────────────────────────────────

  // Controllo versione — ogni 3 minuti, per mostrare il banner di
  // aggiornamento invece di dover chiedere l'hard refresh a mano a tutti.
  useEffect(() => {
    const controllaVersione = () => {
      get(ref(db, 'app_meta/version')).then(snap => {
        const versioneRemota = snap.val();
        if (versioneRemota && versioneRemota !== APP_VERSION) setNuovaVersioneDisponibile(true);
      }).catch(() => {});
    };
    controllaVersione();
    const interval = setInterval(controllaVersione, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Ricaricamento automatico quando disponibile una nuova versione, ma SOLO
  // se l'utente è inattivo da almeno 2 minuti (nessun click/tasto/mouse) —
  // così non perde mai lavoro in corso su un contratto/preventivo aperto.
  // Se sta lavorando, aspetta che si fermi prima di ricaricare da solo.
  // Elimina la necessità dell'hard refresh manuale PC per PC.
  //
  // Ritardo casuale aggiuntivo (0-8 minuti, diverso per ogni sessione): se
  // molte persone diventano inattive nello stesso momento (es. tutte a
  // pranzo insieme), senza questo ritardo si ricaricherebbero TUTTE nello
  // stesso istante, creando un picco di banda concentrato (ognuna riscarica
  // contratti+preventivi, ~7MB) invece di un consumo spalmato nel tempo.
  useEffect(() => {
    if (!nuovaVersioneDisponibile) return;
    let ultimaAttivita = Date.now();
    const segnaAttivita = () => { ultimaAttivita = Date.now(); };
    const eventi = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    eventi.forEach(ev => window.addEventListener(ev, segnaAttivita, { passive: true }));
    const SOGLIA_INATTIVITA_MS = 2 * 60 * 1000; // 2 minuti
    const RITARDO_CASUALE_MAX_MS = 8 * 60 * 1000; // fino a 8 minuti extra, diverso per sessione
    const ritardoCasuale = Math.random() * RITARDO_CASUALE_MAX_MS;
    const controllo = setInterval(() => {
      if (Date.now() - ultimaAttivita > SOGLIA_INATTIVITA_MS + ritardoCasuale) {
        window.location.reload();
      }
    }, 15 * 1000); // controlla ogni 15s
    return () => {
      eventi.forEach(ev => window.removeEventListener(ev, segnaAttivita));
      clearInterval(controllo);
    };
  }, [nuovaVersioneDisponibile]);

  // Nomi NLT da escludere dal CRM (in tutte le varianti possibili)
  const NLT_NOMI_CRM = ['riccardo meregalli','raffaele crispo','francesco sementa'];
  const filtraCrm = (tutti) => tutti.filter(c => {
    const ruolo = (c.venditoreRuolo||'').toLowerCase().trim();
    if (['admin_nlt','consulente_nlt'].includes(ruolo)) return false;
    const nomeCheck = (c.venditoreNome||c.venditore||'').toLowerCase().trim();
    if (NLT_NOMI_CRM.some(n => {
      const [nome, cognome] = n.split(' ');
      return nomeCheck.includes(nome) && nomeCheck.includes(cognome);
    })) return false;
    return true;
  });

  // A CHIAMATA — non più caricamento automatico all'apertura dell'app.
  // contratti/preventivi si scaricano SOLO quando l'utente apre davvero una
  // tab che ne ha bisogno (radar, stock, preventivi, contratti, backoffice,
  // finance), non appena fai login. Cache in memoria con TTL 5 minuti: se
  // cambi tab avanti e indietro entro 5 minuti, non riscarica. ATTENZIONE:
  // non è più live — se un collega salva un contratto, gli altri lo vedono
  // solo alla prossima apertura di quella tab dopo la scadenza cache, non
  // istantaneamente. Compromesso scelto esplicitamente per abbattere il
  // costo del caricamento sempre-attivo su TUTTE le sessioni per TUTTA la
  // sessione, indipendentemente da quale tab l'utente stia effettivamente
  // usando in quel momento.
  // ─── DELTA-SYNC — download completo UNA VOLTA AL GIORNO, poi solo i
  // record nuovi/modificati da allora ───────────────────────────────────
  // A differenza del TTL a scorrimento di prima (rinnovo ogni 4 ore), qui
  // il download pesante (7MB) avviene una sola volta per giorno di
  // calendario — controllato tramite la data salvata in IndexedDB, non un
  // timer. Dopo quel primo download, un controllo leggero ogni 2 minuti
  // chiede a Firebase solo "dammi i record con dataModifica più recente di
  // X" — se non c'è nulla di nuovo, la risposta è quasi vuota (pochi
  // byte). Sicuro SOLO perché ieri sera abbiamo verificato che ogni punto
  // di scrittura su contratti/preventivi aggiorna sempre dataModifica,
  // altrimenti una modifica ai campi backoffice non aggiornerebbe mai qui.
  const oggiISO = () => new Date().toISOString().slice(0, 10);

  const mergeDelta = (baseArr, nuoviArr) => {
    if (!nuoviArr.length) return baseArr;
    const mappa = new Map(baseArr.map(x => [x.id, x]));
    nuoviArr.forEach(x => mappa.set(x.id, x));
    return Array.from(mappa.values());
  };

  // Scrittura sicura verso IndexedDB — protegge dal caso in cui un'altra
  // scheda dello stesso browser (aperta con RadaR, magari rimasta indietro
  // su una versione meno aggiornata) scriva DOPO di noi e sovrascriva per
  // errore dati più recenti con dati più vecchi. Prima di scrivere, legge
  // quello che c'è già e unisce (mai sostituisce) — il cursore
  // ultimoDeltaC/P finale è sempre il più avanzato tra i due, mai quello
  // che "arriva per ultimo a scrivere".
  const idbScriviCrmSicuro = async (contratti, preventivi, ts, giorno, ultimoDeltaC, ultimoDeltaP) => {
    // Rete di sicurezza aggiuntiva: non scrivere mai un "giorno" diverso da
    // quello reale di oggi — anche se chi chiama questa funzione lo
    // passasse per errore (es. una scheda rimasta aperta durante la
    // notte, con il proprio giorno interno non aggiornato).
    if (giorno !== oggiISO()) return;
    let contrattiFinali = contratti, preventiviFinali = preventivi;
    let ultimoDeltaCFinale = ultimoDeltaC, ultimoDeltaPFinale = ultimoDeltaP;
    try {
      const esistente = await idbLeggi('radar_crm_v2');
      if (esistente && esistente.giorno === giorno) {
        if ((esistente.ultimoDeltaC || '') > ultimoDeltaC) {
          contrattiFinali = mergeDelta(contratti, esistente.contratti || []);
          ultimoDeltaCFinale = esistente.ultimoDeltaC;
        }
        if ((esistente.ultimoDeltaP || '') > ultimoDeltaP) {
          preventiviFinali = mergeDelta(preventivi, esistente.preventivi || []);
          ultimoDeltaPFinale = esistente.ultimoDeltaP;
        }
      }
    } catch (e) { /* IndexedDB non disponibile: procedi comunque con la scrittura diretta */ }
    idbScrivi('radar_crm_v2', { contratti: contrattiFinali, preventivi: preventiviFinali, ts, giorno, ultimoDeltaC: ultimoDeltaCFinale, ultimoDeltaP: ultimoDeltaPFinale });
    // Aggiorna anche lo state locale se il merge ha portato dati che questa
    // stessa scheda non aveva ancora (caso: un'altra scheda era più avanti).
    if (contrattiFinali !== contratti || preventiviFinali !== preventivi) {
      window._radarCrmContratti = contrattiFinali;
      window._radarCrmPreventivi = preventiviFinali;
      window._radarCrmUltimoDeltaC = ultimoDeltaCFinale;
      window._radarCrmUltimoDeltaP = ultimoDeltaPFinale;
      setContrattiGlobali(filtraCrm(contrattiFinali));
      setContrattiGlobaliSenzaFiltro(contrattiFinali);
      setPreventiviGlobali(preventiviFinali);
    }
  };

  // Punto di partenza per il prossimo delta-check: il valore PIÙ RECENTE di
  // dataModifica realmente presente nei dati, MAI l'orologio del client.
  // L'orologio locale è pericoloso qui: se lo calcoli DOPO che un
  // download è completato (che richiede tempo di rete), qualsiasi
  // scrittura avvenuta durante quell'attesa avrebbe un dataModifica
  // precedente al timestamp registrato, e verrebbe esclusa per sempre da
  // ogni controllo successivo — un contratto "perso" silenziosamente fino
  // al giorno dopo. Usando il dato più recente TRA quelli scaricati, il
  // punto di partenza è sempre corretto per costruzione.
  const maxDataModifica = (arr, partenza = '') =>
    arr.reduce((m, x) => (x.dataModifica && x.dataModifica > m) ? x.dataModifica : m, partenza);

  const eseguiDeltaCheck = () => {
    const cutoffC = window._radarCrmUltimoDeltaC;
    const cutoffP = window._radarCrmUltimoDeltaP;
    if (!cutoffC) return; // nessun sync completo ancora fatto, niente da fare
    Promise.all([
      get(query(ref(db, 'contratti'), orderByChild('dataModifica'), startAt(cutoffC))),
      get(query(ref(db, 'preventivi'), orderByChild('dataModifica'), startAt(cutoffP))),
    ]).then(([snapC, snapP]) => {
      const nuoviC = snapC.val() ? Object.values(snapC.val()) : [];
      const nuoviP = snapP.val() ? Object.values(snapP.val()) : [];
      // startAt (non startAfter) include anche record con dataModifica
      // identico al cutoff — può ri-scaricare qualcosa di già noto, ma
      // mai perdere un record con lo stesso timestamp di un altro. Il
      // merge sotto è comunque idempotente (dedup per id).
      if (!nuoviC.length && !nuoviP.length) return; // nessuna novità, niente da riscrivere
      const contrattiUniti = mergeDelta(window._radarCrmContratti || [], nuoviC);
      const preventiviUniti = mergeDelta(window._radarCrmPreventivi || [], nuoviP);
      window._radarCrmContratti = contrattiUniti;
      window._radarCrmPreventivi = preventiviUniti;
      window._radarCrmUltimoDeltaC = maxDataModifica(nuoviC, cutoffC);
      window._radarCrmUltimoDeltaP = maxDataModifica(nuoviP, cutoffP);
      idbScriviCrmSicuro(contrattiUniti, preventiviUniti, window._radarCrmTs, window._radarCrmGiorno, window._radarCrmUltimoDeltaC, window._radarCrmUltimoDeltaP);
      setContrattiGlobali(filtraCrm(contrattiUniti));
      setContrattiGlobaliSenzaFiltro(contrattiUniti);
      setPreventiviGlobali(preventiviUniti);
    }).catch(e => console.error('[RadaR] Delta-check contratti/preventivi fallito — probabile indice Firebase mancante su dataModifica:', e)); // visibile in console, non più silenzioso — riprova comunque al prossimo giro
  };

  const scaricaCompleto = () => {
    return Promise.all([get(ref(db, 'contratti')), get(ref(db, 'preventivi'))]).then(([snapC, snapP]) => {
      const tuttiC = snapC.val() ? Object.values(snapC.val()) : [];
      const tuttiP = snapP.val() ? Object.values(snapP.val()) : [];
      const ts = Date.now();
      const giorno = oggiISO();
      window._radarCrmContratti = tuttiC;
      window._radarCrmPreventivi = tuttiP;
      window._radarCrmTs = ts;
      window._radarCrmGiorno = giorno;
      // Punto di partenza per i delta successivi: il dato più recente
      // REALMENTE presente in questo download, non l'ora corrente — così
      // niente si perde per la latenza di rete di questa stessa chiamata.
      window._radarCrmUltimoDeltaC = maxDataModifica(tuttiC);
      window._radarCrmUltimoDeltaP = maxDataModifica(tuttiP);
      idbScriviCrmSicuro(tuttiC, tuttiP, ts, giorno, window._radarCrmUltimoDeltaC, window._radarCrmUltimoDeltaP);
      setContrattiGlobali(filtraCrm(tuttiC));
      setContrattiGlobaliSenzaFiltro(tuttiC);
      setPreventiviGlobali(tuttiP);
    });
  };

  const TAB_CHE_SERVONO_CRM = ['radar', 'stock', 'finance', 'contratti', 'preventivi'];
  useEffect(() => {
    if (!user) return;
    if (!TAB_CHE_SERVONO_CRM.includes(activeTab)) return;
    // 1. Già in memoria e scaricato OGGI (stesso giorno di calendario) —
    //    istantaneo, nessun download.
    if (window._radarCrmGiorno === oggiISO()) {
      setContrattiGlobali(filtraCrm(window._radarCrmContratti || []));
      setContrattiGlobaliSenzaFiltro(window._radarCrmContratti || []);
      setPreventiviGlobali(window._radarCrmPreventivi || []);
      return;
    }
    // 2. IndexedDB — persiste tra refresh/riavvii. Se il giorno salvato è
    //    quello di oggi, evita del tutto il download completo.
    let annullato = false;
    idbLeggi('radar_crm_v2').then(cache => {
      if (annullato) return;
      if (cache && cache.giorno === oggiISO()) {
        window._radarCrmContratti = cache.contratti;
        window._radarCrmPreventivi = cache.preventivi;
        window._radarCrmTs = cache.ts;
        window._radarCrmGiorno = cache.giorno;
        // Se la cache è di prima di questa correzione (ultimoDeltaC/P
        // assenti), li ricalcolo dai dati stessi invece di fidarmi di ts.
        window._radarCrmUltimoDeltaC = cache.ultimoDeltaC || maxDataModifica(cache.contratti || []);
        window._radarCrmUltimoDeltaP = cache.ultimoDeltaP || maxDataModifica(cache.preventivi || []);
        setContrattiGlobali(filtraCrm(cache.contratti || []));
        setContrattiGlobaliSenzaFiltro(cache.contratti || []);
        setPreventiviGlobali(cache.preventivi || []);
        return;
      }
      // 3. Nessun sync ancora fatto oggi da nessuna parte — download
      //    completo, una volta sola per questo giorno di calendario.
      scaricaCompleto();
    });
    return () => { annullato = true; };
  }, [user, activeTab]);

  // Controllo delta periodico — ogni 2 minuti, leggero (scarica solo i
  // record cambiati dall'ultimo controllo, quasi sempre vuoto). Il primo
  // controllo scatta SUBITO al caricamento/ricarica pagina (non aspetta i
  // primi 2 minuti) — protezione contro il caso in cui la cache locale
  // (memoria o IndexedDB) fosse rimasta indietro per qualsiasi motivo, es.
  // un'altra scheda dello stesso browser che ha sovrascritto per errore
  // una versione più aggiornata: così ci si riallinea subito, non dopo
  // un'attesa fino a 2 minuti.
  useEffect(() => {
    if (!user) return;
    // Ogni ciclo (e al primo avvio) controlla PRIMA se il giorno è
    // cambiato da quando questa scheda ha fatto l'ultimo sync completo —
    // se sì, una scheda rimasta aperta durante la notte farebbe altrimenti
    // un delta-check con un punto di partenza vecchio, rischiando di
    // riscrivere la cache condivisa con dati etichettati come "il giorno
    // sbagliato" e farla sembrare tornata indietro per tutti. In quel
    // caso rifà un download completo invece del delta, riallineandosi da
    // sola invece di propagare lo stato vecchio.
    const tick = () => {
      if (window._radarCrmGiorno && window._radarCrmGiorno !== oggiISO()) {
        scaricaCompleto();
      } else {
        eseguiDeltaCheck();
      }
    };
    const timeoutIniziale = setTimeout(tick, 3000); // piccolo ritardo, dà tempo al caricamento iniziale di completarsi prima
    const interval = setInterval(tick, 2 * 60 * 1000);
    return () => { clearTimeout(timeoutIniziale); clearInterval(interval); };
  }, [user]);

  // Refresh forzato — usato subito dopo che l'utente stesso salva un
  // contratto/preventivo. Usa lo stesso delta-check (leggero, scarica solo
  // il record appena modificato) invece di riscaricare tutto — dato che
  // dataModifica è stato appena impostato ad "ora" dal salvataggio stesso,
  // il delta-check lo trova sempre.
  window._radarScaricaCompletoCrm = () => scaricaCompleto();
  window._forzaRefreshContrattiPreventivi = () => {
    eseguiDeltaCheck();
    // Invalida anche la cache indipendente di Back Office — così un
    // contratto appena approvato/venduto altrove (es. da bozza) compare
    // al prossimo accesso a quella tab, non solo dopo 4 ore. Non forza un
    // refresh immediato se la tab è già aperta in quel momento (limite
    // noto, accettabile: il caso comune è cambiare tab DOPO aver salvato).
    window._radarBackofficeTs = 0;
  };
  // Rimozione locale immediata dopo un'eliminazione — il delta-check si
  // basa su dataModifica, quindi non può MAI intercettare una
  // cancellazione (un record cancellato semplicemente sparisce, non
  // lascia traccia da cercare). Va tolto subito dallo stato locale di chi
  // ha appena eliminato, senza aspettare nessun ciclo di sincronizzazione.
  window._rimuoviContrattoDallaCache = (id) => {
    const aggiornati = (window._radarCrmContratti || []).filter(c => c.id !== id);
    window._radarCrmContratti = aggiornati;
    setContrattiGlobali(filtraCrm(aggiornati));
    setContrattiGlobaliSenzaFiltro(aggiornati);
  };
  window._rimuoviPreventivoDallaCache = (id) => {
    const aggiornati = (window._radarCrmPreventivi || []).filter(p => p.id !== id);
    window._radarCrmPreventivi = aggiornati;
    setPreventiviGlobali(aggiornati);
  };
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark';
    localStorage.setItem('radar_theme', next);
    return next;
  });
  const T = THEMES[theme];

  // Ascolta stato autenticazione Firebase
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const snap = await get(ref(db, `utenti/${firebaseUser.uid}`));
        if (snap.exists()) {
          const val = snap.val();
          // Normalizza i campi — Firebase può avere Cognome/cognome, Ruolo/ruolo etc.
          const normalized = {
            nome: val.nome || val.Nome || '',
            cognome: val.cognome || val.Cognome || '',
            ruolo: val.ruolo || val.Ruolo || '',
            email: val.email || val.Email || '',
            permessi: val.permessi || val.Permessi || [],
            sede: (val.sede || val.Sede || '').toLowerCase().trim(),
          };
          setUser({ uid: firebaseUser.uid, email: firebaseUser.email, ...normalized });
          window._radarRuoloUtente = (normalized.ruolo || '').toLowerCase().trim();
          window._radarEmailUtente = String(firebaseUser.email || '').toLowerCase().trim();
          const esente = MFA_ESENTI.includes(String(firebaseUser.email || '').toLowerCase());
          setMfaDaConfigurare(MFA_OBBLIGATORIA && !esente && multiFactor(firebaseUser).enrolledFactors.length === 0);
          // I partner e gli osservatori non devono mai vedere/atterrare sul tab RadaR
          if (['partner', 'osservatore'].includes((normalized.ruolo || '').toLowerCase().trim())) {
            setActiveTab('stock');
          }
          // Il ruolo service atterra direttamente su Service, non sulla
          // Dashboard (che mostra dati aggregati di tutte le sedi).
          if ((normalized.ruolo || '').toLowerCase().trim() === 'service') {
            setActiveTab('service');
          }
        } else {
          // Utente auth ma senza ruolo in DB — logout
          await signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  // Sessione unica: ascolta sessioni_attive/{uid} (pochi byte) e disconnette
  // questo dispositivo se l'utente entra da un altro
  useEffect(() => {
    const fbUser = auth.currentUser;
    if (!user || !fbUser || SESSIONE_UNICA_ESENTI.includes(String(fbUser.email || '').toLowerCase())) return;
    // Sessione aperta prima di questa funzione: questo dispositivo la prende
    if (!leggiSessioneLocale(fbUser.uid)) prendiSessione(fbUser);
    const unsub = onValue(ref(db, `sessioni_attive/${fbUser.uid}`), snap => {
      const v = snap.val();
      const mia = leggiSessioneLocale(fbUser.uid);   // letta ogni volta: può cambiare durante il login
      // Si esce solo se un ALTRO dispositivo ha preso la sessione DOPO di noi
      if (!v?.id || !mia || v.id === mia.id || String(v.at) <= String(mia.at)) return;
      try { localStorage.removeItem(chiaveSessioneLocale(fbUser.uid)); } catch (e) { /* ok */ }
      setLoginError(`Sei stato disconnesso: il tuo account è stato usato per entrare da un altro dispositivo (${v.dispositivo || 'sconosciuto'}).`);
      signOut(auth);
    }, () => { /* regole non aggiornate: nessun controllo */ });
    return () => unsub();
  }, [user?.uid]);

  // Redirect automatico per ruolo meccanico → service/marcatempo

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    const email = loginCreds.user.includes('@')
      ? loginCreds.user
      : `${loginCreds.user.toLowerCase()}@radargas.it`;
    try {
      const cred = await signInWithEmailAndPassword(auth, email, loginCreds.pass);
      await prendiSessione(cred.user);
      registraAccesso(cred.user, 'password');
    } catch (err) {
      if (err?.code === 'auth/multi-factor-auth-required') {
        // Password corretta: ora serve il codice dell'app Authenticator
        setMfaResolver(getMultiFactorResolver(auth, err));
        setMfaCodice(''); setMfaErrore('');
      } else {
        setLoginError('Credenziali errate.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Verifica del codice a 6 cifre al login
  const verificaCodiceMfa = async (e) => {
    e.preventDefault();
    setMfaErrore(''); setLoginLoading(true);
    try {
      const hint = mfaResolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) || mfaResolver.hints[0];
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCodice.replace(/\s/g, ''));
      const cred = await mfaResolver.resolveSignIn(assertion);
      await prendiSessione(cred.user);
      registraAccesso(cred.user, 'password + Authenticator');
      setMfaResolver(null); setMfaCodice('');
    } catch (err) {
      setMfaErrore(err?.code === 'auth/invalid-verification-code' || err?.code === 'auth/totp-challenge-timeout' ? 'Codice non valido o scaduto: inserisci quello attuale.' : 'Verifica non riuscita: ' + (err?.message || err));
    } finally { setLoginLoading(false); }
  };

  // Primo accesso: genera il codice QR da inquadrare con l'app Authenticator
  const avviaConfigurazioneMfa = async () => {
    setMfaErrore('');
    try {
      const sessione = await multiFactor(auth.currentUser).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(sessione);
      const url = secret.generateQrCodeUrl(auth.currentUser.email, 'RadaR GAS');
      const qr = await QRCode.toDataURL(url, { margin: 1, width: 220 });
      setMfaSegreto({ secret, qr, chiave: secret.secretKey });
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        setMfaErrore('Per sicurezza esci e rientra, poi configura subito il secondo fattore.');
      } else if (/operation-not-allowed|admin-restricted|not-enabled|configuration-not-found|unverified-email/i.test(String(err?.code || err?.message))) {
        // 2FA non attivabile per questo account (2FA non abilitato sul progetto
        // o email non verificata): non si blocca l'accesso, si riproverà al
        // prossimo login una volta sistemato da un amministratore
        // 2FA non ancora abilitato sul progetto Firebase: non si blocca
        // l'accesso a nessuno, la configurazione verrà chiesta dopo l'attivazione
        console.warn('[RadaR] 2FA non abilitato sul progetto: accesso consentito senza secondo fattore.');
        setMfaDaConfigurare(false);
      } else {
        setMfaErrore('Impossibile avviare la configurazione: ' + (err?.message || err));
      }
    }
  };
  const confermaConfigurazioneMfa = async (e) => {
    e.preventDefault();
    setMfaErrore(''); setLoginLoading(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(mfaSegreto.secret, mfaCodice.replace(/\s/g, ''));
      await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator');
      setMfaDaConfigurare(false); setMfaSegreto(null); setMfaCodice('');
    } catch (err) {
      setMfaErrore(err?.code === 'auth/invalid-verification-code' ? 'Codice non valido: controlla l\'orario del telefono e inserisci quello attuale.' : 'Attivazione non riuscita: ' + (err?.message || err));
    } finally { setLoginLoading(false); }
  };

  const handleLogout = () => {
    try { if (auth.currentUser) localStorage.removeItem(chiaveSessioneLocale(auth.currentUser.uid)); } catch (e) { /* ok */ }
    window._radarStockCache = null;
    window._radarStockTs = null;
    window._radarDataCache = null;
    window._radarRicambiCache = null;
    window._radarErogatoCache = null;
    signOut(auth);
  };

  // Schermata di caricamento iniziale
  if (authLoading) return (
    <div style={{ background: '#0a0c10', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <img src="/radar-logo.png" alt="RadaR" style={{ width: '180px', opacity: 0.92 }} />
        <div style={{ fontSize: '11px', color: '#475569', letterSpacing: '2px', textTransform: 'uppercase' }}>Connessione in corso...</div>
        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6',
              animation: 'pulse 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
              opacity: 0.7
            }} />
          ))}
        </div>
        <style>{`@keyframes pulse { 0%,100%{transform:scale(0.7);opacity:0.4} 50%{transform:scale(1);opacity:1} }`}</style>
      </div>
    </div>
  );

  // ── Schermate dell'autenticazione a due fattori ──
  const schermataMfa = (contenuto) => (
    <ThemeContext.Provider value={theme}>
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ background: T.surface, padding: '36px 28px', borderRadius: '28px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '400px', textAlign: 'center', boxSizing: 'border-box' }}>
        <img src="/radar-logo.png" alt="RadaR" style={{ width: '120px', margin: '0 auto 14px', display: 'block' }} />
        {contenuto}
        {mfaErrore && <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '700', marginTop: '12px' }}>{mfaErrore}</div>}
      </div>
    </div>
    </ThemeContext.Provider>
  );
  const campoCodice = (
    <input inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="000000" autoFocus
      value={mfaCodice} onChange={e => setMfaCodice(e.target.value.replace(/[^0-9 ]/g, ''))}
      style={{ width: '100%', padding: '15px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '14px', color: T.text, fontSize: '24px', letterSpacing: '8px', textAlign: 'center', outline: 'none', boxSizing: 'border-box', margin: '14px 0' }} />
  );
  const bottonePrimario = testo => (
    <button type="submit" disabled={loginLoading || mfaCodice.replace(/\s/g, '').length !== 6}
      style={{ width: '100%', padding: '15px', background: (loginLoading || mfaCodice.replace(/\s/g, '').length !== 6) ? T.border : '#3b82f6', color: '#fff', border: 'none', borderRadius: '14px', fontWeight: '900', cursor: 'pointer', fontSize: '14px' }}>
      {loginLoading ? 'Verifica…' : testo}
    </button>
  );

  // Login: password corretta, ora il codice
  if (!user && mfaResolver) return schermataMfa(
    <form onSubmit={verificaCodiceMfa}>
      <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>Verifica in due passaggi</div>
      <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '6px' }}>Apri l'app Authenticator sul telefono e inserisci il codice a 6 cifre di RadaR GAS.</div>
      {campoCodice}
      {bottonePrimario('VERIFICA')}
      <button type="button" onClick={() => { setMfaResolver(null); setMfaCodice(''); setMfaErrore(''); }}
        style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: '12px', cursor: 'pointer', marginTop: '12px' }}>Annulla</button>
    </form>
  );

  // Primo accesso senza secondo fattore: configurazione obbligatoria
  if (user && mfaDaConfigurare) return schermataMfa(
    <div>
      <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>Attiva la verifica in due passaggi</div>
      <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '6px', lineHeight: 1.5 }}>
        Per entrare in RadaR serve un codice dall'app <b>Google Authenticator</b> o <b>Microsoft Authenticator</b> sul tuo telefono. Si configura una volta sola.
      </div>
      {!mfaSegreto ? (
        <>
          <button onClick={avviaConfigurazioneMfa}
            style={{ width: '100%', padding: '15px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '14px', fontWeight: '900', cursor: 'pointer', fontSize: '14px', marginTop: '18px' }}>
            CONFIGURA ORA
          </button>
          <button onClick={() => { setMfaErrore(''); signOut(auth); }}
            style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: '12px', cursor: 'pointer', marginTop: '12px' }}>Esci</button>
        </>
      ) : (
        <form onSubmit={confermaConfigurazioneMfa}>
          <ol style={{ textAlign: 'left', fontSize: '12px', color: T.textSub || T.textMuted, paddingLeft: '18px', lineHeight: 1.6, margin: '14px 0 10px' }}>
            <li>Apri l'app Authenticator e tocca <b>+</b> per aggiungere un account.</li>
            <li>Inquadra il codice QR qui sotto.</li>
            <li>Scrivi il codice a 6 cifre che compare nell'app.</li>
          </ol>
          <img src={mfaSegreto.qr} alt="Codice QR per Authenticator" style={{ width: '200px', height: '200px', borderRadius: '12px', background: '#fff', padding: '6px' }} />
          <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '8px' }}>Non riesci a inquadrarlo? Inserisci a mano questa chiave:</div>
          <div style={{ fontSize: '13px', fontFamily: 'monospace', color: T.text, wordBreak: 'break-all', marginTop: '4px', userSelect: 'all' }}>{mfaSegreto.chiave}</div>
          {campoCodice}
          {bottonePrimario('ATTIVA')}
        </form>
      )}
    </div>
  );

  if (!user) return (
    <ThemeContext.Provider value={theme}>
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '20px', boxSizing: 'border-box' }}>
      <form onSubmit={handleLogin} style={{ background: T.surface, padding: '40px 32px', borderRadius: '40px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '380px', textAlign: 'center', boxSizing: 'border-box' }}>
        <img src={theme === 'light' ? '/logo-blue.png' : '/logo.png'} alt="Gruppo Autoscala" style={{ width: '160px', marginBottom: '20px' }} />
        <img src="/radar-logo.png" alt="RadaR" style={{ width: '140px', margin: '0 auto 8px', display: 'block' }} />
        <p style={{ color: '#3b82f6', marginBottom: '28px', fontSize: '11px', fontWeight: '900', letterSpacing: '3px' }}>GAS INTELLIGENCE CLOUD</p>
        <input
          type="text" placeholder="nome.cognome"
          value={loginCreds.user} onChange={e => setLoginCreds({...loginCreds, user: e.target.value})}
          style={{ width: '100%', padding: '15px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '14px', color: T.text, marginBottom: '12px', outline: 'none', fontSize: '16px', boxSizing: 'border-box' }}
        />
        <input
          type="password" placeholder="password"
          value={loginCreds.pass} onChange={e => setLoginCreds({...loginCreds, pass: e.target.value})}
          style={{ width: '100%', padding: '15px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '14px', color: T.text, marginBottom: loginError ? '10px' : '24px', outline: 'none', fontSize: '16px', boxSizing: 'border-box' }}
        />
        {loginError && <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '700', marginBottom: '16px' }}>{loginError}</div>}
        <button type="submit" disabled={loginLoading} style={{ width: '100%', padding: '16px', background: loginLoading ? T.border : '#3b82f6', color: '#fff', border: 'none', borderRadius: '14px', fontWeight: '900', cursor: loginLoading ? 'default' : 'pointer', fontSize: '15px' }}>
          {loginLoading ? 'Accesso...' : 'ACCEDI'}
        </button>
        <button type="button" onClick={() => setPrivacyOpen(true)} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: '11px', fontWeight: '700', cursor: 'pointer', marginTop: '18px', textDecoration: 'underline' }}>
          🔒 Informativa Privacy
        </button>
      </form>
      {privacyOpen && <PrivacyInfoModal T={T} onClose={() => setPrivacyOpen(false)} />}
    </div>
    </ThemeContext.Provider>
  );

  // Utente autenticato ma con ruolo "disattivato" — persona uscita
  // dall'azienda: l'account Firebase resta attivo (storico contratti/ODL
  // intatto), ma non deve vedere né usare nulla dell'app.
  if ((user.ruolo || '').toLowerCase().trim() === 'disattivato') return (
    <ThemeContext.Provider value={theme}>
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ background: T.surface, padding: '48px 36px', borderRadius: '28px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '420px', textAlign: 'center', boxSizing: 'border-box' }}>
        <div style={{ fontSize: '44px', marginBottom: '16px' }}>🚫</div>
        <div style={{ fontSize: '16px', fontWeight: '900', color: '#ef4444', letterSpacing: '1px', marginBottom: '10px' }}>
          UTENTE NON PIÙ ESISTENTE IN RADAR
        </div>
        <div style={{ fontSize: '13px', color: T.textMuted, marginBottom: '28px' }}>
          Il tuo accesso è stato disattivato. Contatta un amministratore se pensi si tratti di un errore.
        </div>
        <button onClick={() => signOut(auth)} style={{ padding: '14px 28px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', fontSize: '14px' }}>
          Esci
        </button>
      </div>
    </div>
    </ThemeContext.Provider>
  );

  return (
    <ThemeContext.Provider value={theme}>
    <div style={{ background: T.bg, minHeight: '100vh', color: T.text, fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontSize: '14px', lineHeight: '1.5' }}>
      {nuovaVersioneDisponibile && (
        <div onClick={() => window.location.reload()} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 5000, background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '10px 16px', textAlign: 'center', fontWeight: '900', fontSize: '13px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
          🔄 Nuova versione di RadaR disponibile — clicca per aggiornare subito, o si aggiornerà da sola appena sei inattivo
        </div>
      )}
      <NavBar T={T} theme={theme} toggleTheme={toggleTheme} activeTab={activeTab} setActiveTab={setActiveTab} user={user} onLogout={handleLogout} collapsed={sidebarCollapsed} onToggleCollapsed={toggleSidebarCollapsed} />
      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH}px`,
        paddingBottom: isMobile ? `${BOTTOM_BAR_HEIGHT}px` : 0,
        minHeight: '100vh',
        transition: 'margin-left 0.18s ease',
      }}>
        {activeTab === 'radar' && !['partner', 'osservatore'].includes((user?.ruolo || '').toLowerCase().trim()) && <RadarView user={user} contratti={contrattiGlobali} preventiviGlobali={preventiviGlobali} />}
        {activeTab === 'stock'      && <StockView      user={user} preventivi={preventiviGlobali} contratti={contrattiGlobali} statiVeicoliLive={statiVeicoliLive} />}
        {activeTab === 'preventivi' && <PreventiviView user={user} statiVeicoliLive={statiVeicoliLive} preventiviGlobaliRaw={preventiviGlobali} />}
        {activeTab === 'contratti'  && <ContrattiView  user={user} statiContrattiLive={statiContrattiLive} contrattiGlobaliRaw={contrattiGlobaliSenzaFiltro} />}
        {activeTab === 'backoffice' && <BackOfficeView  user={user} statiContrattiLive={statiContrattiLive} />}
        {activeTab === 'finance'    && <FinanceView     user={user} contratti={contrattiGlobali} />}
        {activeTab === 'service'    && <ServiceView     user={user} />}
        {activeTab === 'noleggio'   && <NoleggioView   user={user} db={db} />}
        {activeTab === 'logistica'  && <LogisticaView  user={user} db={db} />}
        {activeTab === 'analisi'    && puoVedereAnalisi(user) && <AnalisiView user={user} contratti={contrattiGlobali} preventivi={preventiviGlobali} />}
      </div>
    </div>
    </ThemeContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB RADAR (invariato)
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// CONVERSIONE VIEW
// ═══════════════════════════════════════════════════════════════════════════════

// Unica fonte: le liste in cima al file (prima qui erano duplicate e
// disallineate — mancavano Pasquale Del Villano e Simone Bruno)
const SEDE_NOLA    = NOLA_CONSULENTI;
const SEDE_SALERNO = SALERNO_CONSULENTI;
const RUOLI_CONSULENTI_CRM = ['consulente','brand_manager_se','bdc'];

function ConversioneView({ user, deals, T, isMobile }) {
  const fmt1 = v => v.toFixed(1);
  const [fAnno, setFAnno] = useState('');
  const [fMese, setFMese] = useState('');
  const [fConsulente, setFConsulente] = useState('');
  const [selectedConsulenti, setSelectedConsulenti] = useState(new Set()); // set vuoto = tutti

  const toggleConsulente = (nome) => {
    setSelectedConsulenti(prev => {
      const next = new Set(prev);
      if (next.has(nome)) next.delete(nome);
      else next.add(nome);
      return next;
    });
  };

  const resetFlagConsulenti = () => setSelectedConsulenti(new Set());

  const safeDeals = deals || [];

  // Anni/mesi disponibili dai deals
  const anni = useMemo(()=>{
    const s=new Set();
    safeDeals.forEach(d=>{ const m=d.meseAnno||d.meseAnnoChiusura||''; const y=m.split('/')[1]; if(y) s.add(y); });
    return [...s].sort((a,b)=>b-a);
  },[safeDeals]);

  const mesi = useMemo(()=>{
    const s=new Set();
    safeDeals.forEach(d=>{ const m=d.meseAnno||d.meseAnnoChiusura||''; if(m&&(!fAnno||m.endsWith(fAnno))) s.add(m); });
    return [...s].sort((a,b)=>{ const [ma,ya]=a.split('/'); const [mb,yb]=b.split('/'); return ya!==yb?yb-ya:mb-ma; });
  },[safeDeals,fAnno]);

  // Filtra deals per anno/mese
  const dealsFilt = useMemo(()=>{
    return safeDeals.filter(d=>{
      const mese = d.meseAnno||d.meseAnnoChiusura||'';
      if(fAnno && !mese.endsWith(fAnno)) return false;
      if(fMese && mese!==fMese) return false;
      if(fConsulente && (d.venditore||'').trim() !== fConsulente) return false;
      if(selectedConsulenti.size > 0 && !selectedConsulenti.has((d.venditore||'').trim())) return false;
      return true;
    });
  },[safeDeals,fAnno,fMese,fConsulente,selectedConsulenti]);

  const getSede = nome => {
    const n = (nome||'').toLowerCase().trim();
    if (SEDE_NOLA.includes(n))    return 'Nola';
    if (SEDE_SALERNO.includes(n)) return 'Salerno';
    return 'Agnano';
  };

  // Aggrega per venditore — conta clienti unici (non preventivi)
  const consulentiMap = useMemo(()=>{
    const map={};
    dealsFilt.forEach(d=>{
      const nome=(d.venditore||'').trim();
      if(!nome) return;
      if(!map[nome]) map[nome]={ nome, sede:getSede(nome), clientiSet:new Set(), clientiChiusiSet:new Set() };
      // Chiave cliente univoca: nome cliente normalizzato
      const clienteKey = (d.cliente||'').trim().toLowerCase();
      if(clienteKey) map[nome].clientiSet.add(clienteKey);
      if((d.stato==='Chiusa'||d.stato==='chiusa'||d.stato==='CHIUSA') && clienteKey) {
        map[nome].clientiChiusiSet.add(clienteKey);
      }
    });
    return Object.values(map).map(v=>({
      nome: v.nome,
      sede: v.sede,
      preventivi: v.clientiSet.size,
      chiusi: v.clientiChiusiSet.size,
    })).sort((a,b)=>{
      const pA=a.preventivi>0?(a.chiusi/a.preventivi)*100:0;
      const pB=b.preventivi>0?(b.chiusi/b.preventivi)*100:0;
      return pB-pA;
    });
  },[dealsFilt]);

  // Totali per sede
  const totSede = (sede) => {
    const list=consulentiMap.filter(c=>c.sede===sede);
    const prev=list.reduce((s,c)=>s+c.preventivi,0);
    const chius=list.reduce((s,c)=>s+c.chiusi,0);
    return { prev, chius, pct:prev>0?((chius/prev)*100):0 };
  };

  const PctBar = ({pct,color='#6366f1'}) => (
    <div style={{width:'100%',height:'6px',background:T.bg,borderRadius:'3px',overflow:'hidden',marginTop:'4px'}}>
      <div style={{width:`${Math.min(pct,100)}%`,height:'100%',background:color,borderRadius:'3px',transition:'width 0.6s'}}/>
    </div>
  );
  const pctColor = pct => pct>=40?'#10b981':pct>=20?'#f59e0b':'#ef4444';

  const consulenti = useMemo(()=>{
    const s=new Set();
    safeDeals.forEach(d=>{ if(d.venditore?.trim()) s.add(d.venditore.trim()); });
    return [...s].sort();
  },[safeDeals]);

  if(!safeDeals.length) return (
    <div style={{textAlign:'center',padding:'60px',color:T.textMuted}}>
      <div style={{fontSize:'36px',marginBottom:'10px'}}>📊</div>
      <div style={{fontSize:'13px',fontWeight:'700'}}>Carica un file CSV nella dashboard per vedere i dati di conversione</div>
    </div>
  );

  return (
    <div>
      {/* Filtri */}
      <div style={{display:'flex',gap:'10px',marginBottom:'20px',flexWrap:'wrap',alignItems:'center'}}>
        <select value={fAnno} onChange={e=>{setFAnno(e.target.value);setFMese('');}} style={{background:T.inputBg,border:`1px solid ${fAnno?'#6366f1':T.border2}`,borderRadius:'10px',padding:'8px 12px',color:T.text,fontSize:'12px',outline:'none'}}>
          <option value="">Tutti gli anni</option>
          {anni.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <select value={fMese} onChange={e=>setFMese(e.target.value)} style={{background:T.inputBg,border:`1px solid ${fMese?'#6366f1':T.border2}`,borderRadius:'10px',padding:'8px 12px',color:T.text,fontSize:'12px',outline:'none'}}>
          <option value="">Tutti i mesi</option>
          {mesi.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
        <select value={fConsulente} onChange={e=>setFConsulente(e.target.value)} style={{background:T.inputBg,border:`1px solid ${fConsulente?'#6366f1':T.border2}`,borderRadius:'10px',padding:'8px 12px',color:T.text,fontSize:'12px',outline:'none'}}>
          <option value="">Tutti i consulenti</option>
          {consulenti.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{fontSize:'11px',color:T.textMuted,marginLeft:'4px'}}>{dealsFilt.length} trattative nel periodo</div>
        {(fAnno||fMese||fConsulente)&&<button onClick={()=>{setFAnno('');setFMese('');setFConsulente('');}} style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'8px',padding:'7px 12px',color:'#ef4444',cursor:'pointer',fontSize:'10px',fontWeight:'700'}}>✕ Reset filtri</button>}
        {selectedConsulenti.size > 0 && <button onClick={resetFlagConsulenti} style={{background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:'8px',padding:'7px 12px',color:'#6366f1',cursor:'pointer',fontSize:'10px',fontWeight:'700'}}>☐ Deseleziona ({selectedConsulenti.size})</button>}
      </div>

      {/* Totali per sede */}
      <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'14px',marginBottom:'24px'}}>
        {['Agnano','Nola','Salerno'].map(sede=>{
          const t=totSede(sede);
          return (
            <div key={sede} style={{background:T.surface,borderRadius:'16px',border:`2px solid ${pctColor(t.pct)}30`,padding:'18px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                <div style={{fontSize:'13px',fontWeight:'900',color:T.text}}>📍 {sede}</div>
                <div style={{fontSize:'28px',fontWeight:'900',color:pctColor(t.pct)}}>{fmt1(t.pct)}%</div>
              </div>
              <PctBar pct={t.pct} color={pctColor(t.pct)}/>
              <div style={{display:'flex',gap:'16px',marginTop:'8px',fontSize:'10px',color:T.textMuted}}>
                <span>👤 {t.prev} clienti</span>
                <span style={{color:'#10b981'}}>✅ {t.chius} chiuse</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabella per sede */}
      {['Agnano','Nola','Salerno'].map(sede=>{
        const list=consulentiMap.filter(c=>c.sede===sede);
        if(!list.length) return null;
        return (
          <div key={sede} style={{marginBottom:'24px'}}>
            <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'10px'}}>📍 {sede.toUpperCase()}</div>
            <div style={{background:T.surface,borderRadius:'16px',border:`1px solid ${T.border}`,overflow:'hidden'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 90px 90px 110px',padding:'8px 16px',background:T.bg,borderBottom:`1px solid ${T.border}`}}>
                {['CONSULENTE','CLIENTI','CHIUSE','CONV. %'].map(h=><div key={h} style={{fontSize:'9px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px'}}>{h}</div>)}
              </div>
              {list.map((c,i)=>{
                const pct=c.preventivi>0?(c.chiusi/c.preventivi)*100:0;
                const col=pctColor(pct);
                const isSelected = selectedConsulenti.has(c.nome);
                const isFiltered = selectedConsulenti.size > 0;
                return (
                  <div key={c.nome} onClick={()=>toggleConsulente(c.nome)}
                    style={{display:'grid',gridTemplateColumns:'1fr 90px 90px 110px',padding:'12px 16px',borderBottom:i<list.length-1?`1px solid ${T.border}`:'none',
                      background: isFiltered ? (isSelected ? 'rgba(99,102,241,0.08)' : T.bg+'80') : (i%2===0?'transparent':T.bg+'80'),
                      opacity: isFiltered && !isSelected ? 0.4 : 1,
                      cursor:'pointer', alignItems:'center', transition:'all 0.15s'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                      <div style={{width:'15px',height:'15px',borderRadius:'4px',border:`2px solid ${isSelected?'#6366f1':T.border}`,background:isSelected?'#6366f1':'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}>
                        {isSelected && <span style={{color:'#fff',fontSize:'9px',fontWeight:'900',lineHeight:1}}>✓</span>}
                      </div>
                      <span style={{fontSize:'12px',fontWeight:'700',color:T.text}}>{c.nome}</span>
                    </div>
                    <div style={{fontSize:'12px',color:T.textMuted}}>{c.preventivi}</div>
                    <div style={{fontSize:'12px',color:'#10b981',fontWeight:'700'}}>{c.chiusi}</div>
                    <div>
                      <div style={{fontSize:'15px',fontWeight:'900',color:col}}>{fmt1(pct)}%</div>
                      <PctBar pct={pct} color={col}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROVVIGIONI VIEW
// ═══════════════════════════════════════════════════════════════════════════════
const SENIOR_CONSULENTI = ['roberto.castaldo','marco.castaldo','enrico.palumbo','luca.ambrosino'];
const BASE_JUNIOR = 0;

const FASCE_PREZZO = [
  { min: 5000,  max: 17500, junior: 25,  senior: 50  },
  { min: 17501, max: 27500, junior: 50,  senior: 75  },
  { min: 27501, max: 40000, junior: 75,  senior: 100 },
  { min: 40001, max: Infinity, junior: 100, senior: 100 },
];

// Junior aggiornati al piano provvigioni di ottobre 2026 (8,95% → 40 €, 9,95% → 60 €)
const TASSI_FIN = { '6.95': {j:0,s:0}, '7.95': {j:20,s:20}, '7.99': {j:20,s:20}, '8.95': {j:40,s:40}, '9.95': {j:60,s:60} };
// Mappatura codici → tasso
const CODICI_TASSO = { '8ET':'7.95', '89T':'7.95', '883':'6.95', 'X8X':'8.95', 'XCN':'9.95' };

function normalizeTasso(val) {
  if (!val) return null;
  const v = String(val).trim().toUpperCase();
  if (CODICI_TASSO[v]) return CODICI_TASSO[v];
  // Prova a parsare come numero (es. "7.95", "8,95")
  const n = parseFloat(v.replace(',','.'));
  if (!isNaN(n)) return n.toFixed(2);
  return null;
}
const ASS = { 'PP': {j:30,s:30}, 'FIR': {j:10,s:10}, 'GAP': {j:10,s:10} };

function normalizeAss(sv, allText) {
  const txt = (allText||'').toString().toUpperCase();
  // Rimuove separatori per match compatto (F.I.R → FIR, F/I/R → FIR)
  const txtStrip = txt.replace(/[.\s/\-]/g,'');

  const matchFIR = txtStrip.includes('FIR') || /\bF\.?I\.?R\.?\b/.test(txt);
  const matchPP  = /\bPP\b/.test(txt) || txtStrip.includes('PROTEZIONEPAGAMENTI') || txtStrip.includes('PROTPAGAM');
  const matchGAP = /\bGAP\b/.test(txt) || txtStrip.includes('GARANTEEDPRICE');

  return {
    pp:  sv.assPP  || matchPP,
    fir: sv.assFIR || matchFIR,
    gap: sv.assGAP || matchGAP,
  };
}

function buildAllText(contratto) {
  const sv = contratto.servizi || {};
  const pr = contratto.prezzo || {};
  const de = contratto.datiExtra || {};
  // Raccoglie TUTTI i valori stringa da ogni oggetto del contratto
  const extractStrings = (obj) => Object.values(obj || {}).filter(v => typeof v === 'string' && v.length > 1);
  const parts = [
    ...extractStrings(sv),
    ...extractStrings(pr),
    ...extractStrings(de),
    contratto.note, contratto.noteInterne, contratto.descrizione,
    contratto.noteBackoffice, contratto.notaConsulente,
    // Campi specifici dove i consulenti scrivono pacchetti
    sv.altroAntifurto, sv.altroAccessorio, sv.altroInstallazione,
    sv.altroServizio, sv.pacchetto, sv.descrizioneServizi,
    pr.note, pr.descrizionePacchetto,
  ];
  return parts.filter(Boolean).join(' ');
}
// Junior aggiornati al piano provvigioni di ottobre 2026 (24 → 20 €, 36 → 30 €, 48 → 40 €, 60 → 50 €)
const GARANZIE = { 12:{j:0,s:0}, 24:{j:20,s:20}, 36:{j:30,s:30}, 48:{j:40,s:40}, 60:{j:50,s:50} };
// Pack manutenzione: stesso importo per junior e senior
const PACK_MANUT = { '3': 20, '4': 25 };
const ACC = { lojack:{j:100,s:100,min:1300}, blockshaft:{j:40,s:40,min:400}, n1:{j:50,s:50,min:0}, gpl:{j:50,s:50,min:1300}, retrocamera:{j:30,s:30,min:550} };

function isSenior(nomeEmail) {
  const n = (nomeEmail||'').toLowerCase().replace(/\s+/g,'.');
  return SENIOR_CONSULENTI.some(s => n.includes(s.split('.')[0]) && n.includes(s.split('.')[1]));
}

function calcolaProvvigione(contratto) {
  const pr = contratto.prezzo || {};
  const sv = contratto.servizi || {};
  const nomeV = contratto.venditoreNome || '';
  const senior = isSenior(nomeV);
  const t = senior ? 's' : 'j';
  let tot = 0;
  const dettagli = [];

  // Testo completo del contratto per scansione
  const allText = buildAllText(contratto);
  const txt = allText.toUpperCase().replace(/\./g,'');

  // Prezzo vettura
  const prezzo = parseFloat(pr.prezzoConcordato || pr.prezzoListino || 0);
  const fascia = FASCE_PREZZO.find(f => prezzo >= f.min && prezzo <= f.max);
  if (fascia) {
    const v = fascia[t === 's' ? 'senior' : 'junior'];
    tot += v;
    dettagli.push({ voce: `Vettura (${new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(prezzo)})`, importo: v });
  }

  // Finanziamento
  if (pr.hasFinanziamento) {
    const rawTasso = pr.tassoFinanziamento || pr.tabellaFinanziamento || '';
    const tasso = normalizeTasso(rawTasso);
    if (tasso && TASSI_FIN[tasso]) {
      const v = TASSI_FIN[tasso][t];
      tot += v;
      if (v > 0) dettagli.push({ voce: `Finanziamento ${tasso}%${rawTasso !== tasso ? ` (${rawTasso})` : ''}`, importo: v });
    } else if (TASSI_FIN['7.99']) {
      // Tasso non specificato → applica default 7.99%
      const v = TASSI_FIN['7.99'][t];
      tot += v;
      if (v > 0) dettagli.push({ voce: 'Finanziamento 7.99% (default)', importo: v });
    }
  }

  // Assicurazioni — cerca nei campi dedicati e nel testo libero
  const ass = normalizeAss(sv, allText);
  if (ass.pp)  { const v = ASS['PP'][t];  tot += v; if (v > 0) dettagli.push({ voce: 'Assicurazione PP',  importo: v }); }
  if (ass.fir) { const v = ASS['FIR'][t]; tot += v; if (v > 0) dettagli.push({ voce: 'Assicurazione FIR', importo: v }); }
  if (ass.gap) { const v = ASS['GAP'][t]; tot += v; if (v > 0) dettagli.push({ voce: 'Assicurazione GAP', importo: v }); }

  // Garanzia estesa — SOLO dal campo strutturato garanziaEstesaMesi (quello
  // impostato dalla tendina mesi nel preventivo/contratto). Prima si cercava
  // anche testo libero tipo "XX MESI" ovunque nel contratto, ma questo
  // pattern troppo generico agganciava per errore altre voci non collegate
  // a Mawdy (es. "Tagliando 48 mesi"), causando provvigioni sbagliate.
  // L'unico fallback rimasto è per i contratti storici che hanno solo il
  // vecchio flag booleono mawdy36 senza il campo mesi (sempre 36 in quel
  // caso, mai dedotto da testo libero).
  let mesiGar = parseInt(sv.garanziaEstesaMesi || 0);
  if (!mesiGar && (sv.mawdy36 || sv.mawdy)) mesiGar = 36;
  if (mesiGar && GARANZIE[mesiGar]) {
    const v = GARANZIE[mesiGar][t];
    tot += v;
    if (v > 0) dettagli.push({ voce: `Garanzia Mawdy ${mesiGar} mesi`, importo: v });
  }

  // Accessori — controlla sia flag che testo libero
  const hasLojack     = sv.lojack     || /\bLOJACK\b/.test(txt);
  const hasBlockshaft = sv.blockshaft || /\bBLOCKSHAFT\b/.test(txt);
  const hasN1         = sv.n1         || /\bN1\b/.test(txt) || /\bN\.1\b/.test(allText.toUpperCase());
  const hasGpl        = sv.gpl        || /\bGPL\b/.test(txt);
  const hasRC         = sv.retrocamera|| /RETROCAMERA/.test(txt);

  const costoLojack = parseFloat(sv.costoLojack || 0);
  if (hasLojack)
    { tot += ACC.lojack[t]; dettagli.push({ voce: 'LoJack', importo: ACC.lojack[t] }); }
  const costoBS = parseFloat(sv.costoBlockshaft || 0);
  if (hasBlockshaft)
    { tot += ACC.blockshaft[t]; dettagli.push({ voce: 'Blockshaft', importo: ACC.blockshaft[t] }); }
  if (hasN1)
    { tot += ACC.n1[t]; dettagli.push({ voce: 'N1', importo: ACC.n1[t] }); }
  const costoGpl = parseFloat(sv.costoGpl || 0);
  if (hasGpl)
    { tot += ACC.gpl[t]; dettagli.push({ voce: 'GPL', importo: ACC.gpl[t] }); }
  const costoRC = parseFloat(sv.costoRetrocamera || 0);
  if (hasRC)
    { tot += ACC.retrocamera[t]; dettagli.push({ voce: 'Retrocamera', importo: ACC.retrocamera[t] }); }

  // Pack manutenzione (voce dedicata, non il "Tagliando" singolo)
  if (sv.packManutenzione) {
    const nT = String(sv.packManutenzioneTagliandi || '');
    const v = PACK_MANUT[nT] || 0;
    if (v > 0) { tot += v; dettagli.push({ voce: `Pack manutenzione ${nT} tagliandi`, importo: v }); }
  }

  // Extra incentivo vendita permuta: va a chi VENDE l'auto ritirata in
  // permuta (mai a chi la ritira). Giorni dall'ingresso in stock della
  // permuta alla firma del contratto di vendita.
  const gp = giorniPermutaInStock(contratto);
  if (gp !== null && !senior) {
    const v = PREMIO_PERMUTA.find(f => gp <= f.max).importo;
    tot += v;
    dettagli.push({ voce: `Vendita permuta in ${gp} gg`, importo: v });
  }

  return { totale: tot, dettagli, senior, nome: nomeV };
}

// Premio vendita permuta (piano Junior ott 2026): ≤30 gg +60 €, ≤60 gg +30 €,
// ≤90 gg 0 €, oltre −30 €
const PREMIO_PERMUTA = [ { max: 30, importo: 60 }, { max: 60, importo: 30 }, { max: 90, importo: 0 }, { max: Infinity, importo: -30 } ];
// Se il contratto vende un'auto entrata in stock come permuta, restituisce i
// giorni di giacenza fino alla firma; altrimenti null
function giorniPermutaInStock(contratto) {
  const ve = contratto.veicolo || {};
  const id = String(ve.id || '');
  const daStock = (() => {
    try {
      const lista = window._radarStockCache?.vehicles;
      const arr = lista ? (Array.isArray(lista) ? lista : Object.values(lista)) : [];
      return arr.find(x => x && String(x.id) === id) || null;
    } catch (e) { return null; }
  })();
  const isPermuta = id.startsWith('PERMUTA-') || ve.fonte === 'Permuta' || daStock?.fonte === 'Permuta';
  if (!isPermuta) return null;
  const tsId = id.startsWith('PERMUTA-') ? parseInt(id.slice(8)) : NaN;
  const ingresso = new Date(daStock?.enteredInStock || ve.enteredInStock || (isNaN(tsId) ? '' : tsId));
  const vendita = new Date(contratto.datiExtra?.dataFirma || contratto.approvazioneData || contratto.dataCreazione || '');
  if (isNaN(ingresso) || isNaN(vendita)) return null;
  return Math.max(0, Math.round((vendita - ingresso) / 864e5));
}

// Regole di RADAR → Provvigioni in un unico punto (usate anche da ANALISI):
// il contratto conta se venduto/consegnato e il venditore è un consulente;
// restituisce la data di riferimento provvigionale oppure null
const NON_CONSULENTI_PROVV = ['daniele fiore','rosario scala','luca giordano','emanuele amendola','gianluca scala','silvio capitelli',
  'francesca foglietta','rita pallante','roberta caiazzo','federica perna','martina grasso',
  'salvatore amirante','carlo postiglione','giulio palma','clemente palladino',
  'davide scognamiglio','giuseppe palladino'];
const RUOLI_ESCLUSI_PROVV = ['admin','super_admin','back_office','finance','service','logistica','admin_nlt','consulente_nlt','meccanico'];
function dataProvvigionale(c) {
  const isVenduto = c.stato === 'venduto';
  const isConsegnato = c.backoffice?.statoConsegna === 'consegnato';
  if (!isVenduto && !isConsegnato) return null;
  const ruoloV = (c.venditoreRuolo || '').toLowerCase().trim();
  if (ruoloV && RUOLI_ESCLUSI_PROVV.includes(ruoloV)) return null;
  if (!ruoloV) {
    const nomeV = (c.venditoreNome || '').toLowerCase().trim();
    if (NON_CONSULENTI_PROVV.some(n => nomeV.includes(n.split(' ')[0]) && nomeV.includes(n.split(' ')[1] || ''))) return null;
  }
  const dataConsegnaLog = c.backoffice_log
    ? Object.values(c.backoffice_log).filter(l => l && l.campo === 'statoConsegna' && l.valore === 'consegnato').sort((x, y) => new Date(y.timestamp) - new Date(x.timestamp))[0]?.timestamp
    : null;
  const d = new Date(c.dataVendita || c.vendutoAt || dataConsegnaLog || c.approvazioneData || c.dataCreazione || 0);
  return isNaN(d) ? null : d;
}

function ProvvigioniView({ user, contratti, T, isMobile }) {
  const fmt = v => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
  const [fMese, setFMese] = useState('');
  const [fAnno, setFAnno] = useState(new Date().getFullYear().toString());
  const [expandedV, setExpandedV] = useState(null);

  // Filtra contratti venduti per mese/anno — solo consulenti
  const contrattiVenduti = useMemo(() => {
    // Nomi non-consulenti da escludere (admin/back_office/finance/service)
    const nonConsulenti = ['daniele fiore','rosario scala','luca giordano','emanuele amendola','gianluca scala','silvio capitelli',
      'francesca foglietta','rita pallante','roberta caiazzo','federica perna','martina grasso',
      'salvatore amirante','carlo postiglione','giulio palma','clemente palladino',
      'davide scognamiglio','giuseppe palladino'];
    const ruoliEsclusi = ['admin','super_admin','back_office','finance','service','logistica','admin_nlt','consulente_nlt','meccanico'];
    return contratti.filter(c => {
      const isVenduto = c.stato === 'venduto';
      const isConsegnato = c.backoffice?.statoConsegna === 'consegnato';
      if (!isVenduto && !isConsegnato) return false;
      const ruoloV = (c.venditoreRuolo || '').toLowerCase().trim();
      if (ruoloV && ruoliEsclusi.includes(ruoloV)) return false;
      if (!ruoloV) {
        const nomeV = (c.venditoreNome || '').toLowerCase().trim();
        if (nonConsulenti.some(n => nomeV.includes(n.split(' ')[0]) && nomeV.includes(n.split(' ')[1] || ''))) return false;
      }
      // Data di riferimento: dataVendita → vendutoAt → data consegna dal log → approvazioneData → dataCreazione
      const dataConsegnaLog = c.backoffice_log
        ? Object.values(c.backoffice_log).filter(l=>l.campo==='statoConsegna'&&l.valore==='consegnato').sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp))[0]?.timestamp
        : null;
      // Data di riferimento per il calcolo provvigionale: dataVendita e
      // vendutoAt riflettono già il momento reale di consegna — vengono
      // salvati esattamente quando il contratto viene flaggato "venduto"
      // (sia dal prompt "Data di consegna/vendita" nella conferma vendita
      // manuale, sia in automatico quando il Back Office segna
      // "consegnato"). Hanno quindi sempre la priorità.
      const d = new Date(c.dataVendita || c.vendutoAt || dataConsegnaLog || c.approvazioneData || c.dataCreazione || 0);
      if (fAnno && d.getFullYear() !== parseInt(fAnno)) return false;
      if (fMese && (d.getMonth() + 1) !== parseInt(fMese)) return false;
      return true;
    });
  }, [contratti, fMese, fAnno]);

  // Ristorni provvigione finanziamento (estinzione anticipata entro 12 mesi):
  // salvati sul contratto; scalati nel mese in cui vengono registrati, il
  // mese della vendita resta com'era (già liquidato)
  const [ristorniLocali, setRistorniLocali] = useState({});
  const ristornoDi = c => ristorniLocali[c.id] || c.ristornoFinanziamento || null;
  const puoStornare = ['admin', 'super_admin'].includes(user?.ruolo);
  const stornaFinanziamento = async (c) => {
    const voce = (calcolaProvvigione(c).dettagli || []).find(d => d.voce.startsWith('Finanziamento'));
    if (!voce || !(voce.importo > 0)) return;
    if (!window.confirm(`Stornare la provvigione finanziamento di ${voce.importo} € a ${c.venditoreNome}?\n(estinzione anticipata entro 12 mesi)\n\nVerrà scalata nel mese corrente.`)) return;
    const r = { at: new Date().toISOString(), da: `${user?.nome || ''} ${user?.cognome || ''}`.trim(), importo: voce.importo };
    try {
      await set(ref(db, `contratti/${c.id}/ristornoFinanziamento`), r);
      setRistorniLocali(p => ({ ...p, [c.id]: r }));
    } catch (e) { alert('Errore: ' + e.message); }
  };

  // Raggruppa per venditore
  const perVenditore = useMemo(() => {
    const map = {};
    const slot = nome => (map[nome] = map[nome] || { nome, contratti: [], totProvv: 0, senior: isSenior(nome) });
    contrattiVenduti.forEach(c => {
      const nome = c.venditoreNome || 'N.D.';
      const prov = calcolaProvvigione(c);
      slot(nome).contratti.push({ ...c, _provv: prov });
      map[nome].totProvv += prov.totale;
    });
    // Ristorni registrati nel periodo selezionato
    contratti.forEach(c => {
      const r = ristornoDi(c);
      if (!r?.at) return;
      const d = new Date(r.at);
      if (fAnno && d.getFullYear() !== parseInt(fAnno)) return;
      if (fMese && (d.getMonth() + 1) !== parseInt(fMese)) return;
      const nome = c.venditoreNome || 'N.D.';
      const imp = -(parseFloat(r.importo) || 0);
      slot(nome).contratti.push({ ...c, id: `${c.id}-ristorno`, dataVendita: r.at, _ristorno: true,
        _provv: { totale: imp, dettagli: [{ voce: `Ristorno finanziamento (estinzione anticipata) · contratto ${c.datiExtra?.numeroContratto || c.id}`, importo: imp }] } });
      map[nome].totProvv += imp;
    });
    return Object.values(map).sort((a, b) => b.totProvv - a.totProvv);
  }, [contrattiVenduti, contratti, ristorniLocali, fMese, fAnno]);

  const meseNomi = ['','Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

  const exportProvvigioniExcel = (venditori, titolo) => {
    if (bloccaExportExcel()) return;
    const fmtN = v => parseFloat(v || 0); // vero numero, non testo con virgola
    const fd = iso => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d; }; // vera Date, non testo
    const wb = XLSX.utils.book_new();

    venditori.forEach(v => {
      const base = v.senior ? 0 : BASE_JUNIOR;
      const totale = base + v.totProvv;
      const rows = [
        [`Consulente: ${v.nome}`, '', '', '', '', '', '', '', ''],
        [''],
        ['N. Contratto','Data Vendita','Cliente','Veicolo','Prezzo','Sconto','Servizi','Finanziamento','Provvigione'],
      ];
      const rigaInizioDati = rows.length; // indice 0-based della prima riga dati (dopo titolo+vuota+intestazione)
      v.contratti.forEach(c => {
        const pr = c.prezzo || {};
        const cl = c.cliente || {};
        const ve = c.veicolo || {};
        const dataConsegnaLog = c.backoffice_log
          ? Object.values(c.backoffice_log).filter(l=>l.campo==='statoConsegna'&&l.valore==='consegnato').sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp))[0]?.timestamp
          : null;
        const dataRef = fd(c.dataVendita || c.vendutoAt || dataConsegnaLog || c.approvazioneData || c.dataCreazione);
        const nomeCliente = cl.ragioneSociale || `${cl.cognome||''} ${cl.nome||''}`.trim();
        const veicolo = `${ve.marca||''} ${ve.modello||''}`.trim();
        const dettagliProv = c._provv?.dettagli?.map(d=>d.voce).join(', ') || '';
        rows.push([
          c.datiExtra?.numeroContratto || c.id,
          dataRef,
          nomeCliente,
          veicolo,
          fmtN(pr.prezzoListino || ve.prezzo || 0),
          fmtN(pr.sconto || 0),
          dettagliProv,
          pr.hasFinanziamento ? (pr.finanziariaSocieta || pr.bancaFinanziamento || 'Sì') : 'No',
          fmtN(c._provv?.totale || 0),
        ]);
      });
      const rigaFineDati = rows.length - 1; // ultima riga dati inclusa
      rows.push(['']);
      const rigaTotaleServizi = rows.length;
      rows.push(['','','','','','','','Totale servizi', fmtN(v.totProvv)]);
      const rigaTotale = rows.length;
      rows.push(['','','','','','','','TOTALE', fmtN(v.totProvv)]);

      const ws = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
      ws['!cols'] = [14,14,22,20,12,10,30,16,12].map(w=>({wch:w}));

      // Formattazione valuta/data — solo sulle righe dati e sui due totali
      // finali, saltando titolo/intestazione/righe vuote che non sono numeri.
      const colonneValuta = [4, 5, 8]; // Prezzo, Sconto, Provvigione
      for (let r = rigaInizioDati; r <= rigaFineDati; r++) {
        const addrData = XLSX.utils.encode_cell({ r, c: 1 });
        if (ws[addrData] && ws[addrData].v instanceof Date) ws[addrData].z = 'dd/mm/yyyy';
        colonneValuta.forEach(c => {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (ws[addr] && typeof ws[addr].v === 'number') ws[addr].z = '#.##0,00 €';
        });
      }
      [rigaTotaleServizi, rigaTotale].forEach(r => {
        const addr = XLSX.utils.encode_cell({ r, c: 8 });
        if (ws[addr] && typeof ws[addr].v === 'number') ws[addr].z = '#.##0,00 €';
      });

      // Nome foglio: max 31 char, no caratteri speciali
      const nomeFoglio = v.nome.replace(/[^a-zA-Z0-9 ]/g,'').slice(0,31);
      XLSX.utils.book_append_sheet(wb, ws, nomeFoglio);
    });

    const oggi = new Date();
    const tag = `${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}`;
    const periodo = fMese ? `${meseNomi[parseInt(fMese)]}_${fAnno}` : fAnno;
    scriviExcel(wb, `RadaR_Provvigioni_${periodo}${titolo ? '_'+titolo.replace(/ /g,'_') : ''}.xlsx`);
  };

  return (
    <div>
      {/* Filtri */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select value={fAnno} onChange={e => setFAnno(e.target.value)}
          style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', padding: '8px 12px', color: T.text, fontSize: '12px', outline: 'none' }}>
          {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={fMese} onChange={e => setFMese(e.target.value)}
          style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', padding: '8px 12px', color: T.text, fontSize: '12px', outline: 'none' }}>
          <option value="">Tutti i mesi</option>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{meseNomi[m]}</option>)}
        </select>
        <div style={{ fontSize: '11px', color: T.textMuted }}>{contrattiVenduti.length} contratti venduti · {perVenditore.length} consulenti</div>
        {perVenditore.length > 0 && (
          <button onClick={() => exportProvvigioniExcel(perVenditore, '')}
            style={{ padding: '8px 14px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
            📊 Excel Tutti ({perVenditore.length})
          </button>
        )}
      </div>

      {/* Card per venditore */}
      {perVenditore.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>💰</div>
          <div style={{ fontSize: '13px', fontWeight: '700' }}>Nessun contratto venduto nel periodo</div>
        </div>
      ) : perVenditore.map(v => {
        const isExpanded = expandedV === v.nome;
        const base = v.senior ? 0 : BASE_JUNIOR;
        const totale = base + v.totProvv;
        return (
          <div key={v.nome} style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, marginBottom: '12px', overflow: 'hidden' }}>
            {/* Header venditore */}
            <div style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isExpanded ? `rgba(99,102,241,0.05)` : 'transparent' }}>
              <div onClick={() => setExpandedV(isExpanded ? null : v.nome)} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: v.senior ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#6366f1,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '900', color: '#fff', flexShrink: 0 }}>
                  {v.nome.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: T.text }}>{v.nome}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '2px' }}>{v.contratti.filter(x => !x._ristorno).length} auto vendute</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button onClick={e => { e.stopPropagation(); exportProvvigioniExcel([v], v.nome); }}
                  style={{ padding: '6px 10px', background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '10px', whiteSpace: 'nowrap' }}>
                  📊 Excel
                </button>
                <div onClick={() => setExpandedV(isExpanded ? null : v.nome)} style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '22px', fontWeight: '900', color: '#10b981' }}>{fmt(totale)}</div>
                  <div style={{ fontSize: '10px', color: T.textMuted }}>{v.contratti.filter(x => !x._ristorno).length} contratti</div>
                </div>
              </div>
            </div>

            {/* Dettaglio contratti */}
            {isExpanded && (
              <div style={{ borderTop: `1px solid ${T.border}` }}>
                {v.contratti.map((c, i) => {
                  const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim();
                  const dataConsegnaLog = c.backoffice_log
                    ? Object.values(c.backoffice_log).filter(l=>l.campo==='statoConsegna'&&l.valore==='consegnato').sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp))[0]?.timestamp
                    : null;
                  const d = new Date(c.dataVendita || c.vendutoAt || dataConsegnaLog || c.dataCreazione || 0).toLocaleDateString('it-IT');
                  return (
                    <div key={c.id || i} style={{ padding: '12px 20px', borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? 'transparent' : T.bg + '80', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{nc}</div>
                        <div style={{ fontSize: '10px', color: T.textMuted }}>{c.veicolo?.marca} {c.veicolo?.modello} · {d}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {c._provv.dettagli.map((det, j) => (
                            <span key={j} style={{ fontSize: '9px', background: det.importo < 0 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: det.importo < 0 ? '#ef4444' : '#10b981', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>
                              {det.voce}: {det.importo >= 0 ? '+' : ''}{det.importo}€
                            </span>
                          ))}
                        </div>
                        {!c._ristorno && ristornoDi(c) && (
                          <div style={{ fontSize: '9px', color: '#ef4444', fontWeight: '700', marginTop: '4px' }}>
                            ↩ Finanziamento stornato il {new Date(ristornoDi(c).at).toLocaleDateString('it-IT')}{ristornoDi(c).da ? ` da ${ristornoDi(c).da}` : ''} (scalato in quel mese)
                          </div>
                        )}
                        {!c._ristorno && !ristornoDi(c) && puoStornare && c._provv.dettagli.some(d => d.voce.startsWith('Finanziamento') && d.importo > 0) && (
                          <button onClick={() => stornaFinanziamento(c)}
                            style={{ marginTop: '6px', padding: '3px 8px', fontSize: '9px', fontWeight: '800', borderRadius: '6px', border: '1px dashed #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
                            ↩ Storna provvigione finanziamento
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '900', color: c._provv.totale < 0 ? '#ef4444' : '#10b981', flexShrink: 0 }}>{c._provv.totale >= 0 ? '+' : ''}{fmt(c._provv.totale)}</div>
                    </div>
                  );
                })}
                {/* Totale rigo */}
                <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', background: 'rgba(99,102,241,0.05)', borderTop: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: '12px', fontWeight: '900', color: T.text }}>TOTALE MESE ({fMese ? meseNomi[parseInt(fMese)] : 'tutti'} {fAnno})</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: '900', color: '#10b981' }}>{fmt(totale)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Tabella riepilogo */}
      {perVenditore.length > 0 && (
        <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px 20px', marginTop: '8px' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '12px' }}>💰 RIEPILOGO PROVVIGIONI</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ background: T.bg, borderRadius: '12px', padding: '12px 14px' }}>
              <div style={{ fontSize: '10px', color: T.textMuted }}>Totale da erogare</div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#10b981' }}>
                {fmt(perVenditore.reduce((s,v) => s + (v.senior ? 0 : BASE_JUNIOR) + v.totProvv, 0))}
              </div>
            </div>
            <div style={{ background: T.bg, borderRadius: '12px', padding: '12px 14px' }}>
              <div style={{ fontSize: '10px', color: T.textMuted }}>Solo provvigioni</div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#6366f1' }}>
                {fmt(perVenditore.reduce((s,v) => s + v.totProvv, 0))}
              </div>
            </div>
            <div style={{ background: T.bg, borderRadius: '12px', padding: '12px 14px' }}>
              <div style={{ fontSize: '10px', color: T.textMuted }}>Auto vendute totali</div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#f59e0b' }}>
                {contrattiVenduti.length}
              </div>
            </div>
            <div style={{ background: T.bg, borderRadius: '12px', padding: '12px 14px' }}>
              <div style={{ fontSize: '10px', color: T.textMuted }}>Consulenti attivi</div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: '#3b82f6' }}>
                {perVenditore.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TASSO DI CONVERSIONE WIDGET ─────────────────────────────────────────────
function TassoConversioneWidget({ safeDeals, filteredDeals, fMese, fVenditore, fSede, T, user }) {
  const [confrontoAttivo, setConfrontoAttivo] = React.useState(false);
  const [periodoA, setPeriodoA] = React.useState({ mese: '', anno: new Date().getFullYear().toString() });
  const [periodoB, setPeriodoB] = React.useState({ mese: '', anno: (new Date().getFullYear() - 1).toString() });

  const meseNomi = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

  const anni = React.useMemo(() => {
    const s = new Set((safeDeals||[]).map(d => (d.meseAnno||d.meseAnnoChiusura||'').split('/')[1]).filter(Boolean));
    return [...s].sort((a,b)=>b-a);
  }, [safeDeals]);

  // Ruolo utente corrente
  const ruoloWidget = (user?.ruolo || '').toLowerCase().trim();
  const isAdminWidget = ['admin','super_admin'].includes(ruoloWidget);
  const nomeConsulente = `${user?.nome||''} ${user?.cognome||''}`.trim();

  // Funzione calcola: dal CSV LeadSpark
  // Trattative totali = tutti i deal nel periodo
  // CTR = deal con stato Chiusa nel periodo
  const calcola = React.useCallback((opts = {}) => {
    const { mese, anno } = opts;

    const filtra = (d) => {
      if (!d) return false;
      const meseAnnoD    = d.meseAnno || '';
      const meseAnnoChiD = d.meseAnnoChiusura || '';
      // Filtro anno
      if (anno) {
        const matchA = meseAnnoD.endsWith(anno) || meseAnnoChiD.endsWith(anno);
        if (!matchA) return false;
      }
      // Filtro mese (MM/YYYY)
      if (mese && anno) {
        const key = `${mese.padStart(2,'0')}/${anno}`;
        const matchM = meseAnnoD === key || meseAnnoChiD === key;
        if (!matchM) return false;
      }
      // Filtri attivi RadaR
      if (fVenditore.length && !fVenditore.includes(d.venditore)) return false;
      if (fSede.length && !fSede.includes(d.sede)) return false;
      // Rosario Scala (titolare) escluso da questo widget: appare solo nella BI di RadaR con i dati importati
      if ((d.venditore || '').toLowerCase().includes('rosario') && (d.venditore || '').toLowerCase().includes('scala')) return false;
      return true;
    };

    const deals    = (safeDeals||[]).filter(filtra);
    const chiuse   = deals.filter(d => d.stato === 'Chiusa' || d.stato === 'chiusa' || d.stato === 'CHIUSA');

    // Per venditore
    const byVend = {};
    deals.forEach(d => {
      const v = d.venditore || 'N.D.';
      if (!byVend[v]) byVend[v] = { tot: 0, chiuse: 0 };
      byVend[v].tot++;
    });
    chiuse.forEach(d => {
      const v = d.venditore || 'N.D.';
      if (!byVend[v]) byVend[v] = { tot: 0, chiuse: 0 };
      byVend[v].chiuse++;
    });

    const nTot   = deals.length;
    const nChiuse = chiuse.length;
    const tasso  = nTot > 0 ? Math.round((nChiuse / nTot) * 100) : 0;
    return { nTot, nChiuse, tasso, byVend };
  }, [safeDeals, fVenditore, fSede]);

  // Periodo principale — segue filtri attivi se nessun confronto
  const datiA = React.useMemo(() => {
    if (confrontoAttivo) return calcola(periodoA);
    if (fMese.length === 1) {
      const [mm, yyyy] = fMese[0].split('/');
      return calcola({ mese: mm, anno: yyyy });
    }
    return calcola({});
  }, [calcola, confrontoAttivo, periodoA, fMese]);

  const datiB = React.useMemo(() =>
    confrontoAttivo ? calcola(periodoB) : null,
  [calcola, confrontoAttivo, periodoB]);

  const delta = (a, b) => b === 0 ? null : Math.round(((a - b) / b) * 100);

  const DeltaBadge = ({ a, b }) => {
    const d = delta(a, b);
    if (d === null) return null;
    const pos = d >= 0;
    return (
      <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', marginLeft: '4px',
        background: pos ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
        color: pos ? '#10b981' : '#ef4444' }}>
        {pos ? '▲' : '▼'} {Math.abs(d)}%
      </span>
    );
  };

  const labelPeriodo = (p) => p.mese && p.anno
    ? `${meseNomi[parseInt(p.mese)-1]} ${p.anno}`
    : p.anno || 'Tutti';

  const iS = { padding:'4px 8px', background:'#0a0c10', border:'1px solid #1e2230', borderRadius:'6px', color:'#e5e7eb', fontSize:'11px', outline:'none' };

  // Lista consulenti ufficiali — solo questi appaiono nel breakdown per venditore
  // (Rosario Scala escluso ovunque in questo widget: titolare, appare solo nella BI di RadaR con i dati importati)
  const CONSULENTI_LIST = ['Roberto Castaldo', 'Marco Castaldo', 'Luca Ambrosino', 'Davide Zaritto', 'Guido Fiore', 'Enrico Palumbo', 'Mattia Zuppa', 'Claudio Filosa', 'Francesco Montuori', 'Claudio Gerardi', 'Antonio Castaldo'];

  const allVend = React.useMemo(() => {
    const keys = new Set([...Object.keys(datiA.byVend), ...(datiB ? Object.keys(datiB.byVend) : [])]);
    return [...keys]
      .filter(v => CONSULENTI_LIST.some(c => v === c || v.toLowerCase().includes(c.split(' ')[0].toLowerCase())))
      .sort((a,b) => (datiA.byVend[b]?.chiuse/Math.max(datiA.byVend[b]?.tot,1)||0) - (datiA.byVend[a]?.chiuse/Math.max(datiA.byVend[a]?.tot,1)||0));
  }, [datiA, datiB]);

  return (
    <div style={{ background: '#141722', padding: '24px', borderRadius: '20px', border: '1px solid #1e2230' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: '900', color: '#ef4444', letterSpacing: '1px' }}>
          TASSO DI CONVERSIONE
        </div>
        <button onClick={() => setConfrontoAttivo(!confrontoAttivo)}
          style={{ padding: '4px 12px', borderRadius: '6px', border: `1px solid ${confrontoAttivo ? '#ef4444' : '#1e2230'}`, background: confrontoAttivo ? 'rgba(239,68,68,0.1)' : 'transparent', color: confrontoAttivo ? '#ef4444' : '#64748b', fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}>
          {confrontoAttivo ? '✕ Chiudi confronto' : '⇄ Confronta periodi'}
        </button>
      </div>

      {/* Selettori periodo confronto */}
      {confrontoAttivo && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px', padding: '12px', background: '#0d1117', borderRadius: '10px', border: '1px solid #1e2230' }}>
          {[['A', periodoA, setPeriodoA], ['B', periodoB, setPeriodoB]].map(([label, periodo, setPeriodo]) => (
            <div key={label}>
              <div style={{ fontSize: '9px', color: '#64748b', fontWeight: '900', marginBottom: '6px' }}>PERIODO {label}</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select value={periodo.mese} onChange={e=>setPeriodo(p=>({...p,mese:e.target.value}))} style={iS}>
                  <option value="">Tutti i mesi</option>
                  {meseNomi.map((m,i)=><option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
                </select>
                <select value={periodo.anno} onChange={e=>setPeriodo(p=>({...p,anno:e.target.value}))} style={iS}>
                  {anni.map(a=><option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPI globali */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            {isAdminWidget ? (
              <>
                <span style={{ fontSize: '36px', fontWeight: '900', color: '#ef4444' }}>{datiA.tasso}%</span>
                {datiB && <DeltaBadge a={datiA.tasso} b={datiB.tasso} />}
              </>
            ) : (
              <>
                <span style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px', display: 'block' }}>Il tuo tasso personale</span>
                <span style={{ fontSize: '36px', fontWeight: '900', color: '#6366f1' }}>
                  {(() => {
                    const myDeals = (safeDeals||[]).filter(d => {
                      const vend = (d.venditore||d.salesman_name||d.venditoreNome||'').toLowerCase();
                      return vend.includes(nomeConsulente.toLowerCase().split(' ')[0]) ||
                             vend.includes(nomeConsulente.toLowerCase().split(' ').slice(-1)[0]);
                    });
                    const myChiuse = myDeals.filter(d => (d.stato||d.deal_status||'').toLowerCase().includes('chius'));
                    return myDeals.length > 0 ? Math.round((myChiuse.length / myDeals.length) * 100) : 0;
                  })()}%
                </span>
                <span style={{ fontSize: '10px', color: '#475569', marginTop: '4px', display: 'block' }}>su {(safeDeals||[]).filter(d => { const vend=(d.venditore||d.salesman_name||d.venditoreNome||'').toLowerCase(); return vend.includes(nomeConsulente.toLowerCase().split(' ')[0]); }).length} trattative</span>
              </>
            )}
          </div>
          <div style={{ fontSize: '10px', color: '#64748b' }}>
            TASSO {confrontoAttivo ? labelPeriodo(periodoA) : 'GLOBALE'}
          </div>
          {datiB && (
            <div style={{ fontSize: '10px', color: '#475569', marginTop: '2px' }}>
              vs {datiB.tasso}% {labelPeriodo(periodoB)}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span style={{ fontSize: '24px', fontWeight: '900', color: '#94a3b8' }}>
              {datiA.nChiuse} / {datiA.nTot}
            </span>
            {datiB && <DeltaBadge a={datiA.nChiuse} b={datiB.nChiuse} />}
          </div>
          <div style={{ fontSize: '10px', color: '#64748b' }}>CTR / TRATTATIVE</div>
          {datiB && (
            <div style={{ fontSize: '10px', color: '#475569', marginTop: '2px' }}>
              vs {datiB.nChiuse} / {datiB.nTot}
            </div>
          )}
        </div>
      </div>

      {/* Barre per venditore */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {allVend.filter(v => (datiA.byVend[v]?.tot||0) > 0 || (datiB?.byVend[v]?.tot||0) > 0).map(vend => {
          const a = datiA.byVend[vend] || { tot: 0, chiuse: 0 };
          const b = datiB?.byVend[vend];
          const tA = a.tot > 0 ? Math.round((a.chiuse / a.tot) * 100) : 0;
          const tB = b?.tot > 0 ? Math.round((b.chiuse / b.tot) * 100) : 0;
          return (
            <div key={vend}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: datiB ? '3px' : '0' }}>
                <div style={{ fontSize: '10px', color: '#94a3b8', minWidth: '120px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{vend}</div>
                <div style={{ flex: 1, height: '8px', background: '#1e2230', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${tA}%`, height: '100%', background: '#ef4444', borderRadius: '4px', transition: 'width 0.5s' }} />
                </div>
                <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: '700', minWidth: '30px', textAlign: 'right' }}>{tA}%</div>
                <div style={{ fontSize: '9px', color: '#475569', minWidth: '40px' }}>{a.chiuse}/{a.tot}</div>
                {datiB && <DeltaBadge a={tA} b={tB} />}
              </div>
              {datiB && b && b.tot > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '88px' }}>
                  <div style={{ flex: 1, height: '5px', background: '#1e2230', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${tB}%`, height: '100%', background: '#475569', borderRadius: '4px' }} />
                  </div>
                  <div style={{ fontSize: '9px', color: '#475569', minWidth: '30px', textAlign: 'right' }}>{tB}%</div>
                  <div style={{ fontSize: '9px', color: '#374151', minWidth: '40px' }}>{b.chiuse}/{b.tot}</div>
                  <div style={{ minWidth: '36px' }} />
                </div>
              )}
            </div>
          );
        })}
        {(safeDeals||[]).length === 0 && (
          <div style={{ fontSize: '11px', color: '#475569', textAlign: 'center', padding: '20px 0' }}>
            Carica il CSV LeadSpark per vedere il tasso di conversione
          </div>
        )}
      </div>
    </div>
  );
}


function RadarView({ user, contratti = [], preventiviGlobali = [] }) {
  const contrattiGlobali = contratti; // alias per chiarezza
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile, isTablet } = useBreakpoint();
  const [deals, setDeals] = useState(null);
  const [loadingCloud, setLoadingCloud] = useState(true);
  const [viewVenditori, setViewVenditori] = useState('pezzi');
  const [fSede, setFSede] = useState([]);
  const [fStatus, setFStatus] = useState([]);
  const [fVenditore, setFVenditore] = useState([]);
  const [fMese, setFMese] = useState([]);
  const [fCanale, setFCanale] = useState([]);
  const [fCitta, setFCitta] = useState([]);
  const [fMarca, setFMarca] = useState([]);
  const [search, setSearch] = useState('');
  const [radarSubTab, setRadarSubTab] = useState('dashboard');

  const puoVedereProvvigioni = ['admin','super_admin'].includes((user?.ruolo||'').toLowerCase().trim());

  useEffect(() => {
    if (window._radarDataCache) { setDeals(window._radarDataCache); setLoadingCloud(false); return; }
    get(ref(db, 'radar_data')).then(snapshot => {
      const data = snapshot.val();
      if (data) { window._radarDataCache = data; setDeals(data); }
      setLoadingCloud(false);
    });
  }, []);

  const handleFileUpload = (e) => {
    const reader = new FileReader();
    reader.onload = (ev) => { const parsed = parseCSV(ev.target.result); setDeals(parsed); window._radarDataCache = parsed; set(ref(db, 'radar_data'), parsed); };
    reader.readAsText(e.target.files[0]);
  };

  const safeDeals = useMemo(() => deals || [], [deals]);

  const filteredDeals = useMemo(() => safeDeals.filter(d => {
    if (!d) return false;
    const matchRole   = (user?.ruolo === 'consulente' || user?.ruolo === 'brand_manager_se') ? (d.venditore.toLowerCase().includes(user.nome.toLowerCase()) && d.venditore.toLowerCase().includes(user.cognome.toLowerCase())) : true;
    const matchSede   = !fSede.length   || fSede.includes(d.sede);
    const matchStatus = !fStatus.length || fStatus.includes(d.stato);
    const matchVend   = !fVenditore.length || fVenditore.includes(d.venditore);
    const matchMese   = !fMese.length   || (d.stato === 'Chiusa' ? fMese.includes(d.meseAnnoChiusura) : fMese.includes(d.meseAnno));
    const matchCanale = !fCanale.length || fCanale.includes(d.origine);
    const matchCitta  = !fCitta.length  || fCitta.includes(d.citta);
    const matchMarca  = !fMarca.length  || fMarca.includes(d.marca);
    const matchSearch = !search || d.cliente.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchSede && matchStatus && matchVend && matchMese && matchCanale && matchCitta && matchMarca && matchSearch;
  }), [safeDeals, fSede, fStatus, fVenditore, fMese, fCanale, fCitta, fMarca, search, user]);

  const analysis = useMemo(() => {
    const res = { brands: {}, origini: {}, citta: {}, mixModelli: {}, totVal: 0, chiuse: 0, chiuseVal: 0, chiuseLead: 0, sedi: {}, venditoriStats: {}, tuttiChiusi: [] };
    // Lead = TUTTE le fonti tranne queste (contatti diretti, non lead)
    const fontiNonLead = ['ingresso', 'passaggio', 'contatto personale', 'segnalato da titolar', 'segnalata da titolar'];
    const norm = x => String(x || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
    const isFonteLead = origine => {
      const o = norm(origine);
      if (!o || o === 'n.d.' || o === 'nd') return false;   // fonte mancante: non conteggiata come lead
      if (fontiNonLead.some(f => o.includes(f))) return false;
      return true;
    };
    filteredDeals.forEach(d => {
      res.totVal += d.importo;
      res.brands[d.marca] = (res.brands[d.marca] || 0) + 1;
      res.origini[d.origine] = (res.origini[d.origine] || 0) + 1;
      res.citta[d.citta] = (res.citta[d.citta] || 0) + 1;
      res.sedi[d.sede] = (res.sedi[d.sede] || 0) + 1;
      if (d.stato === 'Chiusa') {
        res.chiuse += 1; res.chiuseVal += d.importo;
        const mix = `${d.marca} ${d.modello}`.trim();
        if (mix) res.mixModelli[mix] = (res.mixModelli[mix] || 0) + 1;
        if (!res.venditoriStats[d.venditore]) res.venditoriStats[d.venditore] = { pezzi: 0, fatturato: 0 };
        res.venditoriStats[d.venditore].pezzi += 1;
        res.venditoriStats[d.venditore].fatturato += d.importo;
        const originLower = d.origine.toLowerCase();
        if (isFonteLead(originLower)) res.chiuseLead += 1;
        res.tuttiChiusi.push(d);
      }
    });
    const sort = (obj, lim = 6) => Object.entries(obj).sort((a,b) => b[1] - a[1]).slice(0, lim);
    return { ...res, avgVal: res.chiuseVal / (res.chiuse || 1), percLead: res.chiuse > 0 ? (res.chiuseLead / res.chiuse) * 100 : 0, topBrands: sort(res.brands), topOrigini: sort(res.origini), topCitta: sort(res.citta, 8), topMix: sort(res.mixModelli, 10), topSedi: sort(res.sedi), elencoIntegrale: res.tuttiChiusi };
  }, [filteredDeals]);

  const mesiOptions = useMemo(() => [...new Set([...safeDeals.map(d => d.meseAnno), ...safeDeals.map(d => d.meseAnnoChiusura)])].filter(v => v && v !== 'N.D.').sort(), [safeDeals]);
  const getOptions = (key) => [...new Set(safeDeals.map(d => d[key]))].filter(Boolean).sort();

  if (loadingCloud) return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>Sincronizzazione RadaR...</div>;

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>

      {/* SUB-TAB: Dashboard | Provvigioni */}
      {puoVedereProvvigioni && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: T.surface, borderRadius: '14px', padding: '4px', border: `1px solid ${T.border}`, width: 'fit-content' }}>
          {[['dashboard','📊 Dashboard'],['provvigioni','💰 Provvigioni'],['conversione','🎯 Conversione']].map(([id,label]) => (
            <button key={id} onClick={() => setRadarSubTab(id)}
              style={{ padding: '8px 18px', fontSize: '11px', fontWeight: '900', border: 'none', borderRadius: '10px', cursor: 'pointer', background: radarSubTab === id ? '#6366f1' : 'transparent', color: radarSubTab === id ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* TAB CONVERSIONE */}
      {radarSubTab === 'conversione' && puoVedereProvvigioni && (
        <ConversioneView user={user} deals={deals} T={T} isMobile={isMobile} />
      )}

      {/* TAB PROVVIGIONI */}
      {radarSubTab === 'provvigioni' && puoVedereProvvigioni && (
        <ProvvigioniView user={user} contratti={contratti} T={T} isMobile={isMobile} />
      )}

      {/* TAB DASHBOARD — contenuto esistente */}
      {(radarSubTab === 'dashboard' || !puoVedereProvvigioni) && (
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <input placeholder="🔍 Cerca cliente..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '280px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', padding: '10px 15px', color: '#fff', fontSize: '12px', outline: 'none' }} />
        {puoAggiornareDbRadar(user) && (
          <label style={{ background: '#fff', padding: '10px 20px', borderRadius: '12px', color: '#000', cursor: 'pointer', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center' }}>
            AGGIORNA DB <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileUpload} />
          </label>
        )}
      </div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', flexWrap: 'wrap', background: T.surface, padding: '15px', borderRadius: '25px', border: `1px solid ${T.border}`, backdropFilter: 'blur(10px)' }}>
        {(user.ruolo === 'admin' || user.ruolo === 'super_admin') && (<>
          <RadarMultiSelect label="SEDI"      options={getOptions('sede')}      selected={fSede}      onChange={setFSede}      onClear={() => setFSede([])} />
          <RadarMultiSelect label="VENDITORI" options={getOptions('venditore')} selected={fVenditore} onChange={setFVenditore} onClear={() => setFVenditore([])} />
        </>)}
        {/* CANALI: admin, super admin e BDC (che gestisce i lead) */}
        {['admin', 'super_admin', 'bdc'].includes(user.ruolo) && (
          <RadarMultiSelect label="CANALI"    options={getOptions('origine')}   selected={fCanale}    onChange={setFCanale}    onClear={() => setFCanale([])} />
        )}
        <RadarMultiSelect label="STATI"  options={getOptions('stato')}  selected={fStatus} onChange={setFStatus} onClear={() => setFStatus([])} />
        <RadarMultiSelect label="MESI"   options={mesiOptions}          selected={fMese}   onChange={setFMese}   onClear={() => setFMese([])} />
        <RadarMultiSelect label="MARCHE" options={getOptions('marca')}  selected={fMarca}  onChange={setFMarca}  onClear={() => setFMarca([])} />
        <RadarMultiSelect label="CITTÀ" options={getOptions('citta')} selected={fCitta} onChange={setFCitta} onClear={() => setFCitta([])} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <RadarStat label="Fatturato Chiuso"   value={fmt(analysis.chiuseVal)}            sub="Valore reale"        color="#10b981" />
        <RadarStat label="Contratti Firmati"  value={fmtN(analysis.chiuse)}              sub="Totale pezzi"        color="#ef4444" />
        <RadarStat label="% Chiusure da Lead" value={analysis.percLead.toFixed(1) + '%'} sub="Digital Performance" color="#ec4899" />
        <RadarStat label="Volume Trattative"  value={fmtN(filteredDeals.length)}         sub="Ingressi totali"     color="#8b5cf6" />
        <RadarStat label="Media Valore"       value={fmt(analysis.avgVal)}               sub="Ticket medio"        color="#3b82f6" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '25px', marginBottom: '30px' }}>
        <RadarChart title="Performance Marche" data={analysis.topBrands}  color="#3b82f6" />
        <RadarChart title="Canali Lead Reali"  data={analysis.topOrigini} color="#8b5cf6" />
        <div style={{ background: '#141722', padding: '20px', borderRadius: '25px', border: '1px solid #1e2230', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '11px', color: '#ec4899', fontWeight: '900' }}>INCIDENZA LEAD SU VENDITE</h4>
          <div style={{ fontSize: '48px', fontWeight: '900', color: '#fff', marginBottom: '5px' }}>{analysis.percLead.toFixed(1)}%</div>
          <div style={{ color: '#9ca3af', fontSize: '12px', fontWeight: '700' }}>{analysis.chiuseLead} Contratti digitali su {analysis.chiuse} Totali</div>
          <div style={{ width: '80%', height: '8px', background: '#0a0c10', borderRadius: '10px', marginTop: '20px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${analysis.percLead}%`, background: '#ec4899', borderRadius: '10px', transition: 'width 1s ease-in-out' }} />
          </div>
        </div>
        {(user.ruolo === 'admin' || user.ruolo === 'super_admin') ? (
          <div style={{ background: '#141722', padding: '25px', borderRadius: '30px', border: '1px solid #1e2230' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ margin: 0, fontSize: '11px', color: '#3b82f6', fontWeight: '900' }}>CLASSIFICA VENDITORI</h4>
              <div style={{ display: 'flex', background: '#0a0c10', borderRadius: '10px', padding: '4px' }}>
                <button onClick={() => setViewVenditori('pezzi')}    style={{ padding: '6px 12px', fontSize: '9px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: viewVenditori === 'pezzi'    ? '#3b82f6' : 'transparent', color: '#fff', fontWeight: '800' }}>PEZZI</button>
                <button onClick={() => setViewVenditori('fatturato')} style={{ padding: '6px 12px', fontSize: '9px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: viewVenditori === 'fatturato' ? '#3b82f6' : 'transparent', color: '#fff', fontWeight: '800' }}>VALORE</button>
              </div>
            </div>
            {Object.entries(analysis.venditoriStats).sort((a, b) => b[1][viewVenditori] - a[1][viewVenditori]).map(([name, stat], i) => (
              <div key={name} style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
                  <span style={{ color: '#9ca3af' }}>{i+1}. {name.toUpperCase()}</span>
                  <b style={{ color: '#fff' }}>{viewVenditori === 'pezzi' ? stat.pezzi : fmt(stat.fatturato)}</b>
                </div>
                <div style={{ height: '6px', background: T.chartBg, borderRadius: '10px' }}>
                  <div style={{ height: '100%', width: `${(stat[viewVenditori] / Math.max(...Object.values(analysis.venditoriStats).map(s => s[viewVenditori]))) * 100}%`, background: '#3b82f6', borderRadius: '10px' }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ background: '#141722', padding: '25px', borderRadius: '30px', border: '1px solid #1e2230', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <p style={{ color: '#475569', fontSize: '11px', fontWeight: '700' }}>CLASSIFICA RISERVATA ALLA DIREZIONE</p>
          </div>
        )}
        <RadarChart title="Ranking Modelli" data={analysis.topMix}  color="#10b981" />
        {/* Tasso di Conversione — dati Firebase + confronto periodi */}
        <TassoConversioneWidget
          safeDeals={safeDeals}
          filteredDeals={filteredDeals}
          fMese={fMese}
          fVenditore={fVenditore}
          fSede={fSede}
          T={T}
          user={user}
        />
      </div>
      <div style={{ background: '#141722', borderRadius: '30px', border: '1px solid #1e2230', overflow: 'hidden' }}>
        <div style={{ padding: '25px 40px', borderBottom: '1px solid #1e2230', background: T.surface2 }}>
          <h4 style={{ margin: 0, fontSize: '13px', color: '#10b981', fontWeight: '900', letterSpacing: '1px' }}>REGISTRO ANALITICO CONTRATTI CHIUSI</h4>
        </div>
        <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto', padding: '0 20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: '#9ca3af', fontSize: '11px', textAlign: 'left', minWidth: '580px' }}>
            <thead style={{ position: 'sticky', top: 0, background: T.surface, zIndex: 10 }}>
              <tr style={{ borderBottom: '2px solid #1e2230', color: '#64748b' }}>
                <th style={{ padding: '15px 10px' }}>DATA TRATTATIVA</th>
                <th style={{ padding: '15px 10px' }}>DATA CHIUSURA</th>
                <th style={{ padding: '15px 10px' }}>CLIENTE</th>
                <th style={{ padding: '15px 10px' }}>MARCA</th>
                <th style={{ padding: '15px 10px' }}>MODELLO</th>
                <th style={{ padding: '15px 10px' }}>VENDITORE</th>
                <th style={{ padding: '15px 10px', textAlign: 'right' }}>PREZZO</th>
              </tr>
            </thead>
            <tbody>
              {analysis.elencoIntegrale.map((c, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #1e2230' }}>
                  <td style={{ padding: '12px 10px' }}>{c.data}</td>
                  <td style={{ padding: '12px 10px', color: '#3b82f6', fontWeight: '700' }}>{c.dataChiusura}</td>
                  <td style={{ padding: '12px 10px', color: '#fff', fontWeight: '700' }}>{String(c.cliente).toUpperCase()}</td>
                  <td style={{ padding: '12px 10px' }}>{c.marca.toUpperCase()}</td>
                  <td style={{ padding: '12px 10px' }}>{c.modello.toUpperCase()}</td>
                  <td style={{ padding: '12px 10px', fontSize: '10px' }}>{c.venditore.toUpperCase()}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: '#10b981', fontWeight: '900', fontSize: '12px' }}>{fmt(c.importo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB STOCK — con pulsante Preventivo su ogni card
// ═══════════════════════════════════════════════════════════════════════════════
// ─── Export Stock in Excel — dettagli auto, ubicazione, note (NO preventivi) ──
// ─── Tabella costi Garanzia Mawdy (Barcellona CC ms) — da PDF fornitore ──
// 3 fasce età/km × 6 fasce cilindrata/marca × 5 durate (12/24/36/48/60 mesi).
// Fonte: COSTI_MAWDY-14072026124231.pdf — Ragione Sociale Gruppo Autoscala.
// Tariffe Mawdy IN VIGORE FINO al cambio tariffe (contratti già esistenti/più
// vecchi continuano a usare queste, per non alterare retroattivamente conti
// economici già chiusi).
const TABELLA_COSTI_GARANZIA_MAWDY_VECCHIA = {
  fascia1: { // fino a 5 anni di età e con meno di 50.000 km
    fino1500:      { 12: 160, 24: 250, 36: 320,  48: 400,  60: 600  },
    da1501a2000:   { 12: 180, 24: 280, 36: 420,  48: 530,  60: 750  },
    da2001a2500:   { 12: 200, 24: 310, 36: 460,  48: 550,  60: 810  },
    oltre2500:     { 12: 450, 24: 800, 36: 1050, 48: 1400, 60: 1800 },
    landRoverJaguar: { 12: 500, 24: 950, 36: 1300, 48: 1650, 60: 2000 },
  },
  fascia2: { // fino a 10 anni di età e con meno di 100.000 km
    fino1500:      { 12: 160, 24: 250, 36: 320,  48: 400,  60: 600  },
    da1501a2000:   { 12: 180, 24: 280, 36: 420,  48: 530,  60: 750  },
    da2001a2500:   { 12: 200, 24: 310, 36: 460,  48: 550,  60: 810  },
    oltre2500:     { 12: 450, 24: 800, 36: 1050, 48: 1400, 60: 1800 },
    landRoverJaguar: { 12: 500, 24: 950, 36: 1300, 48: 1650, 60: 2000 },
  },
  fascia3: { // fino a 15 anni di età (nessun limite km)
    fino1500:      { 12: 180, 24: 270, 36: 340,  48: 420,  60: 620  },
    da1501a2000:   { 12: 200, 24: 300, 36: 440,  48: 550,  60: 770  },
    da2001a2500:   { 12: 220, 24: 330, 36: 480,  48: 570,  60: 830  },
    oltre2500:     { 12: 470, 24: 820, 36: 1070, 48: 1420, 60: 1820 },
    landRoverJaguar: { 12: 520, 24: 970, 36: 1320, 48: 1670, 60: 2020 },
  },
};

// Tariffe Mawdy AGGIORNATE (fornite da Daniele il 30/07/2026, file "DB -
// mAWDY - SIVIGLIA.xlsx") — SOLO 2 fasce d'età, nessun limite km (a
// differenza della tabella vecchia). Si applicano SOLO ai contratti creati
// da DATA_CAMBIO_TARIFFE_MAWDY in poi — non retroattive.
// Land Rover/Jaguar: il file fornito non riporta tariffe speciali per questi
// marchi — manteniamo provvisoriamente le vecchie finché non arrivano
// importi aggiornati specifici.
const TABELLA_COSTI_GARANZIA_MAWDY_NUOVA = {
  fascia1: { // fino a 10 anni di età, nessun limite km
    fino1500:      { 12: 180, 24: 270, 36: 340,  48: 420,  60: 620  },
    da1501a2000:   { 12: 200, 24: 300, 36: 440,  48: 550,  60: 770  },
    da2001a2500:   { 12: 220, 24: 330, 36: 480,  48: 570,  60: 830  },
    oltre2500:     { 12: 470, 24: 820, 36: 1070, 48: 1420, 60: 1800 },
    landRoverJaguar: { 12: 500, 24: 950, 36: 1300, 48: 1650, 60: 2000 },
  },
  fascia2: { // da 10 a 15 anni di età, nessun limite km
    fino1500:      { 12: 200, 24: 320, 36: 450,  48: 570,  60: 690  },
    da1501a2000:   { 12: 220, 24: 400, 36: 550,  48: 670,  60: 780  },
    da2001a2500:   { 12: 250, 24: 490, 36: 735,  48: 850,  60: 1000 },
    oltre2500:     { 12: 500, 24: 850, 36: 1200, 48: 1500, 60: 1700 },
    landRoverJaguar: { 12: 520, 24: 970, 36: 1320, 48: 1670, 60: 2020 },
  },
};

// Data da cui si applicano le tariffe nuove — i contratti creati prima
// continuano a usare la tabella vecchia, per non alterare conti economici
// già chiusi.
const DATA_CAMBIO_TARIFFE_MAWDY = new Date('2026-07-30');

// Calcola il costo garanzia Mawdy automaticamente da cilindrata, data di
// immatricolazione, km e marca del veicolo, per la durata scelta nel
// contratto. Restituisce null se non è possibile calcolarlo (dati mancanti,
// durata non standard, o veicolo troppo vecchio per essere in tabella) —
// in quel caso il campo nel conto economico resta vuoto/da compilare a mano.
// Parsa una data di immatricolazione in uno dei formati usati in RadaR:
// "MM/YYYY" (il più comune), "DD/MM/YYYY", o formato ISO. new Date() da solo
// non gestisce "MM/YYYY" (lo scarta come data non valida), causando il
// mancato calcolo per la stragrande maggioranza dei veicoli.
function parseDataImmatricolazione(str) {
  if (!str) return null;
  const s = str.toString().trim();
  const parti = s.split('/');
  if (parti.length === 2) {
    // MM/YYYY
    const mese = parseInt(parti[0], 10) - 1;
    const anno = parseInt(parti[1], 10);
    if (!isNaN(mese) && !isNaN(anno) && anno > 1900) return new Date(anno, mese, 1);
  } else if (parti.length === 3) {
    // DD/MM/YYYY
    const giorno = parseInt(parti[0], 10);
    const mese = parseInt(parti[1], 10) - 1;
    const anno = parseInt(parti[2], 10);
    if (!isNaN(giorno) && !isNaN(mese) && !isNaN(anno) && anno > 1900) return new Date(anno, mese, giorno);
  }
  // Fallback: prova il parsing nativo (es. formato ISO "YYYY-MM-DD")
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function calcolaCostoGaranziaMawdy(cc, dataRegistrazione, km, marca, mesi, dataContratto) {
  return diagnosticaCostoGaranziaMawdy(cc, dataRegistrazione, km, marca, mesi, dataContratto).valore;
}

// Versione diagnostica: restituisce sempre { valore, motivo } — usata nel
// riepilogo per spiegare a schermo perché il calcolo automatico non riesce
// (dato mancante, veicolo troppo vecchio, durata non riconosciuta, ecc.)
// invece di lasciare semplicemente il campo vuoto senza spiegazione.
// dataContratto (data di creazione del contratto) decide quale tabella
// tariffaria usare — non retroattivo: solo i contratti creati dal
// 30/07/2026 in poi usano le tariffe nuove.
function diagnosticaCostoGaranziaMawdy(cc, dataRegistrazione, km, marca, mesi, dataContratto) {
  const durata = parseInt(mesi);
  if (!durata) return { valore: null, motivo: 'Nessuna garanzia Mawdy rilevata nel contratto' };
  if (![12, 24, 36, 48, 60].includes(durata)) return { valore: null, motivo: `Durata non standard (${durata} mesi) — non presente in tabella` };
  if (!dataRegistrazione) return { valore: null, motivo: 'Data di immatricolazione mancante sul veicolo' };

  const dataReg = parseDataImmatricolazione(dataRegistrazione);
  if (!dataReg) return { valore: null, motivo: `Data di immatricolazione non leggibile ("${dataRegistrazione}")` };
  const etaAnni = (Date.now() - dataReg.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  const kmNum = parseFloat(km) || 0;

  const dataContrattoParsed = dataContratto ? new Date(dataContratto) : new Date();
  const usaTariffeNuove = dataContrattoParsed >= DATA_CAMBIO_TARIFFE_MAWDY;
  const tabella = usaTariffeNuove ? TABELLA_COSTI_GARANZIA_MAWDY_NUOVA : TABELLA_COSTI_GARANZIA_MAWDY_VECCHIA;

  let fascia;
  if (usaTariffeNuove) {
    // Tariffe nuove: solo 2 fasce d'età, nessun limite km.
    if (etaAnni <= 10) fascia = tabella.fascia1;
    else if (etaAnni <= 15) fascia = tabella.fascia2;
    else return { valore: null, motivo: `Veicolo troppo vecchio (${etaAnni.toFixed(1)} anni) — fuori tabella` };
  } else {
    // Tariffe vecchie: 3 fasce, con limite km sulle prime due.
    if (etaAnni <= 5 && kmNum < 50000) fascia = tabella.fascia1;
    else if (etaAnni <= 10 && kmNum < 100000) fascia = tabella.fascia2;
    else if (etaAnni <= 15) fascia = tabella.fascia3;
    else return { valore: null, motivo: `Veicolo troppo vecchio (${etaAnni.toFixed(1)} anni) — fuori tabella` };
  }

  const marcaNorm = (marca || '').toLowerCase().trim();
  let riga;
  if (marcaNorm.includes('land rover') || marcaNorm.includes('jaguar')) {
    riga = fascia.landRoverJaguar;
  } else {
    const ccNum = parseFloat(cc) || 0;
    if (!ccNum) return { valore: null, motivo: 'Cilindrata mancante (non trovata in stock, nel contratto, né deducibile da versione/modello/optionals)' };
    if (ccNum <= 1500) riga = fascia.fino1500;
    else if (ccNum <= 2000) riga = fascia.da1501a2000;
    else if (ccNum <= 2500) riga = fascia.da2001a2500;
    else riga = fascia.oltre2500;
  }
  const valore = riga[durata] ?? null;
  return { valore, motivo: valore !== null ? `Calcolato (tariffe ${usaTariffeNuove ? 'nuove' : 'vecchie'}): ${etaAnni.toFixed(1)} anni, ${cc || (marcaNorm.includes('land rover')||marcaNorm.includes('jaguar') ? marca : '?')}cc, ${durata} mesi` : 'Combinazione non presente in tabella' };
}

// Rileva la presenza di una garanzia Mawdy nel contratto, cercando in tutti
// i punti dove può comparire (checkbox dedicati, testo libero nei servizi,
// nel prezzo, nelle note, nei dettagli costi) — non solo nel campo esplicito
// "Garanzia Estesa". Restituisce anche la durata in mesi quando determinabile,
// necessaria per il lookup nella tabella costi.
function rilevaGaranziaMawdy(c) {
  const sv = c.servizi || {};
  const pr = c.prezzo || {};

  // 1. Campo più affidabile: durata esplicita da "Garanzia Estesa"
  if (parseInt(sv.garanziaEstesaMesi) > 0) return { presente: true, mesi: parseInt(sv.garanziaEstesaMesi) };

  // 2. Checkbox specifico "Mawdy 36 mesi"
  if (sv.mawdy36) return { presente: true, mesi: 36 };
  if (sv.mawdy) return { presente: true, mesi: 36 }; // flag generico senza durata propria: assume 36 mesi

  // 3. Cerca "mawdy" nel testo libero (servizi, prezzo, note, dettagli costi)
  //    e prova a estrarre il numero di mesi scritto vicino (es. "Mawdy 36",
  //    "Mawdy36", "Estensione Garanzia Mawdy 24 mesi").
  const det = c.costiDettaglio || [];
  const note = (c.note || '') + (c.datiExtra?.noteVendita || '') + (c.datiExtra?.noteBack || '');
  const testoCompleto = [
    ...Object.values(sv).filter(v => typeof v === 'string'),
    ...Object.values(pr).filter(v => typeof v === 'string'),
    note,
    ...(Array.isArray(det) ? det.map(d => d.voce || '') : []),
  ].join(' ').toLowerCase();

  if (testoCompleto.includes('mawdy')) {
    const match = testoCompleto.match(/mawdy\D{0,12}(\d{2})/);
    const mesiTrovati = match ? parseInt(match[1]) : null;
    return { presente: true, mesi: [12, 24, 36, 48, 60].includes(mesiTrovati) ? mesiTrovati : 36 };
  }

  return { presente: false, mesi: null };
}

// Estrae la cilindrata dal testo della "versione" del veicolo quando il
// campo dedicato manca — le versioni auto quasi sempre iniziano con la
// cilindrata in litri puntata (es. "1.2 PureTech 130cv" -> 1200cc,
// "1.6 BlueHDi" -> 1600cc, "2.0 TDI" -> 2000cc).
function estraiCilindrataDaVersione(versione) {
  if (!versione) return null;
  const match = versione.toString().match(/\b([0-6])\.(\d)\b/);
  if (!match) return null;
  return Math.round(parseFloat(`${match[1]}.${match[2]}`) * 1000);
}

// Classifica un veicolo come "Commerciale" o "Vettura" in base alla
// carrozzeria dichiarata da StockSpark. Restituisce null se la carrozzeria
// è vuota o non riconosciuta — in quel caso il veicolo resta "non
// classificato" (non compare in nessuno dei due filtri, ma resta visibile
// con "Tutti").
function classificaCategoriaVeicolo(carrozzeria) {
  const c = (carrozzeria || '').toLowerCase().trim();
  if (!c) return null;
  const COMMERCIALI = ['furgone', 'furgonato', 'autocarro', 'pickup', 'pick-up', 'van', 'combi', 'cassonato', 'telonato', 'frigo', 'ribaltabile', 'minibus', 'autotelaio'];
  if (COMMERCIALI.some(k => c.includes(k))) return 'Commerciale';
  const VETTURE = ['berlina', 'suv', 'citycar', 'city car', 'station wagon', 'wagon', 'coupé', 'coupe', 'cabrio', 'monovolume', 'crossover', 'utilitaria', 'fuoristrada', 'spider', '2 volumi', '3 volumi', 'hatchback'];
  if (VETTURE.some(k => c.includes(k))) return 'Vettura';
  return null;
}

// Colonne disponibili per l'export Stock, raggruppate per il selettore.
// I nomi devono combaciare esattamente con gli header di esportaStockExcel.
const COLONNE_EXPORT_STOCK = [
  { gruppo: 'Dati Veicolo', colonne: ['Stato', 'Marca', 'Modello', 'Versione', 'Tipo', 'Classe', 'Targa', 'Telaio (VIN)', 'Colore', 'Carburante', 'Cambio', 'Carrozzeria', 'Categoria', 'Km', 'Immatricolazione', 'Anno'] },
  { gruppo: 'Prezzi e Stock', colonne: ['Prezzo Listino (€)', 'Prezzo Bottom (€)', 'Fornitore', 'Giorni in Stock', 'Data Ingresso Stock', 'Alert (+150gg)', 'IVA Esposta', 'Sede / Ubicazione', 'Optionals', 'Note', 'Documenti'] },
  { gruppo: 'Interventi', colonne: ['N. Interventi Officina', 'Costo Interventi Officina (€)', 'Dettaglio Interventi Officina', 'N. Interventi Carrozzeria', 'Costo Interventi Carrozzeria (€)', 'Dettaglio Interventi Carrozzeria'] },
  { gruppo: 'Vendita (se presente)', colonne: ['Cliente Nome', 'Cliente Cognome', 'Prezzo di Vendita (€)', 'Prezzo Servizi (€)', 'Costo Servizi (€)'] },
];

// Modale riusabile per scegliere quali colonne includere in un export Excel
// — non tocca la logica di calcolo dei dati, filtra solo cosa scrivere nel
// file finale. Usabile per qualunque export (Stock, Back Office, ecc.)
// passando la propria lista di gruppi/colonne.
function ColonneExportModal({ titolo, colonneDisponibili, onClose, onEsporta }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const tutteLeColonne = colonneDisponibili.flatMap(g => g.colonne);
  const [selezionate, setSelezionate] = useState(new Set(tutteLeColonne));
  const [esportando, setEsportando] = useState(false);

  const toggleColonna = (col) => {
    setSelezionate(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  };
  const toggleGruppo = (gruppo, tutteAttive) => {
    setSelezionate(prev => {
      const next = new Set(prev);
      gruppo.colonne.forEach(c => tutteAttive ? next.delete(c) : next.add(c));
      return next;
    });
  };

  const handleEsporta = async () => {
    if (selezionate.size === 0) { alert('Seleziona almeno una colonna.'); return; }
    setEsportando(true);
    await onEsporta([...selezionate]);
    setEsportando(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '520px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>📊 {titolo} — Scegli colonne</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button onClick={() => setSelezionate(new Set(tutteLeColonne))}
              style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${T.border}`, background: T.bg, color: T.textSub, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
              Seleziona tutto
            </button>
            <button onClick={() => setSelezionate(new Set())}
              style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${T.border}`, background: T.bg, color: T.textSub, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
              Deseleziona tutto
            </button>
          </div>

          {colonneDisponibili.map(gruppo => {
            const tutteAttive = gruppo.colonne.every(c => selezionate.has(c));
            return (
              <div key={gruppo.gruppo} style={{ marginBottom: '18px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input type="checkbox" checked={tutteAttive} onChange={() => toggleGruppo(gruppo, tutteAttive)} style={{ accentColor: '#10b981', width: '15px', height: '15px' }} />
                  <span style={{ fontSize: '11px', fontWeight: '900', color: '#10b981', letterSpacing: '0.5px' }}>{gruppo.gruppo.toUpperCase()}</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '6px', paddingLeft: '10px' }}>
                  {gruppo.colonne.map(col => (
                    <label key={col} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selezionate.has(col)} onChange={() => toggleColonna(col)} style={{ accentColor: '#10b981', width: '13px', height: '13px', flexShrink: 0 }} />
                      <span style={{ fontSize: '11px', color: T.textSub }}>{col}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '11px', color: T.textMuted }}>{selezionate.size} colonne selezionate</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Annulla</button>
            <button onClick={handleEsporta} disabled={esportando}
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '10px', color: '#fff', cursor: esportando ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', opacity: esportando ? 0.6 : 1 }}>
              {esportando ? '⏳ Esportando...' : '📊 Esporta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function esportaStockExcel(vehicles, noteTextMap, interventiOfficina, interventiCarrozzeria, contratti, colonneSelezionate, documentiVeicoli) {
  if (bloccaExportExcel()) return;
  const sv = v => (v === undefined || v === null) ? '' : v.toString();
  const fmtE = n => n ? new Intl.NumberFormat('it-IT').format(parseFloat(n)) : '';
  const yn2 = b => b ? 'SI' : 'NO';
  const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

  const headers = [
    'Stato', 'Marca', 'Modello', 'Versione', 'Tipo', 'Classe',
    'Targa', 'Telaio (VIN)', 'Colore', 'Carburante', 'Cambio',
    'Carrozzeria', 'Categoria',
    'Km', 'Immatricolazione', 'Anno',
    'Prezzo Listino (€)', 'Prezzo Bottom (€)', 'Fornitore',
    'Giorni in Stock', 'Data Ingresso Stock', 'Alert (+150gg)', 'IVA Esposta',
    'Sede / Ubicazione', 'Optionals', 'Note', 'Documenti',
    'N. Interventi Officina', 'Costo Interventi Officina (€)', 'Dettaglio Interventi Officina',
    'N. Interventi Carrozzeria', 'Costo Interventi Carrozzeria (€)', 'Dettaglio Interventi Carrozzeria',
    'Cliente Nome', 'Cliente Cognome', 'Prezzo di Vendita (€)', 'Prezzo Servizi (€)', 'Costo Servizi (€)',
  ];

  // Trova tutti gli interventi (officina o carrozzeria) che riguardano un
  // veicolo, cercando corrispondenza per VIN o targa — normalizzati per non
  // perdere match per differenze di maiuscole/spazi.
  const trovaInterventi = (lista, vin, targa) => {
    const vinN = norm(vin), targaN = norm(targa);
    if (!vinN && !targaN) return [];
    return (lista || []).filter(i => {
      const iVin = norm(i.telaio || i.vin);
      const iTarga = norm(i.targa);
      return (vinN && iVin === vinN) || (targaN && iTarga === targaN);
    });
  };

  // Trova il contratto collegato a questo telaio/targa (se il veicolo è
  // stato venduto) — stessi dati già presenti altrove nell'app (Finance,
  // conto economico), qui semplicemente riportati anche nell'export.
  const trovaContratto = (vin, targa) => {
    const vinN = norm(vin), targaN = norm(targa);
    if (!vinN && !targaN) return null;
    return (contratti || []).find(c => {
      const cVin = norm(c.veicolo?.vin);
      const cTarga = norm(c.veicolo?.targa);
      return (vinN && cVin === vinN) || (targaN && cTarga === targaN);
    }) || null;
  };

  const righe = (vehicles || []).map(v => {
    const anno = v.registrazione ? (new Date(v.registrazione).getFullYear() || '') : '';

    const offVeicolo = trovaInterventi(interventiOfficina, v.vin, v.targa);
    const costoOfficina = offVeicolo.reduce((s, i) => s + (parseFloat(i.totaleNetto || i.totaleLordo || 0)), 0);
    const dettaglioOfficina = offVeicolo.map(i => `${(i.data || '').slice(0, 10)}: ${fmtE(i.totaleNetto || i.totaleLordo || 0)}€`).join(' | ');

    const carVeicolo = trovaInterventi(interventiCarrozzeria, v.vin, v.targa);
    const costoCarrozzeria = carVeicolo.reduce((s, i) => s + (parseFloat(i.importo || i.totale || 0)), 0);
    const dettaglioCarrozzeria = carVeicolo.map(i => `${(i.data || '').slice(0, 10)}: ${fmtE(i.importo || i.totale || 0)}€`).join(' | ');

    // Dati vendita — solo se esiste un contratto collegato a questo telaio
    const contrattoV = trovaContratto(v.vin, v.targa);
    let clienteNome = '', clienteCognome = '', prezzoVendita = '', prezzoServizi = '', costoServiziV = '';
    if (contrattoV) {
      clienteNome = contrattoV.cliente?.nome || '';
      clienteCognome = contrattoV.cliente?.cognome || contrattoV.cliente?.ragioneSociale || '';
      const pr = contrattoV.prezzo || {};
      const s = contrattoV.servizi || {};
      const pL = parseFloat(pr.prezzoListino || v.prezzo || 0);
      const sc = parseFloat(pr.sconto || 0);
      const totS = Object.entries(s).filter(([k]) => k.startsWith('costo')).reduce((t, [, val]) => t + (parseFloat(val) || 0), 0);
      const ivaImp = pr.hasIVA ? Math.round((pL - sc) * 0.22) : 0;
      prezzoVendita = pL - sc + parseFloat(pr.ipt || 0) + totS + ivaImp;
      prezzoServizi = totS > 0 ? totS : '';
      // Costo servizi REALE per l'azienda (diverso da quanto fatturato al
      // cliente) — stesso criterio usato nel conto economico: LoJack 476€,
      // Blockshaft 182€, Metasystem (testo libero) 85€.
      const costoServiziCalc = (s.lojack ? 476 : 0) + (s.blockshaft ? 182 : 0) + (/metasystem/i.test(s.altroAntifurto || '') ? 85 : 0);
      costoServiziV = costoServiziCalc > 0 ? costoServiziCalc : '';
    }

    const dataRegistrazione = v.registrazione ? (() => { const d = new Date(v.registrazione); return isNaN(d) ? '' : d; })() : '';
    const dataIngresso = v.enteredInStock ? (() => { const d = new Date(v.enteredInStock); return isNaN(d) ? '' : d; })() : '';

    // Documenti: SI se esiste almeno un documento caricato per questo
    // veicolo in documenti_veicoli/{id}, letto una tantum al momento
    // dell'export (non un listener permanente — coerente con la rimozione
    // del badge "📎 DOC" dalla lista, che scaricava questo dato per intero
    // ad ogni piccola modifica di chiunque; qui costa solo quando serve
    // davvero, cioè quando l'utente genera l'export).
    const docsVeicolo = documentiVeicoli?.[v.id];
    const haDocumenti = docsVeicolo && (Array.isArray(docsVeicolo) ? docsVeicolo.length > 0 : Object.keys(docsVeicolo).length > 0);

    return [
      sv(v.status), sv(v.marca), sv(v.modello), sv(v.versione), sv(v.tipo), sv(v.classe),
      sv(v.targa), sv(v.vin), sv(v.colore), sv(v.carburante), sv(v.gearType),
      sv(v.carrozzeria), sv(v.categoria),
      parseFloat(v.km) || 0, dataRegistrazione, parseFloat(anno) || '',
      parseFloat(v.prezzo) || 0, parseFloat(v.bottomPrice) || 0, sv(v.fornitore || 'N.D.'),
      parseFloat(v.giorni) || 0, dataIngresso, yn2(v.alert), yn2(v.ivaEsposta),
      sv(v.sede), sv(v.optionals), sv(noteTextMap?.[v.id] || ''), yn2(haDocumenti),
      offVeicolo.length, offVeicolo.length ? costoOfficina : 0, dettaglioOfficina,
      carVeicolo.length, carVeicolo.length ? costoCarrozzeria : 0, dettaglioCarrozzeria,
      clienteNome, clienteCognome, prezzoVendita || 0, prezzoServizi || 0, costoServiziV || 0,
    ];
  });

  // Se è stata passata una selezione di colonne, tiene solo quelle — per
  // indice, calcolato sui nomi header originali (nessuna riscrittura della
  // logica sopra, solo un filtro finale prima di scrivere il file).
  let headersFinali = headers, righeFinali = righe;
  if (Array.isArray(colonneSelezionate) && colonneSelezionate.length > 0) {
    const indiciTenuti = headers.map((h, i) => colonneSelezionate.includes(h) ? i : -1).filter(i => i !== -1);
    headersFinali = indiciTenuti.map(i => headers[i]);
    righeFinali = righe.map(riga => indiciTenuti.map(i => riga[i]));
  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headersFinali, ...righeFinali], { cellDates: true });
  ws['!cols'] = headersFinali.map(() => ({ wch: 18 }));
  applicaFormatiExcel(ws, headersFinali, righeFinali.length,
    ['Prezzo Listino (€)', 'Prezzo Bottom (€)', 'Costo Interventi Officina (€)', 'Costo Interventi Carrozzeria (€)', 'Prezzo di Vendita (€)', 'Prezzo Servizi (€)', 'Costo Servizi (€)'],
    ['Immatricolazione', 'Data Ingresso Stock']);
  XLSX.utils.book_append_sheet(wb, ws, 'Stock');
  const oggi = new Date();
  const tag = `${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}`;
  scriviExcel(wb, `Stock_RadaR_${tag}.xlsx`);
}

function StockView({ user, preventivi = [], contratti = [], statiVeicoliLive = {} }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile, isTablet } = useBreakpoint();
  const [vehiclesGrezzi, setVehiclesGrezzi] = useState([]);
  // "vehicles" — derivato automaticamente da vehiclesGrezzi con lo stato
  // live (status/sede) applicato sopra. Tutti i punti sotto che leggono
  // "vehicles" ricevono così sempre il dato aggiornato istantaneamente,
  // anche se vehiclesGrezzi arriva da una cache vecchia di ore — senza
  // dover toccare individualmente ogni singolo punto che lo usa.
  const vehicles = useMemo(() => {
    if (!vehiclesGrezzi.length || !statiVeicoliLive || !Object.keys(statiVeicoliLive).length) return vehiclesGrezzi;
    return vehiclesGrezzi.map(v => {
      const live = statiVeicoliLive[v.id];
      if (!live) return v;
      return { ...v, status: live.status ?? v.status, sede: live.sede ?? v.sede, bloccoAdmin: live.bloccoAdmin || null };
    });
  }, [vehiclesGrezzi, statiVeicoliLive]);
  const [veicoliInTrattativa, setVeicoliInTrattativa] = useState({});
  const [lastUpdate, setLastUpdate] = useState('');
  const [aggiungiAutoOpen, setAggiungiAutoOpen] = useState(false);
  const [refreshingStock, setRefreshingStock] = useState(false);
  const [esportandoExcel, setEsportandoExcel] = useState(false);
  const [colonnePickerOpen, setColonnePickerOpen] = useState(false);
  // ── Selezione multipla di auto per estrazione mirata ──
  const chiaveAuto = c => `${c.id}|${c.vin || ''}|${c.targa || ''}`;
  const [selezionate, setSelezionate] = useState({});          // chiave → auto
  const [pannelloSelOpen, setPannelloSelOpen] = useState(true); // tendina laterale (desktop) / foglio (mobile)
  const [exportSoloSelezionate, setExportSoloSelezionate] = useState(false);
  const nSel = Object.keys(selezionate).length;
  const toggleSel = car => setSelezionate(p => { const k = chiaveAuto(car); const n = { ...p }; if (n[k]) delete n[k]; else n[k] = car; return n; });
  const [rimuovendoDup, setRimuovendoDup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState(null);
  const [preventivocar, setPreventivocar] = useState(null); // auto per cui fare preventivo
  const [fMarca, setFMarca] = useState([]);
  const [fModello, setFModello] = useState([]);
  const [fTipo, setFTipo] = useState([]);
  const [fCategoria, setFCategoria] = useState([]);
  // ── IMPORT CSV STOCKSPARK ────────────────────────────────────────────────────
  const importCSVStockSpark = async (file) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    const sep = ';';
    const headers = parseCSVLine(lines[0], sep).map(h => h.trim().replace(/"/g, ''));

    const parseVal = (v) => (v || '').trim().replace(/"/g, '');
    // Formato italiano: il punto è separatore delle migliaia, la virgola è
    // il decimale (es. "36.885" = 36885, "36.885,50" = 36885,50). Prima si
    // rimuovono tutti i punti, poi si converte l'eventuale virgola in punto
    // decimale per parseFloat — altrimenti "36.885" viene letto come 36,885.
    // Numeri in formato italiano: il punto è di solito separatore delle
    // migliaia, la virgola il decimale (es. "36.885" = 36885). MA se non
    // c'è virgola e il punto è seguito da 1-2 cifre a fine numero (es.
    // "36885.5" o "36885.50"), è un vero decimale e va preservato — non
    // rimosso, altrimenti "36885.50" diventerebbe 3688550 (100 volte troppo).
    // Un punto seguito da esattamente 3 cifre, invece, è quasi certamente
    // un raggruppamento delle migliaia e viene eliminato.
    const parseNum = (v) => {
      let s = parseVal(v);
      if (!s) return 0;
      if (s.includes(',')) {
        s = s.replace(/\./g, '').replace(',', '.');
      } else {
        s = s.replace(/\.(\d{3})(?!\d)/g, '$1');
      }
      return parseFloat(s) || 0;
    };
    // Match header tollerante: case-insensitive + accetta nomi alternativi (StockSpark a volte cambia dicitura)
    const findHeaderIdx = (nomi) => {
      const list = Array.isArray(nomi) ? nomi : [nomi];
      for (const n of list) {
        const idx = headers.findIndex(h => h.toLowerCase().trim() === n.toLowerCase().trim());
        if (idx !== -1) return idx;
      }
      return -1;
    };
    const getH = (row, nome) => { const i = findHeaderIdx(nome); return i === -1 ? '' : parseVal(row[i] || ''); };
    const getN = (row, nome) => { const i = findHeaderIdx(nome); return i === -1 ? 0 : parseNum(row[i] || ''); };

    const veicoli = [];
    for (let i = 1; i < lines.length; i++) {
      const row = parseCSVLine(lines[i], sep);
      if (row.length < 5) continue;
      const vin   = getH(row, ['Vin', 'Telaio (VIN)', 'Telaio']);
      const targa = getH(row, 'Targa');
      if (!vin && !targa) continue;

      const id = vin || targa || `ss_${Date.now()}_${i}`;
      const ivaValRaw = getH(row, 'IVA esposta').toLowerCase().trim();
      // Tri-stato: true/false solo se il file lo dichiara esplicitamente.
      // Colonna assente o valore non riconosciuto → non impostare il campo
      // (undefined), altrimenti un dato mancante finisce per essere mostrato
      // come "Art. 36 (Margine)" anche quando semplicemente non si sa.
      const ivaEsposta = ['sì','si','yes','true','1'].includes(ivaValRaw) ? true
                        : ['no','nò','false','0'].includes(ivaValRaw) ? false
                        : undefined;
      const statoRaw = getH(row, 'Stato').toLowerCase().trim();
      // Tri-stato, come ivaEsposta: solo se il file dice chiaramente qualcosa
      // di riconoscibile. Colonna vuota/valore ambiguo → non impostare nulla,
      // così il merge selettivo mantiene lo stato già presente in stock
      // invece di forzarlo a un default. Valori allineati al resto dell'app
      // (FREE/VENDUTA/RESERVED), non l'italiano usato qui in passato.
      const status = (statoRaw.includes('liber') || statoRaw === 'free') ? 'FREE'
                    : statoRaw.includes('vendut') ? 'VENDUTA'
                    : (statoRaw.includes('opzionat') || statoRaw.includes('prenotat') || statoRaw.includes('riservat') || statoRaw === 'reserved') ? 'RESERVED'
                    : undefined;

      // Fix bug import StockSpark: alcuni veicoli "Sportequipe 8 GT" arrivano
      // con il modello scritto male (es. "Sportequipe 7GTW") — ma la
      // descrizione/allestimento riporta comunque il nome corretto. Se lo
      // troviamo lì, forziamo il modello a quello giusto, a prescindere da
      // cosa dice la colonna Modello.
      const modelloRaw = getH(row, 'Modello');
      const allestimentoRaw = getH(row, ['Allestimento', 'Versione']);
      const opzionaleRaw = getH(row, ['Opzionale', 'Descrizione', 'Note']);
      const testoDescrittivo = `${modelloRaw} ${allestimentoRaw} ${opzionaleRaw}`;
      const modelloCorretto = /sportequipe\s*8\s*gt/i.test(testoDescrittivo) ? 'Sportequipe 8 GT' : modelloRaw;

      veicoli.push({
        id,
        vin,
        targa,
        marca:         getH(row, 'Marca'),
        modello:       modelloCorretto,
        versione:      allestimentoRaw,
        optionals:     opzionaleRaw,
        tipo:          getH(row, 'Tipo'),
        registrazione: getH(row, 'Immatricolazione'),
        km:            getN(row, 'Km'),
        carburante:    getH(row, ['Alimentazione', 'Carburante']),
        gearType:      getH(row, 'Cambio'),
        colore:        getH(row, ['Colore', 'Colore esterno', 'Colore carrozzeria', 'Colore Esterno', 'Color']),
        kw:            getN(row, 'Kw'),
        hp:            getN(row, 'CV'),
        cc:            getN(row, 'Cilindrata'),
        carrozzeria:   getH(row, 'Carrozzeria'),
        categoria:     classificaCategoriaVeicolo(getH(row, 'Carrozzeria')),
        prezzo:        getN(row, ['Prezzo al pubblico (B2C)', 'Prezzo Listino (€)', 'Prezzo Listino']),
        prezzoPartner: getN(row, 'Prezzo partner') || getN(row, 'Prezzo commercianti (B2B)'),
        bottomPrice:   getN(row, ['Prezzo minimo di vendita', 'Prezzo Minimo Vendita', 'Prezzo Minimo', 'Bottom Price', 'Prezzo min. vendita', 'Prezzo Bottom', 'Prezzo Bottom (€)']),
        ...(ivaEsposta !== undefined ? { ivaEsposta } : {}),
        ...(status !== undefined ? { status } : {}),
        giorni:        0,
        sede:          getH(row, ['Concessionario', 'Sede / Ubicazione', 'Sede', 'Ubicazione']) || 'Agnano',
        fonte:         'StockSpark',
        importatoAt:   new Date().toISOString(),
      });
    }

    if (!veicoli.length) { alert('Nessun veicolo trovato nel file.'); return; }

    setImportando(true);
    try {
      // Leggi stock esistente per upsert da stock_data
      const snap = await get(ref(db, 'stock_data'));
      const existing = snap.exists() ? (snap.val().vehicles || []) : [];

      // Normalizzazione per il matching: senza questa, differenze di
      // maiuscole/minuscole o spazi tra il file e i dati già salvati fanno
      // fallire il match, trattando un veicolo già esistente come "nuovo"
      // e duplicandolo — causa più probabile di duplicati in stock.
      const normPlate = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

      // Costruisci mappa targa/vin → indice esistente
      const idxByVin   = {};
      const idxByTarga = {};
      existing.forEach((v, i) => {
        if (v?.vin)   idxByVin[normPlate(v.vin)]     = i;
        if (v?.targa) idxByTarga[normPlate(v.targa)] = i;
      });

      // Merge selettivo: aggiorna un campo esistente SOLO se il file importato
      // porta un valore reale per quel campo. Un campo vuoto/0 nel file NON
      // cancella mai un dato già presente in stock — evita di "svuotare"
      // veicoli già completi quando il file di import è parziale.
      const mergeSelettivo = (esistente, nuovo) => {
        const risultato = { ...esistente };
        Object.entries(nuovo).forEach(([k, val]) => {
          if (val === undefined || val === null || val === '') return;
          if (typeof val === 'number' && val === 0) return;
          if (Array.isArray(val) && val.length === 0) return;
          risultato[k] = val;
        });
        return risultato;
      };

      const merged = [...existing];
      let nuovi = 0, aggiornati = 0, ignorati = 0;
      veicoli.forEach(v => {
        const idx = (v.vin && idxByVin[normPlate(v.vin)] !== undefined ? idxByVin[normPlate(v.vin)] : null)
                 ?? (v.targa && idxByTarga[normPlate(v.targa)] !== undefined ? idxByTarga[normPlate(v.targa)] : null);
        if (idx !== null) {
          // Auto già esistente — AGGIORNA solo i campi che il file porta
          // davvero valorizzati (es. bottom price, status), protegge sempre
          // giorni in stock e data ingresso, che RadaR gestisce da solo.
          const esistente = merged[idx];
          // Se il regime IVA (ivaEsposta / badge Art.36) è stato corretto
          // manualmente e bloccato (es. da fix-iva-esposta-art36.js), l'import
          // non deve più poterlo sovrascrivere — anche se il file porta un
          // valore diverso per "IVA esposta". Stesso discorso per la sede:
          // una volta che un DDT del modulo Logistica ha spostato il
          // veicolo, RadaR diventa l'autorità sulla sua ubicazione — SSK
          // non deve più poterla riportare indietro al vecchio valore.
          let vDaImportare = v;
          if (esistente.ivaEspostaLocked) {
            const { ivaEsposta, ...resto } = vDaImportare;
            vDaImportare = resto;
          }
          if (esistente.sedeLocked) {
            const { sede, ...resto } = vDaImportare;
            vDaImportare = resto;
          }
          merged[idx] = {
            ...mergeSelettivo(esistente, vDaImportare),
            // L'id NON va mai sovrascritto: le note (note_telaio/{id}) sono
            // agganciate a questo id specifico. Se cambiasse anche di poco
            // (case diversa, VIN vs targa) le note esistenti diventerebbero
            // orfane — sembrerebbero sparite pur restando su Firebase.
            id: esistente.id,
            ...(esistente.giorni !== undefined ? { giorni: esistente.giorni } : {}),
            ...(esistente.enteredInStock !== undefined ? { enteredInStock: esistente.enteredInStock } : {}),
            fonte: esistente.fonte || 'StockSpark',
          };
          aggiornati++;
        } else if (v.marca && v.modello) {
          // Auto nuova — SOLO se la riga ha almeno marca e modello, cioè è
          // davvero un veicolo completo (export StockSpark pieno) e non una
          // semplice riga di aggiornamento parziale (es. file "solo VIN +
          // prezzo minimo") che non deve mai generare veicoli fantasma.
          // Registriamo subito la sua posizione nelle mappe targa/VIN: se una
          // riga successiva dello STESSO file ha la stessa targa (capita
          // negli export), viene riconosciuta e aggiornata invece di creare
          // un secondo doppione.
          const nuovoIdx = merged.length;
          merged.push({ ...v, status: v.status || 'FREE', fonte: 'StockSpark' });
          if (v.vin)   idxByVin[normPlate(v.vin)]     = nuovoIdx;
          if (v.targa) idxByTarga[normPlate(v.targa)] = nuovoIdx;
          nuovi++;
        } else {
          // Telaio/targa non trovato in stock E la riga non ha dati
          // sufficienti per essere un veicolo vero: viene ignorata, non
          // inserisce nulla. Questo è il comportamento richiesto per i file
          // di solo aggiornamento (es. VIN + prezzo minimo di vendita).
          ignorati++;
        }
      });

      await set(ref(db, 'stock_data'), {
        vehicles: merged,
        lastUpdate: new Date().toISOString(),
        importSource: 'StockSpark CSV',
      });
      window._radarStockCache = null;
      window._radarStockTs = null;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e2) {}

      alert(`✅ Import completato!\n🆕 Nuovi: ${nuovi}\n🔄 Aggiornati (già presenti, dati sovrascritti): ${aggiornati}${ignorati > 0 ? `\n⏭️ Ignorati (telaio/targa non trovati, dati insufficienti per un nuovo veicolo): ${ignorati}` : ''}`);
      setTimeout(() => window.location.reload(), 500);
    } catch(e) {
      alert('❌ Errore durante import: ' + e.message);
    } finally {
      setImportando(false);
    }
  };

  // ─── Aggiorna SOLO il bottom price — nessun altro campo viene toccato ───
  // Match sul telaio (VIN), con targa come fallback. Se il file contiene
  // altre colonne (marca, prezzo, km, ecc.) vengono ignorate del tutto,
  // anche se valorizzate — a differenza dell'import CSV generale, qui
  // SOLO bottomPrice può cambiare. Nessun veicolo nuovo viene mai creato.
  const importaSoloBottomPrice = async (file) => {
    if (!file) return;
    setImportandoBP(true);
    try {
      const isExcel = /\.xlsx?$/i.test(file.name);
      let headers, righeDati; // righeDati: array di array di stringhe (celle), stesso formato per CSV e Excel

      if (isExcel) {
        // File Excel vero (es. lo stesso export di Stock riscaricato e
        // modificato) — letto come binario con la libreria XLSX, non come
        // testo semplice.
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const wsNome = wb.SheetNames[0];
        const matrice = XLSX.utils.sheet_to_json(wb.Sheets[wsNome], { header: 1, raw: false, defval: '' });
        if (!matrice.length) { alert('File Excel vuoto o illeggibile.'); setImportandoBP(false); return; }
        headers = matrice[0].map(h => (h || '').toString().trim());
        righeDati = matrice.slice(1).filter(r => r.some(cella => (cella || '').toString().trim()));
      } else {
        const text = await file.text();
        const lines = text.split('\n').filter(l => l.trim());
        const sep = ';';
        headers = parseCSVLine(lines[0], sep).map(h => h.trim().replace(/"/g, ''));
        righeDati = lines.slice(1).map(l => parseCSVLine(l, sep));
      }

      const parseVal = (v) => (v || '').toString().trim().replace(/"/g, '');
      const parseNum = (v) => {
        let s = parseVal(v);
        if (!s) return 0;
        if (s.includes(',')) { s = s.replace(/\./g, '').replace(',', '.'); }
        else { s = s.replace(/\.(\d{3})(?!\d)/g, '$1'); }
        return parseFloat(s) || 0;
      };
      const findHeaderIdx = (nomi) => {
        const list = Array.isArray(nomi) ? nomi : [nomi];
        for (const n of list) {
          const idx = headers.findIndex(h => h.toLowerCase().trim() === n.toLowerCase().trim());
          if (idx !== -1) return idx;
        }
        return -1;
      };
      const getH = (row, nome) => { const i = findHeaderIdx(nome); return i === -1 ? '' : parseVal(row[i] || ''); };
      const getN = (row, nome) => { const i = findHeaderIdx(nome); return i === -1 ? 0 : parseNum(row[i] || ''); };
      const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (!stockSnap.exists()) { alert('Stock non trovato.'); setImportandoBP(false); return; }
      const raw = stockSnap.val();
      const arr = Array.isArray(raw) ? raw : Object.values(raw);

      const idxByVin = {}, idxByTarga = {};
      arr.forEach((v, i) => {
        if (v.vin) idxByVin[norm(v.vin)] = i;
        if (v.targa) idxByTarga[norm(v.targa)] = i;
      });

      let aggiornati = 0, nonTrovati = 0, ignorati = 0, numRiga = 1;
      const nonTrovatiList = [];
      const merged = [...arr];

      for (const row of righeDati) {
        numRiga++;
        const vin = getH(row, ['Telaio', 'Vin', 'VIN', 'Telaio (VIN)']);
        const targa = getH(row, ['Targa']);
        const bp = getN(row, ['Prezzo minimo di vendita', 'Prezzo Minimo Vendita', 'Prezzo Minimo', 'Bottom Price', 'Prezzo min. vendita', 'Prezzo Bottom', 'Prezzo Bottom (€)']);

        if (!bp || bp <= 0) { ignorati++; continue; } // niente da aggiornare per questa riga


        const idx = (vin && idxByVin[norm(vin)] !== undefined ? idxByVin[norm(vin)] : null)
                 ?? (targa && idxByTarga[norm(targa)] !== undefined ? idxByTarga[norm(targa)] : null);

        if (idx === null) {
          nonTrovati++;
          nonTrovatiList.push(vin || targa || `riga ${numRiga}`);
          continue;
        }

        // SOLO bottomPrice viene scritto. Nessun altro campo del veicolo
        // esistente viene toccato, qualunque altra colonna ci sia nel file.
        merged[idx] = { ...merged[idx], bottomPrice: bp };
        aggiornati++;
      }

      await set(ref(db, 'stock_data/vehicles'), merged);
      window._radarStockCache = null;
      window._radarStockTs = null;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e2) {}

      if (aggiornati === 0) {
        alert(`⚠️ NESSUN PREZZO AGGIORNATO!\n\nIl file è stato letto ma nessuna riga aveva un prezzo valido da applicare — probabilmente il nome della colonna nel file non è tra quelli riconosciuti, oppure nessun telaio/targa combacia con lo stock.\n\n⏭️ Righe ignorate (senza prezzo valido): ${ignorati}${nonTrovati > 0 ? `\n⚠️ Telaio/targa non trovati (${nonTrovati}): ${nonTrovatiList.slice(0, 10).join(', ')}${nonTrovatiList.length > 10 ? '...' : ''}` : ''}\n\nControlla che la colonna del prezzo nel file si chiami esattamente "Prezzo Bottom (€)", "Bottom Price" o simile.`);
      } else {
        alert(`✅ Aggiornamento bottom price completato!\n💰 Aggiornati: ${aggiornati}\n⏭️ Righe senza prezzo valido (ignorate): ${ignorati}${nonTrovati > 0 ? `\n⚠️ Telaio/targa non trovati in stock (${nonTrovati}): ${nonTrovatiList.slice(0, 10).join(', ')}${nonTrovatiList.length > 10 ? '...' : ''}` : ''}`);
      }
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      alert('❌ Errore durante l\'aggiornamento: ' + e.message);
    } finally {
      setImportandoBP(false);
    }
  };

  // Aggiorna il bottom price di UN SOLO veicolo — scrittura mirata al
  // singolo campo, mai sull'intero array stock_data/vehicles (diverso dal
  // bulk import sopra, che per sua natura tocca più righe insieme).
  const aggiornaBottomPrice = async (car, nuovoValore) => {
    try {
      const val = await sincronizzaPrezzoAcquisto({ vin: car.vin, targa: car.targa, veicoloId: car.id, valore: nuovoValore });
      // Aggiornamento locale immediato — l'utente vede subito il nuovo
      // valore sulla card, senza aspettare nessun ciclo di sync.
      setVehiclesGrezzi(prev => prev.map(v =>
        ((car.vin && v.vin === car.vin) || (car.targa && v.targa === car.targa) || v.id === car.id)
          ? { ...v, bottomPrice: val } : v
      ));
    } catch (e) {
      alert('❌ Errore aggiornamento prezzo d\'acquisto: ' + e.message);
    }
  };

  const [importando, setImportando] = useState(false);
  const inputImportRef = useRef(null);
  const [importandoBP, setImportandoBP] = useState(false);
  const inputBottomPriceRef = useRef(null);
  const [importandoForn, setImportandoForn] = useState(false);
  const inputFornitoreRef = useRef(null);

  // ─── Import massivo FORNITORE — a differenza del bottom price, qui ogni
  // veicolo dello stock riceve SEMPRE un valore: quello dal file se trovato
  // e valorizzato, altrimenti "N.D." esplicito (mai lasciato vuoto/assente).
  const importaFornitori = async (file) => {
    if (!file) return;
    setImportandoForn(true);
    try {
      const isExcel = /\.xlsx?$/i.test(file.name);
      let headers, righeDati;

      if (isExcel) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const wsNome = wb.SheetNames[0];
        const matrice = XLSX.utils.sheet_to_json(wb.Sheets[wsNome], { header: 1, raw: false, defval: '' });
        if (!matrice.length) { alert('File Excel vuoto o illeggibile.'); setImportandoForn(false); return; }
        headers = matrice[0].map(h => (h || '').toString().trim());
        righeDati = matrice.slice(1).filter(r => r.some(cella => (cella || '').toString().trim()));
      } else {
        const text = await file.text();
        const lines = text.split('\n').filter(l => l.trim());
        const sep = ';';
        headers = parseCSVLine(lines[0], sep).map(h => h.trim().replace(/"/g, ''));
        righeDati = lines.slice(1).map(l => parseCSVLine(l, sep));
      }

      const parseVal = (v) => (v || '').toString().trim().replace(/"/g, '');
      const findHeaderIdx = (nomi) => {
        const list = Array.isArray(nomi) ? nomi : [nomi];
        for (const n of list) {
          const idx = headers.findIndex(h => h.toLowerCase().trim() === n.toLowerCase().trim());
          if (idx !== -1) return idx;
        }
        return -1;
      };
      const getH = (row, nome) => { const i = findHeaderIdx(nome); return i === -1 ? '' : parseVal(row[i] || ''); };
      const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (!stockSnap.exists()) { alert('Stock non trovato.'); setImportandoForn(false); return; }
      const raw = stockSnap.val();
      const arr = Array.isArray(raw) ? raw : Object.values(raw);

      // Mappa targa/VIN → fornitore, dal file caricato.
      const fornitorePerVin = {}, fornitorePerTarga = {};
      for (const row of righeDati) {
        const vin = norm(getH(row, ['Telaio', 'Vin', 'VIN', 'Telaio (VIN)']));
        const targa = norm(getH(row, ['Targa']));
        const fornitore = getH(row, ['Fornitore', 'Fornitore Auto', 'Provenienza']);
        if (!fornitore) continue; // riga senza fornitore indicato: non sovrascrive nulla per questo veicolo
        if (vin) fornitorePerVin[vin] = fornitore;
        if (targa) fornitorePerTarga[targa] = fornitore;
      }

      let daFile = 0, comeND = 0;
      const merged = arr.map(v => {
        const vin = norm(v.vin), targa = norm(v.targa);
        const fornitore = fornitorePerVin[vin] || fornitorePerTarga[targa];
        if (fornitore) { daFile++; return { ...v, fornitore }; }
        comeND++;
        return { ...v, fornitore: v.fornitore || 'N.D.' }; // non tocca chi ha già un fornitore valido salvato in precedenza
      });

      await set(ref(db, 'stock_data/vehicles'), merged);
      window._radarStockCache = null;
      window._radarStockTs = null;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e2) {}

      alert(`✅ Fornitori aggiornati!\n📦 Assegnati dal file: ${daFile}\n❓ Impostati a N.D. (non trovati nel file o già N.D.): ${comeND}`);
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      alert('❌ Errore durante l\'importazione: ' + e.message);
    } finally {
      setImportandoForn(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────
  const [fCarburante, setFCarburante] = useState([]);
  const [fStatus, setFStatus] = useState([]);
  const [fSede, setFSede] = useState([]);
  const [fGearType, setFGearType] = useState([]);
  const [fVersione, setFVersione] = useState([]);
  const [fFornitore, setFFornitore] = useState([]);
  const [fNote, setFNote] = useState('');           // testo cercato in note / etichette del telaio
  const deferredNote = useDeferredValue(fNote);
  const [fAnnoMin, setFAnnoMin] = useState('');
  const [fAnnoMax, setFAnnoMax] = useState('');
  const [fPrezzoMin, setFPrezzoMin] = useState('');
  const [fPrezzoMax, setFPrezzoMax] = useState('');
  const [onlyAlert, setOnlyAlert] = useState(false);
  const [showVendute, setShowVendute] = useState(false);
  const [sortBy, setSortBy] = useState('giorni');
  const [search, setSearch] = useState('');
  const [statsOpen, setStatsOpen] = useState(false);
  const [filtriAperti, setFiltriAperti] = useState(false);

  useEffect(() => {
    // Usa cache locale se già caricato in sessione
    // Prova prima localStorage (persiste tra sessioni), poi window (fallback)
    const lsCache = (() => { try { return JSON.parse(localStorage.getItem('radar_stock_cache')); } catch { return null; } })();
    const lsTs    = parseInt(localStorage.getItem('radar_stock_ts') || '0');
    const now = Date.now();
    const TTL = 4 * 60 * 60 * 1000; // 4 ore — lo stato veicolo/ubicazione ora arriva live tramite il canale separato, quindi lo stock completo può permettersi di essere raro

    if (lsCache && (now - lsTs) < TTL) {
      const toArr = (d) => d ? (Array.isArray(d) ? d : Object.entries(d).map(([id,v]) => ({...v, id}))) : [];
      setVehiclesGrezzi(toArr(lsCache.vehicles));
      if (lsCache.lastUpdate) setLastUpdate(new Date(lsCache.lastUpdate).toLocaleString('it-IT'));
      setLoading(false);
      window._radarStockCache = lsCache;
      window._radarStockTs = lsTs;
    } else if (window._radarStockCache) {
      const data = window._radarStockCache;
      setVehiclesGrezzi(data.vehicles ? (Array.isArray(data.vehicles) ? data.vehicles : Object.entries(data.vehicles).map(([id,v]) => ({...v, id}))) : []); setLastUpdate(new Date(data.lastUpdate).toLocaleString('it-IT')); setLoading(false);
    }
    if (!window._radarStockTs || now - window._radarStockTs > TTL) {
      // Collegamento diretto Firebase — onValue puro, stesso motivo di
      // Contratti/Preventivi: il polling consuma di più con poca attività.
      get(ref(db, 'stock_data')).then(snapshot => {
        const data = snapshot.val();
        if (data && data.vehicles) {
          window._radarStockCache = data;
          window._radarStockTs = now;
          try { localStorage.setItem('radar_stock_cache', JSON.stringify(data)); localStorage.setItem('radar_stock_ts', String(now)); } catch(e) { /* localStorage pieno */ }
          setVehiclesGrezzi(data.vehicles ? (Array.isArray(data.vehicles) ? data.vehicles : Object.entries(data.vehicles).map(([id,v]) => ({...v, id}))) : []); setLastUpdate(new Date(data.lastUpdate).toLocaleString('it-IT'));
        }
        setLoading(false);
      });
    } else { setLoading(false); }
    // Mantieni onValue solo per veicoli_in_trattativa (cambia frequentemente)
  }, []);

  // ── Note per telaio: caricate in blocco così il badge viola compare
  // subito in lista, senza dover prima aprire il dettaglio del veicolo ──
  const [noteCountMap, setNoteCountMap] = useState({});
  const [noteTextMap, setNoteTextMap] = useState({});
  useEffect(() => {
    // Stesso principio già applicato a ODL/interventi/contratti — le note
    // non richiedono aggiornamento istantaneo per tutti, solo per chi le
    // scrive/legge attivamente (quello resta comunque coperto perché
    // l'utente stesso vede subito la propria nota appena salvata, tramite
    // lo state locale del componente di dettaglio, non da qui).
    let annullato = false;
    const now = Date.now();
    const elabora = (data) => {
      const map = {}; const textMap = {};
      Object.entries(data || {}).forEach(([carId, notes]) => {
        const arr = notes ? (Array.isArray(notes) ? notes : Object.values(notes)) : [];
        if (arr.length > 0) {
          map[carId] = arr.length;
          textMap[carId] = arr.map(n => `${n.testo || ''}${n.autore ? ` (${n.autore}` : ''}${n.data ? `, ${new Date(n.data).toLocaleDateString('it-IT')}` : ''}${n.autore ? ')' : ''}`).join(' | ');
        }
      });
      return { map, textMap };
    };
    if (window._radarNoteTelaioTs && (now - window._radarNoteTelaioTs) < 4*60*60*1000) {
      setNoteCountMap(window._radarNoteTelaioMap || {});
      setNoteTextMap(window._radarNoteTelaioTextMap || {});
      return;
    }
    idbLeggi('note_telaio_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarNoteTelaioMap = cache.map; window._radarNoteTelaioTextMap = cache.textMap; window._radarNoteTelaioTs = cache.ts;
        setNoteCountMap(cache.map); setNoteTextMap(cache.textMap);
        return;
      }
      get(ref(db, 'note_telaio')).then(snap => {
        if (annullato) return;
        const { map, textMap } = elabora(snap.val());
        const ts = Date.now();
        window._radarNoteTelaioMap = map; window._radarNoteTelaioTextMap = textMap; window._radarNoteTelaioTs = ts;
        idbScrivi('note_telaio_cache', { map, textMap, ts });
        setNoteCountMap(map); setNoteTextMap(textMap);
      });
    });
    return () => { annullato = true; };
  }, []);

  // Badge "📎 DOC" rimosso: scaricava l'intero nodo documenti_veicoli
  // (metadati di TUTTI i documenti di TUTTI i veicoli) ad ogni singolo
  // upload/eliminazione di qualsiasi collega, per chiunque avesse la tab
  // Stock aperta. Il tab "📎 Documenti" nella scheda del singolo veicolo
  // resta invariato (legge solo i documenti di quel veicolo).

  useEffect(() => {
    const unsubTrattativa = onValue(ref(db, 'veicoli_in_trattativa'), (snap) => {
      setVeicoliInTrattativa(snap.val() || {});
    });
    return () => unsubTrattativa();
  }, []);

  // Normalizza per confronti case-insensitive: marca/modello/tipo/ecc. possono
  // arrivare da fonti diverse (sync automatico vs import CSV) con scritture
  // diverse per lo stesso valore (es. "Dacia" vs "DACIA") — senza normalizzare,
  // il menu a tendina le tratta come opzioni distinte e il filtro ne perde
  // metà, pur trovandole tutte con la ricerca libera (che già normalizza).
  const normCI = s => (s || '').toString().trim().toLowerCase();
  const [pagina, setPagina] = useState(1);
  const PAGE_SIZE = 60;

  // Reset pagina quando cambiano i filtri
  useEffect(() => { setPagina(1); }, [fMarca, fModello, fTipo, fCategoria, fCarburante, fStatus, fSede, fGearType, fVersione, fFornitore, deferredNote, noteTextMap, search, onlyAlert, showVendute, fAnnoMin, fAnnoMax, fPrezzoMin, fPrezzoMax]);

  const deferredSearch = useDeferredValue(search);
  const deferredShowVendute = useDeferredValue(showVendute);
  const matchAny = (selezionati, valore) => !selezionati.length || selezionati.some(s => normCI(s) === normCI(valore));

  // Predicato di filtro condiviso — "escludi" permette di calcolare le
  // opzioni di un menu ignorando SOLO quel filtro specifico, così il menu
  // "Marca" mostra le marche disponibili dato il resto dei filtri attivi
  // (compreso lui stesso quando non stiamo escludendolo). Questo rende i
  // filtri "intelligenti": un'opzione sparisce dal menu se, con gli altri
  // filtri correnti, nessun veicolo la soddisfa più, e ricompare da sola
  // appena torna disponibile.
  const passaFiltri = (v, escludi) => {
    // I partner vedono solo veicoli con un prezzo commercianti (B2B) valorizzato
    if (user?.ruolo === 'partner' && !(parseFloat(v.prezzoPartner) > 0)) return false;
    if (escludi !== 'vendute' && !deferredShowVendute && (v.status === 'VENDUTA' || v.status === 'SOLD')) return false;
    if (escludi !== 'alert' && onlyAlert && v.giorni <= 150) return false;
    if (escludi !== 'marca' && !matchAny(fMarca, v.marca)) return false;
    if (escludi !== 'modello' && !matchAny(fModello, v.modello)) return false;
    if (escludi !== 'tipo' && !matchAny(fTipo, v.tipo)) return false;
    if (escludi !== 'categoria' && !matchAny(fCategoria, v.categoria)) return false;
    if (escludi !== 'carburante' && !matchAny(fCarburante, v.carburante)) return false;
    if (escludi !== 'status' && fStatus.length && !fStatus.includes(v.status)) return false;
    if (escludi !== 'sede' && !matchAny(fSede, v.sede)) return false;
    if (escludi !== 'gearType' && !matchAny(fGearType, v.gearType)) return false;
    if (escludi !== 'versione' && !matchAny(fVersione, v.versione)) return false;
    if (escludi !== 'fornitore' && !matchAny(fFornitore, v.fornitore || 'N.D.')) return false;
    if (escludi !== 'note' && deferredNote.trim()) {
      const testoNote = `${noteTextMap[v.id] || ''} ${v.noteInserimento || ''} ${v.etichetta || ''}`.toLowerCase();
      if (!testoNote.includes(deferredNote.trim().toLowerCase())) return false;
    }
    if (escludi !== 'search' && deferredSearch && !`${v.marca} ${v.modello} ${v.versione} ${v.targa} ${v.vin||''}`.toLowerCase().includes(deferredSearch.toLowerCase())) return false;
    if (escludi !== 'anno' && (fAnnoMin || fAnnoMax)) {
      const anno = parseInt((v.registrazione || '').split('/').pop()) || 0;
      if (fAnnoMin && anno < parseInt(fAnnoMin)) return false;
      if (fAnnoMax && anno > parseInt(fAnnoMax)) return false;
    }
    if (escludi !== 'prezzo' && fPrezzoMin && v.prezzo < parseFloat(fPrezzoMin)) return false;
    if (escludi !== 'prezzo' && fPrezzoMax && v.prezzo > parseFloat(fPrezzoMax)) return false;
    return true;
  };

  const getOpts = key => {
    const escludeMap = { marca: 'marca', modello: 'modello', tipo: 'tipo', categoria: 'categoria', carburante: 'carburante', gearType: 'gearType', status: 'status', sede: 'sede', versione: 'versione', fornitore: 'fornitore' };
    const escludi = escludeMap[key];
    const canonici = new Map(); // valore normalizzato → prima scrittura incontrata
    (vehicles || []).forEach(v => {
      const raw = v[key];
      if (!raw) return;
      if (!passaFiltri(v, escludi)) return; // "intelligente": nasconde opzioni senza veicoli disponibili
      const k = normCI(raw);
      if (!canonici.has(k)) canonici.set(k, raw.toString().trim());
    });
    return [...canonici.values()].sort();
  };
  const modelliOptions = useMemo(() => {
    const canonici = new Map();
    (vehicles || []).forEach(v => {
      if (!v.modello) return;
      if (!passaFiltri(v, 'modello')) return;
      const k = normCI(v.modello);
      if (!canonici.has(k)) canonici.set(k, v.modello.toString().trim());
    });
    return [...canonici.values()].sort();
  }, [vehicles, fMarca, fTipo, fCategoria, fCarburante, fGearType, fVersione, fFornitore, deferredNote, noteTextMap, fStatus, fSede, deferredSearch, onlyAlert, deferredShowVendute, fAnnoMin, fAnnoMax, fPrezzoMin, fPrezzoMax]);

  const filtered = useMemo(() => (vehicles || [])
    .filter(v => passaFiltri(v, null))
    .sort((a, b) => sortBy === 'giorni' ? b.giorni - a.giorni : sortBy === 'ultimi_arrivi' ? a.giorni - b.giorni : sortBy === 'prezzo_asc' ? a.prezzo - b.prezzo : sortBy === 'prezzo_desc' ? b.prezzo - a.prezzo : b.km - a.km),
    [vehicles, fMarca, fModello, fTipo, fCategoria, fCarburante, fStatus, fSede, fGearType, fVersione, fFornitore, deferredNote, noteTextMap, deferredSearch, onlyAlert, sortBy, deferredShowVendute, fAnnoMin, fAnnoMax, fPrezzoMin, fPrezzoMax]);

  const paginatedVehicles = useMemo(() => filtered.slice(0, pagina * PAGE_SIZE), [filtered, pagina]);

  // Chip dei filtri attivi — rimovibili singolarmente, mostrano a colpo
  // d'occhio cosa è filtrato senza dover riaprire il pannello.
  const chipsAttivi = useMemo(() => {
    const chips = [];
    fMarca.forEach(v => chips.push({ label: v, onRemove: () => setFMarca(p => p.filter(x => x !== v)) }));
    fModello.forEach(v => chips.push({ label: v, onRemove: () => setFModello(p => p.filter(x => x !== v)) }));
    fTipo.forEach(v => chips.push({ label: v, onRemove: () => setFTipo(p => p.filter(x => x !== v)) }));
    fCategoria.forEach(v => chips.push({ label: v, onRemove: () => setFCategoria(p => p.filter(x => x !== v)) }));
    fCarburante.forEach(v => chips.push({ label: v, onRemove: () => setFCarburante(p => p.filter(x => x !== v)) }));
    fGearType.forEach(v => chips.push({ label: v, onRemove: () => setFGearType(p => p.filter(x => x !== v)) }));
    fStatus.forEach(v => chips.push({ label: v, onRemove: () => setFStatus(p => p.filter(x => x !== v)) }));
    fSede.forEach(v => chips.push({ label: v, onRemove: () => setFSede(p => p.filter(x => x !== v)) }));
    fVersione.forEach(v => chips.push({ label: `Versione: ${v}`, onRemove: () => setFVersione(p => p.filter(x => x !== v)) }));
    fFornitore.forEach(v => chips.push({ label: `Fornitore: ${v}`, onRemove: () => setFFornitore(p => p.filter(x => x !== v)) }));
    if (fNote.trim()) chips.push({ label: `Note: "${fNote.trim()}"`, onRemove: () => setFNote('') });
    if (fAnnoMin || fAnnoMax) chips.push({ label: `Anno ${fAnnoMin || '…'}–${fAnnoMax || '…'}`, onRemove: () => { setFAnnoMin(''); setFAnnoMax(''); } });
    if (fPrezzoMin || fPrezzoMax) chips.push({ label: `€ ${fPrezzoMin || '0'}–${fPrezzoMax || '∞'}`, onRemove: () => { setFPrezzoMin(''); setFPrezzoMax(''); } });
    if (onlyAlert) chips.push({ label: '🟠 Alert +150gg', onRemove: () => setOnlyAlert(false) });
    if (showVendute) chips.push({ label: '🚗 Include Vendute', onRemove: () => setShowVendute(false) });
    return chips;
  }, [fMarca, fModello, fTipo, fCategoria, fCarburante, fGearType, fVersione, fFornitore, deferredNote, noteTextMap, fStatus, fSede, fAnnoMin, fAnnoMax, fPrezzoMin, fPrezzoMax, onlyAlert, showVendute]);
  const nFiltriAttivi = fMarca.length + fModello.length + fTipo.length + fCategoria.length + fCarburante.length + fGearType.length + fStatus.length + fSede.length + fVersione.length + fFornitore.length + (fNote.trim() ? 1 : 0) + (fAnnoMin || fAnnoMax ? 1 : 0) + (fPrezzoMin || fPrezzoMax ? 1 : 0);

  const stats = useMemo(() => {
    const totale = filtered.length, valoreStock = filtered.reduce((s, v) => s + v.prezzo, 0), alertCount = filtered.filter(v => v.giorni > 150).length, giorniMedi = totale > 0 ? Math.round(filtered.reduce((s, v) => s + v.giorni, 0) / totale) : 0;
    const groupByCI = (arr, key) => {
      const acc = {}; // valore normalizzato → { label, count }
      arr.forEach(v => {
        const raw = v[key];
        if (!raw) return;
        const k = normCI(raw);
        if (!acc[k]) acc[k] = { label: raw.toString().trim(), count: 0 };
        acc[k].count++;
      });
      return Object.values(acc).map(o => [o.label, o.count]).sort((a, b) => b[1] - a[1]);
    };
    const byMarca = groupByCI(filtered, 'marca').slice(0, 6);
    const byTipo = groupByCI(filtered, 'tipo');
    const byCarburante = groupByCI(filtered, 'carburante').slice(0, 6);
    return { totale, valoreStock, alertCount, giorniMedi, byMarca, byTipo, byCarburante };
  }, [filtered]);

  // Mappa vetture con contratto approvato O bozza (in trattativa) — HOOK prima del return condizionale
  const vetturePrenot = useMemo(() => {
    const map = {};
    contratti.filter(c => c.stato === 'approvato' || c.stato === 'bozza').forEach(c => {
      const vin   = (c.veicolo?.vin   || '').toUpperCase().trim();
      const targa = (c.veicolo?.targa || '').toUpperCase().trim();
      const id    = (c.veicolo?.id    || '').toString().trim();
      if (vin)   map[vin]   = c;
      if (targa) map[targa] = c;
      if (id)    map[id]    = c;
    });
    return map;
  }, [contratti]);

  const [alertScadenza, setAlertScadenza] = useState([]);
  const [showAlertPopup, setShowAlertPopup] = useState(false);

  useEffect(() => {
    const ora = Date.now();
    const scadenti = contratti.filter(c => {
      if (c.stato !== 'approvato') return false;
      const dataApp = c.approvazioneData ? new Date(c.approvazioneData).getTime() : 0;
      return dataApp && (ora - dataApp) > 7 * 24 * 60 * 60 * 1000;
    });
    setAlertScadenza(scadenti);
    // NON apre il popup automaticamente — solo banner in alto
  }, [contratti]);

  const ruoloBO = (user?.ruolo||user?.Ruolo||'').toLowerCase().trim();
  const emailBO = (user?.email||'').toLowerCase();
  const isFrontModifica = ruoloBO === 'front' && (emailBO.includes('roberta') || emailBO.includes('caiazzo'));
  const puoConfermare = ['admin','super_admin','back_office'].includes(ruoloBO) || isFrontModifica;

  const confermaPrenotazione = async (contratto) => {
    if (!window.confirm(`Confermare la vendita di ${contratto.veicolo?.marca} ${contratto.veicolo?.modello} a ${contratto.cliente?.nome || contratto.cliente?.ragioneSociale}? L'auto risulterà VENDUTA.`)) return;
    const oggi = new Date().toISOString().split('T')[0];
    const dataInput = window.prompt('Data di consegna/vendita (AAAA-MM-GG):', oggi);
    const dataVendita = dataInput && dataInput.match(/^\d{4}-\d{2}-\d{2}$/) ? new Date(dataInput).toISOString() : new Date().toISOString();
    try {
      // 1. Contratto → stato venduto
      await set(ref(db, `contratti/${contratto.id}/stato`), 'venduto');
      await set(ref(db, `contratti/${contratto.id}/vendutoAt`), dataVendita);
      await set(ref(db, `contratti/${contratto.id}/dataVendita`), dataVendita);
      await set(ref(db, `contratti/${contratto.id}/dataModifica`), new Date().toISOString());
      await set(ref(db, `stati_contratti/${contratto.id}`), 'venduto');
      // 2. Vettura in stock → VENDUTA — confronto normalizzato, non esatto
      const normTargaConf = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
      const vin = normTargaConf(contratto.veicolo?.vin);
      const targa = normTargaConf(contratto.veicolo?.targa);
      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (stockSnap.exists()) {
        const vsRaw = stockSnap.val();
        const vs = Array.isArray(vsRaw) ? vsRaw : Object.values(vsRaw);
        const idx = vs.findIndex(v => (vin && normTargaConf(v.vin) === vin) || (targa && normTargaConf(v.targa) === targa));
        if (idx > -1) {
          await set(ref(db, `stock_data/vehicles/${idx}/status`), 'VENDUTA');
          await set(ref(db, `stock_data/vehicles/${idx}/vendutoAt`), new Date().toISOString());
          // Canale live leggero — vedi in cima al file la nota su
          // stati_veicoli: permette a tutti di vedere lo stato aggiornato
          // istantaneamente anche con la cache di stock_data lunga (ore).
          await set(ref(db, `stati_veicoli/${vs[idx].id}/status`), 'VENDUTA');
          // Invalida cache locale
          window._radarStockCache = null; window._radarStockTs = 0;
        }
      }
      // 3. Aggiorna stato locale
      setAlertScadenza(prev => prev.filter(c => c.id !== contratto.id));
      alert('✅ Vendita confermata. Vettura marcata come VENDUTA.');
    } catch(e) { alert('Errore: ' + e.message); }
  };

  if (loading) return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: '15px' }}><div style={{ fontSize: '14px', fontWeight: '900', color: '#10b981' }}>Caricamento Stock...</div><div style={{ fontSize: '11px', color: '#64748b' }}>Lettura dati da Firebase</div></div>;

  const isInTrattativa = (car) => {
    const chiavi = [car.targa, car.vin, car.id].filter(Boolean).map(k => k.replace(/[^a-zA-Z0-9]/g, '_'));
    return chiavi.some(k => !!veicoliInTrattativa[k]);
  };

  // Cognome del cliente collegato alla trattativa — per mostrare a colpo
  // d'occhio a chi è legata la prenotazione, senza dover aprire il
  // contratto.
  const getCognomeTrattativa = (car) => {
    const chiavi = [car.targa, car.vin, car.id].filter(Boolean).map(k => k.replace(/[^a-zA-Z0-9]/g, '_'));
    for (const k of chiavi) {
      if (veicoliInTrattativa[k]?.clienteCognome) return veicoliInTrattativa[k].clienteCognome;
    }
    return '';
  };

  const isPrenotata = (car) => {
    const vin   = (car.vin   || '').toUpperCase().trim();
    const targa = (car.targa || '').toUpperCase().trim();
    const id    = (car.id    || '').toString().trim();
    return vetturePrenot[vin] || vetturePrenot[targa] || vetturePrenot[id] || null;
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>

      {/* POPUP PRENOTAZIONI IN SCADENZA */}
      {showAlertPopup && alertScadenza.length > 0 && (
        <div onClick={() => setShowAlertPopup(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backdropFilter: 'blur(4px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: '20px', border: '2px solid #f59e0b', width: '100%', maxWidth: '520px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}`, background: 'rgba(245,158,11,0.08)' }}>
              <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '900', letterSpacing: '2px', marginBottom: '4px' }}>⚠️ ATTENZIONE</div>
              <div style={{ fontSize: '18px', fontWeight: '900', color: '#f59e0b' }}>Prenotazioni in Scadenza</div>
              <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '4px' }}>{alertScadenza.length} vettura{alertScadenza.length > 1 ? 'e' : ''} prenotata da oltre 7 giorni</div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 24px' }}>
              {alertScadenza.map(c => {
                const giorni = c.approvazioneData ? Math.floor((Date.now() - new Date(c.approvazioneData).getTime()) / (1000 * 60 * 60 * 24)) : '?';
                const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome || ''} ${c.cliente?.cognome || ''}`.trim();
                return (
                  <div key={c.id} style={{ background: T.bg, borderRadius: '12px', border: '1px solid #f59e0b40', padding: '14px 16px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '900', color: T.text, marginBottom: '4px' }}>{nc}</div>
                        <div style={{ fontSize: '11px', color: T.textMuted }}>{c.veicolo?.marca} {c.veicolo?.modello} · {c.veicolo?.targa || '—'}</div>
                        <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>Venditore: {c.venditoreNome || '—'}</div>
                        {c.cliente?.telefono && <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '2px' }}>📞 {c.cliente.telefono}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '22px', fontWeight: '900', color: giorni > 14 ? '#ef4444' : '#f59e0b' }}>{giorni}gg</div>
                        <div style={{ fontSize: '9px', color: T.textMuted }}>dalla prenotazione</div>
                      </div>
                    </div>
                    {puoConfermare && (
                      <button onClick={() => confermaPrenotazione(c)}
                        style={{ width: '100%', marginTop: '10px', padding: '8px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '900', fontSize: '11px', cursor: 'pointer' }}>
                        ✅ Conferma Vendita
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}` }}>
              <button onClick={() => setShowAlertPopup(false)} style={{ width: '100%', padding: '12px', background: '#f59e0b', border: 'none', borderRadius: '12px', color: '#000', fontWeight: '900', cursor: 'pointer', fontSize: '13px' }}>
                ✓ Ho capito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BANNER prenotazioni in scadenza — sempre visibile se presenti */}
      {alertScadenza.length > 0 && (
        <div onClick={() => setShowAlertPopup(true)} style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', borderRadius: '12px', padding: '10px 16px', marginBottom: '14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b' }}>⚠️ {alertScadenza.length} prenotazione{alertScadenza.length > 1 ? 'i' : ''} in scadenza (oltre 7 giorni)</span>
          <span style={{ fontSize: '11px', color: '#f59e0b' }}>Visualizza →</span>
        </div>
      )}

      {/* BARRA FILTRI — next-level: ricerca prominente, chip rimovibili, pannello avanzato a comparsa */}
      <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, marginBottom: '20px', overflow: 'visible', position: 'relative', zIndex: 100 }}>
        {/* RIGA PRINCIPALE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '200px' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', pointerEvents: 'none', opacity: 0.6 }}>🔍</span>
            <input placeholder="Cerca marca, modello, targa, telaio..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 14px 12px 38px', background: '#0a0c10', border: '1px solid #1e2230', borderRadius: '12px', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ padding: '11px 14px', borderRadius: '12px', border: '1px solid #1e2230', background: '#0a0c10', color: '#fff', fontWeight: '800', fontSize: '11px', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
            <option value="giorni">Giacenza ↓</option>
            <option value="ultimi_arrivi">Ultimi arrivi</option>
            <option value="prezzo_desc">Prezzo decrescente</option>
            <option value="prezzo_asc">Prezzo crescente</option>
            <option value="km">Km</option>
          </select>
          <button onClick={() => setFiltriAperti(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 16px', borderRadius: '12px', border: `1.5px solid ${nFiltriAttivi > 0 ? '#3b82f6' : T.border}`, background: nFiltriAttivi > 0 ? 'rgba(59,130,246,0.12)' : T.bg, color: nFiltriAttivi > 0 ? '#3b82f6' : T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 }}>
            🎛️ Filtri
            {nFiltriAttivi > 0 && (
              <span style={{ background: '#3b82f6', color: '#fff', borderRadius: '20px', fontSize: '10px', fontWeight: '900', padding: '1px 7px', minWidth: '18px', textAlign: 'center' }}>{nFiltriAttivi}</span>
            )}
            <span style={{ fontSize: '9px', transition: 'transform 0.15s', display: 'inline-block', transform: filtriAperti ? 'rotate(180deg)' : 'none' }}>▾</span>
          </button>
          <button onClick={() => setStatsOpen(o => !o)}
            style={{ padding: '11px 16px', borderRadius: '12px', border: `1px solid ${statsOpen ? '#8b5cf6' : T.border}`, background: statsOpen ? 'rgba(139,92,246,0.12)' : T.bg, color: statsOpen ? '#8b5cf6' : T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 }}>
            📊 Statistiche {statsOpen ? '▴' : '▾'}
          </button>
          <span title="Data dell'ultimo sync stock automatico" style={{ fontSize: '9px', color: '#475569', fontWeight: '700', whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0 }}>🔄 {lastUpdate}</span>
        </div>

        {/* CHIP FILTRI ATTIVI — rimovibili singolarmente, un colpo d'occhio su cosa è filtrato */}
        {chipsAttivi.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '0 16px 14px' }}>
            {chipsAttivi.map((chip, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 6px 5px 12px', borderRadius: '20px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.35)', color: '#3b82f6', fontSize: '11px', fontWeight: '700' }}>
                {chip.label}
                <button onClick={chip.onRemove} style={{ background: 'rgba(59,130,246,0.25)', border: 'none', borderRadius: '50%', width: '16px', height: '16px', color: '#3b82f6', cursor: 'pointer', fontSize: '11px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>
              </span>
            ))}
            <button onClick={() => { setFMarca([]); setFModello([]); setFTipo([]); setFCategoria([]); setFCarburante([]); setFGearType([]); setFStatus([]); setFSede([]); setFVersione([]); setFFornitore([]); setFNote(''); setFAnnoMin(''); setFAnnoMax(''); setFPrezzoMin(''); setFPrezzoMax(''); setOnlyAlert(false); setShowVendute(false); }}
              style={{ padding: '5px 12px', borderRadius: '20px', background: 'none', border: `1px solid ${T.border}`, color: '#ef4444', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
              × Pulisci tutti
            </button>
          </div>
        )}

        {/* PANNELLO FILTRI AVANZATI — a comparsa, nascosto di default per non affollare la vista.
            Griglia responsive: su desktop più colonne affiancate, su mobile
            una colonna sola a piena larghezza — touch target più grandi e
            niente scroll orizzontale involontario. */}
        {filtriAperti && (
          <div style={{ padding: '4px 16px 18px', borderTop: `1px solid ${T.border}` }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: isMobile ? '8px' : '10px',
              paddingTop: '14px',
            }}>
              <RadarMultiSelect label="MARCA"      options={getOpts('marca')}      selected={fMarca}      onChange={v => { setFMarca(v); setFModello([]); }} onClear={() => { setFMarca([]); setFModello([]); }} fullWidth={isMobile} />
              <RadarMultiSelect label="MODELLO"    options={modelliOptions}        selected={fModello}    onChange={setFModello}    onClear={() => setFModello([])} fullWidth={isMobile} />
              <RadarMultiSelect label="VERSIONE"   options={getOpts('versione')}   selected={fVersione}   onChange={setFVersione}   onClear={() => setFVersione([])} fullWidth={isMobile} />
              <RadarMultiSelect label="TIPOLOGIA"  options={getOpts('tipo')}       selected={fTipo}       onChange={setFTipo}       onClear={() => setFTipo([])} fullWidth={isMobile} />
              <RadarMultiSelect label="CATEGORIA"  options={getOpts('categoria')}  selected={fCategoria}  onChange={setFCategoria}  onClear={() => setFCategoria([])} fullWidth={isMobile} />
              <RadarMultiSelect label="CARBURANTE" options={getOpts('carburante')} selected={fCarburante} onChange={setFCarburante} onClear={() => setFCarburante([])} fullWidth={isMobile} />
              <RadarMultiSelect label="CAMBIO"     options={getOpts('gearType').filter(Boolean)} selected={fGearType} onChange={setFGearType} onClear={() => setFGearType([])} fullWidth={isMobile} />
              <RadarMultiSelect label="STATUS"     options={getOpts('status')}     selected={fStatus}     onChange={setFStatus}     onClear={() => setFStatus([])} fullWidth={isMobile} />
              <RadarMultiSelect label="UBICAZIONE" options={getOpts('sede')}       selected={fSede}       onChange={setFSede}       onClear={() => setFSede([])} fullWidth={isMobile} />
              <RadarMultiSelect label="FORNITORE"  options={[...new Set((vehicles || []).filter(v => passaFiltri(v, 'fornitore')).map(v => String(v.fornitore || 'N.D.').trim()))].sort()} selected={fFornitore} onChange={setFFornitore} onClear={() => setFFornitore([])} fullWidth={isMobile} />
              <input value={fNote} onChange={e => setFNote(e.target.value)} placeholder="ETICHETTA / NOTE…"
                title="Mostra le auto che hanno questo testo nelle note o nelle etichette del telaio"
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', background: T.inputBg, border: `1px solid ${fNote.trim() ? '#3b82f6' : T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', outline: 'none' }} />
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
                <span style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, whiteSpace: 'nowrap' }}>ANNO</span>
                <input type="number" placeholder="Dal" value={fAnnoMin} onChange={e => setFAnnoMin(e.target.value)}
                  style={{ width: isMobile ? '100%' : '58px', flex: isMobile ? 1 : 'none', padding: '9px 6px', background: T.inputBg, border: `1px solid ${fAnnoMin ? '#3b82f6' : T.border}`, borderRadius: '8px', color: T.text, fontSize: '10px', outline: 'none', textAlign: 'center' }} />
                <span style={{ fontSize: '10px', color: T.textMuted }}>–</span>
                <input type="number" placeholder="Al" value={fAnnoMax} onChange={e => setFAnnoMax(e.target.value)}
                  style={{ width: isMobile ? '100%' : '58px', flex: isMobile ? 1 : 'none', padding: '9px 6px', background: T.inputBg, border: `1px solid ${fAnnoMax ? '#3b82f6' : T.border}`, borderRadius: '8px', color: T.text, fontSize: '10px', outline: 'none', textAlign: 'center' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
                <span style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, whiteSpace: 'nowrap' }}>€</span>
                <input type="number" placeholder="Min" value={fPrezzoMin} onChange={e => setFPrezzoMin(e.target.value)}
                  style={{ width: isMobile ? '100%' : '72px', flex: isMobile ? 1 : 'none', padding: '9px 6px', background: T.inputBg, border: `1px solid ${fPrezzoMin ? '#10b981' : T.border}`, borderRadius: '8px', color: T.text, fontSize: '10px', outline: 'none', textAlign: 'center' }} />
                <span style={{ fontSize: '10px', color: T.textMuted }}>–</span>
                <input type="number" placeholder="Max" value={fPrezzoMax} onChange={e => setFPrezzoMax(e.target.value)}
                  style={{ width: isMobile ? '100%' : '72px', flex: isMobile ? 1 : 'none', padding: '9px 6px', background: T.inputBg, border: `1px solid ${fPrezzoMax ? '#10b981' : T.border}`, borderRadius: '8px', color: T.text, fontSize: '10px', outline: 'none', textAlign: 'center' }} />
              </div>
              <button onClick={() => setOnlyAlert(!onlyAlert)}
                style={{ padding: '9px 14px', borderRadius: '10px', border: `1px solid ${onlyAlert ? '#f59e0b' : T.border}`, background: onlyAlert ? 'rgba(245,158,11,0.12)' : T.bg, color: onlyAlert ? '#f59e0b' : T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto' }}>
                🟠 ALERT +150gg {onlyAlert ? `(${stats.alertCount})` : ''}
              </button>
              <button onClick={() => setShowVendute(!showVendute)}
                style={{ padding: '9px 14px', borderRadius: '10px', border: `1px solid ${showVendute ? '#10b981' : T.border}`, background: showVendute ? 'rgba(16,185,129,0.12)' : T.bg, color: showVendute ? '#10b981' : T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap', opacity: deferredShowVendute !== showVendute ? 0.6 : 1, width: isMobile ? '100%' : 'auto' }}>
                🚗 {showVendute ? 'Nascondi Vendute' : 'Consulta Auto Vendute'}
              </button>
            </div>
          </div>
        )}

        {/* AZIONI — export, import, amministrazione. Raggruppate a parte per non affollare i filtri */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px 16px', borderTop: `1px solid ${T.border}`, alignItems: 'center' }}>
          <button onClick={() => setSelezionate(p => { const n = { ...p }; filtered.forEach(c => { n[chiaveAuto(c)] = c; }); return n; })}
            title="Aggiunge alla selezione tutte le auto filtrate"
            style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', cursor: 'pointer', fontWeight: '900', fontSize: '9px' }}>
            ☑ Seleziona filtrate ({filtered.length})
          </button>
          {puoEsportareExcel(user) && (
          <button onClick={() => setColonnePickerOpen(true)}
            style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap' }}>
            📊 Esporta Excel {filtered.length ? `(${filtered.length})` : ''}
          </button>
          )}
          {['admin','super_admin'].includes(user?.ruolo) && (
            <>
              <button onClick={() => setAggiungiAutoOpen(true)}
                style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.5)', background: 'rgba(16,185,129,0.12)', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: '9px', letterSpacing: '0.5px' }}>
                ➕ Aggiungi auto
              </button>
              <input ref={inputImportRef} type="file" accept=".csv" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) importCSVStockSpark(e.target.files[0]); e.target.value=''; }} />
              <button onClick={() => inputImportRef.current?.click()} disabled={importando}
                style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.1)', color: '#6366f1', cursor: importando ? 'default' : 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap', opacity: importando ? 0.6 : 1 }}>
                {importando ? '⏳ Importando...' : '📥 Importa CSV'}
              </button>
              <input ref={inputBottomPriceRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) importaSoloBottomPrice(e.target.files[0]); e.target.value=''; }} />
              <button onClick={() => inputBottomPriceRef.current?.click()} disabled={importandoBP}
                title="Aggiorna solo il bottom price dei telai presenti nel file — non tocca nessun altro campo"
                style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid #f59e0b60', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', cursor: importandoBP ? 'default' : 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap', opacity: importandoBP ? 0.6 : 1 }}>
                {importandoBP ? '⏳ Aggiornando...' : '💰 Aggiorna Bottom Price'}
              </button>
              <input ref={inputFornitoreRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) importaFornitori(e.target.files[0]); e.target.value=''; }} />
              <button onClick={() => inputFornitoreRef.current?.click()} disabled={importandoForn}
                title="Assegna il fornitore a ogni veicolo dello stock dal file — chi non è nel file (o senza fornitore indicato) resta/diventa N.D."
                style={{ padding: '9px 14px', borderRadius: '10px', border: '1px solid #8b5cf660', background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', cursor: importandoForn ? 'default' : 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap', opacity: importandoForn ? 0.6 : 1 }}>
                {importandoForn ? '⏳ Aggiornando...' : '📦 Importa Fornitori'}
              </button>
            </>
          )}
          {user?.ruolo === 'super_admin' && (
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', flexWrap: 'wrap' }}>
              <button onClick={() => {
                setRefreshingStock(true);
                window._radarStockCache = null; window._radarStockTs = null;
                try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}
                window.location.reload();
              }} disabled={refreshingStock}
                style={{ padding: '8px 12px', borderRadius: '9px', border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', cursor: refreshingStock ? 'default' : 'pointer', fontWeight: '900', fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0, opacity: refreshingStock ? 0.6 : 1 }}>
                {refreshingStock ? '⏳ Aggiornamento...' : '🔄 Aggiorna Stock'}
              </button>
              <button onClick={async () => {
                setRimuovendoDup(true);
                try {
                  const normPlate = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
                  const stockSnap = await get(ref(db, 'stock_data/vehicles'));
                  if (!stockSnap.exists()) { alert('Stock non trovato.'); setRimuovendoDup(false); return; }
                  const raw = stockSnap.val();
                  const arr = Array.isArray(raw) ? raw : Object.values(raw);

                  const gruppi = {};
                  arr.forEach(v => {
                    const chiave = normPlate(v.targa) || normPlate(v.vin);
                    if (!chiave) return;
                    if (!gruppi[chiave]) gruppi[chiave] = [];
                    gruppi[chiave].push(v);
                  });

                  const daRimuovere = new Set();
                  const daAggiornare = new Map();
                  let gruppiConDuplicati = 0;

                  Object.values(gruppi).forEach(gruppo => {
                    if (gruppo.length < 2) return;
                    gruppiConDuplicati++;

                    const punteggio = v => {
                      let p = 0;
                      if (noteCountMap[v.id] > 0) p += 100;
                      const haPreventivo = toArraySafe(preventivi).some(pr =>
                        (v.id && pr.veicolo?.id === v.id) ||
                        (v.targa && normPlate(pr.veicolo?.targa) === normPlate(v.targa)) ||
                        (v.vin && normPlate(pr.veicolo?.vin) === normPlate(v.vin))
                      );
                      if (haPreventivo) p += 50;
                      const haContratto = toArraySafe(contratti).some(c =>
                        (v.id && c.veicolo?.id === v.id) ||
                        (v.targa && normPlate(c.veicolo?.targa) === normPlate(v.targa)) ||
                        (v.vin && normPlate(c.veicolo?.vin) === normPlate(v.vin))
                      );
                      if (haContratto) p += 50;
                      if (parseFloat(v.bottomPrice) > 0) p += 40;
                      if (parseFloat(v.prezzo) > 0) p += 20;
                      if (parseFloat(v.prezzoPartner) > 0) p += 20;
                      p += Object.values(v).filter(val => val !== '' && val !== 0 && val != null).length * 0.1;
                      return p;
                    };

                    const ordinato = [...gruppo].sort((a, b) => punteggio(b) - punteggio(a));
                    const [migliore, ...altri] = ordinato;

                    let unito = { ...migliore };
                    altri.forEach(v => {
                      Object.entries(v).forEach(([k, val]) => {
                        if (val === undefined || val === null || val === '') return;
                        if (typeof val === 'number' && val === 0) return;
                        if (Array.isArray(val) && val.length === 0) return;
                        const attuale = unito[k];
                        if (attuale === undefined || attuale === null || attuale === '' ||
                            (typeof attuale === 'number' && attuale === 0) ||
                            (Array.isArray(attuale) && attuale.length === 0)) {
                          unito[k] = val;
                        }
                      });
                    });

                    // Il regime IVA (badge Art.36), se bloccato manualmente su
                    // una QUALSIASI delle copie duplicate, vince sempre — a
                    // prescindere da quale copia ha più note/contratti/dati e
                    // viene scelta come principale. Altrimenti una copia
                    // vecchia non ancora corretta potrebbe far "tornare
                    // indietro" una correzione già fatta e bloccata.
                    const copiaBloccata = gruppo.find(v => v.ivaEspostaLocked);
                    if (copiaBloccata) {
                      unito.ivaEspostaLocked = true;
                      if ('ivaEsposta' in copiaBloccata) unito.ivaEsposta = copiaBloccata.ivaEsposta;
                      else delete unito.ivaEsposta;
                    }

                    daAggiornare.set(migliore, unito);
                    altri.forEach(v => daRimuovere.add(v));
                  });

                  if (daRimuovere.size === 0) {
                    alert('✅ Nessun duplicato trovato.');
                    setRimuovendoDup(false);
                    return;
                  }

                  if (!window.confirm(`Trovati ${gruppiConDuplicati} gruppi di targhe duplicate, per un totale di ${daRimuovere.size} veicoli da rimuovere.\n\nPer ogni gruppo, i dati validi di tutte le copie (prezzi, note, ecc.) vengono uniti nella copia superstite — nessun dato valorizzato viene perso.\n\nProcedere?`)) {
                    setRimuovendoDup(false);
                    return;
                  }

                  const pulito = arr
                    .filter(v => !daRimuovere.has(v))
                    .map(v => daAggiornare.has(v) ? daAggiornare.get(v) : v);
                  await set(ref(db, 'stock_data'), { vehicles: pulito, lastUpdate: new Date().toISOString() });
                  window._radarStockCache = null; window._radarStockTs = null;
                  try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e2) {}
                  alert(`✅ Rimossi ${daRimuovere.size} veicoli duplicati da ${gruppiConDuplicati} targhe.`);
                  window.location.reload();
                } catch (e) {
                  alert('Errore: ' + e.message);
                }
                setRimuovendoDup(false);
              }} disabled={rimuovendoDup}
                style={{ padding: '8px 12px', borderRadius: '9px', border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: rimuovendoDup ? 'default' : 'pointer', fontWeight: '900', fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0, opacity: rimuovendoDup ? 0.6 : 1 }}>
                {rimuovendoDup ? '⏳ Analisi...' : '🧹 Rimuovi Duplicati'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SEZIONE STATISTICHE — collassabile */}
      {statsOpen && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <RadarStat label="Veicoli in Stock"          value={fmtN(stats.totale)}       sub="Unità disponibili"     color="#10b981" />
            <RadarStat label="Valore Stock"              value={fmt(stats.valoreStock)}   sub="Prezzo listino totale" color="#3b82f6" />
            <RadarStat label="Giacenza Media"            value={stats.giorniMedi + ' gg'} sub="Giorni medi in stock"  color="#8b5cf6" />
            <RadarStat label="Alert +150gg" value={fmtN(stats.alertCount)} sub="Da smaltire" color="#f59e0b" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '25px', marginBottom: '30px' }}>
            <RadarChart title="Stock per Marca"      data={stats.byMarca}      color="#3b82f6" />
            <RadarChart title="Stock per Tipologia"  data={stats.byTipo}       color="#10b981" />
            <RadarChart title="Stock per Carburante" data={stats.byCarburante} color="#8b5cf6" />
            <FasceGiacenza filtered={filtered} />
          </div>
        </>
      )}

      <div style={{ background: '#141722', borderRadius: '30px', border: '1px solid #1e2230', overflow: 'hidden' }}>
        <div style={{ padding: '25px 40px', borderBottom: '1px solid #1e2230', background: T.surface2 }}>
          <h4 style={{ margin: 0, fontSize: '13px', color: '#10b981', fontWeight: '900', letterSpacing: '1px' }}>CATALOGO STOCK — {filtered.length} VEICOLI</h4>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '15px', maxHeight: '700px', overflowY: 'auto' }}>
          {paginatedVehicles.map((car, iCar) => (
            // Chiave sempre univoca: due record con lo stesso id (es. un doppione
            // in stock) facevano "moltiplicare" le card durante la ricerca
            <div key={`${car.id}|${car.vin || ''}|${car.targa || ''}|${iCar}`} style={{ position: 'relative', ...(selezionate[chiaveAuto(car)] ? { boxShadow: '0 0 0 3px #3b82f6' } : {}), background: T.bg, borderRadius: '20px', border: `${(noteCountMap[car.id] > 0 || car.noteTelaio?.length > 0) ? '2px' : '1px'} solid ${(noteCountMap[car.id] > 0 || car.noteTelaio?.length > 0) ? '#8b5cf6' : car.giorni > 200 ? '#7f1d1d' : car.giorni > 150 ? '#92400e' : car.giorni > 90 ? '#4c1d95' : T.border}`, overflow: 'hidden' }}>
              {/* Selezione per estrazione */}
              <label onClick={e => e.stopPropagation()} title="Seleziona per l'estrazione"
                style={{ position: 'absolute', top: '170px', right: '12px', zIndex: 6, width: '26px', height: '26px', borderRadius: '8px', background: selezionate[chiaveAuto(car)] ? '#3b82f6' : 'rgba(15,23,42,0.6)', border: '2px solid #3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!selezionate[chiaveAuto(car)]} onChange={() => toggleSel(car)} style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', margin: 0, cursor: 'pointer' }} />
                <span style={{ color: '#fff', fontSize: '15px', fontWeight: '900', lineHeight: 1 }}>{selezionate[chiaveAuto(car)] ? '✓' : ''}</span>
              </label>
              {(user?.ruolo || '').toLowerCase().trim() === 'super_admin' && (
                <button onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Eliminare definitivamente ${car.marca} ${car.modello} (${car.targa || car.vin || '—'}) dallo Stock?\n\nQuesta azione NON è reversibile. Il veicolo sparirà completamente da RadaR.`)) return;
                  if (!window.confirm(`Confermi ancora una volta l'eliminazione di ${car.targa || car.vin || 'questo veicolo'}? Non si può annullare.`)) return;
                  try {
                    const stockSnap = await get(ref(db, 'stock_data/vehicles'));
                    if (!stockSnap.exists()) { alert('Stock non trovato.'); return; }
                    const raw = stockSnap.val();
                    const isArr = Array.isArray(raw);
                    const vs = isArr ? raw : Object.values(raw);
                    const pulito = vs.filter(v => !((car.id && v.id === car.id) || (car.targa && v.targa === car.targa) || (car.vin && v.vin === car.vin)));
                    if (pulito.length === vs.length) { alert('Veicolo non trovato nello stock.'); return; }
                    await set(ref(db, 'stock_data'), { vehicles: pulito, lastUpdate: new Date().toISOString() });
                    window._radarStockCache = null; window._radarStockTs = 0;
                    try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e2) {}
                    setVehiclesGrezzi(prevVs => prevVs.filter(pv => pv.id !== car.id));
                    alert('✅ Veicolo eliminato dallo Stock.');
                  } catch (err) { alert('Errore: ' + err.message); }
                }}
                  title="Elimina definitivamente dallo Stock"
                  style={{ position: 'absolute', top: '170px', right: '46px', zIndex: 5, width: '26px', height: '26px', borderRadius: '8px', background: 'rgba(15,23,42,0.75)', border: '1px solid #ef4444', color: '#ef4444', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  🗑️
                </button>
              )}
              {car.mainImg && (
                <div onClick={() => setSelectedCar(car)} style={{ position: 'relative', cursor: 'pointer' }}>
                  <img src={car.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '100%', height: '160px', objectFit: 'cover' }} />
                  {car.alert && <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#ef4444', color: '#fff', fontSize: '8px', fontWeight: '900', padding: '4px 8px', borderRadius: '6px' }}>+{ALERT_DAYS}gg</div>}
                  <div style={{ position: 'absolute', top: '10px', left: '10px', background: car.status === 'FREE' ? '#10b981' : '#f59e0b', color: '#fff', fontSize: '8px', fontWeight: '900', padding: '4px 8px', borderRadius: '6px' }}>{car.status === 'RESERVED' ? 'PRENOTATA' : car.status}</div>
                  {car.ivaEsposta === false && <div style={{ position: 'absolute', top: '10px', right: car.alert ? '70px' : '10px', background: '#0f172a', border: '1px solid #f59e0b', color: '#f59e0b', fontSize: '8px', fontWeight: '900', padding: '4px 8px', borderRadius: '6px' }}>ART.36</div>}
                  {(car.tipo || '').toLowerCase().trim() === 'km0' && <div style={{ position: 'absolute', top: '30px', left: '10px', background: '#f59e0b', color: '#0f172a', fontSize: '8px', fontWeight: '900', padding: '4px 8px', borderRadius: '6px' }}>🧾 BOLLO DA PAGARE</div>}
                  <div style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '8px', fontWeight: '900', padding: '4px 8px', borderRadius: '6px' }}>{car.tipo}</div>
                  <div style={{ position: 'absolute', bottom: '10px', right: '10px', display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-end' }}>
                    {(noteCountMap[car.id] > 0 || car.noteTelaio?.length > 0) && (
                      <div style={{ background: '#8b5cf6', color: '#fff', fontSize: '9px', fontWeight: '900', padding: '5px 9px', borderRadius: '7px', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 8px rgba(139,92,246,0.6)', border: '1px solid rgba(255,255,255,0.25)' }}>
                        🗒️ {noteCountMap[car.id] || car.noteTelaio.length} {(noteCountMap[car.id] || car.noteTelaio.length) === 1 ? 'NOTA' : 'NOTE'}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div style={{ padding: '15px' }}>
                <div onClick={() => setSelectedCar(car)} style={{ cursor: 'pointer' }}>
                  <div style={{ fontSize: '9px', color: '#3b82f6', fontWeight: '900', marginBottom: '4px' }}>{car.classe?.toUpperCase()}</div>
                  <div style={{ fontSize: '14px', fontWeight: '900', color: T.text, marginBottom: '2px' }}>{car.marca} {car.modello}</div>
                  <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '8px' }}>{car.versione}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <span style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>
                      {(() => {
                        const pp = parseFloat(car.prezzoPartner || car.priceB2b || car.prezzoB2b || car.partnerPrice || 0);
                        const isPartner = user?.ruolo === 'partner';
                        return <>
                          <span style={{ color: '#10b981' }}>{fmt(isPartner && pp > 0 ? pp : car.prezzo)}</span>
                          {isPartner && pp > 0 && <span style={{fontSize:'8px',color:'#f59e0b',marginLeft:'4px',fontWeight:'900'}}>PARTNER</span>}
                        </>;
                      })()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#64748b' }}>{fmtN(car.km)} km</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#475569', marginBottom: '5px' }}>
                    <span>{car.carburante?.toUpperCase()}</span>
                    <span style={{ color: car.giorni > 200 ? '#ef4444' : car.giorni > 150 ? '#f59e0b' : car.giorni > 90 ? '#8b5cf6' : '#10b981', fontWeight: car.giorni > 90 ? '900' : '400' }}>{car.giorni} gg</span>
                  </div>
                  {/* Immatricolazione, Targa e Telaio sempre visibili in card */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#475569', marginBottom: '5px', paddingTop: '5px', borderTop: '1px solid #1e2230' }}>
                    <span>🗓 {car.registrazione || '—'}</span>
                    <span style={{ color: '#64748b' }}>🏷 {car.targa || '—'}</span>
                  </div>
                  <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '10px' }}>
                    🔑 {car.vin ? '···' + car.vin.slice(-6) : '—'}
                  </div>
                </div>
                {/* PREZZO D'ACQUISTO — solo Finance/Admin/Super Admin, permette
                    di aggiornare il bottom price direttamente dalla card. */}
                {((['finance','admin','super_admin'].includes((user?.ruolo||'').toLowerCase().trim()) && puoVedereMargini(user)) || puoInserirePrezzoAcquisto(user)) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const attuale = car.bottomPrice ? String(car.bottomPrice) : '';
                      const nuovo = window.prompt('Nuovo prezzo d\'acquisto (bottom price) per questo veicolo:', attuale);
                      if (nuovo === null) return; // annullato
                      const valore = parseFloat(nuovo.replace(',', '.'));
                      if (isNaN(valore) || valore < 0) { alert('Inserisci un numero valido.'); return; }
                      aggiornaBottomPrice(car, valore);
                    }}
                    style={{ width: '100%', padding: '7px', marginBottom: '8px', borderRadius: '8px', border: '1px solid #f59e0b60', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', cursor: 'pointer', fontWeight: '800', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                    💰 Prezzo d'Acquisto{car.bottomPrice ? `: ${fmt(car.bottomPrice)}` : ' (non impostato)'}
                  </button>
                )}
                {/* PULSANTE PREVENTIVO o BADGE IN TRATTATIVA */}
                {(() => {
                  const stato = (car.status || '').toUpperCase();
                  const ruolo = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
                  const puoPrev = ruolo !== 'bdc' && ruolo !== 'osservatore';
                  const isVenduta = stato === 'VENDUTA' || stato === 'SOLD';
                  const contrattoPrenotato = isPrenotata(car);
                  const inTratt = isInTrattativa(car);

                  if (isVenduta) return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                      <div style={{ width: '100%', padding: '9px', background: 'rgba(100,116,139,0.15)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: '10px', color: '#64748b', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        🚫 VENDUTA
                      </div>
                      {(ruolo === 'admin' || ruolo === 'super_admin') && (
                        <button onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Ripristinare la disponibilità di ${car.marca} ${car.modello} (${car.targa || car.vin || '—'})?\n\nTornerà visibile come libera in Stock. Usalo solo se sai per certo che il veicolo NON è stato venduto.`)) return;
                          try {
                            const stockSnap = await get(ref(db, 'stock_data/vehicles'));
                            if (!stockSnap.exists()) { alert('Stock non trovato.'); return; }
                            const raw = stockSnap.val();
                            const norm3 = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
                            // stock_data/vehicles a volte torna come oggetto (chiavi numeriche) invece che array —
                            // stesso caso già gestito in sync-stock.js. Normalizziamo prima di cercare l'indice.
                            const isArr = Array.isArray(raw);
                            const vs = isArr ? raw : Object.values(raw);
                            const idx = vs.findIndex(v => (car.id && v.id === car.id) || (car.targa && norm3(v.targa) === norm3(car.targa)) || (car.vin && norm3(v.vin) === norm3(car.vin)));
                            if (idx === -1) { alert('Veicolo non trovato nello stock (nessuna corrispondenza per id/targa/VIN).'); return; }
                            // Se l'oggetto originale non era un array, riscriviamo l'intero nodo come array pulito
                            // (stesso formato che sync-stock.js scrive sempre) invece di path annidati sull'indice
                            // sbagliato di un oggetto con chiavi non sequenziali.
                            if (isArr) {
                              await set(ref(db, `stock_data/vehicles/${idx}/status`), 'FREE');
                              await set(ref(db, `stock_data/vehicles/${idx}/vendutoAt`), null);
                              await set(ref(db, `stock_data/vehicles/${idx}/recuperatoDaContratto`), null);
                            } else {
                              vs[idx] = { ...vs[idx], status: 'FREE', vendutoAt: null, recuperatoDaContratto: null };
                              await set(ref(db, 'stock_data/vehicles'), vs);
                            }
                            await set(ref(db, `stati_veicoli/${car.id}/status`), 'FREE');
                            // Invalida SIA la cache in memoria SIA quella in localStorage (30 min di TTL)
                            // altrimenti la UI continua a mostrare il vecchio stato fino alla scadenza.
                            window._radarStockCache = null; window._radarStockTs = 0;
                            try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e2) {}
                            // Aggiornamento ottimistico: sistema anche lo stato locale del componente, così
                            // il cambiamento si vede subito senza dover ricaricare la pagina.
                            await logBloccoVeicolo(car, { tipo: 'liberazione', bloccatoDa: car.consulenteNome || '', cliente: car.clienteNome || '', motivo: 'Auto venduta rimessa disponibile (ripristino admin)', user });
                            setVehiclesGrezzi(prevVs => prevVs.map(pv => (pv.id === car.id || pv.targa === car.targa || pv.vin === car.vin) ? { ...pv, status: 'FREE', vendutoAt: null, recuperatoDaContratto: null } : pv));
                            alert('✅ Disponibilità ripristinata.');
                          } catch (err) { alert('Errore: ' + err.message); }
                        }}
                          style={{ width: '100%', padding: '7px', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: '9px', color: '#10b981', cursor: 'pointer', fontWeight: '800', fontSize: '9px' }}>
                          ♻️ Ripristina disponibilità
                        </button>
                      )}
                    </div>
                  );
                  if (!puoPrev) return null;
                  if (stato === 'CONTRATTUALIZZATA') {
                    const puoPreventivareContrattualizzata = ['admin','super_admin'].includes((user?.ruolo||'').toLowerCase().trim());
                    return (
                      <div>
                        {puoPreventivareContrattualizzata ? (
                          <button onClick={(e) => { e.stopPropagation(); setPreventivocar(car); }}
                            style={{ width: '100%', padding: '9px', background: 'rgba(139,92,246,0.12)', border: '1px solid #8b5cf6', borderRadius: '10px', color: '#8b5cf6', cursor: 'pointer', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            📝 CONTRATTUALIZZATA — Preventiva Ugualmente
                          </button>
                        ) : (
                          <div style={{ width: '100%', padding: '9px', background: 'rgba(139,92,246,0.12)', border: '1px solid #8b5cf6', borderRadius: '10px', color: '#8b5cf6', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            📝 CONTRATTUALIZZATA
                          </div>
                        )}
                        {puoPreventivareContrattualizzata && (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            const norm4 = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                            const attivo = (contratti || []).find(k => ['bozza','approvato','venduto'].includes(k.stato) && (
                              (car.vin && norm4(k.veicolo?.vin || k.datiExtra?.vin) === norm4(car.vin)) || (car.targa && norm4(k.veicolo?.targa) === norm4(car.targa))));
                            const avviso = attivo
                              ? `ATTENZIONE: risulta ancora collegata al contratto ${attivo.datiExtra?.numeroContratto || attivo.id} (${attivo.stato}) di ${attivo.cliente?.ragioneSociale || attivo.cliente?.cognome || ''}.\n\n`
                              : 'Nessun contratto attivo risulta collegato a questa auto.\n\n';
                            if (!window.confirm(`${avviso}Rimettere ${car.marca} ${car.modello} (${car.targa || car.vin}) DISPONIBILE in stock?`)) return;
                            const ok = await ripristinaVeicoloInStock(car, 'ripristino manuale admin');
                            if (ok) await logBloccoVeicolo(car, { tipo: 'liberazione', bloccatoDa: car.consulenteNome || '', cliente: car.clienteNome || '', motivo: attivo ? `Rimessa disponibile con contratto ${attivo.datiExtra?.numeroContratto || attivo.id} ancora attivo` : 'Rimessa disponibile (contratto non più attivo)', user });
                            if (ok) setVehiclesGrezzi(prevVs => prevVs.map(pv => pv.id === car.id ? { ...pv, status: 'FREE', vendutoAt: null, consulenteNome: null, clienteNome: null } : pv));
                            else alert('Auto non trovata nello stock.');
                          }}
                            style={{ width: '100%', marginTop: '4px', padding: '5px', background: 'transparent', border: '1px dashed #10b981', borderRadius: '8px', color: '#10b981', cursor: 'pointer', fontWeight: '800', fontSize: '9px' }}>
                            ↺ Rimetti disponibile
                          </button>
                        )}
                        {(car.consulenteNome || car.clienteNome) && (
                          <div style={{ fontSize: '9px', color: '#8b5cf6', textAlign: 'center', marginTop: '3px', fontWeight: '700' }}>
                            👤 {car.clienteNome || ''}{car.consulenteNome ? ` — ${car.consulenteNome}` : ''}
                          </div>
                        )}
                      </div>
                    );
                  }
                  // ── BLOCCO DI UN ADMIN / SUPER ADMIN (senza contratto) ──
                  const isAdminRuolo = ruolo === 'admin' || ruolo === 'super_admin';
                  const blocco = car.bloccoAdmin;
                  const scriviBlocco = async (val) => {
                    try { await set(ref(db, `stati_veicoli/${car.id}/bloccoAdmin`), val); }
                    catch (err) { alert('Errore: ' + err.message); }
                  };
                  if (blocco && !contrattoPrenotato) {
                    const quando = blocco.at ? new Date(blocco.at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : '';
                    return (
                      <div>
                        <button onClick={(e) => { e.stopPropagation(); setPreventivocar(car); }}
                          title={blocco.nota ? `Motivo: ${blocco.nota}` : ''}
                          style={{ width: '100%', padding: '9px', background: 'rgba(220,38,38,0.12)', border: '1px solid #dc2626', borderRadius: '10px', color: '#dc2626', cursor: 'pointer', fontWeight: '900', fontSize: '10px' }}>
                          🔒 BLOCCATA DA {String(blocco.nome || 'ADMIN').toUpperCase()} — Preventiva Ugualmente
                        </button>
                        <div style={{ fontSize: '9px', color: '#dc2626', textAlign: 'center', marginTop: '3px', fontWeight: '700' }}>
                          {blocco.ruolo === 'super_admin' ? 'Super admin' : 'Admin'}{quando ? ` · dal ${quando}` : ''}{blocco.nota ? ` · ${blocco.nota}` : ''}
                        </div>
                        {isAdminRuolo && (
                          <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Sbloccare ${car.marca} ${car.modello}? Bloccata da ${blocco.nome || 'admin'}.`)) { scriviBlocco(null); logBloccoVeicolo(car, { tipo: 'liberazione', bloccatoDa: blocco.nome || 'admin', motivo: `Sblocco blocco admin${blocco.nota ? ` (${blocco.nota})` : ''}`, user }); } }}
                            style={{ width: '100%', marginTop: '4px', padding: '5px', background: 'transparent', border: '1px dashed #dc2626', borderRadius: '8px', color: '#dc2626', cursor: 'pointer', fontWeight: '800', fontSize: '9px' }}>
                            🔓 Sblocca
                          </button>
                        )}
                      </div>
                    );
                  }
                  const pulsanteBlocca = isAdminRuolo && !contrattoPrenotato ? (
                    <button onClick={(e) => {
                      e.stopPropagation();
                      const nota = window.prompt(`Bloccare ${car.marca} ${car.modello} a tuo nome?\nMotivo (facoltativo):`, '');
                      if (nota === null) return;
                      scriviBlocco({
                        nome: `${user?.nome || ''} ${user?.cognome || ''}`.trim() || user?.email || 'Admin',
                        ruolo, uid: user?.uid || '', at: new Date().toISOString(), nota: nota.trim(),
                      });
                      logBloccoVeicolo(car, { tipo: 'blocco', bloccatoDa: `${user?.nome || ''} ${user?.cognome || ''}`.trim(), motivo: nota.trim(), user });
                    }}
                      style={{ width: '100%', marginTop: '4px', padding: '5px', background: 'transparent', border: '1px dashed #dc2626', borderRadius: '8px', color: '#dc2626', cursor: 'pointer', fontWeight: '800', fontSize: '9px' }}>
                      🔒 Blocca a mio nome
                    </button>
                  ) : null;
                  if (contrattoPrenotato) {
                    const nomeC = contrattoPrenotato.venditoreNome || '';
                    const isBozza = contrattoPrenotato.stato === 'bozza';
                    return (
                      <div>
                        <button onClick={(e) => { e.stopPropagation(); setPreventivocar(car); }}
                          style={{ width: '100%', padding: '9px', background: isBozza ? 'rgba(99,102,241,0.12)' : 'rgba(245,158,11,0.15)', border: `1px solid ${isBozza ? '#6366f1' : '#f59e0b'}`, borderRadius: '10px', color: isBozza ? '#6366f1' : '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          🔒 {isBozza ? 'IN TRATTATIVA' : 'PRENOTATA'} — Preventiva Ugualmente
                        </button>
                        {nomeC && <div style={{ fontSize: '9px', color: isBozza ? '#6366f1' : '#f59e0b', textAlign: 'center', marginTop: '3px', fontWeight: '700' }}>👤 {nomeC}</div>}
                      </div>
                    );
                  }
                  if (inTratt) {
                    const cognomeTratt = getCognomeTrattativa(car);
                    return (
                      <div>
                        <button onClick={(e) => { e.stopPropagation(); setPreventivocar(car); }}
                          style={{ width: '100%', padding: '9px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '10px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          🔒 IN TRATTATIVA — Preventiva Ugualmente
                        </button>
                        {cognomeTratt && <div style={{ fontSize: '9px', color: '#ef4444', textAlign: 'center', marginTop: '3px', fontWeight: '700' }}>👤 {cognomeTratt}</div>}
                        {isAdminRuolo && (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            const norm5 = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                            const attivo = (contratti || []).find(k => ['bozza','approvato','venduto'].includes(k.stato) && (
                              (car.vin && norm5(k.veicolo?.vin || k.datiExtra?.vin) === norm5(car.vin)) || (car.targa && norm5(k.veicolo?.targa) === norm5(car.targa))));
                            const avviso = attivo
                              ? `ATTENZIONE: questa auto è ancora nel contratto ${attivo.datiExtra?.numeroContratto || attivo.id} (${attivo.stato}) di ${attivo.cliente?.ragioneSociale || attivo.cliente?.cognome || ''}.\n\n`
                              : 'Nessun contratto attivo risulta collegato a questa auto: la prenotazione è rimasta orfana.\n\n';
                            if (!window.confirm(`${avviso}Togliere la trattativa${cognomeTratt ? ` (${cognomeTratt})` : ''} da ${car.marca} ${car.modello}?`)) return;
                            await liberaTrattativaVeicolo(car);
                            await logBloccoVeicolo(car, { tipo: 'liberazione', bloccatoDa: cognomeTratt || '', motivo: attivo ? 'Trattativa liberata con contratto ancora attivo' : 'Trattativa orfana liberata', user });
                          }}
                            style={{ width: '100%', marginTop: '4px', padding: '5px', background: 'transparent', border: '1px dashed #10b981', borderRadius: '8px', color: '#10b981', cursor: 'pointer', fontWeight: '800', fontSize: '9px' }}>
                            ↺ Libera trattativa
                          </button>
                        )}
                        {pulsanteBlocca}
                      </div>
                    );
                  }
                  return (
                    <div>
                    <button onClick={(e) => { e.stopPropagation(); setPreventivocar(car); }}
                      style={{ width: '100%', padding: '9px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      📋 PREVENTIVO
                    </button>
                    {pulsanteBlocca}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Carica altri */}
      {filtered.length > paginatedVehicles.length && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <button onClick={() => setPagina(p => p + 1)}
            style={{ padding: '12px 32px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: '14px', color: T.text, cursor: 'pointer', fontWeight: '700', fontSize: '12px' }}>
            Carica altri ({filtered.length - paginatedVehicles.length} rimanenti)
          </button>
        </div>
      )}

      {selectedCar && <VehicleModal car={selectedCar} user={user} inTrattativa={isInTrattativa(selectedCar)} cognomeTrattativa={getCognomeTrattativa(selectedCar)} onClose={() => setSelectedCar(null)} onPreventivo={(car) => { setSelectedCar(null); setPreventivocar(car); }} preventivi={preventivi} />}
      {preventivocar && <PreventivoModal car={preventivocar} user={user} db={db} onClose={() => setPreventivocar(null)} onSaved={() => setPreventivocar(null)} />}
      {colonnePickerOpen && (
        <ColonneExportModal
          titolo={exportSoloSelezionate ? `Esporta auto selezionate (${nSel})` : 'Esporta Stock'}
          colonneDisponibili={COLONNE_EXPORT_STOCK}
          onClose={() => { setColonnePickerOpen(false); setExportSoloSelezionate(false); }}
          onEsporta={async (colonneScelte) => {
            // Solo le auto flaggate (con lo stato aggiornato) oppure tutte quelle filtrate
            const listaExport = exportSoloSelezionate
              ? (vehicles || []).filter(v => selezionate[chiaveAuto(v)])
              : filtered;
            setEsportandoExcel(true);
            try {
              const [snapOff, snapCar, snapDoc] = await Promise.all([
                get(ref(db, 'service_interventi')),
                get(ref(db, 'service_carrozzeria')),
                get(ref(db, 'documenti_veicoli')),
              ]);
              const interventiOfficina = snapOff.exists() ? Object.values(snapOff.val()) : [];
              const interventiCarrozzeria = snapCar.exists() ? Object.values(snapCar.val()) : [];
              const documentiVeicoli = snapDoc.exists() ? snapDoc.val() : {};
              esportaStockExcel(listaExport, noteTextMap, interventiOfficina, interventiCarrozzeria, contratti, colonneScelte, documentiVeicoli);
            } catch (e) {
              alert('Errore nel recupero interventi/documenti: ' + e.message + ' — esporto comunque senza quei dati.');
              esportaStockExcel(listaExport, noteTextMap, [], [], contratti, colonneScelte, {});
            }
            setEsportandoExcel(false);
            setColonnePickerOpen(false);
            setExportSoloSelezionate(false);
          }}
        />
      )}

      {nSel > 0 && (() => {
        const lista = Object.entries(selezionate).map(([k, c]) => {
          const live = (vehicles || []).find(v => chiaveAuto(v) === k);
          return [k, live || c];
        });
        const totPrezzo = lista.reduce((t, [, c]) => t + (parseFloat(c.prezzo) || 0), 0);
        const mediaGg = Math.round(lista.reduce((t, [, c]) => t + (parseFloat(c.giorni) || 0), 0) / lista.length);
        const fmtE0 = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
        const contenuto = (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: '12px', fontWeight: '900', color: T.text }}>☑ Auto selezionate ({nSel})</div>
              <button onClick={() => setPannelloSelOpen(false)} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: '16px', cursor: 'pointer' }}>{isMobile ? '▾' : '▸'}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '10px 14px' }}>
              <div style={{ background: T.bg, borderRadius: '8px', padding: '8px' }}><div style={{ fontSize: '9px', color: T.textMuted }}>Valore totale</div><div style={{ fontSize: '14px', fontWeight: '900', color: '#10b981' }}>{fmtE0(totPrezzo)}</div></div>
              <div style={{ background: T.bg, borderRadius: '8px', padding: '8px' }}><div style={{ fontSize: '9px', color: T.textMuted }}>Giacenza media</div><div style={{ fontSize: '14px', fontWeight: '900', color: '#f59e0b' }}>{mediaGg} gg</div></div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px' }}>
              {lista.map(([k, c]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.marca} {c.modello}</div>
                    <div style={{ fontSize: '9px', color: T.textMuted }}>{c.targa || c.vin || '—'} · {fmtE0(c.prezzo)} · {c.giorni ?? '—'} gg · {c.status}</div>
                  </div>
                  <button onClick={() => toggleSel(c)} title="Togli dalla selezione" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', padding: '12px 14px', borderTop: `1px solid ${T.border}` }}>
              {puoEsportareExcel(user) && (
              <button onClick={() => { setExportSoloSelezionate(true); setColonnePickerOpen(true); }}
                style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: '#10b981', color: '#fff', fontWeight: '900', fontSize: '11px', cursor: 'pointer' }}>
                Esporta Excel ({nSel})
              </button>
              )}
              <button onClick={() => { if (window.confirm(`Togliere tutte le ${nSel} auto dalla selezione?`)) setSelezionate({}); }}
                style={{ padding: '10px 12px', borderRadius: '10px', border: `1px solid ${T.border}`, background: 'transparent', color: '#ef4444', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>
                Svuota
              </button>
            </div>
          </>
        );
        if (isMobile) {
          return pannelloSelOpen ? (
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1500, maxHeight: '70vh', display: 'flex', flexDirection: 'column', background: T.surface || T.card, borderTop: `2px solid #3b82f6`, borderRadius: '16px 16px 0 0', boxShadow: '0 -8px 24px rgba(0,0,0,0.35)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
              {contenuto}
            </div>
          ) : (
            <button onClick={() => setPannelloSelOpen(true)}
              style={{ position: 'fixed', left: '12px', right: '12px', bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))', zIndex: 1500, padding: '12px', borderRadius: '12px', border: 'none', background: '#3b82f6', color: '#fff', fontWeight: '900', fontSize: '12px', boxShadow: '0 6px 18px rgba(0,0,0,0.35)' }}>
              ☑ {nSel} auto selezionate · {fmtE0(totPrezzo)} — Apri
            </button>
          );
        }
        return pannelloSelOpen ? (
          <div style={{ position: 'fixed', top: '80px', right: '16px', bottom: '16px', width: '300px', zIndex: 1500, display: 'flex', flexDirection: 'column', background: T.surface || T.card, border: `1px solid #3b82f6`, borderRadius: '14px', boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
            {contenuto}
          </div>
        ) : (
          <button onClick={() => setPannelloSelOpen(true)} title="Mostra le auto selezionate"
            style={{ position: 'fixed', top: '50%', right: 0, transform: 'translateY(-50%)', zIndex: 1500, padding: '14px 8px', borderRadius: '10px 0 0 10px', border: 'none', background: '#3b82f6', color: '#fff', fontWeight: '900', fontSize: '12px', writingMode: 'vertical-rl', cursor: 'pointer', boxShadow: '0 6px 18px rgba(0,0,0,0.35)' }}>
            ◂ {nSel} selezionate
          </button>
        );
      })()}
      {aggiungiAutoOpen && (
        <AggiungiAutoStockModal
          user={user}
          sediDisponibili={[...new Set(vehicles.map(v => v.sede).filter(x => x && x !== 'N.D.'))].sort()}
          onClose={() => setAggiungiAutoOpen(false)}
          onSalvato={(nuovo) => { setVehiclesGrezzi(prev => [...prev, nuovo]); setAggiungiAutoOpen(false); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB PREVENTIVI — lista di tutti i preventivi salvati
// ═══════════════════════════════════════════════════════════════════════════════
function PreventiviView({ user, preventiviGlobaliRaw = [] }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [modificando, setModificando] = useState(null);
  const [convertendo, setConvertendo] = useState(null);
  const [nuovoPreventivo, setNuovoPreventivo] = useState(false);
  const [nuovoVeicoloNuovo, setNuovoVeicoloNuovo] = useState(false);
  const [preventivoOffStock, setPreventivoOffStock] = useState(false);
  const offStockCar = useMemo(() => ({ id: `offstock_${Date.now()}`, marca: '', modello: '', versione: '', anno: '', km: 0, carburante: '', targa: '', vin: '', coloreVeicolo: '', prezzo: 0, offStock: true }), []);
  const [viewMode, setViewMode] = useState('list');
  const [fDataDa, setFDataDa] = useState('');
  const [fDataA, setFDataA] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [filtroSede, setFiltroSede] = useState('tutti'); // tutti/agnano/nola
  const [filtroVeicolo, setFiltroVeicolo] = useState('tutti'); // tutti/nuovo/usato
  const [subTabConsulente, setSubTabConsulente] = useState('miei'); // 'miei' | 'altri' — solo per ruolo consulente

  // ─── ORA LEGGE DALLA CACHE CONDIVISA — preventiviGlobaliRaw arriva già
  // cachata una volta al giorno + delta ogni 2 minuti (stesso principio di
  // Radar/Stock/Finance/Contratti). Nessun download separato per questa
  // tab: prima faceva la propria query "ultimi 100 + ricerca on-demand",
  // duplicando quello che il resto dell'app aveva già scaricato.
  const preventiviProp = preventiviGlobaliRaw;
  const loading = false;
  const searchDebounced = useDebounce(search, 220);

  const preventivi = useMemo(() => {
    return (preventiviProp || [])
      .map(p => ({ ...p, _ts: new Date(p.dataCreazione || p.data || 0).getTime() }))
      .sort((a, b) => b._ts - a._ts);
  }, [preventiviProp]);

  const filtered = useMemo(() => {
    let list = preventivi;
    const ruolo = user.ruolo;
    const fullName = `${user.nome} ${user.cognome}`.toLowerCase().trim();
    if (ruolo === 'consulente' || ruolo === 'brand_manager_se') {
      // I consulenti (e i brand manager SE, che sui preventivi devono vedere
      // tutti come un consulente normale — la restrizione ai soli marchi
      // Sportequipe/ICH-X resta SOLO sui Contratti, non qui) vedono i
      // preventivi di tutti, divisi in due tab: "Miei" e "Di tutti gli altri".
      list = subTabConsulente === 'miei'
        ? list.filter(p => p.venditoreNome?.toLowerCase().trim() === fullName)
        : list.filter(p => p.venditoreNome?.toLowerCase().trim() !== fullName);
    } else if (ruolo === 'partner') {
      list = list.filter(p => p.venditoreNome?.toLowerCase().trim() === fullName);
    }
    if (searchDebounced) {
      const s = searchDebounced.toLowerCase();
      list = list.filter(p => {
        const nome = `${p.cliente?.nome||''} ${p.cliente?.cognome||''} ${p.cliente?.ragioneSociale||''}`.toLowerCase();
        const veicolo = `${p.veicolo?.marca||''} ${p.veicolo?.modello||''}`.toLowerCase();
        const targa = (p.veicolo?.targa||'').toLowerCase();
        const vin = (p.veicolo?.vin||p.datiExtra?.vin||'').toLowerCase();
        return nome.includes(s) || veicolo.includes(s) || (p.venditoreNome||'').toLowerCase().includes(s) || targa.includes(s) || vin.includes(s);
      });
    }
    // Filtri data — usa _ts pre-calcolato
    if (fDataDa) { const ts = new Date(fDataDa).getTime(); list = list.filter(p => p._ts >= ts); }
    if (fDataA)  { const ts = new Date(fDataA + 'T23:59:59').getTime(); list = list.filter(p => p._ts <= ts); }
    if (filtroTipo === 'b2b') list = list.filter(p => !!p.cliente?.b2b);
    if (filtroTipo === 'b2c') list = list.filter(p => !p.cliente?.b2b);
    if (filtroVeicolo !== 'tutti') {
      list = list.filter(p => {
        const t = (p.veicolo?.tipo || '').toLowerCase().trim();
        const isNuovo = t === 'nuovo' || t === 'new';
        return filtroVeicolo === 'nuovo' ? isNuovo : !isNuovo;
      });
    }
    const frontSede = getFrontSedeFilter(user);
    if (filtroSede !== 'tutti') {
      list = list.filter(p => getSede((p.venditoreNome||'').toLowerCase()) === filtroSede);
    } else if (frontSede) {
      list = list.filter(p => getSede((p.venditoreNome||'').toLowerCase()) === frontSede);
    }
    return list;
  }, [preventivi, searchDebounced, user, fDataDa, fDataA, filtroTipo, filtroSede, filtroVeicolo, subTabConsulente]);
  if (loading) return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text }}>Caricamento preventivi...</div>;

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      {/* HEADER */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900', color: T.text }}>Preventivi</h2>
            <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>{filtered.length} preventivi</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {!isMobile && (
              <div style={{ display: 'flex', background: T.bg, borderRadius: '10px', padding: '3px', gap: '2px' }}>
                <button onClick={() => setViewMode('cards')} style={{ padding: '6px 10px', fontSize: '10px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: viewMode === 'cards' ? '#f59e0b' : 'transparent', color: viewMode === 'cards' ? '#fff' : T.textSub }}>⊞</button>
                <button onClick={() => setViewMode('list')} style={{ padding: '6px 10px', fontSize: '10px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: viewMode === 'list' ? '#f59e0b' : 'transparent', color: viewMode === 'list' ? '#fff' : T.textSub }}>≡</button>
              </div>
            )}
            <button onClick={() => setNuovoPreventivo(true)} style={{ padding: '9px 14px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
              + Preventivo
            </button>
            <button onClick={() => setNuovoVeicoloNuovo(true)} style={{ padding: '9px 14px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
              + Nuovo
            </button>
            <button onClick={() => setPreventivoOffStock(true)} style={{ padding: '9px 14px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
              + Preventivo Off Stock
            </button>
            {['admin','super_admin'].includes(user?.ruolo) && (
              puoEsportareExcel(user) && (
              <button onClick={() => exportToExcel(filtered, [], 'RadaR_Preventivi')} style={{ padding: '9px 14px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
                📊 Excel ({filtered.length})
              </button>
              )
            )}
          </div>
        </div>
        {/* TAB Miei / Di tutti gli altri — consulente e brand_manager_se */}
        {(user.ruolo === 'consulente' || user.ruolo === 'brand_manager_se') && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', background: T.bg, borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
            <button onClick={() => setSubTabConsulente('miei')}
              style={{ padding: '9px 16px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontWeight: '900', fontSize: '12px', background: subTabConsulente === 'miei' ? '#f59e0b' : 'transparent', color: subTabConsulente === 'miei' ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
              👤 I miei preventivi
            </button>
            <button onClick={() => setSubTabConsulente('altri')}
              style={{ padding: '9px 16px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontWeight: '900', fontSize: '12px', background: subTabConsulente === 'altri' ? '#f59e0b' : 'transparent', color: subTabConsulente === 'altri' ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
              👥 Preventivi di tutti gli altri
            </button>
          </div>
        )}
        <input
          placeholder="🔍 Cerca cliente, veicolo, targa, telaio..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', padding: '9px 14px', color: T.text, fontSize: '12px', outline: 'none', width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}
        />
        {/* FILTRO DATE */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: T.textMuted }}>📅 Periodo:</div>
          <input type="date" value={fDataDa} onChange={e => setFDataDa(e.target.value)}
            style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '8px', padding: '6px 10px', color: T.text, fontSize: '11px', outline: 'none' }} />
          <span style={{ color: T.textMuted, fontSize: '11px' }}>→</span>
          <input type="date" value={fDataA} onChange={e => setFDataA(e.target.value)}
            style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '8px', padding: '6px 10px', color: T.text, fontSize: '11px', outline: 'none' }} />
          {(fDataDa || fDataA) && (
            <button onClick={() => { setFDataDa(''); setFDataA(''); }}
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '6px 10px', color: '#ef4444', cursor: 'pointer', fontSize: '10px', fontWeight: '700' }}>
              ✕ Reset
            </button>
          )}
          {(fDataDa || fDataA) && <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700' }}>{filtered.length} risultati</span>}
        </div>
        {/* FILTRO B2B/B2C */}
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
          {[['tutti','Tutti'],['b2b','🏢 B2B'],['b2c','👤 B2C']].map(([val,label]) => (
            <button key={val} onClick={() => setFiltroTipo(val)}
              style={{ padding: '6px 12px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroTipo === val ? '#6366f1' : T.bg, color: filtroTipo === val ? '#fff' : T.textSub }}>
              {label}
            </button>
          ))}
          <div style={{ width: '1px', background: T.border, margin: '0 4px' }} />
          {[['tutti','Tutte le sedi'],['agnano','📍 Agnano'],['nola','📍 Nola'],['salerno','📍 Salerno']].map(([val,label]) => (
            <button key={val} onClick={() => setFiltroSede(val)}
              style={{ padding: '6px 12px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroSede === val ? '#10b981' : T.bg, color: filtroSede === val ? '#fff' : T.textSub }}>
              {label}
            </button>
          ))}
          <div style={{ width: '1px', background: T.border, margin: '0 4px' }} />
          {[['tutti','Tutti i veicoli'],['nuovo','✨ Nuovo'],['usato','🔧 Usato/KM0']].map(([val,label]) => (
            <button key={val} onClick={() => setFiltroVeicolo(val)}
              style={{ padding: '6px 12px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroVeicolo === val ? '#f59e0b' : T.bg, color: filtroVeicolo === val ? '#fff' : T.textSub }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <div style={{ fontSize: '14px', fontWeight: '700' }}>Nessun preventivo ancora</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>Vai su STOCK e clicca "PREVENTIVO" su una vettura</div>
        </div>

      ) : (isMobile || viewMode === 'cards') ? (
        // ── CARDS — sempre su mobile ──
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map((p, i) => {
            const nomeCliente = p.cliente?.ragioneSociale || `${p.cliente?.nome||''} ${p.cliente?.cognome||''}`.trim() || 'Cliente';
            const prezzo = p.prezzo || {};
            const pL = parseFloat(prezzo.prezzoListino || p.veicolo?.prezzo || 0);
            const sc = parseFloat(prezzo.sconto || 0);
            const ipt = parseFloat(prezzo.ipt || 0);
            const perm = valorePermuteTot(prezzo);
            const cap = parseFloat(prezzo.caparra || 0);
            const fin = parseFloat(prezzo.importoFinanziato || 0);
            const totS1 = Object.entries(p.servizi||{}).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0);
            const saldo = (pL - sc + ipt) + totS1 - perm - cap - fin;
            const data = new Date(p.dataCreazione).toLocaleDateString('it-IT');
            return (
              <div key={p.id||i} onClick={() => setSelected(p)}
                style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '14px 16px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b'}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '900', letterSpacing: '1px', marginBottom: '2px' }}>PREVENTIVO · {new Date(p.dataCreazione).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}</div>
                    <div style={{ fontSize: '14px', fontWeight: '900', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nomeCliente}</div>
                    <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{p.veicolo?.marca} {p.veicolo?.modello}{p.veicolo?.targa ? ` · ${p.veicolo.targa}` : ''}</div>
                    <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '1px' }}>{p.venditoreNome}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {p.veicolo?.mainImg && <img src={p.veicolo.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '56px', height: '38px', objectFit: 'cover', borderRadius: '6px', marginBottom: '4px', display: 'block' }} />}
                    <div style={{ fontSize: '13px', fontWeight: '900', color: '#10b981' }}>{fmt(saldo)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      ) : (
        // ── LISTA — solo desktop ──
        <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px 100px', padding: '10px 16px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
            {['CONSULENTE', 'CLIENTE', 'VEICOLO', 'SALDO', 'DATA'].map(h => (
              <div key={h} style={{ fontSize: '8px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px' }}>{h}</div>
            ))}
          </div>
          {filtered.map((p, i) => {
            const nomeCliente = p.cliente?.ragioneSociale || `${p.cliente?.nome||''} ${p.cliente?.cognome||''}`.trim() || '—';
            const data = new Date(p.dataCreazione).toLocaleDateString('it-IT');
            const prezzo = p.prezzo || {};
            const pL = parseFloat(prezzo.prezzoListino || p.veicolo?.prezzo || 0);
            const sc = parseFloat(prezzo.sconto || 0);
            const ipt = parseFloat(prezzo.ipt || 0);
            const perm = valorePermuteTot(prezzo);
            const cap = parseFloat(prezzo.caparra || 0);
            const fin = parseFloat(prezzo.importoFinanziato || 0);
            const totS2 = Object.entries(p.servizi||{}).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0);
            const saldo = (pL - sc + ipt) + totS2 - perm - cap - fin;
            return (
              <div key={p.id||i} onClick={() => setSelected(p)}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px 100px', padding: '11px 16px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontSize: '11px', fontWeight: '700', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.venditoreNome || '—'}</div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nomeCliente}</div>
                <div style={{ fontSize: '11px', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.veicolo?.marca} {p.veicolo?.modello}{p.veicolo?.targa ? ` · ${p.veicolo.targa}` : ''}</div>
                <div style={{ fontSize: '11px', fontWeight: '900', color: '#10b981' }}>{fmt(saldo)}</div>
                <div style={{ fontSize: '10px', color: T.textMuted }}>{new Date(p.dataCreazione).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</div>
              </div>
            );
          })}
        </div>
      )}

      {selected && <PreventivoDetailModal p={selected} onClose={() => setSelected(null)} user={user} onModifica={(p) => { setSelected(null); setModificando(p); }} onElimina={() => setSelected(null)} onConvertì={async (p) => {
        // Rilegge il preventivo aggiornato da Firebase prima di convertire
        try {
          const snap = await get(ref(db, `preventivi/${p.id}`));
          const fresh = snap.exists() ? { ...snap.val(), id: p.id } : p;
          setSelected(null);
          setConvertendo(fresh);
        } catch(e) {
          setSelected(null);
          setConvertendo(p);
        }
      }} />}
      {modificando && (
        <PreventivoModal car={modificando.veicolo} user={user} db={db} onClose={() => setModificando(null)} onSaved={() => setModificando(null)} preventivoEsistente={modificando} preventivi={preventivi} />
      )}
      {convertendo && (
        <ContrattoModal preventivo={convertendo} user={user} db={db} onClose={() => setConvertendo(null)} onSaved={() => setConvertendo(null)} />
      )}
      {nuovoPreventivo && (
        <NuovoPreventivoAgile
          user={user}
          db={db}
          preventivi={preventivi}
          onClose={() => setNuovoPreventivo(false)}
          onSaved={() => setNuovoPreventivo(false)}
        />
      )}
      {nuovoVeicoloNuovo && (
        <NuovoVeicoloNuovoFlow
          user={user}
          db={db}
          preventivi={preventivi}
          onClose={() => setNuovoVeicoloNuovo(false)}
          onSaved={() => setNuovoVeicoloNuovo(false)}
        />
      )}
      {preventivoOffStock && (
        <PreventivoModal
          car={offStockCar}
          user={user}
          db={db}
          preventivi={preventivi}
          onClose={() => setPreventivoOffStock(false)}
          onSaved={() => setPreventivoOffStock(false)}
        />
      )}
    </div>
  );
}

// ─── DETTAGLIO PREVENTIVO ────────────────────────────────────────────────────
// Lista fallback marche europee nel caso CarQuery non risponda
const MARCHE_FALLBACK = [
  'Alfa Romeo','Audi','BMW','Citroen','Cupra','Dacia','DR Automobiles',
  'DS Automobiles','Ferrari','Fiat','Ford','Honda','Hyundai','ICH-X','Ineos',
  'Isuzu','Jaguar','Jeep','Kia','Lamborghini','Lancia','Land Rover',
  'Maserati','Mazda','Mercedes-Benz','MG','Mini','Mitsubishi','Nissan',
  'Opel','Peugeot','Porsche','Renault','Seat','Skoda','Smart','Sportequipe',
  'Subaru','Suzuki','Tesla','Tiger','Toyota','Volkswagen','Volvo',
];

// ═══════════════════════════════════════════════════════════════════════════════
// NUOVO VEICOLO NUOVO — scegli marca/modello/anno poi apre PreventivoModal
// ═══════════════════════════════════════════════════════════════════════════════
const MOTORNET_PHP = 'https://radargas.it/motornet-proxy.php';
const MOTORNET_LOCAL = 'http://localhost:3002';
const LEADSPARK_PHP = 'https://radargas.it/leadspark-proxy.php';

function fetchWithTimeout(url, ms) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(id));
}

async function motornetFetch(endpoint) {
  const url = new URL(endpoint, 'http://localhost');
  const path = url.pathname.replace('/motornet/', '');
  const params = new URLSearchParams(url.search);
  params.set('action', path);
  const phpUrl = `${MOTORNET_PHP}?${params.toString()}`;
  const localUrl = `${MOTORNET_LOCAL}${endpoint}`;

  // Prova prima il proxy PHP (sempre attivo su radargas.it)
  try {
    const res = await fetchWithTimeout(phpUrl, 15000);
    if (res.ok) {
      const data = await res.json();
      const isEmpty = (data.modelli && data.modelli.length === 0) ||
                      (data.versioni && data.versioni.length === 0);
      if (!data.error && !isEmpty) return data;
    }
  } catch(e) { /* PHP non raggiungibile o timeout */ }

  // Fallback al server locale (solo se il PC è acceso)
  try {
    const res2 = await fetchWithTimeout(localUrl, 8000);
    if (!res2.ok) throw new Error(`Motornet error ${res2.status}`);
    return res2.json();
  } catch(e) {
    throw new Error('Motornet non raggiungibile. Verifica la connessione.');
  }
}

function NuovoVeicoloNuovoFlow({ user, db, preventivi, onClose, onSaved }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [step, setStep] = useState('marca');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [marche, setMarche] = useState([]);
  const [modelli, setModelli] = useState([]);
  const [versioni, setVersioni] = useState([]);
  const [searchMarca, setSearchMarca] = useState('');

  const [sel, setSel] = useState({
    marca: '', marcaAcronimo: '',
    modello: '', codiceModello: '',
    versione: null, // oggetto completo versione
  });

  const [dettaglio, setDettaglio] = useState(null);
  const [immagini, setImmagini] = useState([]);

  // Carica marche
  useEffect(() => {
    setLoading(true);
    motornetFetch('/motornet/marche')
      .then(data => {
        // Motornet restituisce marche come oggetto {ABA: {acronimo, nome}, ...}
        const obj = data.marche || data.Marche || {};
        const list = Array.isArray(obj) ? obj : Object.values(obj);
        setMarche(list.sort((a,b) => (a.nome||'').localeCompare(b.nome||'', 'it')));
      })
      .catch(e => setError('Impossibile caricare le marche: ' + e.message))
      .finally(() => setLoading(false));
  }, []);

  const marcheFiltrate = useMemo(() => {
    if (!searchMarca.trim()) return marche;
    const s = searchMarca.toLowerCase();
    return marche.filter(m => (m.nome || m.descrizione || '').toLowerCase().includes(s));
  }, [marche, searchMarca]);

  const selectMarca = (m) => {
    const acronimo = m.acronimo || m.marca_acronimo || '';
    const nome = m.nome || m.descrizione || '';
    setSel(s => ({ ...s, marca: nome, marcaAcronimo: acronimo }));
    setLoading(true); setError('');
    motornetFetch(`/motornet/modelli?marca=${encodeURIComponent(acronimo)}`)
      .then(data => {
        setModelli(data.modelli || []);
        setStep('modello');
      })
      .catch(e => setError('Errore caricamento modelli: ' + e.message))
      .finally(() => setLoading(false));
  };

  const selectModello = (m) => {
    setSel(s => ({ ...s, modello: m.desc_gamma_mod || m.descrizione_modello, codiceModello: m.cod_gamma_mod || m.codice_modello }));
    setLoading(true); setError('');
    motornetFetch(`/motornet/versioni?marca=${encodeURIComponent(sel.marcaAcronimo)}&codice_modello=${encodeURIComponent(m.cod_gamma_mod || m.codice_modello)}`)
      .then(data => {
        setVersioni(data.versioni || []);
        setStep('versione');
      })
      .catch(e => setError('Errore caricamento versioni: ' + e.message))
      .finally(() => setLoading(false));
  };

  const selectVersione = (v) => {
    setSel(s => ({ ...s, versione: v }));
    setLoading(true); setError('');
    const codice = v.codice_eurotax || v.codice_motornet || '';
    Promise.all([
      motornetFetch(`/motornet/dettaglio?codice_eurotax=${codice}`),
      motornetFetch(`/motornet/immagini?codice_eurotax=${codice}`),
    ])
      .then(([det, img]) => {
        setDettaglio(det.modello || det);
        const imgs = det.modello?.immagine ? [det.modello.immagine] : [];
        if (img.immagini) imgs.push(...img.immagini.slice(0, 3).map(i => i.url || i));
        setImmagini([...new Set(imgs)]);
        setStep('conferma');
      })
      .catch(e => setError('Errore caricamento dettaglio: ' + e.message))
      .finally(() => setLoading(false));
  };

  const [apriPreventivo, setApriPreventivo] = useState(false);

  if (apriPreventivo && dettaglio) {
    const veicoloMotornet = {
      id: `motornet_${dettaglio.codice_eurotax || dettaglio.codice_motornet}`,
      marca: dettaglio.marca || sel.marca,
      modello: dettaglio.allestimento ? dettaglio.allestimento.split(' ').slice(0,2).join(' ') : sel.modello,
      versione: dettaglio.allestimento || sel.versione?.descrizione_versione || '',
      tipo: 'Nuovo',
      status: 'FREE',
      carburante: dettaglio.alimentazione || '',
      km: 0,
      prezzo: parseFloat(dettaglio.prezzo_listino || 0),
      colore: '',
      registrazione: '',
      sede: '',
      targa: '',
      vin: '',
      mainImg: immagini[0] || dettaglio.immagine || '',
      images: immagini,
      optionals: '',
      giorni: 0,
      alert: false,
      // Dati tecnici Motornet
      codice_eurotax: dettaglio.codice_eurotax,
      codice_motornet: dettaglio.codice_motornet,
      kw: dettaglio.kw,
      hp: dettaglio.hp,
      euro: dettaglio.euro,
      emissioni_co2: dettaglio.emissioni_co2,
      data_listino: dettaglio.data_listino,
    };
    return <PreventivoModal car={veicoloMotornet} user={user} db={db} preventivi={preventivi} onClose={onClose} onSaved={onSaved} />;
  }

  const inputS = { width:'100%', padding:'11px 14px', background:T.inputBg, border:`1px solid ${T.border2}`, borderRadius:'12px', color:T.text, fontSize:'14px', outline:'none', boxSizing:'border-box', marginBottom:'14px' };

  const breadcrumb = [
    ['marca','Marca'],['modello','Modello'],['versione','Versione'],['conferma','Conferma']
  ];

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:2000, display:'flex', alignItems:isMobile?'flex-end':'center', justifyContent:'center', padding:isMobile?0:'16px', backdropFilter:'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.surface, borderRadius:isMobile?'24px 24px 0 0':'24px', border:`1px solid ${T.border}`, width:'100%', maxWidth:'600px', maxHeight:isMobile?'92vh':'88vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* HEADER */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
          {isMobile && <div style={{ width:'36px', height:'4px', background:T.border2, borderRadius:'2px', margin:'0 auto 16px' }} />}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:'9px', color:'#3b82f6', fontWeight:'900', letterSpacing:'2px', marginBottom:'4px' }}>
                🚗 PREVENTIVO VEICOLO NUOVO — powered by Motornet
              </div>
              <div style={{ fontSize:'15px', fontWeight:'900', color:T.text }}>
                {step==='marca' && 'Seleziona Marca'}
                {step==='modello' && `${sel.marca} — Seleziona Modello`}
                {step==='versione' && `${sel.marca} ${sel.modello} — Versione`}
                {step==='conferma' && 'Conferma Veicolo'}
              </div>
            </div>
            <button onClick={onClose} style={{ background:T.bg, border:'none', color:T.text, borderRadius:'10px', padding:'8px 14px', cursor:'pointer', fontWeight:'900', fontSize:'18px' }}>×</button>
          </div>
          {/* Breadcrumb */}
          <div style={{ display:'flex', gap:'6px', marginTop:'10px', flexWrap:'wrap' }}>
            {breadcrumb.map(([s,label],i) => (
              <div key={s} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                {i > 0 && <span style={{ color:T.textMuted, fontSize:'10px' }}>›</span>}
                <span style={{ fontSize:'10px', fontWeight:'700', color: step===s ? '#3b82f6' : breadcrumb.findIndex(b=>b[0]===step) > i ? T.text : T.textMuted }}>
                  {label}{sel[s==='versione'?'versione':s] && s!=='conferma' && s!=='versione' ? `: ${typeof sel[s]==='string'?sel[s].slice(0,20):'✓'}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CONTENUTO */}
        <div style={{ overflowY:'auto', flex:1, padding:'16px 24px' }}>
          {error && <div style={{ padding:'10px 14px', background:'rgba(239,68,68,0.1)', border:'1px solid #ef4444', borderRadius:'10px', color:'#ef4444', fontSize:'12px', marginBottom:'14px' }}>⚠️ {error}</div>}
          {loading && <div style={{ textAlign:'center', padding:'30px', color:T.textMuted }}>⏳ Caricamento Motornet...</div>}

          {/* STEP MARCA */}
          {!loading && step==='marca' && (
            <>
              <input autoFocus placeholder="🔍 Cerca marca..." value={searchMarca} onChange={e => setSearchMarca(e.target.value)} style={inputS} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'8px' }}>
                {marcheFiltrate.map((m,i) => {
                  const nome = m.nome || m.descrizione || '';
                  return (
                    <button key={i} onClick={() => selectMarca(m)}
                      style={{ padding:'10px 12px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:'10px', color:T.text, cursor:'pointer', fontWeight:'700', fontSize:'12px', textAlign:'left' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='#3b82f6'}
                      onMouseLeave={e => e.currentTarget.style.borderColor=T.border}
                    >{nome}</button>
                  );
                })}
              </div>
            </>
          )}

          {/* STEP MODELLO */}
          {!loading && step==='modello' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {modelli.map((m,i) => (
                <button key={i} onClick={() => selectModello(m)}
                  style={{ padding:'12px 14px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:'10px', color:T.text, cursor:'pointer', fontWeight:'700', fontSize:'13px', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='#3b82f6'}
                  onMouseLeave={e => e.currentTarget.style.borderColor=T.border}
                >
                  <div>
                    <div>{m.desc_gamma_mod || m.descrizione_modello}</div>
                    {m.da && <div style={{ fontSize:'10px', color:T.textMuted, marginTop:'2px' }}>dal {m.da}{m.a ? ` al ${m.a}` : ' — in produzione'}</div>}
                  </div>
                  <span style={{ color:T.textMuted, fontSize:'16px' }}>›</span>
                </button>
              ))}
              {modelli.length === 0 && <div style={{ textAlign:'center', padding:'30px', color:T.textMuted }}>Nessun modello trovato</div>}
            </div>
          )}

          {/* STEP VERSIONE */}
          {!loading && step==='versione' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {versioni.map((v,i) => (
                <button key={i} onClick={() => selectVersione(v)}
                  style={{ padding:'12px 14px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:'10px', color:T.text, cursor:'pointer', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='#3b82f6'}
                  onMouseLeave={e => e.currentTarget.style.borderColor=T.border}
                >
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'700' }}>{v.descrizione_versione || v.allestimento || ''}</div>
                    <div style={{ fontSize:'10px', color:T.textMuted, marginTop:'2px' }}>
                      {v.alimentazione || ''} · {v.kw ? `${v.kw} kW / ${v.hp || ''} CV` : ''} · {v.cambio || ''}
                    </div>
                    {v.prezzo_listino && <div style={{ fontSize:'12px', fontWeight:'900', color:'#10b981', marginTop:'3px' }}>
                      {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(parseFloat(v.prezzo_listino))}
                    </div>}
                  </div>
                  <span style={{ color:T.textMuted, fontSize:'16px' }}>›</span>
                </button>
              ))}
              {versioni.length === 0 && <div style={{ textAlign:'center', padding:'30px', color:T.textMuted }}>Nessuna versione trovata</div>}
            </div>
          )}

          {/* STEP CONFERMA */}
          {!loading && step==='conferma' && dettaglio && (
            <div>
              {immagini[0] && (
                <div style={{ marginBottom:'16px', borderRadius:'12px', overflow:'hidden', border:`1px solid ${T.border}` }}>
                  <img src={immagini[0]} alt="" style={{ width:'100%', height:'180px', objectFit:'cover' }} onError={e => e.target.style.display='none'} />
                </div>
              )}
              <div style={{ background:T.bg, borderRadius:'14px', padding:'16px', border:`1px solid ${T.border}`, marginBottom:'12px' }}>
                <div style={{ fontSize:'10px', color:'#3b82f6', fontWeight:'900', marginBottom:'10px', letterSpacing:'1px' }}>DATI MOTORNET</div>
                {[
                  ['Marca', dettaglio.marca],
                  ['Modello', dettaglio.modello],
                  ['Versione', dettaglio.allestimento],
                  ['Alimentazione', dettaglio.alimentazione],
                  ['Potenza', `${dettaglio.kw} kW / ${dettaglio.hp} CV`],
                  ['Emissioni CO₂', dettaglio.emissioni_co2 ? `${dettaglio.emissioni_co2} g/km (Euro ${dettaglio.euro})` : '—'],
                  ['Prezzo Listino', dettaglio.prezzo_listino ? new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(parseFloat(dettaglio.prezzo_listino)) : '—'],
                  ['Data Listino', dettaglio.data_listino],
                  ['Codice Eurotax', dettaglio.codice_eurotax],
                ].map(([l,v]) => v && (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${T.border}`, fontSize:'12px' }}>
                    <span style={{ color:T.textSub }}>{l}</span>
                    <span style={{ color:T.text, fontWeight:'600', textAlign:'right', maxWidth:'60%' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', gap:'10px', flexShrink:0 }}>
          <button onClick={() => {
            if (step==='marca') onClose();
            else if (step==='modello') setStep('marca');
            else if (step==='versione') setStep('modello');
            else if (step==='conferma') setStep('versione');
          }} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:'12px', padding:'11px 20px', color:T.text, cursor:'pointer', fontWeight:'800', fontSize:'11px' }}>
            {step==='marca' ? 'Annulla' : '← Indietro'}
          </button>
          {step==='conferma' && dettaglio && (
            <button onClick={() => setApriPreventivo(true)}
              style={{ flex:1, padding:'11px', background:'linear-gradient(135deg, #3b82f6, #2563eb)', border:'none', borderRadius:'12px', color:'#fff', cursor:'pointer', fontWeight:'900', fontSize:'12px' }}>
              📋 Crea Preventivo — {dettaglio.marca} {dettaglio.allestimento?.split(' ').slice(0,3).join(' ')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NuovoPreventivoAgile({ user, db, preventivi, onClose, onSaved }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [step, setStep] = useState('cliente'); // 'cliente' | 'veicolo' | 'preventivo'
  const [searchCliente, setSearchCliente] = useState('');
  const [clienteSelezionato, setClienteSelezionato] = useState(null);
  const [searchVeicolo, setSearchVeicolo] = useState('');
  const [veicoloSelezionato, setVeicoloSelezionato] = useState(null);
  const [vehicles, setVehicles] = useState([]);

  // Carica stock
  useEffect(() => {
    const unsubStock = onValue(ref(db, 'stock_data'), snap => {
      const d = snap.val();
      if (d?.vehicles) setVehicles(Array.isArray(d.vehicles) ? d.vehicles : Object.entries(d.vehicles).map(([id,v]) => ({...v, id})));
    });
    return () => unsubStock();
  }, [db]);

  // Clienti unici dai preventivi precedenti — ordinati per data decrescente
  // PRIMA di costruire la mappa, così per ogni cliente teniamo sempre il suo
  // preventivo più recente (non il primo che capita), e la lista risultante
  // ha già i clienti più recenti in cima.
  const clientiEsistenti = useMemo(() => {
    // Stesso fallback data usato nella lista principale (_ts già calcolato
    // lì: dataCreazione se presente, altrimenti data) — usare solo
    // dataCreazione qui sballava l'ordine per i preventivi che hanno la
    // data vera nel campo "data" invece che "dataCreazione".
    const preventiviOrdinati = [...preventivi].sort((a, b) => (b._ts || new Date(b.dataCreazione || b.data || 0).getTime()) - (a._ts || new Date(a.dataCreazione || a.data || 0).getTime()));
    const map = {};
    preventiviOrdinati.forEach(p => {
      if (!p.cliente) return;
      const key = p.cliente.codiceFiscale || p.cliente.email || `${p.cliente.nome}${p.cliente.cognome}`;
      if (key && !map[key]) {
        map[key] = {
          ...p.cliente,
          ultimoPreventivo: p,
          veicoliPrecedenti: []
        };
      }
      if (key && p.veicolo) {
        map[key].veicoliPrecedenti = [...(map[key].veicoliPrecedenti || []), p.veicolo].slice(0, 3);
      }
    });
    return Object.values(map);
  }, [preventivi]);

  const clientiFiltrati = useMemo(() => {
    if (!searchCliente.trim()) return clientiEsistenti.slice(0, 8);
    const s = searchCliente.toLowerCase();
    return clientiEsistenti.filter(c =>
      `${c.nome||''} ${c.cognome||''} ${c.ragioneSociale||''} ${c.codiceFiscale||''} ${c.cellulare||''}`.toLowerCase().includes(s)
    ).slice(0, 8);
  }, [clientiEsistenti, searchCliente]);

  const veicoliFiltrati = useMemo(() => {
    // Non deve mai essere possibile preventivare un veicolo già venduto,
    // qui come in Stock — filtro applicato PRIMA della ricerca testuale.
    const disponibili = (vehicles || []).filter(v => v.status !== 'VENDUTA' && v.status !== 'SOLD');
    if (!searchVeicolo.trim()) return disponibili.slice(0, 20); // mostra solo 20 di default
    const s = searchVeicolo.toLowerCase();
    return disponibili.filter(v =>
      `${v.marca||''} ${v.modello||''} ${v.versione||''} ${v.targa||''} ${v.vin||''}`.toLowerCase().includes(s)
    ); // nessun limite quando si cerca
  }, [vehicles, searchVeicolo]);

  // Una volta selezionati cliente e veicolo, apri PreventivoModal
  if (step === 'preventivo' && clienteSelezionato && veicoloSelezionato) {
    return (
      <PreventivoModal
        car={veicoloSelezionato}
        user={user}
        db={db}
        onClose={onClose}
        onSaved={onSaved}
        clientePrecompilato={clienteSelezionato}
      />
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '16px', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '24px 24px 0 0' : '24px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '600px', maxHeight: isMobile ? '92vh' : '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* HEADER */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          {isMobile && <div style={{ width: '36px', height: '4px', background: T.border2, borderRadius: '2px', margin: '0 auto 16px' }} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '900', letterSpacing: '2px', marginBottom: '4px' }}>NUOVO PREVENTIVO AGILE</div>
              <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>
                {step === 'cliente' ? '1. Seleziona o crea cliente' : '2. Seleziona veicolo'}
              </div>
            </div>
            <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontWeight: '900', fontSize: '18px' }}>×</button>
          </div>
          {/* Progress */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '14px' }}>
            <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: '#f59e0b' }} />
            <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: step === 'veicolo' ? '#f59e0b' : T.border }} />
          </div>
        </div>

        {/* CONTENUTO */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

          {/* STEP CLIENTE */}
          {step === 'cliente' && (<>
            <input
              autoFocus
              placeholder="🔍 Cerca per nome, cognome, CF, telefono..."
              value={searchCliente}
              onChange={e => setSearchCliente(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', color: T.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '16px' }}
            />

            {/* Lista clienti esistenti */}
            {clientiFiltrati.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '8px' }}>CLIENTI ESISTENTI</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {clientiFiltrati.map((c, i) => {
                    const nomeC = c.ragioneSociale || `${c.nome||''} ${c.cognome||''}`.trim();
                    return (
                      <div key={i} onClick={() => { setClienteSelezionato(c); setStep('veicolo'); }}
                        style={{ padding: '12px 14px', background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, cursor: 'pointer', transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>{nomeC}</div>
                            <div style={{ fontSize: '10px', color: T.textSub, marginTop: '2px' }}>
                              {c.codiceFiscale && <span style={{ marginRight: '10px' }}>CF: {c.codiceFiscale}</span>}
                              {c.cellulare && <span>{c.cellulare}</span>}
                            </div>
                            {/* Ultimi veicoli */}
                            {c.veicoliPrecedenti?.length > 0 && (
                              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                                {c.veicoliPrecedenti.map((v, j) => (
                                  <span key={j} style={{ fontSize: '9px', padding: '2px 8px', background: `${T.border}80`, borderRadius: '6px', color: T.textSub }}>
                                    {v.marca} {v.modello}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <span style={{ fontSize: '18px', color: T.textMuted }}>›</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Opzione nuovo cliente */}
            <div
              onClick={() => { setClienteSelezionato({}); setStep('veicolo'); }}
              style={{ padding: '14px', background: 'rgba(245,158,11,0.08)', borderRadius: '12px', border: '1px dashed #f59e0b', cursor: 'pointer', textAlign: 'center' }}
            >
              <div style={{ fontSize: '13px', fontWeight: '900', color: '#f59e0b' }}>+ Nuovo Cliente</div>
              <div style={{ fontSize: '11px', color: T.textSub, marginTop: '3px' }}>Inserisci i dati nella schermata del preventivo</div>
            </div>
          </>)}

          {/* STEP VEICOLO */}
          {step === 'veicolo' && (<>
            {/* Cliente selezionato */}
            <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.3)', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '900', marginBottom: '2px' }}>CLIENTE SELEZIONATO</div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: T.text }}>
                  {clienteSelezionato.ragioneSociale || `${clienteSelezionato.nome||''} ${clienteSelezionato.cognome||''}`.trim() || 'Nuovo cliente'}
                </div>
              </div>
              <button onClick={() => setStep('cliente')} style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>Cambia</button>
            </div>

            {/* Veicoli precedenti del cliente */}
            {clienteSelezionato.veicoliPrecedenti?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '8px' }}>VEICOLI PRECEDENTI DI QUESTO CLIENTE</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {clienteSelezionato.veicoliPrecedenti.map((v, i) => (
                    <div key={i} onClick={() => { setVeicoloSelezionato(v); setStep('preventivo'); }}
                      style={{ padding: '8px 12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                    >
                      {v.mainImg && <img src={v.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '48px', height: '34px', objectFit: 'cover', borderRadius: '6px' }} />}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: T.text }}>{v.marca} {v.modello}</div>
                        <div style={{ fontSize: '9px', color: T.textMuted }}>{v.targa || v.tipo}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <input
              autoFocus
              placeholder="🔍 Cerca veicolo per marca, modello, targa..."
              value={searchVeicolo}
              onChange={e => setSearchVeicolo(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', color: T.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '14px' }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
              {veicoliFiltrati.map((v, iV) => (
                <div key={`${v.id || ''}|${v.vin || v.targa || ''}|${iV}`} onClick={() => { setVeicoloSelezionato(v); setStep('preventivo'); }}
                  style={{ background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                >
                  {v.mainImg && <img src={v.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '100%', height: '90px', objectFit: 'cover' }} />}
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '900', color: T.text, lineHeight: '1.2' }}>{v.marca} {v.modello}</div>
                    <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '2px' }}>{v.tipo} · {new Intl.NumberFormat('it-IT').format(v.km)} km</div>
                    <div style={{ fontSize: '12px', fontWeight: '900', color: '#10b981', marginTop: '4px' }}>
                      {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
                        user?.ruolo === 'partner' && v.prezzoPartner > 0 ? v.prezzoPartner : v.prezzo
                      )}
                      {user?.ruolo === 'partner' && v.prezzoPartner > 0 && <span style={{fontSize:'9px',color:'#6366f1',fontWeight:'700',marginLeft:'4px'}}>PARTNER</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── PDF PREVIEW COMPONENT ────────────────────────────────────────────────────
function PDFPreview({ tipo, preventivo, contratto, T }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let url = null;
    const genera = async () => {
      try {
        let blob = null;
        if (tipo === 'preventivo') {
          blob = await generatePDFBlob(
            preventivo.veicolo || {},
            preventivo.cliente || {},
            preventivo.prezzo || {},
            preventivo.servizi || {}
          );
        } else {
          const vB = { ...contratto.veicolo };
          if (contratto.datiExtra?.vin) vB.vin = contratto.datiExtra.vin;
          if (contratto.datiExtra?.immatricolazione) vB.registrazione = contratto.datiExtra.immatricolazione;
          if (!vB.vin && contratto.veicolo?.targa) {
            const cachedV = (window._radarStockCache?.vehicles || []).find(sv => sv.targa === contratto.veicolo.targa);
            if (cachedV?.vin) vB.vin = cachedV.vin;
            if (!vB.km && cachedV?.km) vB.km = cachedV.km;
            if (!vB.registrazione && cachedV?.registrazione) vB.registrazione = cachedV.registrazione;
          }
          blob = await generaPacchettoBlob(
            { cliente: contratto.cliente, veicolo: vB, prezzo: contratto.prezzo, servizi: contratto.servizi },
            contratto.datiExtra || {},
            {}
          );
        }
        if (blob) {
          url = URL.createObjectURL(blob);
          setPdfUrl(url);
        } else {
          setError("Impossibile generare l'anteprima.");
        }
      } catch(e) {
        setError('Errore: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    genera();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  if (loading) return <div style={{ padding: '40px', color: T.textMuted, textAlign: 'center' }}><div style={{ fontSize: '28px', marginBottom: '8px' }}>⏳</div><div style={{ fontSize: '12px' }}>Generazione anteprima...</div></div>;
  if (error) return <div style={{ padding: '20px', color: '#ef4444', fontSize: '12px', textAlign: 'center' }}>{error}</div>;
  if (!pdfUrl) return null;

  return (
    <div style={{ width: '100%', borderRadius: '12px', overflow: 'hidden', border: `1px solid ${T.border}` }}>
      <iframe src={pdfUrl} style={{ width: '100%', height: '72vh', border: 'none', display: 'block' }} title="Anteprima PDF" />
    </div>
  );
}

// ─── PRINT BUTTON ─────────────────────────────────────────────────────────────
function PrintButton({ preventivo, stamping, setStamping }) {
  const theme = useTheme(); const T = THEMES[theme];
  const handleStampa = async () => {
    setStamping(true);
    try {
      const v = { ...preventivo.veicolo, ...preventivo.prezzo };
      await generatePDF(
        preventivo.veicolo || {},
        preventivo.cliente || {},
        preventivo.prezzo || {},
        preventivo.servizi || {}
      );
    } catch(e) {
      alert('Errore stampa: ' + e.message);
    } finally {
      setStamping(false);
    }
  };
  return (
    <button
      onClick={handleStampa}
      disabled={stamping}
      style={{ width: '100%', maxWidth: '300px', padding: '14px', background: stamping ? T.border : 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '14px', color: '#fff', cursor: stamping ? 'default' : 'pointer', fontWeight: '900', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 auto' }}
    >
      {stamping ? '⏳ Generazione...' : '📄 Scarica PDF Preventivo'}
    </button>
  );
}

function PreventivoDetailModal({ p, onClose, user, onModifica, onElimina, onConvertì }) {
  const theme = useTheme(); const T = THEMES[theme];
  const [confermaElimina, setConfermaElimina] = useState(false);
  const [activeTab, setActiveTab] = useState('dettagli');
  const [stamping, setStamping] = useState(false);
  const nomeCliente = p.cliente?.ragioneSociale || `${p.cliente?.nome || ''} ${p.cliente?.cognome || ''}`.trim() || 'Cliente';
  const prezzo = p.prezzo || {};
  const prezzoListino = parseFloat(prezzo.prezzoListino || p.veicolo?.prezzo || 0);
  const sconto = parseFloat(prezzo.sconto || 0);
  const ipt = parseFloat(prezzo.ipt || 0);
  const permuta = valorePermuteTot(prezzo);
  const caparra = parseFloat(prezzo.caparra || 0);
  const importoFinanziato = parseFloat(prezzo.importoFinanziato || 0);
  const totServizi = Object.entries(p.servizi||{}).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0);
  const accessoriCM = (p.servizi?.accessoriCasaMadreList || []).reduce((t,a) => t+(parseFloat(a.prezzo)||0), 0);
  const hasIVA = !!prezzo.hasIVA;
  const ivaImporto = hasIVA ? Math.round((prezzoListino - sconto) * 0.22) : 0;
  const estinzioneCosto = prezzo.estinzioneAnticipata ? parseFloat(prezzo.costoEstinzioneAnticipata||0) : 0;
  const speseLeasingCosto = prezzo.speseGestioneLeasing ? parseFloat(prezzo.costoSpeseGestioneLeasing||0) : 0;
  const saldo = (prezzoListino - sconto + ipt) + accessoriCM + totServizi + ivaImporto - permuta - caparra - importoFinanziato + estinzioneCosto + speseLeasingCosto;

  const ruoloP = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
  const puoModificare = ['admin','super_admin','logistica'].includes(ruoloP) ||
    (['consulente','brand_manager_se'].includes(ruoloP) && p.venditoreNome === `${user.nome} ${user.cognome}`);
  // I partner devono poter convertire i PROPRI preventivi in contratto (è
  // l'unico modo che hanno per far arrivare un contratto in approvazione),
  // ma non devono poter modificare il preventivo — flag separato apposta.
  const puoConvertire = puoModificare ||
    (ruoloP === 'partner' && p.venditoreNome === `${user.nome} ${user.cognome}`);
  const puoEliminare = ['admin','super_admin'].includes(ruoloP);

  const handleElimina = async () => {
    try {
      await set(ref(db, `preventivi/${p.id}`), null);
      window._rimuoviPreventivoDallaCache?.(p.id);
      onElimina && onElimina();
    } catch(e) { alert('Errore eliminazione: ' + e.message); }
  };

  // handleStampa rimossa — usa PrintButton direttamente

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: '24px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '600px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* HEADER */}
        <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '900', letterSpacing: '2px', marginBottom: '4px' }}>
                PREVENTIVO · {new Date(p.dataCreazione).toLocaleDateString('it-IT')}
                {p.dataModifica && <span style={{ color: '#64748b', marginLeft: '8px' }}>· Modificato {new Date(p.dataModifica).toLocaleDateString('it-IT')}</span>}
              </div>
              <h2 style={{ margin: 0, color: T.text, fontSize: '20px', fontWeight: '900' }}>{nomeCliente}</h2>
              <div style={{ color: '#64748b', fontSize: '12px', marginTop: '3px' }}>{p.veicolo?.marca} {p.veicolo?.modello} {p.veicolo?.versione}</div>
            </div>
            {p.veicolo?.mainImg && <img src={p.veicolo.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '80px', height: '55px', objectFit: 'cover', borderRadius: '8px', marginLeft: '12px', flexShrink: 0 }} />}
            <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontWeight: '900', fontSize: '18px', lineHeight: 1, marginLeft: '10px', flexShrink: 0 }}>×</button>
          </div>

          {/* TAB BAR */}
          <div style={{ display: 'flex', gap: '2px', borderBottom: `2px solid ${T.border}`, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {[['dettagli','📋 Dettagli'],['riepilogo','📊 Riepilogo'],['stampa','🖨️ Stampa']].map(([id, label]) => (
              <button key={id} onClick={() => setActiveTab(id)} style={{ padding: '8px 16px', fontSize: '11px', fontWeight: '900', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', background: 'transparent', color: activeTab === id ? '#f59e0b' : T.textSub, borderBottom: activeTab === id ? '2px solid #f59e0b' : '2px solid transparent', marginBottom: '-2px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* CONTENUTO SCROLLABILE */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 28px' }}>

          {/* TAB DETTAGLI */}
          {activeTab === 'dettagli' && (<>
            <Section title="CLIENTE" color="#3b82f6">
              <Row label="Nome" value={nomeCliente} />
              <Row label="Codice Fiscale" value={p.cliente?.codiceFiscale || p.cliente?.piva || '—'} />
              <Row label="Cellulare" value={p.cliente?.cellulare || '—'} />
              <Row label="Email" value={p.cliente?.email || '—'} />
              <Row label="Indirizzo" value={p.cliente?.indirizzo ? `${p.cliente.indirizzo}, ${p.cliente.citta || ''} (${p.cliente.provincia || ''})` : '—'} />
              {p.cliente?.provenienza && <Row label="Provenienza" value={p.cliente.provenienza} />}
            </Section>
            <Section title="VEICOLO" color="#8b5cf6">
              <Row label="Modello" value={`${p.veicolo?.marca} ${p.veicolo?.modello}`} />
              <Row label="Targa" value={p.veicolo?.targa || '—'} />
              <Row label="Tipo" value={p.veicolo?.tipo || '—'} />
            </Section>
            <Section title="CONDIZIONI ECONOMICHE" color="#10b981">
              <Row label="Prezzo Listino" value={fmt(prezzoListino)} />
              <Row label="Sconto" value={fmt(sconto)} />
              <Row label="IPT + Messa su strada" value={fmt(ipt)} />
              {accessoriCM > 0 && <Row label="Optional Casa Madre" value={fmt(accessoriCM)} />}
              {totServizi > 0 && <Row label="Totale Servizi e Accessori" value={fmt(totServizi)} />}
              {estinzioneCosto > 0 && <Row label="Estinzione Anticipata Finanziamento" value={fmt(estinzioneCosto)} />}
              {speseLeasingCosto > 0 && <Row label="Spese Gestione Leasing" value={fmt(speseLeasingCosto)} />}
              <Row label="Prezzo Concordato (vettura + servizi)" value={fmt(prezzoListino - sconto + ipt + accessoriCM + totServizi)} />
              {hasIVA && <Row label="IVA 22%" value={`+ ${fmt(ivaImporto)}`} />}
              {prezzo.hasPermuta && <Row label={prezzo.permutaRottamazione ? 'Rottamazione' : 'Permuta Usato'} value={`- ${fmt(permuta)}`} />}
              {prezzo.hasFinanziamento && <Row label="Importo Finanziato" value={`- ${fmt(importoFinanziato)}`} />}
              <Row label="Caparra" value={fmt(caparra)} />
              <Row label="SALDO" value={fmt(saldo)} bold />
              <Row label="Venditore" value={p.venditoreNome || '—'} />
            </Section>
          </>)}

          {/* TAB STAMPA */}
          {activeTab === 'stampa' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖨️</div>
              <div style={{ fontSize: '15px', fontWeight: '900', color: T.text, marginBottom: '8px' }}>Stampa Preventivo</div>
              <div style={{ fontSize: '12px', color: T.textSub, marginBottom: '28px', lineHeight: '1.6' }}>
                {p.veicolo?.marca} {p.veicolo?.modello} · <b>{nomeCliente}</b><br/>
                Saldo: <b style={{ color: '#10b981' }}>{fmt(saldo)}</b>
              </div>
              <PrintButton preventivo={p} stamping={stamping} setStamping={setStamping} />
            </div>
          )}

          {/* TAB RIEPILOGO */}
          {activeTab === 'riepilogo' && (
            <div>
              {/* CLIENTE */}
              {(() => {
                const cl = p.cliente || {};
                const righe = [
                  ['Nome / Ragione Sociale', cl.ragioneSociale || `${cl.nome||''} ${cl.cognome||''}`.trim()],
                  ['Codice Fiscale', cl.codiceFiscale],
                  ['Partita IVA', cl.piva],
                  ['Indirizzo', cl.indirizzo ? `${cl.indirizzo}${cl.citta?', '+cl.citta:''}${cl.cap?' '+cl.cap:''}${cl.provincia?' ('+cl.provincia+')':''}` : null],
                  ['Cellulare', cl.cellulare],
                  ['Telefono', cl.telefono],
                  ['Email', cl.email],
                  ['PEC', cl.pec],
                  ['Provenienza', cl.provenienza],
                ].filter(([,v]) => v && String(v).trim());
                if (!righe.length) return null;
                return (
                  <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '10px' }}>CLIENTE</div>
                    {righe.map(([l,v]) => <Row key={l} label={l} value={v} />)}
                    {cl.b2b && <div style={{ marginTop: '6px', display: 'inline-block', fontSize: '9px', fontWeight: '900', color: '#6366f1', background: 'rgba(99,102,241,0.15)', padding: '2px 8px', borderRadius: '6px' }}>🏢 B2B</div>}
                  </div>
                );
              })()}
              {/* VEICOLO */}
              {(() => {
                const v2 = p.veicolo || {};
                const stockLive = (window._radarStockCache?.vehicles || []).find(sv => sv.id === v2.id || sv.targa === v2.targa || sv.vin === v2.vin);
                const righe = [
                  ['Marca / Modello', `${v2.marca||''} ${v2.modello||''}`.trim()],
                  ['Versione', v2.versione],
                  ['Tipo', v2.tipo],
                  ['Targa', v2.targa],
                  ['Telaio (VIN)', v2.vin],
                  ['Immatricolazione', v2.registrazione],
                  ['Km', v2.km ? new Intl.NumberFormat('it-IT').format(v2.km) + ' km' : null],
                  ['Carburante', v2.carburante],
                  ['Colore', stockLive?.colore || v2.coloreVeicolo || v2.colore],
                  ['Cambio', v2.gearType],
                ].filter(([,v]) => v && String(v).trim());
                return (
                  <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', letterSpacing: '1px', marginBottom: '10px' }}>VEICOLO</div>
                    {righe.map(([l,v]) => <Row key={l} label={l} value={v} />)}
                  </div>
                );
              })()}
              {/* CONDIZIONI ECONOMICHE */}
              <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '10px' }}>CONDIZIONI ECONOMICHE</div>
                {[
                  ['Prezzo Listino', fmt(prezzoListino)],
                  sconto > 0 && ['Sconto', '- ' + fmt(sconto)],
                  ipt > 0 && ['IPT + MSS', '+ ' + fmt(ipt)],
                  totServizi > 0 && ['Servizi e accessori', '+ ' + fmt(totServizi)],
                  permuta > 0 && [p.prezzo?.permutaRottamazione ? 'Rottamazione' : 'Permuta', '- ' + fmt(permuta)],
                  importoFinanziato > 0 && ['Finanziato', '- ' + fmt(importoFinanziato)],
                  caparra > 0 && ['Caparra', '- ' + fmt(caparra)],
                ].filter(Boolean).map(([l,v]) => <Row key={l} label={l} value={v} />)}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', marginTop: '8px', borderTop: `2px solid ${T.border}` }}>
                  <span style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>SALDO</span>
                  <span style={{ fontSize: '17px', fontWeight: '900', color: '#10b981' }}>{fmt(saldo)}</span>
                </div>
              </div>
              {/* PERMUTA */}
              {p.prezzo?.hasPermuta && permuta > 0 && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px', marginBottom: '10px' }}>{p.prezzo?.permutaRottamazione ? 'ROTTAMAZIONE' : 'PERMUTA'}</div>
                  {[
                    ['Marca / Modello', `${p.prezzo.permutaMarca||''} ${p.prezzo.permutaModello||''}`.trim()],
                    ['Targa', p.prezzo.permutaTarga],
                    ['Km', p.prezzo.permutaKm ? new Intl.NumberFormat('it-IT').format(p.prezzo.permutaKm) + ' km' : null],
                    ['Valore', fmt(permuta)],
                  ].filter(([,v]) => v && String(v).trim()).map(([l,v]) => <Row key={l} label={l} value={v} />)}
                </div>
              )}
              {/* FINANZIAMENTO */}
              {p.prezzo?.hasFinanziamento && importoFinanziato > 0 && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '10px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '1px', marginBottom: '10px' }}>FINANZIAMENTO</div>
                  {[
                    ['Banca / Finanziaria', p.prezzo.finanziariaSocieta || p.prezzo.bancaFinanziamento],
                    ['Importo', fmt(importoFinanziato)],
                    ['Durata', p.prezzo.durataFinanziamento ? p.prezzo.durataFinanziamento + ' mesi' : null],
                    ['Rata', p.prezzo.rata ? fmt(p.prezzo.rata) + '/mese' : null],
                    ['Tasso', p.prezzo.tassoFinanziamento || p.prezzo.tabellaFinanziamento],
                  ].filter(([,v]) => v && String(v).trim()).map(([l,v]) => <Row key={l} label={l} value={v} />)}
                </div>
              )}
              {/* VENDITORE */}
              {p.venditoreNome && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '12px 16px', border: `1px solid ${T.border}` }}>
                  <Row label="Consulente" value={p.venditoreNome} />
                  {p.dataCreazione && <Row label="Data preventivo" value={new Date(p.dataCreazione).toLocaleDateString('it-IT')} />}
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER — sempre visibile */}
        <div style={{ padding: '16px 28px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
            {/* Conferma eliminazione */}
            {confermaElimina && (
              <div style={{ marginBottom: '12px', padding: '14px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#ef4444', marginBottom: '8px' }}>⚠️ Eliminare questo preventivo?</div>
                <div style={{ fontSize: '11px', color: T.textSub, marginBottom: '12px' }}>L'operazione è irreversibile.</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setConfermaElimina(false)} style={{ flex: 1, padding: '9px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>Annulla</button>
                  <button onClick={handleElimina} style={{ flex: 1, padding: '9px', background: '#ef4444', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>Sì, elimina</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={onClose} style={{ flex: 1, minWidth: '80px', padding: '11px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '12px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>Chiudi</button>
              {puoEliminare && !confermaElimina && (
                <button onClick={() => setConfermaElimina(true)} style={{ padding: '11px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef444440', borderRadius: '12px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  🗑️ Elimina
                </button>
              )}
              {puoModificare && !confermaElimina && (
                <button onClick={() => onModifica(p)} style={{ flex: 1, minWidth: '100px', padding: '11px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  ✏️ Modifica
                </button>
              )}
              {puoConvertire && !confermaElimina && (
                <button onClick={() => onConvertì(p)} style={{ flex: 1, minWidth: '100px', padding: '11px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  📄 Converti
                </button>
              )}
            </div>
          </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB CONTRATTI
// ═══════════════════════════════════════════════════════════════════════════════
function ContrattiView({ user, statiContrattiLive = {}, contrattiGlobaliRaw = [] }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  // "Avanzamento" — colonna separata da "Stato" (che resta il semplice stato
  // del contratto: approvato/venduto/annullato). Per i clienti B2C riprende
  // lo stato di consegna già tracciato in Back Office. Per i B2B, che non
  // passano dal flusso di consegna B2C, usa un semaforo dedicato basato su
  // approvato → consegnato → pagato (quest'ultimo impostato a mano nel
  // dettaglio contratto).
  const getAvanzamento = (c) => {
    if (c.stato !== 'approvato' && c.stato !== 'venduto') return null; // bozze/annullati: nessun avanzamento da mostrare
    if (c.cliente?.b2b) {
      if (c.backoffice?.pagatoB2B) return { label: '🟢 Pagato', color: '#10b981' };
      if (c.stato === 'venduto') return { label: '🟡 Consegnato, da incassare', color: '#f59e0b' };
      return { label: '🔴 Approvato', color: '#ef4444' };
    }
    const statoConsegna = c.backoffice?.statoConsegna || 'da_preparare';
    const labels = { da_preparare: '⏳ Da preparare', in_lavorazione: '🔧 In lavorazione', pronto: '✅ Pronto', consegnato: '🚀 Consegnato' };
    const colors = { da_preparare: '#f59e0b', in_lavorazione: '#3b82f6', pronto: '#10b981', consegnato: '#8b5cf6' };
    return { label: labels[statoConsegna], color: colors[statoConsegna] };
  };
  // contratti arrivano dalla cache condivisa già pronta
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [colonnePickerOpenContratti, setColonnePickerOpenContratti] = useState(false);
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [filtroSede, setFiltroSede] = useState('tutti');
  const [filtroVeicolo, setFiltroVeicolo] = useState('tutti'); // tutti/nuovo/usato
  const [modificandoContratto, setModificandoContratto] = useState(null);
  const [fDataDa, setFDataDa] = useState('');
  const [fDataA, setFDataA] = useState('');

  // ─── ORA LEGGE DALLA CACHE CONDIVISA — contrattiGlobaliRaw arriva già
  // cachata una volta al giorno + delta ogni 2 minuti (stesso meccanismo
  // di Radar/Stock/Finance). Nessun download separato per questa tab:
  // prima ContrattiView faceva la propria query indipendente (ultimi 100 o
  // propri), duplicando quello che il resto dell'app aveva già scaricato.
  // Qui applichiamo solo il filtro per ruolo, lato client, su dati già in
  // memoria — zero banda aggiuntiva.
  const modalitaPropri = user.ruolo === 'consulente' || user.ruolo === 'partner';
  const loading = false;

  const contrattiProp = useMemo(() => {
    if (modalitaPropri) {
      // Propri = intestati a me come venditore OPPURE creati da me. Prima
      // contava solo il venditore: convertendo il preventivo di un collega
      // il contratto finiva intestato a lui e a me "spariva".
      // Nomi confrontati senza badare a spazi doppi e maiuscole.
      const normN = x => String(x || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const fullName = normN(`${user.nome} ${user.cognome}`);
      const mieiUid = new Set([user.uid, user.email, `${user.nome}.${user.cognome}`].filter(Boolean));
      return contrattiGlobaliRaw.filter(c => normN(c.venditoreNome) === fullName || (c.creatorUid && mieiUid.has(c.creatorUid)));
    }
    // brand_manager_se: propri + marchi Sportequipe/ICH-X di chiunque —
    // ora fattibile senza costo aggiuntivo, i dati sono già tutti locali.
    if (user.ruolo === 'brand_manager_se') {
      const fullName = `${user.nome} ${user.cognome}`.toLowerCase().trim();
      const marchiSpeciali = ['sportequipe', 'ich-x', 'ichx'];
      return contrattiGlobaliRaw.filter(c =>
        c.venditoreNome?.toLowerCase().trim() === fullName ||
        marchiSpeciali.some(m => (c.veicolo?.marca || '').toLowerCase().includes(m))
      );
    }
    return contrattiGlobaliRaw;
  }, [contrattiGlobaliRaw, user.ruolo, user.nome, user.cognome]);

  const contratti = useMemo(() => {
    // Confronto sicuro contro date mancanti/invalide — un solo record
    // senza dataCreazione (o con un valore non valido) può altrimenti
    // corrompere l'intero ordinamento: new Date(undefined) produce un
    // risultato non numerico (NaN) nel confronto, e con un comparatore
    // che a volte restituisce NaN il risultato di .sort() diventa
    // imprevedibile per TUTTO l'array, non solo per quel record — un
    // singolo dato mancante può quindi far sembrare "sballato" l'ordine
    // di record del tutto validi vicini ad esso.
    const dataOrdinamento = c => { const t = new Date(c.dataCreazione || 0).getTime(); return isNaN(t) ? 0 : t; };
    const ordinati = (contrattiProp || []).slice().sort((a, b) => dataOrdinamento(b) - dataOrdinamento(a));
    if (!Object.keys(statiContrattiLive).length) return ordinati;
    // Applica lo stato live sopra l'elenco — corregge "stato" anche se il
    // resto del contratto arriva da una cache più vecchia della modifica.
    return ordinati.map(c => statiContrattiLive[c.id] ? { ...c, stato: statiContrattiLive[c.id] } : c);
  }, [contrattiProp, statiContrattiLive]);

  const filtered = useMemo(() => {
    let list = contratti;
    // Consulenti e partner vedono solo i propri (match nome+cognome esatto)
    if (user.ruolo === 'consulente' || user.ruolo === 'partner') {
      const fullName = `${user.nome} ${user.cognome}`.toLowerCase().trim();
      list = list.filter(c => c.venditoreNome?.toLowerCase().trim() === fullName);
    } else if (user.ruolo === 'brand_manager_se') {
      // Consulente normale sui propri contratti, PIÙ tutti i contratti di
      // chiunque per i marchi Sportequipe e ICH-X — normalizzato per
      // gestire tutte le scritture in uso (ICH-X, ICHX, ICKX, Sport Equipe...).
      const fullName = `${user.nome} ${user.cognome}`.toLowerCase().trim();
      const normMarcaSE = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const marcheSpeciali = ['sportequipe', 'ichx', 'ickx'];
      list = list.filter(c =>
        c.venditoreNome?.toLowerCase().trim() === fullName ||
        marcheSpeciali.includes(normMarcaSE(c.veicolo?.marca))
      );
    }
    // bdc vede tutto
    if (filtroStato !== 'tutti') list = list.filter(c => c.stato === filtroStato);
    if (filtroTipo === 'b2b') list = list.filter(c => !!c.cliente?.b2b);
    if (filtroTipo === 'b2c') list = list.filter(c => !c.cliente?.b2b);
    if (filtroVeicolo !== 'tutti') {
      list = list.filter(c => {
        const t = (c.veicolo?.tipo || '').toLowerCase().trim();
        const isNuovo = t === 'nuovo' || t === 'new';
        return filtroVeicolo === 'nuovo' ? isNuovo : !isNuovo;
      });
    }
    if (filtroSede !== 'tutti') {
      list = list.filter(c => getSede((c.venditoreNome||'').toLowerCase()) === filtroSede);
    }
    // Filtro automatico sede per ruolo front
    const frontSede = getFrontSedeFilter(user);
    if (frontSede) list = list.filter(c => getSede((c.venditoreNome||'').toLowerCase()) === frontSede);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(c => {
        const nc = `${c.cliente?.nome||''} ${c.cliente?.cognome||''} ${c.cliente?.ragioneSociale||''}`.toLowerCase();
        const nv = `${c.veicolo?.marca||''} ${c.veicolo?.modello||''}`.toLowerCase();
        const targa = (c.veicolo?.targa||'').toLowerCase();
        const vin = (c.veicolo?.vin||c.datiExtra?.vin||'').toLowerCase();
        return nc.includes(s) || nv.includes(s) || (c.venditoreNome||'').toLowerCase().includes(s) || (c.datiExtra?.numeroContratto||'').toLowerCase().includes(s) || targa.includes(s) || vin.includes(s);
      });
    }
    if (fDataDa) list = list.filter(c => { const d = new Date(c.dataCreazione || c.data); return d >= new Date(fDataDa); });
    if (fDataA)  list = list.filter(c => { const d = new Date(c.dataCreazione || c.data); return d <= new Date(fDataA + 'T23:59:59'); });
    return list;
  }, [contratti, search, user, filtroStato, filtroTipo, filtroSede, filtroVeicolo, fDataDa, fDataA]);

  const handleApprova = async (contratto) => {
    const approvatore = `${user.nome} ${user.cognome} (${user.ruolo})`;
    try {
      await set(ref(db, `contratti/${contratto.id}/stato`), 'approvato');
      await set(ref(db, `contratti/${contratto.id}/approvazioneData`), new Date().toISOString());
      await set(ref(db, `contratti/${contratto.id}/approvazioneNome`), approvatore);
      await set(ref(db, `contratti/${contratto.id}/dataModifica`), new Date().toISOString());
      await set(ref(db, `stati_contratti/${contratto.id}`), 'approvato');
      // Segna il veicolo in trattativa su Firebase
      const v = contratto.veicolo || {};
      const chiave = v.targa || v.vin || v.id || null;
      if (chiave) {
        await set(ref(db, `veicoli_in_trattativa/${chiave.replace(/[^a-zA-Z0-9]/g, '_')}`), {
          targa: v.targa || '',
          vin: v.vin || '',
          veicoloId: v.id || '',
          marca: v.marca || '',
          modello: v.modello || '',
          contrattoId: contratto.id,
          // Cognome cliente — mostrato in Stock per identificare a colpo
          // d'occhio a chi è legata la prenotazione. Fallback sulla
          // ragione sociale per i clienti aziendali (B2B), che non hanno
          // un "cognome" in senso proprio.
          clienteCognome: contratto.cliente?.cognome || contratto.cliente?.ragioneSociale || '',
          approvazioneData: new Date().toISOString(),
          approvazioneNome: approvatore,
        });
      }
      // Aggiorna subito lo stato in Stock a CONTRATTUALIZZATA — non
      // aspetta il prossimo sync orario di sync-stock.js (che comunque fa
      // la stessa cosa più tardi, come rete di sicurezza). Scrittura
      // mirata al singolo veicolo trovato per VIN/targa, mai sull'intero
      // array stock_data/vehicles.
      try {
        const chiaveMatch = (v.vin || v.targa || '').toUpperCase().trim();
        if (chiaveMatch) {
          const snapStock = await get(ref(db, 'stock_data/vehicles'));
          const vehicles = snapStock.val() || [];
          const idx = vehicles.findIndex(sv =>
            (sv.vin || '').toUpperCase().trim() === chiaveMatch ||
            (sv.targa || '').toUpperCase().trim() === chiaveMatch
          );
          if (idx !== -1 && vehicles[idx].status !== 'VENDUTA' && vehicles[idx].status !== 'CONTRATTUALIZZATA') {
            const nomeCliente = contratto.cliente?.ragioneSociale || `${contratto.cliente?.cognome||''} ${contratto.cliente?.nome||''}`.trim();
            await set(ref(db, `stock_data/vehicles/${idx}/status`), 'CONTRATTUALIZZATA');
            await set(ref(db, `stock_data/vehicles/${idx}/consulenteNome`), contratto.venditoreNome || '');
            await set(ref(db, `stock_data/vehicles/${idx}/clienteNome`), nomeCliente);
          }
        }
      } catch (e) { /* non bloccante: l'approvazione resta valida anche se questo fallisce, sync-stock.js farà comunque da rete di sicurezza al prossimo giro */ }
      // Notifica email approvazione
      try {
        await fetch('http://localhost:3001/notify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'approvato', contratto: { ...contratto, stato: 'approvato', approvazioneNome: approvatore, approvazioneData: new Date().toISOString() } }),
        });
      } catch(e) { console.warn('Notify server non raggiungibile'); }

      // LeadSpark — Trattativa Approvata (stato C)
      sendToLeadSpark({
        ...contratto,
        stato: 'approvato',
        preventivoId: contratto.preventivoId || contratto.id,
      }, 'C');

    } catch(e) { alert('Errore approvazione: ' + e.message); }
  };

  const statoColor = s => s === 'approvato' ? '#10b981' : s === 'annullato' ? '#ef4444' : s === 'venduto' ? '#6366f1' : '#f59e0b';
  const statoLabel = s => s === 'approvato' ? '✅ Approvato' : s === 'annullato' ? '❌ Annullato' : s === 'bozza' ? '⏳ Bozza' : s === 'venduto' ? '🚗 Venduto' : s;

  // Tasso contratti finanziati sul totale filtrato — cambia con tutti i filtri (incl. periodo)
  const finanziati = useMemo(() => filtered.filter(c => !!c.prezzo?.hasFinanziamento).length, [filtered]);
  const percFinanziati = filtered.length > 0 ? Math.round((finanziati / filtered.length) * 100) : 0;

  if (loading) return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text }}>Caricamento contratti...</div>;

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      {/* HEADER */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900', color: T.text }}>Contratti</h2>
            <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>{filtered.length} contratti</div>
          </div>
          {filtered.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '12px' }}>
              <span style={{ fontSize: '15px' }}>💳</span>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '900', color: '#3b82f6', lineHeight: 1.1 }}>{percFinanziati}% finanziati</div>
                <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '700' }}>{finanziati} su {filtered.length} contratti</div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {['admin','super_admin'].includes(user?.ruolo) && <button onClick={async (e) => {
              const b = e.currentTarget; b.disabled = true; b.textContent = '↻ Aggiorno…';
              try { await window._radarScaricaCompletoCrm?.(); } catch (err) { alert('Aggiornamento non riuscito: ' + err.message); }
              b.disabled = false; b.textContent = '↻ Aggiorna elenco';
            }} title="Riscarica tutti i contratti da Firebase"
              style={{ padding: '9px 14px', background: 'transparent', border: '1px solid #3b82f6', borderRadius: '12px', color: '#3b82f6', cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
              ↻ Aggiorna elenco
            </button>}
            {['admin','super_admin'].includes(user?.ruolo) && (
              puoEsportareExcel(user) && (
              <button onClick={() => setColonnePickerOpenContratti(true)} style={{ padding: '9px 14px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
                📊 Excel ({filtered.length})
              </button>
              )
            )}
            {!isMobile && (
              <div style={{ display: 'flex', background: T.bg, borderRadius: '10px', padding: '3px', gap: '2px' }}>
                <button onClick={() => setViewMode('cards')} style={{ padding: '6px 10px', fontSize: '10px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: viewMode === 'cards' ? '#10b981' : 'transparent', color: viewMode === 'cards' ? '#fff' : T.textSub }}>⊞</button>
                <button onClick={() => setViewMode('list')} style={{ padding: '6px 10px', fontSize: '10px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: viewMode === 'list' ? '#10b981' : 'transparent', color: viewMode === 'list' ? '#fff' : T.textSub }}>≡</button>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="🔍 Cerca..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', padding: '8px 12px', color: T.text, fontSize: '12px', outline: 'none', flex: 1, minWidth: '140px' }} />
          <div style={{ display: 'flex', background: T.bg, borderRadius: '10px', padding: '3px', gap: '2px', flexWrap: 'wrap' }}>
            {[['tutti','Tutti'],['bozza','Bozza'],['approvato','✅ Approvati'],['venduto','🚗 Venduti'],['annullato','❌ Annullati']].map(([val,label]) => (
              <button key={val} onClick={() => setFiltroStato(val)}
                style={{ padding: '6px 10px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroStato === val ? '#10b981' : 'transparent', color: filtroStato === val ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', background: T.bg, borderRadius: '10px', padding: '3px', gap: '2px' }}>
            {[['tutti','Tutti'],['b2b','🏢 B2B'],['b2c','👤 B2C']].map(([val,label]) => (
              <button key={val} onClick={() => setFiltroTipo(val)}
                style={{ padding: '6px 10px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroTipo === val ? '#6366f1' : 'transparent', color: filtroTipo === val ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', background: T.bg, borderRadius: '10px', padding: '3px', gap: '2px' }}>
            {[['tutti','Sedi'],['agnano','📍 Agnano'],['nola','📍 Nola'],['salerno','📍 Salerno']].map(([val,label]) => (
              <button key={val} onClick={() => setFiltroSede(val)}
                style={{ padding: '6px 10px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroSede === val ? '#10b981' : 'transparent', color: filtroSede === val ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', background: T.bg, borderRadius: '10px', padding: '3px', gap: '2px' }}>
            {[['tutti','Tutti i veicoli'],['nuovo','✨ Nuovo'],['usato','🔧 Usato/KM0']].map(([val,label]) => (
              <button key={val} onClick={() => setFiltroVeicolo(val)}
                style={{ padding: '6px 10px', fontSize: '9px', fontWeight: '900', border: 'none', borderRadius: '8px', cursor: 'pointer', background: filtroVeicolo === val ? '#f59e0b' : 'transparent', color: filtroVeicolo === val ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* FILTRO DATE */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: T.textMuted }}>📅 Periodo:</div>
          <input type="date" value={fDataDa} onChange={e => setFDataDa(e.target.value)}
            style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '8px', padding: '6px 10px', color: T.text, fontSize: '11px', outline: 'none' }} />
          <span style={{ color: T.textMuted, fontSize: '11px' }}>→</span>
          <input type="date" value={fDataA} onChange={e => setFDataA(e.target.value)}
            style={{ background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '8px', padding: '6px 10px', color: T.text, fontSize: '11px', outline: 'none' }} />
          {(fDataDa || fDataA) && (
            <button onClick={() => { setFDataDa(''); setFDataA(''); }}
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '6px 10px', color: '#ef4444', cursor: 'pointer', fontSize: '10px', fontWeight: '700' }}>
              ✕ Reset
            </button>
          )}
          {(fDataDa || fDataA) && <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700' }}>{filtered.length} risultati</span>}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
          <div style={{ fontSize: '14px', fontWeight: '700' }}>Nessun contratto ancora</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>Vai su PREVENTIVI e clicca "📄 Converti"</div>
        </div>

      ) : (isMobile || viewMode === 'cards') ? (
        /* CARDS — sempre su mobile, opzionale su desktop */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map((c, i) => {
            const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim() || 'Cliente';
            const pr = c.prezzo || {};
            const _totSC = Object.entries(c.servizi||{}).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0);
            const _estC = pr.estinzioneAnticipata ? parseFloat(pr.costoEstinzioneAnticipata||0) : 0;
            const _prezzoListinoC = parseFloat(pr.prezzoListino||c.veicolo?.prezzo||0);
            const _scontoC = parseFloat(pr.sconto||0);
            const _ivaC = pr.hasIVA ? Math.round((_prezzoListinoC - _scontoC) * 0.22) : 0;
            const saldo = (_prezzoListinoC - _scontoC + parseFloat(pr.ipt||0)) + _totSC + _ivaC - valorePermuteTot(pr) - parseFloat(pr.caparra||0) - parseFloat(pr.importoFinanziato||0) + _estC;
            const avanzamentoCard = getAvanzamento(c);
            return (
              <div key={c.id||i} onClick={() => setSelected(c)}
                style={{ background: pr.hasFinanziamento ? `linear-gradient(90deg, rgba(59,130,246,0.07), ${T.surface} 40%)` : T.surface, borderRadius: '16px', border: `1px solid ${statoColor(c.stato)}30`, borderLeft: pr.hasFinanziamento ? '3px solid #3b82f6' : `1px solid ${statoColor(c.stato)}30`, padding: '14px 16px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 16px ${statoColor(c.stato)}20`}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '9px', color: statoColor(c.stato), fontWeight: '900', marginBottom: '2px' }}>
                      {statoLabel(c.stato)} · N. {c.datiExtra?.numeroContratto}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '900', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nc}</div>
                      {c.cliente?.b2b && (
                        <span style={{ fontSize: '9px', fontWeight: '900', color: '#6366f1', background: 'rgba(99,102,241,0.15)', border: '1px solid #6366f140', borderRadius: '6px', padding: '2px 6px', flexShrink: 0 }}>B2B</span>
                      )}
                      {c.cliente?.isPartner && (
                        <span style={{ fontSize: '9px', fontWeight: '900', color: '#f59e0b', background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b40', borderRadius: '6px', padding: '2px 6px', flexShrink: 0 }}>🤝 PARTNER</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{c.veicolo?.marca} {c.veicolo?.modello}</div>
                    <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '1px' }}>{c.venditoreNome}</div>
                    {avanzamentoCard && (
                      <div style={{ fontSize: '9px', fontWeight: '800', color: avanzamentoCard.color, marginTop: '4px' }}>{avanzamentoCard.label}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {c.veicolo?.mainImg && <img src={c.veicolo.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '56px', height: '38px', objectFit: 'cover', borderRadius: '6px', marginBottom: '4px', display: 'block' }} />}
                    <div style={{ fontSize: '13px', fontWeight: '900', color: '#10b981' }}>{fmt(saldo)}</div>
                    <div style={{ fontSize: '9px', color: T.textMuted }}>{c.datiExtra?.dataFirma ? new Date(c.datiExtra.dataFirma).toLocaleDateString('it-IT') : '—'}</div>
                  </div>
                </div>
                {['approvato', 'venduto'].includes(c.stato) && c.approvazioneNome && (
                  <div style={{ marginTop: '8px', padding: '5px 10px', background: 'rgba(16,185,129,0.1)', borderRadius: '6px', fontSize: '9px', color: '#10b981', fontWeight: '700' }}>
                    ✅ {c.approvazioneNome}
                  </div>
                )}
                {c.stato === 'annullato' && c.annullamentoMotivazione && (
                  <div style={{ marginTop: '6px', padding: '5px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: '6px', fontSize: '9px', color: '#ef4444', fontStyle: 'italic' }}>
                    ❌ {c.annullamentoMotivazione}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      ) : (
        /* LISTA — solo desktop */
        <div style={{ background: T.surface, borderRadius: '18px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr 90px 90px 140px', padding: '10px 16px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
            {['N. CONTR.', 'CLIENTE', 'VEICOLO', 'VENDITORE', 'DATA', 'STATO', 'AVANZAMENTO'].map(h => (
              <div key={h} style={{ fontSize: '8px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px' }}>{h}</div>
            ))}
          </div>
          {filtered.map((c, i) => {
            const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim() || '—';
            const haFinanziamento = !!c.prezzo?.hasFinanziamento;
            const avanzamento = getAvanzamento(c);
            return (
              <div key={c.id||i} onClick={() => setSelected(c)}
                style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr 90px 90px 140px', padding: '11px 16px', borderBottom: `1px solid ${T.border}`, borderLeft: haFinanziamento ? '3px solid #3b82f6' : '3px solid transparent', background: haFinanziamento ? 'rgba(59,130,246,0.05)' : 'transparent', cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = haFinanziamento ? 'rgba(59,130,246,0.1)' : T.bg}
                onMouseLeave={e => e.currentTarget.style.background = haFinanziamento ? 'rgba(59,130,246,0.05)' : 'transparent'}
              >
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#10b981' }}>{c.datiExtra?.numeroContratto || '—'}</div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nc}</div>
                <div style={{ fontSize: '11px', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.veicolo?.marca} {c.veicolo?.modello}</div>
                <div style={{ fontSize: '11px', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.venditoreNome || '—'}</div>
                <div style={{ fontSize: '10px', color: T.textSub }}>{c.datiExtra?.dataFirma ? new Date(c.datiExtra.dataFirma).toLocaleDateString('it-IT') : '—'}</div>
                <div style={{ fontSize: '9px', fontWeight: '900', color: statoColor(c.stato) }}>{statoLabel(c.stato)}</div>
                <div style={{ fontSize: '9px', fontWeight: '800', color: avanzamento?.color || T.textMuted }}>{avanzamento?.label || '—'}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETTAGLIO CONTRATTO */}
      {selected && (
        <ContrattoDetailModal
          c={selected}
          onClose={() => setSelected(null)}
          user={user}
          onApprova={() => { handleApprova(selected); setSelected(null); }}
          onModifica={(c) => { setSelected(null); setModificandoContratto(c); }}
        />
      )}
      {modificandoContratto && (
        <ContrattoModal
          preventivo={{ ...modificandoContratto, id: modificandoContratto.preventivoId }}
          contrattoEsistente={modificandoContratto}
          user={user}
          db={db}
          onClose={() => setModificandoContratto(null)}
          onSaved={() => setModificandoContratto(null)}
        />
      )}
      {colonnePickerOpenContratti && (
        <ColonneExportModal
          titolo="Esporta Contratti"
          colonneDisponibili={COLONNE_EXPORT_CONTRATTI}
          onClose={() => setColonnePickerOpenContratti(false)}
          onEsporta={async (colonneScelte) => {
            exportToExcel([], filtered, 'RadaR_Contratti', colonneScelte);
            setColonnePickerOpenContratti(false);
          }}
        />
      )}
    </div>
  );
}

// ─── DETTAGLIO CONTRATTO ──────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// LEADSPARK INTEGRATION
// ═══════════════════════════════════════════════════════════════════════════════
const LEADSPARK_API_KEY = '3a8b2c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b';
const LEADSPARK_BASE = 'https://api.ew5.it/rest/dms';
const LEADSPARK_PREFIX = 'GAS-';

// Branch ID numerici LeadSpark
const LEADSPARK_BRANCH = {
  agnano: '5',
  nola:   '8',
  salerno: '1',
};

function getLeadSparkBranch(venditoreNome) {
  const nome = (venditoreNome || '').toLowerCase();
  // Prima le liste ufficiali per sede (nome completo: "castaldo" da solo
  // indicherebbe anche Roberto/Marco Castaldo, che sono di Agnano)
  if (isNolaNome(nome)) return LEADSPARK_BRANCH.nola;
  if (isSalernoNome(nome)) return LEADSPARK_BRANCH.salerno;
  if (nome.includes('nola') || nome.includes('esposito') || nome.includes('ferrara')) return LEADSPARK_BRANCH.nola;
  if (nome.includes('salerno') || nome.includes('battipaglia') || nome.includes('gerardi')) return LEADSPARK_BRANCH.salerno;
  return LEADSPARK_BRANCH.agnano; // default Agnano
}

function fmtDataLS(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return '';
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function sendToLeadSpark(deal, dealStatus) {
  // Fire & forget — non blocca mai RadaR
  _sendToLeadSparkAsync(deal, dealStatus).catch(e => {
    console.warn('LeadSpark fallito:', e.message);
    // Retry queue su Firebase
    try {
      const qKey = LEADSPARK_PREFIX + (deal.id || Date.now()) + '_' + Date.now();
      set(ref(db, `leadspark_queue/${qKey}`), {
        deal, dealStatus,
        errore: e.message,
        timestamp: new Date().toISOString(),
        tentativi: 1,
      }).catch(() => {});
    } catch(_) {}
  });
}

// ─── LEADSPARK: costruisce il payload formattato ─────────────────────────────
const LS_BRANCH_NAME = {
  'agnano':  'Gruppo Auto Scala srl',
  'nola':    'Gruppo Auto Scala srl Nola',
  'salerno': 'Gruppo Auto Scala srl Salerno',
};
const LS_SALESMAN_MAP = {
  'Roberto Castaldo':  'Roberto Castaldo',
  'Marco Castaldo':    'Marco Castaldo',
  'Luca Ambrosino':    'Luca Ambrosino',
  'Davide Zaritto':    'Davide Zaritto',
  'Guido Fiore':       'Guido Fiore',
  'Enrico Palumbo':    'Enrico Palumbo',
  'Rosario Scala':     'Rosario Scala',
  'Mattia Zuppa':      'Mattia Zuppa',
  'Claudio Filosa':    'Claudio Filosa',
  'Francesco Montuori':'Francesco Montuori',
  'Claudio Gerardi':   'Claudio Gerardi',
  'Ciro Imperato':     'Ciro Imperato',
  'Daniele Fiore':     'Roberto Castaldo', // TEST — rimuovere dopo
};

function buildLSPayload(p, dealStatus) {
  const cl = p.cliente  || {};
  const v  = p.veicolo  || {};
  const pr = p.prezzo   || {};
  const de = p.datiExtra || {};

  // Sede
  const n = (p.venditoreNome || '').toLowerCase();
  const sede = isSalernoNome(n) ? 'salerno'
    : isNolaNome(n) || n.includes('santaniello') || n.includes('montuori') || n.includes('filosa') ? 'nola'
    : isSalernoNome(n) || n.includes('gerardi') ? 'salerno' : 'agnano';
  const branchName = LS_BRANCH_NAME[sede];
  if (!branchName) return null; // sede non attiva

  // Venditore
  const salesmanName = LS_SALESMAN_MAP[p.venditoreNome] || p.venditoreNome || '';
  if (!salesmanName) return null; // venditore non trovato

  // Cliente
  const isAzienda = !!(cl.ragioneSociale?.trim());
  const tel = (cl.cellulare || cl.telefono || '').replace(/\s/g,'').replace(/^\+39/,'');
  const email = cl.email || 'noreply@radargas.it';
  const cod_customer = tel && tel.length >= 8 ? 'RDR-CLI-' + tel
    : email.includes('@') ? 'RDR-CLI-' + email.toLowerCase().replace(/[^a-z0-9]/g,'_')
    : 'RDR-CLI-' + `${cl.cognome||''}${cl.nome||''}`.toLowerCase().replace(/[^a-z]/g,'');

  // Stato
  let deal_status = 'A';
  if (dealStatus === 'C' || p.stato === 'approvato' || p.stato === 'venduto') deal_status = 'C';
  if (dealStatus === 'F' || p.stato === 'annullato') deal_status = 'F';

  // Data
  const lsDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    } catch { return ''; }
  };

  // amount_offer = listino + IPT + servizi - sconto
  const pL  = parseFloat(pr.prezzoListino || v.prezzo || 0);
  const sc  = parseFloat(pr.sconto || pr.scontoDiscrezionale || 0);
  const ipt = parseFloat(pr.ipt || 0);
  const totS = Object.entries(p.servizi||{}).filter(([k])=>k.startsWith('costo')).reduce((t,[,v2])=>t+(parseFloat(v2)||0),0);
  const amount = Math.max(0, pL + ipt + totS - sc);

  // Fonte → origine
  const fonte = (p.fonte || '').toLowerCase().trim();
  const origin = !fonte ? 'Ingresso'
    : fonte.includes('meta') || fonte.includes('facebook') ? 'Facebook/Instagram'
    : fonte.includes('google') ? 'Google'
    : fonte.includes('ingresso') ? 'Ingresso'
    : fonte.includes('telefon') ? 'Telefono'
    : fonte.includes('rete') || fonte.includes('esterno') ? 'Rete Esterna'
    : fonte.includes('personale') ? 'Cliente Personale'
    : fonte.includes('gruppoautoscala') ? 'Web'
    : p.fonte;

  // Data immatricolazione
  const regRaw = v.registrazione || '';
  let reg = '';
  if (/^\d{2}\/\d{4}$/.test(regRaw)) reg = '01/' + regRaw;
  else if (/^\d{4}-\d{2}$/.test(regRaw)) { const [y,m]=regRaw.split('-'); reg=`01/${m}/${y}`; }
  else reg = regRaw;

  const payload = {
    // Usa sempre il preventivoId come cod_deal — così contratto e preventivo
    // aggiornano la STESSA trattativa su LeadSpark invece di crearne una nuova
    cod_deal:        `RDR-${p.preventivoId || p.id}`,
    id_deal_offer:   `RDR-OFF-${p.preventivoId || p.id}`,
    date_deal:       lsDate(p.dataCreazione) || lsDate(new Date().toISOString()),
    deal_status,
    branch_name:     branchName,
    salesman_name:   salesmanName,
    cod_customer,
    customer_type:   isAzienda ? 'A' : 'P',
    surname:         isAzienda ? (cl.ragioneSociale||'N.D.') : (cl.cognome||'N.D.'),
    name:            isAzienda ? '' : (cl.nome||''),
    surname_name_or_company_name: isAzienda
      ? (cl.ragioneSociale||'N.D.')
      : `${cl.cognome||''} ${cl.nome||''}`.trim()||'N.D.',
    ...(isAzienda ? { company_name: cl.ragioneSociale } : {}),
    tel_number1:     tel || '0000000000',
    email1:          email,
    vehicle_type:    v.tipo === 'Nuovo' ? 'nuovo' : v.tipo === 'Km0' ? 'km0' : 'usato',
    brand_name:      v.marca || 'N.D.',
    vehicle_model:   (v.modello||'N.D.').replace(/&amp;/g,'&'),
    vehicle_version: (v.versione||'').replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>'),
    vehicle_plate:   v.targa || '',
    vehicle_vin:     de.vin || v.vin || '',
    vehicle_registration_date: reg,
    km_miles:        parseInt(v.km||0),
    amount_offer:    amount.toFixed(2),
    financing:       pr.hasFinanziamento ? 'S' : 'N',
    warranty:        (p.servizi?.mawdy36||p.servizi?.mawdy||p.servizi?.garanziaEstesa) ? 'S' : 'N',
    origin,
    note1:           p.fonte ? `Fonte: ${p.fonte}` : '',
    note2:           '',
  };

  if (deal_status === 'C') {
    payload.date_sales    = lsDate(p.approvazioneData || p.dataCreazione);
    // payload.date_delivery rimosso — data consegna non disponibile al momento del contratto
  }

  return payload;
}


async function _sendToLeadSparkAsync(deal, dealStatus) {
  // Prova prima il proxy PHP su radargas.it (sempre attivo, tutti i PC)
  // Fallback al notify-server locale (solo se il PC è acceso)
  const tryFetch = async (url, body) => {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);
    try {
      const resp = await fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal:  ctrl.signal,
      });
      clearTimeout(timer);
      return resp;
    } finally {
      clearTimeout(timer);
    }
  };

  // 1. Proxy PHP (sempre disponibile)
  try {
    const lsPayload = buildLSPayload(deal, dealStatus);
    if (!lsPayload) {
      console.warn('LeadSpark SKIP — venditore o sede non configurata');
      return;
    }

    // ── Trattativa unica per cliente ─────────────────────────────────────
    // Se il cliente ha già una trattativa aperta su LeadSpark, riuso cod_deal
    const clienteKey = lsPayload.cod_customer.replace(/[^a-zA-Z0-9_-]/g, '_');
    const trattativaRef = ref(db, `leadspark_trattative/${clienteKey}`);
    const trattativaSnap = await get(trattativaRef);

    if (trattativaSnap.exists() && dealStatus === 'A') {
      // Cliente già presente — riusa cod_deal, nuovo id_deal_offer
      const existing = trattativaSnap.val();
      lsPayload.cod_deal = existing.cod_deal;
      lsPayload.id_deal_offer = `RDR-OFF-${deal.preventivoId || deal.id}`;
      console.log(`[LeadSpark] Riuso cod_deal esistente: ${lsPayload.cod_deal}`);
    } else if (dealStatus === 'A') {
      // Prima trattativa — salva cod_deal su Firebase
      await set(trattativaRef, {
        cod_deal: lsPayload.cod_deal,
        cod_customer: lsPayload.cod_customer,
        cliente: `${lsPayload.surname} ${lsPayload.name}`.trim(),
        dataApertura: new Date().toISOString(),
      });
      console.log(`[LeadSpark] Nuova trattativa: ${lsPayload.cod_deal}`);
    } else if (['C','F'].includes(dealStatus) && trattativaSnap.exists()) {
      // Chiusa/Fallita — usa cod_deal esistente
      lsPayload.cod_deal = trattativaSnap.val().cod_deal;
      if (dealStatus === 'F') await set(trattativaRef, null); // reset per futura trattativa
    }

    console.log('[LeadSpark] payload:', JSON.stringify({
      cod_deal:     lsPayload.cod_deal,
      deal_status:  lsPayload.deal_status,
      salesman:     lsPayload.salesman_name,
      branch:       lsPayload.branch_name,
      amount_offer: lsPayload.amount_offer,
      origin:       lsPayload.deal_origin,
      cliente:      lsPayload.surname + ' ' + lsPayload.name,
      veicolo:      lsPayload.brand_name + ' ' + lsPayload.vehicle_model,
    }));
    const resp = await tryFetch(
      LEADSPARK_PHP,
      JSON.stringify({ method: 'TransferDeal', payload: [lsPayload] })
    );
    const json = await resp.json();
    if (json.Message === 'success') {
      console.log(`LeadSpark OK [${dealStatus}] id=${deal.id}`);
      return;
    }
    throw new Error(JSON.stringify(json));
  } catch(e) {
    console.warn('LeadSpark PHP proxy fallito, provo localhost:', e.message);
  }

  // 2. Fallback notify-server locale
  try {
    const resp = await tryFetch(
      'http://localhost:3002/leadspark/sync',
      JSON.stringify({ deal, dealStatus })
    );
    const json = await resp.json();
    if (json.error) throw new Error(json.error);
    console.log(`LeadSpark queued [${dealStatus}] id=${deal.id}`);
  } catch(e) {
    console.warn('LeadSpark fallito (PHP + localhost):', e.message);
  }
}
// Esponi globalmente per PreventivoModal
if (typeof window !== 'undefined') window.sendToLeadSpark = sendToLeadSpark;

function ContrattoDetailModal({ c: cProp, onClose, user, onApprova, onModifica }) {
  const theme = useTheme(); const T = THEMES[theme];
  const [c, setC] = useState(cProp);

  // Ricarica dati freschi da Firebase all'apertura
  useEffect(() => {
    if (!cProp?.id) return;
    get(ref(db, `contratti/${cProp.id}`)).then(snap => {
      if (snap.exists()) setC({ ...snap.val(), id: cProp.id });
    }).catch(() => {});
  }, [cProp?.id]);
  const [confermaApprova, setConfermaApprova] = useState(false);
  const [permutaStockModal, setPermutaStockModal] = useState(null);
  const [confermaEliminaContratto, setConfermaEliminaContratto] = useState(false);
  const [confermaAnnulla, setConfermaAnnulla] = useState(false);
  const [motivazioneAnnullo, setMotivazioneAnnullo] = useState('');
  const [activeTab, setActiveTab] = useState('dettagli');
  const [stamping, setStamping] = useState(false);
  const [isB2B, setIsB2B] = useState(!!c.cliente?.b2b);
  const [interventiVeicolo, setInterventiVeicolo] = useState([]);
  const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim() || 'Cliente';
  const pr = c.prezzo || {};
  const pL = parseFloat(pr.prezzoListino || c.veicolo?.prezzo || 0);
  const sc = parseFloat(pr.sconto || 0);
  const ipt = parseFloat(pr.ipt || 0);
  const perm = valorePermuteTot(pr);
  const cap = parseFloat(pr.caparra || 0);
  const fin = parseFloat(pr.importoFinanziato || 0);
  const totServiziC = Object.entries(c.servizi||{}).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0);
  const accessoriCMc = (c.servizi?.accessoriCasaMadreList || []).reduce((t,a) => t+(parseFloat(a.prezzo)||0), 0);
  const hasIVAc = !!pr.hasIVA;
  const ivaImportoC = hasIVAc ? Math.round((pL - sc) * 0.22) : 0;
  const estinzioneCostoC = pr.estinzioneAnticipata ? parseFloat(pr.costoEstinzioneAnticipata||0) : 0;
  const speseLeasingCostoC = pr.speseGestioneLeasing ? parseFloat(pr.costoSpeseGestioneLeasing||0) : 0;
  const saldo = (pL - sc + ipt) + accessoriCMc + totServiziC + ivaImportoC - perm - cap - fin + estinzioneCostoC + speseLeasingCostoC;
  const statoColor = s => s === 'approvato' ? '#10b981' : '#f59e0b';
  const puoApprovare = c.stato !== 'approvato' && (user.ruolo === 'admin' || user.ruolo === 'super_admin') && user.ruolo !== 'bdc';
  const puoStampare = true; // tutti i ruoli possono stampare i documenti contrattuali
  const puoEliminare = user.ruolo === 'super_admin';
  const puoAnnullare = c.stato === 'approvato' && (user.ruolo === 'admin' || user.ruolo === 'super_admin');
  const ruoloN = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
  // Consulente/brand_manager_se possono modificare una bozza SOLO se è la
  // propria (il venditore associato al contratto coincide con l'utente
  // loggato) — back_office/finance restano senza questo vincolo, dato che
  // per il loro ruolo lavorano su bozze di chiunque, non hanno un "proprio
  // contratto".
  const nomeUtenteCompletoBO = `${user?.nome || ''} ${user?.cognome || ''}`.trim().toLowerCase();
  const contrattoEProprio = (c.venditoreNome || '').toLowerCase().trim() === nomeUtenteCompletoBO;
  const puoModificareContratto = ['admin','super_admin'].includes(ruoloN)
    || (c.stato === 'bozza' && ['back_office','finance'].includes(ruoloN))
    || (c.stato === 'bozza' && ['consulente','brand_manager_se'].includes(ruoloN) && contrattoEProprio)
    // Il consulente può riaprire anche un PROPRIO contratto approvato: al
    // salvataggio torna in bozza e va riapprovato (vedi ContrattoModal)
    || (c.stato === 'approvato' && ['consulente','brand_manager_se'].includes(ruoloN) && contrattoEProprio);
  const riaperturaDaConsulente = c.stato === 'approvato' && ['consulente','brand_manager_se'].includes(ruoloN);
  const puoConfermaVendita = c.stato === 'approvato' && ['admin','super_admin','back_office'].includes(ruoloN);
  const isAdminSuper = ['admin','super_admin'].includes(ruoloN);

  // ── Cambio veicolo dallo stock disponibile (solo admin/super_admin) ──
  const [cambioVeicoloOpen, setCambioVeicoloOpen] = useState(false);
  const [stockDisponibile, setStockDisponibile] = useState([]);
  const [caricandoStock, setCaricandoStock] = useState(false);
  const [searchVeicolo, setSearchVeicolo] = useState('');
  const [salvandoVeicolo, setSalvandoVeicolo] = useState(false);

  useEffect(() => {
    if (!cambioVeicoloOpen) return;
    setCaricandoStock(true);
    get(ref(db, 'stock_data/vehicles')).then(snap => {
      const d = snap.val();
      const arr = d ? (Array.isArray(d) ? d : Object.values(d)) : [];
      setStockDisponibile(arr.filter(v => (v.status||'').toUpperCase() !== 'VENDUTA'));
      setCaricandoStock(false);
    }).catch(() => setCaricandoStock(false));
  }, [cambioVeicoloOpen]);

  const veicoliFiltrati = useMemo(() => {
    if (!searchVeicolo.trim()) return stockDisponibile;
    const s = searchVeicolo.toLowerCase();
    return stockDisponibile.filter(v => `${v.marca||''} ${v.modello||''} ${v.versione||''} ${v.targa||''} ${v.vin||''}`.toLowerCase().includes(s));
  }, [stockDisponibile, searchVeicolo]);

  const handleCambiaVeicolo = async (nuovo) => {
    if (!window.confirm(`Sostituire il veicolo del contratto con:\n\n${nuovo.marca} ${nuovo.modello} ${nuovo.versione||''}\nTarga: ${nuovo.targa||'N.D.'} · VIN: ${nuovo.vin||'N.D.'}\n\nIl prezzo concordato NON verrà modificato automaticamente — se necessario aggiornalo da "Modifica contratto".`)) return;
    setSalvandoVeicolo(true);
    try {
      const vecchio = c.veicolo || {};
      const nuovoVeicoloData = {
        id: nuovo.id || '', marca: nuovo.marca||'', modello: nuovo.modello||'', versione: nuovo.versione||'',
        targa: nuovo.targa||'', vin: nuovo.vin||'', tipo: nuovo.tipo||'', colore: nuovo.colore||'',
        km: nuovo.km||0, registrazione: nuovo.registrazione||'',
        prezzo: nuovo.prezzo||0, bottomPrice: nuovo.bottomPrice||0, mainImg: nuovo.mainImg||'', images: nuovo.images||[],
      };
      await set(ref(db, `contratti/${c.id}/veicolo`), nuovoVeicoloData);
      // Telaio, immatricolazione e colore scritti a mano nei dati del contratto
      // (tipico degli off stock) hanno la precedenza in stampa: vanno
      // aggiornati col nuovo veicolo, altrimenti la stampa mostra il vecchio
      await update(ref(db, `contratti/${c.id}/datiExtra`), {
        vin: nuovo.vin || '',
        immatricolazione: nuovo.registrazione || '',
        coloreVeicolo: nuovo.colore || '',
      });
      // Storico completo delle sostituzioni (veicoloSostituito tiene solo l'ultima)
      await set(ref(db, `contratti/${c.id}/veicoliSostituiti/s_${Date.now()}`), {
        da: { marca: vecchio.marca||'', modello: vecchio.modello||'', targa: vecchio.targa||'', vin: vecchio.vin||'', id: vecchio.id||'' },
        a: { marca: nuovo.marca||'', modello: nuovo.modello||'', targa: nuovo.targa||'', vin: nuovo.vin||'', id: nuovo.id||'' },
        utente: `${user.nome} ${user.cognome}`, timestamp: new Date().toISOString(),
      });
      await set(ref(db, `contratti/${c.id}/veicoloSostituito`), {
        da: { marca: vecchio.marca||'', modello: vecchio.modello||'', targa: vecchio.targa||'', vin: vecchio.vin||'' },
        a: { marca: nuovo.marca||'', modello: nuovo.modello||'', targa: nuovo.targa||'', vin: nuovo.vin||'' },
        utente: `${user.nome} ${user.cognome}`, timestamp: new Date().toISOString(),
      });
      await set(ref(db, `contratti/${c.id}/dataModifica`), new Date().toISOString());
      // Prenotazione stock: si toglie SEMPRE dal vecchio veicolo (qualunque
      // sia lo stato del contratto) e, se approvato, passa al nuovo
      await liberaTrattativaVeicolo(vecchio);
      if (c.stato === 'approvato') {
        const nuovaChiave = (nuovo.targa || nuovo.vin || nuovo.id || '').toString().replace(/[^a-zA-Z0-9]/g, '_');
        if (nuovaChiave) {
          await set(ref(db, `veicoli_in_trattativa/${nuovaChiave}`), {
            targa: nuovo.targa||'', vin: nuovo.vin||'', veicoloId: nuovo.id||'', marca: nuovo.marca||'', modello: nuovo.modello||'',
            contrattoId: c.id, clienteCognome: c.cliente?.cognome || c.cliente?.ragioneSociale || '',
            approvazioneData: new Date().toISOString(), approvazioneNome: `${user.nome} ${user.cognome}`,
          });
        }
      }
      // Stato in Stock: il vecchio veicolo torna DISPONIBILE (prima restava
      // CONTRATTUALIZZATA/VENDUTA per sempre), il nuovo prende lo stato del contratto
      await ripristinaVeicoloInStock(vecchio, 'cambio veicolo contratto');
      const nomeClienteC = c.cliente?.ragioneSociale || `${c.cliente?.cognome||''} ${c.cliente?.nome||''}`.trim();
      if (c.stato === 'approvato') await impostaVeicoloInStock(nuovo, { status: 'CONTRATTUALIZZATA', consulenteNome: c.venditoreNome || '', clienteNome: nomeClienteC });
      if (c.stato === 'venduto') await impostaVeicoloInStock(nuovo, { status: 'VENDUTA', vendutoAt: c.vendutoAt || new Date().toISOString(), consulenteNome: c.venditoreNome || '', clienteNome: nomeClienteC });
      setC(prev => ({ ...prev, veicolo: nuovoVeicoloData, datiExtra: { ...(prev.datiExtra || {}), vin: nuovo.vin || '', immatricolazione: nuovo.registrazione || '', coloreVeicolo: nuovo.colore || '' } }));
      setCambioVeicoloOpen(false);
      setSearchVeicolo('');
    } catch(e) { alert('Errore nel cambio veicolo: ' + e.message); }
    setSalvandoVeicolo(false);
  };

  // Carica interventi service per questo veicolo (solo admin/super_admin)
  useEffect(() => {
    if (!isAdminSuper) return;
    const vin = (c.veicolo?.vin||'').toLowerCase().trim();
    const targa = (c.veicolo?.targa||'').toLowerCase().trim();
    if (!vin && !targa) return;
    // Fatture officina + ODL non ancora fatturati (es. ordini interni di preparazione)
    Promise.all([
      window._radarInterventiCache ? Promise.resolve(window._radarInterventiCache)
        : get(ref(db, 'service_interventi')).then(snap => (snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ ...v, id: v.id || id })) : [])),
      caricaOdlPerCosti().catch(() => []),
    ]).then(([fatture, odl]) => {
      setInterventiVeicolo(documentiOfficinaVeicolo({ vin: c.veicolo?.vin, targa: c.veicolo?.targa, fatture, odl }));
    });
  }, [c.veicolo?.vin, c.veicolo?.targa]);

  // Calcolo marginalità — prezzoListino è l'imponibile, il prezzo di vendita
  // reale include l'IVA quando il contratto ce l'ha esposta.
  const _ivaBadge = pr.hasIVA ? Math.round((pL - sc) * 0.22) : 0;
  // Prezzo auto venduta = listino − sconto + optional casa madre + IPT (come nei Dettagli)
  const prezzoAutoVenduta = pL - sc + accessoriCMc + ipt;
  // Il margine si calcola sul prezzo auto venduta completo (IPT inclusa)
  // IPT / passaggio di proprietà: incassata ma versata al PRA → non è ricavo
  const prezzoVendita = prezzoAutoVenduta - ipt + _ivaBadge;
  // Se restano duplicati in stock (targa/VIN ripetuti da import precedenti a
  // questo fix), preferisci sempre la copia con bottomPrice valorizzato
  // invece del primo trovato — altrimenti il costo può risultare 0/sbagliato
  // per pura sfortuna nell'ordine dell'array. Consiglio: usa "Rimuovi
  // Duplicati" nello Stock per ripulire definitivamente i doppioni residui.
  const stockCandidatiBP = (window._radarStockCache?.vehicles || []).filter(sv2 => (c.veicolo?.id && sv2.id === c.veicolo.id) || (c.veicolo?.targa && sv2.targa === c.veicolo.targa) || (c.veicolo?.vin && sv2.vin === c.veicolo.vin));
  const stockLiveBP = stockCandidatiBP.find(sv2 => parseFloat(sv2.bottomPrice) > 0) || stockCandidatiBP[0];
  const bottomPrice = parseFloat(c.bottomPrice || c.veicolo?.bottomPrice || stockLiveBP?.bottomPrice || 0);
  // ODL non fatturati non hanno totaleNetto: si somma dalle righe (sconti inclusi)
  const nettoDoc = d => {
    if (parseFloat(d.totaleNetto) > 0) return parseFloat(d.totaleNetto);
    const righe = d.righe || [];
    const base = righe.filter(r => r.tipo !== 'sconto').reduce((t, r) => t + (parseFloat(r.qta) || 0) * (parseFloat(r.prezzoUnit) || 0), 0);
    const sconto = righe.filter(r => r.tipo === 'sconto').reduce((t, r) => t + base * ((parseFloat(r.percentuale) || 0) / 100), 0);
    return Math.max(0, base - sconto) / fattoreScorporo(d);
  };
  const totInterventi = interventiVeicolo.reduce((s,i) => s + nettoDoc(i), 0);
  const margine = bottomPrice > 0 ? prezzoVendita - bottomPrice - totInterventi : null;

  const handleConfermaVendita = async () => {
    const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim();
    if (!window.confirm(`Confermare la vendita di ${c.veicolo?.marca} ${c.veicolo?.modello} a ${nc}? L'auto risulterà VENDUTA e non sarà più preventivabile.`)) return;
    const oggi = new Date().toISOString().split('T')[0];
    const dataInput = window.prompt('Data di consegna/vendita (AAAA-MM-GG):', oggi);
    const dataVendita = dataInput && dataInput.match(/^\d{4}-\d{2}-\d{2}$/) ? new Date(dataInput).toISOString() : new Date().toISOString();
    try {
      await set(ref(db, `contratti/${c.id}/stato`), 'venduto');
      await set(ref(db, `contratti/${c.id}/vendutoAt`), dataVendita);
      await set(ref(db, `contratti/${c.id}/dataVendita`), dataVendita);
      // Segna vettura come VENDUTA in stock — confronto normalizzato (non
      // più esatto) per non fallire silenziosamente su piccole differenze
      // di formato tra targa/VIN del contratto e quelli in stock.
      const normTargaVend = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
      const vin = normTargaVend(c.veicolo?.vin);
      const targa = normTargaVend(c.veicolo?.targa);
      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (stockSnap.exists()) {
        const vsRaw = stockSnap.val();
        const vs = Array.isArray(vsRaw) ? vsRaw : Object.values(vsRaw);
        const idx = vs.findIndex(v => (vin && normTargaVend(v.vin) === vin) || (targa && normTargaVend(v.targa) === targa));
        if (idx > -1) {
          await set(ref(db, `stock_data/vehicles/${idx}/status`), 'VENDUTA');
          await set(ref(db, `stock_data/vehicles/${idx}/vendutoAt`), new Date().toISOString());
          await set(ref(db, `stati_veicoli/${vs[idx].id}/status`), 'VENDUTA');
          window._radarStockCache = null; window._radarStockTs = 0;
        }
      }
      // LeadSpark — Trattativa Chiusa
      sendToLeadSpark({...c, stato:'venduto', dataVendita}, 'C');
      // Sincronizza statoConsegna in backoffice (solo B2C)
      if (!c.cliente?.b2b) {
        await set(ref(db, `contratti/${c.id}/backoffice/statoConsegna`), 'consegnato');
        await logBackoffice(c.id, 'statoConsegna', 'consegnato', user);
      }
      await set(ref(db, `contratti/${c.id}/dataModifica`), new Date().toISOString());
      await set(ref(db, `stati_contratti/${c.id}`), 'venduto');
      onClose();
    } catch(e) { alert('Errore: ' + e.message); }
  };

  const handleAnnulla = async () => {
    if (!motivazioneAnnullo.trim()) { alert('Inserisci una motivazione per l\'annullamento.'); return; }
    try {
      await set(ref(db, `contratti/${c.id}/stato`), 'annullato');
      await set(ref(db, `contratti/${c.id}/annullamentoData`), new Date().toISOString());
      await set(ref(db, `contratti/${c.id}/annullamentoNome`), `${user.nome} ${user.cognome} (${user.ruolo})`);
      await set(ref(db, `contratti/${c.id}/annullamentoMotivazione`), motivazioneAnnullo.trim());
      await set(ref(db, `contratti/${c.id}/dataModifica`), new Date().toISOString());
      await set(ref(db, `stati_contratti/${c.id}`), 'annullato');
      // Libera il veicolo dalla trattativa
      const v = c.veicolo || {};
      const chiave = v.targa || v.vin || v.id || null;
      await liberaTrattativaVeicolo(v);

      // Auto di nuovo DISPONIBILE in stock
      await ripristinaVeicoloInStock(v, 'annullamento contratto');

      // LeadSpark — Trattativa Fallita
      sendToLeadSpark({...c, stato:'annullato'}, 'F');
      setConfermaAnnulla(false);
      onClose();
    } catch(e) { alert('Errore annullamento: ' + e.message); }
  };

  const handleEliminaContratto = async () => {
    try {
      await set(ref(db, `contratti/${c.id}`), null);
      window._rimuoviContrattoDallaCache?.(c.id);
      // Rimuovi il veicolo da trattativa
      const v = c.veicolo || {};
      const chiave = v.targa || v.vin || v.id || null;
      await liberaTrattativaVeicolo(v);
      // Auto di nuovo DISPONIBILE in stock
      await ripristinaVeicoloInStock(v, 'eliminazione contratto');
      onClose();
    } catch(e) { alert('Errore eliminazione: ' + e.message); }
  };

  const handleStampaContratto = async () => {
    setStamping(true);
    try {
      // generaPacchettoPDF già importata staticamente in cima al file
      // Carica stock se non in cache
      if (!window._radarStockCache?.vehicles) {
        try {
          const snap = await get(ref(db, 'stock_data'));
          if (snap.val()?.vehicles) {
            window._radarStockCache = snap.val();
            window._radarStockTs = Date.now();
          }
        } catch(e) {}
      }
      const veicoloArricchito = { ...c.veicolo };
      // Priorità VIN: datiExtra (modificato manualmente) > veicolo.vin > stock
      const vinOverride = c.datiExtra?.vin || c.veicolo?.vin || '';
      if (vinOverride) veicoloArricchito.vin = vinOverride;
      if (c.datiExtra?.immatricolazione) veicoloArricchito.registrazione = c.datiExtra.immatricolazione;
      // Poi cerca in stock per targa o id
      if (!veicoloArricchito.vin || !veicoloArricchito.registrazione) {
        const cached = window._radarStockCache?.vehicles || [];
        const stockV = cached.find(sv => sv.targa === c.veicolo?.targa || sv.id === c.veicolo?.id);
        if (stockV) {
          if (!veicoloArricchito.vin && stockV.vin) veicoloArricchito.vin = stockV.vin;
          if (!veicoloArricchito.km && stockV.km) veicoloArricchito.km = stockV.km;
          if (!veicoloArricchito.registrazione && stockV.registrazione) veicoloArricchito.registrazione = stockV.registrazione;
        }
      }
      await generaPacchettoPDF(
        { cliente: c.cliente, veicolo: veicoloArricchito, prezzo: c.prezzo, servizi: c.servizi, creatoDaRuolo: c.creatoDaRuolo, venditoreNome: c.venditoreNome },
        c.datiExtra || {},
        user
      );
    } catch(e) {
      alert('Errore stampa: ' + e.message);
    } finally {
      setStamping(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: '24px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '580px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* HEADER */}
        <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '9px', color: statoColor(c.stato), fontWeight: '900', letterSpacing: '2px', marginBottom: '4px' }}>
                CONTRATTO N. {c.datiExtra?.numeroContratto} · {c.stato?.toUpperCase()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ margin: 0, color: T.text, fontSize: '18px', fontWeight: '900' }}>{nc}</h2>
                {isB2B && (
                  <span style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', background: 'rgba(99,102,241,0.15)', border: '1px solid #6366f140', borderRadius: '6px', padding: '3px 8px', flexShrink: 0 }}>🏢 B2B</span>
                )}
                {pr.legge104 && (
                  <span style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', background: 'rgba(59,130,246,0.15)', border: '1px solid #3b82f640', borderRadius: '6px', padding: '3px 8px', flexShrink: 0 }}>♿ LEGGE 104</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px', flexWrap: 'wrap' }}>
                <div style={{ color: T.textSub, fontSize: '12px' }}>{c.veicolo?.marca} {c.veicolo?.modello} {c.veicolo?.targa ? `· ${c.veicolo.targa}` : ''}</div>
                {isAdminSuper && (
                  <button onClick={() => setCambioVeicoloOpen(true)}
                    style={{ fontSize: '9px', fontWeight: '900', color: '#3b82f6', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '7px', padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    🔄 Cambia veicolo
                  </button>
                )}
              </div>
            </div>
            {c.veicolo?.mainImg && <img src={c.veicolo.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '75px', height: '52px', objectFit: 'cover', borderRadius: '8px', marginLeft: '10px', flexShrink: 0 }} />}
            <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', fontWeight: '900', fontSize: '18px', marginLeft: '10px', flexShrink: 0 }}>×</button>
          </div>

          {/* PANNELLO CAMBIO VEICOLO */}
          {cambioVeicoloOpen && (
            <div style={{ background: T.bg, border: '1px solid rgba(59,130,246,0.35)', borderRadius: '14px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: '900', color: '#3b82f6', letterSpacing: '0.5px' }}>🔄 SELEZIONA NUOVO VEICOLO DALLO STOCK</div>
                <button onClick={() => { setCambioVeicoloOpen(false); setSearchVeicolo(''); }} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: '16px', fontWeight: '900' }}>×</button>
              </div>
              <input placeholder="🔍 Cerca marca, modello, targa, VIN..." value={searchVeicolo} onChange={e => setSearchVeicolo(e.target.value)} autoFocus
                style={{ width: '100%', padding: '10px 12px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }} />
              {caricandoStock ? (
                <div style={{ textAlign: 'center', padding: '20px', color: T.textMuted, fontSize: '12px' }}>Caricamento stock...</div>
              ) : veicoliFiltrati.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: T.textMuted, fontSize: '12px' }}>Nessun veicolo trovato</div>
              ) : (
                <div style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {veicoliFiltrati.slice(0, 50).map((v, idx) => (
                    <button key={v.id||v.vin||v.targa||idx} disabled={salvandoVeicolo} onClick={() => handleCambiaVeicolo(v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: '10px', cursor: salvandoVeicolo ? 'default' : 'pointer', textAlign: 'left', opacity: salvandoVeicolo ? 0.6 : 1 }}>
                      {v.mainImg ? <img src={v.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '48px', height: '34px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} /> : <div style={{ width: '48px', height: '34px', background: T.bg, borderRadius: '6px', flexShrink: 0 }} />}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '800', color: T.text }}>{v.marca} {v.modello} {v.versione||''}</div>
                        <div style={{ fontSize: '10px', color: T.textMuted }}>{v.targa || 'N.D.'} · {v.colore||'—'} · {v.km ? `${v.km.toLocaleString('it-IT')} km` : '—'} · {v.prezzo ? new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v.prezzo) : '—'}</div>
                      </div>
                      {v.status === 'RESERVED' && <span style={{ fontSize: '9px', fontWeight: '900', color: '#f59e0b', flexShrink: 0 }}>PRENOTATA</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: '2px', borderBottom: `2px solid ${T.border}`, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {[['dettagli','📋 Dettagli'], ['riepilogo','📊 Riepilogo'], puoStampare && ['stampa','🖨️ Stampa'], ['documenti','📎 Documenti']].filter(Boolean).map(([id, label]) => (
              <button key={id} onClick={() => setActiveTab(id)} style={{ padding: '8px 16px', fontSize: '11px', fontWeight: '900', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', background: 'transparent', color: activeTab === id ? '#10b981' : T.textSub, borderBottom: activeTab === id ? '2px solid #10b981' : '2px solid transparent', marginBottom: '-2px', transition: 'color 0.15s', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* CONTENUTO SCROLLABILE */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 28px' }}>

          {/* TAB DETTAGLI */}
          {activeTab === 'dettagli' && (<>
            <Section title="CLIENTE" color="#6366f1">
              <Row label="Nome" value={nc} />
              {c.cliente?.codiceFiscale && <Row label="Codice Fiscale" value={c.cliente.codiceFiscale} />}
              {c.cliente?.piva && <Row label="P.IVA" value={c.cliente.piva} />}
              {c.cliente?.telefono && <Row label="Telefono" value={c.cliente.telefono} />}
              <div style={{ padding: '6px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: isB2B ? 'rgba(99,102,241,0.12)' : T.bg, border: `2px solid ${isB2B ? '#6366f1' : T.border}`, borderRadius: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={isB2B}
                    onChange={async e => {
                      const val = e.target.checked;
                      setIsB2B(val);
                      await set(ref(db, `contratti/${c.id}/cliente/b2b`), val || null);
                      await set(ref(db, `contratti/${c.id}/dataModifica`), new Date().toISOString());
                    }}
                    style={{ accentColor: '#6366f1', width: '15px', height: '15px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '900', color: isB2B ? '#6366f1' : T.text }}>🏢 Cliente B2B</div>
                    <div style={{ fontSize: '9px', color: T.textMuted }}>Rivenditore / Concessionaria / Operatore settore</div>
                  </div>
                </label>
              </div>
            </Section>
            <Section title="DOCUMENTO IDENTITÀ" color="#3b82f6">
              <Row label="Tipo / Numero" value={`${c.datiExtra?.tipoDocumento||'—'} n. ${c.datiExtra?.numeroDocumento||'—'}`} />
              <Row label="Rilasciato da" value={c.datiExtra?.rilasciatoDa || '—'} />
              <Row label="Data Rilascio / Scadenza" value={`${c.datiExtra?.dataRilascio ? new Date(c.datiExtra.dataRilascio).toLocaleDateString('it-IT') : '—'} / ${c.datiExtra?.dataScadenzaDoc ? new Date(c.datiExtra.dataScadenzaDoc).toLocaleDateString('it-IT') : '—'}`} />
            </Section>
            <Section title="CONTRATTO" color="#10b981">
              <Row label="Data Firma" value={c.datiExtra?.dataFirma ? new Date(c.datiExtra.dataFirma).toLocaleDateString('it-IT') : '—'} />
              <Row label="Luogo Firma" value={c.datiExtra?.luogoFirma || '—'} />
              <Row label="Creato da" value={c.creatorNome || '—'} />
              <Row label="Venditore" value={c.venditoreNome || '—'} />
              {c.veicoloSostituito && (
                <Row label="🔄 Veicolo sostituito" value={`da ${c.veicoloSostituito.da?.marca||''} ${c.veicoloSostituito.da?.modello||''} (${c.veicoloSostituito.da?.targa||'N.D.'}) — ${c.veicoloSostituito.utente}, ${new Date(c.veicoloSostituito.timestamp).toLocaleDateString('it-IT')}`} />
              )}
            </Section>
            <Section title="CONDIZIONI ECONOMICHE" color="#f59e0b">
              {/* Prezzo dell'auto venduta: listino − sconto + optional casa madre + IPT */}
              <Row label="Prezzo Listino" value={fmt(pL)} />
              <Row label="Sconto" value={sc > 0 ? `- ${fmt(sc)}` : fmt(0)} />
              {(c.servizi?.accessoriCasaMadreList || []).filter(a => parseFloat(a.prezzo) > 0 || a.nome).map((a, i) => (
                <Row key={a.id || i} label={`Optional C.M. · ${a.nome || 'senza nome'}`} value={`+ ${fmt(parseFloat(a.prezzo) || 0)}`} />
              ))}
              <Row label="Optional Casa Madre" value={`+ ${fmt(accessoriCMc)}`} />
              <Row label="IPT + MSS" value={`+ ${fmt(ipt)}`} />
              <Row label="PREZZO AUTO VENDUTA" value={fmt(pL - sc + accessoriCMc + ipt)} bold />
              {totServiziC > 0 && <Row label="Servizi e Accessori" value={`+ ${fmt(totServiziC)}`} />}
              {hasIVAc && <Row label="IVA 22%" value={`+ ${fmt(ivaImportoC)}`} />}
              {estinzioneCostoC > 0 && <Row label="Estinzione Anticipata" value={`+ ${fmt(estinzioneCostoC)}`} />}
              {speseLeasingCostoC > 0 && <Row label="Spese Gestione Leasing" value={`+ ${fmt(speseLeasingCostoC)}`} />}
              {perm > 0 && <Row label={pr.permutaRottamazione ? 'Rottamazione' : 'Permuta'} value={`- ${fmt(perm)}`} />}
              {fin > 0 && <Row label={pr.tipoFinanziamento || 'Finanziato'} value={`- ${fmt(fin)}`} />}
              {cap > 0 && <Row label="Caparra" value={`- ${fmt(cap)}`} />}
              <Row label="SALDO" value={fmt(saldo)} bold />
            </Section>

            {/* MARGINALITÀ — solo admin/super_admin */}
            {isAdminSuper && puoVedereMargini(user) && (
              <div style={{background: margine !== null ? (margine >= 0 ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)') : T.bg, border:`1px solid ${margine !== null ? (margine >= 0 ? '#10b981' : '#ef4444') : T.border}`, borderRadius:'14px', padding:'14px 16px', marginBottom:'16px'}}>
                <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'10px'}}>📊 MARGINALITÀ</div>
                {/* Campo bottomPrice editabile */}
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px',padding:'8px',background:T.bg,borderRadius:'10px'}}>
                  <span style={{fontSize:'11px',color:T.textMuted,flex:1}}>Prezzo acquisto (bottom price)</span>
                  <input type="number" defaultValue={bottomPrice||''} placeholder="0.00" onWheel={e=>e.preventDefault()}
                    onBlur={async e=>{
                      const nuovoVal=parseFloat(e.target.value)||0;
                      await set(ref(db,`contratti/${c.id}/veicolo/bottomPrice`),nuovoVal);
                      await set(ref(db,`contratti/${c.id}/bottomPrice`),nuovoVal);
                      // Sincronizza con conto_economico_acquisti (usato da Finance)
                      const vin = c.veicolo?.vin||'';
                      const targa = c.veicolo?.targa||'';
                      const veicoloId = c.veicolo?.id||'';
                      const key = (vin||targa||veicoloId).replace(/[^a-zA-Z0-9]/g,'_');
                      if(key) await set(ref(db,`conto_economico_acquisti/${key}`),nuovoVal);
                      // Aggiorna anche in stock se c'è il vin
                      if(vin){
                        const stockSnap=await get(ref(db,'stock_data/vehicles'));
                        if(stockSnap.exists()){const vs=stockSnap.val();const idx=vs.findIndex(v=>v.vin===vin);if(idx>-1)await set(ref(db,`stock_data/vehicles/${idx}/bottomPrice`),nuovoVal);}
                      }
                    }}
                    style={{width:'110px',padding:'6px 10px',background:T.inputBg,border:'1px solid #ef444450',borderRadius:'8px',color:'#ef4444',fontSize:'12px',fontWeight:'700',outline:'none',textAlign:'right'}}
                  />
                </div>
                {bottomPrice > 0 ? (<>
                  <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}>
                    <span style={{color:T.textMuted}}>Prezzo auto venduta (listino − sconto + optional C.M. + IPT)</span>
                    <span style={{color:T.text,fontWeight:'600'}}>{fmt(prezzoAutoVenduta)}</span>
                  </div>
                  {ipt > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}>
                      <span style={{color:T.textMuted}}>IPT / passaggio di proprietà (non è ricavo)</span>
                      <span style={{color:'#ef4444',fontWeight:'600'}}>- {fmt(ipt)}</span>
                    </div>
                  )}
                  {_ivaBadge > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}>
                      <span style={{color:T.textMuted}}>IVA 22% esposta</span>
                      <span style={{color:T.text,fontWeight:'600'}}>+ {fmt(_ivaBadge)}</span>
                    </div>
                  )}
                  <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}>
                    <span style={{color:T.textMuted}}>Prezzo acquisto (bottom)</span>
                    <span style={{color:'#ef4444',fontWeight:'600'}}>- {fmt(bottomPrice)}</span>
                  </div>
                  {totInterventi > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}>
                      <span style={{color:T.textMuted}}>Interventi officina ({interventiVeicolo.length})</span>
                      <span style={{color:'#ef4444',fontWeight:'600'}}>- {fmt(totInterventi)}</span>
                    </div>
                  )}
                  <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0 0',fontSize:'14px'}}>
                    <span style={{color:T.text,fontWeight:'900'}}>MARGINE</span>
                    <span style={{color: margine >= 0 ? '#10b981' : '#ef4444', fontWeight:'900', fontSize:'18px'}}>{fmt(margine)}</span>
                  </div>
                  {margine !== null && prezzoVendita > 0 && (
                    <div style={{fontSize:'10px',color:T.textMuted,marginTop:'4px',textAlign:'right'}}>
                      {((margine/prezzoVendita)*100).toFixed(1)}% sul prezzo di vendita
                    </div>
                  )}
                </>) : (
                  <div style={{fontSize:'11px',color:T.textMuted,fontStyle:'italic'}}>Inserisci il prezzo di acquisto sopra per calcolare il margine</div>
                )}
              </div>
            )}
            {/* Storico: approvazioni revocate da riaperture del consulente */}
            {c.storicoApprovazioni && Object.values(c.storicoApprovazioni).length > 0 && (
              <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px', marginBottom: '6px' }}>STORICO APPROVAZIONI</div>
                {Object.values(c.storicoApprovazioni).sort((a, b) => String(a.revocataIl).localeCompare(String(b.revocataIl))).map((a, i) => (
                  <div key={i} style={{ fontSize: '11px', color: T.textSub, marginBottom: '3px' }}>
                    Approvato da <b style={{ color: T.text }}>{a.approvatoDa}</b>{a.approvatoIl ? ` il ${new Date(a.approvatoIl).toLocaleString('it-IT')}` : ''} · riaperto da <b style={{ color: T.text }}>{a.revocataDa}</b> il {new Date(a.revocataIl).toLocaleString('it-IT')}
                  </div>
                ))}
              </div>
            )}
            {/* Consegna: prevista ed effettiva, visibile anche quando la pratica
                è uscita dal Back Office */}
            {['approvato', 'venduto'].includes(c.stato) && (() => {
              const ic = infoConsegna(c);
              if (!ic.consegnato && !ic.prevista) return null;
              const fd = d => { const x = new Date(d); return isNaN(x) ? String(d) : x.toLocaleDateString('it-IT'); };
              const fdt = d => { const x = new Date(d); return isNaN(x) ? String(d) : x.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
              return (
                <div style={{ padding: '12px 14px', background: ic.consegnato ? 'rgba(59,130,246,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${ic.consegnato ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.3)'}`, borderRadius: '12px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '900', color: ic.consegnato ? '#3b82f6' : '#f59e0b', letterSpacing: '1px', marginBottom: '4px' }}>{ic.consegnato ? '🚗 AUTO CONSEGNATA' : '📅 CONSEGNA PREVISTA'}</div>
                  {ic.consegnato && ic.data && <div style={{ fontSize: '12px', color: T.textSub }}>Consegnata il <b style={{ color: T.text }}>{fdt(ic.data)}</b>{ic.da ? <> da <b style={{ color: T.text }}>{ic.da}</b></> : null}</div>}
                  {ic.prevista && <div style={{ fontSize: '12px', color: T.textSub, marginTop: ic.consegnato ? '2px' : 0 }}>{ic.consegnato ? 'Era prevista per' : 'Prevista per'} il <b style={{ color: T.text }}>{fd(ic.prevista)}</b>{ic.oraPrevista ? ` alle ${ic.oraPrevista}` : ''}</div>}
                </div>
              );
            })()}
            {/* Chi ha approvato resta visibile anche dopo la vendita */}
            {['approvato', 'venduto'].includes(c.stato) && c.approvazioneNome && (
              <div style={{ padding: '12px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '12px', marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', marginBottom: '4px' }}>✅ CONTRATTO APPROVATO</div>
                <div style={{ fontSize: '11px', color: T.textSub }}>da <b style={{ color: T.text }}>{c.approvazioneNome}</b></div>
                <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '2px' }}>{c.approvazioneData ? new Date(c.approvazioneData).toLocaleString('it-IT') : ''}</div>
              </div>
            )}
            {c.stato === 'annullato' && (
              <div style={{ padding: '12px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '12px', marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', fontWeight: '900', color: '#ef4444', marginBottom: '4px' }}>❌ CONTRATTO ANNULLATO</div>
                <div style={{ fontSize: '11px', color: T.textSub }}>da <b style={{ color: T.text }}>{c.annullamentoNome}</b></div>
                <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '2px' }}>{c.annullamentoData ? new Date(c.annullamentoData).toLocaleString('it-IT') : ''}</div>
                {c.annullamentoMotivazione && (
                  <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px', fontStyle: 'italic' }}>
                    Motivazione: {c.annullamentoMotivazione}
                  </div>
                )}
              </div>
            )}
            {confermaApprova && (
              <div style={{ marginBottom: '14px', padding: '14px', background: 'rgba(16,185,129,0.08)', border: '1px solid #10b981', borderRadius: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#10b981', marginBottom: '8px' }}>Approvare questo contratto?</div>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '12px' }}>Verrai registrato come approvatore: <b>{user.nome} {user.cognome} ({user.ruolo})</b></div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setConfermaApprova(false)} style={{ flex: 1, padding: '9px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>Annulla</button>
                  <button onClick={onApprova} style={{ flex: 1, padding: '9px', background: '#10b981', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>✅ Sì, Approva</button>
                </div>
              </div>
            )}
          </>)}

          {/* TAB STAMPA */}
          {activeTab === 'stampa' && (
            <div style={{ padding: '4px 0' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '14px' }}>
                DOCUMENTI CONTRATTO — stampa singoli o tutti insieme
              </div>

              {/* Pulsanti singoli documenti */}
              {[
                { id: 'contratto',          label: 'Ordine di Acquisto',     icon: '📄', sempre: true },
                { id: 'lavorazione',        label: 'Scheda Lavorazione',     icon: '🔧', sempre: true },
                { id: 'tt2120',             label: (c.cliente?.piva || c.cliente?.ragioneSociale) ? 'TT2120 SOC' : 'TT2120 PRIV', icon: '📋', sempre: true },
                { id: 'autocertificazione', label: 'Istanza Autocertificazione', icon: '📝', sempre: true },
                { id: 'condizioni',         label: String(c.veicolo?.tipo||'').toLowerCase()==='nuovo' ? 'Condizioni Nuovo' : 'Condizioni Usato/KM0', icon: '📃', sempre: true },
                { id: 'lojack',             label: 'Modulo LoJack',          icon: '🛡️', sempre: false, attivo: !!c.servizi?.lojack },
                { id: 'permuta',            label: 'Lista Documenti Permuta', icon: '🔄', sempre: false, attivo: !!c.prezzo?.hasPermuta },
              ].map(({ id, label, icon, sempre, attivo }) => {
                const disponibile = sempre || attivo;
                return (
                  <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, marginBottom: '8px', opacity: disponibile ? 1 : 0.4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '20px' }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{label}</div>
                        {!disponibile && <div style={{ fontSize: '10px', color: T.textMuted }}>Non presente in questo contratto</div>}
                      </div>
                    </div>
                    <button
                      disabled={!disponibile || stamping}
                      onClick={async () => {
                        setStamping(true);
                        try {
                          if (id === 'permuta') {
                            const { generaDocPermuta } = await import('./ContrattoModal.jsx');
                            await generaDocPermuta(c, c.datiExtra || {});
                          } else {
                            await generaPacchettoPDF(c, c.datiExtra || {}, user, id);
                          }
                        } catch(e) { alert('Errore: ' + e.message); }
                        finally { setStamping(false); }
                      }}
                      style={{ padding: '7px 14px', background: disponibile ? 'rgba(16,185,129,0.1)' : T.bg, border: `1px solid ${disponibile ? '#10b981' : T.border}`, borderRadius: '8px', color: disponibile ? '#10b981' : T.textMuted, cursor: disponibile ? 'pointer' : 'default', fontWeight: '700', fontSize: '10px', flexShrink: 0 }}>
                      {stamping ? '⏳' : '🖨️ Stampa'}
                    </button>
                  </div>
                );
              })}

              {/* Pulsante stampa tutto */}
              <button onClick={handleStampaContratto} disabled={stamping}
                style={{ width: '100%', padding: '13px', background: stamping ? T.border : 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '14px', color: '#fff', cursor: stamping ? 'default' : 'pointer', fontWeight: '900', fontSize: '13px', marginTop: '8px' }}>
                {stamping ? '⏳ Generazione PDF...' : '📦 Scarica Tutti i Documenti'}
              </button>

              {/* Pulsante privacy finanziaria — appare solo se c'è finanziamento con finanziaria riconosciuta */}
              {(() => {
                const nomeFin = c.prezzo?.finanziariaSocieta || c.prezzo?.bancaFinanziamento || '';
                const privacy = c.prezzo?.hasFinanziamento && nomeFin ? getPrivacyFinanziaria(nomeFin) : null;
                if (!privacy) return null;
                return (
                  <a href={privacy.file} target="_blank" rel="noreferrer"
                    style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none', borderRadius: '14px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '13px', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none', boxSizing: 'border-box' }}>
                    🔏 Privacy {privacy.label}
                  </a>
                );
              })()}
            </div>
          )}

          {/* TAB DOCUMENTI */}
          {activeTab === 'documenti' && (
            <DocumentiSection db={db} user={user} T={T}
              percorsoFirebase={`documenti_contratti/${c.id}`}
              cartellaR2={`contratti/${c.id}`} />
          )}

          {/* TAB RIEPILOGO */}
          {activeTab === 'riepilogo' && (
            <div>
              {/* ANAGRAFICA CLIENTE */}
              {(() => {
                const cl = c.cliente || {};
                const de = c.datiExtra || {};
                const righe = [
                  ['Nome / Ragione Sociale', cl.ragioneSociale || `${cl.nome||''} ${cl.cognome||''}`.trim()],
                  ['Codice Fiscale', cl.codiceFiscale],
                  ['Partita IVA', cl.piva],
                  ['Indirizzo', cl.indirizzo ? `${cl.indirizzo}${cl.citta ? ', ' + cl.citta : ''}${cl.cap ? ' ' + cl.cap : ''}${cl.provincia ? ' (' + cl.provincia + ')' : ''}` : null],
                  ['Telefono', cl.telefono || cl.cellulare],
                  ['Email', cl.email],
                  ['PEC', cl.pec],
                  ['Documento', de.tipoDocumento && de.numeroDocumento ? `${de.tipoDocumento} n. ${de.numeroDocumento}` : null],
                  ['Rilasciato da', de.rilasciatoDa],
                  ['Data rilascio / Scadenza', de.dataRilascio || de.dataScadenzaDoc ? `${de.dataRilascio ? new Date(de.dataRilascio).toLocaleDateString('it-IT') : '—'} / ${de.dataScadenzaDoc ? new Date(de.dataScadenzaDoc).toLocaleDateString('it-IT') : '—'}` : null],
                  ['Provenienza', cl.provenienza],
                ].filter(([,v]) => v && String(v).trim());
                if (!righe.length) return null;
                return (
                  <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '10px' }}>CLIENTE</div>
                    {righe.map(([l,v2]) => <Row key={l} label={l} value={v2} />)}
                    {cl.b2b && <div style={{ marginTop: '6px', display: 'inline-block', fontSize: '9px', fontWeight: '900', color: '#6366f1', background: 'rgba(99,102,241,0.15)', padding: '2px 8px', borderRadius: '6px' }}>🏢 B2B</div>}
                  </div>
                );
              })()}
              {/* CONTRATTO */}
              {(() => {
                const de = c.datiExtra || {};
                const righe = [
                  ['N. Contratto', de.numeroContratto],
                  ['Data Firma', de.dataFirma ? new Date(de.dataFirma).toLocaleDateString('it-IT') : null],
                  ['Luogo Firma', de.luogoFirma],
                  ['Venditore', c.venditoreNome],
                  ['Creato da', c.creatorNome],
                  ['Consegna prevista', de.consegnaTipo && de.consegnaTipo !== 'personalizzata' ? `Entro ${de.consegnaTipo} giorni` : de.dataConsegna ? new Date(de.dataConsegna).toLocaleDateString('it-IT') : null],
                ].filter(([,v]) => v && String(v).trim());
                if (!righe.length) return null;
                return (
                  <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '10px' }}>CONTRATTO</div>
                    {righe.map(([l,v2]) => <Row key={l} label={l} value={v2} />)}
                  </div>
                );
              })()}
              {/* VEICOLO */}
              {(() => {
                const v2 = c.veicolo || {};
                const de = c.datiExtra || {};
                // Lookup live nello stock: v2.colore è uno snapshot congelato
                // alla creazione del contratto, può restare sbagliato per
                // sempre se lo stock viene corretto dopo. Stessa fonte dati
                // della tab Stock, priorità massima quando disponibile.
                const stockLive = (window._radarStockCache?.vehicles || []).find(sv => sv.id === v2.id || sv.targa === v2.targa || sv.vin === (de.vin || v2.vin));
                const righe = [
                  ['Marca / Modello', `${v2.marca||''} ${v2.modello||''}`.trim()],
                  ['Versione', v2.versione],
                  ['Tipo', v2.tipo],
                  ['Targa', v2.targa],
                  ['Telaio (VIN)', de.vin || v2.vin],
                  ['Immatricolazione', de.immatricolazione || v2.registrazione],
                  ['Km', v2.km ? new Intl.NumberFormat('it-IT').format(v2.km) + ' km' : null],
                  ['Carburante', v2.carburante],
                  ['Colore', stockLive?.colore || de.coloreVeicolo || v2.colore],
                  ['Cambio', v2.gearType],
                ].filter(([,v]) => v && String(v).trim());
                return (
                  <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', letterSpacing: '1px', marginBottom: '10px' }}>VEICOLO</div>
                    {righe.map(([l,v]) => <Row key={l} label={l} value={v} />)}
                  </div>
                );
              })()}
              {/* CONDIZIONI ECONOMICHE */}
              {(() => {
                const sv = c.servizi || {};
                const pr = c.prezzo || {};
                // Mappa nome servizio → campo costo
                const serviziMap = [
                  [sv.lojack,             'LoJack',                    sv.costoLojack],
                  [sv.mawdy36||sv.mawdy,  `Ext. Garanzia Mawdy ${sv.garanziaEstesaMesi || 36}m`,   sv.costoMawdy36||sv.costoMawdy],
                  [sv.blockshaft,         'Blockshaft',                sv.costoBlockshaft],
                  [sv.gpl,                'GPL',                       sv.costoGpl],
                  [sv.tagliando,          'Tagliando',                 sv.costoTagliando],
                  [sv.packManutenzione,   `Pack manutenzione${sv.packManutenzioneTagliandi ? ` (${sv.packManutenzioneTagliandi} tagliandi)` : ''}`, sv.costoPackManutenzione],
                  [sv.revisione,          'Revisione',                 sv.costoRevisione],
                  [sv.secondaChiave,      '2a Chiave',                 sv.costoSecondaChiave],
                  [sv.sensoriParcheggio,  'Sensori Parcheggio',        sv.costoSensoriParcheggio],
                  [sv.retrocamera,        'Retrocamera',               sv.costoRetrocamera],
                  [sv.bracciolo,          'Bracciolo',                 sv.costoBracciolo],
                  [sv.vetriPrivacy,       'Vetri Privacy',             sv.costoVetriPrivacy],
                  [sv.kitAutocarro,       'Kit Autocarro',             sv.costoKitAutocarro],
                  [sv.assPP,              `PP (Protezione Pagamenti)${sv.durAssPP ? ` (${sv.durAssPP} mesi)` : ''}`, sv.costoAssPP],
                  [sv.assFIR,             `FIR${sv.durAssFIR ? ` (${sv.durAssFIR} mesi)` : ''}`,                    sv.costoAssFIR],
                  [sv.assGAP,             `GAP${sv.durAssGAP ? ` (${sv.durAssGAP} mesi)` : ''}`,                    sv.costoAssGAP],
                  [sv.altroAntifurto,     sv.altroAntifurto,           sv.costoAltroAntifurto     || sv.altroAntifurtoCosto     || sv.costoAntifurto],
                  [sv.altroAccessorio,    sv.altroAccessorio,          sv.costoAltroAccessorio    || sv.altroAccessorioCosto    || sv.costoAccessorio],
                  [sv.altroInstallazione, sv.altroInstallazione,       sv.costoAltroInstallazione || sv.altroInstallazioneCosto || sv.costoInstallazione],
                ].filter(([flag]) => flag);

                // Aggiunge eventuali campi costo non mappati (vecchi contratti con struttura diversa)
                const costiMappati = new Set(['costoLojack','costoMawdy','costoMawdy36','costoBlockshaft','costoGpl','costoTagliando','costoPackManutenzione','costoRevisione','costoSecondaChiave','costoSensoriParcheggio','costoRetrocamera','costoBracciolo','costoVetriPrivacy','costoKitAutocarro','costoEstinzioneAnticipata','costoAssPP','costoAssFIR','costoAssGAP','costoAltroAntifurto','costoAltroAccessorio','costoAltroInstallazione']);
                Object.entries(sv).forEach(([k, v]) => {
                  if (k.startsWith('costo') && !costiMappati.has(k) && parseFloat(v) > 0) {
                    const nomeCampo = k.replace('costo','').replace(/([A-Z])/g,' $1').trim();
                    serviziMap.push([true, nomeCampo, v]);
                  }
                });
                const totS = serviziMap.reduce((t,[,,costo]) => t + (parseFloat(costo)||0), 0);
                const pL = parseFloat(pr.prezzoListino || c.veicolo?.prezzo || 0);
                const sc = parseFloat(pr.sconto || 0);
                const ipt = parseFloat(pr.ipt || 0);
                const perm = valorePermuteTot(pr);
                const fin = parseFloat(pr.importoFinanziato || 0);
                const cap = parseFloat(pr.caparra || 0);
                const hasIVAd = !!pr.hasIVA;
                const ivaD = hasIVAd ? Math.round((pL - sc) * 0.22) : 0;
                const estD = (sv.estinzioneAnticipata || pr.estinzioneAnticipata) ? parseFloat(sv.costoEstinzioneAnticipata || pr.costoEstinzioneAnticipata || 0) : 0;
                const saldoCalc = (pL - sc + ipt) + totS + ivaD - perm - fin - cap + estD;
                return (
                  <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '10px' }}>CONDIZIONI ECONOMICHE</div>
                    <Row label="Prezzo Listino" value={fmt(pL)} />
                    {sc > 0 && <Row label="Sconto" value={'- ' + fmt(sc)} />}
                    {ipt > 0 && <Row label="IPT + MSS" value={'+ ' + fmt(ipt)} />}
                    {/* Dettaglio servizi riga per riga */}
                    {serviziMap.map(([,nome,costo]) => (
                      <Row key={nome} label={`  ↳ ${nome}`} value={parseFloat(costo) > 0 ? '+ ' + fmt(parseFloat(costo)) : 'incluso'} />
                    ))}
                    {totS > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderTop: `1px dashed ${T.border}`, marginTop: '2px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: T.textSub }}>Totale servizi</span>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b' }}>+ {fmt(totS)}</span>
                      </div>
                    )}
                    {hasIVAd && <Row label="IVA 22%" value={'+ ' + fmt(ivaD)} />}
                    {estD > 0 && <Row label="Estinzione Anticipata Finanziamento" value={'+ ' + fmt(estD)} />}
                    {perm > 0 && <Row label={pr.permutaRottamazione ? 'Rottamazione' : 'Permuta'} value={'- ' + fmt(perm)} />}
                    {fin > 0  && <Row label={pr.tipoFinanziamento || 'Finanziato'} value={'- ' + fmt(fin)} />}
                    {cap > 0  && <Row label="Caparra" value={'- ' + fmt(cap)} />}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', marginTop: '8px', borderTop: `2px solid ${T.border}` }}>
                      <span style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>SALDO</span>
                      <span style={{ fontSize: '17px', fontWeight: '900', color: '#10b981' }}>{fmt(saldoCalc)}</span>
                    </div>
                  </div>
                );
              })()}
              {/* PAGAMENTO B2B — solo per clienti aziendali, editabile da
                  admin/super_admin/back_office/finance. Serve a distinguere,
                  per lo stato "a semaforo" in elenco Contratti, quando un
                  contratto B2B è stato effettivamente incassato (non basta
                  che l'auto sia stata consegnata). */}
              {isB2B && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '10px' }}>PAGAMENTO B2B</div>
                  {['admin', 'super_admin', 'back_office', 'finance'].includes(user?.ruolo) ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!c.backoffice?.pagatoB2B} onChange={async e => {
                        const checked = e.target.checked;
                        await update(ref(db, `contratti/${c.id}/backoffice`), {
                          pagatoB2B: checked,
                          dataPagamentoB2B: checked ? new Date().toISOString() : null,
                        });
                        await set(ref(db, `contratti/${c.id}/dataModifica`), new Date().toISOString());
                        setC(prev => ({ ...prev, backoffice: { ...prev.backoffice, pagatoB2B: checked, dataPagamentoB2B: checked ? new Date().toISOString() : null } }));
                      }} style={{ accentColor: '#10b981', width: '16px', height: '16px' }} />
                      <span style={{ fontSize: '12px', fontWeight: '800', color: c.backoffice?.pagatoB2B ? '#10b981' : T.textSub }}>
                        {c.backoffice?.pagatoB2B ? '✅ Pagato' : 'Non ancora incassato'}
                      </span>
                      {c.backoffice?.pagatoB2B && c.backoffice?.dataPagamentoB2B && (
                        <span style={{ fontSize: '10px', color: T.textMuted }}>il {new Date(c.backoffice.dataPagamentoB2B).toLocaleDateString('it-IT')}</span>
                      )}
                    </label>
                  ) : (
                    <div style={{ fontSize: '12px', fontWeight: '800', color: c.backoffice?.pagatoB2B ? '#10b981' : T.textMuted }}>
                      {c.backoffice?.pagatoB2B ? `✅ Pagato il ${new Date(c.backoffice.dataPagamentoB2B).toLocaleDateString('it-IT')}` : 'Non ancora incassato'}
                    </div>
                  )}
                </div>
              )}
              {/* PERMUTA */}
              {c.prezzo?.hasPermuta && valorePermuteTot(c.prezzo) > 0 && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px' }}>PERMUTA</div>
                    {['admin', 'super_admin'].includes(user?.ruolo) && (
                      c.prezzo?.permutaInStockId ? (
                        <span style={{ fontSize: '9px', fontWeight: '900', color: '#10b981', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '20px', padding: '3px 10px' }}>
                          ✓ Già in Stock
                        </span>
                      ) : c.prezzo?.permutaRottamazione ? (
                        <span title="Marcato per rottamazione — non entra nel circuito di permuta/rivendita" style={{ fontSize: '9px', fontWeight: '900', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '20px', padding: '3px 10px' }}>
                          🗑️ Rottamazione
                        </span>
                      ) : (
                        <button onClick={() => setPermutaStockModal(c)}
                          style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '10px', whiteSpace: 'nowrap' }}>
                          🚗 Fai Entrare in Stock
                        </button>
                      )
                    )}
                  </div>
                  {[
                    ['Marca / Modello', `${c.prezzo.permutaMarca||''} ${c.prezzo.permutaModello||''}`.trim()],
                    ['Targa', c.prezzo.permutaTarga],
                    ['Km', c.prezzo.permutaKm ? new Intl.NumberFormat('it-IT').format(c.prezzo.permutaKm) + ' km' : null],
                    ['Valore', fmt(parseFloat(c.prezzo.permutaValore||0))],
                    ...((c.prezzo.permuteAggiuntive || []).filter(x => parseFloat(x?.valore) > 0).map((x, i) => [`Permuta ${i + 2}`, `${x.marca || ''} ${x.modello || ''} ${x.targa ? '· ' + x.targa : ''} — ${fmt(parseFloat(x.valore))}`])),
                  ].filter(([,v]) => v && String(v).trim()).map(([l,v]) => <Row key={l} label={l} value={v} />)}
                </div>
              )}
              {/* FINANZIAMENTO */}
              {c.prezzo?.hasFinanziamento && parseFloat(c.prezzo?.importoFinanziato||0) > 0 && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', marginBottom: '12px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '10px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '1px', marginBottom: '10px' }}>FINANZIAMENTO</div>
                  {[
                    ['Banca / Finanziaria', c.prezzo.finanziariaSocieta || c.prezzo.bancaFinanziamento],
                    ['Importo', fmt(parseFloat(c.prezzo.importoFinanziato||0))],
                    ['Durata', c.prezzo.durataFinanziamento ? c.prezzo.durataFinanziamento + ' mesi' : null],
                    ['Rata', c.prezzo.rata ? fmt(c.prezzo.rata) + '/mese' : null],
                    ['Tasso', c.prezzo.tassoFinanziamento || c.prezzo.tabellaFinanziamento],
                  ].filter(([,v]) => v && String(v).trim()).map(([l,v]) => <Row key={l} label={l} value={v} />)}
                </div>
              )}
              {/* NOTE CONSULENTE */}
              {(c.datiExtra?.note || c.noteConsulente) && (
                <div style={{ background: T.bg, borderRadius: '14px', padding: '16px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '10px', fontWeight: '900', color: T.textSub, letterSpacing: '1px', marginBottom: '8px' }}>NOTE</div>
                  <div style={{ fontSize: '12px', color: T.text, lineHeight: 1.6 }}>{c.datiExtra?.note || c.noteConsulente}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER — solo dettagli */}
        {activeTab === 'dettagli' && (
          <div style={{ padding: '16px 28px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
            {/* Conferma eliminazione */}
            {confermaEliminaContratto && (
              <div style={{ marginBottom: '12px', padding: '14px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#ef4444', marginBottom: '8px' }}>⚠️ Eliminare questo contratto?</div>
                <div style={{ fontSize: '11px', color: T.textSub, marginBottom: '12px' }}>L'operazione è irreversibile.</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setConfermaEliminaContratto(false)} style={{ flex: 1, padding: '9px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>Annulla</button>
                  <button onClick={handleEliminaContratto} style={{ flex: 1, padding: '9px', background: '#ef4444', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>Sì, elimina</button>
                </div>
              </div>
            )}
            {/* Conferma annullamento con motivazione */}
            {confermaAnnulla && (
              <div style={{ marginBottom: '12px', padding: '14px', background: 'rgba(245,158,11,0.08)', border: '1px solid #f59e0b', borderRadius: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>⚠️ Annullare questo contratto approvato?</div>
                <div style={{ fontSize: '11px', color: T.textSub, marginBottom: '10px' }}>Il veicolo tornerà disponibile. Inserisci la motivazione:</div>
                <textarea
                  value={motivazioneAnnullo}
                  onChange={e => setMotivazioneAnnullo(e.target.value)}
                  placeholder="Es. Finanziamento non approvato, cliente rinuncia, permuta rifiutata..."
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', background: T.inputBg, border: `1px solid ${motivazioneAnnullo.trim() ? '#f59e0b' : T.border2}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => { setConfermaAnnulla(false); setMotivazioneAnnullo(''); }} style={{ flex: 1, padding: '9px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>Indietro</button>
                  <button onClick={handleAnnulla} disabled={!motivazioneAnnullo.trim()} style={{ flex: 1, padding: '9px', background: motivazioneAnnullo.trim() ? '#f59e0b' : T.border, border: 'none', borderRadius: '10px', color: '#fff', cursor: motivazioneAnnullo.trim() ? 'pointer' : 'default', fontWeight: '900', fontSize: '11px' }}>Conferma Annullamento</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '12px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>Chiudi</button>
              {puoModificareContratto && !confermaAnnulla && !confermaEliminaContratto && !confermaApprova && (
                <button onClick={() => {
                  if (riaperturaDaConsulente && !window.confirm('Il contratto è già APPROVATO.\n\nSe lo modifichi e salvi tornerà in BOZZA e dovrà essere riapprovato da un admin. Lo storico dell\'approvazione e tutti i log restano salvati.\n\nProcedere?')) return;
                  onModifica && onModifica(c);
                }} style={{ padding: '11px 16px', background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b40', borderRadius: '12px', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  ✏️ Modifica
                </button>
              )}
              {puoEliminare && !confermaEliminaContratto && !confermaApprova && !confermaAnnulla && (
                <button onClick={() => setConfermaEliminaContratto(true)} style={{ padding: '11px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef444440', borderRadius: '12px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  🗑️ Elimina
                </button>
              )}
              {puoAnnullare && !confermaAnnulla && !confermaEliminaContratto && !confermaApprova && (
                <button onClick={() => setConfermaAnnulla(true)} style={{ padding: '11px 16px', background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b40', borderRadius: '12px', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  ↩️ Annulla Contratto
                </button>
              )}
              {puoConfermaVendita && !confermaAnnulla && !confermaEliminaContratto && !confermaApprova && (
                <button onClick={handleConfermaVendita} style={{ padding: '11px 16px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  🚗 Conferma Vendita
                </button>
              )}
              {puoApprovare && !confermaApprova && !confermaEliminaContratto && !confermaAnnulla && (
                <button onClick={() => setConfermaApprova(true)} style={{ flex: 2, padding: '11px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
                  ✅ Approva Contratto
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {permutaStockModal && (
        <PermutaToStockModal contratto={permutaStockModal} user={user} onClose={() => setPermutaStockModal(null)}
          onSalvato={(veicoloId) => { setC(prev => ({ ...prev, prezzo: { ...prev.prezzo, permutaInStockId: veicoloId } })); setPermutaStockModal(null); }} />
      )}
    </div>
  );
}

// ─── Fai entrare la permuta in Stock — admin/super_admin, con validazione ──
// ─── AGGIUNGI AUTO MANUALMENTE IN STOCK ─────────────────────────────────────
// Stesso schema dei veicoli del feed, con fonte 'manuale': sync-stock.js non
// la segna mai venduta solo perché non è nel feed MotorK, e la sede resta
// quella scelta qui (sedeLocked).
// Campo etichettato del modulo "Aggiungi auto" (componente esterno: se fosse
// definito dentro il modulo, a ogni tasto i campi perderebbero il focus)
function CampoAggiungiAuto({ T, t, req, children, full }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: full ? '1 / -1' : undefined }}>
      <span style={{ fontSize: '10px', fontWeight: '800', color: T.textMuted, letterSpacing: '0.5px' }}>{t}{req && <span style={{ color: '#ef4444' }}> *</span>}</span>
      {children}
    </label>
  );
}

function AggiungiAutoStockModal({ user, sediDisponibili = [], onClose, onSalvato }) {
  const theme = useTheme(); const T = THEMES[theme];
  const oggi = new Date().toISOString().slice(0, 10);
  const [f, setF] = React.useState({
    tipo: 'Usato', marca: '', modello: '', versione: '', targa: '', vin: '', registrazione: '', km: '',
    carburante: '', gearType: '', colore: '', carrozzeria: '', prezzo: '', prezzoPartner: '', bottomPrice: '',
    sede: sediDisponibili[0] || '', ivaEsposta: false, dataIngresso: oggi, foto: '', note: '',
  });
  const [errori, setErrori] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const up = (k, v) => setF(p => ({ ...p, [k]: v }));
  const norm = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  const salva = async () => {
    const mancanti = [];
    if (!f.marca.trim()) mancanti.push('Marca');
    if (!f.modello.trim()) mancanti.push('Modello');
    if (!f.vin.trim() && !f.targa.trim()) mancanti.push('Telaio (VIN) o Targa');
    if (!(parseFloat(f.prezzo) > 0)) mancanti.push('Prezzo di vendita');
    if (!f.sede) mancanti.push('Sede');
    if (f.tipo !== 'Nuovo' && f.km === '') mancanti.push('Km');
    if (mancanti.length) { setErrori(mancanti); return; }
    setErrori([]); setSaving(true);
    try {
      const snap = await get(ref(db, 'stock_data/vehicles'));
      const raw = snap.exists() ? snap.val() : [];
      const arr = (Array.isArray(raw) ? raw : Object.values(raw)).filter(Boolean);
      // Doppione? stesso telaio o stessa targa già in stock
      const V = norm(f.vin), Tg = norm(f.targa);
      const doppio = arr.find(v => (V && norm(v.vin) === V) || (Tg && norm(v.targa) === Tg));
      if (doppio) {
        setSaving(false);
        setErrori([`Esiste già in stock: ${doppio.marca || ''} ${doppio.modello || ''} (${doppio.targa || doppio.vin || doppio.id}, stato ${doppio.status || 'N.D.'})`]);
        return;
      }
      const ingresso = f.dataIngresso ? new Date(`${f.dataIngresso}T09:00:00`) : new Date();
      const giorni = Math.max(0, Math.round((Date.now() - ingresso.getTime()) / 864e5));
      const foto = f.foto.split(/\s+/).map(x => x.trim()).filter(x => /^https?:\/\//.test(x));
      const nuovo = {
        id: `MAN-${Date.now()}`,
        marca: f.marca.trim(), modello: f.modello.trim(), versione: f.versione.trim(),
        tipo: f.tipo, carrozzeria: f.carrozzeria.trim(), categoria: '',
        status: 'FREE',
        carburante: f.carburante, gearType: f.gearType, colore: f.colore.trim(),
        km: parseInt(f.km) || 0, registrazione: f.registrazione,
        prezzo: parseFloat(f.prezzo) || 0,
        prezzoPartner: parseFloat(f.prezzoPartner) || 0,
        bottomPrice: parseFloat(f.bottomPrice) || 0,
        ivaEsposta: !!f.ivaEsposta,
        targa: f.targa.trim().toUpperCase(), vin: f.vin.trim().toUpperCase(),
        sede: f.sede, sedeLocked: true,
        enteredInStock: ingresso.toISOString(), giorni, alert: giorni >= 150,
        mainImg: foto[0] || '', images: foto,
        fonte: 'manuale',
        inseritoDa: `${user?.nome || ''} ${user?.cognome || ''}`.trim(), inseritoAt: new Date().toISOString(),
        ...(f.note.trim() ? { noteInserimento: f.note.trim() } : {}),
      };
      await set(ref(db, 'stock_data/vehicles'), [...arr, nuovo]);
      window._radarStockCache = null; window._radarStockTs = 0;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) { /* ok */ }
      alert(`✅ ${nuovo.marca} ${nuovo.modello} aggiunta allo stock.`);
      onSalvato && onSalvato(nuovo);
    } catch (e) {
      setErrori([`Errore nel salvataggio: ${e.message}`]);
    }
    setSaving(false);
  };

  const iS = { width: '100%', padding: '9px 11px', background: T.inputBg || T.bg, border: `1px solid ${T.border2 || T.border}`, borderRadius: '9px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface || T.card, borderRadius: '16px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '760px', maxHeight: '92vh', overflowY: 'auto', padding: '22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>➕ Aggiungi auto in stock</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T.textMuted, fontSize: '18px', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <CampoAggiungiAuto T={T} t="Tipologia" req>
            <select value={f.tipo} onChange={e => up('tipo', e.target.value)} style={iS}>
              {['Nuovo', 'Km0', 'Usato'].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Marca" req><input value={f.marca} onChange={e => up('marca', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Modello" req><input value={f.modello} onChange={e => up('modello', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Versione / allestimento"><input value={f.versione} onChange={e => up('versione', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Telaio (VIN)" req><input value={f.vin} onChange={e => up('vin', e.target.value)} style={iS} placeholder="o la targa" /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Targa"><input value={f.targa} onChange={e => up('targa', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Immatricolazione"><input value={f.registrazione} onChange={e => up('registrazione', e.target.value)} style={iS} placeholder="mm/aaaa" /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Km" req={f.tipo !== 'Nuovo'}><input type="number" value={f.km} onChange={e => up('km', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Carburante">
            <select value={f.carburante} onChange={e => up('carburante', e.target.value)} style={iS}>
              <option value="">—</option>
              {['Benzina', 'Diesel', 'Ibrida', 'Ibrida plug-in', 'Elettrica', 'GPL', 'Metano'].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Cambio">
            <select value={f.gearType} onChange={e => up('gearType', e.target.value)} style={iS}>
              <option value="">—</option>
              {['Manuale', 'Automatico'].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Colore"><input value={f.colore} onChange={e => up('colore', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Carrozzeria"><input value={f.carrozzeria} onChange={e => up('carrozzeria', e.target.value)} style={iS} placeholder="es. SUV, Berlina" /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Prezzo di vendita €" req><input type="number" value={f.prezzo} onChange={e => up('prezzo', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Prezzo partner €"><input type="number" value={f.prezzoPartner} onChange={e => up('prezzoPartner', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Prezzo d'acquisto (bottom price) €"><input type="number" value={f.bottomPrice} onChange={e => up('bottomPrice', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Sede" req>
            <select value={f.sede} onChange={e => up('sede', e.target.value)} style={iS}>
              <option value="">Seleziona…</option>
              {sediDisponibili.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Data ingresso in stock"><input type="date" value={f.dataIngresso} onChange={e => up('dataIngresso', e.target.value)} style={iS} /></CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Regime IVA">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 0', color: T.text, fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={f.ivaEsposta} onChange={e => up('ivaEsposta', e.target.checked)} /> IVA esposta
            </label>
          </CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Foto (link, uno per riga — facoltativo)" full>
            <textarea value={f.foto} onChange={e => up('foto', e.target.value)} rows={2} style={{ ...iS, resize: 'vertical' }} placeholder="https://…" />
          </CampoAggiungiAuto>
          <CampoAggiungiAuto T={T} t="Note" full>
            <textarea value={f.note} onChange={e => up('note', e.target.value)} rows={2} style={{ ...iS, resize: 'vertical' }} />
          </CampoAggiungiAuto>
        </div>
        {errori.length > 0 && (
          <div style={{ marginTop: '14px', padding: '10px 12px', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', fontSize: '12px', fontWeight: '700' }}>
            {errori.length === 1 && errori[0].startsWith('Esiste') || errori[0]?.startsWith('Errore') ? errori[0] : `Campi mancanti: ${errori.join(', ')}`}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', borderRadius: '10px', border: `1px solid ${T.border}`, background: 'transparent', color: T.text, cursor: 'pointer', fontWeight: '700' }}>Annulla</button>
          <button onClick={salva} disabled={saving} style={{ padding: '10px 22px', borderRadius: '10px', border: 'none', background: saving ? '#64748b' : '#10b981', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: '900' }}>
            {saving ? 'Salvataggio…' : 'Aggiungi allo stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PermutaToStockModal({ contratto, user, onClose, onSalvato }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const pr = contratto.prezzo || {};
  const [marca, setMarca] = useState(pr.permutaMarca || '');
  const [modello, setModello] = useState(pr.permutaModello || '');
  const [targa, setTarga] = useState(pr.permutaTarga || '');
  const [vin, setVin] = useState(pr.permutaVin || '');
  const [km, setKm] = useState(pr.permutaKm || '');
  const [anno, setAnno] = useState(pr.permutaAnno || '');
  const [bottomPrice, setBottomPrice] = useState(pr.permutaValore || '');
  const [prezzoListino, setPrezzoListino] = useState('');
  const [sede, setSede] = useState('');
  const [errori, setErrori] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleSalva = async () => {
    // Validazione campi fondamentali — se manca qualcosa, il sistema lo
    // dice esplicitamente invece di far entrare un veicolo incompleto.
    const mancanti = [];
    if (!marca.trim()) mancanti.push('Marca');
    if (!modello.trim()) mancanti.push('Modello');
    if (!km || parseFloat(km) <= 0) mancanti.push('Km');
    if (!vin.trim()) mancanti.push('Telaio (VIN)');
    if (!targa.trim()) mancanti.push('Targa');
    if (!bottomPrice || parseFloat(bottomPrice) <= 0) mancanti.push('Prezzo di ritiro permuta (bottom price)');
    if (!prezzoListino || parseFloat(prezzoListino) <= 0) mancanti.push('Prezzo di listino');
    if (!sede) mancanti.push('Sede');

    if (mancanti.length > 0) {
      setErrori(mancanti);
      return;
    }
    setErrori([]);
    setSaving(true);
    try {
      const id = `PERMUTA-${Date.now()}`;
      const nuovoVeicolo = {
        id,
        marca: marca.trim(), modello: modello.trim(), versione: '',
        targa: targa.trim().toUpperCase(), vin: vin.trim().toUpperCase(),
        km: parseFloat(km), registrazione: anno ? `01/${anno}` : '',
        tipo: 'Usato', status: 'FREE',
        prezzo: parseFloat(prezzoListino), bottomPrice: parseFloat(bottomPrice),
        sede, sedeLocked: true, // entra già "bloccata": tracciata da RadaR, non da StockSpark
        fonte: 'Permuta',
        enteredInStock: new Date().toISOString(),
        giorni: 0, // appena entrato in stock — mancava, causava "undefined gg" nel dettaglio
        permutaDaContrattoId: contratto.id,
      };

      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      const raw = stockSnap.exists() ? stockSnap.val() : [];
      const arr = Array.isArray(raw) ? raw : Object.values(raw);
      await set(ref(db, 'stock_data/vehicles'), [...arr, nuovoVeicolo]);

      // Segna sul contratto che la permuta è già entrata in stock, per non
      // permettere di ripetere l'operazione creando un doppione.
      await set(ref(db, `contratti/${contratto.id}/prezzo/permutaInStockId`), id);
      await set(ref(db, `contratti/${contratto.id}/dataModifica`), new Date().toISOString());

      window._radarStockCache = null; window._radarStockTs = 0;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}

      alert('✅ Permuta inserita in Stock.');
      onSalvato(id);
    } catch (e) {
      alert('Errore: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '520px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>🚗 Fai Entrare la Permuta in Stock</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          {errori.length > 0 && (
            <div style={{ fontSize: '11px', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', lineHeight: 1.6 }}>
              ⚠️ Mancano informazioni obbligatorie: <b>{errori.join(', ')}</b>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>MARCA *</label>
              <input type="text" value={marca} onChange={e => setMarca(e.target.value)} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>MODELLO *</label>
              <input type="text" value={modello} onChange={e => setModello(e.target.value)} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>TARGA *</label>
              <input type="text" value={targa} onChange={e => setTarga(e.target.value.toUpperCase())} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase' }} />
            </div>
            <div style={{ flex: 1.5 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>TELAIO (VIN) *</label>
              <input type="text" value={vin} onChange={e => setVin(e.target.value.toUpperCase())} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>KM *</label>
              <input type="number" value={km} onChange={e => setKm(e.target.value)} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>ANNO</label>
              <input type="text" value={anno} onChange={e => setAnno(e.target.value)} placeholder="Es. 2019" style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          {/* Campi prezzo — valorizzabili solo da admin/super_admin. Il
              modale stesso è già raggiungibile solo da questi ruoli (vedi
              pulsante nel dettaglio contratto), ma il blocco resta
              esplicito qui per chiarezza. */}
          <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '10px', padding: '12px 14px', marginBottom: '10px' }}>
            <div style={{ fontSize: '9px', fontWeight: '900', color: '#f59e0b', letterSpacing: '0.5px', marginBottom: '8px' }}>🔐 PREZZI (solo admin/super admin)</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>PREZZO DI RITIRO (bottom price) *</label>
                <input type="number" value={bottomPrice} onChange={e => setBottomPrice(e.target.value)} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>PREZZO DI LISTINO *</label>
                <input type="number" value={prezzoListino} onChange={e => setPrezzoListino(e.target.value)} style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
          </div>

          <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>SEDE *</label>
          <select value={sede} onChange={e => setSede(e.target.value)}
            style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', cursor: 'pointer' }}>
            <option value="">Seleziona...</option>
            {SEDI_CONOSCIUTE.map(s => <option key={s.nome} value={s.nome}>{s.nome}</option>)}
          </select>
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Annulla</button>
          <button onClick={handleSalva} disabled={saving}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '10px', color: '#0f172a', cursor: saving ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', opacity: saving ? 0.6 : 1 }}>
            {saving ? '⏳ Salvataggio...' : '✅ Fai Entrare in Stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB SERVICE
// ═══════════════════════════════════════════════════════════════════════════════
function ServiceView({ user }) {
  const ruolo = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
  const puoModificare = ruolo === 'admin' || ruolo === 'super_admin' || ruolo === 'service';
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [ricambi, setRicambi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroSedeRicambi, setFiltroSedeRicambi] = useState('tutti'); // solo filtro di visualizzazione — i magazzini restano visibili a tutti, mai una restrizione di accesso
  const [sortBy, setSortBy] = useState('descrizione');
  const [sortDir, setSortDir] = useState('asc');
  const [subTab, setSubTab] = useState(() => ruolo === 'meccanico' ? 'marcatempo' : 'ricambi');
  // Etichetta unità di misura — "pz" (pezzi, default) o "L" (litri, per
  // prodotti come olio motore venduti/caricati a volume, non a conteggio).
  const um = (articolo) => articolo?.unitaMisura === 'litri' ? 'L' : 'pz';
  // Stesso helper già usato in MovimentiMagazzinoView — vedi lì per il
  // dettaglio del criterio di fallback sul pregresso.
  const getEsistenzaSede = (articolo, sede) => {
    if (sede === 'Salerno') return parseFloat(articolo.esistenzaSalerno) || 0;
    if (articolo.esistenzaAgnano !== undefined) return parseFloat(articolo.esistenzaAgnano) || 0;
    if (articolo.esistenzaSalerno === undefined) return parseFloat(articolo.esistenza) || 0;
    return 0;
  };
  // Quantità da mostrare a schermo — se è attivo un filtro per sede
  // specifica (Agnano/Salerno), mostra SOLO quella quantità, non il
  // totale complessivo: guardando "Salerno" non deve interessare cosa
  // c'è anche ad Agnano, solo quanto c'è fisicamente lì.
  const esistenzaVista = (r) => {
    if (filtroSedeRicambi === 'agnano') return getEsistenzaSede(r, 'Agnano');
    if (filtroSedeRicambi === 'salerno') return getEsistenzaSede(r, 'Salerno');
    return r.esistenza;
  };
  const [odlPrecompilato, setOdlPrecompilato] = useState(null); // bridge Planning→ODL

  const [stockVehicles, setStockVehicles] = useState([]);

  useEffect(() => {
    if (window._radarRicambiCache) { setRicambi(window._radarRicambiCache); setLoading(false); }
    get(ref(db, 'service_ricambi')).then(snap => {
      const data = snap.val();
      if (data) {
        const list = Object.values(data);
        window._radarRicambiCache = list;
        setRicambi(list);
      } else setRicambi([]);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // Carica stock immediatamente per la ricerca veicoli in Officina
    const cached = window._radarStockCache;
    if (cached?.vehicles) {
      setStockVehicles(cached.vehicles);
      return;
    }
    get(ref(db, 'stock_data')).then(snap => {
      const data = snap.val();
      if (data?.vehicles) {
        window._radarStockCache = data;
        window._radarStockTs = Date.now();
        setStockVehicles(data.vehicles);
      }
    });
  }, []);

  const filtered = useMemo(() => {
    let list = ricambi;
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(r =>
        r.codice?.toLowerCase().includes(s) ||
        r.descrizione?.toLowerCase().includes(s)
      );
    }
    if (filtroSedeRicambi !== 'tutti') {
      // Un pezzo può avere scorte in entrambe le sedi contemporaneamente
      // (bolle diverse, sedi diverse, stesso codice) — mostrato nel filtro
      // di ciascuna sede dove ha effettivamente quantità disponibile,
      // anche in entrambe insieme. Non più un tag fisso "questo pezzo
      // appartiene a una sola sede", che non reggeva più con scorte miste.
      const sedeFiltro = filtroSedeRicambi === 'agnano' ? 'Agnano' : 'Salerno';
      list = list.filter(r => getEsistenzaSede(r, sedeFiltro) > 0);
    }
    return [...list].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [ricambi, search, sortBy, sortDir, filtroSedeRicambi]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const [editing, setEditing] = useState(null); // { key, field, value }
  const [saving, setSaving] = useState(false);

  const fmtE = n => n ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n) : '—';
  const totValore = useMemo(() => ricambi.reduce((s, r) => s + (r.prezzoNetto * r.esistenza || 0), 0), [ricambi]);
  const totArticoli = useMemo(() => ricambi.reduce((s, r) => s + (r.esistenza || 0), 0), [ricambi]);

  const SortIcon = ({ col }) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const startEdit = (codice, field, currentVal) => setEditing({ codice, field, value: String(currentVal || '') });

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    const key = editing.codice.replace(/[./ ]/g, '_');
    const val = ['esistenza','prListino','sconto','prezzoNetto','prezzoMedio','lifo'].includes(editing.field)
      ? parseFloat(editing.value) || 0
      : editing.value;
    try {
      await set(ref(db, `service_ricambi/${key}/${editing.field}`), val);
    } catch(e) { alert('Errore salvataggio: ' + e.message); }
    setSaving(false);
    setEditing(null);
  };

  const EditCell = ({ r, field, isNum }) => {
    const isEditing = editing?.codice === r.codice && editing?.field === field;
    const val = r[field];
    if (isEditing) return (
      <input
        autoFocus
        type={isNum ? 'number' : 'text'}
        value={editing.value}
        onChange={e => setEditing(ed => ({ ...ed, value: e.target.value }))}
        onBlur={saveEdit}
        onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
        style={{ width: '100%', padding: '3px 6px', background: T.inputBg, border: '1px solid #6366f1', borderRadius: '6px', color: T.text, fontSize: '11px', outline: 'none' }}
      />
    );
    return (
      <div onClick={() => startEdit(r.codice, field, val)}
        style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '4px', transition: 'background 0.1s' }}
        title="Clicca per modificare"
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {isNum ? (field === 'sconto' ? (val ? val + '%' : '—') : fmtE(val)) : val}
        <span style={{ fontSize: '8px', color: '#6366f1', marginLeft: '3px', opacity: 0.6 }}>✎</span>
      </div>
    );
  };

  const adjustQta = async (r, delta) => {
    const key = r.codice.replace(/[./ ]/g, '_');
    // Il correttore rapido +/-1 agisce sulla sede attualmente filtrata a
    // schermo (Agnano o Salerno) — se non è filtrata nessuna sede
    // specifica ("Tutte le sedi"), corregge Agnano per default. Scrive
    // SEMPRE entrambe le sedi esplicitamente (anche quella non toccata),
    // stessa protezione già applicata a eseguiCarico/eseguiScaricoVendita,
    // altrimenti un pezzo mai corretto su una sede ma già caricato
    // sull'altra perderebbe il pregresso dalla scomposizione.
    const sedeCorretta = filtroSedeRicambi === 'salerno' ? 'Salerno' : 'Agnano';
    const esistenzaAgnanoAttuale = getEsistenzaSede(r, 'Agnano');
    const esistenzaSalernoAttuale = getEsistenzaSede(r, 'Salerno');
    const nuovaAgnano = sedeCorretta === 'Agnano' ? Math.max(0, esistenzaAgnanoAttuale + delta) : esistenzaAgnanoAttuale;
    const nuovaSalerno = sedeCorretta === 'Salerno' ? Math.max(0, esistenzaSalernoAttuale + delta) : esistenzaSalernoAttuale;
    const nuovaTotale = nuovaAgnano + nuovaSalerno;
    // Aggiorna subito lo stato locale per feedback immediato
    setRicambi(prev => prev.map(x => x.codice === r.codice ? { ...x, esistenzaAgnano: nuovaAgnano, esistenzaSalerno: nuovaSalerno, esistenza: nuovaTotale } : x));
    await update(ref(db, `service_ricambi/${key}`), { esistenzaAgnano: nuovaAgnano, esistenzaSalerno: nuovaSalerno, esistenza: nuovaTotale });
  };

  if (loading) return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text }}>Caricamento magazzino...</div>;

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      {/* HEADER */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '900', color: T.text, letterSpacing: '-0.5px' }}>Service</h2>
            <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '3px' }}>Gestione magazzino ricambi</div>
          </div>
        </div>
        {/* KPI veloci */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '14px' }}>
          {[
            ['Articoli totali', ricambi.length, '#6366f1'],
            ['Unità in magazzino', Math.round(totArticoli), '#10b981'],
            ['Valore magazzino', fmtE(totValore), '#f59e0b'],
            ['Risultati ricerca', filtered.length, '#3b82f6'],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: T.surface, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 14px' }}>
              <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '700', marginBottom: '3px' }}>{l}</div>
              <div style={{ fontSize: '16px', fontWeight: '900', color: c }}>{v}</div>
            </div>
          ))}
        </div>
        {/* Sub-tab */}
        <div style={{ overflowX: 'auto', borderBottom: `2px solid ${T.border}`, marginBottom: '14px' }}>
          <div style={{ display: 'flex', gap: '2px', minWidth: 'max-content' }}>
            {(ruolo === 'meccanico' ? [
              ['marcatempo',  isMobile ? '⏱' : '⏱ Marcatempo'],
            ] : [
              ['cruscotto',   isMobile ? '📊' : '📊 Cruscotto'],
              ['ricambi',     isMobile ? '🔩' : '🔩 Ricambi'],
              ['movimenti',   isMobile ? '📦' : '📦 Movimenti'],
              ['odl',         isMobile ? '🔧' : '🔧 ODL'],
              ['storico_odl', isMobile ? '📂' : '📂 Storico ODL'],
              ['officina',    isMobile ? '🧾' : '🧾 Fattura'],
              ['storico',     isMobile ? '📋' : '📋 Storico'],
              ['scheda',      isMobile ? '📝' : '📝 Scheda Service'],
              ['carrozzeria', isMobile ? '🚗' : '🚗 Carrozzeria'],
              ['planning',    isMobile ? '📅' : '📅 Planning'],
              ['marcatempo',  isMobile ? '⏱' : '⏱ Marcatempo'],
            ]).map(([id, label]) => (
              <button key={id} onClick={() => setSubTab(id)}
                style={{ padding: isMobile ? '8px 12px' : '8px 16px', fontSize: isMobile ? '11px' : '12px', fontWeight: '700', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', background: 'transparent', color: subTab === id ? '#6366f1' : T.textSub, borderBottom: subTab === id ? '2px solid #6366f1' : '2px solid transparent', marginBottom: '-2px', whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* RICAMBI */}
      {subTab === 'ricambi' && (
        <>
          <input
            placeholder="🔍 Cerca per codice o descrizione..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
          />
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
            {[['tutti','📦 Tutti i magazzini'],['agnano','🏭 Agnano'],['salerno','🏭 Salerno']].map(([val,label]) => (
              <button key={val} onClick={() => setFiltroSedeRicambi(val)} style={{
                padding: '7px 14px', borderRadius: '8px', border: `1px solid ${filtroSedeRicambi===val ? '#6366f1' : T.border2}`, cursor: 'pointer', fontWeight: '700', fontSize: '11px',
                background: filtroSedeRicambi===val ? '#6366f1' : 'transparent', color: filtroSedeRicambi===val ? '#fff' : T.textSub,
              }}>
                {label}
              </button>
            ))}
          </div>

          {isMobile ? (
            /* MOBILE — card per ogni ricambio */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filtered.slice(0, 200).map((r, i) => (
                <div key={r.codice + i} style={{ background: T.surface, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div>
                      <div style={{ fontSize: '9px', color: '#6366f1', fontWeight: '900', marginBottom: '2px' }}>{r.codice}</div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: T.text }}>{r.descrizione}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '10px' }}>
                      <div style={{ fontSize: '9px', color: T.textMuted, marginBottom: '2px' }}>Qtà</div>
                      {puoModificare ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button onClick={e => { e.stopPropagation(); adjustQta(r, -1); }} style={{ background: 'rgba(239,68,68,0.15)', border: 'none', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '14px', width: '24px', height: '24px', padding: 0 }}>−</button>
                          <span style={{ fontSize: '16px', fontWeight: '900', color: esistenzaVista(r) > 0 ? '#10b981' : '#ef4444', minWidth: '28px', textAlign: 'center' }}>{esistenzaVista(r)} {um(r)}</span>
                          <button onClick={e => { e.stopPropagation(); adjustQta(r, +1); }} style={{ background: 'rgba(16,185,129,0.15)', border: 'none', borderRadius: '6px', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: '14px', width: '24px', height: '24px', padding: 0 }}>+</button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '16px', fontWeight: '900', color: esistenzaVista(r) > 0 ? '#10b981' : '#ef4444' }}>{esistenzaVista(r)} {um(r)}</span>
                      )}
                      <div style={{ fontSize: '8px', color: T.textMuted, marginTop: '2px' }}>Agnano {getEsistenzaSede(r,'Agnano')} {um(r)} · Salerno {getEsistenzaSede(r,'Salerno')} {um(r)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', paddingTop: '8px', borderTop: `1px solid ${T.border}` }}>
                    {[['Listino', fmtE(r.prListino)],['Sconto', r.sconto ? r.sconto+'%' : '—'],['Netto', fmtE(r.prezzoNetto)]].map(([l, v]) => (
                      <div key={l}>
                        <div style={{ fontSize: '8px', color: T.textMuted, fontWeight: '700' }}>{l}</div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: T.text }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {filtered.length > 200 && <div style={{ textAlign: 'center', color: T.textMuted, fontSize: '12px', padding: '10px' }}>Mostrando 200 di {filtered.length} — affina la ricerca</div>}
            </div>
          ) : (
            /* DESKTOP — tabella con ordinamento */
            <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 90px 1fr 80px 100px 70px 100px 100px 100px', padding: '10px 16px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                {[['codice','CODICE'],['sede','SEDE'],['descrizione','DESCRIZIONE'],['esistenza','QTÀ'],['prListino','LISTINO'],['sconto','SCONTO'],['prezzoNetto','NETTO'],['prezzoMedio','MEDIO'],['lifo','LIFO']].map(([col, label]) => (
                  <div key={col} onClick={() => handleSort(col)}
                    style={{ fontSize: '8px', fontWeight: '900', color: sortBy === col ? '#6366f1' : T.textMuted, letterSpacing: '1px', cursor: 'pointer', userSelect: 'none' }}>
                    {label}<SortIcon col={col} />
                  </div>
                ))}
              </div>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {filtered.slice(0, 500).map((r, i) => (
                  <div key={r.codice + i}
                    style={{ display: 'grid', gridTemplateColumns: '120px 90px 1fr 80px 100px 70px 100px 100px 100px', padding: '9px 16px', borderBottom: `1px solid ${T.border}`, alignItems: 'center', background: i % 2 === 0 ? 'transparent' : T.bg + '80', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bg}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : T.bg + '80'}
                  >
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#6366f1' }}>{r.codice}</div>
                    <div style={{ fontSize: '9px', color: T.textMuted, lineHeight: 1.5 }}>
                      🏭 Agnano {getEsistenzaSede(r,'Agnano')} {um(r)}<br/>🏭 Salerno {getEsistenzaSede(r,'Salerno')} {um(r)}
                    </div>
                    <div style={{ fontSize: '11px', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>{r.descrizione}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {puoModificare ? (<>
                        <button onClick={() => adjustQta(r, -1)} style={{ background: 'rgba(239,68,68,0.15)', border: 'none', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '12px', width: '18px', height: '18px', lineHeight: 1, padding: 0 }}>−</button>
                        <span style={{ fontSize: '12px', fontWeight: '900', color: esistenzaVista(r) > 0 ? '#10b981' : '#ef4444', minWidth: '24px', textAlign: 'center' }}>{esistenzaVista(r)}</span>
                        <button onClick={() => adjustQta(r, +1)} style={{ background: 'rgba(16,185,129,0.15)', border: 'none', borderRadius: '4px', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: '12px', width: '18px', height: '18px', lineHeight: 1, padding: 0 }}>+</button>
                      </>) : (
                        <span style={{ fontSize: '12px', fontWeight: '900', color: esistenzaVista(r) > 0 ? '#10b981' : '#ef4444' }}>{esistenzaVista(r)} {um(r)}</span>
                      )}
                    </div>
                    {puoModificare ? <EditCell r={r} field="prListino" isNum={true} /> : <div style={{ fontSize: '11px', color: T.text }}>{fmtE(r.prListino)}</div>}
                    {puoModificare ? <EditCell r={r} field="sconto" isNum={true} /> : <div style={{ fontSize: '11px', color: T.text }}>{r.sconto ? r.sconto + '%' : '—'}</div>}
                    {puoModificare ? <EditCell r={r} field="prezzoNetto" isNum={true} /> : <div style={{ fontSize: '11px', color: T.text }}>{fmtE(r.prezzoNetto)}</div>}
                    {puoModificare ? <EditCell r={r} field="prezzoMedio" isNum={true} /> : <div style={{ fontSize: '11px', color: T.text }}>{fmtE(r.prezzoMedio)}</div>}
                    {puoModificare ? <EditCell r={r} field="lifo" isNum={true} /> : <div style={{ fontSize: '11px', color: T.text }}>{fmtE(r.lifo)}</div>}
                  </div>
                ))}
                {filtered.length > 500 && <div style={{ textAlign: 'center', color: T.textMuted, fontSize: '12px', padding: '12px' }}>Mostrando 500 di {filtered.length} — affina la ricerca</div>}
              </div>
            </div>
          )}
        </>
      )}

      {/* OFFICINA */}
      {subTab === 'officina' && (
        <OfficinaPreventivo ricambi={ricambi} T={T} isMobile={isMobile} db={db} vehicles={stockVehicles} />
      )}

      {/* STORICO INTERVENTI */}
      {subTab === 'storico' && (
        <StoricoInterventi T={T} isMobile={isMobile} db={db} />
      )}

      {/* SCHEDA SERVICE */}
      {subTab === 'scheda' && (
        <SchedaServiceView T={T} isMobile={isMobile} db={db} user={user} />
      )}
      {subTab === 'carrozzeria' && (
        <CarrozzeriaView T={T} isMobile={isMobile} db={db} vehicles={stockVehicles} user={user} />
      )}
      {subTab === 'planning' && (
        <PlanningView user={user} T={T} isMobile={isMobile} db={db} stockVehicles={stockVehicles}
          onApriODL={(dati) => { setOdlPrecompilato(dati); setSubTab('odl'); }} />
      )}
      {subTab === 'storico_odl' && (
        <StoricoODLView T={T} isMobile={isMobile} db={db} />
      )}
      {subTab === 'cruscotto' && (
        <CruscottoServiceView T={T} isMobile={isMobile} db={db} />
      )}
      {subTab === 'marcatempo' && (
        <MarcatempoView T={T} isMobile={isMobile} db={db} user={user} />
      )}
      {subTab === 'movimenti' && (
        <MovimentiMagazzinoView T={T} isMobile={isMobile} db={db} ricambi={ricambi} setRicambi={setRicambi} user={user} />
      )}
      {subTab === 'odl' && (
        <ODLView T={T} isMobile={isMobile} db={db} stockVehicles={stockVehicles} ricambi={ricambi} user={user}
          onOpenPlanning={() => setSubTab('planning')}
          odlPrecompilato={odlPrecompilato}
          onConsumaPrecompilato={() => setOdlPrecompilato(null)} />
      )}
    </div>
  );
}

// ─── GENERAZIONE XML FATTURA ELETTRONICA ─────────────────────────────────────
// ─── P.IVA / CF PER XML FATTURA ELETTRONICA ─────────────────────────────────
// In anagrafica la P.IVA può essere salvata come "IT01234567890", "IT 012…"
// o "01234567890". Nell'XML il paese va SOLO in <IdPaese>: <IdCodice> deve
// avere il numero senza prefisso, altrimenti diventa "ITIT0123…" e il
// gestionale / SdI rifiuta il file. Gestisce anche P.IVA estere (DE…, FR…).
function normalizzaPivaXML(v) {
  const pulita = String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Un codice fiscale finito per errore nel campo P.IVA non va "spezzato"
  if (/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(pulita)) return { paese: 'IT', codice: pulita };
  const m = /^([A-Z]{2})([A-Z0-9]+)$/.exec(pulita);
  return m ? { paese: m[1], codice: m[2] } : { paese: 'IT', codice: pulita };
}
function normalizzaCfXML(v) {
  const cf = String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return /^IT\d{11}$/.test(cf) ? cf.slice(2) : cf;   // P.IVA inserita nel campo CF con prefisso
}

// Rilegge la fattura da Firebase prima di generare l'XML: l'elenco è in
// cache e non vedrebbe correzioni fatte nel frattempo (anche dalla console)
async function generaXMLFatturaAggiornata(inv) {
  let fresca = inv;
  try {
    const snap = await get(ref(db, `service_interventi/${inv.id}`));
    if (snap.exists()) fresca = { ...inv, ...snap.val(), id: inv.id };
  } catch (e) { /* si usa quella in elenco */ }
  generaXMLFattura(fresca);
}

function generaXMLFattura(fattura) {
  if (fattura?.senzaIva) {
    alert('Questa è una fattura di ordine interno SENZA IVA: non è una fattura elettronica da inviare allo SdI, quindi l\'XML non viene generato.');
    return;
  }
  const fmtN = n => (parseFloat(n) || 0).toFixed(2);
  const fmtQ = n => (parseFloat(n) || 0).toFixed(2);
  const esc  = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const righe  = (fattura.righe || []).filter(r => r.tipo !== 'sconto');
  const sconti = (fattura.righe || []).filter(r => r.tipo === 'sconto');
  const imponibileRighe = righe.reduce((s,r)=>(parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0)+s, 0);
  const totSconto = sconti.reduce((s,r)=>s+imponibileRighe*((parseFloat(r.percentuale)||0)/100), 0);
  const imponibile = Math.max(0, imponibileRighe - totSconto);
  const imposta    = imponibile * 0.22;
  const totale     = imponibile + imposta;

  // Dati cliente
  const cli        = fattura;
  const isAz       = !!(cli.pivaCliente);
  const cfCliente  = normalizzaCfXML(cli.cfCliente);
  const { paese: paesePiva, codice: pivaCliente } = normalizzaPivaXML(cli.pivaCliente);
  const nomeCliente = cli.nomeCliente || '';
  // "Mario Rossi" → Nome=Mario, Cognome=Rossi
  const parts  = nomeCliente.trim().split(' ');
  const nome   = isAz ? '' : (parts[0] || '');
  const cognome = isAz ? '' : parts.slice(1).join(' ') || nome;

  // CAP e Comune: estrai dall'indirizzo se presenti (es. "Via Roma 1, 80078 Pozzuoli NA")
  const indirizzoCliente = cli.indirizzoCliente || '';
  const capMatch   = indirizzoCliente.match(/\b(\d{5})\b/);
  const capCliente  = capMatch ? capMatch[1] : '00000';
  // Tutto dopo il CAP è comune
  const comuneMatch = capMatch ? indirizzoCliente.slice(indirizzoCliente.indexOf(capMatch[1]) + 5).replace(/^[, ]+/,'').split(/[ ,]/)[0] : 'ND';
  const comuneCliente = comuneMatch || 'ND';
  // Indirizzo senza CAP e comune (parte iniziale)
  const indirizzoSolo = capMatch ? indirizzoCliente.slice(0, indirizzoCliente.indexOf(capMatch[1])).replace(/[, ]+$/,'') || indirizzoCliente : indirizzoCliente || 'ND';

  // Data e numero documento
  // Data documento: il campo 'data' (quello che si corregge) ha la precedenza
  // sull'ora tecnica di creazione 'creatoAt'
  const data    = String(fattura.data || fattura.creatoAt || new Date().toISOString()).split('T')[0];
  const numDoc  = fattura.numeroFattura || fattura.id || ('OF-' + Date.now());

  // Progressivo invio (ultime 3 cifre dell'ID)
  const progInvio = numDoc.replace(/[^A-Z0-9]/g,'').slice(-6) || 'RAD001';

  // Righe dettaglio
  let linee = '';
  let numLinea = 1;
  righe.forEach(r => {
    const sub = (parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0);
    const um  = r.tipo === 'manodopera' ? 'UL' : 'PZ';
    const codTipo = r.tipo === 'manodopera' ? 'MANODOPERA' : 'RICAMBIO';
    linee += `
      <DettaglioLinee>
        <NumeroLinea>${numLinea++}</NumeroLinea>
        <CodiceArticolo>
          <CodiceTipo>${esc(codTipo)}</CodiceTipo>
          <CodiceValore>${esc(r.codice||'01')}</CodiceValore>
        </CodiceArticolo>
        <Descrizione>${esc(r.descrizione||'Prestazione')}</Descrizione>
        <Quantita>${fmtQ(r.qta||1)}</Quantita>
        <UnitaMisura>${um}</UnitaMisura>
        <PrezzoUnitario>${fmtN(r.prezzoUnit)}</PrezzoUnitario>
        <PrezzoTotale>${fmtN(sub)}</PrezzoTotale>
        <AliquotaIVA>22.00</AliquotaIVA>
        ${fattura.targa ? `<AltriDatiGestionali><TipoDato>TARGA</TipoDato><RiferimentoTesto>${esc(fattura.targa)}</RiferimentoTesto></AltriDatiGestionali>` : ''}
        ${fattura.telaio ? `<AltriDatiGestionali><TipoDato>TELAIO</TipoDato><RiferimentoTesto>${esc(fattura.telaio)}</RiferimentoTesto></AltriDatiGestionali>` : ''}
      </DettaglioLinee>`;
  });
  // Eventuale riga sconto
  if (totSconto > 0) {
    linee += `
      <DettaglioLinee>
        <NumeroLinea>${numLinea++}</NumeroLinea>
        <Descrizione>Sconto</Descrizione>
        <Quantita>1.00</Quantita>
        <UnitaMisura>PZ</UnitaMisura>
        <PrezzoUnitario>-${fmtN(totSconto)}</PrezzoUnitario>
        <PrezzoTotale>-${fmtN(totSconto)}</PrezzoTotale>
        <AliquotaIVA>22.00</AliquotaIVA>
      </DettaglioLinee>`;
  }

  const xml = `<?xml version="1.0" encoding="utf-8"?>
<FatturaElettronica xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" versione="FPR12" xmlns="http://ivaservizi.agenziaentrate.gov.it/docs/xsd/fatture/v1.2">
  <FatturaElettronicaHeader xmlns="">
    <DatiTrasmissione>
      <IdTrasmittente>
        <IdPaese>IT</IdPaese>
        <IdCodice>07821241218</IdCodice>
      </IdTrasmittente>
      <ProgressivoInvio>${esc(progInvio)}</ProgressivoInvio>
      <FormatoTrasmissione>FPR12</FormatoTrasmissione>
      <CodiceDestinatario>${esc(cli.sdiCliente||'0000000')}</CodiceDestinatario>
      <ContattiTrasmittente>
        <Telefono>08118658721</Telefono>
        <Email>rosario.scala@gruppoautoscala.com</Email>
      </ContattiTrasmittente>
    </DatiTrasmissione>
    <CedentePrestatore>
      <DatiAnagrafici>
        <IdFiscaleIVA>
          <IdPaese>IT</IdPaese>
          <IdCodice>07821241218</IdCodice>
        </IdFiscaleIVA>
        <CodiceFiscale>07821241218</CodiceFiscale>
        <Anagrafica>
          <Denominazione>Gruppo Autoscala S.r.l.</Denominazione>
        </Anagrafica>
        <RegimeFiscale>RF01</RegimeFiscale>
      </DatiAnagrafici>
      <Sede>
        <Indirizzo>Via Antiniana, 2/B</Indirizzo>
        <CAP>80078</CAP>
        <Comune>Pozzuoli</Comune>
        <Nazione>IT</Nazione>
      </Sede>
      <IscrizioneREA>
        <Ufficio>XX</Ufficio>
        <NumeroREA>XX</NumeroREA>
        <CapitaleSociale>0.00</CapitaleSociale>
        <StatoLiquidazione>LN</StatoLiquidazione>
      </IscrizioneREA>
    </CedentePrestatore>
    <CessionarioCommittente>
      <DatiAnagrafici>
        ${pivaCliente ? `<IdFiscaleIVA><IdPaese>${esc(paesePiva)}</IdPaese><IdCodice>${esc(pivaCliente)}</IdCodice></IdFiscaleIVA>` : ''}
        ${cfCliente ? `<CodiceFiscale>${esc(cfCliente)}</CodiceFiscale>` : ''}
        <Anagrafica>
          ${isAz ? `<Denominazione>${esc(nomeCliente)}</Denominazione>` : `<Nome>${esc(nome)}</Nome><Cognome>${esc(cognome)}</Cognome>`}
        </Anagrafica>
      </DatiAnagrafici>
      <Sede>
        <Indirizzo>${esc(indirizzoSolo)}</Indirizzo>
        <CAP>${esc(capCliente)}</CAP>
        <Comune>${esc(comuneCliente)}</Comune>
        <Nazione>IT</Nazione>
      </Sede>
    </CessionarioCommittente>
  </FatturaElettronicaHeader>
  <FatturaElettronicaBody xmlns="">
    <DatiGenerali>
      <DatiGeneraliDocumento>
        <TipoDocumento>TD01</TipoDocumento>
        <Divisa>EUR</Divisa>
        <Data>${esc(data)}</Data>
        <Numero>${esc(numDoc)}</Numero>
        <ImportoTotaleDocumento>${fmtN(totale)}</ImportoTotaleDocumento>
      </DatiGeneraliDocumento>
    </DatiGenerali>
    <DatiBeniServizi>${linee}
      <DatiRiepilogo>
        <AliquotaIVA>22.00</AliquotaIVA>
        <ImponibileImporto>${fmtN(imponibile)}</ImponibileImporto>
        <Imposta>${fmtN(imposta)}</Imposta>
        <EsigibilitaIVA>I</EsigibilitaIVA>
      </DatiRiepilogo>
    </DatiBeniServizi>
  </FatturaElettronicaBody>
</FatturaElettronica>`;

  // Download
  const blob = new Blob([xml], { type: 'application/xml' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `IT07821241218_${progInvio}.xml`;
  a.click();
  URL.revokeObjectURL(url);
}


// ─── STORICO ODL ─────────────────────────────────────────────────────────────
function StoricoODLView({ T, isMobile, db }) {
  const [odlList,  setOdlList]  = React.useState([]);
  const [fatList,  setFatList]  = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [filtro,   setFiltro]   = React.useState('');
  const [tabType,  setTabType]  = React.useState('tutti'); // 'tutti'|'odl'|'fatture'
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    // Prima: onValue live su TUTTO lo storico ODL+fatture, sempre acceso,
    // si riattivava ad ogni scrittura di chiunque — stesso pattern di
    // contratti/preventivi prima di ieri. Ora: cache con TTL 4 ore +
    // IndexedDB, stesso schema già collaudato oggi, PIÙ un controllo
    // leggero ogni 2 minuti (stesso principio della vista ODL principale)
    // così un ODL o una fattura nuova compaiono per tutti entro 2 minuti,
    // non fino a 4 ore, senza il costo di un vero ascoltatore live.
    let annullato = false;
    const maxCampo = (arr, campo, partenza = '') =>
      arr.reduce((m, x) => (x[campo] && x[campo] > m) ? x[campo] : m, partenza);
    const eseguiDeltaCheckStorico = () => {
      const cutoffOdl = window._radarOdlStoricoDeltaOdl;
      const cutoffFat = window._radarOdlStoricoDeltaFat;
      if (!cutoffOdl && !cutoffFat) return; // nessun caricamento completo ancora fatto
      Promise.all([
        cutoffOdl ? get(query(ref(db, 'odl'), orderByChild('aggiornatoAt'), startAt(cutoffOdl))) : null,
        cutoffFat ? get(query(ref(db, 'service_interventi'), orderByChild('creatoAt'), startAt(cutoffFat))) : null,
      ]).then(([snapO, snapF]) => {
        if (annullato) return;
        let cambiato = false;
        let odl = window._radarOdlStoricoList || [];
        let fat = window._radarFatStoricoList || [];
        if (snapO?.exists()) {
          const nuovi = Object.entries(snapO.val()).map(([id, v]) => ({ ...v, id, _tipo: 'odl' }));
          // ODL tornati aperti (fattura eliminata da un altro PC): la fattura
          // collegata sparisce anche da questo storico
          const riaperti = new Set(nuovi.filter(o => !o.fatturaId).map(o => o.id));
          if (riaperti.size) {
            const prima = fat.length;
            fat = fat.filter(f => !(f.odlId && riaperti.has(f.odlId)));
            if (fat.length !== prima) cambiato = true;
          }
          const mappa = new Map(odl.map(o => [o.id, o]));
          nuovi.forEach(o => mappa.set(o.id, o));
          odl = Array.from(mappa.values()).sort((a, b) => (b.dataOdl || b.creatoAt || '').localeCompare(a.dataOdl || a.creatoAt || ''));
          window._radarOdlStoricoDeltaOdl = maxCampo(nuovi, 'aggiornatoAt', cutoffOdl);
          cambiato = true;
        }
        if (snapF?.exists()) {
          const nuove = Object.entries(snapF.val()).map(([id, v]) => ({ ...v, id, _tipo: 'fattura' }));
          const mappa = new Map(fat.map(f => [f.id, f]));
          nuove.forEach(f => mappa.set(f.id, f));
          fat = Array.from(mappa.values()).sort((a, b) => (b.creatoAt || b.data || '').localeCompare(a.creatoAt || a.data || ''));
          window._radarOdlStoricoDeltaFat = maxCampo(nuove, 'creatoAt', cutoffFat);
          cambiato = true;
        }
        if (cambiato) {
          const ts = Date.now();
          window._radarOdlStoricoList = odl; window._radarFatStoricoList = fat; window._radarOdlStoricoTs = ts;
          idbScrivi('odl_storico_cache', { odl, fat, ts });
          setOdlList(odl); setFatList(fat);
        }
      }).catch(e => console.error('[RadaR] Delta-check Storico ODL/Fatture fallito — probabile indice Firebase mancante su aggiornatoAt/creatoAt:', e));
    };
    const now = Date.now();
    if (window._radarOdlStoricoTs && (now - window._radarOdlStoricoTs) < 4*60*60*1000) {
      setOdlList(window._radarOdlStoricoList || []);
      setFatList(window._radarFatStoricoList || []);
      setLoading(false);
    } else {
      idbLeggi('odl_storico_cache').then(cache => {
        if (annullato) return;
        if (cache && (now - cache.ts) < 4*60*60*1000) {
          window._radarOdlStoricoList = cache.odl; window._radarFatStoricoList = cache.fat; window._radarOdlStoricoTs = cache.ts;
          window._radarOdlStoricoDeltaOdl = maxCampo(cache.odl, 'aggiornatoAt');
          window._radarOdlStoricoDeltaFat = maxCampo(cache.fat, 'creatoAt');
          setOdlList(cache.odl); setFatList(cache.fat);
          setLoading(false);
        } else {
          Promise.all([get(ref(db, 'odl')), get(ref(db, 'service_interventi'))]).then(([snapO, snapF]) => {
            if (annullato) return;
            const odl = snapO.val() ? Object.entries(snapO.val()).map(([id,v])=>({...v,id,_tipo:'odl'})).sort((a,b)=>(b.dataOdl||b.creatoAt||'').localeCompare(a.dataOdl||a.creatoAt||'')) : [];
            const fat = snapF.val() ? Object.entries(snapF.val()).map(([id,v])=>({...v,id,_tipo:'fattura'})).sort((a,b)=>(b.creatoAt||b.data||'').localeCompare(a.creatoAt||a.data||'')) : [];
            const ts = Date.now();
            window._radarOdlStoricoList = odl; window._radarFatStoricoList = fat; window._radarOdlStoricoTs = ts;
            window._radarOdlStoricoDeltaOdl = maxCampo(odl, 'aggiornatoAt');
            window._radarOdlStoricoDeltaFat = maxCampo(fat, 'creatoAt');
            idbScrivi('odl_storico_cache', { odl, fat, ts });
            setOdlList(odl); setFatList(fat);
            setLoading(false);
          });
        }
      });
    }
    const intervalDelta = setInterval(eseguiDeltaCheckStorico, 2 * 60 * 1000);
    // Controllo leggero anche SUBITO all'apertura (prima si aspettavano fino a 2 minuti)
    const primoDelta = setTimeout(eseguiDeltaCheckStorico, 1500);
    // Fattura creata in questo browser: compare all'istante
    const suFattura = () => { setOdlList(window._radarOdlStoricoList || []); setFatList(window._radarFatStoricoList || []); };
    window.addEventListener('radar:fattura-creata', suFattura);
    return () => { annullato = true; clearInterval(intervalDelta); clearTimeout(primoDelta); window.removeEventListener('radar:fattura-creata', suFattura); };
  }, []);

  const fmtE = n => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(n||0);
  const fmtD = d => d ? new Date(d).toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—';

  const tutti = React.useMemo(() => {
    let list = tabType === 'odl' ? odlList : tabType === 'fatture' ? fatList : [...odlList, ...fatList]
      .sort((a,b)=>(b.creatoAt||b.dataOdl||b.data||'').localeCompare(a.creatoAt||a.dataOdl||a.data||''));
    if (filtro.trim()) {
      const s = filtro.toLowerCase();
      list = list.filter(r =>
        (r.nomeCliente||'').toLowerCase().includes(s) ||
        (r.targa||'').toLowerCase().includes(s) ||
        (r.telaio||'').toLowerCase().includes(s) ||
        (r.id||'').toLowerCase().includes(s) ||
        (r.accettatore||'').toLowerCase().includes(s) ||
        (r.operatore||'').toLowerCase().includes(s)
      );
    }
    return list;
  }, [odlList, fatList, tabType, filtro]);

  const statoColor = { aperto:'#f59e0b', in_lavorazione:'#6366f1', chiuso:'#10b981', annullato:'#ef4444' };
  const iS = { padding:'9px 12px', background:T.inputBg||T.bg, border:`1px solid ${T.border2||T.border}`, borderRadius:'10px', color:T.text, fontSize:'12px', outline:'none', width:'100%', boxSizing:'border-box' };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', marginBottom:'16px', alignItems:'center' }}>
        <input value={filtro} onChange={e=>setFiltro(e.target.value)}
          placeholder="🔍 Cliente, targa, telaio, ID, accettatore..."
          style={{ ...iS, flex:1, minWidth:'180px', marginBottom:0 }} />
        <div style={{ display:'flex', gap:'6px' }}>
          {[['tutti','Tutti'],['odl','ODL'],['fatture','Fatture']].map(([v,l])=>(
            <button key={v} onClick={()=>setTabType(v)}
              style={{ padding:'8px 14px', borderRadius:'8px', border:`2px solid ${tabType===v?'#6366f1':T.border}`, background:tabType===v?'#6366f1':'transparent', color:tabType===v?'#fff':'#6366f1', fontWeight:'700', fontSize:'11px', cursor:'pointer' }}>
              {l}
            </button>
          ))}
        </div>
        <div style={{ fontSize:'11px', color:T.textMuted||T.textSub }}>
          {odlList.length} ODL · {fatList.length} Fatture
        </div>
      </div>

      {loading && <div style={{ textAlign:'center', padding:'40px', color:T.textMuted||T.textSub }}>Caricamento...</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {tutti.map(r => (
          <div key={r.id} style={{ background:T.surface||T.card, borderRadius:'12px', border:`1px solid ${T.border}`, overflow:'hidden' }}>
            {/* Header card */}
            <div onClick={()=>setSelected(selected?.id===r.id?null:r)}
              style={{ padding:'12px 16px', display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center', cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(99,102,241,0.05)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{ flex:1, minWidth:'140px' }}>
                <div style={{ display:'flex', gap:'6px', alignItems:'center', marginBottom:'3px', flexWrap:'wrap' }}>
                  <span style={{ fontSize:'10px', fontWeight:'900', color: r._tipo==='fattura'?'#10b981':'#6366f1', background: r._tipo==='fattura'?'rgba(16,185,129,0.1)':'rgba(99,102,241,0.1)', padding:'2px 8px', borderRadius:'4px' }}>
                    {r._tipo==='fattura'?'🧾 FATTURA':'🔧 ODL'}
                  </span>
                  <span style={{ fontSize:'10px', color:T.textMuted||T.textSub }}>{r.id}</span>
                  {r._tipo==='odl' && r.stato && (
                    <span style={{ fontSize:'10px', fontWeight:'700', color:statoColor[r.stato]||'#6366f1', background:(statoColor[r.stato]||'#6366f1')+'18', padding:'2px 8px', borderRadius:'12px' }}>
                      {r.stato.replace('_',' ').toUpperCase()}
                    </span>
                  )}
                  {r.fatturaId && (
                    <span style={{ fontSize:'9px', color:'#10b981', fontWeight:'700' }}>→ {r.fatturaId}</span>
                  )}
                </div>
                <div style={{ fontSize:'14px', fontWeight:'800', color:T.text }}>{r.nomeCliente||'—'}</div>
                <div style={{ fontSize:'11px', color:T.textMuted||T.textSub, display:'flex', gap:'10px', flexWrap:'wrap', marginTop:'2px' }}>
                  {r.targa && <span>🏷 {r.targa}</span>}
                  {r.marca && <span>{r.marca} {r.modello}</span>}
                  {r.operatore && <span>👷 {r.operatore}</span>}
                  {r.accettatore && <span>✍️ {r.accettatore}</span>}
                </div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:'15px', fontWeight:'900', color:'#10b981' }}>{fmtE(r.totaleLordo)}</div>
                <div style={{ fontSize:'10px', color:T.textMuted||T.textSub }}>{fmtD(r.dataOdl||r.data||r.creatoAt)}</div>
                <div style={{ fontSize:'10px', color:T.textMuted||T.textSub }}>{(r.righe||[]).length} voci</div>
              </div>
            </div>

            {/* Dettaglio espandibile */}
            {selected?.id === r.id && (
              <div style={{ borderTop:`1px solid ${T.border}`, padding:'12px 16px' }}>
                {/* Dati veicolo */}
                {(r.targa||r.telaio||r.marca) && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:'8px', marginBottom:'10px', background:T.bg, borderRadius:'8px', padding:'8px 12px' }}>
                    {[['Targa',r.targa],['Telaio',r.telaio],['Km',r.km],['Immatr.',r.dataImmatricolazione],['Marca',r.marca],['Modello',r.modello],['CC',r.cc],['Kw/CV',r.kwcv],['N°Motore',r.nMotore]].filter(([,v])=>v).map(([l,v])=>(
                      <div key={l}><div style={{ fontSize:'8px', color:T.textMuted||T.textSub, textTransform:'uppercase' }}>{l}</div><div style={{ fontSize:'11px', fontWeight:'700', color:T.text }}>{v}</div></div>
                    ))}
                  </div>
                )}
                {/* Righe */}
                {(r.righe||[]).length > 0 && (
                  <div style={{ background:T.bg, borderRadius:'8px', overflow:'hidden', marginBottom:'10px' }}>
                    {(r.righe||[]).map((riga,i)=>{
                      const sub = riga.tipo==='sconto'
                        ? -(r.righe.filter(x=>x.tipo!=='sconto').reduce((s,x)=>s+(parseFloat(x.qta)||0)*(parseFloat(x.prezzoUnit)||0),0) * ((parseFloat(riga.percentuale)||0)/100))
                        : (parseFloat(riga.qta)||0)*(parseFloat(riga.prezzoUnit)||0);
                      return (
                        <div key={i} style={{ display:'grid', gridTemplateColumns:isMobile?'1fr 50px 80px':'80px 1fr 50px 80px 90px', padding:'6px 10px', borderBottom:i<(r.righe.length-1)?`1px solid ${T.border}`:'none', background:riga.tipo==='sconto'?'rgba(245,158,11,0.06)':i%2===0?'transparent':T.bg+'50' }}>
                          {!isMobile && <div style={{ fontSize:'9px', color:riga.tipo==='sconto'?'#f59e0b':'#6366f1', fontWeight:'700' }}>{riga.codice||'—'}</div>}
                          <div style={{ fontSize:'11px', color:T.text }}>{riga.descrizione}{riga.tipo==='sconto'?` (${riga.percentuale||0}%)`:''}
                          </div>
                          <div style={{ fontSize:'11px', color:T.textMuted||T.textSub }}>{riga.tipo==='sconto'?'—':(riga.qta+(riga.tipo==='manodopera'?' h':''))}</div>
                          {!isMobile && <div style={{ fontSize:'11px', color:T.textMuted||T.textSub }}>{riga.tipo==='sconto'?'—':fmtE(riga.prezzoUnit)}</div>}
                          <div style={{ fontSize:'11px', fontWeight:'700', color:riga.tipo==='sconto'?'#f59e0b':'#10b981', textAlign:'right' }}>{fmtE(sub)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Totali e azioni */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
                  <div style={{ display:'flex', gap:'16px', fontSize:'11px', flexWrap:'wrap' }}>
                    <span style={{ color:T.textMuted||T.textSub }}>Imponibile: <b style={{ color:T.text }}>{fmtE(r.totaleNetto)}</b></span>
                    <span style={{ color:T.textMuted||T.textSub }}>IVA 22%: <b style={{ color:'#f59e0b' }}>{fmtE(r.totaleIVA)}</b></span>
                    <span style={{ color:T.textMuted||T.textSub }}>Totale: <b style={{ color:'#10b981' }}>{fmtE(r.totaleLordo)}</b></span>
                  </div>
                  <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                    <button onClick={()=>stampaFatturaOfficina(r)}
                      style={{ padding:'6px 14px', borderRadius:'8px', border:'2px solid #6366f1', background:'transparent', color:'#6366f1', fontWeight:'700', fontSize:'11px', cursor:'pointer' }}>
                      🖨️ Stampa
                    </button>

                    <button onClick={async()=>{
                      const nodo = r._tipo==='fattura' ? 'service_interventi' : 'odl';
                      if (!window.confirm(`Eliminare ${r._tipo==='fattura'?'fattura':'ODL'} ${r.id}?`)) return;
                      await set(ref(db,`${nodo}/${r.id}`),null);
                    }}
                      style={{ padding:'6px 14px', borderRadius:'8px', border:'1px solid #ef444440', background:'rgba(239,68,68,0.08)', color:'#ef4444', fontWeight:'700', fontSize:'11px', cursor:'pointer' }}>
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {!loading && tutti.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px', color:T.textMuted||T.textSub }}>
            Nessun risultato
          </div>
        )}
      </div>
    </div>
  );
}


// ─── MARCATEMPO OFFICINA ──────────────────────────────────────────────────────
function MarcatempoView({ T, isMobile, db, user }) {
  const [odlTutti, setOdlList] = React.useState([]);
  // Filtro per sede (come il Planning): ogni utenza vede solo gli ODL della
  // propria sede (campo user.sede su Firebase). admin/super_admin vedono tutto
  // e possono scegliere la sede. ODL senza sede salvata = Agnano (default storico).
  // Utenze senza sede valida continuano a vedere tutto, come prima.
  const ruoloMt = (user?.ruolo || '').toLowerCase().trim();
  const sedeUtenteMt = (user?.sede || '').toLowerCase().trim();
  const mtVedeTutto = ['admin', 'super_admin'].includes(ruoloMt) || !SEDI_PONTI[sedeUtenteMt];
  const [sedeMt, setSedeMt] = React.useState(mtVedeTutto ? 'tutte' : sedeUtenteMt);
  const odlList = React.useMemo(() => {
    const sedeFiltro = mtVedeTutto ? sedeMt : sedeUtenteMt;
    if (sedeFiltro === 'tutte') return odlTutti;
    return odlTutti.filter(o => (o.sedePlan || 'agnano').toLowerCase() === sedeFiltro);
  }, [odlTutti, sedeMt, mtVedeTutto, sedeUtenteMt]);
  const [tempi, setTempi]     = React.useState({});
  const [tick,  setTick]      = React.useState(0);
  const [schermo, setSchermo] = React.useState(false);

  React.useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  React.useEffect(() => {
    // Prima scaricava TUTTO lo storico ODL (anche chiusi da anni) solo per
    // filtrare i pochi lavori attivi in questo momento. Due query mirate,
    // live (serve il tempo reale per il planning), invece di tutto+filtro.
    let attivi = {}; let inLav = {};
    const aggiorna = () => setOdlList([...Object.values(attivi), ...Object.values(inLav)]);
    const unsub1 = onValue(query(ref(db, 'odl'), orderByChild('stato'), equalTo('aperto')), snap => {
      attivi = snap.val() || {}; aggiorna();
    });
    const unsub2 = onValue(query(ref(db, 'odl'), orderByChild('stato'), equalTo('in_lavorazione')), snap => {
      inLav = snap.val() || {}; aggiorna();
    });
    return () => { unsub1(); unsub2(); };
  }, []);


  React.useEffect(() => {
    const unsub = onValue(ref(db, 'odl_tempi'), snap => {
      setTempi(snap.val() || {});
    });
    return () => unsub();
  }, []);


  const aggiornaStato = async (odlId, azione) => {
    const now = new Date().toISOString();
    const t   = tempi[odlId] || { stato: 'attesa', eventi: [], totaleMs: 0 };
    const eventi = [...(t.eventi || [])];
    try {
      if (azione === 'inizio') {
        if (t.stato === 'attesa' || t.stato === 'pausa') {
          eventi.push({ tipo: 'inizio', ts: now });
          await set(ref(db, `odl_tempi/${odlId}`), { ...t, stato: 'lavoro', eventi });
          await set(ref(db, `odl/${odlId}/stato`), 'in_lavorazione');
          await set(ref(db, `odl/${odlId}/aggiornatoAt`), now);
        }
      } else if (azione === 'pausa') {
        if (t.stato === 'lavoro') {
          eventi.push({ tipo: 'pausa', ts: now });
          await set(ref(db, `odl_tempi/${odlId}`), { ...t, stato: 'pausa', eventi, totaleMs: calcElapsed(t) });
        }
      } else if (azione === 'fine') {
        eventi.push({ tipo: 'fine', ts: now });
        await set(ref(db, `odl_tempi/${odlId}`), { ...t, stato: 'completato', eventi, totaleMs: calcElapsed(t), fineTs: now });
        await set(ref(db, `odl/${odlId}/stato`), 'chiuso');
        await set(ref(db, `odl/${odlId}/aggiornatoAt`), now);
      }
    } catch (e) {
      // Prima non c'era nessun avviso in caso di errore — il pulsante
      // sembrava "non fare nulla". Ora l'errore (es. permessi Firebase
      // mancanti sul nodo odl_tempi) diventa visibile.
      alert('❌ Errore nell\'aggiornare il marcatempo: ' + e.message);
    }
  };

  const calcElapsed = (t) => {
    if (!t || !t.eventi) return 0;
    let tot = t.totaleMs || 0, lastInizio = null;
    for (const e of t.eventi) {
      if (e.tipo === 'inizio') lastInizio = new Date(e.ts).getTime();
      if ((e.tipo === 'pausa' || e.tipo === 'fine') && lastInizio) { tot += Date.now() - lastInizio; lastInizio = null; }
    }
    if (lastInizio) tot += Date.now() - lastInizio;
    return tot;
  };

  const fmtDur = (ms) => {
    const s = Math.floor((ms||0) / 1000);
    return String(Math.floor(s/3600)).padStart(2,'0') + ':' + String(Math.floor((s%3600)/60)).padStart(2,'0') + ':' + String(s%60).padStart(2,'0');
  };
  const fmtOra  = (iso) => iso ? new Date(iso).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'}) : '—';
  const fmtData = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

  const statoInfo = (s) => ({
    lavoro:     { label:'IN LAVORO',  color:'#16a34a', bg:'#dcfce7', border:'#16a34a' },
    pausa:      { label:'IN PAUSA',   color:'#b45309', bg:'#fef3c7', border:'#f59e0b' },
    completato: { label:'COMPLETATO', color:'#1d4ed8', bg:'#dbeafe', border:'#3b82f6' },
    attesa:     { label:'IN ATTESA',  color:'#6b7280', bg:'#f3f4f6', border:'#d1d5db' },
  }[s] || { label:'IN ATTESA', color:'#6b7280', bg:'#f3f4f6', border:'#d1d5db' });

  // ── SCHERMO OFFICINA ──────────────────────────────────────────────────────
  if (schermo) {
    const oraStr  = new Date().toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit',second:'2-digit'});
    const dataStr = new Date().toLocaleDateString('it-IT',{weekday:'long',day:'2-digit',month:'long',year:'numeric'});
    const cols = odlList.length <= 2 ? odlList.length || 1 : odlList.length <= 4 ? 2 : 3;

    return (
      <div style={{ position:'fixed', inset:0, zIndex:9999, background:'#f8fafc', fontFamily:"'Inter','Segoe UI',sans-serif", display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* HEADER */}
        <div style={{ background:'#1e293b', padding:'16px 40px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
            <div style={{ fontSize:'26px', fontWeight:'900', color:'#fff', letterSpacing:'3px' }}>🔧 OFFICINA</div>
            <div style={{ fontSize:'13px', color:'#94a3b8', textTransform:'capitalize' }}>{dataStr}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'20px' }}>
            <div style={{ fontSize:'40px', fontWeight:'900', color:'#fff', fontFamily:'monospace', letterSpacing:'3px' }}>{oraStr}</div>
            <button onClick={() => setSchermo(false)}
              style={{ padding:'8px 16px', borderRadius:'8px', border:'1px solid #475569', background:'transparent', color:'#94a3b8', fontSize:'13px', cursor:'pointer' }}>
              ✕ Chiudi
            </button>
          </div>
        </div>

        {/* GRIGLIA LAVORI */}
        <div style={{ flex:1, overflow:'auto', padding:'24px 32px' }}>
          {odlList.length === 0 ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
              <div style={{ textAlign:'center', color:'#94a3b8' }}>
                <div style={{ fontSize:'72px' }}>🔧</div>
                <div style={{ fontSize:'28px', fontWeight:'700', color:'#475569', marginTop:'16px' }}>Nessun lavoro aperto</div>
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap:'20px' }}>
              {odlList.map(o => {
                const t     = tempi[o.id] || {};
                const stato = t.stato || 'attesa';
                const ms    = calcElapsed(t);
                const info  = statoInfo(stato);
                const inAttesa = stato === 'attesa' || stato === 'pausa';

                return (
                  <div key={o.id} style={{ background:'#fff', borderRadius:'16px', border:`3px solid ${info.border}`, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.08)' }}>
                    {/* Barra stato */}
                    <div style={{ background:info.color, padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'15px', fontWeight:'900', color:'#fff', letterSpacing:'2px' }}>{info.label}</span>
                      <span style={{ fontSize:'28px' }}>{inAttesa ? '🔴' : '🟢'}</span>
                    </div>

                    <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:'14px' }}>
                      {/* Operatore */}
                      <div>
                        <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', letterSpacing:'1px', marginBottom:'3px' }}>OPERATORE</div>
                        <div style={{ fontSize:'32px', fontWeight:'900', color:'#0f172a', lineHeight:1 }}>{o.operatore || '—'}</div>
                      </div>

                      {/* Veicolo */}
                      <div style={{ background:'#f1f5f9', borderRadius:'10px', padding:'12px 16px' }}>
                        <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', letterSpacing:'1px', marginBottom:'4px' }}>VEICOLO</div>
                        <div style={{ fontSize:'28px', fontWeight:'900', color:'#1d4ed8', letterSpacing:'3px' }}>{o.targa || '—'}</div>
                        <div style={{ fontSize:'14px', color:'#475569', marginTop:'2px' }}>{o.marca} {o.modello}</div>
                      </div>

                      {/* Tipo lavorazione */}
                      <div>
                        <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', letterSpacing:'1px', marginBottom:'4px' }}>TIPO LAVORAZIONE</div>
                        <div style={{ fontSize:'16px', fontWeight:'600', color:'#1e293b', lineHeight:1.4 }}>{o.note || o.tipoIntervento || '—'}</div>
                      </div>

                      {/* Timer + Consegna */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', paddingTop:'12px', borderTop:'2px solid #f1f5f9' }}>
                        <div>
                          <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', letterSpacing:'1px', marginBottom:'4px' }}>TEMPO</div>
                          <div style={{ fontFamily:'monospace', fontSize:'36px', fontWeight:'900', color: stato==='lavoro' ? '#16a34a' : stato==='pausa' ? '#b45309' : '#9ca3af', letterSpacing:'2px' }}>
                            {fmtDur(ms)}
                          </div>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', letterSpacing:'1px', marginBottom:'4px' }}>CONSEGNA PREVISTA</div>
                          <div style={{ fontSize:'20px', fontWeight:'900', color:'#b45309' }}>{fmtData(o.dataConsegna || o.dataOdl)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ background:'#1e293b', padding:'10px 40px', display:'flex', gap:'24px', alignItems:'center' }}>
          {[['#16a34a','IN LAVORO'],['#b45309','IN PAUSA'],['#6b7280','IN ATTESA'],['#1d4ed8','COMPLETATO']].map(([c,l]) => (
            <div key={l} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:c }} />
              <span style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'700' }}>{l}</span>
            </div>
          ))}
          <div style={{ marginLeft:'auto', fontSize:'11px', color:'#475569' }}>RadaR GAS — Gruppo Auto Scala</div>
        </div>
      </div>
    );
  }

  // ── CONTROLLO MARCATEMPO ──────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'8px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
          <div style={{ fontSize:'11px', fontWeight:'900', color:'#6366f1', letterSpacing:'1px' }}>
            ⏱ MARCATEMPO OFFICINA{!mtVedeTutto && ` — ${sedeUtenteMt.toUpperCase()}`}
          </div>
          {mtVedeTutto && (
            <div style={{ display:'flex', gap:'4px' }}>
              {[['tutte','Tutte'],['agnano','Agnano'],['salerno','Salerno']].map(([k, l]) => (
                <button key={k} onClick={() => setSedeMt(k)}
                  style={{ padding:'5px 12px', borderRadius:'6px', border:`1px solid ${sedeMt === k ? '#6366f1' : 'rgba(128,128,128,0.3)'}`, background: sedeMt === k ? 'rgba(99,102,241,0.15)' : 'transparent', color: sedeMt === k ? '#6366f1' : '#9ca3af', fontWeight:'700', fontSize:'10px', cursor:'pointer' }}>
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => setSchermo(true)}
          style={{ padding:'8px 18px', borderRadius:'8px', border:'2px solid #6366f1', background:'rgba(99,102,241,0.1)', color:'#6366f1', fontWeight:'700', fontSize:'11px', cursor:'pointer' }}>
          🖥 Apri schermo officina
        </button>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {odlList.map(o => {
          const t     = tempi[o.id] || {};
          const stato = t.stato || 'attesa';
          const ms    = calcElapsed(t);
          const info  = statoInfo(stato);
          const isLavoro = stato === 'lavoro', isPausa = stato === 'pausa', isAttesa = stato === 'attesa', isDone = stato === 'completato';
          return (
            <div key={o.id} style={{ background:T.surface||T.card, borderRadius:'12px', border:`1px solid ${isLavoro ? '#10b98140' : T.border}`, padding:'14px 16px' }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'center' }}>
                <div style={{ flex:1, minWidth:'160px' }}>
                  <div style={{ fontSize:'10px', color:info.color, fontWeight:'900', marginBottom:'2px' }}>{info.label}</div>
                  <div style={{ fontSize:'13px', fontWeight:'800', color:T.text }}>{o.operatore || '—'}</div>
                  <div style={{ fontSize:'11px', color:T.textMuted||T.textSub }}>{o.targa && <span style={{marginRight:'8px'}}>🏷 {o.targa}</span>}{o.marca} {o.modello}</div>
                  <div style={{ fontSize:'10px', color:T.textMuted||T.textSub, marginTop:'2px' }}>{o.note||''}</div>
                </div>
                <div style={{ fontFamily:'monospace', fontSize:'28px', fontWeight:'900', color: isLavoro ? '#10b981' : isPausa ? '#f59e0b' : '#475569', minWidth:'110px', textAlign:'center' }}>
                  {fmtDur(ms)}
                </div>
                {!isDone && (
                  <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                    {(isAttesa||isPausa) && <button onClick={() => aggiornaStato(o.id,'inizio')} style={{ padding:'8px 16px', borderRadius:'8px', border:'none', background:'#10b981', color:'#fff', fontWeight:'900', fontSize:'12px', cursor:'pointer' }}>{isPausa ? '▶ Riprendi' : '▶ Inizia'}</button>}
                    {isLavoro && <button onClick={() => aggiornaStato(o.id,'pausa')} style={{ padding:'8px 16px', borderRadius:'8px', border:'none', background:'#f59e0b', color:'#fff', fontWeight:'900', fontSize:'12px', cursor:'pointer' }}>⏸ Pausa</button>}
                    {(isLavoro||isPausa) && <button onClick={() => { if(window.confirm('Segnare come completato?')) aggiornaStato(o.id,'fine'); }} style={{ padding:'8px 16px', borderRadius:'8px', border:'none', background:'#6366f1', color:'#fff', fontWeight:'900', fontSize:'12px', cursor:'pointer' }}>✓ Fine</button>}
                  </div>
                )}
                {isDone && <div style={{ fontSize:'11px', color:'#6366f1', fontWeight:'700' }}>Completato — {fmtDur(t.totaleMs)}</div>}
              </div>
              {(t.eventi||[]).length > 0 && (
                <div style={{ marginTop:'10px', paddingTop:'10px', borderTop:`1px solid ${T.border}`, display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  {t.eventi.map((e,i) => (
                    <span key={i} style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'4px', fontWeight:'700',
                      background: e.tipo==='inizio'?'rgba(16,185,129,0.1)':e.tipo==='pausa'?'rgba(245,158,11,0.1)':'rgba(99,102,241,0.1)',
                      color: e.tipo==='inizio'?'#10b981':e.tipo==='pausa'?'#f59e0b':'#6366f1' }}>
                      {e.tipo==='inizio'?'▶':e.tipo==='pausa'?'⏸':'✓'} {fmtOra(e.ts)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {odlList.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px', color:T.textMuted||T.textSub, fontSize:'13px' }}>
            Nessun ODL aperto — crea un ODL per iniziare il marcatempo
          </div>
        )}
      </div>
    </div>
  );
}


// ─── CRUSCOTTO SERVICE ───────────────────────────────────────────────────────
function CruscottoServiceView({ T, isMobile, db }) {
  const [odlList,  setOdlList]  = React.useState([]);
  const [fatList,  setFatList]  = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [fAnno,    setFAnno]    = React.useState(new Date().getFullYear().toString());
  const [fMese,    setFMese]    = React.useState('');

  React.useEffect(() => {
    // Stesso principio di StoricoODLView — dashboard statistiche, non
    // richiede aggiornamento istantaneo, cache 4 ore + IndexedDB.
    let annullato = false;
    const now = Date.now();
    if (window._radarOdlCruscottoTs && (now - window._radarOdlCruscottoTs) < 4*60*60*1000) {
      setOdlList(window._radarOdlCruscottoList || []);
      setFatList(window._radarFatCruscottoList || []);
      setLoading(false);
      return;
    }
    idbLeggi('odl_cruscotto_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarOdlCruscottoList = cache.odl; window._radarFatCruscottoList = cache.fat; window._radarOdlCruscottoTs = cache.ts;
        setOdlList(cache.odl); setFatList(cache.fat);
        setLoading(false);
        return;
      }
      Promise.all([get(ref(db, 'odl')), get(ref(db, 'service_interventi'))]).then(([snapO, snapF]) => {
        if (annullato) return;
        const odl = snapO.val() ? Object.values(snapO.val()) : [];
        const fat = snapF.val() ? Object.values(snapF.val()) : [];
        const ts = Date.now();
        window._radarOdlCruscottoList = odl; window._radarFatCruscottoList = fat; window._radarOdlCruscottoTs = ts;
        idbScrivi('odl_cruscotto_cache', { odl, fat, ts });
        setOdlList(odl); setFatList(fat);
        setLoading(false);
      });
    });
    return () => { annullato = true; };
  }, []);

  const fmtE = n => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(n||0);

  const filtra = (list, dateField) => list.filter(r => {
    const d = r[dateField] || r.creatoAt || r.data || '';
    if (fAnno && !d.startsWith(fAnno)) return false;
    if (fMese && d.slice(5,7) !== fMese.padStart(2,'0')) return false;
    return true;
  });

  // Fatture filtrate (storico)
  const fatFiltrate = filtra(fatList, 'creatoAt');
  const fatEsterneList = fatFiltrate.filter(f => !f.ordineInterno);
  const fatInterneList = fatFiltrate.filter(f => !!f.ordineInterno);

  // ODL filtrati (aperti/in lavorazione)
  const odlFiltrati = filtra(odlList, 'dataOdl');
  const odlAperti   = odlFiltrati.filter(o => o.stato === 'aperto' || o.stato === 'in_lavorazione');
  const odlChiusi   = odlFiltrati.filter(o => o.stato === 'chiuso');

  const totFat      = fatFiltrate.reduce((s,f)=>s+(f.totaleLordo||0),0);
  const totEsterno  = fatEsterneList.reduce((s,f)=>s+(f.totaleLordo||0),0);
  const totInterno  = fatInterneList.reduce((s,f)=>s+(f.totaleLordo||0),0);
  const totImpon    = fatFiltrate.reduce((s,f)=>s+(f.totaleNetto||0),0);
  const totIVA      = fatFiltrate.reduce((s,f)=>s+(f.totaleIVA||0),0);

  // Per mese (grafico barre semplice)
  const perMese = React.useMemo(() => {
    const mesi = {};
    fatFiltrate.forEach(f => {
      const m = (f.creatoAt||f.data||'').slice(5,7);
      if (!m) return;
      if (!mesi[m]) mesi[m] = { esterno: 0, interno: 0 };
      if (f.ordineInterno) mesi[m].interno += (f.totaleLordo||0);
      else mesi[m].esterno += (f.totaleLordo||0);
    });
    return Object.entries(mesi).sort((a,b)=>a[0].localeCompare(b[0])).map(([m,v])=>({
      mese: new Date(2000,parseInt(m)-1,1).toLocaleString('it-IT',{month:'short'}).toUpperCase(),
      ...v,
      tot: v.esterno + v.interno,
    }));
  }, [fatFiltrate]);

  const maxBar = Math.max(...perMese.map(m=>m.tot), 1);

  const anni = React.useMemo(() => {
    const s = new Set([...odlList,...fatList].map(r=>(r.dataOdl||r.creatoAt||r.data||'').slice(0,4)).filter(Boolean));
    return [...s].sort((a,b)=>b-a);
  }, [odlList, fatList]);

  const iS = { padding:'7px 12px', background:T.inputBg||T.bg, border:`1px solid ${T.border}`, borderRadius:'8px', color:T.text, fontSize:'12px', outline:'none' };
  const cardS = { background:T.surface||T.card, borderRadius:'14px', border:`1px solid ${T.border}`, padding:'16px 20px' };

  if (loading) return <div style={{textAlign:'center',padding:'40px',color:T.textMuted||T.textSub}}>Caricamento...</div>;

  return (
    <div>
      {/* Filtri */}
      <div style={{ display:'flex', gap:'10px', marginBottom:'20px', flexWrap:'wrap', alignItems:'center' }}>
        <select value={fAnno} onChange={e=>setFAnno(e.target.value)} style={iS}>
          <option value="">Tutti gli anni</option>
          {anni.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <select value={fMese} onChange={e=>setFMese(e.target.value)} style={iS}>
          <option value="">Tutti i mesi</option>
          {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m,i)=>(
            <option key={m} value={m}>{new Date(2000,i,1).toLocaleString('it-IT',{month:'long'})}</option>
          ))}
        </select>
        <div style={{fontSize:'11px',color:T.textMuted||T.textSub}}>
          {fatFiltrate.length} fatture · {odlAperti.length} ODL aperti
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {[
          { label:'Fatturato Totale',    val: fmtE(totFat),     color:'#10b981', sub: `${fatFiltrate.length} fatture` },
          { label:'Ordini Esterni',      val: fmtE(totEsterno), color:'#6366f1', sub: `${fatEsterneList.length} fatture` },
          { label:'Ordini Interni',      val: fmtE(totInterno), color:'#f59e0b', sub: `${fatInterneList.length} fatture` },
          { label:'Imponibile',          val: fmtE(totImpon),   color:'#3b82f6', sub: `IVA ${fmtE(totIVA)}` },
        ].map(({ label, val, color, sub }) => (
          <div key={label} style={{ ...cardS, borderLeft:`4px solid ${color}` }}>
            <div style={{fontSize:'9px',color:T.textMuted||T.textSub,fontWeight:'900',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'6px'}}>{label}</div>
            <div style={{fontSize:'20px',fontWeight:'900',color}}>{val}</div>
            <div style={{fontSize:'10px',color:T.textMuted||T.textSub,marginTop:'4px'}}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Grafico barre per mese */}
      {perMese.length > 0 && (
        <div style={{ ...cardS, marginBottom:'20px' }}>
          <div style={{fontSize:'10px',fontWeight:'900',color:'#6366f1',letterSpacing:'1px',marginBottom:'16px'}}>ANDAMENTO MENSILE</div>
          <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', height:'140px', overflowX:'auto', paddingBottom:'4px' }}>
            {perMese.map(({ mese, esterno, interno, tot }) => (
              <div key={mese} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', minWidth:'44px', flex:1 }}>
                <div style={{fontSize:'9px',color:T.textMuted||T.textSub,fontWeight:'700'}}>{fmtE(tot)}</div>
                <div style={{ width:'100%', display:'flex', flexDirection:'column-reverse', gap:'1px' }}>
                  {interno > 0 && (
                    <div style={{ width:'100%', height:`${(interno/maxBar)*110}px`, background:'rgba(245,158,11,0.7)', borderRadius:'3px 3px 0 0', minHeight:'3px' }} title={`Interno: ${fmtE(interno)}`} />
                  )}
                  {esterno > 0 && (
                    <div style={{ width:'100%', height:`${(esterno/maxBar)*110}px`, background:'rgba(99,102,241,0.8)', borderRadius:'3px 3px 0 0', minHeight:'3px' }} title={`Esterno: ${fmtE(esterno)}`} />
                  )}
                </div>
                <div style={{fontSize:'9px',color:T.textMuted||T.textSub}}>{mese}</div>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:'16px',marginTop:'10px',fontSize:'10px'}}>
            <div style={{display:'flex',alignItems:'center',gap:'5px'}}><div style={{width:'10px',height:'10px',borderRadius:'2px',background:'rgba(99,102,241,0.8)'}}/><span style={{color:T.text}}>Esterni</span></div>
            <div style={{display:'flex',alignItems:'center',gap:'5px'}}><div style={{width:'10px',height:'10px',borderRadius:'2px',background:'rgba(245,158,11,0.7)'}}/><span style={{color:T.text}}>Interni</span></div>
          </div>
        </div>
      )}

      {/* Tabella dettaglio */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'14px' }}>
        {/* Esterni */}
        <div style={cardS}>
          <div style={{fontSize:'10px',fontWeight:'900',color:'#6366f1',letterSpacing:'1px',marginBottom:'12px'}}>📋 ORDINI ESTERNI — {fmtE(totEsterno)}</div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px',maxHeight:'300px',overflowY:'auto'}}>
            {fatEsterneList.slice(0,20).map(f=>(
              <div key={f.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 8px',background:T.bg,borderRadius:'8px',fontSize:'11px'}}>
                <div>
                  <div style={{fontWeight:'700',color:T.text}}>{f.numeroFattura ? `${f.numeroFattura} — ` : ''}{f.nomeCliente||'—'}</div>
                  <div style={{color:T.textMuted||T.textSub,fontSize:'10px'}}>{f.targa||''} · {(f.creatoAt||f.data||'').slice(0,10)}</div>
                </div>
                <div style={{fontWeight:'900',color:'#10b981'}}>{fmtE(f.totaleLordo)}</div>
              </div>
            ))}
            {fatEsterneList.length === 0 && <div style={{color:T.textMuted||T.textSub,fontSize:'12px'}}>Nessun ordine esterno</div>}
          </div>
        </div>
        {/* Interni */}
        <div style={cardS}>
          <div style={{fontSize:'10px',fontWeight:'900',color:'#f59e0b',letterSpacing:'1px',marginBottom:'12px'}}>🏢 ORDINI INTERNI — {fmtE(totInterno)}</div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px',maxHeight:'300px',overflowY:'auto'}}>
            {fatInterneList.slice(0,20).map(f=>(
              <div key={f.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 8px',background:T.bg,borderRadius:'8px',fontSize:'11px'}}>
                <div>
                  <div style={{fontWeight:'700',color:T.text}}>{f.numeroFattura ? `${f.numeroFattura} — ` : ''}{f.nomeCliente||'—'}</div>
                  <div style={{color:T.textMuted||T.textSub,fontSize:'10px'}}>{f.targa||''} · {(f.creatoAt||f.data||'').slice(0,10)}</div>
                </div>
                <div style={{fontWeight:'900',color:'#10b981'}}>{fmtE(f.totaleLordo)}</div>
              </div>
            ))}
            {fatInterneList.length === 0 && <div style={{color:T.textMuted||T.textSub,fontSize:'12px'}}>Nessun ordine interno</div>}
          </div>
        </div>
      </div>

      {/* ODL aperti */}
      {odlAperti.length > 0 && (
        <div style={{ ...cardS, marginTop:'14px' }}>
          <div style={{fontSize:'10px',fontWeight:'900',color:'#ef4444',letterSpacing:'1px',marginBottom:'10px'}}>🔧 ODL APERTI / IN LAVORAZIONE — {odlAperti.length}</div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px',maxHeight:'200px',overflowY:'auto'}}>
            {odlAperti.map(o=>(
              <div key={o.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 8px',background:T.bg,borderRadius:'8px',fontSize:'11px'}}>
                <div>
                  <div style={{fontWeight:'700',color:T.text}}>{o.nomeCliente||'—'} {o.ordineInterno && <span style={{fontSize:'9px',background:'rgba(245,158,11,0.15)',border:'1px solid #f59e0b',borderRadius:'4px',padding:'1px 4px',color:'#f59e0b',marginLeft:'4px'}}>INTERNO</span>}</div>
                  <div style={{color:T.textMuted||T.textSub,fontSize:'10px'}}>{o.targa||''} · {o.dataOdl||''} · {o.stato}</div>
                </div>
                <div style={{fontWeight:'900',color:'#6366f1'}}>{fmtE(o.totaleLordo)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── MOVIMENTI MAGAZZINO ─────────────────────────────────────────────────────
function MovimentiMagazzinoView({ T, isMobile, db, ricambi, setRicambi, user }) {
  const [search, setSearch]         = React.useState('');
  const [modalType, setModalType]   = React.useState(null); // 'carico'|'scarico'|'nuovo'
  const [selArticolo, setSelArticolo] = React.useState(null);
  const [qta, setQta]               = React.useState('');
  const [causale, setCausale]        = React.useState('');
  // Nuovi campi — solo per il carico: prezzo di questa specifica bolla,
  // così si può calcolare la media ponderata, più il riferimento al
  // documento (numero bolla, fornitore, data) per tracciabilità.
  const [caricoPrezzoNetto, setCaricoPrezzoNetto] = React.useState('');
  const [caricoSconto, setCaricoSconto]           = React.useState('');
  const [caricoNumeroBolla, setCaricoNumeroBolla] = React.useState('');
  const [caricoFornitore, setCaricoFornitore]     = React.useState('');
  const [caricoDataBolla, setCaricoDataBolla]     = React.useState(new Date().toISOString().split('T')[0]);

  // ─── Flusso "una bolla, più righe insieme" ───────────────────────────
  const [bollaModalOpen, setBollaModalOpen] = React.useState(false);
  const [bollaNumero, setBollaNumero] = React.useState('');
  const [bollaFornitore, setBollaFornitore] = React.useState('');
  const [bollaData, setBollaData] = React.useState(new Date().toISOString().split('T')[0]);
  const [bollaSede, setBollaSede] = React.useState('Agnano');
  // Storico bolle — caricato a chiamata, raggruppa i movimenti di carico
  // per numero bolla (una bolla genera più righe/movimenti, uno per
  // articolo caricato, tutti con lo stesso numeroBolla).
  const [storicoBolleOpen, setStoricoBolleOpen] = React.useState(false);
  const [bolleCaricate, setBolleCaricate] = React.useState(false);
  const [bolleList, setBolleList] = React.useState([]);
  const [loadingBolle, setLoadingBolle] = React.useState(false);
  const caricaStoricoBolle = () => {
    setLoadingBolle(true);
    get(ref(db, 'movimenti_magazzino')).then(snap => {
      const tutti = snap.val() ? Object.entries(snap.val()) : [];
      const righeBolla = tutti.filter(([,m]) => m.causale === 'Bolla' && m.numeroBolla);
      const gruppi = {};
      righeBolla.forEach(([movId, m]) => {
        const chiave = `${m.numeroBolla}__${m.dataBolla}__${m.fornitore}`;
        if (!gruppi[chiave]) {
          gruppi[chiave] = { numeroBolla: m.numeroBolla, dataBolla: m.dataBolla, fornitore: m.fornitore, sede: m.sede || '—', righe: [], totale: 0, movimentiIds: [] };
        }
        gruppi[chiave].righe.push({ codice: m.codice, descrizione: m.descrizione, qta: m.qta, prezzoNetto: m.prezzoNetto, unitaMisura: m.unitaMisura || 'pezzi' });
        gruppi[chiave].totale += (parseFloat(m.qta) || 0) * (parseFloat(m.prezzoNetto) || 0);
        gruppi[chiave].movimentiIds.push(movId);
      });
      const lista = Object.values(gruppi).sort((a, b) => new Date(b.dataBolla || 0) - new Date(a.dataBolla || 0));
      setBolleList(lista);
      setBolleCaricate(true);
      setLoadingBolle(false);
    });
  };

  // Elimina una bolla — riduce di nuovo l'esistenza dei ricambi caricati
  // (annullando l'aumento causato dal carico) e rimuove i movimenti di
  // log collegati. Riservato ad admin/super_admin (controllato nel
  // pulsante, non qui, per coerenza con il resto dell'app).
  const eliminaBolla = async (bolla) => {
    if (!window.confirm(`Eliminare la bolla ${bolla.numeroBolla}? Verranno sottratte di nuovo le quantità caricate (${bolla.righe.length} articoli) dalle scorte attuali. Operazione irreversibile.`)) return;
    for (const riga of bolla.righe) {
      const key = riga.codice.replace(/[./ ]/g, '_');
      const articoloAttuale = ricambi.find(r => r.codice === riga.codice);
      const esistenzaAttuale = parseFloat(articoloAttuale?.esistenza) || 0;
      const nuovaEsistenza = Math.max(0, esistenzaAttuale - (parseFloat(riga.qta) || 0));
      await update(ref(db, `service_ricambi/${key}`), { esistenza: nuovaEsistenza });
    }
    for (const movId of bolla.movimentiIds) {
      await set(ref(db, `movimenti_magazzino/${movId}`), null);
    }
    setRicambi(prev => prev.map(r => {
      const rigaBolla = bolla.righe.find(x => x.codice === r.codice);
      if (!rigaBolla) return r;
      return { ...r, esistenza: Math.max(0, (parseFloat(r.esistenza) || 0) - (parseFloat(rigaBolla.qta) || 0)) };
    }));
    setBolleList(prev => prev.filter(b => b.numeroBolla !== bolla.numeroBolla || b.dataBolla !== bolla.dataBolla || b.fornitore !== bolla.fornitore));
  };
  const [bollaRighe, setBollaRighe] = React.useState([]); // { id, ricerca, articolo, qta, prezzoNetto, sconto }
  const [bollaSaving, setBollaSaving] = React.useState(false);
  // ─── Flusso "vendita a cliente esterno" (carrozziere/officina) ────────
  const [venditaModalOpen, setVenditaModalOpen] = React.useState(false);
  const [venditaCliente, setVenditaCliente] = React.useState('');
  const [venditaIndirizzo, setVenditaIndirizzo] = React.useState('');
  const [venditaPiva, setVenditaPiva] = React.useState('');
  const [venditaData, setVenditaData] = React.useState(new Date().toISOString().split('T')[0]);
  const [venditaSede, setVenditaSede] = React.useState('Agnano');
  const [venditaRighe, setVenditaRighe] = React.useState([]); // { id, ricerca, articolo, qta, prezzoVendita }
  const [venditaSaving, setVenditaSaving] = React.useState(false);
  // Storico vendite esterne — caricato a chiamata, solo quando si apre
  // questa sezione (non ad ogni apertura di Movimenti Magazzino).
  const [storicoVenditeOpen, setStoricoVenditeOpen] = React.useState(false);
  const [venditeCaricate, setVenditeCaricate] = React.useState(false);
  const [venditeList, setVenditeList] = React.useState([]);
  const [loadingVendite, setLoadingVendite] = React.useState(false);
  const caricaVenditeEsterne = () => {
    setLoadingVendite(true);
    get(ref(db, 'vendite_ricambi_esterne')).then(snap => {
      const lista = snap.val() ? Object.values(snap.val()) : [];
      setVenditeList(lista.sort((a, b) => new Date(b.creatoAt||0) - new Date(a.creatoAt||0)));
      setVenditeCaricate(true);
      setLoadingVendite(false);
    });
  };
  const [saving, setSaving]          = React.useState(false);
  const [movimenti, setMovimenti]    = React.useState([]);

  // Form nuovo articolo
  const [nuovoCodice,      setNuovoCodice]      = React.useState('');
  const [nuovaDescrizione, setNuovaDescrizione] = React.useState('');
  const [nuovaEsistenza,   setNuovaEsistenza]   = React.useState('');
  const [nuovoPrezzoNetto, setNuovoPrezzoNetto] = React.useState('');
  const [nuovaUnitaMisura, setNuovaUnitaMisura] = React.useState('pezzi');

  React.useEffect(() => {
    let annullato = false;
    // I movimenti sono un log — una volta creati non cambiano mai (sono
    // eventi passati, non stato corrente) — quindi possiamo scaricare
    // SOLO quelli con timestamp più recente dell'ultimo già visto, invece
    // di riscaricare tutto il nodo ogni volta. Cache persistente in
    // IndexedDB: sopravvive anche a un refresh della pagina.
    idbLeggi('movimenti_magazzino_cache').then(cache => {
      if (annullato) return;
      const movimentiCache = cache?.movimenti || [];
      const ultimoTs = cache?.ultimoTs || null;
      if (movimentiCache.length) setMovimenti(movimentiCache.slice(0, 100));

      const q = ultimoTs
        ? query(ref(db, 'movimenti_magazzino'), orderByChild('timestamp'), startAfter(ultimoTs))
        : query(ref(db, 'movimenti_magazzino'), orderByChild('timestamp'), limitToLast(100)); // primo caricamento in assoluto su questo browser
      get(q).then(snap => {
        if (annullato) return;
        const nuovi = snap.val() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : [];
        if (!nuovi.length && movimentiCache.length) return; // nessuna novità, la cache resta valida
        const uniti = [...nuovi, ...movimentiCache]
          .filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i) // dedup per id
          .sort((a, b) => b.timestamp?.localeCompare?.(a.timestamp) || 0)
          .slice(0, 200); // tetto per non far crescere la cache all'infinito
        const nuovoUltimoTs = uniti[0]?.timestamp || ultimoTs;
        setMovimenti(uniti.slice(0, 100));
        idbScrivi('movimenti_magazzino_cache', { movimenti: uniti, ultimoTs: nuovoUltimoTs });
      });
    });
    return () => { annullato = true; };
  }, []);


  const filtered = React.useMemo(() => {
    const s = search.toLowerCase();
    return ricambi.filter(r =>
      (r.codice||'').toLowerCase().includes(s) ||
      (r.descrizione||'').toLowerCase().includes(s)
    ).slice(0, 80);
  }, [ricambi, search]);

  // Etichetta unità di misura — "pz" (pezzi, default) o "L" (litri).
  const um = (articolo) => articolo?.unitaMisura === 'litri' ? 'L' : 'pz';
  // Esistenza per sede specifica — con fallback per i ricambi caricati
  // prima della divisione per magazzino: se un pezzo non ha mai visto
  // esistenzaAgnano/esistenzaSalerno (nessuno dei due campi impostato),
  // il vecchio campo "esistenza" viene considerato tutto ad Agnano, come
  // deciso. Una volta che una qualsiasi bolla lo tocca, i campi espliciti
  // prendono il sopravvento e questo fallback non serve più per quel pezzo.
  const getEsistenzaSede = (articolo, sede) => {
    if (sede === 'Salerno') return parseFloat(articolo.esistenzaSalerno) || 0;
    if (articolo.esistenzaAgnano !== undefined) return parseFloat(articolo.esistenzaAgnano) || 0;
    if (articolo.esistenzaSalerno === undefined) return parseFloat(articolo.esistenza) || 0;
    return 0;
  };

  const apriMovimento = (articolo, tipo) => {
    setSelArticolo(articolo);
    setModalType(tipo);
    setQta('');
    setCausale('');
    // Pre-compilo col prezzo/sconto attuali — comodo se non è cambiato
    // nulla rispetto all'ultima bolla, basta confermare.
    setCaricoPrezzoNetto(articolo.prezzoNetto ? String(articolo.prezzoNetto) : '');
    setCaricoSconto(articolo.sconto ? String(articolo.sconto) : '');
    setCaricoNumeroBolla('');
    setCaricoFornitore('');
    setCaricoDataBolla(new Date().toISOString().split('T')[0]);
  };

  // Esegue un singolo carico (aggiorna prezzo/media ponderata/esistenza,
  // salva il movimento) — riutilizzata sia dal modale a singolo articolo
  // sia dal flusso "una bolla, più righe insieme".
  const eseguiCarico = async (articolo, delta, prezzoNetto, sconto, bollaInfo) => {
    const key = articolo.codice.replace(/[./ ]/g, '_');
    const sede = bollaInfo.sede === 'Salerno' ? 'Salerno' : 'Agnano'; // default sicuro se non specificata
    // IMPORTANTE: scriviamo SEMPRE entrambe le sedi esplicitamente, anche
    // quella non toccata da questo carico — altrimenti un pezzo mai
    // caricato prima su una sede specifica (col pregresso "nascosto" nel
    // vecchio campo esistenza) perde quel pregresso dalla scomposizione al
    // primo carico sull'ALTRA sede: il totale lo includerebbe ancora, ma
    // sparirebbe dalla vista per sede. Scrivendo sempre entrambi i campi,
    // il pregresso viene "migrato" esplicitamente al primo tocco, qualunque
    // sede si stia caricando in quel momento.
    const esistenzaAgnanoAttuale = getEsistenzaSede(articolo, 'Agnano');
    const esistenzaSalernoAttuale = getEsistenzaSede(articolo, 'Salerno');
    const nuovaAgnano = sede === 'Agnano' ? esistenzaAgnanoAttuale + delta : esistenzaAgnanoAttuale;
    const nuovaSalerno = sede === 'Salerno' ? esistenzaSalernoAttuale + delta : esistenzaSalernoAttuale;
    const esistenzaTotaleAttuale = esistenzaAgnanoAttuale + esistenzaSalernoAttuale;
    const nuovaEsistenzaTotale = nuovaAgnano + nuovaSalerno;
    const prezzoMedioAttuale = parseFloat(articolo.prezzoMedio) || parseFloat(articolo.prezzoNetto) || 0;
    // Il prezzo (media ponderata) resta unico per il pezzo, indipendente
    // dalla sede — lo stesso ricambio ha lo stesso valore di mercato
    // ovunque si trovi fisicamente, cambia solo dove si trova la scorta.
    const nuovoPrezzoMedio = nuovaEsistenzaTotale > 0
      ? ((esistenzaTotaleAttuale * prezzoMedioAttuale) + (delta * prezzoNetto)) / nuovaEsistenzaTotale
      : prezzoNetto;
    const aggiornamenti = {
      esistenzaAgnano:  nuovaAgnano,
      esistenzaSalerno: nuovaSalerno,
      esistenza:    nuovaEsistenzaTotale, // totale complessivo — per tutto il resto del codice che legge questo campo (scarico, display, ecc.)
      prezzoNetto: prezzoNetto,
      prListino:   prezzoNetto / (1 - (sconto || 0) / 100) || prezzoNetto,
      sconto:      sconto || 0,
      prezzoMedio: Math.round(nuovoPrezzoMedio * 100) / 100,
    };
    await update(ref(db, `service_ricambi/${key}`), aggiornamenti);

    const movId = `MOV-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
    const movimento = {
      codice: articolo.codice, descrizione: articolo.descrizione, tipo: 'carico',
      qta: delta, esistenzaPre: esistenzaTotaleAttuale, esistenzaPost: nuovaEsistenzaTotale,
      causale: bollaInfo.causale || '', timestamp: new Date().toISOString(),
      utente: `${user.nome} ${user.cognome}`,
      prezzoNetto, sconto: sconto || 0, prezzoMedioPost: aggiornamenti.prezzoMedio,
      numeroBolla: bollaInfo.numeroBolla || '', fornitore: bollaInfo.fornitore || '', dataBolla: bollaInfo.dataBolla || '',
      sede: sede, unitaMisura: articolo.unitaMisura || 'pezzi',
    };
    await set(ref(db, `movimenti_magazzino/${movId}`), movimento);

    setRicambi(prev => prev.map(r => r.codice === articolo.codice ? { ...r, ...aggiornamenti } : r));
    setMovimenti(prev => [{ id: movId, ...movimento }, ...prev].slice(0, 100));
    idbLeggi('movimenti_magazzino_cache').then(cache => {
      const aggiornati = [{ id: movId, ...movimento }, ...(cache?.movimenti || [])].slice(0, 200);
      idbScrivi('movimenti_magazzino_cache', { movimenti: aggiornati, ultimoTs: movimento.timestamp });
    });
    return aggiornamenti;
  };

  const apriBollaModal = () => {
    setBollaNumero('');
    setBollaFornitore('');
    setBollaData(new Date().toISOString().split('T')[0]);
    setBollaSede('Agnano');
    setBollaRighe([{ id: Date.now(), ricerca: '', articolo: null, qta: '', prezzoNetto: '', sconto: '' }]);
    setBollaModalOpen(true);
  };
  const aggiungiRigaBolla = () => {
    setBollaRighe(prev => [...prev, { id: Date.now(), ricerca: '', articolo: null, qta: '', prezzoNetto: '', sconto: '' }]);
  };
  const rimuoviRigaBolla = (id) => setBollaRighe(prev => prev.filter(r => r.id !== id));
  const aggiornaRigaBolla = (id, campo, valore) => {
    setBollaRighe(prev => prev.map(r => {
      if (r.id !== id) return r;
      const aggiornata = { ...r, [campo]: valore };
      // Se si seleziona un articolo, precompila prezzo/sconto con l'ultimo noto
      if (campo === 'articolo' && valore) {
        aggiornata.prezzoNetto = valore.prezzoNetto ? String(valore.prezzoNetto) : '';
        aggiornata.sconto = valore.sconto ? String(valore.sconto) : '';
        aggiornata.ricerca = `${valore.codice} — ${valore.descrizione}`;
      }
      return aggiornata;
    }));
  };
  const righeValide = React.useMemo(() =>
    bollaRighe.filter(r => r.articolo && r.qta && parseFloat(r.qta) > 0 && r.prezzoNetto && parseFloat(r.prezzoNetto) >= 0)
  , [bollaRighe]);

  const salvaBolla = async () => {
    if (!righeValide.length) { alert('Aggiungi almeno una riga valida (articolo, quantità e prezzo).'); return; }
    setBollaSaving(true);
    const bollaInfo = { numeroBolla: bollaNumero.trim(), fornitore: bollaFornitore.trim(), dataBolla: bollaData, sede: bollaSede, causale: 'Bolla' };
    // In sequenza, non in parallelo — ogni riga aggiorna la media
    // ponderata partendo dall'esistenza risultante dalla riga precedente.
    // Uso uno snapshot locale (non lo state React, che si aggiorna in modo
    // asincrono) per gestire correttamente il caso raro di due righe con
    // lo stesso codice nella stessa bolla.
    let ricambiLocali = [...ricambi];
    for (const riga of righeValide) {
      const articoloAggiornato = ricambiLocali.find(r => r.codice === riga.articolo.codice) || riga.articolo;
      const aggiornamenti = await eseguiCarico(articoloAggiornato, parseFloat(riga.qta), parseFloat(riga.prezzoNetto) || 0, parseFloat(riga.sconto) || 0, bollaInfo);
      ricambiLocali = ricambiLocali.map(r => r.codice === riga.articolo.codice ? { ...r, ...aggiornamenti } : r);
    }
    setBollaSaving(false);
    setBollaModalOpen(false);
    // Stampa automatica del report appena creata — stesso schema già
    // usato per la vendita a cliente esterno.
    stampaBolla({
      numeroBolla: bollaInfo.numeroBolla, dataBolla: bollaInfo.dataBolla, fornitore: bollaInfo.fornitore, sede: bollaInfo.sede,
      righe: righeValide.map(r => ({ codice: r.articolo.codice, descrizione: r.articolo.descrizione, qta: parseFloat(r.qta), prezzoNetto: parseFloat(r.prezzoNetto) || 0, unitaMisura: r.articolo.unitaMisura || 'pezzi' })),
      totale: righeValide.reduce((s, r) => s + parseFloat(r.qta) * (parseFloat(r.prezzoNetto) || 0), 0),
    });
  };

  // ─── Vendita a cliente esterno (carrozziere/officina) ──────────────────
  const apriVenditaModal = () => {
    setVenditaCliente(''); setVenditaIndirizzo(''); setVenditaPiva('');
    setVenditaData(new Date().toISOString().split('T')[0]);
    setVenditaSede('Agnano');
    setVenditaRighe([{ id: Date.now(), ricerca: '', articolo: null, qta: '', prezzoVendita: '' }]);
    setVenditaModalOpen(true);
  };
  const aggiungiRigaVendita = () => {
    setVenditaRighe(prev => [...prev, { id: Date.now(), ricerca: '', articolo: null, qta: '', prezzoVendita: '' }]);
  };
  const rimuoviRigaVendita = (id) => setVenditaRighe(prev => prev.filter(r => r.id !== id));
  const aggiornaRigaVendita = (id, campo, valore) => {
    setVenditaRighe(prev => prev.map(r => {
      if (r.id !== id) return r;
      const aggiornata = { ...r, [campo]: valore };
      if (campo === 'articolo' && valore) {
        // Prezzo di vendita precompilato dal prezzo netto (il costo) —
        // modificabile: qui è il prezzo di VENDITA al cliente esterno,
        // non il costo di acquisto.
        aggiornata.prezzoVendita = valore.prezzoNetto ? String(valore.prezzoNetto) : '';
        aggiornata.ricerca = `${valore.codice} — ${valore.descrizione}`;
      }
      return aggiornata;
    }));
  };
  const righeVenditaValide = React.useMemo(() =>
    venditaRighe.filter(r => r.articolo && r.qta && parseFloat(r.qta) > 0 && r.prezzoVendita && parseFloat(r.prezzoVendita) >= 0)
  , [venditaRighe]);

  const eseguiScaricoVendita = async (articolo, delta, prezzoVendita, clienteInfo, sede) => {
    const key = articolo.codice.replace(/[./ ]/g, '_');
    const sedeScarico = sede === 'Salerno' ? 'Salerno' : 'Agnano';
    const esistenzaAgnanoAttuale = getEsistenzaSede(articolo, 'Agnano');
    const esistenzaSalernoAttuale = getEsistenzaSede(articolo, 'Salerno');
    const esistenzaTotaleAttuale = esistenzaAgnanoAttuale + esistenzaSalernoAttuale;
    const nuovaAgnano = sedeScarico === 'Agnano' ? Math.max(0, esistenzaAgnanoAttuale - delta) : esistenzaAgnanoAttuale;
    const nuovaSalerno = sedeScarico === 'Salerno' ? Math.max(0, esistenzaSalernoAttuale - delta) : esistenzaSalernoAttuale;
    const nuovaEsistenzaTotale = nuovaAgnano + nuovaSalerno;
    await update(ref(db, `service_ricambi/${key}`), { esistenzaAgnano: nuovaAgnano, esistenzaSalerno: nuovaSalerno, esistenza: nuovaEsistenzaTotale });

    const movId = `MOV-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
    const movimento = {
      codice: articolo.codice, descrizione: articolo.descrizione, tipo: 'scarico',
      qta: delta, esistenzaPre: esistenzaTotaleAttuale, esistenzaPost: nuovaEsistenzaTotale,
      causale: `Vendita esterna — ${clienteInfo.cliente}`, timestamp: new Date().toISOString(),
      utente: `${user.nome} ${user.cognome}`,
      prezzoVendita, clienteEsterno: clienteInfo.cliente, sede: sedeScarico,
    };
    await set(ref(db, `movimenti_magazzino/${movId}`), movimento);

    setRicambi(prev => prev.map(r => r.codice === articolo.codice ? { ...r, esistenzaAgnano: nuovaAgnano, esistenzaSalerno: nuovaSalerno, esistenza: nuovaEsistenzaTotale } : r));
    setMovimenti(prev => [{ id: movId, ...movimento }, ...prev].slice(0, 100));
    idbLeggi('movimenti_magazzino_cache').then(cache => {
      const aggiornati = [{ id: movId, ...movimento }, ...(cache?.movimenti || [])].slice(0, 200);
      idbScrivi('movimenti_magazzino_cache', { movimenti: aggiornati, ts: Date.now() });
    });
    return nuovaEsistenzaTotale;
  };

  const salvaVendita = async () => {
    if (!venditaCliente.trim()) { alert('Inserisci il nome del cliente esterno.'); return; }
    if (!righeVenditaValide.length) { alert('Aggiungi almeno una riga valida (articolo, quantità e prezzo).'); return; }
    // Controllo scorte insufficienti nella sede scelta — evita di
    // scaricare più di quanto sia realmente disponibile LÌ (una scorta
    // sufficiente nell'altra sede non basta, il pezzo è fisicamente altrove).
    const insufficienti = righeVenditaValide.filter(r => getEsistenzaSede(r.articolo, venditaSede) < parseFloat(r.qta));
    if (insufficienti.length) {
      const nomi = insufficienti.map(r => r.articolo.codice).join(', ');
      if (!window.confirm(`Attenzione: scorte insufficienti a ${venditaSede} per ${nomi}. La quantità scaricata risulterà negativa/azzerata. Procedere comunque?`)) return;
    }
    setVenditaSaving(true);
    const clienteInfo = { cliente: venditaCliente.trim(), indirizzo: venditaIndirizzo.trim(), piva: venditaPiva.trim() };
    let ricambiLocali = [...ricambi];
    for (const riga of righeVenditaValide) {
      const articoloAggiornato = ricambiLocali.find(r => r.codice === riga.articolo.codice) || riga.articolo;
      const nuovaEsistenza = await eseguiScaricoVendita(articoloAggiornato, parseFloat(riga.qta), parseFloat(riga.prezzoVendita) || 0, clienteInfo, venditaSede);
      ricambiLocali = ricambiLocali.map(r => r.codice === riga.articolo.codice ? { ...r, esistenza: nuovaEsistenza } : r);
    }
    // Salva un record della vendita per tracciabilità e per poterla
    // ristampare come preventivo/documento di vendita.
    const venditaId = `VEND-${Date.now()}`;
    const vendita = {
      id: venditaId,
      cliente: clienteInfo.cliente, indirizzoCliente: clienteInfo.indirizzo, pivaCliente: clienteInfo.piva,
      data: venditaData,
      righe: righeVenditaValide.map(r => ({ codice: r.articolo.codice, descrizione: r.articolo.descrizione, qta: parseFloat(r.qta), prezzoVendita: parseFloat(r.prezzoVendita) })),
      totale: righeVenditaValide.reduce((s, r) => s + parseFloat(r.qta) * parseFloat(r.prezzoVendita), 0),
      creatoAt: new Date().toISOString(),
      creatoDa: user?.nome ? `${user.nome} ${user.cognome || ''}`.trim() : '',
    };
    await set(ref(db, `vendite_ricambi_esterne/${venditaId}`), vendita);
    setVenditaSaving(false);
    setVenditaModalOpen(false);
    stampaVenditaRicambi(vendita);
  };


  const salvaMovimento = async () => {
    if (!selArticolo || !qta || parseFloat(qta) <= 0) return;
    if (modalType === 'carico' && (!caricoPrezzoNetto || parseFloat(caricoPrezzoNetto) < 0)) {
      alert('Inserisci il prezzo netto di questo carico.');
      return;
    }
    setSaving(true);
    const delta = parseFloat(qta);

    if (modalType === 'carico') {
      await eseguiCarico(selArticolo, delta, parseFloat(caricoPrezzoNetto) || 0, parseFloat(caricoSconto) || 0, {
        causale, numeroBolla: caricoNumeroBolla.trim(), fornitore: caricoFornitore.trim(), dataBolla: caricoDataBolla,
      });
    } else {
      // Scarico: nessun prezzo, solo quantità (comportamento invariato).
      const key = selArticolo.codice.replace(/[./ ]/g, '_');
      const esistenzaAttuale = parseFloat(selArticolo.esistenza) || 0;
      const nuovaEsistenzaVal = Math.max(0, esistenzaAttuale - delta);
      await update(ref(db, `service_ricambi/${key}`), { esistenza: nuovaEsistenzaVal });
      const movId = `MOV-${Date.now()}`;
      const movimento = {
        codice: selArticolo.codice, descrizione: selArticolo.descrizione, tipo: 'scarico',
        qta: delta, esistenzaPre: esistenzaAttuale, esistenzaPost: nuovaEsistenzaVal,
        causale: causale || '', timestamp: new Date().toISOString(), utente: `${user.nome} ${user.cognome}`,
      };
      await set(ref(db, `movimenti_magazzino/${movId}`), movimento);
      setRicambi(prev => prev.map(r => r.codice === selArticolo.codice ? { ...r, esistenza: nuovaEsistenzaVal } : r));
      setMovimenti(prev => [{ id: movId, ...movimento }, ...prev].slice(0, 100));
      idbLeggi('movimenti_magazzino_cache').then(cache => {
        const aggiornati = [{ id: movId, ...movimento }, ...(cache?.movimenti || [])].slice(0, 200);
        idbScrivi('movimenti_magazzino_cache', { movimenti: aggiornati, ultimoTs: movimento.timestamp });
      });
    }
    setSaving(false);
    setModalType(null);
    setSelArticolo(null);
  };

  const salvaNuovoArticolo = async () => {
    if (!nuovoCodice || !nuovaDescrizione) return;
    setSaving(true);
    const key = nuovoCodice.trim().replace(/[./ ]/g, '_');
    const esistenzaIniziale = parseFloat(nuovaEsistenza) || 0;
    const articolo = {
      codice:      nuovoCodice.trim().toUpperCase(),
      descrizione: nuovaDescrizione.trim(),
      esistenza:   esistenzaIniziale,
      // La quantità iniziale va ad Agnano di default — stessa convenzione
      // già usata per il pregresso caricato prima della divisione per sede.
      esistenzaAgnano: esistenzaIniziale,
      unitaMisura: nuovaUnitaMisura,
      prezzoNetto: parseFloat(nuovoPrezzoNetto) || 0,
      prListino:   parseFloat(nuovoPrezzoNetto) || 0,
      sconto:      0,
      importedAt:  new Date().toISOString(),
    };
    await set(ref(db, `service_ricambi/${key}`), articolo);
    setRicambi(prev => [...prev, articolo]);
    setModalType(null);
    setNuovoCodice(''); setNuovaDescrizione(''); setNuovaEsistenza(''); setNuovoPrezzoNetto(''); setNuovaUnitaMisura('pezzi');
    setSaving(false);
  };

  const iS = { padding: '10px 12px', background: T.inputBg || T.bg, border: `1px solid ${T.border2||T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Cerca codice o descrizione..."
            style={{ ...iS, marginBottom: 0 }} />
        </div>
        <button onClick={apriBollaModal}
          style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: '#10b981', color: '#fff', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
          🧾 Nuovo Carico da Bolla
        </button>
        <button onClick={apriVenditaModal}
          style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: '#f59e0b', color: '#fff', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
          🏪 Vendita a Cliente Esterno
        </button>
        <button onClick={() => { setStoricoVenditeOpen(true); if (!venditeCaricate) caricaVenditeEsterne(); }}
          style={{ padding: '10px 18px', borderRadius: '10px', border: `2px solid ${T.border2||T.border}`, cursor: 'pointer', background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
          📋 Storico Vendite Esterne
        </button>
        <button onClick={() => { setStoricoBolleOpen(true); if (!bolleCaricate) caricaStoricoBolle(); }}
          style={{ padding: '10px 18px', borderRadius: '10px', border: `2px solid ${T.border2||T.border}`, cursor: 'pointer', background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
          🧾 Storico Bolle
        </button>
        <button onClick={() => setModalType('nuovo')}
          style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: '#6366f1', color: '#fff', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
          + Nuovo Articolo
        </button>
      </div>

      {/* Log ultimi movimenti */}
      {movimenti.length > 0 && (
        <div style={{ background: T.surface||T.card, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 14px', marginBottom: '16px' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '8px' }}>ULTIMI MOVIMENTI</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '120px', overflowY: 'auto' }}>
            {movimenti.slice(0, 10).map(m => (
              <div key={m.id} style={{ display: 'flex', gap: '8px', fontSize: '11px', alignItems: 'center' }}>
                <span style={{ color: m.tipo === 'carico' ? '#10b981' : '#ef4444', fontWeight: '900', minWidth: '16px' }}>
                  {m.tipo === 'carico' ? '▲' : '▼'}
                </span>
                <span style={{ color: '#6366f1', fontWeight: '700', minWidth: '80px' }}>{m.codice}</span>
                <span style={{ color: T.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.descrizione}</span>
                <span style={{ color: m.tipo === 'carico' ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                  {m.tipo === 'carico' ? '+' : '-'}{m.qta}
                </span>
                {m.numeroBolla && (
                  <span style={{ color: T.textMuted||T.textSub, fontSize: '10px' }}>
                    📄 {m.numeroBolla}{m.fornitore ? ` · ${m.fornitore}` : ''}
                  </span>
                )}
                <span style={{ color: T.textMuted||T.textSub, fontSize: '10px', minWidth: '70px', textAlign: 'right' }}>
                  {new Date(m.timestamp).toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista articoli */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {filtered.map((r, i) => (
          <div key={r.codice+i} style={{ background: T.surface||T.card, borderRadius: '10px', border: `1px solid ${T.border}`, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <div style={{ fontSize: '10px', color: '#6366f1', fontWeight: '900' }}>{r.codice}</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: T.text }}>{r.descrizione}</div>
            </div>
            <div style={{ textAlign: 'center', minWidth: '60px' }}>
              <div style={{ fontSize: '9px', color: T.textMuted||T.textSub }}>ESISTENZA</div>
              <div style={{ fontSize: '16px', fontWeight: '900', color: (parseFloat(r.esistenza)||0) > 0 ? '#10b981' : '#ef4444' }}>
                {parseFloat(r.esistenza)||0}
              </div>
            </div>
            <div style={{ textAlign: 'center', minWidth: '70px' }}>
              <div style={{ fontSize: '9px', color: T.textMuted||T.textSub }}>PREZZO NETTO</div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>
                €{(parseFloat(r.prezzoNetto)||0).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => apriMovimento(r, 'carico')}
                style={{ padding: '7px 14px', borderRadius: '8px', border: '2px solid #10b981', background: 'transparent', color: '#10b981', fontWeight: '900', fontSize: '11px', cursor: 'pointer' }}>
                ▲ Carico
              </button>
              <button onClick={() => apriMovimento(r, 'scarico')}
                style={{ padding: '7px 14px', borderRadius: '8px', border: '2px solid #ef4444', background: 'transparent', color: '#ef4444', fontWeight: '900', fontSize: '11px', cursor: 'pointer' }}>
                ▼ Scarico
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted||T.textSub, fontSize: '13px' }}>
            Nessun articolo trovato
          </div>
        )}
      </div>

      {/* Modal carico/scarico */}
      {(modalType === 'carico' || modalType === 'scarico') && selArticolo && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setModalType(null); }}>
          <div style={{ background: T.card||T.surface, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '400px', border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: '16px', fontWeight: '900', color: T.text, marginBottom: '4px' }}>
              {modalType === 'carico' ? '▲ Carico' : '▼ Scarico'}
            </div>
            <div style={{ fontSize: '12px', color: '#6366f1', fontWeight: '700', marginBottom: '4px' }}>{selArticolo.codice}</div>
            <div style={{ fontSize: '13px', color: T.text, marginBottom: '4px' }}>{selArticolo.descrizione}</div>
            <div style={{ fontSize: '11px', color: T.textMuted||T.textSub, marginBottom: '20px' }}>
              Esistenza attuale: <strong style={{ color: T.text }}>{parseFloat(selArticolo.esistenza)||0}</strong>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '10px', color: modalType==='carico'?'#10b981':'#ef4444', fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>
                QUANTITÀ *
              </div>
              <input type="number" min="0.01" step="0.01" value={qta} onChange={e => setQta(e.target.value)}
                placeholder="Es. 5"
                style={{ ...iS, border: `2px solid ${modalType==='carico'?'#10b981':'#ef4444'}` }} />
            </div>
            {modalType === 'carico' && (
              <>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', color: '#10b981', fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>
                      PREZZO NETTO (€) *
                    </div>
                    <input type="number" min="0" step="0.01" value={caricoPrezzoNetto} onChange={e => setCaricoPrezzoNetto(e.target.value)}
                      placeholder="Es. 12.50" style={iS} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>
                      SCONTO (%)
                    </div>
                    <input type="number" min="0" max="100" step="0.1" value={caricoSconto} onChange={e => setCaricoSconto(e.target.value)}
                      placeholder="Es. 10" style={iS} />
                  </div>
                </div>
                {qta && caricoPrezzoNetto && (
                  <div style={{ marginBottom: '14px', padding: '8px 12px', background: 'rgba(16,185,129,0.1)', borderRadius: '8px', fontSize: '11px', color: '#10b981', fontWeight: '700' }}>
                    Prezzo medio ponderato dopo questo carico: €{(() => {
                      const esistenzaAttuale = parseFloat(selArticolo.esistenza) || 0;
                      const delta = parseFloat(qta) || 0;
                      const prezzoMedioAttuale = parseFloat(selArticolo.prezzoMedio) || parseFloat(selArticolo.prezzoNetto) || 0;
                      const prezzoQuestoCarico = parseFloat(caricoPrezzoNetto) || 0;
                      const media = (esistenzaAttuale + delta) > 0
                        ? ((esistenzaAttuale * prezzoMedioAttuale) + (delta * prezzoQuestoCarico)) / (esistenzaAttuale + delta)
                        : prezzoQuestoCarico;
                      return media.toLocaleString('it-IT', { minimumFractionDigits: 2 });
                    })()}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>
                      N. BOLLA
                    </div>
                    <input value={caricoNumeroBolla} onChange={e => setCaricoNumeroBolla(e.target.value)}
                      placeholder="Es. 1234/2026" style={iS} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>
                      DATA BOLLA
                    </div>
                    <input type="date" value={caricoDataBolla} onChange={e => setCaricoDataBolla(e.target.value)} style={iS} />
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>
                    FORNITORE
                  </div>
                  <input value={caricoFornitore} onChange={e => setCaricoFornitore(e.target.value)}
                    placeholder="Es. Ricambi Auto Srl" style={iS} />
                </div>
              </>
            )}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>CAUSALE</div>
              <input value={causale} onChange={e => setCausale(e.target.value)}
                placeholder="Es. Acquisto, Reso fornitore, Inventario..."
                style={iS} />
            </div>
            {qta && parseFloat(qta) > 0 && (
              <div style={{ marginBottom: '16px', padding: '8px 12px', background: 'rgba(99,102,241,0.1)', borderRadius: '8px', fontSize: '12px', color: '#6366f1', fontWeight: '700' }}>
                Nuova esistenza: {modalType === 'carico'
                  ? (parseFloat(selArticolo.esistenza)||0) + parseFloat(qta)
                  : Math.max(0, (parseFloat(selArticolo.esistenza)||0) - parseFloat(qta))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setModalType(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: `1px solid ${T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', cursor: 'pointer' }}>
                Annulla
              </button>
              <button onClick={salvaMovimento} disabled={saving || !qta || parseFloat(qta) <= 0 || (modalType === 'carico' && !caricoPrezzoNetto)}
                style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: modalType==='carico'?'#10b981':'#ef4444', color: '#fff', fontWeight: '900', cursor: 'pointer', opacity: (!qta||parseFloat(qta)<=0||(modalType === 'carico' && !caricoPrezzoNetto))?0.5:1 }}>
                {saving ? '...' : `Conferma ${modalType === 'carico' ? 'Carico' : 'Scarico'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nuovo articolo */}
      {modalType === 'nuovo' && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setModalType(null); }}>
          <div style={{ background: T.card||T.surface, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '440px', border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: '16px', fontWeight: '900', color: T.text, marginBottom: '20px' }}>+ Nuovo Articolo</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '10px', color: '#6366f1', fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>CODICE *</div>
                <input value={nuovoCodice} onChange={e => setNuovoCodice(e.target.value.toUpperCase())}
                  placeholder="Es. F026407022" style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>ESISTENZA INIZIALE</div>
                <input type="number" value={nuovaEsistenza} onChange={e => setNuovaEsistenza(e.target.value)}
                  placeholder="0" style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>UNITÀ DI MISURA</div>
                <select value={nuovaUnitaMisura} onChange={e => setNuovaUnitaMisura(e.target.value)} style={iS}>
                  <option value="pezzi">Pezzi</option>
                  <option value="litri">Litri</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '10px', color: '#6366f1', fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>DESCRIZIONE *</div>
              <input value={nuovaDescrizione} onChange={e => setNuovaDescrizione(e.target.value)}
                placeholder="Es. FILTRO OLIO BOSCH" style={{ ...iS, textTransform: 'uppercase' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '6px' }}>PREZZO NETTO (€)</div>
              <input type="number" value={nuovoPrezzoNetto} onChange={e => setNuovoPrezzoNetto(e.target.value)}
                placeholder="0.00" style={iS} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setModalType(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: `1px solid ${T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', cursor: 'pointer' }}>
                Annulla
              </button>
              <button onClick={salvaNuovoArticolo} disabled={saving || !nuovoCodice || !nuovaDescrizione}
                style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: '900', cursor: 'pointer', opacity: (!nuovoCodice||!nuovaDescrizione)?0.5:1 }}>
                {saving ? '...' : '✓ Salva Articolo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal "Nuovo Carico da Bolla" — più righe insieme, un'unica bolla */}
      {bollaModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setBollaModalOpen(false); }}>
          <div style={{ background: T.card||T.surface, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: '16px', fontWeight: '900', color: T.text, marginBottom: '4px' }}>🧾 Nuovo Carico da Bolla</div>
            <div style={{ fontSize: '11px', color: T.textMuted||T.textSub, marginBottom: '18px' }}>
              Inserisci i dati della bolla una volta sola, poi aggiungi tutti i ricambi che contiene.
            </div>

            {/* Header bolla */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '18px', padding: '14px', background: T.bg, borderRadius: '12px' }}>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>N. BOLLA</div>
                <input value={bollaNumero} onChange={e => setBollaNumero(e.target.value)} placeholder="Es. 1234/2026" style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>FORNITORE</div>
                <input value={bollaFornitore} onChange={e => setBollaFornitore(e.target.value)} placeholder="Es. Ricambi Auto Srl" style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>DATA BOLLA</div>
                <input type="date" value={bollaData} onChange={e => setBollaData(e.target.value)} style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>MAGAZZINO DESTINAZIONE</div>
                <select value={bollaSede} onChange={e => setBollaSede(e.target.value)} style={iS}>
                  <option value="Agnano">Agnano</option>
                  <option value="Salerno">Salerno</option>
                </select>
              </div>
            </div>

            {/* Righe */}
            <div style={{ fontSize: '10px', color: '#10b981', fontWeight: '900', letterSpacing:'1px', marginBottom: '8px' }}>RICAMBI IN QUESTA BOLLA</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
              {bollaRighe.map((riga, idx) => {
                const suggerimenti = riga.ricerca && !riga.articolo
                  ? ricambi.filter(r => (r.codice||'').toLowerCase().includes(riga.ricerca.toLowerCase()) || (r.descrizione||'').toLowerCase().includes(riga.ricerca.toLowerCase())).slice(0, 6)
                  : [];
                return (
                  <div key={riga.id} style={{ padding: '12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}`, position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: riga.articolo ? '10px' : '0' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input value={riga.ricerca}
                          onChange={e => aggiornaRigaBolla(riga.id, 'ricerca', e.target.value)}
                          onFocus={() => { if (riga.articolo) aggiornaRigaBolla(riga.id, 'articolo', null); }}
                          placeholder="🔍 Cerca codice o descrizione ricambio..."
                          style={{ ...iS, border: `2px solid ${riga.articolo ? '#10b981' : T.border2||T.border}` }} />
                        {suggerimenti.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.card||T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', marginTop: '4px', zIndex: 10, maxHeight: '160px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            {suggerimenti.map(s => (
                              <div key={s.codice} onClick={() => aggiornaRigaBolla(riga.id, 'articolo', s)}
                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: `1px solid ${T.border}` }}
                                onMouseEnter={e => e.currentTarget.style.background = T.bg}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <span style={{ color: '#6366f1', fontWeight: '700' }}>{s.codice}</span> — {s.descrizione}
                                <span style={{ float: 'right', color: T.textMuted, fontSize: '9px' }}>Agnano {getEsistenzaSede(s,'Agnano')} {um(s)} · Salerno {getEsistenzaSede(s,'Salerno')} {um(s)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {bollaRighe.length > 1 && (
                        <button onClick={() => rimuoviRigaBolla(riga.id)}
                          style={{ padding: '10px 12px', borderRadius: '8px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontWeight: '900', flexShrink: 0 }}>
                          ✕
                        </button>
                      )}
                    </div>
                    {riga.articolo && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, marginBottom: '4px' }}>QUANTITÀ ({um(riga.articolo)})</div>
                          <input type="number" min="0.01" step="0.01" value={riga.qta} onChange={e => aggiornaRigaBolla(riga.id, 'qta', e.target.value)} placeholder="Es. 5" style={iS} />
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, marginBottom: '4px' }}>PREZZO NETTO (€)</div>
                          <input type="number" min="0" step="0.01" value={riga.prezzoNetto} onChange={e => aggiornaRigaBolla(riga.id, 'prezzoNetto', e.target.value)} placeholder="Es. 12.50" style={iS} />
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, marginBottom: '4px' }}>SCONTO (%)</div>
                          <input type="number" min="0" max="100" step="0.1" value={riga.sconto} onChange={e => aggiornaRigaBolla(riga.id, 'sconto', e.target.value)} placeholder="0" style={iS} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button onClick={aggiungiRigaBolla}
              style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `2px dashed ${T.border2||T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginBottom: '18px' }}>
              + Aggiungi un altro ricambio
            </button>

            {righeValide.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', background: 'rgba(16,185,129,0.1)', borderRadius: '10px', fontSize: '12px', color: '#10b981', fontWeight: '700' }}>
                {righeValide.length} rig{righeValide.length === 1 ? 'a pronta' : 'he pronte'} da salvare — totale valore bolla: €{righeValide.reduce((s, r) => s + parseFloat(r.qta) * parseFloat(r.prezzoNetto), 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setBollaModalOpen(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: `1px solid ${T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', cursor: 'pointer' }}>
                Annulla
              </button>
              <button onClick={salvaBolla} disabled={bollaSaving || righeValide.length === 0}
                style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: '#10b981', color: '#fff', fontWeight: '900', cursor: 'pointer', opacity: (bollaSaving || righeValide.length === 0) ? 0.5 : 1 }}>
                {bollaSaving ? 'Salvataggio in corso...' : `✓ Conferma Bolla (${righeValide.length} rig${righeValide.length === 1 ? 'a' : 'he'})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal "Vendita a Cliente Esterno" — carrozziere/officina che compra ricambi */}
      {venditaModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setVenditaModalOpen(false); }}>
          <div style={{ background: T.card||T.surface, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: '16px', fontWeight: '900', color: T.text, marginBottom: '4px' }}>🏪 Vendita a Cliente Esterno</div>
            <div style={{ fontSize: '11px', color: T.textMuted||T.textSub, marginBottom: '18px' }}>
              Vendita di ricambi dal magazzino a un carrozziere/officina esterna, senza legame con un veicolo/ODL.
            </div>

            {/* Header cliente */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '18px', padding: '14px', background: T.bg, borderRadius: '12px' }}>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>CLIENTE *</div>
                <input value={venditaCliente} onChange={e => setVenditaCliente(e.target.value)} placeholder="Es. Carrozzeria Rossi Srl" style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>INDIRIZZO / P.IVA</div>
                <input value={venditaIndirizzo} onChange={e => setVenditaIndirizzo(e.target.value)} placeholder="Via Roma 1, Napoli" style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>DATA</div>
                <input type="date" value={venditaData} onChange={e => setVenditaData(e.target.value)} style={iS} />
              </div>
              <div>
                <div style={{ fontSize: '9px', color: T.textMuted||T.textSub, fontWeight: '900', letterSpacing:'1px', marginBottom: '5px' }}>MAGAZZINO DI SCARICO</div>
                <select value={venditaSede} onChange={e => setVenditaSede(e.target.value)} style={iS}>
                  <option value="Agnano">Agnano</option>
                  <option value="Salerno">Salerno</option>
                </select>
              </div>
            </div>

            {/* Righe */}
            <div style={{ fontSize: '10px', color: '#f59e0b', fontWeight: '900', letterSpacing:'1px', marginBottom: '8px' }}>RICAMBI VENDUTI</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
              {venditaRighe.map((riga) => {
                const suggerimenti = riga.ricerca && !riga.articolo
                  ? ricambi.filter(r => (r.codice||'').toLowerCase().includes(riga.ricerca.toLowerCase()) || (r.descrizione||'').toLowerCase().includes(riga.ricerca.toLowerCase())).slice(0, 6)
                  : [];
                return (
                  <div key={riga.id} style={{ padding: '12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}`, position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: riga.articolo ? '10px' : '0' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input value={riga.ricerca}
                          onChange={e => aggiornaRigaVendita(riga.id, 'ricerca', e.target.value)}
                          onFocus={() => { if (riga.articolo) aggiornaRigaVendita(riga.id, 'articolo', null); }}
                          placeholder="🔍 Cerca codice o descrizione ricambio..."
                          style={{ ...iS, border: `2px solid ${riga.articolo ? '#f59e0b' : T.border2||T.border}` }} />
                        {suggerimenti.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.card||T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', marginTop: '4px', zIndex: 10, maxHeight: '160px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            {suggerimenti.map(s => (
                              <div key={s.codice} onClick={() => aggiornaRigaVendita(riga.id, 'articolo', s)}
                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: `1px solid ${T.border}` }}
                                onMouseEnter={e => e.currentTarget.style.background = T.bg}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <span style={{ color: '#f59e0b', fontWeight: '700' }}>{s.codice}</span> — {s.descrizione}
                                <span style={{ float: 'right', color: T.textMuted, fontSize: '9px' }}>Agnano {getEsistenzaSede(s,'Agnano')} {um(s)} · Salerno {getEsistenzaSede(s,'Salerno')} {um(s)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {venditaRighe.length > 1 && (
                        <button onClick={() => rimuoviRigaVendita(riga.id)}
                          style={{ padding: '10px 12px', borderRadius: '8px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontWeight: '900', flexShrink: 0 }}>
                          ✕
                        </button>
                      )}
                    </div>
                    {riga.articolo && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '9px', color: T.textMuted, marginBottom: '4px' }}>QUANTITÀ ({um(riga.articolo)}) — disp. a {venditaSede}: {getEsistenzaSede(riga.articolo, venditaSede)}</div>
                          <input type="number" min="0.01" step="0.01" value={riga.qta} onChange={e => aggiornaRigaVendita(riga.id, 'qta', e.target.value)} placeholder="Es. 2" style={iS} />
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', color: T.textMuted, marginBottom: '4px' }}>PREZZO DI VENDITA (€/pz)</div>
                          <input type="number" min="0" step="0.01" value={riga.prezzoVendita} onChange={e => aggiornaRigaVendita(riga.id, 'prezzoVendita', e.target.value)} placeholder="Es. 25.00" style={iS} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button onClick={aggiungiRigaVendita}
              style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `2px dashed ${T.border2||T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginBottom: '18px' }}>
              + Aggiungi un altro ricambio
            </button>

            {righeVenditaValide.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', background: 'rgba(245,158,11,0.1)', borderRadius: '10px', fontSize: '12px', color: '#f59e0b', fontWeight: '700' }}>
                {righeVenditaValide.length} rig{righeVenditaValide.length === 1 ? 'a pronta' : 'he pronte'} — totale vendita: €{righeVenditaValide.reduce((s, r) => s + parseFloat(r.qta) * parseFloat(r.prezzoVendita), 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setVenditaModalOpen(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: `1px solid ${T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', cursor: 'pointer' }}>
                Annulla
              </button>
              <button onClick={salvaVendita} disabled={venditaSaving || righeVenditaValide.length === 0}
                style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: '#f59e0b', color: '#fff', fontWeight: '900', cursor: 'pointer', opacity: (venditaSaving || righeVenditaValide.length === 0) ? 0.5 : 1 }}>
                {venditaSaving ? 'Salvataggio in corso...' : `✓ Conferma Vendita e Stampa (${righeVenditaValide.length} rig${righeVenditaValide.length === 1 ? 'a' : 'he'})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storico Vendite Esterne — con Ristampa e Genera XML (alimenta Finance) */}
      {storicoVenditeOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setStoricoVenditeOpen(false); }}>
          <div style={{ background: T.card||T.surface, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>📋 Storico Vendite Esterne</div>
              <button onClick={() => setStoricoVenditeOpen(false)} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: '18px', fontWeight: '900' }}>×</button>
            </div>
            <div style={{ fontSize: '11px', color: T.textMuted||T.textSub, marginBottom: '16px' }}>
              "Genera XML" produce il file FatturaPA da caricare nella sezione Finance — stessa logica delle altre fatture generate in Service.
            </div>
            {loadingVendite && <div style={{ textAlign: 'center', padding: '30px', color: T.textMuted }}>Caricamento...</div>}
            {!loadingVendite && venditeList.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: T.textMuted||T.textSub }}>Nessuna vendita esterna registrata.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {venditeList.map(v => (
                <div key={v.id} style={{ background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>{v.cliente}</div>
                    <div style={{ fontSize: '11px', color: T.textMuted||T.textSub }}>
                      {new Date(v.creatoAt).toLocaleDateString('it-IT')} · {(v.righe||[]).length} ricambi · €{(v.totale||0).toLocaleString('it-IT', {minimumFractionDigits:2})}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => stampaVenditaRicambi(v)}
                      style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #6366f1', background: 'transparent', color: '#6366f1', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
                      🖨️ Ristampa
                    </button>
                    <button onClick={() => generaXMLVenditaEsterna(v)}
                      style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #10b981', background: 'transparent', color: '#10b981', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
                      📄 Genera XML
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Storico Bolle — archivio dei carichi ricevuti, raggruppati per numero bolla */}
      {storicoBolleOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setStoricoBolleOpen(false); }}>
          <div style={{ background: T.card||T.surface, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '760px', maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>🧾 Storico Bolle</div>
              <button onClick={() => setStoricoBolleOpen(false)} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: '18px', fontWeight: '900' }}>×</button>
            </div>
            {loadingBolle && <div style={{ textAlign: 'center', padding: '30px', color: T.textMuted }}>Caricamento...</div>}
            {!loadingBolle && bolleList.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: T.textMuted||T.textSub }}>Nessuna bolla registrata.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {bolleList.map((b, i) => (
                <details key={i} style={{ background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 14px' }}>
                  <summary style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', listStyle: 'none' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>
                        Bolla {b.numeroBolla} — {b.fornitore || 'Fornitore N.D.'}
                      </div>
                      <div style={{ fontSize: '11px', color: T.textMuted||T.textSub }}>
                        {b.dataBolla ? new Date(b.dataBolla).toLocaleDateString('it-IT') : '—'} · 📍 {b.sede} · {b.righe.length} articoli · €{b.totale.toLocaleString('it-IT', {minimumFractionDigits:2})}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={(e) => { e.preventDefault(); stampaBolla(b); }}
                        style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #6366f1', background: 'transparent', color: '#6366f1', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
                        🖨️ Stampa
                      </button>
                      {['admin','super_admin'].includes((user?.ruolo||'').toLowerCase().trim()) && (
                        <button onClick={(e) => { e.preventDefault(); eliminaBolla(b); }}
                          style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #ef4444', background: 'transparent', color: '#ef4444', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
                          🗑️ Elimina
                        </button>
                      )}
                    </div>
                  </summary>
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${T.border}` }}>
                    {b.righe.map((r, j) => (
                      <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0', color: T.textSub||T.text }}>
                        <span><b>{r.codice}</b> — {r.descrizione}</span>
                        <span>{r.qta} {um(r)} × €{(parseFloat(r.prezzoNetto)||0).toLocaleString('it-IT', {minimumFractionDigits:2})}</span>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
            <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '10px', textAlign: 'center' }}>
              🗑️ Elimina è visibile solo per admin e super admin.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function generaXMLVenditaEsterna(vendita) {
  const fmtN = n => (parseFloat(n) || 0).toFixed(2);
  const fmtQ = n => (parseFloat(n) || 0).toFixed(2);
  const esc  = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const righe = vendita.righe || [];
  const imponibile = righe.reduce((s,r)=>s+(parseFloat(r.qta)||0)*(parseFloat(r.prezzoVendita)||0), 0);
  const imposta     = imponibile * 0.22;
  const totale      = imponibile + imposta;

  // Dati cliente — stessa logica di estrazione CAP/Comune di generaXMLFattura
  const isAz        = true; // i clienti esterni (carrozzerie/officine) sono sempre aziende
  const { paese: paesePiva, codice: pivaCliente } = normalizzaPivaXML(vendita.pivaCliente);
  const nomeCliente = vendita.cliente || '';
  const indirizzoCliente = vendita.indirizzoCliente || '';
  const capMatch    = indirizzoCliente.match(/\b(\d{5})\b/);
  const capCliente  = capMatch ? capMatch[1] : '00000';
  const comuneMatch = capMatch ? indirizzoCliente.slice(indirizzoCliente.indexOf(capMatch[1]) + 5).replace(/^[, ]+/,'').split(/[ ,]/)[0] : 'ND';
  const comuneCliente = comuneMatch || 'ND';
  const indirizzoSolo = capMatch ? indirizzoCliente.slice(0, indirizzoCliente.indexOf(capMatch[1])).replace(/[, ]+$/,'') || indirizzoCliente : indirizzoCliente || 'ND';

  const data   = (vendita.creatoAt || vendita.data || new Date().toISOString()).split('T')[0];
  const numDoc = vendita.id || ('VE-' + Date.now());
  const progInvio = numDoc.replace(/[^A-Z0-9]/g,'').slice(-6) || 'RAD001';

  let linee = '';
  let numLinea = 1;
  righe.forEach(r => {
    const sub = (parseFloat(r.qta)||0)*(parseFloat(r.prezzoVendita)||0);
    linee += `
      <DettaglioLinee>
        <NumeroLinea>${numLinea++}</NumeroLinea>
        <CodiceArticolo>
          <CodiceTipo>RICAMBIO</CodiceTipo>
          <CodiceValore>${esc(r.codice||'01')}</CodiceValore>
        </CodiceArticolo>
        <Descrizione>${esc(r.descrizione||'Ricambio')}</Descrizione>
        <Quantita>${fmtQ(r.qta||1)}</Quantita>
        <UnitaMisura>PZ</UnitaMisura>
        <PrezzoUnitario>${fmtN(r.prezzoVendita)}</PrezzoUnitario>
        <PrezzoTotale>${fmtN(sub)}</PrezzoTotale>
        <AliquotaIVA>22.00</AliquotaIVA>
      </DettaglioLinee>`;
  });

  const xml = `<?xml version="1.0" encoding="utf-8"?>
<FatturaElettronica xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" versione="FPR12" xmlns="http://ivaservizi.agenziaentrate.gov.it/docs/xsd/fatture/v1.2">
  <FatturaElettronicaHeader xmlns="">
    <DatiTrasmissione>
      <IdTrasmittente>
        <IdPaese>IT</IdPaese>
        <IdCodice>07821241218</IdCodice>
      </IdTrasmittente>
      <ProgressivoInvio>${esc(progInvio)}</ProgressivoInvio>
      <FormatoTrasmissione>FPR12</FormatoTrasmissione>
      <CodiceDestinatario>0000000</CodiceDestinatario>
      <ContattiTrasmittente>
        <Telefono>08118658721</Telefono>
        <Email>rosario.scala@gruppoautoscala.com</Email>
      </ContattiTrasmittente>
    </DatiTrasmissione>
    <CedentePrestatore>
      <DatiAnagrafici>
        <IdFiscaleIVA>
          <IdPaese>IT</IdPaese>
          <IdCodice>07821241218</IdCodice>
        </IdFiscaleIVA>
        <CodiceFiscale>07821241218</CodiceFiscale>
        <Anagrafica>
          <Denominazione>Gruppo Autoscala S.r.l.</Denominazione>
        </Anagrafica>
        <RegimeFiscale>RF01</RegimeFiscale>
      </DatiAnagrafici>
      <Sede>
        <Indirizzo>Via Antiniana, 2/B</Indirizzo>
        <CAP>80078</CAP>
        <Comune>Pozzuoli</Comune>
        <Nazione>IT</Nazione>
      </Sede>
      <IscrizioneREA>
        <Ufficio>XX</Ufficio>
        <NumeroREA>XX</NumeroREA>
        <CapitaleSociale>0.00</CapitaleSociale>
        <StatoLiquidazione>LN</StatoLiquidazione>
      </IscrizioneREA>
    </CedentePrestatore>
    <CessionarioCommittente>
      <DatiAnagrafici>
        ${pivaCliente ? `<IdFiscaleIVA><IdPaese>${esc(paesePiva)}</IdPaese><IdCodice>${esc(pivaCliente)}</IdCodice></IdFiscaleIVA>` : ''}
        <Anagrafica>
          <Denominazione>${esc(nomeCliente)}</Denominazione>
        </Anagrafica>
      </DatiAnagrafici>
      <Sede>
        <Indirizzo>${esc(indirizzoSolo)}</Indirizzo>
        <CAP>${esc(capCliente)}</CAP>
        <Comune>${esc(comuneCliente)}</Comune>
        <Nazione>IT</Nazione>
      </Sede>
    </CessionarioCommittente>
  </FatturaElettronicaHeader>
  <FatturaElettronicaBody xmlns="">
    <DatiGenerali>
      <DatiGeneraliDocumento>
        <TipoDocumento>TD01</TipoDocumento>
        <Divisa>EUR</Divisa>
        <Data>${esc(data)}</Data>
        <Numero>${esc(numDoc)}</Numero>
        <ImportoTotaleDocumento>${fmtN(totale)}</ImportoTotaleDocumento>
      </DatiGeneraliDocumento>
    </DatiGenerali>
    <DatiBeniServizi>${linee}
      <DatiRiepilogo>
        <AliquotaIVA>22.00</AliquotaIVA>
        <ImponibileImporto>${fmtN(imponibile)}</ImponibileImporto>
        <Imposta>${fmtN(imposta)}</Imposta>
        <EsigibilitaIVA>I</EsigibilitaIVA>
      </DatiRiepilogo>
    </DatiBeniServizi>
  </FatturaElettronicaBody>
</FatturaElettronica>`;

  const blob = new Blob([xml], { type: 'application/xml' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `IT07821241218_${progInvio}.xml`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── FUNZIONE STAMPA VENDITA RICAMBI A CLIENTE ESTERNO ───────────────────────
function stampaVenditaRicambi(vendita) {
  const win = window.open('', '_blank');
  if (!win) {
    alert('⚠️ Il browser ha bloccato il popup. Consenti i popup per radargas.it e riprova.');
    return;
  }
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const dataDoc = vendita.data ? new Date(vendita.data).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
  const righeHTML = (vendita.righe || []).map((r, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;font-weight:700;">${r.codice||''}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;">${r.descrizione||''}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${r.qta}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;">${fmtE(r.prezzoVendita)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;font-weight:700;">${fmtE(r.qta * r.prezzoVendita)}</td>
    </tr>`).join('');

  win.document.write(`<html><head><title>Vendita Ricambi ${vendita.id}</title>
<meta charset="utf-8">
<style>
body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;padding:20px;}
.page{max-width:800px;margin:0 auto;}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;border-bottom:2px solid #000;padding-bottom:12px;}
.azienda-info{font-size:10px;line-height:1.6;}
.dest-box{border:1px solid #333;padding:10px 14px;font-size:11px;min-width:220px;}
table{width:100%;border-collapse:collapse;margin-top:14px;}
th{background:#1e293b;color:#fff;padding:8px;font-size:10px;text-transform:uppercase;text-align:left;}
.totali-box{display:flex;justify-content:flex-end;margin-top:8px;}
.tot-finale{display:flex;justify-content:space-between;font-size:15px;font-weight:900;padding:8px 14px;background:#1e293b;color:#fff;border-radius:6px;min-width:220px;}
</style></head><body>
<button onclick="window.print()" style="background:#1e293b;color:#fff;border:none;padding:8px 20px;border-radius:6px;cursor:pointer;margin-bottom:16px;font-size:12px;font-weight:700;">🖨️ Stampa / Salva PDF</button>
<div class="page">
  <div class="header">
    <div>
      <img src="data:image/png;base64,${LOGO_GAS_BASE64}" alt="Gruppo Autoscala" style="height:52px;display:block;" />
      <div class="azienda-info" style="margin-top:8px;">
        Ragione Sociale: Gruppo Autoscala S.r.l.<br>
        Sede Legale: Via Antiniana, 2/B - 80078 Pozzuoli (NA)<br>
        Tel. 0811865872 · amministrazione@gruppoautoscala.com
      </div>
    </div>
    <div class="dest-box">
      <div style="font-size:9px;color:#777;text-transform:uppercase;margin-bottom:4px;">Documento</div>
      <div style="font-size:13px;font-weight:900;">VENDITA RICAMBI</div>
      <div style="font-size:10px;color:#555;margin-top:2px;">${vendita.id} · ${dataDoc}</div>
    </div>
  </div>

  <div style="margin-bottom:10px;">
    <div style="font-size:9px;color:#777;text-transform:uppercase;font-weight:700;">Cliente</div>
    <div style="font-size:13px;font-weight:700;">${vendita.cliente || ''}</div>
    ${vendita.indirizzoCliente ? `<div style="font-size:11px;color:#555;">${vendita.indirizzoCliente}</div>` : ''}
    ${vendita.pivaCliente ? `<div style="font-size:11px;color:#555;">P.IVA/CF: ${vendita.pivaCliente}</div>` : ''}
  </div>

  <table>
    <thead><tr><th>Codice</th><th>Descrizione</th><th style="text-align:center;">Qtà</th><th style="text-align:right;">Prezzo</th><th style="text-align:right;">Totale</th></tr></thead>
    <tbody>${righeHTML}</tbody>
  </table>

  <div class="totali-box">
    <div class="tot-finale"><span>TOTALE</span><span>${fmtE(vendita.totale)}</span></div>
  </div>
</div>
</body></html>`);
  win.document.close();
}

// ─── FUNZIONE STAMPA BOLLA (CARICO RICAMBI) ───────────────────────────────────
function stampaBolla(bolla) {
  const win = window.open('', '_blank');
  if (!win) {
    alert('⚠️ Il browser ha bloccato il popup. Consenti i popup per radargas.it e riprova.');
    return;
  }
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const dataDoc = bolla.dataBolla ? new Date(bolla.dataBolla).toLocaleDateString('it-IT') : '';
  const umStampa = r => r.unitaMisura === 'litri' ? 'L' : 'pz';
  const righeHTML = (bolla.righe || []).map((r, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;font-weight:700;">${r.codice||''}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;">${r.descrizione||''}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${r.qta} ${umStampa(r)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;">${fmtE(r.prezzoNetto)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;font-weight:700;">${fmtE((r.qta||0) * (r.prezzoNetto||0))}</td>
    </tr>`).join('');

  win.document.write(`<html><head><title>Bolla ${bolla.numeroBolla}</title>
<meta charset="utf-8">
<style>
body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;padding:20px;}
.page{max-width:800px;margin:0 auto;}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;border-bottom:2px solid #000;padding-bottom:12px;}
.azienda-info{font-size:10px;line-height:1.6;}
.dest-box{border:1px solid #333;padding:10px 14px;font-size:11px;min-width:220px;}
table{width:100%;border-collapse:collapse;margin-top:14px;}
th{background:#1e293b;color:#fff;padding:8px;font-size:10px;text-transform:uppercase;text-align:left;}
.totali-box{display:flex;justify-content:flex-end;margin-top:8px;}
.tot-finale{display:flex;justify-content:space-between;font-size:15px;font-weight:900;padding:8px 14px;background:#1e293b;color:#fff;border-radius:6px;min-width:220px;}
</style></head><body>
<button onclick="window.print()" style="background:#1e293b;color:#fff;border:none;padding:8px 20px;border-radius:6px;cursor:pointer;margin-bottom:16px;font-size:12px;font-weight:700;">🖨️ Stampa / Salva PDF</button>
<div class="page">
  <div class="header">
    <div>
      <img src="data:image/png;base64,${LOGO_GAS_BASE64}" alt="Gruppo Autoscala" style="height:52px;display:block;" />
      <div class="azienda-info" style="margin-top:8px;">
        Ragione Sociale: Gruppo Autoscala S.r.l.<br>
        Sede Legale: Via Antiniana, 2/B - 80078 Pozzuoli (NA)<br>
        Tel. 0811865872 · amministrazione@gruppoautoscala.com
      </div>
    </div>
    <div class="dest-box">
      <div style="font-size:9px;color:#777;text-transform:uppercase;margin-bottom:4px;">Documento</div>
      <div style="font-size:13px;font-weight:900;">BOLLA DI CARICO RICAMBI</div>
      <div style="font-size:10px;color:#555;margin-top:2px;">N. ${bolla.numeroBolla} · ${dataDoc}</div>
    </div>
  </div>

  <div style="margin-bottom:10px;">
    <div style="font-size:9px;color:#777;text-transform:uppercase;font-weight:700;">Fornitore</div>
    <div style="font-size:13px;font-weight:700;">${bolla.fornitore || 'N.D.'}</div>
    <div style="font-size:9px;color:#777;text-transform:uppercase;font-weight:700;margin-top:8px;">Magazzino di Destinazione</div>
    <div style="font-size:13px;font-weight:700;">📍 ${bolla.sede || 'N.D.'}</div>
  </div>

  <table>
    <thead><tr><th>Codice</th><th>Descrizione</th><th style="text-align:center;">Qtà</th><th style="text-align:right;">Prezzo</th><th style="text-align:right;">Totale</th></tr></thead>
    <tbody>${righeHTML}</tbody>
  </table>

  <div class="totali-box">
    <div class="tot-finale"><span>TOTALE</span><span>${fmtE(bolla.totale)}</span></div>
  </div>
</div>
</body></html>`);
  win.document.close();
}

// ─── FUNZIONE STAMPA ODL / FATTURA OFFICINA ──────────────────────────────────
// ─── SINCRONIZZAZIONE PREZZO D'ACQUISTO ──────────────────────────────────────
// Scrive lo stesso valore in TUTTI i punti dove il prezzo d'acquisto (bottom
// price) viene mostrato — Stock, Conto Economico, e il contratto collegato
// se disponibile — nello stesso momento. Così non serve confrontare
// timestamp per capire "qual è il valore più recente": sono sempre uguali
// per costruzione, ovunque venga inserito il dato.
async function sincronizzaPrezzoAcquisto({ vin, targa, veicoloId, valore, contrattoId }) {
  const key = (vin || targa || veicoloId || '').replace(/[^a-zA-Z0-9]/g, '_');
  if (!key) return;
  const val = parseFloat(valore) || 0;

  // 1) Conto Economico — update() per non toccare provvFin/commServizi/
  // costiVari già presenti, scritti in un momento diverso.
  await update(ref(db, `conto_economico_acquisti/${key}`), { costoAcquisto: val });

  // 2) Stock — scrittura mirata al singolo veicolo trovato per VIN/targa,
  // mai sull'intero array stock_data/vehicles.
  try {
    const snapStock = await get(ref(db, 'stock_data/vehicles'));
    const vehicles = snapStock.val() || [];
    const idx = vehicles.findIndex(sv =>
      (vin && (sv.vin || '').toUpperCase().trim() === vin.toUpperCase().trim()) ||
      (targa && (sv.targa || '').toUpperCase().trim() === targa.toUpperCase().trim())
    );
    if (idx !== -1) await set(ref(db, `stock_data/vehicles/${idx}/bottomPrice`), val);
  } catch (e) { /* non bloccante — il conto economico resta comunque aggiornato anche se questo fallisce */ }

  // 3) Contratto collegato, se questa chiamata arriva da un punto che lo
  // conosce (es. il conto economico, che riceve sempre il contrattoId).
  if (contrattoId) {
    await set(ref(db, `contratti/${contrattoId}/bottomPrice`), val);
    await set(ref(db, `contratti/${contrattoId}/veicolo/bottomPrice`), val);
    await set(ref(db, `contratti/${contrattoId}/dataModifica`), new Date().toISOString());
  }
  return val;
}

// ─── PREZZI IVA INCLUSA (scorporo) su ODL ───────────────────────────────────
// Con doc.prezziIvaInclusa i prezzi delle righe sono scritti IVA inclusa
// (es. 400 €): il netto si ricava dividendo per 1,22. Le fatture salvano già
// le righe al netto, quindi per loro questo fattore vale sempre 1.
const fattoreScorporo = doc => (doc?.prezziIvaInclusa && !(doc?.senzaIva || (doc?.ordineInterno && doc?.applicaIva === false)) ? 1.22 : 1);

// ─── CACHE STORICO / FATTURE: aggiornamento immediato dopo una fattura ──────
// Storico ODL e lista fatture usano cache (memoria + IndexedDB) per i costi
// Firebase. Dopo aver fatturato, la nuova fattura e l'ODL chiuso vengono
// inseriti subito nelle cache e le viste aperte si aggiornano all'istante.
function aggiornaCacheDopoFattura(fattura, odlAggiornato) {
  const upsert = (lista, el) => { const m = new Map((lista || []).map(x => [x.id, x])); m.set(el.id, { ...(m.get(el.id) || {}), ...el }); return Array.from(m.values()); };
  try {
    if (window._radarFatStoricoList) {
      window._radarFatStoricoList = upsert(window._radarFatStoricoList, { ...fattura, _tipo: 'fattura' })
        .sort((a, b) => (b.creatoAt || b.data || '').localeCompare(a.creatoAt || a.data || ''));
      if (odlAggiornato && window._radarOdlStoricoList) window._radarOdlStoricoList = upsert(window._radarOdlStoricoList, { ...odlAggiornato, _tipo: 'odl' });
      idbScrivi('odl_storico_cache', { odl: window._radarOdlStoricoList || [], fat: window._radarFatStoricoList, ts: window._radarOdlStoricoTs || Date.now() });
    }
    if (window._radarInterventiCache) {
      window._radarInterventiCache = upsert(window._radarInterventiCache, fattura).sort((a, b) => new Date(b.data) - new Date(a.data));
      idbScrivi('interventi_cache', { list: window._radarInterventiCache, ts: window._radarInterventiTs || Date.now() });
    }
    if (odlAggiornato && window._radarOdlCostiList) window._radarOdlCostiList = upsert(window._radarOdlCostiList, odlAggiornato);
  } catch (e) { /* le viste si riallineano comunque col controllo periodico */ }
  window.dispatchEvent(new CustomEvent('radar:fattura-creata', { detail: { id: fattura.id } }));
}

// Eliminazione fattura: via subito da tutte le cache e dalle viste aperte
function aggiornaCacheDopoEliminazioneFattura(idFattura, odlRiaperto) {
  const senza = lista => (lista || []).filter(x => x.id !== idFattura);
  const upsert = (lista, el) => { const m = new Map((lista || []).map(x => [x.id, x])); m.set(el.id, { ...(m.get(el.id) || {}), ...el }); return Array.from(m.values()); };
  try {
    if (window._radarFatStoricoList) {
      window._radarFatStoricoList = senza(window._radarFatStoricoList);
      if (odlRiaperto && window._radarOdlStoricoList) window._radarOdlStoricoList = upsert(window._radarOdlStoricoList, { ...odlRiaperto, _tipo: 'odl' });
      idbScrivi('odl_storico_cache', { odl: window._radarOdlStoricoList || [], fat: window._radarFatStoricoList, ts: window._radarOdlStoricoTs || Date.now() });
    }
    if (window._radarInterventiCache) {
      window._radarInterventiCache = senza(window._radarInterventiCache);
      idbScrivi('interventi_cache', { list: window._radarInterventiCache, ts: window._radarInterventiTs || Date.now() });
    }
    if (odlRiaperto && window._radarOdlCostiList) window._radarOdlCostiList = upsert(window._radarOdlCostiList, odlRiaperto);
  } catch (e) { /* non bloccante */ }
  window.dispatchEvent(new CustomEvent('radar:fattura-creata', { detail: { id: idFattura, eliminata: true } }));
}

// ─── Rimette un'auto DISPONIBILE in stock (annullamento / eliminazione contratto)
// Stato 'FREE' (quello usato da tutto lo Stock: prima veniva scritto
// 'DISPONIBILE', che lo Stock non riconosce); confronto su id, telaio e
// targa senza badare a spazi e maiuscole.
// Storico blocchi: registra sul telaio (azioni_telaio) chi bloccava l'auto
// e chi l'ha liberata. Visibile nella scheda Azioni del veicolo e nel tab
// ANALISI → Storico blocchi.
async function logBloccoVeicolo(car, { tipo, bloccatoDa = '', cliente = '', motivo = '', user }) {
  if (!car?.id) return;
  try {
    const chi = `${user?.nome || ''} ${user?.cognome || ''}`.trim() || 'N.D.';
    const testo = tipo === 'blocco'
      ? `🔒 Bloccata da ${bloccatoDa || chi}${motivo ? ` — ${motivo}` : ''}`
      : `🔓 Liberata da ${chi}${bloccatoDa ? ` (era bloccata da ${bloccatoDa}${cliente ? `, cliente ${cliente}` : ''})` : ''}${motivo ? ` — ${motivo}` : ''}`;
    const snap = await get(ref(db, `azioni_telaio/${car.id}`));
    const curr = Array.isArray(snap.val()) ? snap.val() : Object.values(snap.val() || {});
    await set(ref(db, `azioni_telaio/${car.id}`), [...curr, {
      testo, autore: chi, data: new Date().toISOString(), tipo: tipo === 'blocco' ? 'blocco' : 'liberazione',
      bloccatoDa, cliente, motivo, targa: car.targa || '', vin: car.vin || '', marca: car.marca || '', modello: car.modello || '',
    }]);
  } catch (e) { /* il log non blocca l'operazione */ }
}

// Toglie la prenotazione "in trattativa" di un veicolo sotto TUTTE le chiavi
// possibili (targa, telaio, id): prima se ne cancellava una sola e, se la
// prenotazione era registrata sotto un'altra, l'auto restava IN TRATTATIVA
async function liberaTrattativaVeicolo(v) {
  const chiavi = [...new Set([v?.targa, v?.vin, v?.id].filter(Boolean).map(k => String(k).replace(/[^a-zA-Z0-9]/g, '_')))];
  for (const k of chiavi) { try { await set(ref(db, `veicoli_in_trattativa/${k}`), null); } catch (e) { /* ok */ } }
}

// Scrive dei campi sul veicolo in stock (trovato per id, telaio o targa) e
// aggiorna lo stato live per tutti
async function impostaVeicoloInStock(v, campi) {
  try {
    const norm = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const stockSnap = await get(ref(db, 'stock_data/vehicles'));
    if (!stockSnap.exists()) return false;
    const raw = stockSnap.val();
    const voci = Array.isArray(raw) ? raw.map((x, i) => [String(i), x]) : Object.entries(raw);
    const V = norm(v?.vin), Tg = norm(v?.targa), ID = String(v?.id || '');
    const trovata = voci.find(([, sv]) => sv && ((ID && String(sv.id) === ID) || (V && norm(sv.vin) === V) || (Tg && norm(sv.targa) === Tg)));
    if (!trovata) return false;
    const [chiaveStock, sv] = trovata;
    await update(ref(db, `stock_data/vehicles/${chiaveStock}`), campi);
    if (sv.id && campi.status) await set(ref(db, `stati_veicoli/${sv.id}/status`), campi.status);
    window._radarStockCache = null; window._radarStockTs = 0;
    try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) { /* ok */ }
    return true;
  } catch (e) { return false; }
}

async function ripristinaVeicoloInStock(v, motivo) {
  try {
    const norm = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const stockSnap = await get(ref(db, 'stock_data/vehicles'));
    if (!stockSnap.exists()) return false;
    const raw = stockSnap.val();
    const voci = Array.isArray(raw) ? raw.map((x, i) => [String(i), x]) : Object.entries(raw);
    const V = norm(v?.vin), Tg = norm(v?.targa), ID = String(v?.id || '');
    const trovata = voci.find(([, sv]) => sv && ((ID && String(sv.id) === ID) || (V && norm(sv.vin) === V) || (Tg && norm(sv.targa) === Tg)));
    if (!trovata) return false;
    const [chiaveStock, sv] = trovata;
    await update(ref(db, `stock_data/vehicles/${chiaveStock}`), {
      status: 'FREE', vendutoAt: null, consulenteNome: null, clienteNome: null,
      ripristinatoAt: new Date().toISOString(), ripristinatoPer: motivo || '',
    });
    if (sv.id) await set(ref(db, `stati_veicoli/${sv.id}/status`), 'FREE');
    window._radarStockCache = null; window._radarStockTs = 0;
    try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) { /* ok */ }
    return true;
  } catch (e) { return false; }   // lo stock non è critico: l'operazione sul contratto resta valida
}

// Registra nel log del Back Office chi ha cambiato un campo della pratica
// (anche quando il cambio parte dal Calendario o dal dettaglio contratto)
async function logBackoffice(contrattoId, campo, valore, user, nota) {
  try {
    await set(ref(db, `contratti/${contrattoId}/backoffice_log/${campo}_${Date.now()}`), {
      campo, valore, utente: `${user?.nome || ''} ${user?.cognome || ''}`.trim() || 'N.D.', ruolo: user?.ruolo || '',
      timestamp: new Date().toISOString(), ...(nota ? { nota } : {}),
    });
  } catch (e) { /* il log non blocca l'operazione */ }
}

// Consegna effettiva di un contratto: quando è stato segnato "consegnato"
// (log del Back Office, esito del Calendario o data di vendita) e da chi
function infoConsegna(c) {
  const bo = c?.backoffice || {};
  const log = Object.values(c?.backoffice_log || {}).filter(l => l && l.campo === 'statoConsegna' && l.valore === 'consegnato' && l.timestamp)
    .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)))[0];
  const consegnato = bo.statoConsegna === 'consegnato' || c?.stato === 'venduto';
  const data = consegnato ? (log?.timestamp || bo.dataEsitoConsegna || c?.vendutoAt || c?.dataVendita || '') : '';
  return { consegnato, data, da: log?.utente || '', prevista: bo.dataConsegnaPrevista || c?.prezzo?.dataConsegna || c?.datiExtra?.dataConsegnaPrevista || '', oraPrevista: bo.oraConsegnaPrevista || '' };
}

// Totale permute: prima permuta (campi storici) + permute aggiuntive
const valorePermuteTot = pr => (parseFloat(pr?.permutaValore) || 0)
  + (Array.isArray(pr?.permuteAggiuntive) ? pr.permuteAggiuntive : []).reduce((t, x) => t + (parseFloat(x?.valore) || 0), 0);

// ─── SALDO CONTRATTO (unica formula per Back Office, Calendario consegne…) ──
// Stessa formula di ContrattoDetailModal. Se il back office ha scritto un
// saldo a mano (backoffice.saldoManuale) quello ha la precedenza ovunque.
function calcolaSaldoContratto(c) {
  const pr = c?.prezzo || {};
  const pL = parseFloat(pr.prezzoListino || c?.veicolo?.prezzo || 0);
  const sc = parseFloat(pr.sconto || 0), ipt = parseFloat(pr.ipt || 0);
  const perm = valorePermuteTot(pr), cap = parseFloat(pr.caparra || 0), fin = parseFloat(pr.importoFinanziato || 0);
  const totServizi = Object.entries(c?.servizi || {}).filter(([k]) => k.startsWith('costo')).reduce((t, [, v]) => t + (parseFloat(v) || 0), 0);
  const accessoriCM = (c?.servizi?.accessoriCasaMadreList || []).reduce((t, a) => t + (parseFloat(a.prezzo) || 0), 0);
  const iva = pr.hasIVA ? Math.round((pL - sc) * 0.22) : 0;
  const estinzione = pr.estinzioneAnticipata ? parseFloat(pr.costoEstinzioneAnticipata || 0) : 0;
  const leasing = pr.speseGestioneLeasing ? parseFloat(pr.costoSpeseGestioneLeasing || 0) : 0;
  return (pL - sc + ipt) + accessoriCM + totServizi + iva - perm - cap - fin + estinzione + leasing;
}
const haSaldoManuale = bo => bo?.saldoManuale !== undefined && bo?.saldoManuale !== null && String(bo.saldoManuale).trim() !== '';
const formattaSaldo = n => (n > 0 ? `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Saldato');
// Testo del saldo da mostrare: manuale se impostato, altrimenti calcolato
const testoSaldoContratto = c => (haSaldoManuale(c?.backoffice) ? String(c.backoffice.saldoManuale) : formattaSaldo(calcolaSaldoContratto(c)));

// ─── RECAPITI CONSULENTI NLT (stampati sul preventivo noleggio) ────────────
const RECAPITI_CONSULENTI_NLT = [
  ['Raffaele Crispo', 'raffaele.crispo@gruppoautoscala.com', '351 503 5657'],
  ['Ciro Imperato', 'ciro.imperato@gruppoautoscala.com', '338 972 0889'],
  ['Francesco Sementa', 'francesco.sementa@gruppoautoscala.com', '335 571 2271'],
];

// ─── FRANCHIGIE POLIZZE NOLEGGIO ────────────────────────────────────────────
const FRANCHIGIE_NLT = {
  rca:           { label: 'RCA',              valori: ['0 €', '150 €', '250 €'] },
  kasko:         { label: 'Kasko',            valori: ['0 €', '250 €', '500 €', '550 €', '750 €', '1.000 €', '2.000 €', '250 € (scop. 10%)'] },
  furtoIncendio: { label: 'Furto e Incendio', valori: ['0 €', '250 €', '500 €', '500 € (scop. 10%)', '750 €', '1.000 €', '2.000 €', '5%', '10%'] },
};
const campoFranchigia = k => 'franchigia' + k.charAt(0).toUpperCase() + k.slice(1);   // rca → franchigiaRca
// Testo "Franchigie" generato dalle scelte: stampa e dettaglio continuano a
// leggere polizze.franchigie, quindi i preventivi vecchi (testo libero) restano visibili
const testoFranchigieNlt = pol => Object.entries(FRANCHIGIE_NLT)
  .filter(([k]) => pol?.[k] && pol?.[campoFranchigia(k)])
  .map(([k, d]) => `${d.label}: ${pol[campoFranchigia(k)]}`).join('\n');

// ─── STATI PREVENTIVI NOLEGGIO LUNGO TERMINE ────────────────────────────────
// Le chiavi salvate su Firebase restano quelle storiche (bozza, inviato,
// accettato, rifiutato, non_interessato): cambiano solo le etichette, quindi
// preventivi esistenti, filtri e provvigioni continuano a funzionare.
// Provvigione: solo RENT FIRMATO (chiave 'accettato'), come prima.
const STATI_NLT = [
  { v: 'bozza',           l: 'BOZZA',           c: '#f59e0b' },
  { v: 'inviato',         l: 'QTZ. INVIATA',    c: '#3b82f6' },
  { v: 'qtz_accettata',   l: 'QTZ. ACCETTATA',  c: '#06b6d4' },  // nuovo
  { v: 'non_interessato', l: 'NON INTERESSATO', c: '#94a3b8' },
  { v: 'prt_caricata',    l: 'PRT CARICATA',    c: '#8b5cf6' },  // nuovo
  { v: 'prt_approvata',   l: 'PRT APPROVATA',   c: '#6366f1' },  // nuovo
  { v: 'rifiutato',       l: 'PRT RESPINTA',    c: '#ef4444' },
  { v: 'accettato',       l: 'RENT FIRMATO',    c: '#10b981' },
];
const STATO_NLT = Object.fromEntries(STATI_NLT.map(x => [x.v, x]));
const etichettaStatoNlt = st => (STATO_NLT[st || 'bozza'] || { l: String(st || 'bozza').toUpperCase() }).l;
// Trattative ancora aperte e in avanzamento (tra quotazione inviata e firma)
const STATI_NLT_IN_CORSO = ['inviato', 'qtz_accettata', 'prt_caricata', 'prt_approvata'];

// ─── ODL PER I COSTI OFFICINA ────────────────────────────────────────────────
// I costi officina di un'auto vanno contati anche quando l'ODL NON è (ancora)
// diventato fattura — tipico degli ordini interni di preparazione sulle auto
// in stock, che non vengono mai fatturati a un cliente. Cache 4 ore (memoria
// + IndexedDB) per non riscaricare il nodo odl a ogni apertura.
async function caricaOdlPerCosti() {
  const ora = Date.now(), QUATTRO_ORE = 4 * 60 * 60 * 1000;
  if (window._radarOdlCostiList && ora - (window._radarOdlCostiTs || 0) < QUATTRO_ORE) return window._radarOdlCostiList;
  // Se lo Storico ODL ha già una cache fresca, si riusa (sola lettura)
  if (window._radarOdlStoricoList && ora - (window._radarOdlStoricoTs || 0) < QUATTRO_ORE) return window._radarOdlStoricoList;
  try {
    const cache = await idbLeggi('odl_costi_cache');
    if (cache && ora - cache.ts < QUATTRO_ORE) {
      window._radarOdlCostiList = cache.list; window._radarOdlCostiTs = cache.ts;
      return cache.list;
    }
  } catch (e) { /* cache non disponibile */ }
  const snap = await get(ref(db, 'odl'));
  const list = snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ ...v, id })) : [];
  window._radarOdlCostiList = list; window._radarOdlCostiTs = ora;
  try { await idbScrivi('odl_costi_cache', { list, ts: ora }); } catch (e) { /* non bloccante */ }
  return list;
}
const normTelaioTarga = s => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
// Fatture officina + ODL non fatturati di un veicolo, senza doppi conteggi:
// un ODL già diventato fattura si conta una volta sola, tramite la fattura.
function documentiOfficinaVeicolo({ vin, targa, fatture, odl }) {
  const V = normTelaioTarga(vin), T = normTelaioTarga(targa);
  const corrisponde = d => {
    const dv = normTelaioTarga(d.veicolo?.vin || d.telaio || d.vin);
    const dt = normTelaioTarga(d.veicolo?.targa || d.targa);
    return (V && dv && dv === V) || (T && dt && dt === T);
  };
  const fattureAuto = (fatture || []).filter(corrisponde).map(f => ({ ...f, _origine: 'fattura' }));
  const idFatture = new Set((fatture || []).map(f => f.id));
  const odlAuto = (odl || []).filter(o => corrisponde(o) && o.stato !== 'annullato' && !(o.fatturaId && idFatture.has(o.fatturaId)))
    .map(o => ({ ...o, data: o.dataOdl || o.creatoAt, _origine: 'odl' }));
  return [...fattureAuto, ...odlAuto];
}

// ─── DATI CLIENTE ODL → FATTURA ──────────────────────────────────────────────
// Tutti i campi cliente di un ODL/fattura (chiavi che finiscono in "Cliente"),
// senza undefined (Firebase li rifiuta)
function datiClienteDa(obj) {
  return Object.fromEntries(Object.entries(obj || {})
    .filter(([k, v]) => /Cliente$/.test(k) && v !== undefined && v !== null)
    .map(([k, v]) => [k, typeof v === 'string' ? v : String(v)]));
}

// Stampa una fattura officina. Se la fattura è stata creata prima della
// correzione e le mancano dati cliente, li recupera dall'ODL di origine,
// li stampa e li salva anche sulla fattura (riparazione una tantum).
async function stampaFatturaOfficina(doc) {
  const isFattura = doc?._tipo === 'fattura' || String(doc?.id || '').startsWith('FAT-') || doc?.tipo === 'fattura_officina';
  if (!isFattura || !doc.odlId) { stampaDocumentoOfficina(doc); return; }
  // Finestra aperta subito, dentro il clic: dopo l'attesa il browser la bloccherebbe
  const win = window.open('', '_blank');
  if (!win) { alert('⚠️ Il browser ha bloccato il popup. Consenti i popup per radargas.it e riprova.'); return; }
  win.document.write('<p style="font-family:sans-serif;padding:40px;text-align:center;">Preparazione fattura…</p>');
  let completo = doc;
  try {
    const snap = await get(ref(db, `odl/${doc.odlId}`));
    if (snap.exists()) {
      const daOdl = datiClienteDa(snap.val());
      const mancanti = Object.fromEntries(Object.entries(daOdl).filter(([k, v]) => v && !doc[k]));
      if (Object.keys(mancanti).length) {
        completo = { ...doc, ...mancanti };
        const { _tipo, ...salvabile } = mancanti;
        try { await update(ref(db, `service_interventi/${doc.id}`), salvabile); } catch (e) { /* stampa comunque */ }
      }
    }
  } catch (e) { /* ODL non leggibile: si stampa con i dati presenti */ }
  win.document.open();
  stampaDocumentoOfficina(completo, undefined, win);
}

function stampaDocumentoOfficina(doc, tipoDocumentoOverride, winEsistente) {
  // ODL con prezzi IVA inclusa: in stampa righe e imponibile al netto,
  // l'IVA calcolata sopra restituisce esattamente i lordi inseriti
  if (fattoreScorporo(doc) > 1) {
    const f = fattoreScorporo(doc);
    doc = { ...doc, prezziIvaInclusa: false, righe: (doc.righe || []).map(r => r.tipo === 'sconto' ? r : { ...r, prezzoUnit: (parseFloat(r.prezzoUnit) || 0) / f }) };
  }
  // Se non viene passata una finestra già aperta, la apriamo qui (caso
  // normale: stampa diretta, senza salvataggi asincroni prima). Se invece
  // arriva già aperta (caso: generaPreventivoDaODL, che deve salvare su
  // Firebase PRIMA di stampare), la riusiamo — aprirla solo DOPO
  // l'attesa del salvataggio farebbe scattare il blocco popup del
  // browser, perché non sarebbe più "direttamente" nel gesto di click.
  const win = winEsistente || window.open('', '_blank');
  if (!win) {
    alert('⚠️ Il browser ha bloccato il popup. Consenti i popup per radargas.it e riprova.');
    return;
  }
  const IVA = 0.22;
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const righe = doc.righe || [];
  // tipoDocumento: 'odl' (nessun prezzo) | 'preventivo' (con prezzi, non fiscale) | 'fattura' (con prezzi, fiscale)
  const tipoDocumento = tipoDocumentoOverride || (doc.id?.startsWith('ODL-') || doc.id?.startsWith('BOZZA') ? 'odl' : 'fattura');
  const isODL = tipoDocumento === 'odl';
  const isPreventivo = tipoDocumento === 'preventivo';
  const mostraPrezzi = !isODL; // preventivo E fattura mostrano i prezzi, solo ODL no
  const titoloDoc = isODL ? 'ORDINE DI LAVORO' : isPreventivo ? 'PREVENTIVO' : 'FATTURA OFFICINA';

  // Calcoli con sconto
  const righeNormali = righe.filter(r=>r.tipo!=='sconto');
  const righeSconto  = righe.filter(r=>r.tipo==='sconto');
  const imponibileRighe = righeNormali.reduce((s,r)=>s+(parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0),0);
  const totaleSconto    = righeSconto.reduce((s,r)=>s+imponibileRighe*((parseFloat(r.percentuale)||0)/100),0);
  const senzaIva  = !!doc.senzaIva || (!!doc.ordineInterno && doc.applicaIva === false);
  const totNetto  = doc.totaleNetto  || Math.max(0, imponibileRighe - totaleSconto);
  // IVA a 0 è un valore valido (ordine interno senza IVA): non va ricalcolata
  const totIVA    = senzaIva ? 0 : (doc.totaleIVA !== undefined && doc.totaleIVA !== null ? doc.totaleIVA : totNetto * IVA);
  const totLordo  = senzaIva ? totNetto : (doc.totaleLordo || totNetto + totIVA);
  const manoTot   = righeNormali.filter(r=>r.tipo==='manodopera').reduce((s,r)=>s+(parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0),0);
  const matTot    = righeNormali.filter(r=>r.tipo!=='manodopera').reduce((s,r)=>s+(parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0),0);
  const numDoc    = doc.numeroFattura || doc.id || `OF-${Date.now().toString().slice(-6)}`;
  const dataDoc   = doc.dataOdl || doc.data || new Date().toLocaleDateString('it-IT');

  // Righe HTML — ODL: no prezzi; Preventivo/Fattura: con prezzi
  const righeHTML = !mostraPrezzi
    ? righeNormali.map((r,i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;font-weight:700;">${r.codice||''}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;">${r.descrizione||''}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${r.tipo==='manodopera'?'UL':'PZ'}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${parseFloat(r.qta)||1}</td>
      </tr>`).join('')
    : righe.map((r,i) => {
        if (r.tipo==='sconto') {
          const val = imponibileRighe*((parseFloat(r.percentuale)||0)/100);
          return `<tr style="background:#fffbeb">
            <td colspan="4" style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;color:#92400e;">Sconto ${r.percentuale||0}%</td>
            <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;color:#92400e;font-weight:700;">- ${fmtE(val)}</td>
            <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${senzaIva ? '—' : '22'}</td>
          </tr>`;
        }
        const sub = (parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0);
        return `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;font-weight:700;">${r.codice||''}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;">${r.descrizione||''}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${r.tipo==='manodopera'?'UL':'PZ'}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${parseFloat(r.qta)||1}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;">${fmtE(r.prezzoUnit)}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;font-weight:700;">${fmtE(sub)}</td>
          <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${senzaIva ? '—' : '22'}</td>
        </tr>`;
      }).join('');

  // Colonne tabella diverse per ODL (no prezzi) e Fattura (con prezzi)
  const theadODL = `<thead><tr>
    <th style="width:80px">Codice</th><th>Descrizione</th>
    <th style="width:40px;text-align:center">UM</th>
    <th style="width:55px;text-align:center">Qta</th>
  </tr></thead>`;
  const theadFAT = `<thead><tr>
    <th style="width:70px">Codice</th><th>Descrizione</th>
    <th style="width:35px;text-align:center">UM</th>
    <th style="width:50px;text-align:center">Qta</th>
    <th style="width:80px;text-align:right">Prezzo</th>
    <th style="width:80px;text-align:right">Importo</th>
    <th style="width:35px;text-align:center">IVA%</th>
  </tr></thead>`;

  const totaliHTML = !mostraPrezzi ? '' : `
  <div class="totali-box" style="margin-top:8px;">
    <div class="totali-inner">
      ${totaleSconto>0 ? `<div class="tot-row"><span>Subtotale</span><span>${fmtE(imponibileRighe)}</span></div><div class="tot-row" style="color:#92400e"><span>Sconto</span><span>- ${fmtE(totaleSconto)}</span></div>` : ''}
      <div class="tot-row"><span>Imponibile</span><span>${fmtE(totNetto)}</span></div>
      <div class="tot-row"><span>${senzaIva ? 'IVA (non applicata)' : 'IVA 22%'}</span><span>${fmtE(totIVA)}</span></div>
      <div class="tot-finale"><span>TOTALE</span><span>${fmtE(totLordo)}</span></div>
    </div>
  </div>`;

  const footerHTML = (isODL || isPreventivo) ? `
  <!-- Disegno auto stilizzato -->
  <div style="margin:14px 0;border:1px solid #333;padding:10px;">
    <div style="font-size:9px;font-weight:900;text-transform:uppercase;margin-bottom:8px;color:#555;">STATO CARROZZERIA — segnare eventuali danni</div>
    <div style="display:flex;gap:20px;justify-content:center;align-items:flex-start;flex-wrap:wrap;">
      <!-- Vista dall'alto -->
      <div style="text-align:center;">
        <div style="font-size:8px;color:#555;margin-bottom:4px;">DALL'ALTO</div>
        <img src="data:image/png;base64,${AUTO_ALTO_BASE64}" alt="Vista dall'alto" style="height:170px;" />
      </div>
      <!-- Vista frontale/laterale -->
      <div style="text-align:center;">
        <div style="font-size:8px;color:#555;margin-bottom:4px;">FRONTALE / LATERALE</div>
        <img src="data:image/png;base64,${AUTO_FL_BASE64}" alt="Vista frontale e laterale" style="height:170px;" />
      </div>
    </div>
  </div>

  <!-- Firme -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:16px 0;border:1px solid #333;padding:12px;">
    <div>
      <div style="font-size:9px;font-weight:900;text-transform:uppercase;color:#555;margin-bottom:8px;">CLIENTE</div>
      <div style="margin-bottom:20px;">
        <div style="font-size:8px;color:#777;margin-bottom:2px;">Firma per accettazione intervento</div>
        <div style="border-bottom:1px solid #333;height:35px;margin-top:4px;"></div>
      </div>
      <div>
        <div style="font-size:8px;color:#777;margin-bottom:2px;">Firma per accettazione condizioni</div>
        <div style="border-bottom:1px solid #333;height:35px;margin-top:4px;"></div>
      </div>
    </div>
    <div>
      <div style="font-size:9px;font-weight:900;text-transform:uppercase;color:#555;margin-bottom:8px;">ACCETTATORE</div>
      <div style="margin-bottom:20px;">
        <div style="font-size:8px;color:#777;margin-bottom:2px;">Firma e timbro</div>
        <div style="border-bottom:1px solid #333;height:35px;margin-top:4px;"></div>
      </div>
      <div>
        <div style="font-size:8px;color:#777;margin-bottom:2px;">Firma di chiusura ODL</div>
        <div style="border-bottom:1px solid #333;height:35px;margin-top:4px;"></div>
      </div>
    </div>
  </div>` : `
  <div class="footer-box">
    <div class="cell"><div class="cell-label">Manodopera</div><div class="cell-val">${fmtE(manoTot)}</div></div>
    <div class="cell"><div class="cell-label">Materiale</div><div class="cell-val">${fmtE(matTot)}</div></div>
    <div class="cell"><div class="cell-label">Note</div><div style="font-size:10px;">${doc.note||'—'}</div></div>
    <div class="cell"><div class="cell-label">Firma</div></div>
  </div>`;

  const privacyHTML = isODL ? `
  <div style="margin-top:12px;border-top:1px solid #ccc;padding-top:8px;">
    <div style="font-size:7px;color:#666;line-height:1.5;text-align:justify;">
      <b>CONDIZIONI DI INTERVENTO</b> — Con la firma della presente commessa il Cliente dichiara di: 1. Autorizzare Gruppo Autoscala S.r.l. ad eseguire i lavori richiesti e quelli che risultassero necessari per assicurare il sicuro e regolare funzionamento del veicolo. 2. Autorizzare il personale di Autoscala ad effettuare, ove fosse necessario, prove su strada. 3. Accettare la riparazione con l'avvenuta consegna del veicolo e rinunciare alle parti sostituite. 5. Accettare quale Foro competente quello della sede legale di Gruppo Autoscala S.r.l. ove si è eseguito l'intervento. 6. Aver indicato correttamente il numero di codice fiscale o il numero della partita IVA.
      <br><br>
      <b>INFORMATIVA PRIVACY (Reg. UE 679/2016 e D.lgs. 101/2018)</b> — Il Cliente dichiara di aver ricevuto l'informativa sul trattamento dei dati personali da parte di Gruppo Autoscala S.r.l. (Titolare del trattamento). I dati personali forniti saranno trattati per le seguenti finalità: (a) esecuzione del contratto di assistenza e adempimenti connessi; (b) obblighi di legge; (c) previo consenso, invio di comunicazioni commerciali/promozionali. I dati non saranno ceduti a terzi salvo obblighi di legge. Il Cliente ha diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento, rivolgendosi a: privacy@gruppoautoscala.com.
      <br><br>
      <div style="display:flex;gap:40px;margin-top:6px;">
        <span>☐ Presto il consenso al trattamento per finalità promozionali (punto c)</span>
        <span>☐ Nego il consenso al trattamento per finalità promozionali (punto c)</span>
      </div>
      <div style="margin-top:4px;">
        Le clausole di cui ai punti 1–3–4–5 vengono espressamente e specificamente approvate ai sensi dell'articolo 1341 C.C.
      </div>
    </div>
  </div>` : `<div class="disclaimer">* Documento non valido ai fini fiscali — FATTURA OFFICINA</div>`;

  win.document.write(`<!DOCTYPE html><html><head><title>${titoloDoc}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;font-size:11px;color:#111;background:#fff;padding:20px;}
  .page{max-width:800px;margin:0 auto;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;border-bottom:2px solid #000;padding-bottom:12px;}
  .logo{font-size:22px;font-weight:900;letter-spacing:-1px;}
  .logo span{font-size:10px;font-weight:400;letter-spacing:2px;display:block;margin-bottom:2px;}
  .azienda-info{font-size:10px;line-height:1.6;}
  .dest-box{border:1px solid #333;padding:10px 14px;font-size:11px;min-width:220px;}
  .cell-label{font-size:8px;color:#555;text-transform:uppercase;}
  .cell-val{font-size:11px;font-weight:700;margin-top:1px;}
  .dati-veicolo{display:grid;grid-template-columns:repeat(5,1fr);border:1px solid #333;margin-bottom:0;}
  .dati-veicolo .cell{border-right:1px solid #333;padding:4px 6px;}
  .dati-veicolo .cell:last-child{border-right:none;}
  .dati2{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid #333;border-top:none;margin-bottom:8px;}
  .dati2 .cell{border-right:1px solid #333;padding:4px 6px;}
  .dati2 .cell:last-child{border-right:none;}
  table{width:100%;border-collapse:collapse;margin-bottom:0;}
  th{background:#1e293b;color:#fff;padding:5px 6px;font-size:9px;text-transform:uppercase;text-align:left;}
  .totali-box{display:flex;justify-content:flex-end;margin-bottom:8px;}
  .totali-inner{border:1px solid #333;min-width:300px;}
  .tot-row{display:flex;justify-content:space-between;padding:4px 10px;border-bottom:1px solid #ddd;font-size:11px;}
  .tot-finale{display:flex;justify-content:space-between;padding:6px 10px;font-size:15px;font-weight:900;background:#f0f0f0;}
  .footer-box{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;border:1px solid #333;margin-bottom:8px;}
  .footer-box .cell{border-right:1px solid #333;padding:4px 6px;}
  .footer-box .cell:last-child{border-right:none;}
  .disclaimer{font-size:9px;color:#555;margin-top:12px;border-top:1px solid #ddd;padding-top:8px;}
  @media print{button{display:none!important}}
</style></head><body>
<button onclick="window.print()" style="background:#1e293b;color:#fff;border:none;padding:8px 20px;border-radius:6px;cursor:pointer;margin-bottom:16px;font-size:12px;font-weight:700;">🖨️ Stampa / Salva PDF</button>
<div class="page">
  <div class="header">
    <div>
      <img src="data:image/png;base64,${LOGO_GAS_BASE64}" alt="Gruppo Autoscala" style="height:52px;display:block;" />
      <div class="azienda-info" style="margin-top:8px;">
        Ragione Sociale: Gruppo Autoscala S.r.l.<br>
        Sede Legale: Via Antiniana, 2/B - 80078 Pozzuoli (NA)<br>
        Tel. 0811865872 · amministrazione@gruppoautoscala.com
      </div>
    </div>
    <div>
      <div class="dest-box">
        <div class="cell-label">${titoloDoc}</div>
        <div style="font-size:15px;font-weight:900;margin:4px 0;">${numDoc}</div>
        <div class="cell-label">Data: <b>${dataDoc}</b></div>
        ${isODL && doc.stato ? `<div style="margin-top:4px;padding:2px 8px;background:#1e293b;color:#fff;border-radius:4px;font-size:10px;display:inline-block;">${doc.stato.toUpperCase()}</div>` : ''}
      </div>
      <div style="border:1px solid #333;border-top:none;padding:8px 14px;font-size:11px;">
        <div class="cell-label" style="margin-bottom:5px;">Cliente / Destinatario</div>
        <div style="font-weight:900;font-size:13px;margin-bottom:5px;">${doc.societaCliente || ((doc.nomeCliente||'') + ' ' + (doc.cognomeCliente||'')).trim() || doc.nomeCliente || '—'}</div>
        ${doc.indirizzoCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">Indirizzo</span><span>${doc.indirizzoCliente}</span></div>` : ''}
        ${(doc.capCliente || doc.cittaCliente || doc.provinciaCliente) ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">Città</span><span>${[doc.capCliente, doc.cittaCliente, doc.provinciaCliente ? `(${doc.provinciaCliente})` : ''].filter(Boolean).join(' ')}</span></div>` : ''}
        ${doc.pivaCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">P.IVA</span><span>${doc.pivaCliente}</span></div>` : ''}
        ${doc.cfCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">Cod. Fiscale</span><span>${doc.cfCliente}</span></div>` : ''}
        ${doc.emailCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">Email</span><span>${doc.emailCliente}</span></div>` : ''}
        ${doc.cellulareCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">Cellulare</span><span>${doc.cellulareCliente}</span></div>` : ''}
        ${doc.sdiCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">SDI</span><span>${doc.sdiCliente}</span></div>` : ''}
        ${doc.pecCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:65px;">PEC</span><span>${doc.pecCliente}</span></div>` : ''}
      </div>
    </div>
  </div>

  <div class="dati-veicolo">
    <div class="cell"><div class="cell-label">Targa</div><div class="cell-val">${doc.targa||'—'}</div></div>
    <div class="cell"><div class="cell-label">Telaio</div><div class="cell-val" style="font-size:9px;">${doc.telaio||'—'}</div></div>
    <div class="cell"><div class="cell-label">Km</div><div class="cell-val">${doc.km||'—'}</div></div>
    <div class="cell"><div class="cell-label">Immatricolazione</div><div class="cell-val">${doc.dataImmatricolazione||'—'}</div></div>
    <div class="cell"><div class="cell-label">Marca / Modello</div><div class="cell-val">${doc.marca||''} ${doc.modello||''}</div></div>
  </div>
  <div class="dati2">
    <div class="cell"><div class="cell-label">Cilindrata (cc)</div><div class="cell-val">${doc.cc||'—'}</div></div>
    <div class="cell"><div class="cell-label">Kw / CV</div><div class="cell-val">${doc.kwcv||'—'}</div></div>
    <div class="cell"><div class="cell-label">N° Motore</div><div class="cell-val">${doc.nMotore||'—'}</div></div>
    ${isODL ? `<div class="cell"><div class="cell-label">Accettatore</div><div class="cell-val">${doc.accettatore||'—'}</div></div>` : ''}
  </div>

  <div style="font-size:9px;font-weight:900;background:#1e293b;color:#fff;padding:4px 8px;margin-bottom:0;">LAVORAZIONI RICHIESTE</div>
  <table>
    ${!mostraPrezzi ? theadODL : theadFAT}
    <tbody>${righeHTML}</tbody>
  </table>

  ${totaliHTML}
  ${footerHTML}
  ${privacyHTML}
</div></body></html>`);
  win.document.close();
  win.focus();
}


// ─── ODL — ORDINI DI LAVORO ──────────────────────────────────────────────────
function ODLView({ T, isMobile, db, stockVehicles, ricambi, user, onOpenPlanning, odlPrecompilato, onConsumaPrecompilato }) {
  // Stessa restrizione per sede della vista Planning — un utente service
  // può creare ODL solo per la propria sede (admin/super_admin liberi).
  const ruoloOdl = (user?.ruolo || '').toLowerCase().trim();
  const sedeUtenteOdl = (user?.sede || '').toLowerCase().trim();
  const puoVedereTutteLeSediOdl = ['admin', 'super_admin'].includes(ruoloOdl);
  const sediConsentiteOdl = puoVedereTutteLeSediOdl ? ['agnano', 'salerno'] : (['agnano','salerno'].includes(sedeUtenteOdl) ? [sedeUtenteOdl] : []);
  const COSTO_MO = 47;
  const IVA = 0.22;
  const [vista, setVista]       = React.useState('lista'); // 'lista'|'nuovo'|'dettaglio'|'preventivi'
  const [odlList, setOdlList]   = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [loading, setLoading]   = React.useState(true);
  const [saving, setSaving]     = React.useState(false);
  const [filtro, setFiltro]     = React.useState('');
  const [filtroStato, setFiltroStato] = React.useState('tutti');

  // ─── Preventivi Generati — caricati a chiamata solo quando si apre
  // questa vista, non ad ogni apertura di Service/ODL.
  const [preventiviList, setPreventiviList] = React.useState([]);
  const [loadingPreventivi, setLoadingPreventivi] = React.useState(false);
  React.useEffect(() => {
    if (vista !== 'preventivi') return;
    setLoadingPreventivi(true);
    get(ref(db, 'preventivi_officina')).then(snap => {
      const lista = snap.val() ? Object.values(snap.val()) : [];
      setPreventiviList(lista.sort((a, b) => new Date(b.creatoAt||0) - new Date(a.creatoAt||0)));
      setLoadingPreventivi(false);
    });
  }, [vista]);
  // Filtra: nasconde i convertiti in fattura e quelli più vecchi di 30
  // giorni — restano comunque nel database, solo non più mostrati qui.
  const preventiviVisibili = React.useMemo(() => {
    const trentaGiorniFa = new Date(); trentaGiorniFa.setDate(trentaGiorniFa.getDate() - 30);
    return preventiviList.filter(p => !p.convertito && new Date(p.creatoAt || 0) >= trentaGiorniFa);
  }, [preventiviList]);

  // ─── Genera Preventivo da ODL ────────────────────────────────────────
  // Crea uno snapshot dell'ODL (righe con prezzi già presenti nei dati,
  // anche se l'ODL stampato non li mostra) in un nodo dedicato
  // preventivi_officina — separato sia da odl che da service_interventi.
  // Non tocca il magazzino (a differenza della fatturazione), non
  // modifica l'ODL stesso: è solo un documento da mostrare al cliente.
  const generaPreventivoDaODL = async (odl) => {
    // La finestra va aperta SUBITO, in modo sincrono nel click — se
    // aspettassimo il salvataggio su Firebase (asincrono) prima di aprirla,
    // il browser la bloccherebbe silenziosamente, perché non sarebbe più
    // percepita come "direttamente" originata dal click dell'utente.
    const win = window.open('', '_blank');
    if (!win) {
      alert('⚠️ Il browser ha bloccato il popup. Consenti i popup per radargas.it e riprova.');
      return;
    }
    const id = `PREV-${Date.now()}`;
    const preventivoGrezzo = {
      id,
      odlId: odl.id,
      nomeCliente: odl.nomeCliente, cognomeCliente: odl.cognomeCliente, societaCliente: odl.societaCliente,
      indirizzoCliente: odl.indirizzoCliente, cittaCliente: odl.cittaCliente, capCliente: odl.capCliente, provinciaCliente: odl.provinciaCliente,
      pivaCliente: odl.pivaCliente, cfCliente: odl.cfCliente, sdiCliente: odl.sdiCliente, pecCliente: odl.pecCliente,
      emailCliente: odl.emailCliente, cellulareCliente: odl.cellulareCliente,
      targa: odl.targa, telaio: odl.telaio, km: odl.km, dataImmatricolazione: odl.dataImmatricolazione,
      marca: odl.marca, modello: odl.modello, cc: odl.cc, kwcv: odl.kwcv, nMotore: odl.nMotore,
      righe: odl.righe || [],
      note: odl.note || '',
      dataOdl: odl.dataOdl,
      // Opzioni IVA dell'ODL: senza queste il preventivo trattava i prezzi
      // IVA inclusa come netti e ci aggiungeva di nuovo il 22%
      ordineInterno: !!odl.ordineInterno,
      applicaIva: odl.applicaIva !== false,
      prezziIvaInclusa: !!odl.prezziIvaInclusa,
      creatoAt: new Date().toISOString(),
      creatoDa: user?.nome ? `${user.nome} ${user.cognome || ''}`.trim() : '',
      convertito: false,
    };
    // Firebase rifiuta "undefined" nei salvataggi (l'ODL non ha tutti i
    // campi che il documento stampato può mostrare, es. cognomeCliente) —
    // sanificazione ricorsiva: sostituisce ogni undefined con stringa
    // vuota, anche dentro oggetti/array annidati come le righe.
    const sanificaUndefined = (val) => {
      if (val === undefined) return '';
      if (Array.isArray(val)) return val.map(sanificaUndefined);
      if (val && typeof val === 'object') return Object.fromEntries(Object.entries(val).map(([k, v]) => [k, sanificaUndefined(v)]));
      return val;
    };
    const preventivo = sanificaUndefined(preventivoGrezzo);
    try {
      await set(ref(db, `preventivi_officina/${id}`), preventivo);
      stampaDocumentoOfficina(preventivo, 'preventivo', win);
    } catch (e) {
      win.document.write(`<html><body style="font-family:sans-serif;padding:40px;text-align:center;">
        <h2 style="color:#ef4444;">⚠️ Errore nel salvataggio del preventivo</h2>
        <p>${e.message}</p>
        <p style="color:#666;font-size:13px;">Puoi chiudere questa finestra e riprovare.</p>
      </body></html>`);
    }
  };

  // Form ODL
  const emptyForm = () => ({
    // Cliente
    nomeCliente: '', indirizzoCliente: '', cittaCliente: '', capCliente: '', provinciaCliente: '', pivaCliente: '', cfCliente: '',
    emailCliente: '', cellulareCliente: '', sdiCliente: '', pecCliente: '',
    // Veicolo
    targa: '', telaio: '', km: '', dataImmatricolazione: '',
    marca: '', modello: '', cc: '', kwcv: '', nMotore: '',
    // ODL
    stato: 'aperto', note: '', dataOdl: new Date().toISOString().split('T')[0],
    operatore: '', accettatore: '', ponte: '', slotOrario: '', sedePlan: sediConsentiteOdl[0] || 'agnano',
    ordineInterno: false, inGaranzia: false, applicaIva: true, prezziIvaInclusa: false,
    righe: [],
  });
  const [form, setForm]   = React.useState(emptyForm());
  const [costoMo, setCostoMo] = React.useState(COSTO_MO);
  const [searchRic, setSearchRic] = React.useState('');
  const [showRicSuggest, setShowRicSuggest] = React.useState(false);
  const [searchVeicolo, setSearchVeicolo] = React.useState('');
  const [showVeicSuggest, setShowVeicSuggest] = React.useState(false);
  const [searchCliente, setSearchCliente] = React.useState('');
  const [showCliSuggest, setShowCliSuggest] = React.useState(false);
  const [clientiDB, setClientiDB] = React.useState([]);

  // Ricevi dati precompilati dal Planning
  React.useEffect(() => {
    if (!odlPrecompilato) return;
    setSelected(null);
    setForm({
      ...emptyForm(),
      targa:       odlPrecompilato.targa || '',
      nomeCliente: odlPrecompilato.nomeCliente || '',
      note:        odlPrecompilato.note || '',
      operatore:   odlPrecompilato.operatore || '',
      dataOdl:     odlPrecompilato.dataOdl || new Date().toISOString().split('T')[0],
      slotOrario:  odlPrecompilato.slotOrario || '',
      ponte:       odlPrecompilato.ponte || '',
      sedePlan:    odlPrecompilato.sedePlan || 'agnano',
    });
    setVista('nuovo');
    if (typeof onConsumaPrecompilato === 'function') onConsumaPrecompilato();
  }, [odlPrecompilato]);

  // Carica ODL da Firebase — stesso principio degli altri due punti, cache
  // 4 ore + IndexedDB. Espone un refresh forzato (window._forzaRefreshOdl)
  // usato subito dopo un salvataggio proprio, così chi crea/modifica un
  // ODL lo vede riflesso subito senza aspettare la scadenza della cache.
  React.useEffect(() => {
    let annullato = false;
    const maxAggiornatoAt = (arr, partenza = '') =>
      arr.reduce((m, x) => (x.aggiornatoAt && x.aggiornatoAt > m) ? x.aggiornatoAt : m, partenza);
    const carica = (forzaFresco) => {
      const now = Date.now();
      if (!forzaFresco && window._radarOdlListTs && (now - window._radarOdlListTs) < 4*60*60*1000) {
        setOdlList(window._radarOdlListCache || []);
        setLoading(false);
        return;
      }
      get(ref(db, 'odl')).then(snap => {
        if (annullato) return;
        const raw = snap.val() || {};
        const list = Object.entries(raw).map(([id, v]) => ({ id, ...v }))
          // Ordinato per data di CREAZIONE (dal timestamp nell'id, es.
          // "ODL-1234567890123"), non per dataOdl — quel campo è una data
          // business modificabile dall'utente e non riflette sempre
          // l'ordine reale in cui gli ODL sono stati creati.
          .sort((a, b) => (b.id||'').localeCompare(a.id||''));
        const ts = Date.now();
        window._radarOdlListCache = list;
        window._radarOdlListTs = ts;
        window._radarOdlUltimoDelta = maxAggiornatoAt(list);
        idbScrivi('odl_list_cache', { list, ts });
        setOdlList(list);
        setLoading(false);
      });
    };
    // Delta-check leggero — stesso principio già usato per contratti/
    // preventivi: invece di riscaricare tutti gli ODL ogni 2 minuti,
    // scarica solo quelli con aggiornatoAt più recente dell'ultimo
    // controllo. Così un ODL creato o modificato da un collega compare
    // per tutti entro 2 minuti, non fino a 4 ore, senza il costo di banda
    // di un vero ascoltatore in tempo reale.
    const eseguiDeltaCheckOdl = () => {
      const cutoff = window._radarOdlUltimoDelta;
      if (!cutoff) return; // nessun caricamento completo ancora fatto
      get(query(ref(db, 'odl'), orderByChild('aggiornatoAt'), startAt(cutoff))).then(snap => {
        if (annullato || !snap.exists()) return;
        const nuovi = Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }));
        if (!nuovi.length) return;
        const esistenti = window._radarOdlListCache || [];
        const mappa = new Map(esistenti.map(o => [o.id, o]));
        nuovi.forEach(o => mappa.set(o.id, o));
        const list = Array.from(mappa.values()).sort((a, b) => (b.id||'').localeCompare(a.id||''));
        const ts = Date.now();
        window._radarOdlListCache = list;
        window._radarOdlListTs = ts;
        window._radarOdlUltimoDelta = maxAggiornatoAt(nuovi, cutoff);
        idbScrivi('odl_list_cache', { list, ts });
        setOdlList(list);
      }).catch(e => console.error('[RadaR] Delta-check ODL fallito — probabile indice Firebase mancante su aggiornatoAt:', e));
    };
    if (window._radarOdlListTs) {
      carica(false);
    } else {
      idbLeggi('odl_list_cache').then(cache => {
        if (annullato) return;
        const now = Date.now();
        if (cache && (now - cache.ts) < 4*60*60*1000) {
          window._radarOdlListCache = cache.list; window._radarOdlListTs = cache.ts;
          window._radarOdlUltimoDelta = maxAggiornatoAt(cache.list);
          setOdlList(cache.list);
          setLoading(false);
        } else {
          carica(true);
        }
      });
    }
    window._forzaRefreshOdl = () => carica(true);
    const intervalDelta = setInterval(eseguiDeltaCheckOdl, 2 * 60 * 1000);
    return () => { annullato = true; clearInterval(intervalDelta); };
  }, []);


  // Carica clienti officina da Firebase
  React.useEffect(() => {
    get(ref(db, 'clienti_officina')).then(snap => {
      if (snap.exists()) {
        setClientiDB(Object.values(snap.val()));
      }
    });
  }, []);

  // Condivisione con il database clienti vendita — chi ha comprato
  // un'auto (dati nel campo "cliente" di ogni contratto) ora compare
  // anche nell'autocompletamento ODL del Service, senza doverlo
  // ridigitare da capo. In caso di stesso nominativo in entrambi gli
  // elenchi, resta quello di clienti_officina (più recente/verificato
  // per il Service) — i clienti vendita si aggiungono solo se mancanti.
  React.useEffect(() => {
    get(ref(db, 'contratti')).then(snap => {
      if (!snap.exists()) return;
      const contratti = Object.values(snap.val());
      const clientiVendita = [];
      const vistiVendita = new Set();
      for (const c of contratti) {
        const cl = c.cliente;
        if (!cl?.nome) continue;
        const nomeCompleto = `${cl.nome} ${cl.cognome || ''}`.trim();
        const chiave = nomeCompleto.toLowerCase();
        if (vistiVendita.has(chiave)) continue;
        vistiVendita.add(chiave);
        clientiVendita.push({
          nome: nomeCompleto,
          indirizzo: cl.indirizzo || '',
          citta: cl.citta || '',
          cap: cl.cap || '',
          provincia: cl.provincia || '',
          piva: cl.piva || '',
          cf: cl.codiceFiscale || '',
          email: cl.email || '',
          cellulare: cl.telefono || cl.cellulare || '',
        });
      }
      setClientiDB(prev => {
        const nomiEsistenti = new Set(prev.map(c => (c.nome||'').toLowerCase()));
        const daAggiungere = clientiVendita.filter(c => !nomiEsistenti.has(c.nome.toLowerCase()));
        return [...prev, ...daAggiungere];
      });
    });
  }, []);

  const upF = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // Calcoli
  const imponibileRighe = (form.righe||[]).filter(r=>r.tipo!=='sconto').reduce((s,r) => s + (parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0), 0);
  const totaleSconto    = (form.righe||[]).filter(r=>r.tipo==='sconto').reduce((s,r) => s + imponibileRighe * ((parseFloat(r.percentuale)||0)/100), 0);
  // IVA: sempre applicata, tranne sugli ordini interni dove si può togliere
  const conIva     = !form.ordineInterno || form.applicaIva !== false;
  // Prezzi IVA inclusa: le righe sono lordi (es. 400 €), l'IVA si scorpora
  const ivaInclusa = conIva && !!form.prezziIvaInclusa;
  const sommaRighe = Math.max(0, imponibileRighe - totaleSconto);
  const totNetto   = ivaInclusa ? sommaRighe / (1 + IVA) : sommaRighe;
  const totIVA     = conIva ? (ivaInclusa ? sommaRighe - totNetto : totNetto * IVA) : 0;
  const totLordo   = totNetto + totIVA;

  const ricFiltrati = React.useMemo(() => {
    if (!searchRic.trim()) return [];
    const s = searchRic.toLowerCase();
    return ricambi.filter(r =>
      (r.codice||'').toLowerCase().includes(s) || (r.descrizione||'').toLowerCase().includes(s)
    ).slice(0, 8);
  }, [searchRic, ricambi]);

  const veicFiltrati = React.useMemo(() => {
    if (!searchVeicolo.trim()) return [];
    const s = searchVeicolo.toLowerCase();
    const daStock = (stockVehicles||[]).filter(v =>
      (v.targa||'').toLowerCase().includes(s) || (v.marca||'').toLowerCase().includes(s) || (v.modello||'').toLowerCase().includes(s)
    ).slice(0, 6).map(v => ({ ...v, _origine: 'stock' }));
    // Veicoli già lavorati in officina (anche fuori stock), dalla scheda cliente
    const daOfficina = [];
    (clientiDB || []).forEach(c => Object.values(c.veicoli || {}).forEach(v => {
      const testo = `${v.targa || ''} ${v.telaio || ''} ${v.marca || ''} ${v.modello || ''} ${c.nome || ''}`.toLowerCase();
      if (testo.includes(s)) daOfficina.push({ ...v, vin: v.telaio, registrazione: v.dataImmatricolazione, _origine: 'officina', _cliente: c });
    }));
    return [...daOfficina.slice(0, 6), ...daStock];
  }, [searchVeicolo, stockVehicles, clientiDB]);

  const cliFiltrati = React.useMemo(() => {
    if (!searchCliente.trim()) return [];
    const s = searchCliente.toLowerCase();
    return clientiDB.filter(c => (c.nome||'').toLowerCase().includes(s)).slice(0, 6);
  }, [searchCliente, clientiDB]);

  const aggiungiRiga = (r, tipo, costoOverride) => {
    setForm(f => ({
      ...f,
      righe: [...(f.righe||[]), {
        codice: r.codice || '',
        descrizione: r.descrizione || '',
        tipo: tipo || 'ricambio',
        qta: 1,
        prezzoUnit: tipo === 'manodopera' ? (costoOverride || costoMo) : (r.prezzoNetto || 0),
      }]
    }));
  };

  const aggiungiRigaLibera = () => aggiungiRiga({ codice: '', descrizione: '' }, 'ricambio');
  const aggiungiSconto = () => setForm(f => ({
    ...f,
    righe: [...(f.righe||[]), { codice: 'SC', descrizione: 'Sconto', tipo: 'sconto', percentuale: 10, qta: 1, prezzoUnit: 0 }]
  }));

  const upRiga = (idx, field, val) => {
    setForm(f => {
      const righe = [...(f.righe||[])];
      righe[idx] = { ...righe[idx], [field]: val };
      return { ...f, righe };
    });
  };

  const rimuoviRiga = (idx) => {
    setForm(f => ({ ...f, righe: (f.righe||[]).filter((_, i) => i !== idx) }));
  };

  // Veicolo dell'intervento salvato nella scheda del cliente officina, così
  // un'auto fuori stock si ritrova al prossimo intervento (ricerca per targa,
  // telaio, marca o nome cliente). Chiave: targa, altrimenti telaio.
  const salvaVeicoloInDB = async (formData) => {
    try {
      if (!formData.nomeCliente?.trim()) return;
      const keyCli = formData.nomeCliente.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const keyV = String(formData.targa || formData.telaio || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!keyCli.replace(/_/g, '') || !keyV) return;
      const veicolo = {
        targa: formData.targa || '', telaio: formData.telaio || '', marca: formData.marca || '', modello: formData.modello || '',
        km: formData.km || '', dataImmatricolazione: formData.dataImmatricolazione || '', cc: formData.cc || '',
        kwcv: formData.kwcv || '', nMotore: formData.nMotore || '', aggiornatoAt: new Date().toISOString(),
      };
      await update(ref(db, `clienti_officina/${keyCli}/veicoli/${keyV}`), veicolo);
      setClientiDB(prev => prev.map(c => (c.nome || '').toLowerCase() === formData.nomeCliente.toLowerCase()
        ? { ...c, veicoli: { ...(c.veicoli || {}), [keyV]: veicolo } } : c));
    } catch (e) { /* non blocca il salvataggio dell'ODL */ }
  };

  const salvaClienteInDB = async (formData) => {
    if (!formData.nomeCliente?.trim()) return;
    const key = formData.nomeCliente.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (!key.replace(/_/g, '')) return;   // nome senza lettere/numeri: niente anagrafica (evita di scrivere sull'intero nodo)
    // update e non set: set cancellava anche i veicoli salvati del cliente
    await update(ref(db, `clienti_officina/${key}`), {
      nome: formData.nomeCliente,
      indirizzo: formData.indirizzoCliente || '',
      citta: formData.cittaCliente || '',
      cap: formData.capCliente || '',
      provincia: formData.provinciaCliente || '',
      piva: formData.pivaCliente || '',
      cf: formData.cfCliente || '',
      email: formData.emailCliente || '',
      cellulare: formData.cellulareCliente || '',
      sdi: formData.sdiCliente || '',
      pec: formData.pecCliente || '',
      aggiornatoAt: new Date().toISOString(),
    });
    setClientiDB(prev => {
      const idx = prev.findIndex(c => c.nome.toLowerCase() === formData.nomeCliente.toLowerCase());
      const nuovo = { nome: formData.nomeCliente, indirizzo: formData.indirizzoCliente, citta: formData.cittaCliente, cap: formData.capCliente, provincia: formData.provinciaCliente, piva: formData.pivaCliente, cf: formData.cfCliente, email: formData.emailCliente, cellulare: formData.cellulareCliente, sdi: formData.sdiCliente, pec: formData.pecCliente };
      if (idx > -1) { const n=[...prev]; n[idx]={ ...prev[idx], ...nuovo }; return n; }
      return [...prev, nuovo];
    });
  };

  const salvaODL = async (stato) => {
    setSaving(true);
    try {
    const id = selected?.id || `ODL-${Date.now()}`;
    const odlGrezzo = {
      ...form,
      id,
      stato: stato || form.stato,
      totaleNetto: totNetto,
      totaleIVA: totIVA,
      totaleLordo: totLordo,
      creatoAt: selected?.creatoAt || new Date().toISOString(),
      aggiornatoAt: new Date().toISOString(),
      operatoreCreazione: user?.nome ? `${user.nome} ${user.cognome||''}`.trim() : '',
    };
    // Firebase rifiuta l'intero salvataggio se anche un solo campo è
    // undefined o NaN: si ripulisce l'ODL prima di scriverlo
    const odl = JSON.parse(JSON.stringify(odlGrezzo, (k, v) => (typeof v === 'number' && !isFinite(v) ? 0 : v)));
    // Salva cliente officina ad ogni salvataggio ODL
    await salvaClienteInDB(form);
    await salvaVeicoloInDB(form);
    await set(ref(db, `odl/${id}`), odl);
    try { window._forzaRefreshOdl?.(); } catch (e) {}
    await salvaClienteInDB(form);
    await salvaVeicoloInDB(form);

    // Aggiorna slot nel planning se ODL proviene da uno slot
    if (form.slotOrario && form.ponte && form.dataOdl) {
      const sedePlan = form.sedePlan || 'agnano';
      const planKey = `planning_officina/${sedePlan}/${form.dataOdl}/ponte_${form.ponte}/${form.slotOrario.replace(':', '_')}`;
      const slotEsistente = await get(ref(db, planKey));
      const datiEsistenti = slotEsistente.val() || {};
      await set(ref(db, planKey), {
        ...datiEsistenti,
        operatore:    form.operatore || datiEsistenti.operatore || '',
        cliente:      form.nomeCliente || datiEsistenti.cliente || '',
        targa:        form.targa || datiEsistenti.targa || '',
        nota:         form.note || datiEsistenti.note || '',
        marca:        form.marca || datiEsistenti.marca || '',
        modello:      form.modello || datiEsistenti.modello || '',
        preventivoId: id,
        totaleLordo:  totLordo,
        salvatoAt:    new Date().toISOString(),
      });
    }

    // Scala ricambi dal magazzino — sulla sede specifica dell'ODL
    // (form.sedePlan), non solo sul totale. Prima veniva scalato solo il
    // campo "esistenza" complessivo: la card sembrava "non scaricare"
    // guardando lo Stock filtrato per una sede specifica, perché
    // esistenzaAgnano/esistenzaSalerno restavano invariati — stesso
    // schema già corretto oggi per i carichi da bolla.
    let erroriScarico = 0;
    // Ogni riga si scarica dal magazzino UNA volta sola: prima ogni nuovo
    // salvataggio dello stesso ODL riscaricava di nuovo gli stessi ricambi
    const righeAggiornate = (form.righe || []).map(r => ({ ...r }));
    let nuoviScarichi = 0;
    for (const r of righeAggiornate) {
      if (r.tipo !== 'manodopera' && r.codice && r.codice !== '' && !r.scaricato) {
        const key = r.codice.replace(/[./ ]/g, '_');
        try {
          const snap = await get(ref(db, `service_ricambi/${key}`));
          const articolo = snap.val() || {};
          const sedeOdl = (form.sedePlan || 'agnano') === 'salerno' ? 'Salerno' : 'Agnano';
          const esistenzaAgnanoAttuale = articolo.esistenzaAgnano !== undefined ? parseFloat(articolo.esistenzaAgnano) || 0 : (articolo.esistenzaSalerno === undefined ? parseFloat(articolo.esistenza) || 0 : 0);
          const esistenzaSalernoAttuale = parseFloat(articolo.esistenzaSalerno) || 0;
          const qta = parseFloat(r.qta) || 0;
          const nuovaAgnano = sedeOdl === 'Agnano' ? Math.max(0, esistenzaAgnanoAttuale - qta) : esistenzaAgnanoAttuale;
          const nuovaSalerno = sedeOdl === 'Salerno' ? Math.max(0, esistenzaSalernoAttuale - qta) : esistenzaSalernoAttuale;
          await update(ref(db, `service_ricambi/${key}`), {
            esistenzaAgnano: nuovaAgnano, esistenzaSalerno: nuovaSalerno, esistenza: nuovaAgnano + nuovaSalerno,
          });
          r.scaricato = true; nuoviScarichi++;
        } catch(e) {
          erroriScarico++;
          console.error(`[RadaR] Scarico ricambio ${r.codice} fallito:`, e);
        }
      }
    }
    if (nuoviScarichi > 0) {
      try { await update(ref(db, `odl/${id}`), { righe: JSON.parse(JSON.stringify(righeAggiornate)) }); } catch (e) { /* non bloccante */ }
    }
    if (erroriScarico > 0) {
      alert(`⚠️ Attenzione: ${erroriScarico} ricambi non sono stati scaricati correttamente dal magazzino. Controlla manualmente le quantità in Service → Ricambi.`);
    }
    setSaving(false);
    setVista('lista');
    setSelected(null);
    setForm(emptyForm());
    } catch (e) {
      // Prima un errore lasciava il pulsante bloccato su "salvataggio" senza dire nulla
      console.error('[RadaR] Salvataggio ODL non riuscito:', e);
      const msg = String(e?.message || e);
      alert(/permission/i.test(msg)
        ? '⚠️ ODL NON salvato: permesso negato da Firebase.\nL\'utenza non ha i permessi di scrittura (profilo utente mancante o disattivato). Contatta l\'amministratore.'
        : `⚠️ ODL NON salvato.\n\nErrore: ${msg}\n\nFai uno screenshot di questo messaggio e mandalo all'amministratore.`);
      setSaving(false);
    }
  };

  const generaFattura = async () => {
    // Crea record in service_interventi (storico)
    setSaving(true);
    const id = `FAT-${Date.now()}`;

    // Numerazione dedicata per le fatture officina di SALERNO — formato
    // S26/0001, S26/0002, ecc. Le prime due (Cernera, Micromeccanica SRL)
    // sono state assegnate manualmente; da qui in poi si genera da sola,
    // sempre partendo dal numero più alto già usato (mai dal conteggio:
    // stesso motivo per cui l'avevamo corretto sui DDT — un'eventuale
    // fattura eliminata non deve far ripartire la sequenza da un numero
    // già usato). Lettura sempre fresca da Firebase, non dalla cache,
    // per lo stesso motivo.
    let numeroFattura = null;
    if ((form.sedePlan || 'agnano') === 'salerno') {
      const snapEsistenti = await get(ref(db, 'service_interventi'));
      const esistenti = snapEsistenti.exists() ? Object.values(snapEsistenti.val()) : [];
      const massimo = esistenti.reduce((max, f) => {
        const m = /^S26\/(\d+)$/.exec(f.numeroFattura || '');
        const n = m ? parseInt(m[1], 10) : 0;
        return n > max ? n : max;
      }, 0);
      numeroFattura = `S26/${String(massimo + 1).padStart(4, '0')}`;
      // Rifatturazione dopo l'eliminazione della fattura (es. per correggerla):
      // si riusa il numero che aveva, se nel frattempo non è stato preso
      const precedente = form.numeroFatturaPrecedente;
      if (precedente && /^S26\/\d+$/.test(precedente) && !esistenti.some(f => f.numeroFattura === precedente)) {
        numeroFattura = precedente;
      }
    }

    const fattura = {
      id,
      ...(numeroFattura ? { numeroFattura } : {}),
      tipo: 'fattura_officina',
      odlId: selected?.id || '',
      // Tutti i dati cliente dell'ODL (nome, cognome, società, indirizzo,
      // città, CAP, provincia, P.IVA, CF, SDI, PEC, email, cellulare…):
      // prima ne venivano copiati solo alcuni e la fattura restava incompleta
      ...datiClienteDa(form),
      targa: form.targa,
      telaio: form.telaio,
      km: form.km,
      dataImmatricolazione: form.dataImmatricolazione,
      marca: form.marca,
      modello: form.modello,
      cc: form.cc,
      kwcv: form.kwcv,
      nMotore: form.nMotore,
      // Prezzi IVA inclusa: in fattura le righe vanno al NETTO (prezzo / 1,22,
      // con decimali pieni così i totali tornano al centesimo, anche nell'XML)
      righe: ivaInclusa
        ? (form.righe || []).map(r => r.tipo === 'sconto' ? r : { ...r, prezzoUnit: Math.round((parseFloat(r.prezzoUnit) || 0) / (1 + IVA) * 1e6) / 1e6, prezzoIvaInclusa: parseFloat(r.prezzoUnit) || 0 })
        : form.righe,
      totaleNetto: totNetto,
      totaleIVA: totIVA,
      totaleLordo: totLordo,
      ...(conIva ? {} : { senzaIva: true }),
      note: form.note,
      accettatore: form.accettatore || '',
      data: new Date().toISOString().split('T')[0],
      creatoAt: new Date().toISOString(),
    };
    await set(ref(db, `service_interventi/${id}`), fattura);
    // Salva/aggiorna cliente officina
    await salvaClienteInDB(form);
    await salvaVeicoloInDB(form);
    // Aggiorna ODL come chiuso
    if (selected?.id) {
      await set(ref(db, `odl/${selected.id}/stato`), 'chiuso');
      await set(ref(db, `odl/${selected.id}/fatturaId`), id);
      await set(ref(db, `odl/${selected.id}/aggiornatoAt`), new Date().toISOString());
      try { window._forzaRefreshOdl?.(); } catch (e) {}
      // Marca come "convertiti" tutti i preventivi generati da questo ODL
      // — spariscono dalla vista elenco preventivi, restano nel database.
      try {
        const snapPrev = await get(query(ref(db, 'preventivi_officina'), orderByChild('odlId'), equalTo(selected.id)));
        if (snapPrev.exists()) {
          const aggiornamenti = {};
          Object.keys(snapPrev.val()).forEach(pid => { aggiornamenti[`${pid}/convertito`] = true; aggiornamenti[`${pid}/fatturaId`] = id; });
          await update(ref(db, 'preventivi_officina'), aggiornamenti);
        }
      } catch (e) { /* nessun preventivo collegato, o indice non ancora attivo — non bloccante */ }
    }
    if (selected?.id && form.numeroFatturaPrecedente) {
      try { await update(ref(db, `odl/${selected.id}`), { numeroFatturaPrecedente: null }); } catch (e) { /* non bloccante */ }
    }
    aggiornaCacheDopoFattura(fattura, selected?.id ? { ...form, id: selected.id, stato: 'chiuso', fatturaId: id, aggiornatoAt: new Date().toISOString() } : null);
    setSaving(false);
    alert(`Fattura ${id} generata e salvata nello storico!`);
    setVista('lista');
    setSelected(null);
    setForm(emptyForm());
  };

  const apriODL = (odl) => {
    setSelected(odl);
    setForm({ ...emptyForm(), ...odl });
    setVista('dettaglio');
  };

  const odlFiltrati = React.useMemo(() => {
    let list = odlList;
    if (filtroStato !== 'tutti') list = list.filter(o => o.stato === filtroStato);
    if (filtro.trim()) {
      const s = filtro.toLowerCase();
      list = list.filter(o =>
        (o.nomeCliente||'').toLowerCase().includes(s) ||
        (o.targa||'').toLowerCase().includes(s) ||
        (o.telaio||'').toLowerCase().includes(s) ||
        (o.id||'').toLowerCase().includes(s)
      );
    }
    return list;
  }, [odlList, filtro, filtroStato]);

  const statoColor = { aperto: '#f59e0b', in_lavorazione: '#6366f1', chiuso: '#10b981', annullato: '#ef4444' };
  const statoLabel = { aperto: 'Aperto', in_lavorazione: 'In Lavorazione', chiuso: 'Chiuso', annullato: 'Annullato' };

  const iS = { padding: '9px 12px', background: T.inputBg||T.bg, border: `1px solid ${T.border2||T.border}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '9px', fontWeight: '900', color: T.textMuted||T.textSub, letterSpacing: '1px', marginBottom: '4px', textTransform: 'uppercase', display: 'block' };
  const sectionStyle = { background: T.surface||T.card, borderRadius: '14px', border: `1px solid ${T.border}`, padding: '14px 16px', marginBottom: '12px' };
  const sectionTitle = { fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '12px' };

  if (vista === 'lista') return (
    <div>
      {/* Header lista */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
        <input value={filtro} onChange={e => setFiltro(e.target.value)}
          placeholder="🔍 Cerca cliente, targa, telaio, ID..."
          style={{ ...iS, flex: 1, minWidth: '180px', marginBottom: 0 }} />
        <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)}
          style={{ ...iS, width: 'auto', minWidth: '130px', marginBottom: 0 }}>
          <option value="tutti">Tutti gli stati</option>
          <option value="aperto">Aperti</option>
          <option value="in_lavorazione">In Lavorazione</option>
          <option value="chiuso">Chiusi</option>
          <option value="annullato">Annullati</option>
        </select>
        <button onClick={() => { setSelected(null); setForm(emptyForm()); setVista('nuovo'); }}
          style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: '900', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + Nuovo ODL
        </button>
        <button onClick={() => setVista('preventivi')}
          style={{ padding: '10px 18px', borderRadius: '10px', border: '2px solid #10b981', background: 'transparent', color: '#10b981', fontWeight: '900', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          📝 Preventivi Generati
        </button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted||T.textSub }}>Caricamento...</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {odlFiltrati.map(odl => (
          <div key={odl.id}
            style={{ background: T.surface||T.card, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: '150px', cursor: 'pointer' }} onClick={() => apriODL(odl)}>
              <div style={{ fontSize: '10px', color: '#6366f1', fontWeight: '900' }}>{odl.id}</div>
              <div style={{ fontSize: '14px', fontWeight: '800', color: T.text }}>
                {odl.nomeCliente || '—'}
                {odl.inGaranzia && <span style={{ fontSize: '9px', background: 'rgba(16,185,129,0.15)', border: '1px solid #10b981', borderRadius: '4px', padding: '1px 6px', color: '#10b981', marginLeft: '6px', fontWeight: '900', verticalAlign: 'middle' }}>🛡️ GARANZIA</span>}
              </div>
              <div style={{ fontSize: '11px', color: T.textMuted||T.textSub }}>
                {odl.targa && <span style={{ marginRight: '10px' }}>🏷 {odl.targa}</span>}
                {odl.marca && <span>{odl.marca} {odl.modello}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ padding: '3px 10px', borderRadius: '20px', background: (statoColor[odl.stato]||'#6366f1')+'22', border: `1px solid ${statoColor[odl.stato]||'#6366f1'}`, color: statoColor[odl.stato]||'#6366f1', fontSize: '11px', fontWeight: '700' }}>
                {statoLabel[odl.stato]||odl.stato}
              </div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>
                €{(odl.totaleLordo||0).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}
              </div>
              <button onClick={() => apriODL(odl)}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #f59e0b', background: 'transparent', color: '#f59e0b', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}
                title="Modifica ODL">
                ✏️ Modifica
              </button>
              <button onClick={() => stampaDocumentoOfficina(odl)}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #6366f1', background: 'transparent', color: '#6366f1', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}
                title="Stampa ODL (senza prezzi)">
                🖨️
              </button>
              <button onClick={() => generaPreventivoDaODL(odl)}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #10b981', background: 'transparent', color: '#10b981', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}
                title="Genera Preventivo (con prezzi, da mostrare al cliente)">
                📝 Preventivo
              </button>
              <button onClick={async () => {
                if (!window.confirm('Eliminare ODL ' + odl.id + '?')) return;
                await set(ref(db, `odl/${odl.id}`), null);
                try { window._forzaRefreshOdl?.(); } catch (e) {}
              }}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #ef4444', background: 'transparent', color: '#ef4444', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
                🗑️
              </button>
            </div>
          </div>
        ))}
        {!loading && odlFiltrati.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted||T.textSub, fontSize: '13px' }}>
            Nessun ODL trovato
          </div>
        )}
      </div>
    </div>
  );

  // ── VISTA PREVENTIVI GENERATI ────────────────────────────────────────────────
  if (vista === 'preventivi') return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <button onClick={() => setVista('lista')}
          style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${T.border}`, background: 'transparent', color: '#6366f1', fontWeight: '700', cursor: 'pointer', fontSize: '12px' }}>
          ← Lista ODL
        </button>
        <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>📝 Preventivi Generati</div>
      </div>
      <div style={{ fontSize: '11px', color: T.textMuted||T.textSub, marginBottom: '16px' }}>
        Preventivi con prezzi, generati dagli ODL — spariscono da qui quando l'ODL collegato viene fatturato, o comunque dopo 30 giorni (restano sempre nel database).
      </div>
      {loadingPreventivi && <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted||T.textSub }}>Caricamento...</div>}
      {!loadingPreventivi && preventiviVisibili.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted||T.textSub, background: T.surface||T.card, borderRadius: '14px', border: `1px solid ${T.border}` }}>
          Nessun preventivo attivo al momento.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {preventiviVisibili.map(p => (
          <div key={p.id} style={{ background: T.surface||T.card, borderRadius: '14px', border: `1px solid ${T.border}`, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>
                {p.societaCliente || `${p.nomeCliente||''} ${p.cognomeCliente||''}`.trim() || '—'}
              </div>
              <div style={{ fontSize: '11px', color: T.textMuted||T.textSub }}>
                {p.targa && <span style={{ marginRight: '10px' }}>🏷 {p.targa}</span>}
                {p.marca && <span>{p.marca} {p.modello}</span>}
                <span style={{ marginLeft: '10px' }}>· generato il {new Date(p.creatoAt).toLocaleDateString('it-IT')}</span>
                {p.creatoDa && <span> da {p.creatoDa}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button onClick={() => stampaDocumentoOfficina(p, 'preventivo')}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #10b981', background: 'transparent', color: '#10b981', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
                🖨️ Ristampa
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── FORM NUOVO/MODIFICA ODL ──────────────────────────────────────────────────
  return (
    <div>
      {/* Header form */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <button onClick={() => { setVista('lista'); setSelected(null); setForm(emptyForm()); }}
          style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${T.border}`, background: 'transparent', color: '#6366f1', fontWeight: '700', cursor: 'pointer', fontSize: '12px' }}>
          ← Lista ODL
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '16px', fontWeight: '900', color: T.text }}>
            {selected ? `ODL ${selected.id}` : 'Nuovo Ordine di Lavoro'}
          </div>
          {selected && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              {['aperto','in_lavorazione','chiuso','annullato'].map(s => (
                <button key={s} onClick={() => upF('stato', s)}
                  style={{ padding: '4px 10px', borderRadius: '20px', border: `1px solid ${statoColor[s]}`, background: form.stato===s ? statoColor[s] : 'transparent', color: form.stato===s ? '#fff' : statoColor[s], fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}>
                  {statoLabel[s]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* DATI CLIENTE */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>👤 DATI CLIENTE</div>
        {/* Ricerca cliente */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <label style={labelStyle}>Cerca cliente esistente</label>
          <input value={searchCliente} onChange={e => { setSearchCliente(e.target.value); setShowCliSuggest(true); }}
            onBlur={() => setTimeout(() => setShowCliSuggest(false), 200)}
            placeholder="Cerca per nome..."
            style={iS} />
          {showCliSuggest && cliFiltrati.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.card||T.surface, border: `1px solid ${T.border}`, borderRadius: '10px', zIndex: 50, maxHeight: '180px', overflowY: 'auto', marginTop: '4px' }}>
              {cliFiltrati.map((c, i) => (
                <div key={i} onClick={() => {
                  setForm(f => ({ ...f, nomeCliente: c.nome, indirizzoCliente: c.indirizzo||'', cittaCliente: c.citta||'', capCliente: c.cap||'', provinciaCliente: c.provincia||'', pivaCliente: c.piva||'', cfCliente: c.cf||'', emailCliente: c.email||'', cellulareCliente: c.cellulare||'', sdiCliente: c.sdi||'', pecCliente: c.pec||'' }));
                  setSearchCliente(''); setShowCliSuggest(false);
                }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: T.text }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(99,102,241,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <div style={{ fontWeight: '700' }}>{c.nome}</div>
                  <div style={{ fontSize: '10px', color: T.textMuted||T.textSub }}>{c.piva||c.cf||''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
          <div><label style={labelStyle}>Nome / Ragione Sociale *</label><input value={form.nomeCliente} onChange={e => upF('nomeCliente',e.target.value)} placeholder="Mario Rossi / Rossi SRL" style={iS} /></div>
          <div><label style={labelStyle}>Indirizzo</label><input value={form.indirizzoCliente} onChange={e => upF('indirizzoCliente',e.target.value)} placeholder="Via Roma 1" style={iS} /></div>
          <div><label style={labelStyle}>Città</label><input value={form.cittaCliente} onChange={e => upF('cittaCliente',e.target.value)} placeholder="Napoli" style={iS} /></div>
          <div><label style={labelStyle}>CAP</label><input value={form.capCliente} onChange={e => upF('capCliente',e.target.value)} placeholder="80100" style={iS} /></div>
          <div><label style={labelStyle}>Provincia</label><input value={form.provinciaCliente} onChange={e => upF('provinciaCliente',e.target.value.toUpperCase().slice(0,2))} placeholder="NA" maxLength={2} style={iS} /></div>
          <div><label style={labelStyle}>Partita IVA</label><input value={form.pivaCliente} onChange={e => upF('pivaCliente',e.target.value)} placeholder="IT07821241218" style={iS} /></div>
          <div><label style={labelStyle}>Codice Fiscale</label><input value={form.cfCliente} onChange={e => upF('cfCliente',e.target.value)} placeholder="RSSMRA80A01H703Z" style={iS} /></div>
          <div><label style={labelStyle}>Email</label><input type="email" value={form.emailCliente} onChange={e => upF('emailCliente',e.target.value)} placeholder="mario@email.it" style={iS} /></div>
          <div><label style={labelStyle}>Cellulare</label><input value={form.cellulareCliente} onChange={e => upF('cellulareCliente',e.target.value)} placeholder="+39 333 1234567" style={iS} /></div>
          <div><label style={labelStyle}>Codice SDI</label><input value={form.sdiCliente} onChange={e => upF('sdiCliente',e.target.value)} placeholder="Es. XXXXXXX" style={iS} /></div>
          <div><label style={labelStyle}>PEC</label><input value={form.pecCliente} onChange={e => upF('pecCliente',e.target.value)} placeholder="mario@pec.it" style={iS} /></div>
        </div>
      </div>

      {/* DATI VEICOLO */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>🚗 DATI VEICOLO</div>
        {/* Cerca in stock */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <label style={labelStyle}>Cerca veicolo</label>
          <input value={searchVeicolo} onChange={e => { setSearchVeicolo(e.target.value); setShowVeicSuggest(true); }}
            onBlur={() => setTimeout(() => setShowVeicSuggest(false), 200)}
            placeholder={`Cerca targa, telaio, marca o cliente — stock (${(stockVehicles||[]).length}) e auto già lavorate in officina`}
            style={iS} />
          {showVeicSuggest && veicFiltrati.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.card||T.surface, border: `1px solid ${T.border}`, borderRadius: '10px', zIndex: 50, maxHeight: '200px', overflowY: 'auto', marginTop: '4px' }}>
              {veicFiltrati.map((v, i) => (
                <div key={i} onClick={() => {
                  setForm(f => ({ ...f, targa: v.targa||'', telaio: v.vin||'', km: String(v.km||''), dataImmatricolazione: v.registrazione||'', marca: v.marca||'', modello: v.modello||'',
                    ...(v._origine === 'officina' ? {
                      cc: v.cc || f.cc || '', kwcv: v.kwcv || f.kwcv || '', nMotore: v.nMotore || f.nMotore || '',
                      // cliente compilato in automatico solo se il modulo è ancora vuoto
                      ...(!f.nomeCliente ? { nomeCliente: v._cliente?.nome || '', indirizzoCliente: v._cliente?.indirizzo || '', cittaCliente: v._cliente?.citta || '', capCliente: v._cliente?.cap || '', provinciaCliente: v._cliente?.provincia || '', pivaCliente: v._cliente?.piva || '', cfCliente: v._cliente?.cf || '', emailCliente: v._cliente?.email || '', cellulareCliente: v._cliente?.cellulare || '', sdiCliente: v._cliente?.sdi || '', pecCliente: v._cliente?.pec || '' } : {}),
                    } : {}) }));
                  setSearchVeicolo(''); setShowVeicSuggest(false);
                }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: T.text, display: 'flex', gap: '10px', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(99,102,241,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  {v.mainImg && <img src={v.mainImg} style={{ width:'44px', height:'30px', objectFit:'cover', borderRadius:'4px' }} alt="" onError={e => { e.target.style.display = 'none'; }} />}
                  <div>
                    <div style={{ fontWeight: '700' }}>{v.marca} {v.modello} <span style={{ fontSize: '9px', fontWeight: '800', color: v._origine === 'officina' ? '#8b5cf6' : '#10b981' }}>{v._origine === 'officina' ? `· OFFICINA · ${v._cliente?.nome || ''}` : '· STOCK'}</span></div>
                    <div style={{ fontSize: '10px', color: T.textMuted||T.textSub }}>{v.targa} · {v.vin ? '···'+v.vin.slice(-6) : '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: '10px' }}>
          <div><label style={labelStyle}>Targa</label><input value={form.targa} onChange={e => upF('targa',e.target.value.toUpperCase())} placeholder="AA000BB" style={{ ...iS, letterSpacing: '2px' }} /></div>
          <div><label style={labelStyle}>Telaio (VIN)</label><input value={form.telaio} onChange={e => upF('telaio',e.target.value.toUpperCase())} placeholder="VF3..." style={iS} /></div>
          <div><label style={labelStyle}>Km</label><input type="number" value={form.km} onChange={e => upF('km',e.target.value)} placeholder="75000" style={iS} /></div>
          <div><label style={labelStyle}>Data Immatricolazione</label><input value={form.dataImmatricolazione} onChange={e => upF('dataImmatricolazione',e.target.value)} placeholder="MM/YYYY" style={iS} /></div>
          <div><label style={labelStyle}>Marca</label><input value={form.marca} onChange={e => upF('marca',e.target.value)} placeholder="Volkswagen" style={iS} /></div>
          <div><label style={labelStyle}>Modello</label><input value={form.modello} onChange={e => upF('modello',e.target.value)} placeholder="Golf" style={iS} /></div>
          <div><label style={labelStyle}>Cilindrata (cc)</label><input value={form.cc} onChange={e => upF('cc',e.target.value)} placeholder="1968" style={iS} /></div>
          <div><label style={labelStyle}>Kw / CV</label><input value={form.kwcv} onChange={e => upF('kwcv',e.target.value)} placeholder="110kw / 150cv" style={iS} /></div>
          <div><label style={labelStyle}>N° Motore</label><input value={form.nMotore} onChange={e => upF('nMotore',e.target.value)} placeholder="CUTA..." style={iS} /></div>
        </div>
      </div>

      {/* INFO ODL */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>📋 INFO ODL</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px' }}>
          <div><label style={labelStyle}>Data ODL</label><input type="date" value={form.dataOdl} onChange={e => upF('dataOdl',e.target.value)} style={iS} /></div>
          <div><label style={labelStyle}>Operatore</label>
            <select value={form.operatore} onChange={e => upF('operatore',e.target.value)} style={iS}>
              <option value="">— Seleziona —</option>
              {(() => {
                // Operatori della sede dell'ODL; un operatore già salvato resta visibile anche se di altra sede
                const lista = (form.sedePlan || 'agnano') === 'salerno' ? OPERATORI_OFFICINA_SALERNO : OPERATORI_OFFICINA;
                const conAttuale = form.operatore && !lista.includes(form.operatore) ? [form.operatore, ...lista] : lista;
                return conAttuale.map(op => <option key={op} value={op}>{op}</option>);
              })()}
            </select>
          </div>
          <div><label style={labelStyle}>Accettatore</label>
            <select value={form.accettatore||''} onChange={e => upF('accettatore',e.target.value)} style={iS}>
              <option value="">— Seleziona —</option>
              {ACCETTATORI_OFFICINA.map(ac => <option key={ac} value={ac}>{ac}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: isMobile ? '1' : 'span 2' }}><label style={labelStyle}>Note</label><input value={form.note} onChange={e => upF('note',e.target.value)} placeholder="Tipo intervento..." style={iS} /></div>
        </div>
        {/* Flag ordine interno */}
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
            <div onClick={() => upF('ordineInterno', !form.ordineInterno)}
              style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.ordineInterno ? '#f59e0b' : (T.border||'#2d334a'), position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.ordineInterno ? '21px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: form.ordineInterno ? '#f59e0b' : (T.textMuted||T.textSub) }}>
              🏢 Ordine Interno {form.ordineInterno && <span style={{ fontSize: '10px', background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', borderRadius: '4px', padding: '1px 6px', marginLeft: '4px' }}>INTERNO</span>}
            </span>
          </label>
          {form.ordineInterno && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
              <div onClick={() => upF('applicaIva', form.applicaIva === false)}
                style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.applicaIva !== false ? '#6366f1' : (T.border||'#2d334a'), position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer' }}>
                <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.applicaIva !== false ? '21px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
              </div>
              <span style={{ fontSize: '12px', fontWeight: '700', color: form.applicaIva !== false ? '#6366f1' : (T.textMuted||T.textSub) }}>
                {form.applicaIva !== false ? 'IVA 22% applicata' : 'Senza IVA'}
              </span>
            </label>
          )}
          {(!form.ordineInterno || form.applicaIva !== false) && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
              <div onClick={() => upF('prezziIvaInclusa', !form.prezziIvaInclusa)}
                style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.prezziIvaInclusa ? '#0ea5e9' : (T.border||'#2d334a'), position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer' }}>
                <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.prezziIvaInclusa ? '21px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
              </div>
              <span style={{ fontSize: '12px', fontWeight: '700', color: form.prezziIvaInclusa ? '#0ea5e9' : (T.textMuted||T.textSub) }}>
                {form.prezziIvaInclusa ? 'Prezzi IVA inclusa (scorporo)' : 'Prezzi senza IVA'}
              </span>
            </label>
          )}
          {/* Flag in garanzia — stesso identico schema del toggle Ordine
              Interno, per evidenziare quali ODL sono coperti da garanzia. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
            <div onClick={() => upF('inGaranzia', !form.inGaranzia)}
              style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.inGaranzia ? '#10b981' : (T.border||'#2d334a'), position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.inGaranzia ? '21px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: form.inGaranzia ? '#10b981' : (T.textMuted||T.textSub) }}>
              🛡️ In Garanzia {form.inGaranzia && <span style={{ fontSize: '10px', background: 'rgba(16,185,129,0.15)', border: '1px solid #10b981', borderRadius: '4px', padding: '1px 6px', marginLeft: '4px' }}>GARANZIA</span>}
            </span>
          </label>
        </div>
      </div>

      {/* PIANIFICAZIONE PLANNING */}
      <div style={{ ...sectionStyle, border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.04)' }}>
        <div style={{ ...sectionTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📅 PIANIFICA NEL PLANNING <span style={{ fontSize: '9px', fontWeight: '400', color: T.textMuted||T.textSub }}>(opzionale)</span></span>
          {form.slotOrario && form.ponte && form.dataOdl && form.operatore && (
            <span style={{ fontSize: '10px', fontWeight: '700', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: '6px' }}>
              ✓ Pianificato: {form.operatore.split(' ')[0]} · Ponte {form.ponte} · {form.slotOrario} · {new Date(form.dataOdl+'T12:00:00').toLocaleDateString('it-IT',{weekday:'short',day:'2-digit',month:'2-digit'})}
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Sede</label>
            <select value={form.sedePlan||sediConsentiteOdl[0]||'agnano'} onChange={e => upF('sedePlan',e.target.value)} disabled={sediConsentiteOdl.length <= 1} style={{ ...iS, opacity: sediConsentiteOdl.length <= 1 ? 0.7 : 1 }}>
              {sediConsentiteOdl.length === 0 && <option value="">⚠️ Nessuna sede assegnata</option>}
              {sediConsentiteOdl.includes('agnano') && <option value="agnano">Agnano (9 ponti)</option>}
              {sediConsentiteOdl.includes('salerno') && <option value="salerno">Salerno (6 ponti)</option>}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Data Intervento</label>
            <input type="date" value={form.dataOdl} onChange={e => upF('dataOdl',e.target.value)} style={iS} />
          </div>
          <div>
            <label style={labelStyle}>Orario</label>
            <select value={form.slotOrario||''} onChange={e => upF('slotOrario',e.target.value)} style={iS}>
              <option value="">— Orario —</option>
              {['08:30','09:30','10:30','11:30','12:30','14:30','15:30','16:30','17:30'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Ponte</label>
            <select value={form.ponte||''} onChange={e => upF('ponte',e.target.value)} style={iS}>
              <option value="">— Ponte —</option>
              {Array.from({length: (form.sedePlan||'agnano')==='agnano'?9:5},(_,i)=>(
                <option key={i+1} value={i+1}>Ponte {i+1}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* RIGHE LAVORO */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>🔧 RIGHE LAVORO</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          {[
            { label: 'Standard', costo: 47, color: '#6366f1' },
            { label: 'Premium',  costo: 57, color: '#8b5cf6' },
            { label: 'Luxury',   costo: 67, color: '#f59e0b' },
          ].map(({ label, costo, color }) => (
            <button key={label} onClick={() => aggiungiRiga({ codice: 'MO', descrizione: `Manodopera ${label}` }, 'manodopera', costo)}
              style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${color}`, background: `${color}18`, color, fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
              + MO {label} €{costo}/h
            </button>
          ))}
          <button onClick={aggiungiRigaLibera}
            style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${T.border}`, background: 'transparent', color: T.textSub||T.textMuted, fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
            + Riga libera
          </button>
          <button onClick={aggiungiSconto}
            style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>
            % Sconto
          </button>
          <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
            <input value={searchRic} onChange={e => { setSearchRic(e.target.value); setShowRicSuggest(true); }}
              onBlur={() => setTimeout(() => setShowRicSuggest(false), 200)}
              placeholder="🔍 Cerca ricambio da aggiungere..."
              style={iS} />
            {showRicSuggest && ricFiltrati.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.card||T.surface, border: `1px solid ${T.border}`, borderRadius: '10px', zIndex: 50, maxHeight: '200px', overflowY: 'auto', marginTop: '4px' }}>
                {ricFiltrati.map((r, i) => (
                  <div key={i} onClick={() => { aggiungiRiga(r, 'ricambio'); setSearchRic(''); setShowRicSuggest(false); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: T.text }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(99,102,241,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <span style={{ color: '#6366f1', fontWeight: '700', marginRight: '8px' }}>{r.codice}</span>
                    {r.descrizione}
                    <span style={{ float: 'right', color: '#10b981', fontWeight: '700' }}>€{(r.prezzoNetto||0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabella righe */}
        {(form.righe||[]).length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: 'rgba(99,102,241,0.1)' }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#6366f1', fontWeight: '900', fontSize: '10px' }}>CODICE</th>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#6366f1', fontWeight: '900', fontSize: '10px' }}>DESCRIZIONE</th>
                  <th style={{ padding: '8px', textAlign: 'center', color: '#6366f1', fontWeight: '900', fontSize: '10px', width: '60px' }}>QTÀ</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: '#6366f1', fontWeight: '900', fontSize: '10px', width: '90px' }}>{ivaInclusa ? 'PREZZO IVA INCL.' : 'PREZZO'}</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: '#6366f1', fontWeight: '900', fontSize: '10px', width: '90px' }}>{ivaInclusa ? 'TOTALE IVA INCL.' : 'TOTALE'}</th>
                  <th style={{ width: '30px' }}></th>
                </tr>
              </thead>
              <tbody>
                {(form.righe||[]).map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: r.tipo==='sconto' ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <input value={r.codice} onChange={e => upRiga(i,'codice',e.target.value)}
                        style={{ ...iS, padding: '4px 8px', fontSize: '11px', width: '80px' }} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input value={r.descrizione} onChange={e => upRiga(i,'descrizione',e.target.value)}
                        style={{ ...iS, padding: '4px 8px', fontSize: '11px' }} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {r.tipo === 'sconto' ? (
                        <span style={{ fontSize: '11px', color: T.textSub||T.textMuted }}>—</span>
                      ) : (
                        <input type="number" value={r.qta} onChange={e => upRiga(i,'qta',e.target.value)}
                          style={{ ...iS, padding: '4px 8px', fontSize: '11px', textAlign: 'center', width: '50px' }} />
                      )}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {r.tipo === 'sconto' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input type="number" min="0" max="100" value={r.percentuale||0} onChange={e => upRiga(i,'percentuale',e.target.value)}
                            style={{ ...iS, padding: '4px 8px', fontSize: '11px', textAlign: 'right', width: '55px', color: '#f59e0b', fontWeight: '700' }} />
                          <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '700' }}>%</span>
                        </div>
                      ) : (
                        <input type="number" value={r.prezzoUnit} onChange={e => upRiga(i,'prezzoUnit',e.target.value)}
                          style={{ ...iS, padding: '4px 8px', fontSize: '11px', textAlign: 'right', width: '80px' }} />
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', color: r.tipo==='sconto' ? '#f59e0b' : T.text }}>
                      {r.tipo === 'sconto'
                        ? `- €${(imponibileRighe * ((parseFloat(r.percentuale)||0)/100)).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}`
                        : `€${((parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0)).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}`
                      }
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <button onClick={() => rimuoviRiga(i)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', fontWeight: '900' }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: T.textMuted||T.textSub, fontSize: '12px' }}>
            Nessuna riga — aggiungi manodopera o ricambi
          </div>
        )}

        {/* Totali */}
        {(form.righe||[]).length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ minWidth: '260px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              {imponibileRighe !== totNetto && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: T.text }}>
                  <span>Subtotale</span><span style={{ fontWeight: '700' }}>€{imponibileRighe.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                </div>
              )}
              {totaleSconto > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: '#f59e0b' }}>
                  <span>Sconto</span><span style={{ fontWeight: '700' }}>- €{totaleSconto.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                </div>
              )}
              {[['Imponibile', `€${totNetto.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}`],[conIva ? 'IVA 22%' : 'IVA (non applicata)', `€${totIVA.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}`]].map(([l,v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: T.text }}>
                  <span>{l}</span><span style={{ fontWeight: '700' }}>{v}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'rgba(99,102,241,0.1)', fontSize: '15px', fontWeight: '900', color: '#6366f1' }}>
                <span>TOTALE</span><span>€{totLordo.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Azioni */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px', marginBottom: '24px' }}>
        <button onClick={() => salvaODL(form.stato)} disabled={saving || !form.nomeCliente}
          style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: '900', fontSize: '13px', cursor: 'pointer', minWidth: '140px', opacity: !form.nomeCliente ? 0.5 : 1 }}>
          {saving ? '...' : '💾 Salva ODL'}
        </button>
        <button onClick={() => stampaDocumentoOfficina({ ...form, id: selected?.id || 'BOZZA' })}
          style={{ padding: '12px 18px', borderRadius: '10px', border: '2px solid #6366f1', background: 'transparent', color: '#6366f1', fontWeight: '900', fontSize: '13px', cursor: 'pointer' }}>
          🖨️ Stampa ODL
        </button>
        {selected && (
          <button onClick={() => generaPreventivoDaODL({ ...form, id: selected.id })}
            style={{ padding: '12px 18px', borderRadius: '10px', border: '2px solid #10b981', background: 'transparent', color: '#10b981', fontWeight: '900', fontSize: '13px', cursor: 'pointer' }}>
            📝 Genera Preventivo
          </button>
        )}
        {selected && form.stato !== 'chiuso' && (
          <button onClick={generaFattura} disabled={saving || !form.nomeCliente || (form.righe||[]).length === 0}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#10b981', color: '#fff', fontWeight: '900', fontSize: '13px', cursor: 'pointer', minWidth: '160px', opacity: (!form.nomeCliente||(form.righe||[]).length===0)?0.5:1 }}>
            {saving ? '...' : '🧾 Genera Fattura → Storico'}
          </button>
        )}
        {selected?.fatturaId && (
          <div style={{ padding: '12px', borderRadius: '10px', background: '#10b98115', border: '1px solid #10b981', color: '#10b981', fontWeight: '700', fontSize: '12px' }}>
            ✓ Fattura: {selected.fatturaId}
          </div>
        )}
      </div>
    </div>
  );
}



// ─── PLANNING OFFICINA ───────────────────────────────────────────────────────
const OPERATORI_OFFICINA = [
  'Pasquale Martorelli', 'Adriano Semplice', 'Antonio Esposito',
  'Leonardo Piccegna', 'Antonio Marra', 'Federico Rusciano',
];
// Operatori officina della sede di Salerno (Planning + ODL con sede Salerno)
const OPERATORI_OFFICINA_SALERNO = ['Valerio Sapere', 'Marco Filomeno'];
const ACCETTATORI_OFFICINA = [
  'Carlo Postiglione', 'Gianluca Scala', 'Clemente Palladino', 'Giulio Palma', 'Paolo della Monica',
];
const SEDI_PONTI = { agnano: 9, salerno: 6 };
const SLOT_MATTINA    = ['08:30','09:30','10:30','11:30','12:30'];
const SLOT_POMERIGGIO = ['14:30','15:30','16:30','17:30'];
const SLOT_SABATO     = ['08:30','09:30','10:30','11:30'];

function getPlanningSlots(data) {
  const dow = new Date(data).getDay();
  if (dow === 0) return [];
  if (dow === 6) return SLOT_SABATO;
  return [...SLOT_MATTINA, ...SLOT_POMERIGGIO];
}
function fmtDataBreve(iso) {
  return new Date(iso).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
}
function getSettimana(dataBase) {
  const d = new Date(dataBase);
  const dow = d.getDay();
  const lunedi = new Date(d);
  lunedi.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 6 }, (_, i) => {
    const dd = new Date(lunedi);
    dd.setDate(lunedi.getDate() + i);
    return dd.toISOString().split('T')[0];
  });
}
function coloreOperatore(nome) {
  const colori = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6'];
  // Tutte le sedi insieme, così anche gli operatori di Salerno hanno colori distinti
  const idx = [...OPERATORI_OFFICINA, ...OPERATORI_OFFICINA_SALERNO].indexOf(nome) % colori.length;
  return colori[idx >= 0 ? idx : 0];
}

function PlanningView({ user, T, isMobile, db, stockVehicles, onApriODL }) {
  // Compartimento stagno per sede: un utente service vede/gestisce SOLO la
  // pianificazione della propria sede (campo user.sede su Firebase).
  // admin/super_admin vedono e possono passare tra tutte le sedi.
  const ruoloPlan = (user?.ruolo || '').toLowerCase().trim();
  const sedeUtentePlan = (user?.sede || '').toLowerCase().trim();
  const puoVedereTutteLeSedi = ['admin', 'super_admin'].includes(ruoloPlan);
  const sediVisibili = puoVedereTutteLeSedi ? ['agnano', 'salerno'] : (SEDI_PONTI[sedeUtentePlan] ? [sedeUtentePlan] : []);
  const oggi = new Date().toISOString().split('T')[0];
  const [dataBase, setDataBase] = React.useState(oggi);
  const [sede, setSede]         = React.useState(puoVedereTutteLeSedi ? 'agnano' : (sediVisibili[0] || 'agnano'));
  // Operatori disponibili — dipende dalla sede attualmente selezionata nel
  // Planning: Agnano usa il suo elenco, Salerno il suo (vuoto finché non
  // vengono comunicati i nomi).
  const operatoriPlanning = sede === 'agnano' ? OPERATORI_OFFICINA : OPERATORI_OFFICINA_SALERNO;
  const [planning, setPlanning] = React.useState({});
  const [modal, setModal]       = React.useState(null);
  const [saving, setSaving]     = React.useState(false);
  const [selOperatore, setSelOperatore] = React.useState('');
  const [selCliente,   setSelCliente]   = React.useState('');
  const [selTarga,     setSelTarga]     = React.useState('');
  const [selNote,      setSelNote]      = React.useState('');
  const [searchV,      setSearchV]      = React.useState('');
  const [modoVeicolo,  setModoVeicolo]  = React.useState('manuale');

  const settimana = React.useMemo(() => getSettimana(dataBase), [dataBase]);

  React.useEffect(() => {
    const inizio = settimana[0];
    const fine   = settimana[settimana.length - 1];
    const unsub  = onValue(ref(db, `planning_officina/${sede}`), snap => {
      const raw = snap.val() || {};
      const filtrato = {};
      Object.entries(raw).forEach(([k, v]) => { if (k >= inizio && k <= fine) filtrato[k] = v; });
      setPlanning(filtrato);
    });
    return () => unsub();
  }, [sede, settimana]);

  const nPonti = SEDI_PONTI[sede];
  const getSlot = (data, slot, ponte) => planning?.[data]?.[`ponte_${ponte}`]?.[slot.replace(':', '_')] || null;

  const apriModal = (data, slot, ponte) => {
    const esistente = getSlot(data, slot, ponte);
    setSelOperatore(esistente?.operatore || '');
    setSelCliente(esistente?.cliente || '');
    setSelTarga(esistente?.targa || '');
    setSelNote(esistente?.note || '');
    setSearchV(''); setModoVeicolo('manuale');
    setModal({ data, slot, ponte, esistente });
  };

  const salvaSlot = async () => {
    if (!selOperatore) return;
    setSaving(true);
    const { data, slot, ponte, esistente } = modal;
    const key = `planning_officina/${sede}/${data}/ponte_${ponte}/${slot.replace(':', '_')}`;
    await set(ref(db, key), {
      operatore: selOperatore, cliente: selCliente || '', targa: selTarga || '',
      note: selNote || '', salvatoAt: new Date().toISOString(),
      // Preserva dati preventivo se esistenti
      ...(esistente?.preventivoId ? {
        preventivoId: esistente.preventivoId,
        totaleLordo:  esistente.totaleLordo,
        marca:        esistente.marca,
        modello:      esistente.modello,
      } : {}),
    });
    setSaving(false); setModal(null);
  };

  const eliminaSlot = async () => {
    if (!modal) return;
    setSaving(true);
    const { data, slot, ponte } = modal;
    await set(ref(db, `planning_officina/${sede}/${data}/ponte_${ponte}/${slot.replace(':', '_')}`), null);
    setSaving(false); setModal(null);
  };

  const veicoliFiltrati = React.useMemo(() => {
    if (!searchV.trim()) return [];
    const s = searchV.toLowerCase();
    return (stockVehicles || []).filter(v =>
      (v.targa||'').toLowerCase().includes(s) || (v.marca||'').toLowerCase().includes(s) || (v.modello||'').toLowerCase().includes(s)
    ).slice(0, 6);
  }, [searchV, stockVehicles]);

  const bg = T?.bg || '#0f1117';
  const card = T?.card || '#1a1d2e';
  const border = T?.border || '#2d334a';
  const text = T?.text || '#fff';
  const textSub = T?.textSub || '#94a3b8';

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '16px' }}>
      {/* Controlli */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {sediVisibili.map(s => (
            <button key={s} onClick={() => setSede(s)} style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '12px',
              background: sede === s ? '#6366f1' : 'transparent', color: sede === s ? '#fff' : '#6366f1',
            }}>
              🏭 {s === 'agnano' ? `Agnano (${SEDI_PONTI.agnano}p)` : `Salerno (${SEDI_PONTI.salerno}p)`}
            </button>
          ))}
          {sediVisibili.length === 0 && (
            <div style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontWeight: '700', fontSize: '12px' }}>
              ⚠️ Nessuna sede assegnata al tuo profilo — contatta un amministratore
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <button onClick={() => { const lun = new Date(settimana[0]); lun.setDate(lun.getDate()-7); setDataBase(lun.toISOString().split('T')[0]); }}
            style={{ padding: '8px 18px', borderRadius: '8px', border: '2px solid #6366f1', cursor: 'pointer', background: 'transparent', color: '#6366f1', fontSize: '18px', fontWeight: '900', lineHeight: 1 }}>‹</button>
          <span style={{ color: text, fontWeight: '800', fontSize: '13px', minWidth: '170px', textAlign: 'center' }}>
            {new Date(settimana[0]).toLocaleDateString('it-IT',{day:'2-digit',month:'short'})} – {new Date(settimana[5]).toLocaleDateString('it-IT',{day:'2-digit',month:'short',year:'numeric'})}
          </span>
          <button onClick={() => { const lun = new Date(settimana[0]); lun.setDate(lun.getDate()+7); setDataBase(lun.toISOString().split('T')[0]); }}
            style={{ padding: '8px 18px', borderRadius: '8px', border: '2px solid #6366f1', cursor: 'pointer', background: 'transparent', color: '#6366f1', fontSize: '18px', fontWeight: '900', lineHeight: 1 }}>›</button>
          <button onClick={() => setDataBase(oggi)}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '2px solid #6366f1', cursor: 'pointer', background: '#6366f1', color: '#fff', fontWeight: '900', fontSize: '12px', letterSpacing: '1px' }}>OGGI</button>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(99,102,241,0.07)', borderRadius: '12px', border: '1px solid rgba(99,102,241,0.2)' }}>
        <span style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', alignSelf: 'center', marginRight: '4px' }}>OPERATORI:</span>
        {operatoriPlanning.map(op => (
          <div key={op} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '20px', background: coloreOperatore(op)+'33', border: `2px solid ${coloreOperatore(op)}77` }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: coloreOperatore(op), boxShadow: `0 0 5px ${coloreOperatore(op)}` }} />
            <span style={{ fontSize: '11px', color: text, fontWeight: '700' }}>{op}</span>
          </div>
        ))}
      </div>

      {/* Griglia */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: `${nPonti * 150 + 80}px` }}>
          <thead>
            <tr>
              <th style={{ padding: '8px', fontSize: '11px', color: textSub, textAlign: 'left', minWidth: '72px', position: 'sticky', left: 0, background: bg, zIndex: 2 }}>ORA</th>
              {Array.from({ length: nPonti }, (_, i) => (
                <th key={i+1} style={{ padding: '8px 4px', fontSize: '11px', fontWeight: '900', color: '#6366f1', textAlign: 'center', minWidth: '145px' }}>PONTE {i+1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {settimana.map(data => {
              const slots = getPlanningSlots(data);
              const isOggi = data === oggi;
              if (!slots.length) return null;
              return (
                <React.Fragment key={data}>
                  <tr>
                    <td colSpan={nPonti+1} style={{
                      padding: '8px 12px', fontSize: '12px', fontWeight: '900', letterSpacing: '2px', textTransform: 'uppercase',
                      background: isOggi ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.07)',
                      color: isOggi ? '#818cf8' : '#6366f1',
                      borderTop: `2px solid ${isOggi ? '#6366f1' : 'rgba(99,102,241,0.3)'}`,
                      borderLeft: `4px solid ${isOggi ? '#6366f1' : 'transparent'}`,
                    }}>
                      {isOggi ? '▶ OGGI — ' : ''}{new Date(data+'T12:00:00').toLocaleDateString('it-IT',{weekday:'long',day:'2-digit',month:'long'}).toUpperCase()}
                    </td>
                  </tr>
                  {slots.map(slot => (
                    <React.Fragment key={slot}>
                      {slot === '14:30' && <tr><td colSpan={nPonti+1} style={{ height: '3px', background: border }} /></tr>}
                      <tr style={{ borderBottom: `1px solid ${border}22` }}>
                        <td style={{ padding: '4px 10px', fontSize: '12px', fontWeight: '800', color: '#6366f1', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: bg, zIndex: 1, opacity: 0.85 }}>{slot}</td>
                        {Array.from({ length: nPonti }, (_, i) => {
                          const p = getSlot(data, slot, i+1);
                          const col = p ? coloreOperatore(p.operatore) : null;
                          return (
                            <td key={i+1} onClick={() => apriModal(data, slot, i+1)}
                              style={{ padding: '3px 4px', cursor: 'pointer', minWidth: '145px', verticalAlign: 'top' }}>
                              {p ? (
                                <div style={{ background: col+'33', border: `2px solid ${col}88`, borderRadius: '8px', padding: '6px 8px', borderLeft: `4px solid ${col}`, boxShadow: `0 2px 6px ${col}20` }}>
                                  <div style={{ fontSize: '11px', fontWeight: '900', color: col }}>{p.operatore.split(' ')[0].toUpperCase()}</div>
                                  {p.targa && <div style={{ fontSize: '12px', fontWeight: '900', color: '#fff', letterSpacing: '2px', background: 'rgba(0,0,0,0.25)', borderRadius: '4px', padding: '1px 5px', display: 'inline-block', marginTop: '1px' }}>{p.targa}</div>}
                                  {(p.marca||p.modello) && <div style={{ fontSize: '10px', color: text, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '1px' }}>{p.marca} {p.modello}</div>}
                                  {p.cliente && <div style={{ fontSize: '10px', color: textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.cliente}</div>}
                                  {p.preventivoId && (
                                    <div style={{ fontSize: '10px', color: '#34d399', fontWeight: '800', marginTop: '2px' }}>
                                      📋 #{p.preventivoId.toString().slice(-6)}{p.totaleLordo ? ` · €${Math.round(p.totaleLordo).toLocaleString('it-IT')}` : ''}
                                    </div>
                                  )}
                                  {p.note && <div style={{ fontSize: '9px', color: textSub, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '1px' }}>{p.note}</div>}
                                </div>
                              ) : (
                                <div style={{ height: '52px', borderRadius: '8px', border: '1px dashed rgba(99,102,241,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(99,102,241,0.45)', fontSize: '20px', transition: 'all 0.2s', background: 'rgba(99,102,241,0.03)' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor='#6366f1'; e.currentTarget.style.color='#6366f1'; e.currentTarget.style.background='rgba(99,102,241,0.10)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.35)'; e.currentTarget.style.color='rgba(99,102,241,0.45)'; e.currentTarget.style.background='rgba(99,102,241,0.03)'; }}>+</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target===e.currentTarget) setModal(null); }}>
          <div style={{ background: card, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '440px', border: `1px solid ${border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '900', color: text }}>Ponte {modal.ponte} — {modal.slot}</div>
                <div style={{ fontSize: '12px', color: textSub }}>{fmtDataBreve(modal.data)}</div>
                {modal.esistente?.preventivoId && (
                  <div style={{ marginTop: '6px', padding: '4px 10px', background: '#10b98115', border: '1px solid #10b98140', borderRadius: '6px', fontSize: '11px', color: '#10b981', fontWeight: '700', display: 'inline-block' }}>
                    📋 Preventivo #{modal.esistente.preventivoId.toString().slice(-6)}
                    {modal.esistente.totaleLordo ? ` · €${Math.round(modal.esistente.totaleLordo).toLocaleString('it-IT')}` : ''}
                  </div>
                )}
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', color: textSub, fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>OPERATORE *</label>
              <select value={selOperatore} onChange={e => setSelOperatore(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${selOperatore ? '#6366f1' : '#ef444460'}`, background: bg, color: text, fontSize: '13px' }}>
                <option value="">— Seleziona operatore —</option>
                {operatoriPlanning.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: textSub, letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>VEICOLO</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {['manuale','stock'].map(m => (
                  <button key={m} onClick={() => setModoVeicolo(m)} style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '700', background: modoVeicolo===m ? '#6366f1' : bg, color: modoVeicolo===m ? '#fff' : textSub }}>
                    {m === 'manuale' ? 'Manuale' : 'Da Stock'}
                  </button>
                ))}
              </div>
              {modoVeicolo === 'stock' ? (
                <div style={{ position: 'relative' }}>
                  <input value={searchV} onChange={e => setSearchV(e.target.value)} placeholder="Cerca targa, marca, modello..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '13px', boxSizing: 'border-box' }} />
                  {veicoliFiltrati.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: card, border: `1px solid ${border}`, borderRadius: '8px', zIndex: 10, maxHeight: '180px', overflowY: 'auto' }}>
                      {veicoliFiltrati.map((v, idx) => (
                        <div key={idx} onClick={() => { setSelTarga(v.targa||''); setSelCliente(`${v.marca||''} ${v.modello||''}`.trim()); setSearchV(''); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${border}`, fontSize: '12px', color: text }}
                          onMouseEnter={e => e.currentTarget.style.background='#6366f120'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <span style={{ fontWeight: '700' }}>{v.targa}</span> — {v.marca} {v.modello}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <input value={selTarga} onChange={e => setSelTarga(e.target.value.toUpperCase())} placeholder="Targa veicolo"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '13px', boxSizing: 'border-box', letterSpacing: '2px' }} />
              )}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: textSub, letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>CLIENTE</label>
              <input value={selCliente} onChange={e => setSelCliente(e.target.value)} placeholder="Nome cliente"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '13px', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: textSub, letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>NOTE</label>
              <textarea value={selNote} onChange={e => setSelNote(e.target.value)} placeholder="Tipo intervento, note..." rows={2}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {modal.esistente && (
                <button onClick={eliminaSlot} disabled={saving}
                  style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: '#ef444420', color: '#ef4444', fontWeight: '700', fontSize: '12px' }}>
                  🗑 Elimina
                </button>
              )}
              <button onClick={() => {
                setModal(null);
                if (typeof onApriODL === 'function') {
                  onApriODL({
                    targa: selTarga,
                    nomeCliente: selCliente,
                    note: selNote,
                    operatore: selOperatore,
                    dataOdl: modal.data,
                    slotOrario: modal.slot,
                    ponte: String(modal.ponte),
                    sedePlan: sede,
                  });
                }
              }}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '2px solid #f59e0b', cursor: 'pointer', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontWeight: '700', fontSize: '12px' }}>
                🔧 Apri ODL
              </button>
              <button onClick={() => setModal(null)}
                style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${border}`, cursor: 'pointer', background: 'transparent', color: textSub, fontWeight: '700', fontSize: '12px' }}>
                Annulla
              </button>
              <button onClick={salvaSlot} disabled={saving || !selOperatore}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: !selOperatore ? '#6366f160' : '#6366f1', color: '#fff', fontWeight: '700', fontSize: '12px', flex: 1 }}>
                {saving ? '...' : '✓ Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CARROZZERIA VIEW ────────────────────────────────────────────────────────
// ─── CARROZZERIA FIELD HELPER (fuori dal componente per evitare re-mount) ─────
const CField = ({ label, children, half, third, style }) => (
  <div style={{ flex: third?'0 0 calc(33% - 8px)':half?'0 0 calc(50% - 8px)':'1 1 100%', minWidth:0, ...style }}>
    <label style={{ fontSize:'9px', fontWeight:'900', color:'#64748b', letterSpacing:'0.5px', marginBottom:'4px', display:'block' }}>{label}</label>
    {children}
  </div>
);

// ─── CARROZZERIA VIEW ─────────────────────────────────────────────────────────
function CarrozzeriaView({ T, isMobile, db, vehicles, user }) {
  // Dati veicolo
  const [veicoloAbbinato, setVeicoloAbbinato] = useState(null);
  const [searchVeicolo, setSearchVeicolo] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [manualeMode, setManualeMode] = useState(false);

  // Form principale
  const [odl, setOdl] = useState('');
  const [dataOdl, setDataOdl] = useState(new Date().toISOString().split('T')[0]);
  const [azAzienda, setAzAzienda] = useState('');
  const [azVia, setAzVia] = useState('');
  const [azCap, setAzCap] = useState('');
  const [azLoc, setAzLoc] = useState('');
  const [azTel, setAzTel] = useState('');

  // Fornitori salvati
  const [fornitori, setFornitori] = useState([]);
  const [searchFornitore, setSearchFornitore] = useState('');
  const [showFornitore, setShowFornitore] = useState(false);

  useEffect(() => {
    // Carica fornitori da db dedicato
    get(ref(db, 'fornitori_carrozzeria')).then(snap => {
      const map = {};
      if (snap.exists()) Object.values(snap.val()).forEach(f => { if (f.nome) map[f.nome.toLowerCase()] = f; });

      // Carica anche dallo storico schede esistenti (retrocompatibilità)
      get(ref(db, 'service_carrozzeria')).then(snap2 => {
        if (snap2.exists()) {
          Object.values(snap2.val()).forEach(s => {
            if (s.azAzienda && !map[s.azAzienda.toLowerCase()]) {
              map[s.azAzienda.toLowerCase()] = { nome: s.azAzienda, via: s.azVia||'', cap: s.azCap||'', loc: s.azLoc||'', tel: s.azTel||'' };
            }
          });
        }
        setFornitori(Object.values(map).sort((a,b) => a.nome.localeCompare(b.nome)));

        // Calcola prossimo progressivo OdL
        const anno = new Date().getFullYear();
        const schede = snap2.exists() ? Object.values(snap2.val()) : [];
        const numeri = schede
          .map(s => s.odl || '')
          .filter(o => o.startsWith(`${anno}/`))
          .map(o => parseInt(o.split('/')[1]) || 0);
        const prossimo = Math.max(6, numeri.length > 0 ? Math.max(...numeri) + 1 : 6);
        setOdl(`${anno}/${String(prossimo).padStart(3, '0')}`);
      });
    });
  }, []);

  const fornitoriSuggeriti = useMemo(() => {
    if (!searchFornitore.trim()) return [];
    const s = searchFornitore.toLowerCase();
    return fornitori.filter(f => f.nome.toLowerCase().includes(s) || (f.loc||'').toLowerCase().includes(s)).slice(0, 6);
  }, [fornitori, searchFornitore]);

  const selezionaFornitore = (f) => {
    setAzAzienda(f.nome); setAzVia(f.via||''); setAzCap(f.cap||''); setAzLoc(f.loc||''); setAzTel(f.tel||'');
    setSearchFornitore(''); setShowFornitore(false);
  };

  const salvaFornitore = async () => {
    if (!azAzienda.trim()) return;
    const key = azAzienda.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    const fornitore = { nome: azAzienda.trim(), via: azVia, cap: azCap, loc: azLoc, tel: azTel, aggiornatoAt: new Date().toISOString() };
    await set(ref(db, `fornitori_carrozzeria/${key}`), fornitore);
    setFornitori(prev => {
      const others = prev.filter(f => f.nome.toLowerCase() !== azAzienda.toLowerCase());
      return [...others, fornitore].sort((a,b) => a.nome.localeCompare(b.nome));
    });
  };
  const [tipoModello, setTipoModello] = useState('');
  const [targa, setTarga] = useState('');
  const [telaio, setTelaio] = useState('');
  const [dataRitiro, setDataRitiro] = useState('');
  const [dataConsegna, setDataConsegna] = useState('');

  // Righe lavorazione
  const [righe, setRighe] = useState([{ id: 1, posizione: '', lavorazione: '', importo: '' }]);

  // Storico
  const [storico, setStorico] = useState([]);
  const [loadingStorico, setLoadingStorico] = useState(true);
  const [searchStorico, setSearchStorico] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [modificaScheda, setModificaScheda] = useState(null);
  const [logScheda, setLogScheda] = useState({}); // { schedaId: [voci log] }

  const storicoFiltrato = useMemo(() => {
    if (!searchStorico.trim()) return storico;
    const s = searchStorico.toLowerCase();
    return storico.filter(x =>
      (x.targa||'').toLowerCase().includes(s) ||
      (x.telaio||'').toLowerCase().includes(s) ||
      (x.azAzienda||'').toLowerCase().includes(s) ||
      (x.tipoModello||'').toLowerCase().includes(s) ||
      (toArraySafe(x.righe)).some(r => (r.lavorazione||'').toLowerCase().includes(s) || (r.posizione||'').toLowerCase().includes(s)) ||
      (x.odl||'').toLowerCase().includes(s)
    );
  }, [storico, searchStorico]);

  useEffect(() => {
    const unsub = onValue(ref(db, 'service_carrozzeria'), snap => {
      const data = snap.val();
      if (data) {
        const list = Object.values(data).map(s => ({
          ...s,
          // Firebase può restituire righe come oggetto — normalizziamo
          righe: s.righe ? (Array.isArray(s.righe) ? s.righe : Object.values(s.righe)) : [],
        })).sort((a,b) => new Date(b.data)-new Date(a.data));
        setStorico(list);
      } else setStorico([]);
      setLoadingStorico(false);
    });
    return () => unsub();
  }, []);


  const veicoliSuggeriti = useMemo(() => {
    if (!searchVeicolo.trim()) return [];
    const s = searchVeicolo.toLowerCase();
    return (vehicles||[]).filter(v =>
      (v.targa||'').toLowerCase().includes(s)||(v.vin||'').toLowerCase().includes(s)||
      (v.marca||'').toLowerCase().includes(s)||(v.modello||'').toLowerCase().includes(s)
    ).slice(0,8);
  }, [searchVeicolo, vehicles]);

  const selezionaVeicolo = (v) => {
    setVeicoloAbbinato(v);
    setTipoModello(`${v.marca} ${v.modello} ${v.versione||''}`.trim());
    setTarga(v.targa||'');
    setTelaio(v.vin||'');
    setManualeMode(false);
    setSearchVeicolo(''); setShowSuggest(false);
  };

  const resetVeicolo = () => {
    setVeicoloAbbinato(null);
    setTipoModello(''); setTarga(''); setTelaio('');
  };

  const aggiungiRiga = () => setRighe(r => [...r, { id: Date.now(), posizione: '', lavorazione: '', importo: '' }]);
  const rimuoviRiga = (id) => setRighe(r => r.filter(x => x.id !== id));
  const aggiornaRiga = (id, campo, val) => setRighe(r => r.map(x => x.id===id ? {...x, [campo]: val} : x));

  const totaleImporto = righe.reduce((s, r) => s + (parseFloat(r.importo)||0), 0);

  const stampaScheda = async (dati) => {
    if (!window.jspdf) {
      await new Promise((res,rej) => {
        const sc = document.createElement('script');
        sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        sc.onload = res; sc.onerror = rej; document.head.appendChild(sc);
      });
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W=210, ML=10, CW=190;
    const BLUE=[30,58,138], LGRAY=[235,240,250], WHITE=[255,255,255], DGRAY=[60,60,60];
    const RH=6;
    let y=10;

    // Intestazione
    doc.setFillColor(...BLUE); doc.rect(ML,y,CW,9,'F');
    doc.setFontSize(10); doc.setFont('helvetica','bold'); doc.setTextColor(...WHITE);
    doc.text('COMMISSIONE LAVORI AD AZIENDA ESTERNA', W/2, y+6, {align:'center'});
    y+=11;

    // OdL e data
    doc.setFontSize(8); doc.setFont('helvetica','normal'); doc.setTextColor(...DGRAY);
    const odlStr = dati.odl||'_______________';
    const datStr = dati.dataOdl ? dati.dataOdl.split('-').reverse().join('/') : '___/___/______';
    doc.setFillColor(245,247,252); doc.rect(ML,y,CW,6,'F');
    doc.text(`Relativa all'OdL.: ${odlStr}`, ML+2, y+4);
    doc.text(`del: ${datStr}`, ML+CW-2, y+4, {align:'right'});
    y+=8;

    // Service Partner / Azienda esterna
    const cW=(CW-2)/2;
    doc.setFillColor(...BLUE); doc.rect(ML,y,cW,6,'F'); doc.rect(ML+cW+2,y,cW,6,'F');
    doc.setFontSize(8); doc.setFont('helvetica','bold'); doc.setTextColor(...WHITE);
    doc.text('Service Partner', ML+cW/2, y+4, {align:'center'});
    doc.text('Azienda esterna', ML+cW+2+cW/2, y+4, {align:'center'});
    y+=6;
    const spData = [['Azienda','Gruppo Autoscala Srl'],['Via','Via Antiniana 2B'],['CAP','80078'],['Localita/Prov','POZZUOLI (NA)'],['Tel./Fax','081 19370989']];
    const azData = [['Azienda',dati.azAzienda||''],['Via',dati.azVia||''],['CAP',dati.azCap||''],['Localita/Prov',dati.azLoc||''],['Tel./Fax',dati.azTel||'']];
    spData.forEach(([k,v],i) => {
      const bg = i%2===0 ? LGRAY : [248,250,255];
      doc.setFillColor(...bg); doc.rect(ML,y,cW,6,'F'); doc.rect(ML+cW+2,y,cW,6,'F');
      doc.setFontSize(7.5); doc.setTextColor(...DGRAY);
      doc.setFont('helvetica','bold'); doc.text(k+':', ML+2, y+4);
      doc.setFont('helvetica','normal'); doc.text(v, ML+22, y+4, {maxWidth:cW-24});
      doc.setFont('helvetica','bold'); doc.text(azData[i][0]+':', ML+cW+4, y+4);
      doc.setFont('helvetica','normal'); doc.text(azData[i][1], ML+cW+24, y+4, {maxWidth:cW-26});
      y+=6;
    });
    y+=3;

    // Veicolo
    doc.setFillColor(...BLUE); doc.rect(ML,y,CW,6,'F');
    doc.setFontSize(8); doc.setFont('helvetica','bold'); doc.setTextColor(...WHITE);
    doc.text('Dati Veicolo', ML+2, y+4); y+=6;
    [['Tipo / Modello',String(dati.tipoModello||'').toUpperCase()],
     ['Targa',String(dati.targa||'').toUpperCase()],
     ['Telaio (VIN)',String(dati.telaio||'').toUpperCase()]].forEach(([l,v],i) => {
      doc.setFillColor(...(i%2===0?LGRAY:[248,250,255])); doc.rect(ML,y,CW,6,'F');
      doc.setFontSize(7.5); doc.setFont('helvetica','bold'); doc.setTextColor(...DGRAY);
      doc.text(l+':', ML+2, y+4);
      doc.setFont('helvetica','normal'); doc.text(v, ML+36, y+4, {maxWidth:CW-38});
      y+=6;
    });
    y+=3;

    // Lavorazioni
    const pW=48, iW=28, lW=CW-pW-iW-4;
    doc.setFillColor(...BLUE);
    doc.rect(ML,y,pW,6,'F'); doc.rect(ML+pW+2,y,lW,6,'F'); doc.rect(ML+pW+lW+4,y,iW,6,'F');
    doc.setFontSize(7.5); doc.setFont('helvetica','bold'); doc.setTextColor(...WHITE);
    doc.text('Posizione lavoro', ML+2, y+4);
    doc.text('Intervento commissionato', ML+pW+4, y+4);
    doc.text('Importo', ML+pW+lW+5, y+4);
    y+=6;
    const righe2 = Array.isArray(dati.righe) ? dati.righe : (dati.righe ? Object.values(dati.righe) : []);
    righe2.forEach((r,i) => {
      doc.setFontSize(7.5);
      const lns = doc.splitTextToSize(r.lavorazione||'', lW-4);
      const rh = Math.max(lns.length*4.5+3, 7);
      const bg = i%2===0 ? LGRAY : [248,250,255];
      doc.setFillColor(...bg);
      doc.rect(ML,y,pW,rh,'F'); doc.rect(ML+pW+2,y,lW,rh,'F'); doc.rect(ML+pW+lW+4,y,iW,rh,'F');
      doc.setFont('helvetica','normal'); doc.setTextColor(...DGRAY);
      doc.text(r.posizione||'', ML+2, y+4, {maxWidth:pW-4});
      doc.text(lns, ML+pW+4, y+4);
      const imp = parseFloat(r.importo||0);
      if (imp>0) {
        doc.setFont('helvetica','bold');
        doc.text('€ '+imp.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2}), ML+pW+lW+4+iW-2, y+4, {align:'right'});
      }
      y+=rh;
    });
    // Totale — l'importo salvato è l'IMPONIBILE: aggiungiamo IVA 22% per il totale reale
    const imponibile = parseFloat(dati.totale||dati.importo||0);
    const ivaTot = Math.round(imponibile*0.22*100)/100;
    const totConIva = imponibile + ivaTot;
    doc.setFillColor(240,240,245); doc.rect(ML,y,CW,6,'F');
    doc.setFontSize(8); doc.setFont('helvetica','normal'); doc.setTextColor(...DGRAY);
    doc.text('Imponibile', ML+pW+4, y+4);
    doc.text('€ '+imponibile.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2}), ML+CW-2, y+4, {align:'right'});
    y+=6;
    doc.setFillColor(240,240,245); doc.rect(ML,y,CW,6,'F');
    doc.text('IVA 22%', ML+pW+4, y+4);
    doc.text('€ '+ivaTot.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2}), ML+CW-2, y+4, {align:'right'});
    y+=6;
    doc.setFillColor(215,225,245); doc.rect(ML,y,CW,7,'F');
    doc.setFontSize(8.5); doc.setFont('helvetica','bold'); doc.setTextColor(...BLUE);
    doc.text('TOTALE (IVA INCLUSA)', ML+pW+4, y+4.5);
    doc.text('€ '+totConIva.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2}), ML+CW-2, y+4.5, {align:'right'});
    y+=9;

    // Date e firma
    doc.setFillColor(...LGRAY); doc.rect(ML,y,CW,13,'F');
    doc.setFontSize(8); doc.setFont('helvetica','normal'); doc.setTextColor(...DGRAY);
    const dr = dati.dataRitiro ? dati.dataRitiro.split('-').reverse().join('/') : '___/___/______';
    const dc = dati.dataConsegna ? dati.dataConsegna.split('-').reverse().join('/') : '___/___/______';
    doc.text('Data ritiro: '+dr, ML+2, y+5);
    doc.text('Data prevista consegna: '+dc, ML+2, y+11);
    doc.setFont('helvetica','bold');
    doc.text('Timbro e firma del Service Partner:', ML+CW*0.55, y+5);
    doc.setLineWidth(0.3); doc.setDrawColor(...DGRAY);
    doc.line(ML+CW*0.55, y+12, ML+CW-2, y+12);
    y+=16;

    // Separatore
    doc.setDrawColor(...BLUE); doc.setLineWidth(0.7); doc.line(ML,y,ML+CW,y); y+=4;

    // Annotazioni
    doc.setFillColor(...BLUE); doc.rect(ML,y,CW,6,'F');
    doc.setFontSize(8); doc.setFont('helvetica','bold'); doc.setTextColor(...WHITE);
    doc.text('Annotazioni ad intervento ultimato:', ML+2, y+4); y+=6;
    const anno = [
      ['La commissione e stata eseguita come da ordine.','Il lavoro e stato eseguito correttamente   □ Si  □ No'],
      ['Il controllo qualita e stato effettuato.','Il termine di consegna e stato rispettato  □ Si  □ No'],
    ];
    anno.forEach(([t1,t2],i) => {
      const bg = i%2===0 ? LGRAY : [248,250,255];
      doc.setFillColor(...bg);
      doc.rect(ML,y,cW,6,'F'); doc.rect(ML+cW+2,y,cW,6,'F');
      doc.setFontSize(7.5); doc.setFont('helvetica','normal'); doc.setTextColor(...DGRAY);
      doc.text(t1, ML+2, y+4, {maxWidth:cW-4});
      doc.text(t2, ML+cW+4, y+4, {maxWidth:cW-4});
      y+=6;
    });
    y+=5;

    // Firme finali
    doc.setFillColor(...LGRAY); doc.rect(ML,y,CW,16,'F');
    doc.setFontSize(7.5); doc.setFont('helvetica','normal'); doc.setTextColor(...DGRAY);
    doc.text('Data: ___/___/______', ML+2, y+5);
    doc.text('Data: ___/___/______', ML+cW+4, y+5);
    doc.setLineWidth(0.3);
    doc.line(ML+2, y+14, ML+cW-4, y+14);
    doc.line(ML+cW+4, y+14, ML+CW-2, y+14);
    doc.setFont('helvetica','bold'); doc.setFontSize(7);
    doc.text("Timbro e firma dell'azienda commissionata", ML+2, y+13);
    doc.text('Timbro e firma del Service Partner', ML+cW+4, y+13);

    doc.save('Affidamento_'+(dati.targa||'ND').replace(/[^a-zA-Z0-9]/g,'')+'_'+(dati.dataOdl||'').replace(/-/g,'')+'.pdf');
  };

  const resetFormCarrozzeria = () => {
    setVeicoloAbbinato(null);
    setSearchVeicolo('');
    setManualeMode(false);
    setOdl('');
    setDataOdl(new Date().toISOString().split('T')[0]);
    setAzAzienda(''); setAzVia(''); setAzCap(''); setAzLoc(''); setAzTel('');
    setSearchFornitore('');
    setTipoModello('');
    setTarga('');
    setTelaio('');
    setDataRitiro('');
    setDataConsegna('');
    setRighe([{ id: 1, posizione: '', lavorazione: '', importo: '' }]);
  };

  const salvaESampa = async () => {
    const righeClean = righe.map(r => ({ posizione: r.posizione||'', lavorazione: r.lavorazione||'', importo: parseFloat(r.importo)||0 }));
    const dati = { odl, dataOdl, azAzienda, azVia, azCap, azLoc, azTel, tipoModello, targa, telaio, dataRitiro, dataConsegna, righe: righeClean, totale: totaleImporto };
    const id = Date.now().toString();
    const record = { id, data: new Date().toISOString(), ...dati, vin: telaio, importo: totaleImporto,
      creatoDa: `${user?.nome || ''} ${user?.cognome || ''}`.trim() || user?.email || 'N.D.',
      veicoloAbbinato: veicoloAbbinato?{marca:veicoloAbbinato.marca,modello:veicoloAbbinato.modello,targa:veicoloAbbinato.targa,vin:veicoloAbbinato.vin}:null };
    try {
      await set(ref(db, `service_carrozzeria/${id}`), record);
      await salvaFornitore(); // salva fornitore in db
    } catch(e) { alert('Errore salvataggio: ' + e.message); return; }
    await stampaScheda(dati);
    // Subito dopo salvataggio e stampa — offre di ripartire da un modulo
    // vuoto senza dover passare dallo Storico e tornare indietro solo per
    // svuotare i campi.
    if (window.confirm('✓ Scheda salvata e stampata.\n\nVuoi crearne subito una nuova?')) {
      resetFormCarrozzeria();
    }
  };

  const puoEliminare = user?.ruolo === 'admin' || user?.ruolo === 'super_admin';

  const eliminaScheda = async (id) => {
    if (!puoEliminare) { alert('Non hai i permessi per eliminare questa scheda.'); return; }
    if (!window.confirm('Eliminare questa scheda carrozzeria?')) return;
    await set(ref(db, `service_carrozzeria/${id}`), null);
  };

  const inputS = { padding:'9px 12px', background:T.inputBg, border:`1px solid ${T.border2}`, borderRadius:'10px', color:T.text, fontSize:'12px', outline:'none', width:'100%', boxSizing:'border-box' };

  return (
    <div>
      <div style={{ background:T.surface, borderRadius:'16px', border:`1px solid ${T.border}`, padding:'16px', marginBottom:'20px' }}>
        <div style={{ fontSize:'10px', fontWeight:'900', color:'#6366f1', letterSpacing:'1px', marginBottom:'14px' }}>🚗 NUOVA SCHEDA AFFIDAMENTO CARROZZERIA</div>

        {/* OdL + Data */}
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'12px' }}>
          <CField label="N. OdL" half T={T}>
            <input style={inputS} value={odl} onChange={e=>setOdl(e.target.value)} placeholder="Es. 2025/001" />
          </CField>
          <CField label="Data" half T={T}>
            <input type="date" style={inputS} value={dataOdl} onChange={e=>setDataOdl(e.target.value)} />
          </CField>
        </div>

        {/* Ricerca veicolo */}
        <div style={{ marginBottom:'12px' }}>
          <label style={{ fontSize:'9px', fontWeight:'900', color:T.textMuted, letterSpacing:'0.5px', marginBottom:'4px', display:'block' }}>
            VEICOLO — cerca in stock per targa, telaio, marca o modello
          </label>
          {veicoloAbbinato && !manualeMode ? (
            <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'rgba(99,102,241,0.08)', border:'1px solid #6366f1', borderRadius:'12px', marginBottom:'8px' }}>
              {veicoloAbbinato.mainImg && <img src={veicoloAbbinato.mainImg} style={{ width:'52px', height:'36px', objectFit:'cover', borderRadius:'6px' }} alt="" onError={e => { e.target.style.display = 'none'; }} />}
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'13px', fontWeight:'800', color:T.text }}>{veicoloAbbinato.marca} {veicoloAbbinato.modello} {veicoloAbbinato.versione||''}</div>
                <div style={{ fontSize:'10px', color:'#6366f1' }}>🏷 {veicoloAbbinato.targa} · 🔑 {veicoloAbbinato.vin?'···'+veicoloAbbinato.vin.slice(-6):'—'} · {veicoloAbbinato.registrazione||''}</div>
              </div>
              <button onClick={resetVeicolo} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:'18px', fontWeight:'900' }}>×</button>
            </div>
          ) : (
            <div style={{ position:'relative', marginBottom:'8px' }}>
              <input style={inputS} placeholder={`Cerca tra ${(vehicles||[]).length} veicoli in stock...`}
                value={searchVeicolo}
                onChange={e => { setSearchVeicolo(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
              />
              {showSuggest && veicoliSuggeriti.length > 0 && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:T.surface, border:`1px solid ${T.border}`, borderRadius:'12px', zIndex:600, maxHeight:'220px', overflowY:'auto', marginTop:'4px', boxShadow:'0 8px 24px rgba(0,0,0,0.25)' }}>
                  {veicoliSuggeriti.map(v => (
                    <div key={v.id||v.targa} onClick={() => selezionaVeicolo(v)}
                      style={{ padding:'10px 14px', cursor:'pointer', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:'10px' }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    >
                      {v.mainImg && <img src={v.mainImg} style={{ width:'48px', height:'33px', objectFit:'cover', borderRadius:'6px' }} alt="" onError={e => { e.target.style.display = 'none'; }} />}
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'12px', fontWeight:'700', color:T.text }}>{v.marca} {v.modello} {v.versione||''}</div>
                        <div style={{ fontSize:'10px', color:T.textMuted }}>🏷 {v.targa||'—'} · 🔑 {v.vin?'···'+v.vin.slice(-6):'—'} · {v.registrazione||''}</div>
                      </div>
                      <span style={{ fontSize:'10px', color:'#6366f1', fontWeight:'700' }}>{v.sede}</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setManualeMode(true)}
                style={{ marginTop:'6px', fontSize:'10px', color:T.textMuted, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
                Veicolo non in stock? Compila manualmente →
              </button>
            </div>
          )}

          {/* Dati veicolo — auto-compilati da stock o manuali */}
          <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
            <CField label="Tipo / Modello" T={T}>
              <input style={{ ...inputS, background: veicoloAbbinato&&!manualeMode ? T.bg : T.inputBg }}
                value={tipoModello} onChange={e=>setTipoModello(e.target.value)}
                readOnly={!!(veicoloAbbinato&&!manualeMode)}
                placeholder="Es. AUDI A1 SPORTBACK 40 TFSI" />
            </CField>
            <CField label="Targa" half T={T}>
              <input style={{ ...inputS, textTransform:'uppercase', background: veicoloAbbinato&&!manualeMode ? T.bg : T.inputBg }}
                value={targa} onChange={e=>setTarga(e.target.value.toUpperCase())}
                readOnly={!!(veicoloAbbinato&&!manualeMode)} placeholder="Es. FX907YJ" />
            </CField>
            <CField label="Telaio (VIN)" half T={T}>
              <input style={{ ...inputS, textTransform:'uppercase', background: veicoloAbbinato&&!manualeMode ? T.bg : T.inputBg }}
                value={telaio} onChange={e=>setTelaio(e.target.value.toUpperCase())}
                readOnly={!!(veicoloAbbinato&&!manualeMode)} placeholder="Es. WAUZZZGB5LR003868" />
            </CField>
          </div>
        </div>

        {/* Azienda esterna */}
        <div style={{ fontSize:'9px', fontWeight:'900', color:'#f59e0b', letterSpacing:'1px', marginBottom:'8px', paddingTop:'4px' }}>AZIENDA ESTERNA</div>
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'12px' }}>
          <CField label="Ragione Sociale" T={T}>
            <div style={{ position:'relative' }}>
              <input style={inputS} value={azAzienda}
                onChange={e => { setAzAzienda(e.target.value); setSearchFornitore(e.target.value); setShowFornitore(true); }}
                onFocus={() => { setSearchFornitore(azAzienda); setShowFornitore(true); }}
                onBlur={() => setTimeout(() => setShowFornitore(false), 200)}
                placeholder="Es. Autocarrozzeria Jet SAS" />
              {showFornitore && fornitoriSuggeriti.length > 0 && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#1e293b', border:`1px solid #334155`, borderRadius:'10px', zIndex:600, maxHeight:'180px', overflowY:'auto', marginTop:'4px', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                  {fornitoriSuggeriti.map((f,i) => (
                    <div key={i} onClick={() => selezionaFornitore(f)}
                      style={{ padding:'9px 12px', cursor:'pointer', borderBottom:`1px solid #334155` }}
                      onMouseEnter={e => e.currentTarget.style.background='#334155'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ fontSize:'12px', fontWeight:'700', color:'#f1f5f9' }}>{f.nome}</div>
                      <div style={{ fontSize:'10px', color:'#94a3b8' }}>{f.via||''}{f.loc ? ` — ${f.loc}` : ''}{f.tel ? ` · ${f.tel}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CField>
          <CField label="Via" T={T}><input style={inputS} value={azVia} onChange={e=>setAzVia(e.target.value)} /></CField>
          <CField label="CAP" third T={T}><input style={inputS} value={azCap} onChange={e=>setAzCap(e.target.value)} /></CField>
          <CField label="Località / Prov." half T={T}><input style={inputS} value={azLoc} onChange={e=>setAzLoc(e.target.value)} /></CField>
          <CField label="Tel. / Fax" third T={T}><input style={inputS} value={azTel} onChange={e=>setAzTel(e.target.value)} /></CField>
        </div>

        {/* Righe lavorazione */}
        <div style={{ fontSize:'9px', fontWeight:'900', color:'#10b981', letterSpacing:'1px', marginBottom:'8px' }}>LAVORAZIONI</div>
        <div style={{ background:T.bg, borderRadius:'12px', border:`1px solid ${T.border}`, overflow:'hidden', marginBottom:'10px' }}>
          {/* Header */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 110px 36px', gap:'1px', padding:'8px 12px', background:T.surface, borderBottom:`1px solid ${T.border}` }}>
            {['POSIZIONE','INTERVENTO','IMPORTO (€)',''].map(h => <div key={h} style={{ fontSize:'8px', fontWeight:'900', color:T.textMuted }}>{h}</div>)}
          </div>
          {righe.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 2fr 110px 36px', gap:'6px', padding:'8px 12px', borderBottom:i<righe.length-1?`1px solid ${T.border}`:'none', alignItems:'center', background:i%2===0?'transparent':T.bg+'40' }}>
              <input style={{ ...inputS, fontSize:'11px', padding:'6px 8px' }} value={r.posizione} onChange={e=>aggiornaRiga(r.id,'posizione',e.target.value)} placeholder="Es. Lato basso" />
              <input style={{ ...inputS, fontSize:'11px', padding:'6px 8px' }} value={r.lavorazione} onChange={e=>aggiornaRiga(r.id,'lavorazione',e.target.value)} placeholder="Es. Verniciatura cerchi, lucidatura..." />
              <input type="number" style={{ ...inputS, fontSize:'11px', padding:'6px 8px', textAlign:'right' }} value={r.importo} onChange={e=>aggiornaRiga(r.id,'importo',e.target.value)} placeholder="0.00" />
              <button onClick={() => righe.length>1 && rimuoviRiga(r.id)} style={{ background:'none', border:'none', color: righe.length>1?'#ef4444':T.border, cursor:righe.length>1?'pointer':'default', fontSize:'18px', fontWeight:'900', padding:0 }}>×</button>
            </div>
          ))}
          {/* Totale */}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', borderTop:`2px solid ${T.border}`, background:T.surface }}>
            <button onClick={aggiungiRiga} style={{ padding:'6px 14px', background:'rgba(16,185,129,0.1)', border:'1px solid #10b98140', borderRadius:'8px', color:'#10b981', cursor:'pointer', fontWeight:'700', fontSize:'11px' }}>
              + Aggiungi lavorazione
            </button>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:'10px', fontWeight:'700', color:T.textMuted }}>
                Imponibile € {totaleImporto.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})} + IVA 22% (€ {(totaleImporto*0.22).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})})
              </div>
              <div style={{ fontSize:'14px', fontWeight:'900', color:'#10b981' }}>
                TOTALE: € {(totaleImporto*1.22).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}
              </div>
            </div>
          </div>
        </div>

        {/* Date */}
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'14px' }}>
          <CField label="Data ritiro" half T={T}><input type="date" style={inputS} value={dataRitiro} onChange={e=>setDataRitiro(e.target.value)} /></CField>
          <CField label="Data prevista consegna" half T={T}><input type="date" style={inputS} value={dataConsegna} onChange={e=>setDataConsegna(e.target.value)} /></CField>
        </div>

        <button onClick={salvaESampa}
          style={{ width:'100%', padding:'13px', background:'linear-gradient(135deg, #6366f1, #4f46e5)', border:'none', borderRadius:'12px', color:'#fff', cursor:'pointer', fontWeight:'900', fontSize:'13px' }}>
          🖨️ Salva e Stampa Scheda Affidamento
        </button>
      </div>

      {/* Storico */}
      <div style={{ background:T.surface, borderRadius:'16px', border:`1px solid ${T.border}`, padding:'16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px', flexWrap:'wrap', gap:'8px' }}>
          <div style={{ fontSize:'10px', fontWeight:'900', color:T.textMuted, letterSpacing:'1px' }}>
            STORICO SCHEDE CARROZZERIA — {storico.length} registrate
          </div>
          {!puoEliminare && <div style={{ fontSize:'9px', color:'#f59e0b', fontWeight:'700' }}>👁 Solo visualizzazione</div>}
        </div>
        <input placeholder="🔍 Cerca per targa, telaio, azienda, lavorazione..."
          style={{ ...inputS, marginBottom:'12px' }}
          value={searchStorico} onChange={e => setSearchStorico(e.target.value)} />
        {loadingStorico ? (
          <div style={{ color:T.textMuted, textAlign:'center', padding:'20px' }}>Caricamento...</div>
        ) : storicoFiltrato.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px', color:T.textMuted }}>
            <div style={{ fontSize:'32px', marginBottom:'8px' }}>🚗</div>
            <div style={{ fontSize:'12px', fontWeight:'700' }}>{storico.length===0 ? 'Nessuna scheda ancora' : 'Nessun risultato'}</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {storicoFiltrato.map(s => (
              <div key={s.id} style={{ background:T.bg, borderRadius:'12px', border:`1px solid ${T.border}`, overflow:'hidden' }}>
                {/* Header riga */}
                <div onClick={() => setExpandedId(x => x===s.id ? null : s.id)}
                  style={{ padding:'12px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px' }}
                  onMouseEnter={e => e.currentTarget.style.background=T.surface+'80'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'13px', fontWeight:'900', color:T.text }}>{s.tipoModello||'—'}</div>
                    <div style={{ fontSize:'10px', color:T.textMuted, marginTop:'2px' }}>
                      🏷 {s.targa||'—'} · 🔑 {s.telaio?'···'+s.telaio.slice(-6):'—'} · {s.azAzienda||'—'}
                    </div>
                    <div style={{ fontSize:'10px', color:T.textMuted }}>
                      📅 {new Date(s.data).toLocaleDateString('it-IT')} · OdL: {s.odl||'—'}
                      {['admin','super_admin'].includes((user?.ruolo||'').toLowerCase().trim()) && s.creatoDa && (
                        <> · 👤 {s.creatoDa}</>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'6px' }}>
                    <div style={{ fontSize:'15px', fontWeight:'900', color:'#ef4444' }}>
                      {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format((parseFloat(s.importo)||0)*1.22)}
                    </div>
                    <div style={{ fontSize:'9px', color:T.textMuted }}>
                      imp. {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(s.importo||0)} + IVA 22%
                    </div>
                    <div style={{ fontSize:'10px', color:T.textMuted }}>{expandedId===s.id ? '▲ Chiudi' : '▼ Dettaglio'}</div>
                  </div>
                </div>

                {/* Dettaglio espandibile */}
                {expandedId === s.id && (
                  <div style={{ borderTop:`1px solid ${T.border}`, padding:'12px 16px' }}>
                    {/* Azienda esterna */}
                    {s.azAzienda && (
                      <div style={{ marginBottom:'10px' }}>
                        <div style={{ fontSize:'9px', fontWeight:'900', color:'#f59e0b', marginBottom:'4px' }}>AZIENDA ESTERNA</div>
                        <div style={{ fontSize:'11px', color:T.text }}>{s.azAzienda} — {s.azLoc||''} {s.azTel?`· ${s.azTel}`:''}</div>
                      </div>
                    )}
                    {/* Righe lavorazione */}
                    {s.righe?.length > 0 && (
                      <div style={{ marginBottom:'10px' }}>
                        <div style={{ fontSize:'9px', fontWeight:'900', color:'#10b981', marginBottom:'6px' }}>LAVORAZIONI</div>
                        <div style={{ background:T.surface, borderRadius:'8px', overflow:'hidden', border:`1px solid ${T.border}` }}>
                          {s.righe.filter(r=>r.lavorazione||r.posizione).map((r,i) => (
                            <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 2fr 100px', gap:'8px', padding:'7px 10px', borderBottom:i<s.righe.length-1?`1px solid ${T.border}`:'none', background:i%2===0?'transparent':T.bg+'40' }}>
                              <div style={{ fontSize:'10px', color:T.textSub }}>{r.posizione||'—'}</div>
                              <div style={{ fontSize:'11px', color:T.text, fontWeight:'600' }}>{r.lavorazione||'—'}</div>
                              <div style={{ fontSize:'11px', color:'#10b981', fontWeight:'700', textAlign:'right' }}>
                                {r.importo ? `€ ${parseFloat(r.importo).toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}` : '—'}
                              </div>
                            </div>
                          ))}
                          <div style={{ padding:'8px 10px', borderTop:`2px solid ${T.border}` }}>
                            <div style={{ display:'flex', justifyContent:'flex-end', fontSize:'10px', color:T.textMuted, marginBottom:'2px' }}>
                              Imponibile {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(s.importo||0)} + IVA 22% ({new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format((parseFloat(s.importo)||0)*0.22)})
                            </div>
                            <div style={{ display:'flex', justifyContent:'flex-end' }}>
                              <span style={{ fontSize:'13px', fontWeight:'900', color:'#ef4444' }}>
                                TOTALE: {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format((parseFloat(s.importo)||0)*1.22)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Date */}
                    {(s.dataRitiro||s.dataConsegna) && (
                      <div style={{ fontSize:'10px', color:T.textMuted, marginBottom:'10px' }}>
                        Ritiro: {s.dataRitiro?s.dataRitiro.split('-').reverse().join('/'):'—'} · Consegna prevista: {s.dataConsegna?s.dataConsegna.split('-').reverse().join('/'):'—'}
                      </div>
                    )}
                    {/* Azioni */}
                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                      <button onClick={() => stampaScheda(s)}
                        style={{ padding:'8px 16px', background:'rgba(99,102,241,0.1)', border:'1px solid #6366f1', borderRadius:'8px', color:'#6366f1', cursor:'pointer', fontWeight:'700', fontSize:'11px' }}>
                        🖨️ Ristampa PDF
                      </button>
                      <button onClick={() => setModificaScheda(s)}
                        style={{ padding:'8px 16px', background:'rgba(245,158,11,0.1)', border:'1px solid #f59e0b', borderRadius:'8px', color:'#f59e0b', cursor:'pointer', fontWeight:'700', fontSize:'11px' }}>
                        ✏️ Modifica
                      </button>
                      {puoEliminare && (
                        <button onClick={() => eliminaScheda(s.id)}
                          style={{ padding:'8px 14px', background:'rgba(239,68,68,0.1)', border:'1px solid #ef444440', borderRadius:'8px', color:'#ef4444', cursor:'pointer', fontWeight:'700', fontSize:'11px' }}>
                          🗑️ Elimina
                        </button>
                      )}
                    </div>
                    {/* Cronologia modifiche — caricata al volo quando la
                        scheda viene espansa, cosi non pesa sul caricamento
                        iniziale dell'intero storico. */}
                    <CronologiaModificheScheda db={db} T={T} schedaId={s.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modificaScheda && (
        <ModificaSchedaCarrozzeriaModal
          scheda={modificaScheda} db={db} T={T} user={user}
          onClose={() => setModificaScheda(null)}
          onSalvato={(aggiornata) => {
            setStorico(prev => prev.map(x => x.id === aggiornata.id ? aggiornata : x));
            setModificaScheda(null);
          }}
        />
      )}
    </div>
  );
}

// ─── MODIFICA SCHEDA CARROZZERIA — con log della modifica ───────────────────
function ModificaSchedaCarrozzeriaModal({ scheda, db, T, user, onClose, onSalvato }) {
  const [tipoModello, setTipoModello] = useState(scheda.tipoModello || '');
  const [targa, setTarga] = useState(scheda.targa || '');
  const [telaio, setTelaio] = useState(scheda.telaio || '');
  const [dataRitiro, setDataRitiro] = useState(scheda.dataRitiro || '');
  const [dataConsegna, setDataConsegna] = useState(scheda.dataConsegna || '');
  const [righe, setRighe] = useState((scheda.righe || []).map(r => ({ ...r })));
  const [saving, setSaving] = useState(false);

  const totale = righe.reduce((s, r) => s + (parseFloat(r.importo) || 0), 0);

  const aggiornaRiga = (i, campo, val) => setRighe(prev => prev.map((r, idx) => idx === i ? { ...r, [campo]: val } : r));
  const aggiungiRiga = () => setRighe(prev => [...prev, { posizione: '', lavorazione: '', importo: '' }]);
  const rimuoviRiga = (i) => setRighe(prev => prev.filter((_, idx) => idx !== i));

  const inputS = { padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none', width: '100%', boxSizing: 'border-box' };

  const handleSalva = async () => {
    setSaving(true);
    try {
      const aggiornata = {
        ...scheda,
        tipoModello, targa, telaio, dataRitiro, dataConsegna,
        righe: righe.map(r => ({ posizione: r.posizione || '', lavorazione: r.lavorazione || '', importo: parseFloat(r.importo) || 0 })),
        totale, importo: totale,
      };
      await set(ref(db, `service_carrozzeria/${scheda.id}`), aggiornata);

      // Log della modifica — chi, quando, stessa logica già usata per i DDT.
      const logId = `LOG-${Date.now()}`;
      await set(ref(db, `carrozzeria_log/${logId}`), {
        id: logId, schedaId: scheda.id, azione: 'modificato',
        utente: `${user?.nome || ''} ${user?.cognome || ''}`.trim() || user?.email || 'N.D.',
        ruolo: user?.ruolo || '', data: new Date().toISOString(),
      });

      onSalvato(aggiornata);
    } catch (e) {
      alert('Errore nel salvataggio: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '20px', width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '900', color: T.text }}>✏️ Modifica Scheda Carrozzeria</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '8px', padding: '5px 9px', cursor: 'pointer', fontWeight: '900' }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div><label style={{ fontSize: '10px', color: T.textMuted }}>Modello</label><input value={tipoModello} onChange={e => setTipoModello(e.target.value)} style={inputS} /></div>
          <div><label style={{ fontSize: '10px', color: T.textMuted }}>Targa</label><input value={targa} onChange={e => setTarga(e.target.value)} style={inputS} /></div>
          <div><label style={{ fontSize: '10px', color: T.textMuted }}>Telaio</label><input value={telaio} onChange={e => setTelaio(e.target.value)} style={inputS} /></div>
          <div></div>
          <div><label style={{ fontSize: '10px', color: T.textMuted }}>Data Ritiro</label><input type="date" value={dataRitiro} onChange={e => setDataRitiro(e.target.value)} style={inputS} /></div>
          <div><label style={{ fontSize: '10px', color: T.textMuted }}>Data Consegna Prevista</label><input type="date" value={dataConsegna} onChange={e => setDataConsegna(e.target.value)} style={inputS} /></div>
        </div>

        <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', margin: '14px 0 8px' }}>LAVORAZIONI</div>
        {righe.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <input value={r.posizione} onChange={e => aggiornaRiga(i, 'posizione', e.target.value)} placeholder="Posizione" style={{ ...inputS, flex: '0 0 90px' }} />
            <input value={r.lavorazione} onChange={e => aggiornaRiga(i, 'lavorazione', e.target.value)} placeholder="Lavorazione" style={{ ...inputS, flex: 1 }} />
            <input type="number" value={r.importo} onChange={e => aggiornaRiga(i, 'importo', e.target.value)} placeholder="€" style={{ ...inputS, flex: '0 0 80px' }} />
            <button onClick={() => rimuoviRiga(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
          </div>
        ))}
        <button onClick={aggiungiRiga} style={{ padding: '6px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b98140', borderRadius: '8px', color: '#10b981', cursor: 'pointer', fontWeight: '700', fontSize: '11px', marginTop: '4px' }}>
          + Aggiungi lavorazione
        </button>

        <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: '900', color: T.text, margin: '14px 0' }}>
          Totale: {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(totale)}
        </div>

        <button onClick={handleSalva} disabled={saving}
          style={{ width: '100%', padding: '12px', background: saving ? T.border : '#f59e0b', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '900', cursor: saving ? 'default' : 'pointer', fontSize: '13px' }}>
          {saving ? 'Salvataggio...' : '💾 Salva Modifiche'}
        </button>
      </div>
    </div>
  );
}

// ─── CRONOLOGIA MODIFICHE SCHEDA CARROZZERIA ─────────────────────────────────
function CronologiaModificheScheda({ db, T, schedaId }) {
  const [voci, setVoci] = useState(null); // null = non ancora caricato

  useEffect(() => {
    let annullato = false;
    get(query(ref(db, 'carrozzeria_log'), orderByChild('schedaId'), equalTo(schedaId))).then(snap => {
      if (annullato) return;
      const lista = snap.val() ? Object.values(snap.val()).sort((a, b) => (b.data||'').localeCompare(a.data||'')) : [];
      setVoci(lista);
    }).catch(() => setVoci([]));
    return () => { annullato = true; };
  }, [schedaId]);

  if (!voci || voci.length === 0) return null;

  return (
    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px dashed ${T.border}` }}>
      <div style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '6px' }}>🕐 CRONOLOGIA MODIFICHE</div>
      {voci.map((v, i) => (
        <div key={i} style={{ fontSize: '10px', color: T.textMuted, marginBottom: '2px' }}>
          {new Date(v.data).toLocaleString('it-IT')} — modificata da {v.utente || 'N.D.'}
        </div>
      ))}
    </div>
  );
}

// ─── OFFICINA PREVENTIVO ─────────────────────────────────────────────────────
function OfficinaPreventivo({ ricambi, T, isMobile, db, vehicles }) {
  const COSTO_MANODOPERA = 47;
  const IVA = 0.22;

  const [nomeCliente, setNomeCliente] = useState('');
  const [cognomeCliente, setCognomeCliente] = useState('');
  const [societaCliente, setSocietaCliente] = useState('');
  const [cittaCliente, setCittaCliente] = useState('');
  const [capCliente, setCapCliente] = useState('');
  const [indirizzoCliente, setIndirizzoCliente] = useState('');
  const [pivaCliente, setPivaCliente] = useState('');
  const [cfCliente, setCfCliente] = useState('');
  const [sdiCliente, setSdiCliente] = useState('');
  const [pecCliente, setPecCliente] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [cellulareCliente, setCellulareCliente] = useState('');
  const [targa, setTarga] = useState('');
  const [note, setNote] = useState('');
  const [righe, setRighe] = useState([]);
  const [searchRicambio, setSearchRicambio] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [costoMano, setCostoMano] = useState(COSTO_MANODOPERA);
  const [veicoloAbbinato, setVeicoloAbbinato] = useState(null);
  const [searchVeicolo, setSearchVeicolo] = useState('');
  const [showVeicoloSuggest, setShowVeicoloSuggest] = useState(false);

  // Ricerca clienti da schede service storiche
  const [schedeService, setSchedeService] = useState([]);
  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteSuggest, setShowClienteSuggest] = useState(false);

  // Pianificazione officina
  const [planData,   setPlanData]   = useState('');
  const [planSlot,   setPlanSlot]   = useState('');
  const [planPonte,  setPlanPonte]  = useState('');
  const [planSede,   setPlanSede]   = useState('agnano');
  const [planOperatore, setPlanOperatore] = useState('');

  useEffect(() => {
    const clienti = {};

    const aggiungiCliente = (nome, indirizzo, piva, cf) => {
      if (!nome?.trim()) return;
      const key = nome.toLowerCase().trim();
      if (!clienti[key] || (!clienti[key].indirizzo && indirizzo)) {
        clienti[key] = { nome: nome.trim(), indirizzo: indirizzo||'', piva: piva||'', cf: cf||'' };
      }
    };

    // Da service_interventi
    get(ref(db, 'service_interventi')).then(snap => {
      if (snap.exists()) Object.values(snap.val()).forEach(i =>
        aggiungiCliente(i.nomeCliente, i.indirizzoCliente, i.pivaCliente, i.cfCliente));
    });

    // Da contratti (fonte principale — dati più completi)
    get(ref(db, 'contratti')).then(snap => {
      if (snap.exists()) Object.values(snap.val()).forEach(c => {
        const cl = c.cliente || {};
        const nome = cl.ragioneSociale || `${cl.nome||''} ${cl.cognome||''}`.trim();
        const indirizzo = [cl.indirizzo, cl.cap, cl.citta].filter(Boolean).join(', ');
        aggiungiCliente(nome, indirizzo, cl.piva, cl.cf || cl.codiceFiscale);
      });
      setSchedeService(Object.values(clienti).sort((a,b) => a.nome.localeCompare(b.nome)));
    });

    // Da preventivi
    get(ref(db, 'preventivi')).then(snap => {
      if (snap.exists()) Object.values(snap.val()).forEach(p => {
        const cl = p.cliente || {};
        const nome = cl.ragioneSociale || `${cl.nome||''} ${cl.cognome||''}`.trim();
        const indirizzo = [cl.indirizzo, cl.cap, cl.citta].filter(Boolean).join(', ');
        aggiungiCliente(nome, indirizzo, cl.piva, cl.cf || cl.codiceFiscale);
      });
      setSchedeService(Object.values(clienti).sort((a,b) => a.nome.localeCompare(b.nome)));
    });
  }, []);

  const clientiSuggeriti = useMemo(() => {
    if (!searchCliente.trim()) return [];
    const s = searchCliente.toLowerCase();
    return schedeService.filter(c => c.nome.toLowerCase().includes(s)).slice(0, 6);
  }, [searchCliente, schedeService]);

  const selezionaCliente = (c) => {
    setNomeCliente(c.nome || '');
    setCognomeCliente(c.cognome || '');
    setSocietaCliente(c.societa || c.ragioneSociale || '');
    setCittaCliente(c.citta || '');
    setCapCliente(c.cap || '');
    setIndirizzoCliente(c.indirizzo || '');
    setPivaCliente(c.piva || '');
    setCfCliente(c.cf || '');
    setSdiCliente(c.sdi || '');
    setPecCliente(c.pec || '');
    setEmailCliente(c.email || '');
    setCellulareCliente(c.cellulare || '');
    setShowClienteSuggest(false);
  };

  const veicoliSuggeriti = useMemo(() => {
    if (!searchVeicolo.trim()) return [];
    const s = searchVeicolo.trim().toLowerCase();
    return (vehicles || []).filter(v => {
      const targa = (v.targa || v.plate || '').toLowerCase();
      const vin = (v.vin || '').toLowerCase();
      const marca = (v.marca || '').toLowerCase();
      const modello = (v.modello || '').toLowerCase();
      return targa.includes(s) || vin.includes(s) || marca.includes(s) || modello.includes(s);
    }).slice(0, 8);
  }, [searchVeicolo, vehicles]);

  const selezionaVeicolo = (v) => {
    setVeicoloAbbinato(v);
    setTarga(v.targa || '');
    setSearchVeicolo('');
    setShowVeicoloSuggest(false);
  };

  const suggerimenti = useMemo(() => {
    if (!searchRicambio.trim()) return [];
    const s = searchRicambio.toLowerCase();
    return ricambi.filter(r =>
      r.codice?.toLowerCase().includes(s) || r.descrizione?.toLowerCase().includes(s)
    ).slice(0, 8);
  }, [searchRicambio, ricambi]);

  const aggiungiRicambio = (r) => {
    setRighe(prev => [...prev, { id: Date.now(), tipo: 'ricambio', codice: r.codice, descrizione: r.descrizione, qta: 1, prezzoUnit: r.prezzoNetto || 0 }]);
    setSearchRicambio(''); setShowSuggest(false);
  };
  const aggiungiManodopera = () => setRighe(prev => [...prev, { id: Date.now(), tipo: 'manodopera', codice: 'MAN', descrizione: 'Manodopera', qta: 1, prezzoUnit: parseFloat(costoMano) || COSTO_MANODOPERA }]);
  const aggiungiRigaLibera = () => setRighe(prev => [...prev, { id: Date.now(), tipo: 'libera', codice: '', descrizione: '', qta: 1, prezzoUnit: 0 }]);
  const rimuoviRiga = (id) => setRighe(prev => prev.filter(r => r.id !== id));
  const aggiornaRiga = (id, field, val) => setRighe(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));

  const totaleNetto = righe.reduce((s, r) => s + (parseFloat(r.qta)||0) * (parseFloat(r.prezzoUnit)||0), 0);
  const totaleIVA   = totaleNetto * IVA;
  const totaleLordo = totaleNetto + totaleIVA;
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

  const salvaSuFirebase = async () => {
    const intervento = {
      id: Date.now().toString(),
      data: new Date().toISOString(),
      nomeCliente,
      cognomeCliente,
      societaCliente,
      cittaCliente,
      capCliente,
      indirizzoCliente,
      pivaCliente,
      cfCliente,
      sdiCliente,
      pecCliente,
      emailCliente,
      cellulareCliente,
      targa,
      note,
      veicolo: veicoloAbbinato ? {
        marca: veicoloAbbinato.marca,
        modello: veicoloAbbinato.modello,
        targa: veicoloAbbinato.targa,
        vin: veicoloAbbinato.vin,
        mainImg: veicoloAbbinato.mainImg || '',
      } : null,
      righe: righe.map(r => ({ ...r })),
      totaleNetto,
      totaleIVA: totaleNetto * IVA,
      totaleLordo: totaleNetto * (1 + IVA),
    };
    try {
      await set(ref(db, `service_interventi/${intervento.id}`), intervento);

      // Collega al planning officina se configurato
      if (planData && planSlot && planPonte && planOperatore) {
        const planKey = `planning_officina/${planSede}/${planData}/ponte_${planPonte}/${planSlot.replace(':', '_')}`;
        await set(ref(db, planKey), {
          operatore:      planOperatore,
          cliente:        nomeCliente || '',
          targa:          targa || veicoloAbbinato?.targa || '',
          note:           note || '',
          preventivoId:   intervento.id,
          totaleNetto:    intervento.totaleNetto,
          totaleLordo:    intervento.totaleLordo,
          marca:          veicoloAbbinato?.marca || '',
          modello:        veicoloAbbinato?.modello || '',
          salvatoAt:      new Date().toISOString(),
        });
      }

      // Scala i ricambi dal magazzino
      for (const r of righe) {
        if (r.tipo !== 'ricambio' || !r.codice) continue;
        const key = r.codice.replace(/[./ ]/g, '_');
        const snap = await new Promise(res => {
          const unsub = onValue(ref(db, `service_ricambi/${key}/esistenza`), snap => { unsub(); res(snap); });
        });
        const esistenzaAttuale = parseFloat(snap.val() || 0);
        const nuova = Math.max(0, esistenzaAttuale - (parseFloat(r.qta) || 0));
        await set(ref(db, `service_ricambi/${key}/esistenza`), nuova);
      }
    } catch(e) { console.warn('Errore salvataggio storico:', e.message); }
    return intervento;
  };

  const stampaPreconto = async () => {
    await salvaSuFirebase();
    const win = window.open('', '_blank');
    if (!win) return;

    const oggi = new Date().toLocaleDateString('it-IT');
    const numDoc = `OF-${Date.now().toString().slice(-6)}`;
    const vMarca  = veicoloAbbinato?.marca  || '';
    const vModel  = veicoloAbbinato?.modello || '';
    const vVers   = veicoloAbbinato?.versione || '';
    const vVin    = veicoloAbbinato?.vin    || '—';
    const vImmat  = veicoloAbbinato?.registrazione || '—';
    const vKm     = veicoloAbbinato?.km     || '—';

    const righeHTML = righe.map((r, i) => {
      const sub = (parseFloat(r.qta)||0) * (parseFloat(r.prezzoUnit)||0);
      const iva22 = sub * IVA;
      return `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;font-weight:700;">${r.codice||''}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;">${r.descrizione||''}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${r.tipo==='manodopera'?'UL':'PZ'}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">${parseFloat(r.qta)||1}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;">${fmtE(r.prezzoUnit)}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;"></td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:right;font-weight:700;">${fmtE(sub)}</td>
        <td style="padding:5px 6px;border-bottom:1px solid #ddd;font-size:11px;text-align:center;">22</td>
      </tr>`;
    }).join('');

    const totImp = totaleNetto.toLocaleString('it-IT', {minimumFractionDigits:2});
    const totIVA2 = totaleIVA.toLocaleString('it-IT', {minimumFractionDigits:2});
    const totLor = totaleLordo.toLocaleString('it-IT', {minimumFractionDigits:2});
    const totListino = totaleLordo.toLocaleString('it-IT', {minimumFractionDigits:2});

    const manoTot = righe.filter(r=>r.tipo==='manodopera').reduce((s,r)=>(parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0)+s,0);
    const matTot  = righe.filter(r=>r.tipo!=='manodopera').reduce((s,r)=>(parseFloat(r.qta)||0)*(parseFloat(r.prezzoUnit)||0)+s,0);

        win.document.write(`<!DOCTYPE html><html><head><title>Fattura Officina</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #111; background: #fff; padding: 20px; }
  .page { max-width: 800px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; border-bottom: 2px solid #000; padding-bottom: 12px; }
  .logo { font-size: 22px; font-weight: 900; letter-spacing: -1px; }
  .logo span { font-size: 10px; font-weight: 400; letter-spacing: 2px; display: block; margin-bottom: 2px; }
  .azienda-info { font-size: 10px; line-height: 1.6; }
  .dest-box { border: 1px solid #333; padding: 10px 14px; font-size: 11px; min-width: 220px; }
  .natura { text-align: right; margin-bottom: 4px; }
  .natura-label { font-size: 9px; color: #555; text-transform: uppercase; }
  .natura-val { font-size: 13px; font-weight: 900; border: 2px solid #000; padding: 4px 12px; display: inline-block; margin-top: 2px; }
  .dati-veicolo { display: grid; grid-template-columns: repeat(6, 1fr); border: 1px solid #333; margin-bottom: 0; }
  .dati-veicolo .cell { border-right: 1px solid #333; padding: 4px 6px; }
  .dati-veicolo .cell:last-child { border-right: none; }
  .cell-label { font-size: 8px; color: #555; text-transform: uppercase; }
  .cell-val { font-size: 11px; font-weight: 700; margin-top: 1px; }
  .dati2 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; border: 1px solid #333; border-top: none; margin-bottom: 8px; }
  .dati2 .cell { border-right: 1px solid #333; padding: 4px 6px; }
  .dati2 .cell:last-child { border-right: none; }
  .titolo-tab { font-size: 9px; font-weight: 900; text-transform: uppercase; background: #1e293b; color: #fff; padding: 4px 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
  th { background: #1e293b; color: #fff; padding: 5px 6px; font-size: 9px; text-transform: uppercase; text-align: left; border-right: 1px solid #444; }
  th:last-child { border-right: none; }
  td { font-size: 11px; }
  .iva-box { display: grid; grid-template-columns: 60px 120px 120px; border: 1px solid #333; border-top: none; margin-bottom: 8px; }
  .iva-box .cell { border-right: 1px solid #333; padding: 4px 6px; text-align: right; }
  .iva-box .cell:first-child { text-align: center; }
  .totali-box { display: flex; justify-content: flex-end; margin-bottom: 8px; }
  .totali-inner { border: 1px solid #333; min-width: 340px; }
  .tot-row { display: flex; justify-content: space-between; padding: 3px 10px; border-bottom: 1px solid #ddd; font-size: 11px; }
  .tot-finale { display: flex; justify-content: space-between; padding: 5px 10px; font-size: 14px; font-weight: 900; background: #f0f0f0; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr; border: 1px solid #333; margin-top: 8px; }
  .footer-grid .cell { border-right: 1px solid #333; padding: 4px 6px; }
  .footer-grid .cell:last-child { border-right: none; }
  .note-box { border: 1px solid #333; padding: 6px 8px; margin-bottom: 8px; min-height: 30px; font-size: 10px; }
  .disclaimer { font-size: 9px; color: #555; margin-top: 12px; line-height: 1.5; border-top: 1px solid #ddd; padding-top: 8px; }
  @media print { button { display: none !important; } body { padding: 10px; } }
</style>
</head><body>
<button onclick="window.print()" style="background:#1e293b;color:#fff;border:none;padding:8px 20px;border-radius:6px;cursor:pointer;margin-bottom:16px;font-size:12px;font-weight:700;">🖨️ Stampa / Salva PDF</button>

<div class="page">
  <!-- HEADER -->
  <div class="header">
    <div>
      <img src="data:image/png;base64,${LOGO_GAS_BASE64}" alt="Gruppo Autoscala" style="height:52px;display:block;" />
      <div class="azienda-info" style="margin-top:8px;">
        Ragione Sociale: Gruppo Autoscala S.r.l.<br>
        Sede Legale: Via Antiniana, 2/B - 80078 Pozzuoli (NA)<br>
        Tel. 0811865872<br>
        e-mail: amministrazione@gruppoautoscala.com<br>
        Sito internet: www.gruppoautoscala.com<br>
        P.IVA e Codice Fiscale: IT07821241218
      </div>
    </div>
    <div style="text-align:right;">
      <div class="dest-box" style="text-align:left;margin-bottom:10px;">
        <div style="font-size:8px;color:#555;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;">Cliente / Destinatario</div>
        <div style="font-size:13px;font-weight:900;margin-bottom:5px;">${nomeCliente||'—'}</div>
        ${indirizzoCliente ? `<div style="display:flex;gap:6px;margin-top:3px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:68px;padding-top:1px;">Indirizzo</span><span style="font-size:10px;">${indirizzoCliente}</span></div>` : ''}
        ${pivaCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:68px;padding-top:1px;">P.IVA</span><span style="font-size:10px;">${pivaCliente}</span></div>` : ''}
        ${cfCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:68px;padding-top:1px;">Cod. Fiscale</span><span style="font-size:10px;">${cfCliente}</span></div>` : ''}
        ${emailCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:68px;padding-top:1px;">Email</span><span style="font-size:10px;">${emailCliente}</span></div>` : ''}
        ${cellulareCliente ? `<div style="display:flex;gap:6px;margin-top:2px;"><span style="font-size:8px;color:#777;text-transform:uppercase;min-width:68px;padding-top:1px;">Cellulare</span><span style="font-size:10px;">${cellulareCliente}</span></div>` : ''}
      </div>
      <div class="natura">
        <div class="natura-label">NATURA DOCUMENTO</div>
        <div class="natura-val">FATTURA OFFICINA</div>
      </div>
    </div>
  </div>

  <!-- DATI VEICOLO -->
  <div class="dati-veicolo">
    <div class="cell" style="grid-column:span 2;"><div class="cell-label">NUM. TELAIO</div><div class="cell-val">${vVin}</div></div>
    <div class="cell"><div class="cell-label">TARGA</div><div class="cell-val">${targa||'—'}</div></div>
    <div class="cell"><div class="cell-label">IMMATRICOLAZIONE</div><div class="cell-val">${vImmat}</div></div>
    <div class="cell"><div class="cell-label">MODELLO</div><div class="cell-val">${vMarca} ${vModel} ${vVers}</div></div>
    <div class="cell"><div class="cell-label">NUM. DOCUMENTO</div><div class="cell-val">${numDoc}</div></div>
  </div>
  <div class="dati2">
    <div class="cell"><div class="cell-label">KM</div><div class="cell-val">${vKm}</div></div>
    <div class="cell"><div class="cell-label">DATA</div><div class="cell-val">${oggi}</div></div>
    <div class="cell"><div class="cell-label">PAGAMENTO</div><div class="cell-val">COME CONVENUTO</div></div>
    <div class="cell"><div class="cell-label">NOTE</div><div class="cell-val">${note||'—'}</div></div>
  </div>

  <!-- TABELLA VOCI -->
  <div class="titolo-tab">Autovettura – Ricambi o Prestazione</div>
  <table>
    <thead>
      <tr>
        <th style="width:80px">Codice</th>
        <th>Descrizione</th>
        <th style="width:40px;text-align:center">UM</th>
        <th style="width:55px;text-align:center">Quantità</th>
        <th style="width:90px;text-align:right">Prezzo</th>
        <th style="width:70px;text-align:right">Sconto</th>
        <th style="width:90px;text-align:right">Importo</th>
        <th style="width:40px;text-align:center">% IVA</th>
      </tr>
    </thead>
    <tbody>
      ${righeHTML}
    </tbody>
  </table>

  <!-- RIGA IVA -->
  <div style="border:1px solid #333;border-top:none;display:grid;grid-template-columns:50px 1fr;margin-bottom:8px;">
    <div style="border-right:1px solid #333;padding:4px 6px;text-align:center;font-weight:700;">22</div>
    <div style="padding:4px 6px;font-size:10px;">IMPONIBILE 22%</div>
  </div>

  <!-- TOTALI -->
  <div style="display:flex;justify-content:flex-end;margin-bottom:8px;">
    <div style="border:1px solid #333;min-width:380px;">
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;border-bottom:1px solid #333;">
        <div style="padding:4px 8px;border-right:1px solid #333;"><div class="cell-label">IVA %</div><div style="font-size:12px;font-weight:700;text-align:center;">22</div></div>
        <div style="padding:4px 8px;border-right:1px solid #333;"><div class="cell-label">IMPONIBILE</div><div style="font-size:12px;font-weight:700;text-align:right;">${totImp}</div></div>
        <div style="padding:4px 8px;"><div class="cell-label">IMPOSTA</div><div style="font-size:12px;font-weight:700;text-align:right;">${totIVA2}</div></div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #333;">
        <div style="padding:4px 8px;border-right:1px solid #333;"><div class="cell-label">TOTALE IMPONIBILE</div><div style="font-size:13px;font-weight:700;text-align:right;">${totImp}</div></div>
        <div style="padding:4px 8px;"><div class="cell-label">TOTALE IVA</div><div style="font-size:13px;font-weight:700;text-align:right;">${totIVA2}</div></div>
      </div>
      <div style="display:flex;justify-content:space-between;padding:6px 8px;background:#f0f0f0;">
        <div class="cell-label" style="font-size:10px;font-weight:700;line-height:1.8;">TOTALE IMPORTO</div>
        <div style="font-size:18px;font-weight:900;">${totLor}</div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;border-top:1px solid #333;">
        <div style="padding:4px 8px;border-right:1px solid #333;"><div class="cell-label">TOTALE LISTINO</div><div style="font-size:12px;font-weight:700;text-align:right;">${totListino}</div></div>
        <div style="padding:4px 8px;"><div class="cell-label">CORRISP. NON PAGATO</div><div style="font-size:12px;font-weight:700;text-align:right;">${totLor}</div></div>
      </div>
    </div>
  </div>

  <!-- FOOTER GRIGLIA -->
  <div style="border:1px solid #333;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;margin-bottom:8px;">
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">CAUSALE DEL TRASPORTO</div><div style="font-size:10px;margin-top:2px;">—</div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">TRASPORTO A CURA DEL</div><div style="font-size:10px;margin-top:2px;">—</div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">PORTO</div><div style="font-size:10px;margin-top:2px;">—</div></div>
    <div style="padding:4px 6px;"><div class="cell-label">RIF. ORDINE CLIENTE</div><div style="font-size:10px;margin-top:2px;">—</div></div>
  </div>
  <div style="border:1px solid #333;border-top:none;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;margin-bottom:8px;">
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">N. COLLI / INIZIO TRASPORTO</div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">FIRMA DEL CONDUCENTE</div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"></div>
    <div style="padding:4px 6px;"><div class="cell-label">FIRMA DEL DESTINATARIO</div></div>
  </div>

  <!-- CONTRIBUTO CONAI / MANODOPERA / MATERIALE -->
  <div style="border:1px solid #333;border-top:none;display:grid;grid-template-columns:1fr 1fr 1fr 1fr 1fr 1fr;margin-bottom:16px;">
    <div style="padding:4px 6px;border-right:1px solid #333;grid-column:span 2;"><div class="cell-label">CONTRIBUTO CONAI ASSOLTO OVE PREVISTO</div><div style="font-size:10px;margin-top:2px;"></div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">MANODOPERA</div><div style="font-size:12px;font-weight:700;text-align:right;margin-top:4px;">${manoTot.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}</div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">MATERIALE</div><div style="font-size:12px;font-weight:700;text-align:right;margin-top:4px;">${matTot.toLocaleString('it-IT',{minimumFractionDigits:2, maximumFractionDigits:2})}</div></div>
    <div style="padding:4px 6px;border-right:1px solid #333;"><div class="cell-label">ONERI VARI</div><div style="font-size:12px;font-weight:700;text-align:right;margin-top:4px;">0,00</div></div>
    <div style="padding:4px 6px;"><div class="cell-label">FIRMA CASSIERE</div></div>
  </div>

  <div class="disclaimer">
    LA FORNITURA DELLA MERCE VIENE ESEGUITA A TERMINI DELLE CONDIZIONI GENERALI DEL CONTRATTO IN ATTO TRA IL MITTENTE ED IL DESTINATARIO<br>
    <b>IMPORTANTE</b> – PER IL RITARDATO PAGAMENTO SARANNO ADDEBITATI GLI INTERESSI IN RAGIONE DEI GIORNI DI EFFETTIVO RITARDO, AL TASSO UFFICIALE DI SCONTO MAGGIORATO DI 3 PUNTI.<br>
    <i style="font-size:9px;">* Documento non valido ai fini fiscali — FATTURA OFFICINA</i>
  </div>
</div>
</body></html>`);
    win.document.close();
  };

  const inputStyle = { padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none', width: '100%', boxSizing: 'border-box' };

  return (
    <div>
      {/* Dati cliente */}
      <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '10px' }}>DATI CLIENTE / VEICOLO</div>

        {/* Barra ricerca cliente da archivio */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>
            Cerca cliente <span style={{ color: '#6366f1', fontSize: '9px' }}>— cerca da schede precedenti o compila manualmente i campi sotto</span>
          </div>
          <div style={{ position: 'relative' }}>
            <input style={{ ...inputStyle, background: 'rgba(99,102,241,0.05)', border: '1px dashed #6366f1' }}
              placeholder="Cerca per nome, cognome, p.iva..."
              value={searchCliente}
              onChange={e => { setSearchCliente(e.target.value); setShowClienteSuggest(true); }}
              onFocus={() => setShowClienteSuggest(true)}
              onBlur={() => setTimeout(() => setShowClienteSuggest(false), 200)}
            />
            {showClienteSuggest && clientiSuggeriti.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', zIndex: 600, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
                {clientiSuggeriti.map((c, i) => (
                  <div key={i} onClick={() => selezionaCliente(c)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{c.nome}</div>
                    <div style={{ fontSize: '10px', color: T.textMuted }}>
                      {c.indirizzo || '—'}{c.piva ? ` · P.IVA ${c.piva}` : ''}{c.cf ? ` · CF ${c.cf}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Campi cliente spacchettati */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Nome *</div>
            <input style={inputStyle} placeholder="Mario" value={nomeCliente} onChange={e => setNomeCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Cognome</div>
            <input style={inputStyle} placeholder="Rossi" value={cognomeCliente} onChange={e => setCognomeCliente(e.target.value)} />
          </div>
          <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Società / Ragione Sociale</div>
            <input style={inputStyle} placeholder="Rossi S.r.l." value={societaCliente} onChange={e => setSocietaCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Città</div>
            <input style={inputStyle} placeholder="Napoli" value={cittaCliente} onChange={e => setCittaCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>CAP</div>
            <input style={inputStyle} placeholder="80100" value={capCliente} onChange={e => setCapCliente(e.target.value)} />
          </div>
          <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Via / Indirizzo</div>
            <input style={inputStyle} placeholder="Via Roma 1" value={indirizzoCliente} onChange={e => setIndirizzoCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Partita IVA</div>
            <input style={inputStyle} placeholder="Es. IT07821241218" value={pivaCliente} onChange={e => setPivaCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Codice Fiscale</div>
            <input style={inputStyle} placeholder="Es. RSSMRA80A01H703Z" value={cfCliente} onChange={e => setCfCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Email cliente</div>
            <input type="email" style={inputStyle} placeholder="mario.rossi@email.it" value={emailCliente} onChange={e => setEmailCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>PEC</div>
            <input style={inputStyle} placeholder="mario@pec.it" value={pecCliente} onChange={e => setPecCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Cellulare cliente</div>
            <input type="tel" style={inputStyle} placeholder="+39 333 1234567" value={cellulareCliente} onChange={e => setCellulareCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Codice SDI</div>
            <input style={inputStyle} placeholder="Es. XXXXXXX" value={sdiCliente} onChange={e => setSdiCliente(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Costo manodopera (€/h)</div>
            <input type="number" style={inputStyle} value={costoMano} onChange={e => setCostoMano(e.target.value)} />
          </div>
        </div>
        {/* Ricerca veicolo stock */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Veicolo <span style={{ color: '#6366f1', fontSize: '9px' }}>cerca in stock o inserisci manualmente</span></div>
          {veicoloAbbinato ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(99,102,241,0.08)', border: '1px solid #6366f1', borderRadius: '12px' }}>
              {veicoloAbbinato.mainImg && <img src={veicoloAbbinato.mainImg} style={{ width: '52px', height: '36px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} alt="" onError={e => { e.target.style.display = 'none'; }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>{veicoloAbbinato.marca} {veicoloAbbinato.modello} {veicoloAbbinato.versione}</div>
                <div style={{ fontSize: '10px', color: '#6366f1', marginTop: '2px' }}>
                  🏷 {veicoloAbbinato.targa || 'N.D.'} · 🔑 {veicoloAbbinato.vin ? '···'+veicoloAbbinato.vin.slice(-6) : '—'} · {veicoloAbbinato.km?.toLocaleString('it-IT')} km · {veicoloAbbinato.sede}
                </div>
              </div>
              <button onClick={() => { setVeicoloAbbinato(null); setTarga(''); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', fontWeight: '900', padding: '0', flexShrink: 0 }}>×</button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input style={inputStyle} placeholder={`Cerca targa, telaio, marca o modello... (${(vehicles||[]).length} in stock)`}
                value={searchVeicolo}
                onChange={e => { setSearchVeicolo(e.target.value); setTarga(e.target.value); setShowVeicoloSuggest(true); }}
                onFocus={() => setShowVeicoloSuggest(true)}
                onBlur={() => setTimeout(() => setShowVeicoloSuggest(false), 200)}
              />
              {showVeicoloSuggest && veicoliSuggeriti.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', zIndex: 600, maxHeight: '220px', overflowY: 'auto', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
                  {veicoliSuggeriti.map((v, iV) => (
                    <div key={`${v.id || ''}|${v.vin || v.targa || ''}|${iV}`} onClick={() => selezionaVeicolo(v)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bg}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {v.mainImg && <img src={v.mainImg} style={{ width: '48px', height: '33px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} alt="" onError={e => { e.target.style.display = 'none'; }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{v.marca} {v.modello}</div>
                        <div style={{ fontSize: '10px', color: T.textMuted }}>{v.targa || 'N.D.'} · {v.vin ? '···'+v.vin.slice(-6) : '—'} · {v.registrazione}</div>
                      </div>
                      <div style={{ fontSize: '10px', color: '#10b981', fontWeight: '700', flexShrink: 0 }}>{v.sede}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Note intervento</div>
          <input style={inputStyle} placeholder="Es. Tagliando 30.000 km, sostituzione distribuzione..." value={note} onChange={e => setNote(e.target.value)} />
        </div>
      </div>

      {/* Pianificazione officina */}
      <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '12px' }}>📅 PIANIFICAZIONE OFFICINA <span style={{ fontSize: '9px', fontWeight: '400', color: T.textMuted }}>(opzionale)</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Sede</div>
            <select value={planSede} onChange={e => setPlanSede(e.target.value)} style={inputStyle}>
              <option value="agnano">Agnano</option>
              <option value="salerno">Salerno</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Data</div>
            <input type="date" value={planData} onChange={e => setPlanData(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Orario</div>
            <select value={planSlot} onChange={e => setPlanSlot(e.target.value)} style={inputStyle}>
              <option value="">— Seleziona —</option>
              {['08:30','09:30','10:30','11:30','12:30','14:30','15:30','16:30','17:30'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Ponte</div>
            <select value={planPonte} onChange={e => setPlanPonte(e.target.value)} style={inputStyle}>
              <option value="">— Ponte —</option>
              {Array.from({ length: planSede === 'agnano' ? 9 : 5 }, (_, i) => <option key={i+1} value={i+1}>Ponte {i+1}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>Operatore</div>
            <select value={planOperatore} onChange={e => setPlanOperatore(e.target.value)} style={inputStyle}>
              <option value="">— Operatore —</option>
              {['Pasquale Martorelli','Adriano Semplice','Antonio Esposito','Leonardo Piccegna','Marco',"Nunzio D'Ambrosio"].map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
        </div>
        {planData && planSlot && planPonte && planOperatore && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(99,102,241,0.12)', borderRadius: '8px', fontSize: '11px', color: '#6366f1', fontWeight: '700' }}>
            ✓ Prenotato: {planOperatore.split(' ')[0]} · Ponte {planPonte} · {planSlot} · {planData ? new Date(planData + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' }) : ''}
          </div>
        )}
      </div>

      {/* Aggiungi voci */}
      <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '10px' }}>AGGIUNGI VOCI</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <button onClick={aggiungiManodopera} style={{ padding: '8px 14px', background: 'rgba(99,102,241,0.12)', border: '1px solid #6366f1', borderRadius: '10px', color: '#6366f1', cursor: 'pointer', fontWeight: '700', fontSize: '11px' }}>
            + Manodopera ({fmtE(costoMano)}/h)
          </button>
          <button onClick={aggiungiRigaLibera} style={{ padding: '8px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.textSub, cursor: 'pointer', fontWeight: '700', fontSize: '11px' }}>
            + Riga libera
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <input style={inputStyle} placeholder="🔍 Cerca ricambio da magazzino (codice o descrizione)..."
            value={searchRicambio}
            onChange={e => { setSearchRicambio(e.target.value); setShowSuggest(true); }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
          />
          {showSuggest && suggerimenti.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', zIndex: 500, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
              {suggerimenti.map(r => (
                <div key={r.codice} onClick={() => aggiungiRicambio(r)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bg}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div>
                    <div style={{ fontSize: '9px', color: '#6366f1', fontWeight: '700' }}>{r.codice}</div>
                    <div style={{ fontSize: '12px', color: T.text }}>{r.descrizione}</div>
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#10b981', flexShrink: 0 }}>{fmtE(r.prezzoNetto)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabella righe */}
      {righe.length > 0 && (
        <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, overflow: 'hidden', marginBottom: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 60px 80px 30px' : '90px 1fr 90px 90px 100px 30px', padding: '8px 12px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
            {(isMobile ? ['DESCRIZIONE','QTÀ','SUBTOT',''] : ['CODICE','DESCRIZIONE','QTÀ','PREZZO','SUBTOT','']).map((h,i) => (
              <div key={i} style={{ fontSize: '8px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px' }}>{h}</div>
            ))}
          </div>
          {righe.map(r => {
            const sub = (parseFloat(r.qta)||0) * (parseFloat(r.prezzoUnit)||0);
            const inS = { padding: '5px 8px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '7px', color: T.text, fontSize: '11px', outline: 'none', width: '100%', boxSizing: 'border-box' };
            return (
              <div key={r.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 60px 80px 30px' : '90px 1fr 90px 90px 100px 30px', padding: '8px 12px', borderBottom: `1px solid ${T.border}`, alignItems: 'center', gap: '6px' }}>
                {!isMobile && <div style={{ fontSize: '10px', fontWeight: '700', color: '#6366f1' }}>{r.codice || '—'}</div>}
                <input value={r.descrizione} onChange={e => aggiornaRiga(r.id, 'descrizione', e.target.value)} style={inS} placeholder="Descrizione..." />
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <button onClick={() => aggiornaRiga(r.id,'qta',Math.max(0.5,(parseFloat(r.qta)||1)-0.5))} style={{ background:'rgba(239,68,68,0.15)',border:'none',borderRadius:'5px',color:'#ef4444',cursor:'pointer',fontWeight:'900',fontSize:'11px',width:'18px',height:'22px',padding:0 }}>−</button>
                  <input type="number" value={r.qta} onChange={e => aggiornaRiga(r.id,'qta',e.target.value)} style={{ ...inS, width:'36px', textAlign:'center', padding:'5px 2px' }} />
                  <button onClick={() => aggiornaRiga(r.id,'qta',(parseFloat(r.qta)||1)+0.5)} style={{ background:'rgba(16,185,129,0.15)',border:'none',borderRadius:'5px',color:'#10b981',cursor:'pointer',fontWeight:'900',fontSize:'11px',width:'18px',height:'22px',padding:0 }}>+</button>
                </div>
                {!isMobile && <input type="number" value={r.prezzoUnit} onChange={e => aggiornaRiga(r.id,'prezzoUnit',e.target.value)} style={{ ...inS, textAlign:'right' }} />}
                <div style={{ fontSize: '12px', fontWeight: '900', color: '#10b981', textAlign: 'right' }}>{fmtE(sub)}</div>
                <button onClick={() => rimuoviRiga(r.id)} style={{ background:'none',border:'none',color:'#ef4444',cursor:'pointer',fontSize:'16px',padding:'0',fontWeight:'900' }}>×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Totali + Stampa */}
      {righe.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px', minWidth: '240px' }}>
            {[['Imponibile', totaleNetto, T.text], ['IVA 22%', totaleIVA, '#f59e0b'], ['TOTALE', totaleLordo, '#10b981']].map(([l,v,c]) => (
              <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding: l==='TOTALE'?'10px 0 0':'5px 0', borderTop: l==='TOTALE'?`2px solid ${T.border}`:'none', marginTop: l==='TOTALE'?'5px':0 }}>
                <span style={{ fontSize: l==='TOTALE'?'14px':'12px', fontWeight: l==='TOTALE'?'900':'600', color: T.textSub }}>{l}</span>
                <span style={{ fontSize: l==='TOTALE'?'20px':'13px', fontWeight:'900', color:c }}>{fmtE(v)}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            <button onClick={stampaPreconto} style={{ padding:'12px 24px', background:'linear-gradient(135deg,#6366f1,#4f46e5)', border:'none', borderRadius:'12px', color:'#fff', cursor:'pointer', fontWeight:'900', fontSize:'12px' }}>
              🖨️ Stampa Preconto
            </button>
            <button onClick={() => { setRighe([]); setVeicoloAbbinato(null); setTarga(''); setNomeCliente(''); setNote(''); }} style={{ padding:'10px 24px', background:T.bg, border:`1px solid ${T.border}`, borderRadius:'12px', color:'#ef4444', cursor:'pointer', fontWeight:'700', fontSize:'11px' }}>
              🗑️ Nuovo preconto
            </button>
          </div>
        </div>
      )}

      {righe.length === 0 && (
        <div style={{ textAlign:'center', padding:'40px', color:T.textMuted }}>
          <div style={{ fontSize:'40px', marginBottom:'12px' }}>🛠️</div>
          <div style={{ fontSize:'13px', fontWeight:'700' }}>Aggiungi voci per creare il preconto</div>
          <div style={{ fontSize:'11px', marginTop:'6px' }}>Cerca un veicolo in stock, aggiungi ricambi dal magazzino o manodopera</div>
        </div>
      )}
    </div>
  );
}

// ─── SCHEDA SERVICE VIEW ─────────────────────────────────────────────────────
function SchedaServiceView({ T, isMobile, db, user }) {
  const [contratti, setContratti] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Prima scaricava l'INTERO nodo contratti (senza nemmeno cleanup: ogni
    // apertura di questa vista aggiungeva un listener orfano permanente) ad
    // ogni singola scrittura di qualsiasi contratto da chiunque. Le query
    // filtrate scaricano solo i record negli stati che servono davvero qui.
    let attivo = true;
    const caricaContratti = () => {
      Promise.all([
        get(query(ref(db, 'contratti'), orderByChild('stato'), equalTo('approvato'))),
        get(query(ref(db, 'contratti'), orderByChild('stato'), equalTo('venduto'))),
      ]).then(([snapApprovati, snapVenduti]) => {
        if (!attivo) return;
        const uniti = { ...(snapApprovati.val() || {}), ...(snapVenduti.val() || {}) };
        const dataOrdinamento2 = c => { const t = new Date(c.dataCreazione || 0).getTime(); return isNaN(t) ? 0 : t; };
        let list = Object.values(uniti)
          .filter(c => !c.cliente?.b2b)
          .sort((a, b) => dataOrdinamento2(b) - dataOrdinamento2(a));
        // service: vede solo i contratti della propria sede (stesso
        // meccanismo già applicato in Back Office).
        if (user?.ruolo === 'service' && user?.sede) {
          list = list.filter(c => getSede((c.venditoreNome||'').toLowerCase()) === user.sede);
        }
        setContratti(list);
        setLoading(false);
      });
    };
    caricaContratti();
    const interval = setInterval(caricaContratti, 180000);
    return () => { attivo = false; clearInterval(interval); };
  }, [user]);

  // Carica stock per arricchire VIN/km mancanti
  useEffect(() => {
    const cached = window._radarStockCache;
    if (cached?.vehicles) {
      const map = {};
      cached.vehicles.forEach(v => {
        if (v.targa) map[v.targa] = v;
        if (v.vin) map[v.vin] = v;
        if (v.id) map[v.id] = v;
      });
      setStockMap(map);
    } else {
      get(ref(db, 'stock_data')).then(snap => {
        const data = snap.val();
        if (data?.vehicles) {
          const map = {};
          data.vehicles.forEach(v => {
            if (v.targa) map[v.targa] = v;
            if (v.vin) map[v.vin] = v;
            if (v.id) map[v.id] = v;
          });
          setStockMap(map);
        }
      });
    }
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return contratti;
    const s = search.toLowerCase();
    return contratti.filter(c => {
      const nc = `${c.cliente?.ragioneSociale||''} ${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.toLowerCase();
      const nv = `${c.veicolo?.marca||''} ${c.veicolo?.modello||''} ${c.veicolo?.targa||''} ${c.veicolo?.vin||''}`.toLowerCase();
      return nc.includes(s) || nv.includes(s);
    });
  }, [contratti, search]);

  const getServizi = (s, pr) => {
    if (!s) return [];
    pr = pr || {};
    return [
      s.lojack && 'LoJack',
      (s.mawdy36||s.mawdy) && `Ext Garanzia Mawdy ${s.garanziaEstesaMesi || 36} mesi`,
      (s.estinzioneAnticipata || pr.estinzioneAnticipata) && 'Estinzione anticipata finanziamento',
      s.blockshaft && 'Blockshaft',
      s.gpl && 'Installazione GPL',
      s.kitAutocarro && 'Kit Autocarro',
      s.sensoriParcheggio && 'Sensori Parcheggio',
      s.retrocamera && 'Retrocamera',
      s.bracciolo && 'Bracciolo',
      s.vetriPrivacy && 'Vetri Privacy',
      s.tagliando && 'Tagliando',
      s.revisione && 'Revisione',
      s.secondaChiave && '2a Chiave',
      s.altroAntifurto,
      s.altroAccessorio,
      s.altroInstallazione,
      s.lavorazioniCarrozzeria && `Carrozzeria: ${s.lavorazioniCarrozzeria}`,
    ].filter(Boolean);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: T.textMuted }}>Caricamento...</div>;

  return (
    <div>
      <input placeholder="🔍 Cerca cliente, veicolo, targa, VIN..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '10px 14px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', color: T.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box', marginBottom: '14px' }} />

      <div style={{ fontSize: '11px', color: T.textMuted, marginBottom: '12px' }}>{filtered.length} contratti approvati</div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
          <div style={{ fontSize: '13px', fontWeight: '700' }}>Nessun contratto trovato</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(c => {
            const v = c.veicolo || {};
            const cl = c.cliente || {};
            const pr = c.prezzo || {};
            // Arricchisci con dati stock se VIN o KM mancanti
            const stockV = stockMap[v.targa] || stockMap[v.vin] || stockMap[v.id] || {};
            const vinFinal = v.vin || stockV.vin || '—';
            const kmFinal = v.km || stockV.km || 0;
            const immatFinal = v.registrazione || stockV.registrazione || '—';
            const servizi = getServizi(c.servizi, c.prezzo);
            const nomeCliente = cl.ragioneSociale || `${cl.nome||''} ${cl.cognome||''}`.trim() || '—';
            const isOpen = selected === c.id;

            return (
              <div key={c.id} style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                {/* Header — clic per espandere */}
                <div onClick={() => setSelected(isOpen ? null : c.id)}
                  style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bg + '80'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {v.mainImg && <img src={v.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '56px', height: '38px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>{nomeCliente}</div>
                    <div style={{ fontSize: '11px', color: T.textMuted }}>{v.marca} {v.modello} · {v.targa || '—'} · {v.vin ? `···${v.vin.slice(-6)}` : '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '10px', color: T.textMuted }}>{c.datiExtra?.numeroContratto}</div>
                    <div style={{ fontSize: '9px', color: servizi.length > 0 ? '#6366f1' : T.textMuted, fontWeight: '700', marginTop: '2px' }}>
                      {servizi.length > 0 ? `${servizi.length} servizi` : 'Nessun servizio'}
                    </div>
                    <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '2px' }}>{isOpen ? '▲' : '▼'}</div>
                  </div>
                </div>

                {/* Dettaglio espandibile */}
                {isOpen && (
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>

                      {/* ANAGRAFICA CLIENTE */}
                      <div style={{ background: T.bg, borderRadius: '12px', padding: '14px' }}>
                        <div style={{ fontSize: '9px', fontWeight: '900', color: '#6366f1', letterSpacing: '1px', marginBottom: '10px' }}>ANAGRAFICA CLIENTE</div>
                        {[
                          ['Cliente', nomeCliente],
                          ['CF / P.IVA', cl.codiceFiscale || cl.piva || '—'],
                          ['Indirizzo', cl.indirizzo || '—'],
                          ['Città', `${cl.citta || '—'} ${cl.cap || ''} ${cl.provincia ? `(${cl.provincia})` : ''}`],
                          ['Cellulare', cl.cellulare || '—'],
                          cl.cellulare2 && ['Cellulare 2', cl.cellulare2],
                          ['Email', cl.email || '—'],
                          cl.pec && ['PEC', cl.pec],
                        ].filter(Boolean).map(([l, v2]) => (
                          <div key={l} style={{ display: 'flex', gap: '8px', padding: '4px 0', borderBottom: `1px solid ${T.border}`, fontSize: '11px' }}>
                            <span style={{ color: T.textMuted, minWidth: '80px', flexShrink: 0 }}>{l}</span>
                            <span style={{ color: T.text, fontWeight: '600', wordBreak: 'break-all' }}>{v2}</span>
                          </div>
                        ))}
                      </div>

                      {/* ANAGRAFICA VEICOLO */}
                      <div style={{ background: T.bg, borderRadius: '12px', padding: '14px' }}>
                        <div style={{ fontSize: '9px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '10px' }}>ANAGRAFICA VEICOLO</div>
                        {[
                          ['Marca', v.marca || '—'],
                          ['Modello', v.modello || '—'],
                          ['Versione', v.versione || '—'],
                          ['Tipologia', v.tipo || '—'],
                          ['Targa', v.targa || '—'],
                          ['Telaio (VIN)', vinFinal],
                          ['Chilometri', kmFinal ? new Intl.NumberFormat('it-IT').format(parseInt(kmFinal)) + ' km' : '—'],
                          ['Immatricolazione', immatFinal],
                          ['Carburante', v.carburante || '—'],
                          ['Colore', stockV.colore || v.colore || pr.coloreVeicolo || '—'],
                        ].map(([l, v2]) => (
                          <div key={l} style={{ display: 'flex', gap: '8px', padding: '4px 0', borderBottom: `1px solid ${T.border}`, fontSize: '11px' }}>
                            <span style={{ color: T.textMuted, minWidth: '100px', flexShrink: 0 }}>{l}</span>
                            <span style={{ color: T.text, fontWeight: l === 'Telaio (VIN)' ? '900' : '600' }}>{v2}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* SERVIZI E ACCESSORI */}
                    <div style={{ marginTop: '14px', background: T.bg, borderRadius: '12px', padding: '14px' }}>
                      <div style={{ fontSize: '9px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px', marginBottom: '10px' }}>SERVIZI E ACCESSORI INCLUSI</div>
                      {servizi.length === 0 ? (
                        <div style={{ fontSize: '12px', color: T.textMuted, fontStyle: 'italic' }}>Nessun servizio aggiuntivo incluso</div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                          {servizi.map((s2, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', fontSize: '11px', fontWeight: '600', color: T.text }}>
                              <span style={{ color: '#f59e0b', fontWeight: '900' }}>✓</span> {s2}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Consegna */}
                    <div style={{ marginTop: '10px', padding: '10px 14px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: T.textMuted }}>Consegna prevista</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#6366f1' }}>
                        {c.datiExtra?.consegnaTipo && c.datiExtra.consegnaTipo !== 'personalizzata'
                          ? `Entro ${c.datiExtra.consegnaTipo} giorni lavorativi`
                          : c.datiExtra?.dataConsegna ? new Date(c.datiExtra.dataConsegna).toLocaleDateString('it-IT') : '—'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── STORICO INTERVENTI OFFICINA ─────────────────────────────────────────────
function StoricoInterventi({ T, isMobile, db }) {
  const [interventi, setInterventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Stesso principio già applicato a StoricoODLView/CruscottoServiceView
    // — storico da sfogliare, non serve aggiornamento istantaneo.
    let annullato = false;
    const now = Date.now();
    // Fatture create dopo l'ultima cache (es. da un altro PC): una query leggera
    // su creatoAt invece di riscaricare tutto il nodo
    const deltaFatture = () => {
      const lista = window._radarInterventiCache || [];
      const cutoff = lista.reduce((m, x) => (x.creatoAt && x.creatoAt > m ? x.creatoAt : m), '');
      if (!cutoff) return;
      get(query(ref(db, 'service_interventi'), orderByChild('creatoAt'), startAt(cutoff))).then(snap => {
        if (annullato || !snap.exists()) return;
        const mappa = new Map((window._radarInterventiCache || []).map(x => [x.id, x]));
        let nuove = 0;
        Object.entries(snap.val()).forEach(([id, v]) => { if (!mappa.has(v.id || id)) nuove++; mappa.set(v.id || id, { ...v, id: v.id || id }); });
        if (!nuove) return;
        const list = Array.from(mappa.values()).sort((a, b) => new Date(b.data) - new Date(a.data));
        window._radarInterventiCache = list;
        idbScrivi('interventi_cache', { list, ts: window._radarInterventiTs || Date.now() });
        setInterventi(list);
      }).catch(() => {});
    };
    const suFattura = () => setInterventi(window._radarInterventiCache || []);
    window.addEventListener('radar:fattura-creata', suFattura);
    const pulisci = () => { annullato = true; window.removeEventListener('radar:fattura-creata', suFattura); };
    if (window._radarInterventiTs && (now - window._radarInterventiTs) < 4*60*60*1000) {
      setInterventi(window._radarInterventiCache || []);
      setLoading(false);
      deltaFatture();
      return pulisci;
    }
    idbLeggi('interventi_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarInterventiCache = cache.list; window._radarInterventiTs = cache.ts;
        setInterventi(cache.list);
        setLoading(false);
        deltaFatture();
        return;
      }
      get(ref(db, 'service_interventi')).then(snap => {
        if (annullato) return;
        const data = snap.val();
        const list = data ? Object.entries(data).map(([id, v]) => ({ ...v, id: v.id || id })).sort((a,b) => new Date(b.data) - new Date(a.data)) : [];
        const ts = Date.now();
        window._radarInterventiCache = list; window._radarInterventiTs = ts;
        idbScrivi('interventi_cache', { list, ts });
        setInterventi(list);
        setLoading(false);
      });
    });
    return pulisci;
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return interventi;
    const s = search.toLowerCase();
    return interventi.filter(i =>
      i.nomeCliente?.toLowerCase().includes(s) ||
      i.targa?.toLowerCase().includes(s) ||
      i.veicolo?.marca?.toLowerCase().includes(s) ||
      i.veicolo?.modello?.toLowerCase().includes(s) ||
      i.note?.toLowerCase().includes(s)
    );
  }, [interventi, search]);

  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const fmtD = d => d ? new Date(d).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  const eliminaIntervento = async (inv) => {
    if (!window.confirm(`Eliminare questo intervento? I ricambi usati torneranno in magazzino.${inv.odlId ? `\n\nL'ODL ${inv.odlId} tornerà APERTO e potrà essere rifatturato.` : ''}`)) return;
    try {
      // Ripristina le quantità dei ricambi
      for (const r of (inv.righe || [])) {
        if (r.tipo !== 'ricambio' || !r.codice) continue;
        const key = r.codice.replace(/[./ ]/g, '_');
        const snap = await new Promise(res => {
          const unsub = onValue(ref(db, `service_ricambi/${key}/esistenza`), snap => { unsub(); res(snap); });
        });
        const esistenzaAttuale = parseFloat(snap.val() || 0);
        await set(ref(db, `service_ricambi/${key}/esistenza`), esistenzaAttuale + (parseFloat(r.qta) || 0));
      }
      // Elimina l'intervento
      await set(ref(db, `service_interventi/${inv.id}`), null);
      // L'ODL di origine torna aperto e scollegato, così si può rifatturare.
      // aggiornatoAt aggiornato: gli altri PC lo ricevono col controllo periodico
      // e da lì tolgono anche la fattura eliminata dal loro storico.
      let odlRiaperto = null;
      if (inv.odlId) {
        const ora = new Date().toISOString();
        try {
          const snapOdl = await get(ref(db, `odl/${inv.odlId}`));
          if (snapOdl.exists() && snapOdl.val().fatturaId === inv.id) {
            // Il numero della fattura eliminata resta sull'ODL: rifatturando
            // (es. per correggere la fattura) si riprende lo stesso numero
            const numPrec = inv.numeroFattura || null;
            // I ricambi sono appena rientrati in magazzino: le righe dell'ODL
            // tornano "da scaricare", così al prossimo salvataggio si
            // riscaricano (con le eventuali quantità corrette)
            const righeOdl = (snapOdl.val().righe || []).map(r => { const { scaricato, ...resto } = r || {}; return resto; });
            await update(ref(db, `odl/${inv.odlId}`), { fatturaId: null, stato: 'aperto', aggiornatoAt: ora, numeroFatturaPrecedente: numPrec, righe: righeOdl });
            odlRiaperto = { ...snapOdl.val(), id: inv.odlId, fatturaId: null, stato: 'aperto', aggiornatoAt: ora, numeroFatturaPrecedente: numPrec, righe: righeOdl };
            try { window._forzaRefreshOdl?.(); } catch (e) {}
          }
        } catch (e) { /* ODL non trovato: si elimina solo la fattura */ }
      }
      aggiornaCacheDopoEliminazioneFattura(inv.id, odlRiaperto);
    } catch(e) { alert('Errore eliminazione: ' + e.message); }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: T.textMuted }}>Caricamento...</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input placeholder="🔍 Cerca cliente, veicolo, targa, telaio..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none' }} />
        <div style={{ fontSize: '11px', color: T.textMuted }}>{filtered.length} interventi</div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
          <div style={{ fontSize: '13px', fontWeight: '700' }}>Nessun intervento registrato</div>
          <div style={{ fontSize: '11px', marginTop: '6px' }}>Gli interventi vengono salvati automaticamente quando stampi un preconto</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(inv => (
            <div key={inv.id} style={{ background: T.surface, borderRadius: '14px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              {/* Header */}
              <div onClick={() => setSelected(selected?.id === inv.id ? null : inv)}
                style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '10px' }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg + '80'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                  {inv.veicolo?.mainImg && <img src={inv.veicolo.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '48px', height: '33px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>{inv.nomeCliente || '—'}</div>
                    <div style={{ fontSize: '10px', color: T.textMuted }}>
                      {inv.veicolo ? `${inv.veicolo.marca} ${inv.veicolo.modello}` : ''} {inv.targa ? `· ${inv.targa}` : ''}
                    </div>
                    {inv.note && <div style={{ fontSize: '10px', color: T.textSub, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.note}</div>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '15px', fontWeight: '900', color: '#10b981' }}>{fmtE(inv.totaleLordo)}</div>
                  <div style={{ fontSize: '9px', color: T.textMuted }}>{fmtD(inv.data)}</div>
                  <div style={{ fontSize: '9px', color: T.textMuted }}>{inv.righe?.length || 0} voci</div>
                </div>
              </div>

              {/* Dettaglio espandibile */}
              {selected?.id === inv.id && (
                <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 16px' }}>
                  <div style={{ background: T.bg, borderRadius: '10px', overflow: 'hidden', marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 50px 80px' : '80px 1fr 60px 80px 90px', padding: '6px 10px', borderBottom: `1px solid ${T.border}` }}>
                      {(isMobile ? ['DESCRIZIONE','QTÀ','SUBTOT'] : ['CODICE','DESCRIZIONE','QTÀ','PREZZO','SUBTOT']).map(h => (
                        <div key={h} style={{ fontSize: '8px', fontWeight: '900', color: T.textMuted }}>{h}</div>
                      ))}
                    </div>
                    {(inv.righe || []).map((r, i) => {
                      const sub = (parseFloat(r.qta)||0) * (parseFloat(r.prezzoUnit)||0);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 50px 80px' : '80px 1fr 60px 80px 90px', padding: '7px 10px', borderBottom: i < inv.righe.length-1 ? `1px solid ${T.border}` : 'none', alignItems: 'center', background: i%2===0?'transparent':T.bg+'60' }}>
                          {!isMobile && <div style={{ fontSize: '9px', color: '#6366f1', fontWeight: '700' }}>{r.codice||'—'}</div>}
                          <div style={{ fontSize: '11px', color: T.text }}>{r.descrizione}</div>
                          <div style={{ fontSize: '11px', color: T.textSub }}>{r.qta}{r.tipo==='manodopera'?' h':''}</div>
                          {!isMobile && <div style={{ fontSize: '11px', color: T.textSub }}>{fmtE(r.prezzoUnit)}</div>}
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#10b981' }}>{fmtE(sub)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px' }}>
                      <span style={{ color: T.textSub }}>Imponibile: <b style={{ color: T.text }}>{fmtE(inv.totaleNetto)}</b></span>
                      <span style={{ color: T.textSub }}>IVA 22%: <b style={{ color: '#f59e0b' }}>{fmtE(inv.totaleIVA)}</b></span>
                      <span style={{ color: T.textSub }}>Totale: <b style={{ color: '#10b981' }}>{fmtE(inv.totaleLordo)}</b></span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => stampaFatturaOfficina(inv)}
                        style={{ background: 'rgba(99,102,241,0.1)', border: '2px solid #6366f1', borderRadius: '8px', color: '#6366f1', cursor: 'pointer', fontWeight: '700', fontSize: '10px', padding: '5px 12px' }}>
                        🖨️ Stampa
                      </button>
                      <button onClick={() => eliminaIntervento(inv)}
                        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef444440', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontWeight: '700', fontSize: '10px', padding: '5px 12px' }}>
                        🗑️ Elimina
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB BACK OFFICE
// ═══════════════════════════════════════════════════════════════════════════════
// ─── Export Back Office in Excel — un rigo per contratto, note incluse ──
const COLONNE_EXPORT_BACKOFFICE = [
  { gruppo: 'Contratto', colonne: ['Cliente', 'Veicolo', 'Targa', 'Venditore', 'Data Firma Contratto', 'Data Consegna Prevista'] },
  { gruppo: 'Lavorazioni', colonne: ['Stato Consegna', 'Lavorazioni Completate', 'Lavorazioni Totali'] },
  { gruppo: 'Amministrativo', colonne: ['Ha Finanziamento', 'Liquidato', 'Fatturata', 'Note Back Office'] },
];

function esportaBackOfficeExcel(contratti, getLavorazioni, colonneSelezionate, contrattiConsegnati = []) {
  if (bloccaExportExcel()) return;
  const sv = v => (v === undefined || v === null) ? '' : v.toString();
  const fmtE = n => n ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n) : '';
  const statoLabels = { da_preparare: 'Da preparare', in_lavorazione: 'In lavorazione', pronto: 'Pronto', consegnato: 'Consegnato' };
  const intestazioneLabels = { studio_gianluca: 'Immatricolazione Studio Gianluca', altro: 'Immatricolazione Altro', passaggio_proprieta: 'Passaggio di Proprietà' };
  const targheLabels = { non_disponibili: 'Non disponibili', in_sede: 'In sede', su_veicolo: 'Sul veicolo' };
  // Data/utente dell'ultima selezione di un dato campo, cercando nel log
  // — stessa logica già usata nell'interfaccia per "Selezionato il... da...".
  const ultimoLog = (c, campo) => {
    const voci = Object.values(c.backoffice_log || {}).filter(l => l.campo === campo);
    if (!voci.length) return null;
    return voci.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  };

  const headers = [
    'Cliente', 'Veicolo', 'Targa', 'Venditore',
    'Data Firma Contratto', 'Data Consegna Prevista',
    'Stato Consegna', 'Lavorazioni Completate', 'Lavorazioni Totali',
    'Ha Finanziamento', 'Liquidato', 'Fatturata',
    'Intestazione', 'Intestazione — Data', 'Intestazione — Utente',
    'DU Originale', 'Targhe',
    'Note Back Office',
  ];

  const righe = (contratti || []).map(c => {
    const bo = c.backoffice || {};
    const nc = sv(c.cliente?.ragioneSociale || `${c.cliente?.nome || ''} ${c.cliente?.cognome || ''}`.trim());
    const lavorazioni = getLavorazioni(c);
    const completate = lavorazioni.filter(l => bo[l.id]).length;
    const stato = bo.statoConsegna || 'da_preparare';
    const hasFin = !!c.prezzo?.hasFinanziamento;
    const logIntestazione = ultimoLog(c, 'intestazione');
    return [
      nc,
      `${sv(c.veicolo?.marca)} ${sv(c.veicolo?.modello)}`.trim(),
      sv(c.veicolo?.targa),
      sv(c.venditoreNome),
      c.datiExtra?.dataFirma ? new Date(c.datiExtra.dataFirma) : '',
      c.prezzo?.dataConsegna ? new Date(c.prezzo.dataConsegna) : '',
      statoLabels[stato] || stato,
      completate,
      lavorazioni.length,
      hasFin ? 'SI' : 'NO',
      hasFin ? (bo.liquidato ? 'SI' : 'NO') : '',
      bo.fatturata ? 'SI' : 'NO',
      bo.intestazione ? (intestazioneLabels[bo.intestazione] || bo.intestazione) : '',
      logIntestazione ? new Date(logIntestazione.timestamp) : '',
      logIntestazione ? sv(logIntestazione.utente) : '',
      bo.duOriginale ? 'SI' : 'NO',
      bo.targhe ? (targheLabels[bo.targhe] || bo.targhe) : 'Non disponibili',
      sv(bo.note),
    ];
  });

  let headersFinali = headers, righeFinali = righe;
  if (Array.isArray(colonneSelezionate) && colonneSelezionate.length > 0) {
    const indiciTenuti = headers.map((h, i) => colonneSelezionate.includes(h) ? i : -1).filter(i => i !== -1);
    headersFinali = indiciTenuti.map(i => headers[i]);
    righeFinali = righe.map(riga => indiciTenuti.map(i => riga[i]));
  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headersFinali, ...righeFinali], { cellDates: true });
  ws['!cols'] = headersFinali.map(() => ({ wch: 20 }));
  applicaFormatiExcel(ws, headersFinali, righeFinali.length, [], ['Data Firma Contratto', 'Data Consegna Prevista', 'Intestazione — Data']);
  XLSX.utils.book_append_sheet(wb, ws, 'Back Office');

  // ─── Foglio dedicato: Consegnate ─────────────────────────────────────
  // Auto già consegnate — non più visibili nella lista di lavoro (i dati
  // restano su Firebase, servono solo qui in caso di necessità), con
  // tutti i dati principali e la data di consegna effettiva.
  const headersConsegnate = [
    'Cliente', 'Veicolo', 'Targa', 'Venditore', 'Prezzo Vendita',
    'Data Firma Contratto', 'Data Consegna Effettiva',
    'Ha Finanziamento', 'Liquidato', 'Fatturata',
    'Intestazione', 'Intestazione — Data', 'Intestazione — Utente',
    'DU Originale', 'Targhe',
    'Note Back Office',
  ];
  const righeConsegnate = (contrattiConsegnati || []).map(c => {
    const bo = c.backoffice || {};
    const nc = sv(c.cliente?.ragioneSociale || `${c.cliente?.nome || ''} ${c.cliente?.cognome || ''}`.trim());
    const hasFin = !!c.prezzo?.hasFinanziamento;
    // Data consegna EFFETTIVA (quando è stata segnata "consegnato" per
    // davvero) — diversa da "Data Consegna Prevista" del foglio principale.
    const dataConsegnaEffettiva = bo.dataEsitoConsegna || c.vendutoAt || c.dataVendita || '';
    const logIntestazioneC = ultimoLog(c, 'intestazione');
    return [
      nc,
      `${sv(c.veicolo?.marca)} ${sv(c.veicolo?.modello)}`.trim(),
      sv(c.veicolo?.targa),
      sv(c.venditoreNome),
      parseFloat(c.prezzo?.vendita || c.prezzo?.prezzoVendita || 0) || 0,
      c.datiExtra?.dataFirma ? new Date(c.datiExtra.dataFirma) : '',
      dataConsegnaEffettiva ? new Date(dataConsegnaEffettiva) : '',
      hasFin ? 'SI' : 'NO',
      hasFin ? (bo.liquidato ? 'SI' : 'NO') : '',
      bo.fatturata ? 'SI' : 'NO',
      bo.intestazione ? (intestazioneLabels[bo.intestazione] || bo.intestazione) : '',
      logIntestazioneC ? new Date(logIntestazioneC.timestamp) : '',
      logIntestazioneC ? sv(logIntestazioneC.utente) : '',
      bo.duOriginale ? 'SI' : 'NO',
      bo.targhe ? (targheLabels[bo.targhe] || bo.targhe) : 'Non disponibili',
      sv(bo.note),
    ];
  });
  const wsConsegnate = XLSX.utils.aoa_to_sheet([headersConsegnate, ...righeConsegnate], { cellDates: true });
  wsConsegnate['!cols'] = headersConsegnate.map(() => ({ wch: 20 }));
  applicaFormatiExcel(wsConsegnate, headersConsegnate, righeConsegnate.length, [], ['Data Firma Contratto', 'Data Consegna Effettiva', 'Intestazione — Data']);
  XLSX.utils.book_append_sheet(wb, wsConsegnate, 'Consegnate');

  const oggi = new Date();
  const tag = `${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}`;
  scriviExcel(wb, `BackOffice_RadaR_${tag}.xlsx`);
}

function BackOfficeView({ user, statiContrattiLive = {} }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [fMese, setFMese] = useState('');
  const [fAnno, setFAnno] = useState('');
  const [fStato, setFStato] = useState('');
  const [subTab, setSubTab] = useState('lista'); // 'lista' | 'calendario'
  const _emailBO2 = (user?.email||'').toLowerCase();
  const _isFrontModifica2 = user.ruolo === 'front' && (_emailBO2.includes('roberta') || _emailBO2.includes('caiazzo') || _emailBO2.includes('marianna') || _emailBO2.includes('vetrano') || _emailBO2.includes('simona') || _emailBO2.includes('lagrutta'));
  const puoModificare = user.ruolo === 'back_office' || user.ruolo === 'admin' || user.ruolo === 'super_admin' || user.ruolo === 'service' || user.ruolo === 'logistica' || _isFrontModifica2;
  const puoLeggere = puoModificare || user.ruolo === 'consulente' || user.ruolo === 'brand_manager_se' || user.ruolo === 'front';

  const [mostraStoricoCompleto, setMostraStoricoCompleto] = useState(false);
  const [colonnePickerOpenBO, setColonnePickerOpenBO] = useState(false);
  // Auto consegnate: non più visibili nella lista di lavoro (i dati/log
  // restano su Firebase, servono solo più raramente per Back Office una
  // volta chiuso il ciclo) — raccolte separatamente, solo per l'export
  // Excel dedicato, non renderizzate mai a schermo.
  const [contrattiConsegnati, setContrattiConsegnati] = useState([]);

  // ─── CARICAMENTO INDIPENDENTE — non usa più contrattiGlobali (il
  // blocco condiviso da 7MB con TUTTO lo storico, comprese bozze e
  // annullati che Back Office non usa mai). Query dedicata solo su
  // approvato+venduto — più leggera, scaricata una volta e cacheata in
  // memoria per il resto della sessione. Non elimina le consegnate dal
  // download (Firebase non permette query "diverso da" su un campo
  // annidato come backoffice.statoConsegna senza un indice dedicato da
  // costruire a parte), ma toglie comunque tutto ciò che non serve mai a
  // questa vista.
  const [contrattiProp, setContrattiProp] = useState([]);
  useEffect(() => {
    const now = Date.now();
    const TTL = 4 * 60 * 60 * 1000; // 4 ore, stesso TTL del resto
    if (window._radarBackofficeTs && (now - window._radarBackofficeTs) < TTL) {
      setContrattiProp(window._radarBackofficeContratti || []);
      setLoading(false);
      return;
    }
    Promise.all([
      get(query(ref(db, 'contratti'), orderByChild('stato'), equalTo('approvato'))),
      get(query(ref(db, 'contratti'), orderByChild('stato'), equalTo('venduto'))),
    ]).then(([snapA, snapV]) => {
      const uniti = { ...(snapA.val() || {}), ...(snapV.val() || {}) };
      const lista = Object.values(uniti);
      window._radarBackofficeContratti = lista;
      window._radarBackofficeTs = now;
      setContrattiProp(lista);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const seiMesiFa = new Date(); seiMesiFa.setMonth(seiMesiFa.getMonth() - 6);
    // Applica lo stato live PRIMA di filtrare — altrimenti un contratto
    // appena approvato/venduto (dato live) ma ancora "bozza" nella cache
    // vecchia non entrerebbe nemmeno nella lista filtrata qui sotto.
    const conStatoLive = Object.keys(statiContrattiLive).length
      ? (contrattiProp || []).map(c => statiContrattiLive[c.id] ? { ...c, stato: statiContrattiLive[c.id] } : c)
      : (contrattiProp || []);
    const baseFiltrata = conStatoLive.filter(c => (c.stato === 'approvato' || c.stato === 'venduto') && !c.cliente?.b2b);
    // Le consegnate finiscono SOLO nell'elenco per l'export, mai nella
    // lista di lavoro a schermo — separazione fatta qui, prima di tutti
    // gli altri filtri, così non compaiono mai per nessun ruolo.
    setContrattiConsegnati(baseFiltrata.filter(c => c.backoffice?.statoConsegna === 'consegnato'));
    // Con "Storico completo" admin e super admin vedono anche i consegnati,
    // così una consegna segnata per errore si può riportare in lavorazione
    const vediConsegnati = mostraStoricoCompleto && ['admin', 'super_admin'].includes(user?.ruolo);
    let list = baseFiltrata
      .filter(c => vediConsegnati || c.backoffice?.statoConsegna !== 'consegnato')
      .filter(c => {
        if (mostraStoricoCompleto) return true;
        if (c.stato === 'approvato') return true; // sempre da gestire
        // venduto: mostra solo se recente o se il finanziamento non è
        // ancora liquidato — altrimenti la lista cresce all'infinito nel
        // tempo, rallentando il caricamento della pagina ogni volta.
        if (c.prezzo?.hasFinanziamento && parseFloat(c.prezzo?.importoFinanziato||0) > 0 && !c.backoffice?.liquidato) return true;
        const dataRif = new Date(c.dataVendita || c.vendutoAt || c.dataCreazione || 0);
        return dataRif >= seiMesiFa;
      })
      .sort((a, b) => { const ta = new Date(a.dataCreazione||0).getTime(), tb = new Date(b.dataCreazione||0).getTime(); return (isNaN(tb)?0:tb) - (isNaN(ta)?0:ta); });
    if (user.ruolo === 'consulente' || user.ruolo === 'brand_manager_se') {
      const fullName = `${user.nome} ${user.cognome}`.toLowerCase().trim();
      list = list.filter(c => c.venditoreNome?.toLowerCase().trim() === fullName);
    }
    // front: filtro sede automatico
    const frontSede = getFrontSedeFilter(user);
    if (frontSede) list = list.filter(c => getSede((c.venditoreNome||'').toLowerCase()) === frontSede);
    // service: vede solo i contratti della propria sede (campo user.sede)
    if (user.ruolo === 'service' && user.sede) {
      list = list.filter(c => getSede((c.venditoreNome||'').toLowerCase()) === user.sede);
    }
    setContratti(list);
    setLoading(false);
  }, [contrattiProp, user, mostraStoricoCompleto, statiContrattiLive]);

  const getLavorazioni = (c) => {
    const s = c.servizi || {};
    const pr = c.prezzo || {};
    const lav = [];
    if (s.lojack) lav.push({ id: 'lojack', label: 'Installazione LoJack', icon: '🔴' });
    if (s.mawdy36||s.mawdy) lav.push({ id: 'mawdy36', label: `Ext Garanzia Mawdy ${s.garanziaEstesaMesi || 36} mesi`, icon: '🛡️' });
    if (s.estinzioneAnticipata || pr.estinzioneAnticipata) lav.push({ id: 'estinzione', label: 'Estinzione anticipata fin.', icon: '💰' });
    if (s.blockshaft)        lav.push({ id: 'blockshaft',    label: 'Installazione Blockshaft',    icon: '🔒' });
    if (s.gpl)               lav.push({ id: 'gpl',           label: 'Installazione GPL',           icon: '⛽' });
    if (s.kitAutocarro)      lav.push({ id: 'kitAutocarro',  label: 'Kit Autocarro',               icon: '🚛' });
    if (s.sensoriParcheggio) lav.push({ id: 'sensori',       label: 'Sensori Parcheggio',          icon: '📡' });
    if (s.retrocamera)       lav.push({ id: 'retrocamera',   label: 'Retrocamera',                 icon: '📷' });
    if (s.bracciolo)         lav.push({ id: 'bracciolo',     label: 'Bracciolo',                   icon: '🪑' });
    if (s.vetriPrivacy)      lav.push({ id: 'vetri',         label: 'Vetri Privacy',               icon: '🪟' });
    if (s.tagliando)         lav.push({ id: 'tagliando',     label: 'Tagliando',                   icon: '🔧' });
    if (s.revisione)         lav.push({ id: 'revisione',     label: 'Revisione',                   icon: '✅' });
    if (s.secondaChiave)     lav.push({ id: 'chiave',        label: '2a Chiave',                   icon: '🗝️' });
    if (s.altroAntifurto)    lav.push({ id: 'altroAnt',      label: s.altroAntifurto,              icon: '🔐' });
    if (s.altroAccessorio)   lav.push({ id: 'altroAcc',      label: s.altroAccessorio,             icon: '⚙️' });
    if (s.altroInstallazione)lav.push({ id: 'altroInst',     label: s.altroInstallazione,          icon: '🛠️' });
    if (s.lavorazioniCarrozzeria) lav.push({ id: 'carrozzeria', label: 'Carrozzeria: ' + s.lavorazioniCarrozzeria, icon: '🚗' });
    // Sempre presenti
    lav.push({ id: 'lavorazioneInviata', label: 'Lavorazione Inviata', icon: '📤' });
    lav.push({ id: 'pulizia',  label: 'Pulizia e preparazione', icon: '🧹' });
    lav.push({ id: 'consegna', label: 'Pronto per consegna',     icon: '🚀' });
    lav.push({ id: 'fatturata', label: 'Fatturata',              icon: '🧾' });
    return lav;
  };

  const aggiornaStato = async (contrattoId, campo, valore) => {
    if (!puoModificare) return; // consulente non può flaggare
    await set(ref(db, `contratti/${contrattoId}/backoffice/${campo}`), valore);
    await set(ref(db, `contratti/${contrattoId}/dataModifica`), new Date().toISOString());
    // Log azione
    if (valore !== null && valore !== false && valore !== '') {
      const logEntry = {
        campo,
        valore,
        utente: `${user.nome} ${user.cognome}`,
        ruolo: user.ruolo,
        timestamp: new Date().toISOString(),
      };
      const logKey = `${campo}_${Date.now()}`;
      await set(ref(db, `contratti/${contrattoId}/backoffice_log/${logKey}`), logEntry);
    }
    // Aggiornamento locale mirato (ottimistico): invece di forzare un
    // ridownload completo di contrattiGlobali (7MB) solo per vedere un
    // singolo flag, aggiorniamo SUBITO il campo cambiato nello stato
    // locale della lista — zero banda aggiuntiva, l'utente vede il flag
    // istantaneamente. Il dato "vero" arriverà comunque da Firebase alla
    // prossima sincronizzazione naturale (max 4 ore), ma qui l'utente
    // vede già il valore corretto perché l'ha appena scritto lui stesso.
    setContratti(prev => prev.map(c =>
      c.id === contrattoId ? { ...c, backoffice: { ...c.backoffice, [campo]: valore } } : c
    ));
  };

  const mesiBO = [
    ['1','Gennaio'],['2','Febbraio'],['3','Marzo'],['4','Aprile'],
    ['5','Maggio'],['6','Giugno'],['7','Luglio'],['8','Agosto'],
    ['9','Settembre'],['10','Ottobre'],['11','Novembre'],['12','Dicembre'],
  ];
  const anniBO = useMemo(() => {
    const set = new Set(contratti.map(c => new Date(c.datiExtra?.dataFirma || c.dataCreazione || '').getFullYear()).filter(Boolean));
    return [...set].sort((a, b) => b - a);
  }, [contratti]);

  const filtered = useMemo(() => {
    return contratti.filter(c => {
      const d = new Date(c.datiExtra?.dataFirma || c.dataCreazione || '');
      if (fAnno && d.getFullYear() !== parseInt(fAnno)) return false;
      if (fMese && d.getMonth() + 1 !== parseInt(fMese)) return false;
      if (fStato && (c.backoffice?.statoConsegna || 'da_preparare') !== fStato) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const nc = `${c.cliente?.nome||''} ${c.cliente?.cognome||''} ${c.cliente?.ragioneSociale||''}`.toLowerCase();
        const nv = `${c.veicolo?.marca||''} ${c.veicolo?.modello||''} ${c.veicolo?.targa||''} ${c.veicolo?.vin||''} ${c.datiExtra?.numeroContratto||''}`.toLowerCase();
        if (!nc.includes(s) && !nv.includes(s) && !(c.venditoreNome||'').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [contratti, fAnno, fMese, fStato, search]);

  const statoStats = useMemo(() => {
    const base = { da_preparare: 0, in_lavorazione: 0, pronto: 0, consegnato: 0 };
    contratti.forEach(c => {
      const st = c.backoffice?.statoConsegna || 'da_preparare';
      if (base[st] !== undefined) base[st]++;
    });
    return base;
  }, [contratti]);

  if (loading) return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text }}>Caricamento...</div>;

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '19px' : '22px', fontWeight: '900', color: T.text, letterSpacing: '-0.5px' }}>🔧 Back Office</h2>
          <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>{filtered.length} contratt{filtered.length===1?'o':'i'} da gestire</div>
          {!puoModificare && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px', fontWeight: '700' }}>👁 Solo visualizzazione</div>}
        </div>
        {/* FILTRI */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
          <input placeholder="🔍 Cerca cliente, veicolo, targa..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '10px 12px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: isMobile ? '14px' : '12px', outline: 'none', flex: isMobile ? '1 1 100%' : 'none', minWidth: isMobile ? '0' : '180px', boxSizing: 'border-box' }} />
          <select value={fMese} onChange={e => setFMese(e.target.value)}
            style={{ padding: '10px', background: T.inputBg, border: `1px solid ${fMese ? '#8b5cf6' : T.border}`, borderRadius: '10px', color: T.text, fontSize: isMobile ? '13px' : '12px', outline: 'none', cursor: 'pointer', flex: isMobile ? '1 1 auto' : 'none' }}>
            <option value="">Tutti i mesi</option>
            {mesiBO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={fAnno} onChange={e => setFAnno(e.target.value)}
            style={{ padding: '10px', background: T.inputBg, border: `1px solid ${fAnno ? '#8b5cf6' : T.border}`, borderRadius: '10px', color: T.text, fontSize: isMobile ? '13px' : '12px', outline: 'none', cursor: 'pointer', flex: isMobile ? '1 1 auto' : 'none' }}>
            <option value="">Tutti gli anni</option>
            {anniBO.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {puoEsportareExcel(user) && (
          <button onClick={() => setColonnePickerOpenBO(true)}
            style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: isMobile ? '13px' : '12px', whiteSpace: 'nowrap', flex: isMobile ? '1 1 auto' : 'none' }}>
            📊 Esporta Excel {filtered.length ? `(${filtered.length})` : ''}
          </button>
          )}
          <button onClick={() => setMostraStoricoCompleto(o => !o)}
            title="Di default nascondiamo i venduti più vecchi di 6 mesi già liquidati, per velocizzare il caricamento"
            style={{ padding: '10px 14px', borderRadius: '10px', border: `1px solid ${mostraStoricoCompleto ? '#8b5cf6' : T.border}`, background: mostraStoricoCompleto ? 'rgba(139,92,246,0.1)' : 'transparent', color: mostraStoricoCompleto ? '#8b5cf6' : T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: isMobile ? '13px' : '12px', whiteSpace: 'nowrap', flex: isMobile ? '1 1 auto' : 'none' }}>
            🕐 {mostraStoricoCompleto ? 'Storico completo' : 'Solo recenti'}
          </button>
          {(fMese || fAnno || fStato || search) && (
            <button onClick={() => { setFMese(''); setFAnno(''); setFStato(''); setSearch(''); }}
              style={{ padding: '10px 14px', background: 'none', border: `1px solid ${T.border}`, borderRadius: '10px', color: '#ef4444', fontSize: '12px', cursor: 'pointer', fontWeight: '700', flex: isMobile ? '1 1 auto' : 'none' }}>
              × Reset
            </button>
          )}
        </div>
      </div>

      {/* TOGGLE Lista / Calendario Consegne */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', background: T.surface, borderRadius: '12px', padding: '4px', width: 'fit-content', border: `1px solid ${T.border}` }}>
        <button onClick={() => setSubTab('lista')}
          style={{ padding: '9px 18px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontWeight: '900', fontSize: '12px', background: subTab === 'lista' ? '#8b5cf6' : 'transparent', color: subTab === 'lista' ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
          🔧 Lista Lavorazioni
        </button>
        <button onClick={() => setSubTab('calendario')}
          style={{ padding: '9px 18px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontWeight: '900', fontSize: '12px', background: subTab === 'calendario' ? '#8b5cf6' : 'transparent', color: subTab === 'calendario' ? '#fff' : T.textSub, whiteSpace: 'nowrap' }}>
          📅 Calendario Consegne
        </button>
      </div>

      {subTab === 'calendario' ? (
        <CalendarioConsegneView user={user} contratti={contratti} puoModificare={puoModificare} db={db} T={T} isMobile={isMobile} />
      ) : (
      <>

      {/* BARRA STATISTICHE per stato — cliccabile per filtrare, scroll orizzontale su mobile */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', overflowX: isMobile ? 'auto' : 'visible', paddingBottom: isMobile ? '4px' : '0', WebkitOverflowScrolling: 'touch' }}>
        {[
          ['', '📋', 'Tutti', contratti.length, T.textSub],
          ['da_preparare', '⏳', 'Da preparare', statoStats.da_preparare, '#f59e0b'],
          ['in_lavorazione', '🔧', 'In lavorazione', statoStats.in_lavorazione, '#3b82f6'],
          ['pronto', '✅', 'Pronto', statoStats.pronto, '#10b981'],
        ].map(([val, icon, label, count, color]) => (
          <button key={val || 'tutti'} onClick={() => setFStato(fStato === val ? '' : val)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: isMobile ? '10px 12px' : '10px 16px',
              background: fStato === val ? `${color}22` : T.surface, border: `1.5px solid ${fStato === val ? color : T.border}`,
              borderRadius: '14px', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
            }}>
            <span style={{ fontSize: isMobile ? '16px' : '18px' }}>{icon}</span>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '900', color: fStato === val ? color : T.text, lineHeight: 1 }}>{count}</div>
              <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '700', whiteSpace: 'nowrap' }}>{label}</div>
            </div>
          </button>
        ))}
      </div>

      {(user.ruolo === 'consulente' || user.ruolo === 'brand_manager_se') && (
        <div style={{background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:'12px',padding:'10px 16px',marginBottom:'16px',fontSize:'11px',color:'#6366f1',fontWeight:'700'}}>
          👁 Modalità sola lettura — puoi vedere le lavorazioni sui tuoi contratti ma non puoi modificarle
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔧</div>
          <div style={{ fontSize: '14px', fontWeight: '700' }}>Nessun contratto approvato da gestire</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filtered.map((c, i) => {
            const nc = c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim() || 'Cliente';
            const lavorazioni = getLavorazioni(c);
            const bo = c.backoffice || {};
            const completate = lavorazioni.filter(l => bo[l.id]).length;
            const percento = Math.round((completate / lavorazioni.length) * 100);
            const statoConsegna = bo.statoConsegna || 'da_preparare';
            const statoColors = { da_preparare: '#f59e0b', in_lavorazione: '#3b82f6', pronto: '#10b981', consegnato: '#8b5cf6' };
            const statoLabels = { da_preparare: '⏳ Da preparare', in_lavorazione: '🔧 In lavorazione', pronto: '✅ Pronto', consegnato: '🚀 Consegnato' };
            // Saldo — stessa identica formula di ContrattoDetailModal, così
            // il numero mostrato qui coincide sempre con quello "ufficiale"
            // del dettaglio contratto, senza rischio di disallineamento.
            const pr = c.prezzo || {};
            const saldoBO = calcolaSaldoContratto(c);

            return (
              <div key={c.id||i} style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, borderLeft: `4px solid ${statoColors[statoConsegna]}`, overflow: 'hidden' }}>
                {/* Header card */}
                <div style={{ padding: isMobile ? '14px 16px' : '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '12px' : '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {c.veicolo?.mainImg && <img src={c.veicolo.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: isMobile ? '52px' : '60px', height: isMobile ? '38px' : '42px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>{nc}</div>
                      <div style={{ fontSize: '11px', color: T.textSub }}>{c.veicolo?.marca} {c.veicolo?.modello} · {c.veicolo?.targa || 'N.D.'}</div>
                      <div style={{ fontSize: '10px', color: T.textMuted, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span>📅 Consegna prevista:</span>
                        <input type="date" disabled={!puoModificare}
                          defaultValue={(c.backoffice?.dataConsegnaPrevista || c.prezzo?.dataConsegna) ? (c.backoffice?.dataConsegnaPrevista || c.prezzo?.dataConsegna).slice(0, 10) : ''}
                          onChange={e => aggiornaStato(c.id, 'dataConsegnaPrevista', e.target.value || null)}
                          style={{ padding: '3px 6px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '6px', color: T.text, fontSize: '10px', outline: 'none' }} />
                        <input type="time" disabled={!puoModificare}
                          defaultValue={c.backoffice?.oraConsegnaPrevista || ''}
                          onChange={e => aggiornaStato(c.id, 'oraConsegnaPrevista', e.target.value || null)}
                          style={{ padding: '3px 6px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '6px', color: T.text, fontSize: '10px', outline: 'none' }} />
                        <span>· {c.venditoreNome}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
                    {/* Barra progresso */}
                    <div style={{ flex: isMobile ? '1 1 100%' : 'none', order: isMobile ? -1 : 0 }}>
                      <div style={{ fontSize: '9px', color: T.textMuted, marginBottom: '3px', textAlign: isMobile ? 'left' : 'right' }}>{completate}/{lavorazioni.length} lavorazioni completate</div>
                      <div style={{ width: isMobile ? '100%' : '120px', height: '7px', background: T.chartBg, borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${percento}%`, background: percento === 100 ? '#10b981' : '#3b82f6', borderRadius: '4px', transition: 'width 0.4s' }} />
                      </div>
                    </div>
                    {/* Liquidato — solo per contratti con finanziamento */}
                    {c.prezzo?.hasFinanziamento && parseFloat(c.prezzo?.importoFinanziato||0) > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: bo.liquidato ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.08)', border: `2px solid ${bo.liquidato ? '#10b981' : '#f59e0b'}`, borderRadius: '10px', cursor: puoModificare ? 'pointer' : 'default', flexShrink: 0, flex: isMobile ? '1 1 auto' : 'none' }}>
                        <input type="checkbox" checked={!!bo.liquidato} disabled={!puoModificare}
                          onChange={e => aggiornaStato(c.id, 'liquidato', e.target.checked || null)}
                          style={{ accentColor: '#10b981', width: '16px', height: '16px', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '900', color: bo.liquidato ? '#10b981' : '#f59e0b' }}>💰 LIQUIDATO</div>
                          <div style={{ fontSize: '9px', color: T.textMuted }}>{c.prezzo?.finanziariaSocieta || 'Finanziamento'} · {c.prezzo?.importoFinanziato ? new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(c.prezzo.importoFinanziato) : '—'}</div>
                        </div>
                      </label>
                    )}
                    {puoModificare ? (
                      <select value={statoConsegna} onChange={async e => {
                        const nuovoStato = e.target.value;
                        await aggiornaStato(c.id, 'statoConsegna', nuovoStato);
                        // Se consegnato → marca anche il contratto come venduto (solo B2C)
                        if (nuovoStato === 'consegnato' && c.stato !== 'venduto' && !c.cliente?.b2b) {
                          const oggi = new Date().toISOString();
                          await set(ref(db, `contratti/${c.id}/stato`), 'venduto');
                          await set(ref(db, `contratti/${c.id}/vendutoAt`), oggi);
                          await set(ref(db, `contratti/${c.id}/dataVendita`), oggi);
                          await set(ref(db, `contratti/${c.id}/dataModifica`), oggi);
                          await set(ref(db, `stati_contratti/${c.id}`), 'venduto');
                          // Segna il veicolo come VENDUTA in stock — regola
                          // fissa: un contratto consegnato significa SEMPRE
                          // veicolo venduto, indipendentemente da quale
                          // punto dell'app lo marca come consegnato.
                          const normTargaSC = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
                          const vinSC = normTargaSC(c.veicolo?.vin);
                          const targaSC = normTargaSC(c.veicolo?.targa);
                          const stockSnapSC = await get(ref(db, 'stock_data/vehicles'));
                          if (stockSnapSC.exists()) {
                            const rawSC = stockSnapSC.val();
                            const vsSC = Array.isArray(rawSC) ? rawSC : Object.values(rawSC);
                            const idxSC = vsSC.findIndex(v => (vinSC && normTargaSC(v.vin) === vinSC) || (targaSC && normTargaSC(v.targa) === targaSC));
                            if (idxSC > -1) {
                              await set(ref(db, `stock_data/vehicles/${idxSC}/status`), 'VENDUTA');
                              await set(ref(db, `stock_data/vehicles/${idxSC}/vendutoAt`), oggi);
                              await set(ref(db, `stati_veicoli/${vsSC[idxSC].id}/status`), 'VENDUTA');
                              window._radarStockCache = null; window._radarStockTs = 0;
                            }
                          }
                        }
                        // Se torna da consegnato → rimette approvato (solo B2C)
                        if (nuovoStato !== 'consegnato' && c.stato === 'venduto' && c.backoffice?.statoConsegna === 'consegnato' && !c.cliente?.b2b) {
                          await set(ref(db, `contratti/${c.id}/stato`), 'approvato');
                          await set(ref(db, `contratti/${c.id}/vendutoAt`), null);
                          await set(ref(db, `contratti/${c.id}/dataModifica`), new Date().toISOString());
                          await set(ref(db, `stati_contratti/${c.id}`), 'approvato');
                          // L'auto non è più venduta: torna CONTRATTUALIZZATA in stock
                          const nomeCli = c.cliente?.ragioneSociale || `${c.cliente?.cognome || ''} ${c.cliente?.nome || ''}`.trim();
                          await impostaVeicoloInStock(c.veicolo, { status: 'CONTRATTUALIZZATA', vendutoAt: null, consulenteNome: c.venditoreNome || '', clienteNome: nomeCli });
                        }
                      }}
                        style={{ padding: '9px 12px', background: T.inputBg, border: `1.5px solid ${statoColors[statoConsegna]}`, borderRadius: '9px', color: statoColors[statoConsegna], fontSize: isMobile ? '12px' : '11px', fontWeight: '900', cursor: 'pointer', outline: 'none', flex: isMobile ? '1 1 auto' : 'none' }}>
                        <option value="da_preparare">⏳ Da preparare</option>
                        <option value="in_lavorazione">🔧 In lavorazione</option>
                        <option value="pronto">✅ Pronto</option>
                        <option value="consegnato">🚀 Consegnato</option>
                      </select>
                    ) : (
                      <div style={{ padding: '9px 12px', background: T.inputBg, border: `1.5px solid ${statoColors[statoConsegna]}`, borderRadius: '9px', color: statoColors[statoConsegna], fontSize: '11px', fontWeight: '900' }}>
                        {statoLabels[statoConsegna]}
                      </div>
                    )}
                  </div>
                </div>

                {/* Lavorazioni */}
                <div style={{ padding: isMobile ? '14px 16px' : '16px 20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                    {lavorazioni.map(lav => (
                      <label key={lav.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: bo[lav.id] ? 'rgba(16,185,129,0.1)' : T.bg, border: `1px solid ${bo[lav.id] ? '#10b981' : T.border}`, borderRadius: '10px', cursor: puoModificare ? 'pointer' : 'default', transition: 'all 0.15s' }}>
                        <input type="checkbox" checked={!!bo[lav.id]} disabled={!puoModificare}
                          onChange={e => aggiornaStato(c.id, lav.id, e.target.checked || null)}
                          style={{ accentColor: '#10b981', width: '16px', height: '16px', flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', fontWeight: '600', color: bo[lav.id] ? '#10b981' : T.textSub }}>{lav.icon} {lav.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Intestazione Pratica */}
                  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '8px' }}>📋 INTESTAZIONE PRATICA</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {[
                        { val: 'studio_gianluca', label: 'Immatricolazione Studio Gianluca' },
                        { val: 'altro', label: 'Immatricolazione Altro' },
                        { val: 'passaggio_proprieta', label: 'Passaggio di Proprietà' },
                      ].map(opt => (
                        <button key={opt.val} disabled={!puoModificare}
                          onClick={() => aggiornaStato(c.id, 'intestazione', bo.intestazione === opt.val ? null : opt.val)}
                          style={{ padding: '8px 12px', borderRadius: '10px', border: `1px solid ${bo.intestazione === opt.val ? '#6366f1' : T.border}`, background: bo.intestazione === opt.val ? 'rgba(99,102,241,0.12)' : T.bg, color: bo.intestazione === opt.val ? '#6366f1' : T.textSub, fontWeight: '700', fontSize: '11px', cursor: puoModificare ? 'pointer' : 'default' }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {bo.intestazione && (() => {
                      const logIntestazione = Object.values(c.backoffice_log || {})
                        .filter(l => l.campo === 'intestazione')
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                      return logIntestazione ? (
                        <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '10px' }}>
                          Selezionato il {new Date(logIntestazione.timestamp).toLocaleDateString('it-IT')} da {logIntestazione.utente || '—'}
                        </div>
                      ) : null;
                    })()}

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: bo.duOriginale ? 'rgba(16,185,129,0.1)' : T.bg, border: `1px solid ${bo.duOriginale ? '#10b981' : T.border}`, borderRadius: '10px', cursor: puoModificare ? 'pointer' : 'default' }}>
                        <input type="checkbox" checked={!!bo.duOriginale} disabled={!puoModificare}
                          onChange={e => aggiornaStato(c.id, 'duOriginale', e.target.checked || null)}
                          style={{ accentColor: '#10b981', width: '16px', height: '16px', flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', fontWeight: '600', color: bo.duOriginale ? '#10b981' : T.textSub }}>📄 DU originale disponibile</span>
                      </label>
                      <div style={{ padding: '10px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px' }}>
                        <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '4px' }}>🏷️ Targhe</div>
                        <select disabled={!puoModificare} value={bo.targhe || 'non_disponibili'}
                          onChange={e => aggiornaStato(c.id, 'targhe', e.target.value)}
                          style={{ width: '100%', padding: '4px', background: T.inputBg, border: 'none', color: T.text, fontSize: '12px', fontWeight: '700', outline: 'none' }}>
                          <option value="non_disponibili">Non disponibili</option>
                          <option value="in_sede">Disponibili — in sede</option>
                          <option value="su_veicolo">Disponibili — sul veicolo</option>
                        </select>
                      </div>
                    </div>

                    {/* Dati automatici — non modificabili qui, calcolati dal contratto */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                      <div style={{ padding: '10px 12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: '10px', color: T.textMuted }}>💰 Importo ancora da saldare</div>
                        <input
                          key={`saldo-${c.id}-${haSaldoManuale(bo) ? bo.saldoManuale : saldoBO}`}
                          defaultValue={testoSaldoContratto(c)}
                          disabled={!puoModificare}
                          onBlur={e => aggiornaStato(c.id, 'saldoManuale', e.target.value)}
                          style={{ width: '100%', padding: '2px 0', background: 'transparent', border: 'none', borderBottom: `1px dashed ${T.border2||T.border}`, color: saldoBO > 0 ? '#ef4444' : '#10b981', fontSize: '15px', fontWeight: '900', outline: 'none', opacity: puoModificare ? 1 : 0.7 }}
                        />
                      </div>
                      <div style={{ padding: '10px 12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: '10px', color: T.textMuted }}>🔄 Permuta</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: pr.hasPermuta ? '#f59e0b' : T.textSub }}>
                          {pr.hasPermuta ? `Sì — ${pr.permutaMarca||''} ${pr.permutaModello||''}`.trim() : 'Nessuna'}
                        </div>
                        {pr.hasPermuta && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: puoModificare ? 'pointer' : 'default' }}>
                            <input type="checkbox" checked={!!bo.permutaRitirata} disabled={!puoModificare}
                              onChange={e => aggiornaStato(c.id, 'permutaRitirata', e.target.checked)}
                              style={{ accentColor: '#10b981' }} />
                            <span style={{ fontSize: '11px', fontWeight: '700', color: bo.permutaRitirata ? '#10b981' : '#f59e0b' }}>
                              {bo.permutaRitirata ? '✓ Ritirata' : 'Da ritirare'}
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Note back office */}
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, letterSpacing: '0.5px', marginBottom: '5px' }}>📝 NOTE BACK OFFICE</div>
                    <textarea
                      placeholder="Aggiungi una nota..."
                      defaultValue={bo.note || ''}
                      disabled={!puoModificare}
                      onBlur={e => aggiornaStato(c.id, 'note', e.target.value)}
                      rows={2}
                      style={{ width: '100%', padding: '10px 12px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', opacity: puoModificare ? 1 : 0.7 }}
                    />
                  </div>
                  {/* Log azioni */}
                  {c.backoffice_log && Object.values(c.backoffice_log).length > 0 && (
                    <div style={{ marginTop: '12px', borderTop: `1px solid ${T.border}`, paddingTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ fontSize: '9px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px' }}>📋 LOG AZIONI</div>
                        {(user.ruolo === 'admin' || user.ruolo === 'super_admin') && (
                          <button onClick={async () => { if (window.confirm('Eliminare il log di questo contratto?')) { await set(ref(db, `contratti/${c.id}/backoffice_log`), null); } }}
                            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '10px', cursor: 'pointer', fontWeight: '700', padding: '2px 6px' }}>
                            🗑️ Elimina log
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '120px', overflowY: 'auto' }}>
                        {Object.values(c.backoffice_log)
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                          .map((log, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '10px', padding: '4px 8px', background: T.bg, borderRadius: '6px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                              <span style={{ color: '#10b981', fontWeight: '700', flexShrink: 0 }}>✓</span>
                              <span style={{ color: T.text, fontWeight: '600' }}>{log.utente}</span>
                              <span style={{ color: T.textMuted }}>→</span>
                              <span style={{ color: T.textSub, flex: 1 }}>{log.campo === 'statoConsegna' ? `Stato: ${log.valore}` : log.campo}</span>
                              <span style={{ color: T.textMuted, flexShrink: 0 }}>{new Date(log.timestamp).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
      {colonnePickerOpenBO && (
        <ColonneExportModal
          titolo="Esporta Back Office"
          colonneDisponibili={COLONNE_EXPORT_BACKOFFICE}
          onClose={() => setColonnePickerOpenBO(false)}
          onEsporta={async (colonneScelte) => {
            esportaBackOfficeExcel(filtered, getLavorazioni, colonneScelte, contrattiConsegnati);
            setColonnePickerOpenBO(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Calendario Consegne — pianificazione + esito (Consegnata / Fallita) ───
function CalendarioConsegneView({ contratti, puoModificare, db, T, isMobile, user }) {
  // Costruisce una stringa YYYY-MM-DD dai componenti LOCALI della data — MAI
  // usare toISOString() qui: converte in UTC, e con l'ora legale italiana
  // (UTC+2 in estate) la mezzanotte locale di un giorno diventa le 22:00 UTC
  // del giorno PRIMA, sfasando di un giorno sia l'etichetta "oggi" sia
  // l'abbinamento delle consegne alla colonna giusta.
  const ymdLocale = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const oggi = new Date();
  const oggiYMD = ymdLocale(oggi);
  const [inizioFinestra, setInizioFinestra] = useState(new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate()));
  const [notaFallitaFor, setNotaFallitaFor] = useState(null);
  const [testoNota, setTestoNota] = useState('');
  const [salvando, setSalvando] = useState(null);
  const [dettaglioFor, setDettaglioFor] = useState(null);

  const GIORNI_SETT = ['Dom','Lun','Mar','Mer','Gio','Ven','Sab'];
  const N_GIORNI = isMobile ? 5 : 10; // quanti giorni mostrare affiancati

  const handleConsegnata = async (c) => {
    const nomeCliente = c.cliente?.ragioneSociale || `${c.cliente?.nome || ''} ${c.cliente?.cognome || ''}`.trim();
    if (!window.confirm(`Confermare la consegna di ${c.veicolo?.marca} ${c.veicolo?.modello} a ${nomeCliente}?\n\nIl contratto passerà a "venduto" e da questo momento la provvigione farà riferimento a questa data. Azione non reversibile da qui.`)) return;
    setSalvando(c.id);
    try {
      const oggiIso = new Date().toISOString();
      await set(ref(db, `contratti/${c.id}/stato`), 'venduto');
      await set(ref(db, `contratti/${c.id}/vendutoAt`), oggiIso);
      await set(ref(db, `contratti/${c.id}/dataVendita`), oggiIso);
      await set(ref(db, `contratti/${c.id}/backoffice/statoConsegna`), 'consegnato');
        await logBackoffice(c.id, 'statoConsegna', 'consegnato', user);
      await set(ref(db, `contratti/${c.id}/backoffice/esitoConsegna`), 'consegnata');
      await set(ref(db, `contratti/${c.id}/backoffice/dataEsitoConsegna`), oggiIso);
      await set(ref(db, `contratti/${c.id}/dataModifica`), oggiIso);
      await set(ref(db, `stati_contratti/${c.id}`), 'venduto');
      // Segna il veicolo come VENDUTA in stock — questo passaggio mancava
      // qui (il Calendario Consegne segnava "consegnato" il contratto ma
      // non toccava affatto lo stock), causando veicoli con contratto
      // consegnato ma ancora "preventivabili" in Stock. Confronto
      // normalizzato (maiuscolo, senza spazi) per non fallire per piccole
      // differenze di formato tra targa/VIN salvati nel contratto e quelli
      // in stock.
      const normTarga = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
      const vinC = normTarga(c.veicolo?.vin);
      const targaC = normTarga(c.veicolo?.targa);
      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (stockSnap.exists()) {
        const raw = stockSnap.val();
        const vs = Array.isArray(raw) ? raw : Object.values(raw);
        const idx = vs.findIndex(v => (vinC && normTarga(v.vin) === vinC) || (targaC && normTarga(v.targa) === targaC));
        if (idx > -1) {
          await set(ref(db, `stock_data/vehicles/${idx}/status`), 'VENDUTA');
          await set(ref(db, `stock_data/vehicles/${idx}/vendutoAt`), oggiIso);
          await set(ref(db, `stati_veicoli/${vs[idx].id}/status`), 'VENDUTA');
          window._radarStockCache = null; window._radarStockTs = 0;
        }
      }
      setDettaglioFor(null);
    } catch (e) {
      alert('Errore: ' + e.message);
    }
    setSalvando(null);
  };

  const handleSalvaFallita = async () => {
    if (!testoNota.trim()) { alert('Indica il motivo per cui la consegna non è andata a buon fine.'); return; }
    const c = notaFallitaFor;
    setSalvando(c.id);
    try {
      const oggiIso = new Date().toISOString();
      await set(ref(db, `contratti/${c.id}/backoffice/esitoConsegna`), 'fallita');
      await logBackoffice(c.id, 'esitoConsegna', 'fallita', user);
      await set(ref(db, `contratti/${c.id}/backoffice/noteConsegnaFallita`), testoNota.trim());
      await set(ref(db, `contratti/${c.id}/backoffice/dataEsitoConsegna`), oggiIso);
      await set(ref(db, `contratti/${c.id}/dataModifica`), oggiIso);
      setNotaFallitaFor(null);
      setTestoNota('');
      setDettaglioFor(null);
    } catch (e) {
      alert('Errore: ' + e.message);
    }
    setSalvando(null);
  };

  // Solo visualizzazione: la data/ora si scelgono nella lista principale del
  // Back Office — qui si legge soltanto, con fallback automatico alla data
  // originale del contratto se il Back Office non l'ha ancora corretta.
  const [filtroSedeCal, setFiltroSedeCal] = useState('tutte');

  const { colonne, nonPianificate } = useMemo(() => {
    const cols = [];
    for (let i = 0; i < N_GIORNI; i++) {
      const d = new Date(inizioFinestra);
      d.setDate(d.getDate() + i);
      cols.push({ data: d, ymd: ymdLocale(d), consegne: [] });
    }
    const colByYmd = {};
    cols.forEach(c => { colByYmd[c.ymd] = c; });

    // Filtro per sede — le 3 sedi (Agnano/Nola/Salerno) derivate dal nome
    // del venditore, stesso criterio già usato altrove nell'app.
    const contrattiFiltratiSede = filtroSedeCal === 'tutte'
      ? contratti
      : contratti.filter(c => getSede((c.venditoreNome || '').toLowerCase()) === filtroSedeCal);

    const nonPianificateList = [];
    contrattiFiltratiSede.forEach(c => {
      const dp = c.backoffice?.dataConsegnaPrevista || c.prezzo?.dataConsegna;
      if (!dp) { if (!c.backoffice?.esitoConsegna) nonPianificateList.push(c); return; }
      const ymd = dp.slice(0, 10);
      if (colByYmd[ymd]) colByYmd[ymd].consegne.push(c);
      // se la data è fuori dalla finestra visibile, semplicemente non compare finché non si naviga fin lì
    });

    cols.forEach(c => c.consegne.sort((a, b) => (a.backoffice?.oraConsegnaPrevista || '99:99').localeCompare(b.backoffice?.oraConsegnaPrevista || '99:99')));

    return { colonne: cols, nonPianificate: nonPianificateList };
  }, [contratti, inizioFinestra, N_GIORNI, filtroSedeCal]);

  const sposta = (giorni) => {
    const d = new Date(inizioFinestra);
    d.setDate(d.getDate() + giorni);
    setInizioFinestra(d);
  };

  const CardConsegna = ({ c }) => {
    const ora = c.backoffice?.oraConsegnaPrevista;
    const esito = c.backoffice?.esitoConsegna;
    const colore = esito === 'consegnata' ? '#10b981' : esito === 'fallita' ? '#ef4444' : '#8b5cf6';
    return (
      <div onClick={() => setDettaglioFor(c)}
        style={{ background: `${colore}15`, border: `1px solid ${colore}40`, borderRadius: '7px', padding: '6px 8px', marginBottom: '5px', cursor: 'pointer', fontSize: '11px' }}>
        {ora && <div style={{ fontSize: '9px', fontWeight: '900', color: colore, marginBottom: '2px' }}>🕐 {ora}</div>}
        <div style={{ fontWeight: '800', color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {esito === 'consegnata' ? '✅ ' : esito === 'fallita' ? '❌ ' : ''}{c.veicolo?.marca} {c.veicolo?.modello}
        </div>
        <div style={{ color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px' }}>
          {c.cliente?.ragioneSociale || `${c.cliente?.nome || ''} ${c.cliente?.cognome || ''}`.trim()}
        </div>
      </div>
    );
  };

  const larghezzaColonna = isMobile ? '130px' : '160px';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => sposta(-N_GIORNI)} style={{ padding: '8px 12px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, cursor: 'pointer', fontWeight: '900' }}>‹</button>
          <div style={{ fontSize: '13px', fontWeight: '900', color: T.text, minWidth: isMobile ? '140px' : '220px', textAlign: 'center' }}>
            {colonne[0].data.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} — {colonne[colonne.length-1].data.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
          <button onClick={() => sposta(N_GIORNI)} style={{ padding: '8px 12px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, cursor: 'pointer', fontWeight: '900' }}>›</button>
        </div>
        <button onClick={() => setInizioFinestra(new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate()))}
          style={{ padding: '8px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.textSub, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
          Oggi
        </button>
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[['tutte','Tutte le sedi'],['agnano','Agnano'],['nola','Nola'],['salerno','Salerno']].map(([val,label]) => (
          <button key={val} onClick={() => setFiltroSedeCal(val)}
            style={{ padding: '7px 14px', borderRadius: '9px', border: `1.5px solid ${filtroSedeCal===val ? '#3b82f6' : T.border}`, background: filtroSedeCal===val ? 'rgba(59,130,246,0.12)' : T.surface, color: filtroSedeCal===val ? '#3b82f6' : T.textSub, cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ fontSize: '10px', color: T.textMuted, fontStyle: 'italic', marginBottom: '12px' }}>
        💡 Vista di sola consultazione — data e ora si impostano nella lista "Lista Lavorazioni"
      </div>

      {/* SCROLL ORIZZONTALE: colonna "Non pianificate" fissa a sinistra + una colonna per ciascun giorno */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '10px', WebkitOverflowScrolling: 'touch' }}>
        {/* Colonna NON PIANIFICATE */}
        <div style={{ flex: `0 0 ${larghezzaColonna}`, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', padding: '8px' }}>
          <div style={{ fontSize: '9px', fontWeight: '900', color: '#f59e0b', marginBottom: '6px', textAlign: 'center' }}>⚠️ SENZA DATA ({nonPianificate.length})</div>
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {nonPianificate.map(c => <CardConsegna key={c.id} c={c} />)}
          </div>
        </div>

        {/* Colonne giorno */}
        {colonne.map(col => {
          const isOggi = col.ymd === oggiYMD;
          return (
            <div key={col.ymd} style={{ flex: `0 0 ${larghezzaColonna}`, background: T.surface, border: `1.5px solid ${isOggi ? '#3b82f6' : T.border}`, borderRadius: '10px', padding: '8px' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: isOggi ? '#3b82f6' : T.text, textAlign: 'center', marginBottom: '2px' }}>
                {GIORNI_SETT[col.data.getDay()]} {col.data.getDate()}
              </div>
              <div style={{ fontSize: '8px', color: T.textMuted, textAlign: 'center', marginBottom: '6px' }}>
                {col.data.toLocaleDateString('it-IT', { month: 'short' })}{isOggi ? ' · oggi' : ''}
              </div>
              <div style={{ maxHeight: '360px', overflowY: 'auto', minHeight: '20px' }}>
                {col.consegne.length === 0 ? (
                  <div style={{ fontSize: '9px', color: T.textMuted, textAlign: 'center', padding: '10px 0' }}>—</div>
                ) : col.consegne.map(c => <CardConsegna key={c.id} c={c} />)}
              </div>
            </div>
          );
        })}
      </div>

      {/* POPOVER DETTAGLIO — click su una consegna */}
      {dettaglioFor && (
        <div onClick={() => setDettaglioFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '420px', width: '100%', padding: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>{dettaglioFor.veicolo?.marca} {dettaglioFor.veicolo?.modello}</div>
                <div style={{ fontSize: '11px', color: T.textSub, marginTop: '2px' }}>
                  {dettaglioFor.cliente?.ragioneSociale || `${dettaglioFor.cliente?.nome || ''} ${dettaglioFor.cliente?.cognome || ''}`.trim()} · {dettaglioFor.veicolo?.targa || 'N.D.'}
                </div>
                <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '2px' }}>{dettaglioFor.venditoreNome}</div>
                <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '4px' }}>
                  📅 {(dettaglioFor.backoffice?.dataConsegnaPrevista || dettaglioFor.prezzo?.dataConsegna) ? new Date(dettaglioFor.backoffice?.dataConsegnaPrevista || dettaglioFor.prezzo?.dataConsegna).toLocaleDateString('it-IT') : 'Nessuna data'}
                  {dettaglioFor.backoffice?.oraConsegnaPrevista ? ` · 🕐 ${dettaglioFor.backoffice.oraConsegnaPrevista}` : ''}
                </div>
              </div>
              <button onClick={() => setDettaglioFor(null)} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '8px', padding: '5px 9px', cursor: 'pointer', fontWeight: '900' }}>×</button>
            </div>

            {/* Saldo e Permuta — stesso calcolo/dati della scheda completa
                in Back Office, aggiunti qui per non dover navigare via dal
                calendario solo per verificarli. */}
            {(() => {
              const pr = dettaglioFor.prezzo || {};
              const bo = dettaglioFor.backoffice || {};
              // Stessa formula e stesso saldo manuale della scheda Back Office
              const saldo = calcolaSaldoContratto(dettaglioFor);
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ padding: '10px 12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', color: T.textMuted }}>💰 Saldo</div>
                    <div style={{ fontSize: '14px', fontWeight: '900', color: saldo > 0 ? '#ef4444' : '#10b981' }}>
                      {testoSaldoContratto(dettaglioFor)}
                    </div>
                  </div>
                  <div style={{ padding: '10px 12px', background: T.bg, borderRadius: '10px', border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', color: T.textMuted }}>🔄 Permuta</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: pr.hasPermuta ? '#f59e0b' : T.textSub }}>
                      {pr.hasPermuta ? (bo.permutaRitirata ? '✓ Ritirata' : 'Da ritirare') : 'Nessuna'}
                    </div>
                  </div>
                </div>
              );
            })()}

            {dettaglioFor.backoffice?.esitoConsegna ? (
              <div>
                {dettaglioFor.backoffice.esitoConsegna === 'consegnata' ? (
                  <div style={{ padding: '11px', borderRadius: '10px', border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: '900', fontSize: '12px', textAlign: 'center' }}>
                    ✅ Consegnata
                  </div>
                ) : (
                  <div style={{ padding: '11px', borderRadius: '10px', border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)' }}>
                    <div style={{ color: '#ef4444', fontWeight: '900', fontSize: '12px', textAlign: 'center', marginBottom: dettaglioFor.backoffice?.noteConsegnaFallita ? '8px' : 0 }}>❌ Consegna Fallita</div>
                    {dettaglioFor.backoffice?.noteConsegnaFallita && (
                      <div style={{ fontSize: '11px', color: T.textSub, textAlign: 'center' }}>{dettaglioFor.backoffice.noteConsegnaFallita}</div>
                    )}
                  </div>
                )}
              </div>
            ) : puoModificare && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => handleConsegnata(dettaglioFor)} disabled={salvando === dettaglioFor.id}
                  style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: '12px' }}>
                  ✅ V — Consegnata
                </button>
                <button onClick={() => { setNotaFallitaFor(dettaglioFor); setTestoNota(''); }} disabled={salvando === dettaglioFor.id}
                  style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '12px' }}>
                  ❌ X — Fallita
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODALE NOTA CONSEGNA FALLITA */}
      {notaFallitaFor && (
        <div onClick={() => setNotaFallitaFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '460px', width: '100%', padding: '22px' }}>
            <div style={{ fontSize: '15px', fontWeight: '900', color: T.text, marginBottom: '4px' }}>❌ Consegna Fallita</div>
            <div style={{ fontSize: '11px', color: T.textMuted, marginBottom: '14px' }}>
              {notaFallitaFor.veicolo?.marca} {notaFallitaFor.veicolo?.modello} — {notaFallitaFor.cliente?.ragioneSociale || `${notaFallitaFor.cliente?.nome || ''} ${notaFallitaFor.cliente?.cognome || ''}`.trim()}
            </div>
            <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '6px' }}>MOTIVO (obbligatorio)</label>
            <textarea value={testoNota} onChange={e => setTestoNota(e.target.value)} rows={4} autoFocus
              placeholder="Es. Cliente irreperibile, documenti mancanti, finanziamento non erogato..."
              style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '16px' }} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setNotaFallitaFor(null)} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Annulla</button>
              <button onClick={handleSalvaFallita} disabled={salvando === notaFallitaFor.id}
                style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '12px' }}>
                Conferma Fallimento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════════
// TAB FINANCE
// ═══════════════════════════════════════════════════════════════════════════════
function FinanceView({ user, contratti: contrattiProp }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [contratti, setContratti] = useState([]);
  const [erogatoStorico, setErogatoStorico] = useState({});
  const [interventiOfficina, setInterventiOfficina] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fMese, setFMese] = useState('');
  const [fAnno, setFAnno] = useState('');
  const [activeTab, setActiveTab] = useState('kpi');
  const [permutaStockModal, setPermutaStockModal] = useState(null);
  const [specMarca, setSpecMarca] = useState('');
  const [specModello, setSpecModello] = useState('');
  const [specAnno, setSpecAnno] = useState('');
  const [specDettaglioAperto, setSpecDettaglioAperto] = useState(false);
  // Fetch diretto e completo dello stock per lo specchietto — NON usa la
  // cache condivisa (window._radarStockCache), che può essere parziale o
  // non aggiornata a seconda di cosa è stato caricato l'ultima volta
  // aprendo Stock (es. veicoli venduti esclusi di default). Qui serve
  // invece il quadro completo, quindi si legge tutto direttamente da
  // Firebase una volta sola.
  const [stockCompletoSpec, setStockCompletoSpec] = useState(null);
  useEffect(() => {
    // Prima: fetch completo di stock_data/vehicles (3.6MB) ad OGNI apertura
    // di Finance, mai cacheato. Ora: stessa cache TTL 4 ore + IndexedDB già
    // usata ovunque oggi — nessun cambiamento nel DATO restituito (resta
    // il quadro completo, venduti inclusi), solo meno riscaricamenti.
    let annullato = false;
    const now = Date.now();
    if (window._radarStockSpecTs && (now - window._radarStockSpecTs) < 4*60*60*1000) {
      setStockCompletoSpec(window._radarStockSpecCache || []);
      return;
    }
    idbLeggi('stock_spec_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarStockSpecCache = cache.list; window._radarStockSpecTs = cache.ts;
        setStockCompletoSpec(cache.list);
        return;
      }
      get(ref(db, 'stock_data/vehicles')).then(snap => {
        if (annullato) return;
        const raw = snap.exists() ? snap.val() : [];
        const list = Array.isArray(raw) ? raw : Object.values(raw);
        const ts = Date.now();
        window._radarStockSpecCache = list; window._radarStockSpecTs = ts;
        idbScrivi('stock_spec_cache', { list, ts });
        setStockCompletoSpec(list);
      }).catch(() => setStockCompletoSpec([]));
    });
    return () => { annullato = true; };
  }, []);

  // Se la cache stock è vuota (perché non si è mai aperta la tab Stock in
  // questa sessione), la scarichiamo noi in automatico — una sola volta,
  // un fetch leggero, non serve più visitare Stock prima per vedere i
  // valori corretti nel conto economico.
  const [stockCacheTick, setStockCacheTick] = useState(0);
  useEffect(() => {
    if (!window._radarStockCache?.vehicles) {
      get(ref(db, 'stock_data')).then(snap => {
        if (snap.val()?.vehicles) {
          window._radarStockCache = snap.val();
          window._radarStockTs = Date.now();
          setStockCacheTick(t => t + 1); // forza il ricalcolo del conto economico
        }
      }).catch(() => {});
    }
  }, []);

  // Campi extra del conto economico (costi vari, provvigione, commissione,
  // costo polizza) — necessari per calcolare la marginalità reale, coerente
  // con il conto economico dettagliato per veicolo (stessa fonte dati).
  const [extraFieldsFin, setExtraFieldsFin] = useState({});
  const [costiAcquistoFin, setCostiAcquistoFin] = useState({});
  useEffect(() => {
    // Prima: nessuna cache, riscaricato per intero ad ogni apertura di
    // Finance. Ora: stessa cache TTL 4 ore + IndexedDB — il calcolo di
    // marginalità resta identico, cambia solo la frequenza di download.
    let annullato = false;
    const now = Date.now();
    const elabora = (data) => {
      const costi = {}; const extra = {};
      Object.entries(data || {}).forEach(([k, v]) => {
        if (typeof v === 'object') {
          // Solo se costoAcquisto è STATO davvero impostato (anche a 0
          // esplicitamente) — altrimenti costi[k] resta undefined, così il
          // fallback al bottom price funziona correttamente. Prima
          // "v.costoAcquisto || 0" trattava "mai impostato" come "zero
          // esplicito", nascondendo il bottom price ogni volta che questo
          // nodo esisteva per un altro motivo (es. solo costoPolizza).
          if (v.costoAcquisto !== undefined) costi[k] = v.costoAcquisto;
          extra[k] = { provvFin: v.provvFin || 0, commServizi: v.commServizi || 0, costiVari: v.costiVari || 0, costoPolizza: v.costoPolizza || 0, costoGaranzia: v.costoGaranzia };
        } else {
          costi[k] = v;
        }
      });
      return { costi, extra };
    };
    if (window._radarContoEconTs && (now - window._radarContoEconTs) < 4*60*60*1000) {
      setCostiAcquistoFin(window._radarContoEconCosti || {});
      setExtraFieldsFin(window._radarContoEconExtra || {});
      return;
    }
    idbLeggi('conto_econ_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarContoEconCosti = cache.costi; window._radarContoEconExtra = cache.extra; window._radarContoEconTs = cache.ts;
        setCostiAcquistoFin(cache.costi); setExtraFieldsFin(cache.extra);
        return;
      }
      get(ref(db, 'conto_economico_acquisti')).then(snap => {
        if (annullato) return;
        const { costi, extra } = elabora(snap.val());
        const ts = Date.now();
        window._radarContoEconCosti = costi; window._radarContoEconExtra = extra; window._radarContoEconTs = ts;
        idbScrivi('conto_econ_cache', { costi, extra, ts });
        setCostiAcquistoFin(costi);
        setExtraFieldsFin(extra);
      });
    });
    return () => { annullato = true; };
  }, []);

  useEffect(() => {
    setContratti((contrattiProp || []).filter(c => c.stato === 'approvato' || c.stato === 'venduto'));
    setLoading(false);
  }, [contrattiProp]);

  useEffect(() => {
    let annullato = false;
    const now = Date.now();
    if (window._radarErogatoTs && (now - window._radarErogatoTs) < 4*60*60*1000) {
      setErogatoStorico(window._radarErogatoCache || {});
      return;
    }
    idbLeggi('finance_erogato_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarErogatoCache = cache.data; window._radarErogatoTs = cache.ts;
        setErogatoStorico(cache.data);
        return;
      }
      get(ref(db, 'finance_erogato')).then(snap => {
        if (annullato) return;
        const data = snap.val() || {};
        const ts = Date.now();
        window._radarErogatoCache = data; window._radarErogatoTs = ts;
        idbScrivi('finance_erogato_cache', { data, ts });
        setErogatoStorico(data);
      });
    });
    return () => { annullato = true; };
  }, []);

  useEffect(() => {
    get(ref(db, 'service_interventi')).then(snap => {
      const data = snap.val();
      if (data) setInterventiOfficina(Object.values(data));
      else setInterventiOfficina([]);
    });
  }, []);

  const filtered = useMemo(() => contratti.filter(c => {
    const d = new Date(c.datiExtra?.dataFirma || c.dataCreazione || '');
    if (fAnno && d.getFullYear() !== parseInt(fAnno)) return false;
    if (fMese && d.getMonth() + 1 !== parseInt(fMese)) return false;
    return true;
  }), [contratti, fAnno, fMese]);

  const anni = useMemo(() => {
    const s = new Set(contratti.map(c => new Date(c.datiExtra?.dataFirma || c.dataCreazione || '').getFullYear()).filter(Boolean));
    return [...s].sort((a, b) => b - a);
  }, [contratti]);

  const kpi = useMemo(() => {
    const totFatturato = filtered.reduce((s, c) => {
      const pr = c.prezzo || {};
      const pL = parseFloat(pr.prezzoListino || c.veicolo?.prezzo || 0);
      const sc = parseFloat(pr.sconto || 0);
      const ipt = parseFloat(pr.ipt || 0);
      const costiS = Object.entries(c.servizi || {}).filter(([k]) => k.startsWith('costo')).reduce((t, [, v]) => t + (parseFloat(v) || 0), 0);
      return s + (pL - sc + ipt + costiS);
    }, 0);
    const totPermute = filtered.reduce((s, c) => s + valorePermuteTot(c.prezzo), 0);
    const conFin = filtered.filter(c => c.prezzo?.hasFinanziamento && parseFloat(c.prezzo?.importoFinanziato||0) > 0 && !c.cliente?.b2b).length;
    const percFin = filtered.filter(c => !c.cliente?.b2b).length > 0 ? Math.round((conFin / filtered.filter(c => !c.cliente?.b2b).length) * 100) : 0;
    const hasMawdy = (c) => {
      const sv = c.servizi || {};
      // 1. Flag booleano diretto
      if (sv.mawdy36 || sv.mawdy) return true;
      // 2. Valori stringa nei servizi che contengono "mawdy"
      if (Object.values(sv).some(v => typeof v === 'string' && v.toLowerCase().includes('mawdy'))) return true;
      // 3. Nei pacchetti/note/descrizioni del preventivo
      const pr = c.prezzo || {};
      if (Object.values(pr).some(v => typeof v === 'string' && v.toLowerCase().includes('mawdy'))) return true;
      // 4. Nel testo libero dei servizi extra o note
      const note = (c.note || '') + (c.datiExtra?.noteVendita || '') + (c.datiExtra?.noteBack || '');
      if (note.toLowerCase().includes('mawdy')) return true;
      // 5. In costiDettaglio se presenti
      const det = c.costiDettaglio || [];
      if (Array.isArray(det) && det.some(d => (d.voce||'').toLowerCase().includes('mawdy'))) return true;
      return false;
    };
    const mawdy = filtered.filter(hasMawdy).length;
    const blockshaft = filtered.filter(c => c.servizi?.blockshaft).length;
    const lojack = filtered.filter(c => c.servizi?.lojack).length;
    // B2B
    const filteredB2B = filtered.filter(c => c.cliente?.b2b);
    const totB2B = filteredB2B.reduce((s, c) => {
      const pr = c.prezzo || {};
      const pL = parseFloat(pr.prezzoListino || c.veicolo?.prezzo || 0);
      const sc = parseFloat(pr.sconto || 0);
      const ipt = parseFloat(pr.ipt || 0);
      const costiS = Object.entries(c.servizi || {}).filter(([k]) => k.startsWith('costo')).reduce((t, [, v]) => t + (parseFloat(v) || 0), 0);
      return s + (pL - sc + ipt + costiS);
    }, 0);
    // Marginalità (solo contratti con bottomPrice valorizzato — anche dallo stock live,
    // aggiornato dopo la creazione del contratto es. tramite import Excel)
    const stockLiveFor = c => {
      const candidati = (window._radarStockCache?.vehicles || []).filter(sv2 => (c.veicolo?.id && sv2.id === c.veicolo.id) || (c.veicolo?.targa && sv2.targa === c.veicolo.targa) || (c.veicolo?.vin && sv2.vin === c.veicolo.vin));
      return candidati.find(sv2 => parseFloat(sv2.bottomPrice) > 0) || candidati[0];
    };
    const bpLive = c => {
      const stockLive = stockLiveFor(c);
      const bottomGrezzo = parseFloat(c.bottomPrice || c.veicolo?.bottomPrice || stockLive?.bottomPrice || 0);
      // Legge 104: stesso principio del conto economico dettagliato —
      // uniforma il costo scorporando il 22% e riapplicando il 4%,
      // altrimenti il margine aggregato risulta falsato per questi contratti.
      return c.prezzo?.legge104 ? (bottomGrezzo / 1.22) * 1.04 : bottomGrezzo;
    };
    const conBottomPrice = filtered.filter(c => bpLive(c) > 0);
    const totMargine = conBottomPrice.reduce((s, c) => {
      const pr = c.prezzo || {};
      const sv = c.servizi || {};
      const pL = parseFloat(pr.prezzoListino || c.veicolo?.prezzo || 0);
      const sc = parseFloat(pr.sconto || 0);
      const ipt = parseFloat(pr.ipt || 0);
      const totServizi = sommaCostiServiziNoEstinzione(c.servizi);
      const _ivaAgg = pr.hasIVA ? Math.round((pL - sc) * 0.22) : 0;
      const prezzoVendita = pL - sc + ipt + totServizi + _ivaAgg;
      const bp = bpLive(c);
      const vin = (c.veicolo?.vin||'').toLowerCase().trim();
      const targa = (c.veicolo?.targa||'').toLowerCase().trim();
      const interv = interventiOfficina.filter(i=>{
        const iVin=(i.veicolo?.vin||'').toLowerCase().trim();
        const iTarga=(i.veicolo?.targa||'').toLowerCase().trim();
        return (vin&&iVin===vin)||(targa&&iTarga===targa);
      }).reduce((t,i)=>t+(parseFloat(i.totaleNetto)||0),0);
      // Chiave per i campi extra manuali, stessa logica del conto economico dettagliato
      const key = (vin || targa || c.veicolo?.id || '').replace(/[^a-zA-Z0-9]/g, '_');
      const extra = extraFieldsFin[key] || {};
      const costiVari = parseFloat(extra.costiVari || 0);
      const costoPolizza = parseFloat(extra.costoPolizza || 0);
      const provvFin = parseFloat(extra.provvFin || c.datiExtra?.provvigioneFinanziaria || 0);
      const commServizi = parseFloat(extra.commServizi || 0);
      // Costo servizi — stesso criterio del conto economico dettagliato:
      // LoJack 476€, Blockshaft 182€, Metasystem (testo libero) 85€.
      const costoServizi = (sv.lojack ? 476 : 0) + (sv.blockshaft ? 182 : 0) + (/metasystem/i.test(sv.altroAntifurto || '') ? 85 : 0) + (sv.serviceCare ? 100 : 0);
      // Costo garanzia — valore manuale se salvato, altrimenti calcolo
      // automatico dalla tabella Mawdy (stesso criterio del conto economico
      // dettagliato).
      const costoGaranziaSalvatoFin = extra.costoGaranzia;
      const stockLiveGar = stockLiveFor(c);
      const costoGaranzia = costoGaranziaSalvatoFin !== undefined && costoGaranziaSalvatoFin !== '' && costoGaranziaSalvatoFin !== null
        ? parseFloat(costoGaranziaSalvatoFin || 0)
        : (calcolaCostoGaranziaMawdy(
            stockLiveGar?.cc || c.veicolo?.cc || estraiCilindrataDaVersione(stockLiveGar?.versione || c.veicolo?.versione) || estraiCilindrataDaVersione(stockLiveGar?.modello || c.veicolo?.modello) || estraiCilindrataDaVersione(stockLiveGar?.optionals || c.veicolo?.optionals),
            stockLiveGar?.registrazione || c.veicolo?.registrazione,
            stockLiveGar?.km || c.veicolo?.km,
            stockLiveGar?.marca || c.veicolo?.marca,
            rilevaGaranziaMawdy(c).mesi,
            c.dataCreazione
          ) || 0);
      return s + prezzoVendita - bp - interv - costiVari - costoPolizza - costoServizi - costoGaranzia + provvFin + commServizi;
    }, 0);
    const totVenditaBase = conBottomPrice.reduce((s,c)=>{ 
      const pr=c.prezzo||{}; 
      const totS=sommaCostiServiziNoEstinzione(c.servizi); 
      return s+(parseFloat(pr.prezzoListino||c.veicolo?.prezzo||0)-parseFloat(pr.sconto||0)+parseFloat(pr.ipt||0)+totS); 
    },0);
    const pctMargine = conBottomPrice.length > 0 && totVenditaBase > 0
      ? ((totMargine / totVenditaBase) * 100)
      : 0;

    return { totFatturato, fattuNetto: totFatturato - totPermute, percFin, conFin, mawdy, blockshaft, lojack, totB2B, numB2B: filteredB2B.length, totMargine, pctMargine, numConBottomPrice: conBottomPrice.length };
  }, [filtered, interventiOfficina, extraFieldsFin, stockCacheTick]);

  // ─── SPECCHIETTO VEICOLI — Prezzo Medio (solo ferro) / Margine Medio (solo
  // se c'è bottom price) / Servizi Venduti. Parte dagli stessi contratti già
  // filtrati per mese/anno in cima alla pagina, poi applica i filtri interni
  // marca/modello/anno immatricolazione.
  // Case-insensitive E senza accenti/dieresi: "Citroën"/"Citroen"/"CITROEN"
  // sono la stessa marca. Più alias espliciti per marchi scritti in modi
  // molto diversi tra loro (es. "DR" / "DR Automobiles").
  const ALIAS_MARCHE = {
    'DR AUTOMOBILES': 'DR',
    'DR AUTO': 'DR',
    'ALFA': 'ALFA ROMEO',
    'VW': 'VOLKSWAGEN',
    'MERCEDES': 'MERCEDES-BENZ',
    'MERCEDES BENZ': 'MERCEDES-BENZ',
  };
  const normCI = s => {
    let m = (s || '').toString().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    return ALIAS_MARCHE[m] || m;
  };
  const marcheDisponibiliSpec = useMemo(() => {
    const mappa = new Map();
    filtered.forEach(c => { const m = c.veicolo?.marca; if (m && !mappa.has(normCI(m))) mappa.set(normCI(m), m.trim()); });
    return [...mappa.values()].sort();
  }, [filtered]);
  const modelliDisponibiliSpec = useMemo(() => {
    const mappa = new Map();
    filtered.filter(c => !specMarca || normCI(c.veicolo?.marca) === normCI(specMarca)).forEach(c => { const m = c.veicolo?.modello; if (m && !mappa.has(normCI(m))) mappa.set(normCI(m), m.trim()); });
    return [...mappa.values()].sort();
  }, [filtered, specMarca]);
  // Anno di immatricolazione: preso SEMPRE dal veicolo in stock live quando
  // possibile (match per targa/VIN) — il dato congelato nel contratto
  // (c.veicolo.registrazione) può essere vuoto o non affidabile, quindi
  // resta solo come riserva se il veicolo non è più/non è mai stato in stock.
  const normVeicAnno = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
  const annoImmatricolazione = (c) => {
    const targaVeic = normVeicAnno(c.veicolo?.targa);
    const vinVeic = normVeicAnno(c.veicolo?.vin);
    const stockMatch = (stockCompletoSpec || []).find(sv =>
      (targaVeic && normVeicAnno(sv.targa) === targaVeic) || (vinVeic && normVeicAnno(sv.vin) === vinVeic)
    );
    const d = parseDataImmatricolazione(stockMatch?.registrazione || c.veicolo?.registrazione);
    return d ? d.getFullYear() : null;
  };
  const anniDisponibiliSpec = useMemo(() => [...new Set(filtered.map(c => annoImmatricolazione(c)).filter(Boolean))].sort((a,b) => b-a), [filtered, stockCompletoSpec]);

  const specchietto = useMemo(() => {
    const contrattiSpec = filtered.filter(c => {
      if (specMarca && normCI(c.veicolo?.marca) !== normCI(specMarca)) return false;
      if (specModello && normCI(c.veicolo?.modello) !== normCI(specModello)) return false;
      if (specAnno) {
        const anno = annoImmatricolazione(c);
        if (String(anno) !== specAnno) return false;
      }
      return true;
    });

    // Copertura bottom price PER ANNO — calcolata su marca/modello (se
    // selezionati) ma SENZA il filtro anno, altrimenti vedresti una sola
    // riga invece del quadro completo su dove mancano i dati.
    const contrattiPerCopertura = filtered.filter(c => {
      if (specMarca && normCI(c.veicolo?.marca) !== normCI(specMarca)) return false;
      if (specModello && normCI(c.veicolo?.modello) !== normCI(specModello)) return false;
      const tipoVeicolo = (c.veicolo?.tipo || '').toLowerCase().trim();
      return ['usato', 'used', 'km0', 'km 0'].includes(tipoVeicolo); // il bottom price ha senso solo per questi
    });
    const coperturaPerAnno = {};
    contrattiPerCopertura.forEach(c => {
      const anno = annoImmatricolazione(c) || 'N.D.';
      if (!coperturaPerAnno[anno]) coperturaPerAnno[anno] = { conBP: 0, senzaBP: 0, nessunMatch: 0 };
      const targaVeic = normVeicAnno(c.veicolo?.targa);
      const vinVeic = normVeicAnno(c.veicolo?.vin);
      const stockMatch = (targaVeic || vinVeic) ? (stockCompletoSpec || []).find(sv =>
        (targaVeic && normVeicAnno(sv.targa) === targaVeic) || (vinVeic && normVeicAnno(sv.vin) === vinVeic)
      ) : null;
      if (!stockMatch) coperturaPerAnno[anno].nessunMatch++; // veicolo non trovato affatto in stock (spesso: manca targa/VIN sul contratto, o non è più/mai stato in stock)
      else if (parseFloat(stockMatch?.bottomPrice || 0) > 0) coperturaPerAnno[anno].conBP++;
      else coperturaPerAnno[anno].senzaBP++; // trovato in stock, ma il bottom price non è valorizzato
    });
    const coperturaArr = Object.entries(coperturaPerAnno).sort((a, b) => (b[0] === 'N.D.' ? -1 : a[0] === 'N.D.' ? 1 : b[0] - a[0]));

    if (contrattiSpec.length === 0) return { n: 0, prezzoMedioFerro: 0, margineMedio: 0, nConMargine: 0, totServiziVenduti: 0, dettaglioMargine: [], coperturaArr };

    // Prezzo di vendita "solo ferro": prezzo listino - sconto, esclusi IPT,
    // servizi e IVA — il valore della sola vettura, non del pacchetto.
    let sommaFerro = 0, sommaMargine = 0, nConMargine = 0, sommaServizi = 0;
    const dettaglioMargine = []; // per individuare a colpo d'occhio eventuali veicoli con dati anomali
    contrattiSpec.forEach(c => {
      const pr = c.prezzo || {};
      const prezzoFerro = parseFloat(pr.prezzoListino || c.veicolo?.prezzo || 0) - parseFloat(pr.sconto || 0);
      sommaFerro += prezzoFerro;
      sommaServizi += sommaCostiServiziNoEstinzione(c.servizi);

      // Il bottom price ha senso solo per usato/km0 (prezzo di ritiro
      // permuta) — sul Nuovo non si applica allo stesso modo e falsava il
      // margine. Il Nuovo entra comunque nel prezzo medio di vendita, solo
      // non nel margine.
      const tipoVeicolo = (c.veicolo?.tipo || '').toLowerCase().trim();
      const isUsatoOKm0 = ['usato', 'used', 'km0', 'km 0'].includes(tipoVeicolo);

      // Abbinamento al veicolo in stock — SOLO per targa/VIN (normalizzati),
      // mai per id: gli id possono non essere stabili/unici tra un import e
      // l'altro, col rischio concreto di abbinare per sbaglio un veicolo
      // completamente diverso (e il suo bottom price sbagliato). Se ci sono
      // ancora doppioni in stock per la stessa targa/VIN, preferisce quello
      // che ha effettivamente un bottom price valorizzato.
      if (isUsatoOKm0) {
        const normVeic = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
        const targaVeic = normVeic(c.veicolo?.targa);
        const vinVeic = normVeic(c.veicolo?.vin);
        const candidatiStock = (stockCompletoSpec || []).filter(sv =>
          (targaVeic && normVeic(sv.targa) === targaVeic) || (vinVeic && normVeic(sv.vin) === vinVeic)
        );
        const stockMatch = candidatiStock.find(sv => parseFloat(sv.bottomPrice || 0) > 0) || candidatiStock[0];
        const bottomPriceGrezzoSpec = parseFloat(stockMatch?.bottomPrice || 0);
        // Legge 104: stesso principio del conto economico dettagliato.
        const bottomPrice = pr.legge104 ? (bottomPriceGrezzoSpec / 1.22) * 1.04 : bottomPriceGrezzoSpec;
        if (bottomPrice > 0) {
          const margineVeicolo = prezzoFerro - bottomPrice;
          sommaMargine += margineVeicolo;
          nConMargine++;
          dettaglioMargine.push({
            marca: c.veicolo?.marca, modello: c.veicolo?.modello, targa: c.veicolo?.targa,
            prezzoFerro, bottomPrice, margine: margineVeicolo,
          });
        }
      }
    });

    return {
      n: contrattiSpec.length,
      prezzoMedioFerro: sommaFerro / contrattiSpec.length,
      margineMedio: nConMargine > 0 ? sommaMargine / nConMargine : 0,
      nConMargine,
      totServiziVenduti: sommaServizi,
      dettaglioMargine,
      coperturaArr,
    };
  }, [filtered, specMarca, specModello, specAnno, stockCompletoSpec]);

  // Raggruppamento per finanziaria — escludi B2B
  const perFinanziaria = useMemo(() => {
    const map = {};
    filtered.filter(c => c.prezzo?.hasFinanziamento && c.prezzo?.finanziariaSocieta && !c.cliente?.b2b).forEach(c => {
      const nome = (c.prezzo.finanziariaSocieta || 'N.D.').trim().toUpperCase();
      if (!map[nome]) map[nome] = { nome, totale: 0, count: 0 };
      map[nome].totale += parseFloat(c.prezzo.importoFinanziato || 0);
      map[nome].count++;
    });
    return Object.values(map).sort((a, b) => b.totale - a.totale);
  }, [filtered]);

  // Raggruppamento permute per marca
  const perPermuta = useMemo(() => {
    const map = {};
    filtered.filter(c => c.prezzo?.hasPermuta && valorePermuteTot(c.prezzo) > 0).forEach(c => {
      const pz = c.prezzo;
      const cliente = c.cliente?.ragioneSociale || `${c.cliente?.nome || ''} ${c.cliente?.cognome || ''}`.trim();
      // Prima permuta (campi storici) + permute aggiuntive, una riga per veicolo
      const veicoliPermuta = [
        { marca: pz.permutaMarca, modello: pz.permutaModello, targa: pz.permutaTarga, km: pz.permutaKm, valore: pz.permutaValore },
        ...(Array.isArray(pz.permuteAggiuntive) ? pz.permuteAggiuntive : []),
      ].filter(x => x && parseFloat(x.valore) > 0);
      veicoliPermuta.forEach(x => {
        const marca = (x.marca || 'N.D.').trim();
        if (!map[marca]) map[marca] = { marca, totale: 0, count: 0, veicoli: [] };
        const val = parseFloat(x.valore || 0);
        map[marca].totale += val;
        map[marca].count++;
        map[marca].veicoli.push({ modello: x.modello || '', targa: x.targa || '', km: x.km || '', valore: val, cliente, contratto: c });
      });
    });
    return Object.values(map).sort((a, b) => b.totale - a.totale);
  }, [filtered]);

  const mesi = [['1','Gennaio'],['2','Febbraio'],['3','Marzo'],['4','Aprile'],['5','Maggio'],['6','Giugno'],['7','Luglio'],['8','Agosto'],['9','Settembre'],['10','Ottobre'],['11','Novembre'],['12','Dicembre']];

  const BarChart = ({ label, value, max, color, format }) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
        <span style={{ color: T.textSub }}>{label}</span>
        <b style={{ color }}>{format ? format(value) : value}</b>
      </div>
      <div style={{ height: '8px', background: T.chartBg, borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min((value / (max || 1)) * 100, 100)}%`, background: color, borderRadius: '4px', transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );

  if (loading) return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text }}>Caricamento...</div>;

  const tabs = [['kpi','📊 KPI'],['finanziarie','🏦 Per Finanziaria'],['permute','🔄 Permute'],['officina','🔧 Officina'],['conto','📈 Conto Economico'],['xml','📄 Fatture XML']]
    .filter(([k]) => k !== 'conto' || puoVedereContoEconomico(user));

  // ─── Scarica Contratti (CSV) — stessa logica di flatten/filtro già
  // pensata per l'export automatico via FTP: solo dati contratto veri
  // (cliente con ogni suo campo, veicolo, prezzo, servizi, datiExtra,
  // stato, date, venditore) — esclusi backoffice/log/UID tecnici interni.
  // Punto e virgola come delimitatore, per Excel in italiano.
  const scaricaCSVContratti = () => {
    const flatten = (obj, prefix = '', out = {}) => {
      if (obj === null || obj === undefined) return out;
      if (Array.isArray(obj)) { out[prefix] = JSON.stringify(obj); return out; }
      if (typeof obj === 'object') {
        for (const [k, v] of Object.entries(obj)) {
          const chiave = prefix ? `${prefix}.${k}` : k;
          if (v !== null && typeof v === 'object' && !Array.isArray(v)) flatten(v, chiave, out);
          else if (Array.isArray(v)) out[chiave] = JSON.stringify(v);
          else out[chiave] = v;
        }
        return out;
      }
      out[prefix] = obj;
      return out;
    };
    const csvEscape = val => {
      if (val === undefined || val === null) return '';
      const s = String(val);
      if (s.includes(';') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    // Formati italiani, coerenti col file storico già in uso: data
    // GG/MM/AAAA, importi con la virgola come separatore decimale.
    const fmtDataIT = iso => {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d)) return '';
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    };
    const fmtImportoIT = n => (parseFloat(n) || 0).toFixed(2).replace('.', ',');

    // ── Le 9 colonne storiche — stesso nome, stesso ordine, stesso
    // formato del file già in uso da tempo per la fatturazione. Vanno
    // SEMPRE per prime, identiche, per non rompere l'importazione
    // esistente — tutto il resto va aggiunto a destra, in più.
    const COLONNE_STORICHE = ['ConsulenteCognomeNome', 'ClienteCognomeNome', 'ClienteCodiceFiscale', 'ImportoContratto', 'ImportoCaparra', 'TelaioAuto', 'TargaAuto', 'DataApprovazione', 'IDContratto'];
    const rigaStorica = c => {
      const cl = c.cliente || {};
      const nomeCliente = cl.ragioneSociale || `${cl.cognome || ''} ${cl.nome || ''}`.trim();
      const pr = c.prezzo || {};
      // Importo Contratto — valore totale del contratto (prima di
      // sottrarre caparra/permuta/finanziamento), stessa logica già usata
      // altrove nell'app per calcolare il totale lordo.
      const pL = parseFloat(pr.prezzoListino || 0), sc = parseFloat(pr.sconto || 0), ipt = parseFloat(pr.ipt || 0);
      const totServizi = Object.entries(c.servizi||{}).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0);
      const ivaImporto = pr.hasIVA ? Math.round((pL - sc) * 0.22) : 0;
      const importoContratto = (pL - sc + ipt) + totServizi + ivaImporto;
      return {
        ConsulenteCognomeNome: c.venditoreNome || '',
        ClienteCognomeNome: nomeCliente,
        ClienteCodiceFiscale: cl.codiceFiscale || cl.cf || '',
        ImportoContratto: fmtImportoIT(importoContratto),
        ImportoCaparra: fmtImportoIT(pr.caparra),
        TelaioAuto: c.veicolo?.vin || '',
        TargaAuto: c.veicolo?.targa || '',
        DataApprovazione: fmtDataIT(c.approvazioneData),
        IDContratto: c.id,
      };
    };

    const CAMPI_INCLUSI = ['id', 'cliente', 'veicolo', 'prezzo', 'servizi', 'datiExtra', 'stato', 'dataCreazione', 'dataModifica', 'dataVendita', 'vendutoAt', 'venditoreNome'];
    // cliente.ultimoPreventivo e cliente.veicoliPrecedenti sono campi
    // sintetici (costruiti altrove per l'autocompletamento clienti, non
    // dati veri del cliente) che a volte finiscono copiati dentro il
    // contratto — portando con sé un intero preventivo duplicato con
    // decine di colonne inutili. Rimossi prima di generare il CSV.
    const pulisciCliente = cliente => {
      if (!cliente) return cliente;
      const { ultimoPreventivo, veicoliPrecedenti, ...pulito } = cliente;
      return pulito;
    };
    const filtraCampiRilevanti = c => {
      const f = {};
      CAMPI_INCLUSI.forEach(k => { if (c[k] !== undefined) f[k] = c[k]; });
      if (f.cliente) f.cliente = pulisciCliente(f.cliente);
      return f;
    };

    const righeFlatExtra = contratti.map(c => flatten(filtraCampiRilevanti(c)));
    const colonneExtra = new Set();
    righeFlatExtra.forEach(r => Object.keys(r).forEach(k => colonneExtra.add(k)));
    // Le colonne extra non ripetono l'id (già nelle colonne storiche come
    // IDContratto) — tolto per non duplicarlo inutilmente.
    const colonneExtraOrdinate = [...colonneExtra].filter(c => c !== 'id').sort();

    const header = [...COLONNE_STORICHE, ...colonneExtraOrdinate].map(csvEscape).join(';');
    const righe = contratti.map((c, i) => {
      const storica = rigaStorica(c);
      const valoriStorici = COLONNE_STORICHE.map(col => csvEscape(storica[col]));
      const valoriExtra = colonneExtraOrdinate.map(col => csvEscape(righeFlatExtra[i][col]));
      return [...valoriStorici, ...valoriExtra].join(';');
    });
    const csv = [header, ...righe].join('\n');

    // BOM UTF-8 — necessario perché Excel apra correttamente lettere
    // accentate (è, à, ò...) senza doverle reimportare manualmente.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const oggi = new Date();
    a.href = url;
    a.download = `contratti_${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '900', color: T.text, letterSpacing: '-0.5px' }}>Finance</h2>
          <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '3px' }}>{filtered.length} contratti nel periodo</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={fMese} onChange={e => setFMese(e.target.value)}
            style={{ padding: '8px 12px', background: T.inputBg, border: `1px solid ${fMese ? '#10b981' : T.border}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none' }}>
            <option value="">Tutti i mesi</option>
            {mesi.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={fAnno} onChange={e => setFAnno(e.target.value)}
            style={{ padding: '8px 12px', background: T.inputBg, border: `1px solid ${fAnno ? '#10b981' : T.border}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none' }}>
            <option value="">Tutti gli anni</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            {anni.filter(a => a !== 2025 && a !== 2026).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {(fMese || fAnno) && <button onClick={() => { setFMese(''); setFAnno(''); }} style={{ padding: '8px 12px', background: 'none', border: `1px solid ${T.border}`, borderRadius: '10px', color: '#ef4444', fontSize: '12px', cursor: 'pointer', fontWeight: '700' }}>× Reset</button>}
          <button onClick={scaricaCSVContratti}
            style={{ padding: '8px 16px', background: '#10b981', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '12px', cursor: 'pointer', fontWeight: '900' }}>
            📥 Scarica Contratti
          </button>
        </div>
      </div>

      {/* TAB BAR */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: `2px solid ${T.border}`, marginBottom: '20px' }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '700', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', background: 'transparent', color: activeTab === id ? '#10b981' : T.textSub, borderBottom: activeTab === id ? '2px solid #10b981' : '2px solid transparent', marginBottom: '-2px' }}>
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && Object.keys(erogatoStorico).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>💰</div>
          <div style={{ fontSize: '14px', fontWeight: '700' }}>Nessun contratto nel periodo selezionato</div>
        </div>
      ) : (<>

        {/* TAB KPI */}
        {activeTab === 'kpi' && (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, padding: '24px', borderTop: '4px solid #10b981' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '16px' }}>FATTURATO</div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>Totale Fatturato</div>
                <div style={{ fontSize: '26px', fontWeight: '900', color: '#10b981', letterSpacing: '-1px' }}>{fmt(kpi.totFatturato)}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>Al netto delle Permute</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#3b82f6' }}>{fmt(kpi.fattuNetto)}</div>
              </div>
              <div style={{ fontSize: '11px', color: T.textMuted, paddingTop: '12px', marginTop: '12px', borderTop: `1px solid ${T.border}` }}>su {filtered.length} contratti approvati</div>
            </div>
            <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, padding: '24px', borderTop: '4px solid #8b5cf6' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '16px' }}>FINANZIAMENTI</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>% Contratti Finanziati</div>
                  <div style={{ fontSize: '44px', fontWeight: '900', color: '#8b5cf6', lineHeight: 1, letterSpacing: '-2px' }}>{kpi.percFin}<span style={{ fontSize: '18px' }}>%</span></div>
                </div>
                <div style={{ flex: 1, marginBottom: '4px' }}>
                  <div style={{ height: '70px', background: T.chartBg, borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <div style={{ width: '100%', height: `${kpi.percFin}%`, background: 'linear-gradient(180deg, #8b5cf6, #6d28d9)', transition: 'height 0.8s ease', borderRadius: '10px 10px 0 0' }} />
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '12px', color: T.textSub }}>{kpi.conFin} contratti su {filtered.length} con finanziamento</div>
            </div>
            <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, padding: '24px', borderTop: '4px solid #6366f1' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '16px' }}>🏢 B2B — RIVENDITORI</div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>Fatturato B2B</div>
                <div style={{ fontSize: '26px', fontWeight: '900', color: '#6366f1', letterSpacing: '-1px' }}>{fmt(kpi.totB2B)}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>Auto Vendute B2B</div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#8b5cf6' }}>{kpi.numB2B}</div>
              </div>
              <div style={{ fontSize: '11px', color: T.textMuted, paddingTop: '12px', marginTop: '12px', borderTop: `1px solid ${T.border}` }}>
                {filtered.length > 0 ? Math.round((kpi.numB2B / filtered.length) * 100) : 0}% del totale contratti
              </div>
            </div>
            <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, padding: '24px', borderTop: '4px solid #f59e0b' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '16px' }}>PRODOTTI SPECIALI</div>
              <BarChart label="Garanzie Mawdy" value={kpi.mawdy} max={filtered.length} color="#f59e0b" />
              <BarChart label="Blockshaft" value={kpi.blockshaft} max={filtered.length} color="#ef4444" />
              <BarChart label="Installazioni LoJack" value={kpi.lojack} max={filtered.length} color="#3b82f6" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${T.border}` }}>
                {[['Mawdy', kpi.mawdy, '#f59e0b'],['Blockshaft', kpi.blockshaft, '#ef4444'],['LoJack', kpi.lojack, '#3b82f6']].map(([l, v, c]) => (
                  <div key={l} style={{ background: T.bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '700', marginBottom: '4px' }}>{l}</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: c }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* MARGINALITÀ */}
            <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, padding: '24px', borderTop: `4px solid ${kpi.totMargine >= 0 ? '#10b981' : '#ef4444'}` }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px', marginBottom: '16px' }}>📊 MARGINALITÀ</div>
              {kpi.numConBottomPrice > 0 ? (<>
                <div>
                  <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>Margine Totale ({kpi.numConBottomPrice} auto con bottom price)</div>
                  <div style={{ fontSize: '26px', fontWeight: '900', color: kpi.totMargine >= 0 ? '#10b981' : '#ef4444', letterSpacing: '-1px' }}>{fmt(kpi.totMargine)}</div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '4px' }}>% Margine medio sul prezzo vendita</div>
                  <div style={{ fontSize: '32px', fontWeight: '900', color: kpi.pctMargine >= 10 ? '#10b981' : kpi.pctMargine >= 0 ? '#f59e0b' : '#ef4444' }}>{kpi.pctMargine.toFixed(1)}<span style={{ fontSize: '16px' }}>%</span></div>
                </div>
                <div style={{ fontSize: '11px', color: T.textMuted, paddingTop: '12px', marginTop: '12px', borderTop: `1px solid ${T.border}` }}>
                  Include interventi officina, costi vari, costo polizza, costo garanzia, costo servizi (LoJack/Blockshaft/Metasystem/Service Care), provvigione finanziaria e commissione servizi
                </div>
              </>) : (
                <div style={{ fontSize: '11px', color: T.textMuted, fontStyle: 'italic' }}>
                  Nessun bottom price disponibile. Sincronizza lo stock per aggiornare i prezzi di acquisto.
                </div>
              )}
            </div>
          </div>

          {puoVedereContoEconomico(user) && (
          <div style={{ background: T.surface, borderRadius: '20px', border: `1px solid ${T.border}`, padding: '24px', borderTop: '4px solid #f59e0b', marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '18px' }}>
              <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px' }}>SPECCHIETTO VEICOLI</div>
              <div style={{ fontSize: '10px', color: T.textMuted, fontStyle: 'italic' }}>Rispetta il filtro mese/anno impostato in cima alla pagina</div>
            </div>

            {stockCompletoSpec === null ? (
              <div style={{ fontSize: '12px', color: T.textMuted, fontStyle: 'italic', padding: '20px 0' }}>⏳ Caricamento dati stock completi...</div>
            ) : (
            <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
              <select value={specMarca} onChange={e => { setSpecMarca(e.target.value); setSpecModello(''); }}
                style={{ padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                <option value="">Tutte le marche</option>
                {marcheDisponibiliSpec.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={specModello} onChange={e => setSpecModello(e.target.value)}
                style={{ padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                <option value="">Tutti i modelli</option>
                {modelliDisponibiliSpec.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={specAnno} onChange={e => setSpecAnno(e.target.value)}
                style={{ padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                <option value="">Tutti gli anni</option>
                {anniDisponibiliSpec.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              {(specMarca || specModello || specAnno) && (
                <button onClick={() => { setSpecMarca(''); setSpecModello(''); setSpecAnno(''); }}
                  style={{ padding: '9px 14px', background: 'none', border: `1px solid ${T.border}`, borderRadius: '10px', color: '#ef4444', fontSize: '11px', cursor: 'pointer', fontWeight: '700' }}>
                  × Reset
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '6px' }}>Prezzo Medio di Vendita <span style={{ color: T.textMuted }}>(solo ferro)</span></div>
                <div style={{ fontSize: '24px', fontWeight: '900', color: '#3b82f6', letterSpacing: '-1px' }}>{fmt(specchietto.prezzoMedioFerro)}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '6px' }}>Margine Medio <span style={{ color: T.textMuted }}>(solo con bottom price)</span></div>
                <div style={{ fontSize: '24px', fontWeight: '900', color: specchietto.margineMedio >= 0 ? '#10b981' : '#ef4444', letterSpacing: '-1px' }}>{fmt(specchietto.margineMedio)}</div>
                {specchietto.n > 0 && <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '2px' }}>calcolato su {specchietto.nConMargine} di {specchietto.n} contratti — solo Usato/Km0, il Nuovo non è incluso</div>}
              </div>
              <div>
                <div style={{ fontSize: '10px', color: T.textSub, marginBottom: '6px' }}>Ammontare Servizi Venduti</div>
                <div style={{ fontSize: '24px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '-1px' }}>{fmt(specchietto.totServiziVenduti)}</div>
              </div>
            </div>
            {specchietto.n === 0 && (
              <div style={{ fontSize: '11px', color: T.textMuted, fontStyle: 'italic', marginTop: '14px' }}>Nessun contratto corrisponde ai filtri selezionati.</div>
            )}

            {specchietto.dettaglioMargine?.length > 0 && (
              <div style={{ marginTop: '18px', borderTop: `1px solid ${T.border}`, paddingTop: '14px' }}>
                <button onClick={() => setSpecDettaglioAperto(o => !o)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '900', color: '#f59e0b', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {specDettaglioAperto ? '▾' : '▸'} 🔍 Dettaglio calcolo margine ({specchietto.dettaglioMargine.length} veicoli) — utile per individuare dati anomali
                </button>
                {specDettaglioAperto && (
                  <div style={{ marginTop: '12px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Veicolo</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Targa</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Prezzo Ferro</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Bottom Price</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Margine</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...specchietto.dettaglioMargine].sort((a,b) => a.margine - b.margine).map((r, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: r.margine < -3000 ? 'rgba(239,68,68,0.08)' : 'transparent' }}>
                            <td style={{ padding: '6px 8px', color: T.text }}>{r.marca} {r.modello}</td>
                            <td style={{ padding: '6px 8px', color: T.textSub }}>{r.targa || '—'}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: T.text }}>{fmt(r.prezzoFerro)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: T.text }}>{fmt(r.bottomPrice)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '800', color: r.margine >= 0 ? '#10b981' : '#ef4444' }}>{fmt(r.margine)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '8px', fontStyle: 'italic' }}>
                      Righe evidenziate in rosso: margine negativo oltre 3.000€ — probabile bottom price errato in stock per quel veicolo specifico.
                    </div>
                  </div>
                )}
              </div>
            )}

            {specchietto.coperturaArr?.length > 0 && (
              <div style={{ marginTop: '18px', borderTop: `1px solid ${T.border}`, paddingTop: '14px' }}>
                <div style={{ fontSize: '11px', fontWeight: '900', color: T.textMuted, marginBottom: '10px' }}>
                  📊 Copertura Bottom Price per Anno di Immatricolazione <span style={{ fontWeight: '400', fontStyle: 'italic' }}>(Usato/Km0{specMarca ? ` · ${specMarca}` : ''}{specModello ? ` ${specModello}` : ''}, indipendente dal filtro Anno qui sopra)</span>
                </div>
                <div style={{ fontSize: '9px', color: T.textMuted, marginBottom: '10px', fontStyle: 'italic' }}>
                  "Non trovato in stock" ≠ "senza bottom price": significa che il veicolo del contratto non è stato individuato affatto nello stock attuale (spesso perché manca targa/VIN sul contratto, o non è più/mai stato importato) — è un problema di abbinamento, non di dato mancante.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Anno</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Con Bottom Price</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Trovato ma Senza BP</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>Non Trovato in Stock</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '700' }}>% Copertura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specchietto.coperturaArr.map(([anno, dati]) => {
                      const tot = dati.conBP + dati.senzaBP + dati.nessunMatch;
                      const pct = tot > 0 ? (dati.conBP / tot * 100) : 0;
                      return (
                        <tr key={anno} style={{ borderBottom: `1px solid ${T.border}`, background: pct < 50 ? 'rgba(239,68,68,0.06)' : 'transparent' }}>
                          <td style={{ padding: '6px 8px', color: T.text, fontWeight: '700' }}>{anno}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#10b981' }}>{dati.conBP}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: dati.senzaBP > 0 ? '#f59e0b' : T.textMuted }}>{dati.senzaBP}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: dati.nessunMatch > 0 ? '#ef4444' : T.textMuted }}>{dati.nessunMatch}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '800', color: pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }}>{pct.toFixed(0)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </>
            )}
          </div>
          )}
          </>
        )}

        {/* TAB POTENZIALE EROGATO */}
        {activeTab === 'finanziarie' && (() => {
          const mesiMap = {'1':'GENNAIO','2':'FEBBRAIO','3':'MARZO','4':'APRILE','5':'MAGGIO','6':'GIUGNO','7':'LUGLIO','8':'AGOSTO','9':'SETTEMBRE','10':'OTTOBRE','11':'NOVEMBRE','12':'DICEMBRE'};
          const meseFiltro = fMese ? mesiMap[String(fMese)] : null;
          const annoFiltro = fAnno || '';

          // Filtra storico per anno selezionato
          // Struttura: { COMPASS_2025: {nome, anno, mesi, totale}, COMPASS_2026: {...} }
          const storicoPerAnno = {};
          Object.values(erogatoStorico).forEach(f => {
            const fanno = String(f.anno || 2025);
            if (annoFiltro && fanno !== annoFiltro) return;
            const nome = (f.nome || '').toUpperCase().trim();
            if (!nome) return;
            let val = 0;
            if (meseFiltro && f.mesi) {
              val = parseFloat(f.mesi[meseFiltro] || 0);
            } else if (!meseFiltro) {
              val = parseFloat(f.totale || 0);
            }
            if (!storicoPerAnno[nome]) storicoPerAnno[nome] = 0;
            storicoPerAnno[nome] += val;
          });

          const combinato = {};
          // 1. Storico → va nel CONSOLIDATO
          Object.entries(storicoPerAnno).forEach(([nome, val]) => {
            combinato[nome] = { nome, consolidato: val, radar: 0, totale: val };
          });
          // 2. RadaR contratti
          perFinanziaria.forEach(f => {
            const nome = f.nome.toUpperCase();
            const consolidatoRadar = filtered
              .filter(c => c.prezzo?.hasFinanziamento && (c.prezzo?.finanziariaSocieta||'N.D.').trim().toUpperCase() === nome && c.backoffice?.liquidato)
              .reduce((s, c) => s + parseFloat(c.prezzo?.importoFinanziato || 0), 0);
            if (combinato[nome]) {
              combinato[nome].radar = f.totale;
              combinato[nome].totale += f.totale;
              combinato[nome].consolidato += consolidatoRadar;
            } else {
              combinato[nome] = { nome, consolidato: consolidatoRadar, radar: f.totale, totale: f.totale };
            }
          });
          const lista = Object.values(combinato).filter(f => f.totale > 0 || f.consolidato > 0).sort((a, b) => b.totale - a.totale);
          const maxTot = lista[0]?.totale || 1;
          const totaleGen = lista.reduce((s, f) => s + f.totale, 0);
          const totaleConsolidato = lista.reduce((s, f) => s + f.consolidato, 0);

          return (
            <div>
              <div style={{ fontSize: '11px', color: T.textMuted, marginBottom: '14px' }}>
                {annoFiltro ? `📊 Storico ${annoFiltro} + RadaR` : '📊 Storico 2025-2026 + RadaR'}
                {meseFiltro && <span style={{ color: '#10b981', marginLeft: '8px' }}>· {meseFiltro}</span>}
              </div>
              <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 150px 150px', padding: '10px 16px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                  {['FINANZIARIA', 'CONSOLIDATO', 'RADAR', 'POTENZIALE'].map(h => <div key={h} style={{ fontSize: '9px', fontWeight: '900', color: h==='CONSOLIDATO' ? '#10b981' : T.textMuted, letterSpacing: '1px' }}>{h}</div>)}
                </div>
                {lista.map((f, i) => (
                  <div key={f.nome} style={{ padding: '14px 16px', borderBottom: i < lista.length - 1 ? `1px solid ${T.border}` : 'none', background: i % 2 === 0 ? 'transparent' : T.bg + '80' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 150px 150px', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>{f.nome}</div>
                      <div style={{ fontSize: '13px', fontWeight: '900', color: f.consolidato > 0 ? '#10b981' : T.textMuted }}>{f.consolidato > 0 ? fmt(f.consolidato) : '—'}</div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#8b5cf6' }}>{f.radar > 0 ? fmt(f.radar) : '—'}</div>
                      <div style={{ fontSize: '14px', fontWeight: '900', color: '#3b82f6' }}>{fmt(f.totale)}</div>
                    </div>
                    <div style={{ height: '5px', background: T.chartBg, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', display: 'flex', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: `${(f.consolidato / maxTot) * 100}%`, background: '#10b981', transition: 'width 0.6s' }} />
                        <div style={{ width: `${(f.radar / maxTot) * 100}%`, background: '#8b5cf6', transition: 'width 0.6s' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                      <span style={{ fontSize: '9px', color: '#10b981' }}>■ Consolidato</span>
                      <span style={{ fontSize: '9px', color: '#8b5cf6' }}>■ RadaR</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, background: T.surface, borderRadius: '12px', border: `1px solid #10b981`, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#10b981' }}>💰 CONSOLIDATO TOTALE</span>
                  <span style={{ fontSize: '18px', fontWeight: '900', color: '#10b981' }}>{fmt(totaleConsolidato)}</span>
                </div>
                <div style={{ flex: 1, background: T.surface, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: T.textSub }}>POTENZIALE TOTALE</span>
                  <span style={{ fontSize: '18px', fontWeight: '900', color: '#3b82f6' }}>{fmt(totaleGen)}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* TAB PERMUTE */}
        {activeTab === 'permute' && (
          <div>
            {perPermuta.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: T.textMuted }}>Nessuna permuta nel periodo</div>
            ) : (
              <>
                {perPermuta.map((p, i) => (
                  <div key={p.marca} style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, marginBottom: '12px', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.bg }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '900', color: T.text }}>{p.marca}</div>
                        <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{p.count} {p.count === 1 ? 'veicolo' : 'veicoli'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '700' }}>VALORE TOTALE</div>
                        <div style={{ fontSize: '18px', fontWeight: '900', color: '#f59e0b' }}>{fmt(p.totale)}</div>
                      </div>
                    </div>
                    <div style={{ padding: '10px 16px' }}>
                      {p.veicoli.map((v, j) => (
                        <div key={j} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: j < p.veicoli.length - 1 ? `1px solid ${T.border}` : 'none', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <div>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: T.text }}>{p.marca} {v.modello}</span>
                            {v.targa && <span style={{ fontSize: '10px', color: T.textMuted, marginLeft: '8px' }}>{v.targa}</span>}
                            {v.km && <span style={{ fontSize: '10px', color: T.textMuted, marginLeft: '6px' }}>{new Intl.NumberFormat('it-IT').format(v.km)} km</span>}
                            <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '1px' }}>Cliente: {v.cliente}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '800', color: '#f59e0b', flexShrink: 0 }}>{fmt(v.valore)}</div>
                            {['admin', 'super_admin'].includes(user?.ruolo) && (
                              v.contratto?.prezzo?.permutaInStockId ? (
                                <span style={{ fontSize: '9px', fontWeight: '900', color: '#10b981', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '20px', padding: '3px 9px', whiteSpace: 'nowrap' }}>
                                  ✓ In Stock
                                </span>
                              ) : v.contratto?.prezzo?.permutaRottamazione ? (
                                <span title="Marcato per rottamazione — non entra nel circuito di permuta/rivendita" style={{ fontSize: '9px', fontWeight: '900', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '20px', padding: '3px 9px', whiteSpace: 'nowrap' }}>
                                  🗑️ Rottamazione
                                </span>
                              ) : (
                                <button onClick={() => setPermutaStockModal(v.contratto)}
                                  style={{ padding: '6px 11px', borderRadius: '8px', border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '9px', whiteSpace: 'nowrap' }}>
                                  🚗 Entra in Stock
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{ background: T.surface, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: T.textSub }}>TOTALE VALORE PERMUTE</span>
                  <span style={{ fontSize: '15px', fontWeight: '900', color: '#f59e0b' }}>{fmt(perPermuta.reduce((s, p) => s + p.totale, 0))}</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB OFFICINA */}
        {activeTab === 'officina' && (
          <FinanceOfficinaTab
            interventi={interventiOfficina}
            fMese={fMese}
            fAnno={fAnno}
            T={T}
            isMobile={isMobile}
          />
        )}

        {/* TAB CONTO ECONOMICO */}
        {activeTab === 'conto' && puoVedereContoEconomico(user) && (
          <ContoEconomicoView
            contratti={filtered}
            interventi={interventiOfficina}
            T={T}
            isMobile={isMobile}
            db={db}
          />
        )}
        {activeTab === 'xml' && (
          <FattureOfficinaXMLView
            interventi={interventiOfficina.filter(i => {
              const d = new Date(i.creatoAt || i.data || '');
              if (fAnno && d.getFullYear() !== parseInt(fAnno)) return false;
              if (fMese && d.getMonth() + 1 !== parseInt(fMese)) return false;
              return true;
            })}
            T={T}
            isMobile={isMobile}
            user={user}
          />
        )}

      </>)}

      {permutaStockModal && (
        <PermutaToStockModal contratto={permutaStockModal} user={user} onClose={() => setPermutaStockModal(null)}
          onSalvato={(veicoloId) => {
            setContratti(prev => prev.map(c => c.id === permutaStockModal.id ? { ...c, prezzo: { ...c.prezzo, permutaInStockId: veicoloId } } : c));
            setPermutaStockModal(null);
          }} />
      )}
    </div>
  );
}

// ─── FATTURE OFFICINA XML ────────────────────────────────────────────────────
function FattureOfficinaXMLView({ interventi, T, isMobile, user }) {
  const [filtroMese, setFiltroMese] = React.useState('');
  const [filtroAnno, setFiltroAnno] = React.useState('');
  const [search, setSearch] = React.useState('');

  const anni = React.useMemo(() => {
    const s = new Set((interventi||[]).map(i => (i.creatoAt||i.data||'').slice(0,4)).filter(Boolean));
    return [...s].sort((a,b)=>b-a);
  }, [interventi]);

  const filtrate = React.useMemo(() => {
    return (interventi||[]).filter(inv => {
      const data = inv.creatoAt || inv.data || '';
      if (filtroAnno && !data.startsWith(filtroAnno)) return false;
      if (filtroMese && !(data.slice(5,7) === filtroMese.padStart(2,'0'))) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        return (inv.nomeCliente||'').toLowerCase().includes(s) ||
               (inv.targa||'').toLowerCase().includes(s) ||
               (inv.id||'').toLowerCase().includes(s);
      }
      return true;
    }).sort((a,b)=>(b.creatoAt||b.data||'').localeCompare(a.creatoAt||a.data||''));
  }, [interventi, filtroAnno, filtroMese, search]);

  const fmtE = n => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(n||0);
  const fmtD = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const iS = { padding:'8px 12px', background:T.inputBg||T.bg, border:`1px solid ${T.border}`, borderRadius:'8px', color:T.text, fontSize:'12px', outline:'none' };

  return (
    <div>
      {/* Filtri */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', marginBottom:'16px', alignItems:'center' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Cliente, targa, ID..."
          style={{ ...iS, flex:1, minWidth:'160px' }} />
        <select value={filtroAnno} onChange={e=>setFiltroAnno(e.target.value)} style={iS}>
          <option value="">Tutti gli anni</option>
          {anni.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroMese} onChange={e=>setFiltroMese(e.target.value)} style={iS}>
          <option value="">Tutti i mesi</option>
          {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m,i)=>(
            <option key={m} value={m}>{new Date(2000,i,1).toLocaleString('it-IT',{month:'long'})}</option>
          ))}
        </select>
        <div style={{ fontSize:'11px', color:T.textMuted||T.textSub }}>
          {filtrate.length} fatture
        </div>
      </div>

      {/* Lista fatture */}
      <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
        {filtrate.map(inv => (
          <div key={inv.id} style={{ background:T.surface||T.card, borderRadius:'10px', border:`1px solid ${T.border}`, padding:'10px 14px', display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center' }}>
            <div style={{ flex:1, minWidth:'150px' }}>
              <div style={{ fontSize:'10px', color:'#10b981', fontWeight:'900' }}>{inv.id}</div>
              <div style={{ fontSize:'13px', fontWeight:'800', color:T.text }}>{inv.nomeCliente||'—'}</div>
              <div style={{ fontSize:'11px', color:T.textMuted||T.textSub, marginTop:'2px' }}>
                {inv.targa && <span style={{marginRight:'8px'}}>🏷 {inv.targa}</span>}
                {inv.marca && <span>{inv.marca} {inv.modello}</span>}
              </div>
            </div>
            <div style={{ textAlign:'right', marginRight:'8px' }}>
              <div style={{ fontSize:'14px', fontWeight:'900', color:'#10b981' }}>{fmtE(inv.totaleLordo)}</div>
              <div style={{ fontSize:'10px', color:T.textMuted||T.textSub }}>{fmtD(inv.creatoAt||inv.data)}</div>
            </div>
            <button onClick={()=>generaXMLFatturaAggiornata(inv)}
              style={{ padding:'8px 16px', borderRadius:'8px', border:'2px solid #10b981', background:'rgba(16,185,129,0.1)', color:'#10b981', fontWeight:'900', fontSize:'11px', cursor:'pointer', whiteSpace:'nowrap' }}>
              📄 Scarica XML
            </button>
          </div>
        ))}
        {filtrate.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px', color:T.textMuted||T.textSub, fontSize:'13px' }}>
            Nessuna fattura trovata
          </div>
        )}
      </div>
    </div>
  );
}


// ─── CONTO ECONOMICO AUTO ────────────────────────────────────────────────────
function ContoEconomicoView({ contratti, interventi, T, isMobile, db }) {
  const [odlCosti, setOdlCosti] = useState(window._radarOdlCostiList || []);
  useEffect(() => { caricaOdlPerCosti().then(setOdlCosti).catch(() => {}); }, []);
  const [costiAcquisto, setCostiAcquisto] = useState({});
  const [costiCarrozzeria, setCostiCarrozzeria] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editingCosto, setEditingCosto] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  const [extraFields, setExtraFields] = useState({}); // {key: {provvFin, commServizi}}

  // Se la cache stock è vuota (tab Stock mai aperta in questa sessione), la
  // scarichiamo noi in automatico — una sola volta — così i valori (bottom
  // price, cilindrata, ecc.) sono sempre corretti senza dover prima passare
  // da Stock.
  const [stockCacheTick, setStockCacheTick] = useState(0);
  useEffect(() => {
    if (!window._radarStockCache?.vehicles) {
      get(ref(db, 'stock_data')).then(snap => {
        if (snap.val()?.vehicles) {
          window._radarStockCache = snap.val();
          window._radarStockTs = Date.now();
          setStockCacheTick(t => t + 1); // forza il ricalcolo del conto economico
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    get(ref(db, 'conto_economico_acquisti')).then(snap => {
      const data = snap.val() || {};
      const costi = {};
      const extra = {};
      Object.entries(data).forEach(([k, v]) => {
        if (typeof v === 'object') {
          // Vedi commento nella stessa logica in FinanceView — non
          // trattare "mai impostato" come zero esplicito, altrimenti il
          // fallback al bottom price smette di funzionare.
          if (v.costoAcquisto !== undefined) costi[k] = v.costoAcquisto;
          extra[k] = { provvFin: v.provvFin || 0, commServizi: v.commServizi || 0, costiVari: v.costiVari || 0, costoPolizza: v.costoPolizza || 0, costoGaranzia: v.costoGaranzia };
        } else {
          costi[k] = v;
        }
      });
      setCostiAcquisto(costi);
      setExtraFields(extra);
    });
    get(ref(db, 'service_carrozzeria')).then(snap => {
      const data = snap.val();
      if (data) setCostiCarrozzeria(Object.values(data).filter(s => s.importo > 0));
      else setCostiCarrozzeria([]);
    });
  }, []);

  const saveCostoAcquisto = async (vin, targa, veicoloId, valore, contrattoId) => {
    const key = (vin || targa || veicoloId || '').replace(/[^a-zA-Z0-9]/g, '_');
    if (!key) { alert('Impossibile salvare: nessuna chiave veicolo disponibile'); return; }
    try {
      const val = await sincronizzaPrezzoAcquisto({ vin, targa, veicoloId, valore, contrattoId });
      setCostiAcquisto(p => ({ ...p, [key]: val }));
    } catch(e) { alert('Errore salvataggio: ' + e.message); }
  };

  const saveExtraField = async (key, field, valore, contrattoId) => {
    if (!key) return;
    const val = parseFloat(valore) || 0;
    const existing = extraFields[key] || {};
    const newExtra = { ...existing, [field]: val };
    setExtraFields(p => ({ ...p, [key]: newExtra }));
    // update() invece di set(): tocca solo il campo modificato, non
    // riscrive/azzera mai costoAcquisto (che può essere gestito da
    // saveCostoAcquisto in un momento diverso, o derivare dal bottom price
    // live dello stock e non avere mai avuto un override manuale qui).
    await update(ref(db, `conto_economico_acquisti/${key}`), { [field]: val });
  };

  // Costruisce il conto economico per ogni veicolo con contratto approvato
  const contiEconomici = useMemo(() => {
    // Dal più recente al meno recente — confronto sicuro contro date
    // mancanti/invalide, altrimenti un solo contratto senza data può
    // sballare l'ordine di tutti gli altri (stesso bug corretto ieri per
    // le liste Contratti).
    const dataOrdinamentoCE = c => { const t = new Date(c.dataCreazione || 0).getTime(); return isNaN(t) ? 0 : t; };
    const contrattiOrdinati = [...contratti].sort((a, b) => dataOrdinamentoCE(b) - dataOrdinamentoCE(a));
    return contrattiOrdinati.map(c => {
      const v = c.veicolo || {};
      const vin = v.vin || '';
      const targa = v.targa || '';
      const veicoloId = v.id || '';
      const key = (vin || targa || veicoloId).replace(/[^a-zA-Z0-9]/g, '_');

      // Prezzo vendita dal contratto — prezzoListino è l'imponibile: quando
      // il contratto ha IVA esposta, il prezzo di vendita reale (quello
      // incassato) è imponibile + IVA, va sempre sommata qui.
      const pr = c.prezzo || {};
      const s = c.servizi || {};
      // L'estinzione anticipata è un costo che il cliente paga per chiudere
      // il vecchio finanziamento — un passaggio verso terzi (la finanziaria),
      // non margine reale per l'azienda. Va sempre esclusa dal calcolo del
      // prezzo di vendita usato per il conto economico.
      const totS = sommaCostiServiziNoEstinzione(c.servizi);
      const _pLce = parseFloat(pr.prezzoListino||v.prezzo||0);
      const _scCe = parseFloat(pr.sconto||0);
      const _hasIVAce = !!pr.hasIVA;
      const _ivaCe = _hasIVAce ? Math.round((_pLce - _scCe) * 0.22) : 0;
      // IPT / passaggio di proprietà: il cliente la paga ma va al PRA, non è
      // ricavo dell'azienda → esclusa dal prezzo di vendita del conto economico.
      // Gli optional casa madre invece sono venduti al cliente → inclusi.
      const _accCMce = (c.servizi?.accessoriCasaMadreList || []).reduce((t, a) => t + (parseFloat(a.prezzo) || 0), 0);
      const prezzoVendita = (_pLce - _scCe + _accCMce) + totS + _ivaCe;

      // Lookup live nello stock — v (c.veicolo) è uno snapshot congelato al
      // momento della creazione del contratto: campi aggiunti DOPO (come la
      // cilindrata) non ci sono mai, anche se lo stock viene aggiornato in
      // seguito. Usato sia per il costo garanzia (cilindrata/km/marca) sia
      // per il bottom price.
      const stockCandidatiBP2 = (window._radarStockCache?.vehicles || []).filter(sv2 => (v.id && sv2.id === v.id) || (v.targa && sv2.targa === v.targa) || (vin && sv2.vin === vin));
      const stockLiveBP2 = stockCandidatiBP2.find(sv2 => parseFloat(sv2.bottomPrice) > 0) || stockCandidatiBP2[0];

      // Costo servizi — costo REALE per l'azienda dei dispositivi/servizi
      // installati (diverso dal prezzo fatturato al cliente, già dentro
      // totServizi/prezzoVendita): LoJack 476€, Blockshaft 182€, Metasystem
      // (rilevato dal testo libero "Altro antifurto") 85€. Sempre sottratto
      // dal margine.
      const costoServizi = (s.lojack ? 476 : 0) + (s.blockshaft ? 182 : 0) + (/metasystem/i.test(s.altroAntifurto || '') ? 85 : 0) + (s.serviceCare ? 100 : 0);

      // Costo Garanzia — calcolato automaticamente dalla tabella Mawdy in
      // base a cilindrata, età/km e marca del veicolo, per la durata scelta
      // nel contratto (garanziaEstesaMesi). Preferisce sempre i dati live
      // dello stock (stockLiveBP2) a quelli congelati nel contratto, dato
      // che la cilindrata potrebbe non essere mai stata salvata all'epoca.
      // Resta sempre modificabile a mano nel riepilogo, anche quando il
      // calcolo automatico ha successo.
      const diagGaranzia = diagnosticaCostoGaranziaMawdy(
        stockLiveBP2?.cc || v.cc || estraiCilindrataDaVersione(stockLiveBP2?.versione || v.versione) || estraiCilindrataDaVersione(stockLiveBP2?.modello || v.modello) || estraiCilindrataDaVersione(stockLiveBP2?.optionals || v.optionals),
        stockLiveBP2?.registrazione || v.registrazione,
        stockLiveBP2?.km || v.km,
        stockLiveBP2?.marca || v.marca,
        rilevaGaranziaMawdy(c).mesi,
        c.dataCreazione
      );
      const costoGaranziaAuto = diagGaranzia.valore;

      // Costo acquisto — priorità: conto_economico_acquisti (override manuale) > stock live
      // (aggiornato dopo la creazione del contratto, es. tramite import Excel) > valore
      // congelato nel contratto/veicolo al momento della sua creazione.
      // Legge 104: i prezzi bottom sono calcolati con IVA al 22%. Se il
      // veicolo è stato venduto con Legge 104 (IVA ridotta al 4%), il costo
      // va uniformato — si scorpora il 22% (÷1.22) e si riapplica il 4%
      // (×1.04) — altrimenti il margine risulterebbe falsato confrontando
      // un prezzo di vendita a IVA 4% con un costo calcolato a IVA 22%.
      // Applicato SOLO al valore automatico: un override manuale salvato
      // in conto_economico_acquisti resta come l'ha inserito l'utente, per
      // rispettare una decisione esplicita già presa.
      const bottomPriceGrezzoCE = parseFloat(stockLiveBP2?.bottomPrice || c.bottomPrice || c.veicolo?.bottomPrice || 0);
      const bottomPriceUniformatoCE = pr.legge104 ? (bottomPriceGrezzoCE / 1.22) * 1.04 : bottomPriceGrezzoCE;
      const costoAcquisto = costiAcquisto[key] !== undefined ? parseFloat(costiAcquisto[key]) : bottomPriceUniformatoCE;

      // Interventi officina su questo veicolo: fatture + ODL non fatturati
      // (match su telaio o targa normalizzati, senza doppi conteggi)
      const interventiVeicolo = documentiOfficinaVeicolo({ vin, targa, fatture: interventi, odl: odlCosti });

      // Costi carrozzeria su questo veicolo — l'importo salvato è l'imponibile:
      // qui va sempre riportato al lordo IVA 22%, altrimenti si confronta un
      // prezzo auto (eventualmente lordo IVA) con un costo carrozzeria al netto.
      const interventiCarrozzeria = costiCarrozzeria.filter(s => {
        if (vin && s.telaio === vin) return true;
        if (targa && s.targa === targa) return true;
        return false;
      });
      const totaleCarrozzeria = interventiCarrozzeria.reduce((sum, s) => sum + (parseFloat(s.importo) || 0) * 1.22, 0);

      // Costi ricambi dettagliati dagli interventi
      const costiRicambi = [];
      let totaleOfficina = 0;
      interventiVeicolo.forEach(inv => {
        (inv.righe || []).forEach(r => {
          const subtot = (parseFloat(r.qta)||0) * (parseFloat(r.prezzoUnit)||0) / fattoreScorporo(inv);
          costiRicambi.push({
            data: inv.data, tipo: r.tipo, codice: r.codice, descrizione: r.descrizione,
            qta: r.qta, prezzoUnit: r.prezzoUnit, subtot, interventoNote: inv.note,
            origine: inv._origine === 'odl' ? `ODL ${inv.id}` : `Fattura ${inv.numeroFattura || inv.id}`,
          });
          totaleOfficina += subtot;
        });
      });

      // Campi extra (provvigione finanziaria e commissione servizi)
      const provvFin = parseFloat(extraFields[key]?.provvFin || c.datiExtra?.provvigioneFinanziaria || 0);
      const commServizi = parseFloat(extraFields[key]?.commServizi || 0);
      const costiVari = parseFloat(extraFields[key]?.costiVari || 0);
      const costoPolizza = parseFloat(extraFields[key]?.costoPolizza || 0);
      // Se è già stato salvato un valore manuale, vince quello; altrimenti
      // si usa il calcolo automatico (può comunque essere 0/null se non
      // disponibile — in quel caso il campo resta vuoto, da compilare a mano).
      const costoGaranziaSalvato = extraFields[key]?.costoGaranzia;
      const costoGaranzia = costoGaranziaSalvato !== undefined && costoGaranziaSalvato !== '' && costoGaranziaSalvato !== null
        ? parseFloat(costoGaranziaSalvato || 0)
        : (costoGaranziaAuto || 0);

      const prezzoVettura = _pLce - _scCe + _accCMce + _ivaCe;   // senza IPT
      // prezzoVendita totale = ferro + IPT - sconto + servizi (già calcolato sopra)
      const totCosti = costoAcquisto + costiVari + totaleOfficina + totaleCarrozzeria + costoServizi + costoPolizza + costoGaranzia;
      const margine = prezzoVendita - costoAcquisto - costiVari - totaleOfficina - totaleCarrozzeria - costoServizi - costoPolizza - costoGaranzia + provvFin + commServizi;
      const marginePerc = prezzoVendita > 0 ? (margine / prezzoVendita * 100) : 0;

      return {
        id: c.id,
        vin, targa, veicoloId, key,
        marca: v.marca, modello: v.modello, versione: v.versione,
        mainImg: v.mainImg,
        dataVendita: c.datiExtra?.dataFirma || c.dataCreazione,
        cliente: c.cliente?.ragioneSociale || `${c.cliente?.nome||''} ${c.cliente?.cognome||''}`.trim(),
        venditore: c.venditoreNome,
        prezzoVettura, prezzoVendita, totServizi: totS,
        costoAcquisto, costiVari, costoServizi, costoPolizza, costoGaranzia, costoGaranziaAuto, garanziaMotivo: diagGaranzia.motivo,
        legge104: !!pr.legge104,
        totaleOfficina, totaleCarrozzeria,
        provvFin, commServizi,
        interventiCarrozzeria, costiRicambi, totCosti,
        margine, marginePerc,
        numInterventi: interventiVeicolo.length,
      };
    });
  }, [contratti, interventi, odlCosti, costiAcquisto, costiCarrozzeria, extraFields, stockCacheTick]);

  const filtered = useMemo(() => {
    if (!search.trim()) return contiEconomici;
    const s = search.toLowerCase();
    return contiEconomici.filter(c =>
      c.marca?.toLowerCase().includes(s) ||
      c.modello?.toLowerCase().includes(s) ||
      c.targa?.toLowerCase().includes(s) ||
      c.vin?.toLowerCase().includes(s) ||
      c.cliente?.toLowerCase().includes(s)
    );
  }, [contiEconomici, search]);

  const totMargine = contiEconomici.reduce((s, c) => s + c.margine, 0);
  const totVendite = contiEconomici.reduce((s, c) => s + c.prezzoVendita, 0);
  const totCosti   = contiEconomici.reduce((s, c) => s + c.totCosti, 0);

  return (
    <div>
      {/* KPI globali */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile?2:4}, 1fr)`, gap: '10px', marginBottom: '16px' }}>
        {[
          ['Veicoli venduti', contiEconomici.length, '#3b82f6'],
          ['Fatturato totale', fmtE(totVendite), '#10b981'],
          ['Costi totali', fmtE(totCosti), '#ef4444'],
          ['Margine totale', fmtE(totMargine), totMargine >= 0 ? '#10b981' : '#ef4444'],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background: T.surface, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '12px 14px', borderTop: `3px solid ${c}` }}>
            <div style={{ fontSize: '8px', color: T.textMuted, fontWeight: '700', marginBottom: '3px' }}>{l}</div>
            <div style={{ fontSize: isMobile?'14px':'17px', fontWeight: '900', color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <input placeholder="🔍 Cerca veicolo, targa, cliente..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '10px 14px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '12px', color: T.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box', marginBottom: '14px' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filtered.map(c => (
          <div key={c.id} style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${c.margine >= 0 ? '#10b98130' : '#ef444430'}`, overflow: 'hidden' }}>
            {/* Header */}
            <div onClick={() => setSelected(selected?.id === c.id ? null : c)}
              style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = T.bg + '80'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {c.mainImg && <img src={c.mainImg} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: '60px', height: '42px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>{c.marca} {c.modello}</div>
                <div style={{ fontSize: '10px', color: T.textMuted }}>
                  🏷 {c.targa||'—'} · {c.vin ? `🔑 ···${c.vin.slice(-6)}` : ''} · {c.cliente}
                </div>
                <div style={{ fontSize: '10px', color: T.textMuted }}>
                  {c.dataVendita ? new Date(c.dataVendita).toLocaleDateString('it-IT') : '—'} · {c.venditore}
                </div>
              </div>
              {/* Mini conto */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: '10px', marginBottom: '4px' }}>
                  <span style={{ color: T.textMuted }}>Vendita</span>
                  <span style={{ color: '#10b981', fontWeight: '700' }}>{fmtE(c.prezzoVendita)}</span>
                  <span style={{ color: T.textMuted }}>Costi</span>
                  <span style={{ color: '#ef4444', fontWeight: '700' }}>- {fmtE(c.totCosti)}</span>
                </div>
                <div style={{ fontSize: '15px', fontWeight: '900', color: c.margine >= 0 ? '#10b981' : '#ef4444' }}>
                  {c.margine >= 0 ? '+' : ''}{fmtE(c.margine)}
                </div>
                <div style={{ fontSize: '9px', color: T.textMuted }}>{c.marginePerc.toFixed(1)}% margine</div>
              </div>
            </div>

            {/* Dettaglio espandibile */}
            {selected?.id === c.id && (
              <div style={{ borderTop: `1px solid ${T.border}`, padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                  {/* Colonna sinistra: voci */}
                  <div>
                    {/* Costo acquisto editabile */}
                    <div style={{ background: T.bg, borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '900', marginBottom: '6px' }}>COSTO ACQUISTO</div>
                      {editingCosto === c.id ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input type="number" autoFocus
                            value={editingValue}
                            onChange={e => setEditingValue(e.target.value)}
                            placeholder="Es. 12000"
                            style={{ flex: 1, padding: '7px 10px', background: T.inputBg, border: '1px solid #6366f1', borderRadius: '8px', color: T.text, fontSize: '13px', outline: 'none' }}
                            onKeyDown={async e => {
                              if (e.key === 'Enter') {
                                await saveCostoAcquisto(c.vin, c.targa, c.veicoloId, editingValue, c.id);
                                setEditingCosto(null);
                              }
                              if (e.key === 'Escape') setEditingCosto(null);
                            }}
                            onBlur={async () => {
                              await saveCostoAcquisto(c.vin, c.targa, c.veicoloId, editingValue, c.id);
                              setEditingCosto(null);
                            }}
                          />
                        </div>
                      ) : (
                        <div onClick={() => { setEditingCosto(c.id); setEditingValue(String(c.costoAcquisto || '')); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}
                          title="Clicca per modificare">
                          <div>
                            <span style={{ fontSize: '16px', fontWeight: '900', color: '#ef4444' }}>- {fmtE(c.costoAcquisto)}</span>
                            {c.legge104 && (
                              <div style={{ fontSize: '9px', color: '#3b82f6', fontWeight: '700', marginTop: '2px' }}>(acquisto con legge 104)</div>
                            )}
                          </div>
                          <span style={{ fontSize: '10px', color: '#6366f1' }}>✎ modifica</span>
                        </div>
                      )}
                    </div>

                    {/* Costi officina */}
                    {c.costiRicambi.length > 0 && (
                      <div style={{ background: T.bg, borderRadius: '10px', padding: '12px' }}>
                        <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '900', marginBottom: '8px' }}>COSTI OFFICINA ({c.numInterventi} interventi)</div>
                        {c.costiRicambi.map((r, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < c.costiRicambi.length-1 ? `1px solid ${T.border}` : 'none', gap: '8px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {r.codice && r.codice !== 'MAN' && <div style={{ fontSize: '8px', color: '#6366f1', fontWeight: '700' }}>{r.codice}</div>}
                              <div style={{ fontSize: '11px', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.descrizione}</div>
                              <div style={{ fontSize: '9px', color: T.textMuted }}>{r.qta} {r.tipo === 'manodopera' ? 'h' : 'pz'} × {fmtE(r.prezzoUnit)}{r.origine ? ` · ${r.origine}` : ''}</div>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#ef4444', flexShrink: 0 }}>- {fmtE(r.subtot)}</div>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: `2px solid ${T.border}` }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: T.textSub }}>Totale officina</span>
                          <span style={{ fontSize: '13px', fontWeight: '900', color: '#ef4444' }}>- {fmtE(c.totaleOfficina)}</span>
                        </div>
                      </div>
                    )}
                    {c.costiRicambi.length === 0 && (
                      <div style={{ background: T.bg, borderRadius: '10px', padding: '12px', textAlign: 'center', color: T.textMuted, fontSize: '11px' }}>
                        Nessun intervento officina registrato
                      </div>
                    )}

                    {/* Costi carrozzeria */}
                    {c.interventiCarrozzeria?.length > 0 && (
                      <div style={{ background: T.bg, borderRadius: '10px', padding: '12px', marginTop: '10px' }}>
                        <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '900', marginBottom: '8px' }}>COSTI CARROZZERIA ({c.interventiCarrozzeria.length} schede)</div>
                        {c.interventiCarrozzeria.map((s, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < c.interventiCarrozzeria.length-1 ? `1px solid ${T.border}` : 'none', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '11px', color: T.text }}>{s.azAzienda || '—'}</div>
                              <div style={{ fontSize: '9px', color: T.textMuted }}>{s.intervento ? s.intervento.slice(0, 60) : '—'} · {s.dataOdl || ''}</div>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b', flexShrink: 0 }}>- {fmtE(s.importo)}</div>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: `2px solid ${T.border}` }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: T.textSub }}>Totale carrozzeria</span>
                          <span style={{ fontSize: '13px', fontWeight: '900', color: '#f59e0b' }}>- {fmtE(c.totaleCarrozzeria)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Colonna destra: riepilogo */}
                  <div>
                    <div style={{ background: T.bg, borderRadius: '10px', padding: '14px' }}>
                      <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '900', marginBottom: '10px' }}>RIEPILOGO ECONOMICO</div>
                      {[
                        ['Prezzo Vettura (netto sconto + optional C.M., IPT esclusa)', c.prezzoVettura, '#10b981', '+'],
                        ...(c.totServizi > 0 ? [['Servizi Venduti', c.totServizi, '#10b981', '+']] : []),
                        ['Costo Acquisto' + (c.legge104 ? ' (acquisto con legge 104)' : ''), c.costoAcquisto, '#ef4444', '-'],
                        ...(c.costoServizi > 0 ? [['Costo Servizi (LoJack/Blockshaft/Metasystem/Service Care)', c.costoServizi, '#ef4444', '-']] : []),
                      ].map(([l,v,col,sign]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: '11px', color: T.textSub }}>{l}</span>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: col }}>{sign} {fmtE(v)}</span>
                        </div>
                      ))}
                      {/* Costi vari — editabile */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ fontSize: '11px', color: '#f59e0b' }}>− Costi vari</span>
                        <input type="number" defaultValue={extraFields[c.key]?.costiVari||''} placeholder="0"
                          onWheel={e=>e.preventDefault()}
                          onBlur={async e => { await saveExtraField(c.key, 'costiVari', e.target.value, c.id); }}
                          style={{ width: '100px', padding: '4px 8px', background: T.inputBg, border: '1px solid #f59e0b50', borderRadius: '6px', color: '#f59e0b', fontSize: '12px', fontWeight: '700', outline: 'none', textAlign: 'right' }}
                        />
                      </div>
                      {/* Costo polizza — editabile */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ fontSize: '11px', color: '#f59e0b' }}>− Costo polizza</span>
                        <input type="number" defaultValue={extraFields[c.key]?.costoPolizza||''} placeholder="0"
                          onWheel={e=>e.preventDefault()}
                          onBlur={async e => { await saveExtraField(c.key, 'costoPolizza', e.target.value, c.id); }}
                          style={{ width: '100px', padding: '4px 8px', background: T.inputBg, border: '1px solid #f59e0b50', borderRadius: '6px', color: '#f59e0b', fontSize: '12px', fontWeight: '700', outline: 'none', textAlign: 'right' }}
                        />
                      </div>
                      {/* Costo garanzia — prepopolato dal calcolo automatico (tabella Mawdy: cilindrata + età/km + marca + durata), sempre modificabile */}
                      <div style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', color: '#f59e0b' }}>
                            − Costo garanzia
                            {extraFields[c.key]?.costoGaranzia === undefined && c.costoGaranziaAuto > 0 && (
                              <span title="Calcolato automaticamente da tabella Mawdy (cilindrata, età/km, marca, durata)" style={{ marginLeft: '5px', fontSize: '9px', color: '#8b5cf6', fontWeight: '900' }}>AUTO</span>
                            )}
                          </span>
                          <input type="number" defaultValue={c.costoGaranzia || ''} placeholder="0"
                            onWheel={e=>e.preventDefault()}
                            onBlur={async e => { await saveExtraField(c.key, 'costoGaranzia', e.target.value, c.id); }}
                            style={{ width: '100px', padding: '4px 8px', background: T.inputBg, border: '1px solid #f59e0b50', borderRadius: '6px', color: '#f59e0b', fontSize: '12px', fontWeight: '700', outline: 'none', textAlign: 'right' }}
                          />
                        </div>
                        {extraFields[c.key]?.costoGaranzia === undefined && !(c.costoGaranziaAuto > 0) && (
                          <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '3px', fontStyle: 'italic' }}>ℹ️ {c.garanziaMotivo}</div>
                        )}
                      </div>
                      {[
                        ['Costi Officina', c.totaleOfficina, '#ef4444', '-'],
                        ['Costi Carrozzeria', c.totaleCarrozzeria, '#f59e0b', '-'],
                      ].map(([l,v,col,sign]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: '11px', color: T.textSub }}>{l}</span>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: col }}>{sign} {fmtE(v)}</span>
                        </div>
                      ))}
                      {/* Provvigione finanziaria — editabile */}
                      {[
                        ['provvFin', 'Provvigione finanziaria', c.provvFin],
                        ['commServizi', 'Commissione servizi aggiuntivi', c.commServizi],
                      ].map(([field, label, val]) => (
                        <div key={field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: '11px', color: '#6366f1' }}>+ {label}</span>
                          <input type="number" defaultValue={val||''} placeholder="0"
                            onWheel={e=>e.preventDefault()}
                            onBlur={async e => {
                              await saveExtraField(c.key, field, e.target.value, c.id);
                            }}
                            style={{ width: '100px', padding: '4px 8px', background: T.inputBg, border: '1px solid #6366f150', borderRadius: '6px', color: '#6366f1', fontSize: '12px', fontWeight: '700', outline: 'none', textAlign: 'right' }}
                          />
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', marginTop: '4px', borderTop: `2px solid ${T.border}` }}>
                        <span style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>MARGINE</span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '20px', fontWeight: '900', color: c.margine >= 0 ? '#10b981' : '#ef4444' }}>
                            {c.margine >= 0 ? '+' : ''}{fmtE(c.margine)}
                          </div>
                          <div style={{ fontSize: '10px', color: T.textMuted }}>{c.marginePerc.toFixed(1)}% sul venduto</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── FINANCE OFFICINA TAB ────────────────────────────────────────────────────
function FinanceOfficinaTab({ interventi, fMese, fAnno, T, isMobile }) {
  const mesiMap = {'1':'GENNAIO','2':'FEBBRAIO','3':'MARZO','4':'APRILE','5':'MAGGIO','6':'GIUGNO','7':'LUGLIO','8':'AGOSTO','9':'SETTEMBRE','10':'OTTOBRE','11':'NOVEMBRE','12':'DICEMBRE'};
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  const filtered = useMemo(() => interventi.filter(inv => {
    const d = new Date(inv.data);
    if (fAnno && d.getFullYear() !== parseInt(fAnno)) return false;
    if (fMese && d.getMonth() + 1 !== parseInt(fMese)) return false;
    return true;
  }), [interventi, fMese, fAnno]);

  const perAnno = useMemo(() => {
    const m = {};
    interventi.forEach(inv => {
      const anno = new Date(inv.data).getFullYear();
      if (!m[anno]) m[anno] = { count: 0, totale: 0 };
      m[anno].count++; m[anno].totale += inv.totaleLordo || 0;
    });
    return Object.entries(m).sort((a,b) => b[0]-a[0]);
  }, [interventi]);

  const perMese = useMemo(() => {
    const base = fAnno ? interventi.filter(inv => new Date(inv.data).getFullYear() === parseInt(fAnno)) : interventi;
    const m = {};
    base.forEach(inv => {
      const d = new Date(inv.data);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      const label = `${Object.values(mesiMap)[d.getMonth()]} ${d.getFullYear()}`;
      if (!m[key]) m[key] = { label, count: 0, totale: 0 };
      m[key].count++; m[key].totale += inv.totaleLordo || 0;
    });
    return Object.entries(m).sort((a,b) => b[0].localeCompare(a[0])).slice(0, 12);
  }, [interventi, fAnno]);

  const perGiorno = useMemo(() => {
    if (!fMese || !fAnno) return [];
    const m = {};
    filtered.forEach(inv => {
      const key = new Date(inv.data).toLocaleDateString('it-IT');
      if (!m[key]) m[key] = { count: 0, totale: 0 };
      m[key].count++; m[key].totale += inv.totaleLordo || 0;
    });
    return Object.entries(m).sort((a,b) => {
      const da = a[0].split('/').reverse().join('-');
      const db2 = b[0].split('/').reverse().join('-');
      return new Date(db2) - new Date(da);
    });
  }, [filtered, fMese, fAnno]);

  const totFiltered = filtered.reduce((s, inv) => s + (inv.totaleLordo || 0), 0);

  const TableBlock = ({ title, rows }) => (
    <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}`, background: T.bg }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '1px' }}>{title}</div>
      </div>
      <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
        {rows.length === 0 ? <div style={{ padding: '20px', textAlign: 'center', color: T.textMuted, fontSize: '12px' }}>Nessun dato</div> :
        rows.map(([key, d]) => (
          <div key={key} style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{d.label || key}</div>
              <div style={{ fontSize: '10px', color: T.textMuted }}>{d.count} interventi</div>
            </div>
            <div style={{ fontSize: '13px', fontWeight: '900', color: '#10b981' }}>{fmtE(d.totale)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[['Interventi', filtered.length, '#6366f1'], ['Fatturato Officina', fmtE(totFiltered), '#10b981'], ['Media intervento', filtered.length > 0 ? fmtE(totFiltered/filtered.length) : '—', '#f59e0b']].map(([l,v,c]) => (
          <div key={l} style={{ background: T.surface, borderRadius: '14px', border: `1px solid ${T.border}`, padding: '14px 16px', borderTop: `3px solid ${c}` }}>
            <div style={{ fontSize: '9px', color: T.textMuted, fontWeight: '700', marginBottom: '4px' }}>{l}</div>
            <div style={{ fontSize: '18px', fontWeight: '900', color: c }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${perGiorno.length > 0 ? 3 : 2}, 1fr)`, gap: '16px' }}>
        <TableBlock title="PER ANNO" rows={perAnno.map(([a,d]) => [a, { ...d, label: a }])} />
        <TableBlock title={`PER MESE${fAnno ? ` (${fAnno})` : ''}`} rows={perMese} />
        {perGiorno.length > 0 && <TableBlock title={`PER GIORNO (${mesiMap[fMese]} ${fAnno})`} rows={perGiorno.map(([k,d]) => [k, { ...d, label: k }])} />}
      </div>
    </div>
  );
}

function Section({ title, color, children }) {
  const theme = useTheme(); const T = THEMES[theme];
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{ width: '3px', height: '14px', background: color, borderRadius: '2px' }} />
        <div style={{ fontSize: '9px', fontWeight: '900', color, letterSpacing: '1px' }}>{title}</div>
      </div>
      <div style={{ background: T.bg, borderRadius: '12px', padding: '12px 16px' }}>{children}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  const theme = useTheme(); const T = THEMES[theme];
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: '13px', gap: '12px' }}>
      <span style={{ color: T.textSub, flexShrink: 0 }}>{label}</span>
      <span style={{ color: bold ? '#10b981' : T.text, fontWeight: bold ? '800' : '600', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ─── VEHICLE MODAL ────────────────────────────────────────────────────────────
function VehicleModal({ car, onClose, onPreventivo, inTrattativa, cognomeTrattativa = '', preventivi = [], user }) {
  const [imgIdx, setImgIdx] = useState(0);
  const tabBarRef = useRef(null);
  const [activeTab, setActiveTab] = useState('dettagli');
  const theme = useTheme(); const T = THEMES[theme];
  const [nuovaNota, setNuovaNota] = useState('');
  const [savingNota, setSavingNota] = useState(false);
  // Stato locale per bottom price e fornitore — riflette subito la
  // modifica in questo modale dopo il salvataggio, senza dover aspettare
  // che il resto dell'app si aggiorni.
  const [bottomPriceLocale, setBottomPriceLocale] = useState(car.bottomPrice);
  const [fornitoreLocale, setFornitoreLocale] = useState(car.fornitore);
  const [ivaEspostaLocale, setIvaEspostaLocale] = useState(car.ivaEsposta);
  const carConLocali = { ...car, bottomPrice: bottomPriceLocale, fornitore: fornitoreLocale, ivaEsposta: ivaEspostaLocale };
  const [noteLocali, setNoteLocali] = useState(car.noteTelaio || []);
  const isAdminNote = ['admin','super_admin','logistica','back_office'].includes((user?.ruolo||'').toLowerCase());
  // Azioni — stessa identica logica delle Note (compilabili allo stesso modo),
  // ma su un nodo Firebase separato, per tenerle distinte come categoria.
  const [azioniLocali, setAzioniLocali] = useState(car.azioniTelaio || []);
  const [nuovaAzione, setNuovaAzione] = useState('');
  const [savingAzione, setSavingAzione] = useState(false);

  // Carica note in tempo reale da Firebase (path separato dalla cache stock)
  React.useEffect(() => {
    if (!car.id) return;
    const unsubNote = onValue(ref(db, `note_telaio/${car.id}`), snap => {
      const data = snap.val();
      const arr = data ? (Array.isArray(data) ? data : Object.values(data)) : [];
      setNoteLocali(arr);
      car.noteTelaio = arr; // aggiorna anche l'oggetto per il badge
    });
    return () => unsubNote();
  }, [car.id]);

  // Filtra preventivi per questo veicolo (per VIN o targa)
  const prevVeicolo = useMemo(() => {
    if (!preventivi.length) return [];
    return preventivi.filter(p => {
      const vin = (p.veicolo?.vin || p.vin || '').toUpperCase();
      const targa = (p.veicolo?.targa || p.targa || '').toUpperCase();
      const carVin = (car.vin || '').toUpperCase();
      const carTarga = (car.targa || '').toUpperCase();
      return (carVin && vin === carVin) || (carTarga && targa === carTarga);
    }).sort((a,b) => new Date(b.data||b.createdAt||0) - new Date(a.data||a.createdAt||0));
  }, [preventivi, car]);

  // Storico movimentazioni (DDT) per questo specifico veicolo — match su VIN o targa
  const [movimentiVeicolo, setMovimentiVeicolo] = useState([]);
  useEffect(() => {
    const carVin = (car.vin || '').toUpperCase().trim();
    const carTarga = (car.targa || '').toUpperCase().trim();
    if (!carVin && !carTarga) { setMovimentiVeicolo([]); return; }
    const filtraEProiettaMovimenti = (data) => Object.values(data || {}).filter(d =>
      (toArraySafe(d.veicoli)).some(v => (carVin && (v.vin || '').toUpperCase().trim() === carVin) || (carTarga && (v.targa || '').toUpperCase().trim() === carTarga))
    ).sort((a, b) => new Date(b.data) - new Date(a.data));
    // Stesso principio già applicato altrove oggi: cronologia storica di un
    // singolo veicolo, non serve aggiornamento istantaneo — cache
    // condivisa TTL 4 ore invece di onValue live + scarico completo di
    // TUTTI i DDT ogni volta che si apre il dettaglio di un'auto.
    let annullato = false;
    const now = Date.now();
    if (window._radarDdtTs && (now - window._radarDdtTs) < 4*60*60*1000) {
      setMovimentiVeicolo(filtraEProiettaMovimenti(window._radarDdtCache));
      return;
    }
    idbLeggi('ddt_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarDdtCache = cache.data; window._radarDdtTs = cache.ts;
        setMovimentiVeicolo(filtraEProiettaMovimenti(cache.data));
        return;
      }
      get(ref(db, 'ddt')).then(snap => {
        if (annullato) return;
        const data = snap.val() || {};
        const ts = Date.now();
        window._radarDdtCache = data; window._radarDdtTs = ts;
        idbScrivi('ddt_cache', { data, ts });
        setMovimentiVeicolo(filtraEProiettaMovimenti(data));
      });
    });
    return () => { annullato = true; };
  }, [car.vin, car.targa]);

  const tabs = [
    { id: 'dettagli',   label: '📋 Dettagli', color: '#6366f1' },
    { id: 'accessori',  label: '⚙️ Accessori', color: '#8b5cf6', hide: !car.optionals },
    { id: 'foto',       label: `📷 Foto${car.images?.length ? ` (${car.images.length})` : ''}`, color: '#ec4899', hide: !car.images?.length },
    { id: 'preventivi', label: `📄 Preventivi${prevVeicolo.length ? ` (${prevVeicolo.length})` : ''}`, color: '#3b82f6' },
    { id: 'movimenti',  label: '🚚 Movimenti', color: '#0ea5e9' },
    { id: 'documenti',  label: '📎 Documenti', color: '#14b8a6' },
    { id: 'azioni',     label: `✅ Azioni${car.azioniTelaio?.length ? ` (${car.azioniTelaio.length})` : ''}`, color: '#10b981' },
    { id: 'note',       label: `🗒️ Note${car.noteTelaio?.length ? ` (${car.noteTelaio.length})` : ''}`, color: '#f59e0b' },
  ].filter(t => !t.hide);

  const optionalsList = car.optionals
    ? car.optionals.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: '24px', border: `1px solid ${T.border}`, width: '100%', maxWidth: '760px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* HEADER */}
        <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '9px', color: '#3b82f6', fontWeight: '900', letterSpacing: '2px', marginBottom: '4px' }}>{car.tipo} · {car.classe?.toUpperCase()}</div>
              <h2 style={{ margin: 0, color: T.text, fontSize: '20px', fontWeight: '900' }}>{car.marca} {car.modello}</h2>
              <div style={{ color: T.textSub, fontSize: '12px', marginTop: '3px' }}>{car.versione}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button onClick={() => generaSchedaVeicolo(car)} title="Genera scheda veicolo stampabile"
                style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid #3b82f6', color: '#3b82f6', borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
                🪧 Scheda
              </button>
              <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontWeight: '900', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>×</button>
            </div>
          </div>

          {/* TAB BAR — scorrevole orizzontalmente: su mobile i tab in
              eccedenza (Movimenti, Note, Documenti, Azioni) restavano tagliati
              fuori dallo schermo e irraggiungibili SENZA nessun indizio
              visivo che ce ne fossero altri. Ora due frecce ai lati rendono
              esplicito che si può scorrere, e scorrono la barra al click. */}
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: `2px solid ${T.border}` }}>
            <button onClick={() => tabBarRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
              style={{ background: T.bg, border: 'none', color: T.textSub, cursor: 'pointer', padding: '8px 6px', fontSize: '13px', fontWeight: '900', flexShrink: 0 }}>
              ‹
            </button>
            <div ref={tabBarRef} style={{ display: 'flex', gap: '4px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '9px 16px', fontSize: '11px', fontWeight: '900', border: 'none', borderRadius: '10px 10px 0 0', cursor: 'pointer', background: activeTab === t.id ? `${t.color}18` : 'transparent', color: activeTab === t.id ? t.color : T.textSub, borderBottom: activeTab === t.id ? `3px solid ${t.color}` : '3px solid transparent', marginBottom: '-2px', transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={() => tabBarRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
              style={{ background: T.bg, border: 'none', color: T.textSub, cursor: 'pointer', padding: '8px 6px', fontSize: '13px', fontWeight: '900', flexShrink: 0 }}>
              ›
            </button>
          </div>
        </div>

        {/* CONTENUTO SCROLLABILE */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

          {/* TAB DETTAGLI */}
          {activeTab === 'dettagli' && (
            <>
              {car.images && car.images.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <img src={car.images[imgIdx]} alt="" style={{ width: '100%', borderRadius: '12px', height: '220px', objectFit: 'cover' }} />
                  {car.images.length > 1 && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                      {car.images.slice(0, 5).map((img, i) => <img key={i} src={img} alt="" onClick={() => setImgIdx(i)} style={{ width: '52px', height: '38px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', border: i === imgIdx ? '2px solid #3b82f6' : `2px solid ${T.border}`, flexShrink: 0 }} />)}
                    </div>
                  )}
                </div>
              )}
              {(() => {
                // Se "giorni" manca (veicolo entrato in stock tramite un
                // percorso che non lo imposta, es. Permuta→Stock), calcolo
                // un valore di riserva dalla data di ingresso — invece di
                // mostrare "undefined gg".
                const giorniVisualizzati = car.giorni !== undefined && car.giorni !== null
                  ? car.giorni
                  : (car.enteredInStock ? Math.floor((Date.now() - new Date(car.enteredInStock).getTime()) / 86400000) : null);
                return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {[
                  ['Prezzo', fmt(user?.ruolo === 'partner' && car.prezzoPartner > 0 ? car.prezzoPartner : car.prezzo) + (user?.ruolo === 'partner' && car.prezzoPartner > 0 ? ' ★' : ''), '#10b981'],
                  ['Chilometri', fmtN(car.km) + ' km', '#3b82f6'],
                  ['Carburante', car.carburante, '#8b5cf6'],
                  ['Tipologia', car.tipo, '#f59e0b'],
                  ['Colore', car.colore, '#ec4899'],
                  ['Immatricolazione', car.registrazione, '#f59e0b'],
                  ['Giorni in stock', giorniVisualizzati !== null ? giorniVisualizzati + ' gg' : 'N.D.', giorniVisualizzati > 200 ? '#ef4444' : giorniVisualizzati > 150 ? '#f59e0b' : giorniVisualizzati > 90 ? '#8b5cf6' : '#10b981'],
                  ['Sede', car.sede, T.textSub],
                  ['Targa', car.targa || 'N.D.', T.textSub],
                  ['Telaio (VIN)', car.vin || '—', T.textSub],
                  ['Status', car.status === 'RESERVED' ? 'PRENOTATA' : car.status, car.status === 'RESERVED' ? '#f59e0b' : car.status === 'FREE' ? '#10b981' : '#6b7280'],
                  ...(['finance', 'admin', 'super_admin'].includes((user?.ruolo||'').toLowerCase().trim()) ? [['Regime IVA', carConLocali.ivaEsposta === undefined ? 'Non impostato — clicca per scegliere' : (carConLocali.ivaEsposta ? 'IVA Esposta' : 'Art. 36 (Margine)'), carConLocali.ivaEsposta === false ? '#f59e0b' : carConLocali.ivaEsposta === true ? '#10b981' : T.textSub, async () => {
                    // Il cambio si "blocca" (ivaEspostaLocked) cosi la
                    // prossima sincronizzazione StockSpark non lo sovrascrive
                    // piu — stesso meccanismo gia usato per le correzioni
                    // manuali fatte oggi via script.
                    const nuovoValore = !carConLocali.ivaEsposta; // undefined -> true (IVA Esposta) la prima volta
                    const testoStato = nuovoValore ? 'IVA Esposta' : 'Art. 36 (Margine)';
                    if (!window.confirm(`Impostare il regime IVA di questo veicolo su "${testoStato}"?\n\nQuesta scelta resta salvata in RadaR e non verrà più sovrascritta dalla sincronizzazione automatica.`)) return;
                    try {
                      const snapStock = await get(ref(db, 'stock_data/vehicles'));
                      const vehicles = snapStock.val() || [];
                      const idx = vehicles.findIndex(sv => (car.vin && sv.vin === car.vin) || (car.targa && sv.targa === car.targa) || sv.id === car.id);
                      if (idx === -1) { alert('Veicolo non trovato nello stock.'); return; }
                      await set(ref(db, `stock_data/vehicles/${idx}/ivaEsposta`), nuovoValore);
                      await set(ref(db, `stock_data/vehicles/${idx}/ivaEspostaLocked`), true);
                      setIvaEspostaLocale(nuovoValore);
                    } catch (e) { alert('Errore: ' + e.message); }
                  }]] : (car.ivaEsposta !== undefined ? [['Regime IVA', car.ivaEsposta ? 'IVA Esposta' : 'Art. 36 (Margine)', car.ivaEsposta ? '#10b981' : '#f59e0b']] : [])),
                  ...(['finance', 'admin', 'super_admin'].includes((user?.ruolo||'').toLowerCase().trim()) ? [['Prezzo d\'Acquisto', carConLocali.bottomPrice > 0 ? fmt(carConLocali.bottomPrice) : 'N.D.', '#ef4444', async () => {
                    const attuale = carConLocali.bottomPrice ? String(carConLocali.bottomPrice) : '';
                    const nuovo = window.prompt('Nuovo prezzo d\'acquisto (bottom price):', attuale);
                    if (nuovo === null) return;
                    const valore = parseFloat(nuovo.replace(',', '.'));
                    if (isNaN(valore) || valore < 0) { alert('Inserisci un numero valido.'); return; }
                    try {
                      await sincronizzaPrezzoAcquisto({ vin: car.vin, targa: car.targa, veicoloId: car.id, valore });
                      setBottomPriceLocale(valore);
                    } catch (e) { alert('Errore: ' + e.message); }
                  }]] : []),
                  ...(['finance', 'admin', 'super_admin', 'back_office'].includes((user?.ruolo||'').toLowerCase().trim()) ? [['Fornitore', carConLocali.fornitore || 'N.D.', '#8b5cf6', async () => {
                    const attuale = carConLocali.fornitore || '';
                    const nuovo = window.prompt('Nuovo fornitore per questo veicolo:', attuale);
                    if (nuovo === null) return;
                    try {
                      const key = (car.vin || car.targa || car.id || '').replace(/[^a-zA-Z0-9]/g, '_');
                      if (!key) { alert('Impossibile salvare: nessuna chiave veicolo disponibile'); return; }
                      const snapStock = await get(ref(db, 'stock_data/vehicles'));
                      const vehicles = snapStock.val() || [];
                      const idx = vehicles.findIndex(sv => (car.vin && sv.vin === car.vin) || (car.targa && sv.targa === car.targa) || sv.id === car.id);
                      if (idx === -1) { alert('Veicolo non trovato nello stock.'); return; }
                      await set(ref(db, `stock_data/vehicles/${idx}/fornitore`), nuovo.trim());
                      setFornitoreLocale(nuovo.trim());
                    } catch (e) { alert('Errore: ' + e.message); }
                  }]] : []),
                ].map(([label, val, color, onClickAzione]) => (
                  <div key={label} onClick={onClickAzione} style={{ background: T.bg, borderRadius: '10px', padding: '10px 12px', cursor: onClickAzione ? 'pointer' : 'default', border: onClickAzione ? `1px dashed ${color}60` : 'none' }}>
                    <div style={{ fontSize: '8px', color: T.textMuted, fontWeight: '900', marginBottom: '3px' }}>{label}{onClickAzione ? ' ✏️' : ''}</div>
                    <div style={{ fontSize: '12px', fontWeight: '900', color }}>{String(val || 'N.D.').toUpperCase()}</div>
                  </div>
                ))}
              </div>
                );
              })()}
            </>
          )}

          {/* TAB ACCESSORI */}
          {activeTab === 'accessori' && (
            <div>
              <div style={{ fontSize: '11px', color: T.textMuted, marginBottom: '16px', fontWeight: '600' }}>
                {optionalsList.length} accessori rilevati dal feed
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {optionalsList.map((opt, i) => (
                  <div key={i} style={{ padding: '7px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '20px', fontSize: '11px', fontWeight: '600', color: T.text }}>
                    {opt}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB FOTO */}
          {activeTab === 'foto' && (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <img src={car.images[imgIdx]} alt="" style={{ width: '100%', borderRadius: '12px', height: '260px', objectFit: 'cover' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                {car.images.map((img, i) => (
                  <img key={i} src={img} alt="" onClick={() => setImgIdx(i)} style={{ width: '100%', height: '72px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: i === imgIdx ? '2px solid #3b82f6' : `2px solid ${T.border}` }} />
                ))}
              </div>
            </div>
          )}

          {/* TAB PREVENTIVI */}
          {/* TAB NOTE */}
          {activeTab === 'note' && (
            <div style={{ padding: '4px 0' }}>
              {/* Nota automatica KM0 — generata dal sistema, non eliminabile */}
              {(car.tipo || '').toLowerCase().trim() === 'km0' && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: 'rgba(245,158,11,0.1)', borderRadius: '10px', padding: '10px 14px', border: '1px solid rgba(245,158,11,0.35)', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', flexShrink: 0 }}>🧾</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '800', lineHeight: 1.5 }}>Bollo da pagare</div>
                    <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '4px' }}>Nota automatica · veicolo KM0</div>
                  </div>
                </div>
              )}
              {/* Lista note esistenti */}
              {noteLocali.length > 0 ? (
                <ul style={{ margin: '0 0 16px 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {noteLocali.map((nota, i) => (
                    <li key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: T.bg, borderRadius: '10px', padding: '10px 14px', border: `1px solid ${T.border}` }}>
                      <span style={{ color: '#6366f1', fontWeight: '900', fontSize: '14px', flexShrink: 0 }}>•</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: T.text, lineHeight: 1.5 }}>{nota.testo}</div>
                        <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '4px' }}>
                          {nota.autore} · {nota.data ? new Date(nota.data).toLocaleDateString('it-IT') : '—'}
                        </div>
                      </div>
                      {isAdminNote && (
                        <button onClick={async () => {
                          const snap = await get(ref(db, `note_telaio/${car.id}`));
                          const curr = snap.val() || [];
                          const updated = curr.filter((_, idx) => idx !== i);
                          await set(ref(db, `note_telaio/${car.id}`), updated);
                          window._radarNoteTelaioTs = 0;
                          car.noteTelaio = updated;
                          setNoteLocali([...updated]);
                        }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '0 4px', flexShrink: 0 }} title="Elimina nota">✕</button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px', color: T.textMuted, fontSize: '12px' }}>
                  Nessuna nota per questo veicolo
                </div>
              )}

              {/* Input nuova nota — solo admin */}
              {isAdminNote && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input
                    value={nuovaNota}
                    onChange={e => setNuovaNota(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && nuovaNota.trim()) {
                        setSavingNota(true);
                        const snap = await get(ref(db, `note_telaio/${car.id}`));
                        const curr = snap.val() || [];
                        const nuovaEntry = { testo: nuovaNota.trim(), autore: `${user?.nome||''} ${user?.cognome||''}`.trim() || user?.email || 'Admin', data: new Date().toISOString() };
                        const updated = [...curr, nuovaEntry];
                        await set(ref(db, `note_telaio/${car.id}`), updated);
                        window._radarNoteTelaioTs = 0;
                        if (!car.noteTelaio) car.noteTelaio = [];
                        car.noteTelaio.push(nuovaEntry);
                        setNoteLocali(prev => [...prev, nuovaEntry]);
                        setNuovaNota('');
                        setSavingNota(false);
                      }
                    }}
                    placeholder="Scrivi una nota e premi Invio..."
                    style={{ flex: 1, background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', padding: '10px 14px', color: T.text, fontSize: '12px', outline: 'none' }}
                  />
                  <button
                    disabled={savingNota || !nuovaNota.trim()}
                    onClick={async () => {
                      if (!nuovaNota.trim()) return;
                      setSavingNota(true);
                      const snap = await get(ref(db, `note_telaio/${car.id}`));
                      const curr = snap.val() || [];
                      const nuovaEntry = { testo: nuovaNota.trim(), autore: `${user?.nome||''} ${user?.cognome||''}`.trim() || user?.email || 'Admin', data: new Date().toISOString() };
                      const updated = [...curr, nuovaEntry];
                      await set(ref(db, `note_telaio/${car.id}`), updated);
                      window._radarNoteTelaioTs = 0;
                      if (!car.noteTelaio) car.noteTelaio = [];
                      car.noteTelaio.push(nuovaEntry);
                      setNoteLocali(prev => [...prev, nuovaEntry]);
                      setNuovaNota('');
                      setSavingNota(false);
                    }}
                    style={{ padding: '10px 16px', background: savingNota || !nuovaNota.trim() ? T.border : '#6366f1', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '900', cursor: savingNota || !nuovaNota.trim() ? 'default' : 'pointer', fontSize: '12px', flexShrink: 0 }}>
                    {savingNota ? '⏳' : '+ Aggiungi'}
                  </button>
                </div>
              )}
              {!isAdminNote && (
                <div style={{ fontSize: '9px', color: T.textMuted, textAlign: 'center', marginTop: '8px' }}>🔒 Solo admin, logistica e back office possono aggiungere o rimuovere note</div>
              )}
            </div>
          )}

          {activeTab === 'documenti' && (
            <DocumentiSection db={db} user={user} T={T}
              percorsoFirebase={`documenti_veicoli/${car.id}`}
              cartellaR2={`veicoli/${car.id}`} />
          )}

          {activeTab === 'azioni' && (
            <div style={{ padding: '4px 0' }}>
              {/* Lista azioni esistenti — stessa identica logica delle Note */}
              {azioniLocali.length > 0 ? (
                <ul style={{ margin: '0 0 16px 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {azioniLocali.map((azione, i) => (
                    <li key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: T.bg, borderRadius: '10px', padding: '10px 14px', border: `1px solid ${T.border}` }}>
                      <span style={{ color: '#10b981', fontWeight: '900', fontSize: '14px', flexShrink: 0 }}>✅</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: T.text, lineHeight: 1.5 }}>{azione.testo}</div>
                        <div style={{ fontSize: '9px', color: T.textMuted, marginTop: '4px' }}>
                          {azione.autore} · {azione.data ? new Date(azione.data).toLocaleDateString('it-IT') : '—'}
                        </div>
                      </div>
                      {isAdminNote && (
                        <button onClick={async () => {
                          const snap = await get(ref(db, `azioni_telaio/${car.id}`));
                          const curr = snap.val() || [];
                          const updated = curr.filter((_, idx) => idx !== i);
                          await set(ref(db, `azioni_telaio/${car.id}`), updated);
                          car.azioniTelaio = updated;
                          setAzioniLocali([...updated]);
                        }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '0 4px', flexShrink: 0 }} title="Elimina azione">✕</button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px', color: T.textMuted, fontSize: '12px' }}>
                  Nessuna azione per questo veicolo
                </div>
              )}

              {/* Input nuova azione — stesso accesso delle Note */}
              {isAdminNote && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input
                    value={nuovaAzione}
                    onChange={e => setNuovaAzione(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && nuovaAzione.trim()) {
                        setSavingAzione(true);
                        const snap = await get(ref(db, `azioni_telaio/${car.id}`));
                        const curr = snap.val() || [];
                        const nuovaEntry = { testo: nuovaAzione.trim(), autore: `${user?.nome||''} ${user?.cognome||''}`.trim() || user?.email || 'Admin', data: new Date().toISOString() };
                        const updated = [...curr, nuovaEntry];
                        await set(ref(db, `azioni_telaio/${car.id}`), updated);
                        if (!car.azioniTelaio) car.azioniTelaio = [];
                        car.azioniTelaio.push(nuovaEntry);
                        setAzioniLocali(prev => [...prev, nuovaEntry]);
                        setNuovaAzione('');
                        setSavingAzione(false);
                      }
                    }}
                    placeholder="Scrivi un'azione e premi Invio..."
                    style={{ flex: 1, background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', padding: '10px 14px', color: T.text, fontSize: '12px', outline: 'none' }}
                  />
                  <button
                    disabled={savingAzione || !nuovaAzione.trim()}
                    onClick={async () => {
                      if (!nuovaAzione.trim()) return;
                      setSavingAzione(true);
                      const snap = await get(ref(db, `azioni_telaio/${car.id}`));
                      const curr = snap.val() || [];
                      const nuovaEntry = { testo: nuovaAzione.trim(), autore: `${user?.nome||''} ${user?.cognome||''}`.trim() || user?.email || 'Admin', data: new Date().toISOString() };
                      const updated = [...curr, nuovaEntry];
                      await set(ref(db, `azioni_telaio/${car.id}`), updated);
                      if (!car.azioniTelaio) car.azioniTelaio = [];
                      car.azioniTelaio.push(nuovaEntry);
                      setAzioniLocali(prev => [...prev, nuovaEntry]);
                      setNuovaAzione('');
                      setSavingAzione(false);
                    }}
                    style={{ padding: '10px 16px', background: savingAzione || !nuovaAzione.trim() ? T.border : '#10b981', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '900', cursor: savingAzione || !nuovaAzione.trim() ? 'default' : 'pointer', fontSize: '12px', flexShrink: 0 }}>
                    {savingAzione ? '⏳' : '+ Aggiungi'}
                  </button>
                </div>
              )}
              {!isAdminNote && (
                <div style={{ fontSize: '9px', color: T.textMuted, textAlign: 'center', marginTop: '8px' }}>🔒 Solo admin, logistica e back office possono aggiungere o rimuovere azioni</div>
              )}
            </div>
          )}

          {activeTab === 'preventivi' && (
            <div>
              {prevVeicolo.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: T.textMuted }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📄</div>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>Nessun preventivo per questa vettura</div>
                  <div style={{ fontSize: '11px', marginTop: '4px' }}>I preventivi vengono associati per VIN o targa</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {prevVeicolo.map((p, i) => {
                    const data = p.data || p.createdAt;
                    const cliente = p.cliente?.nome ? `${p.cliente.nome} ${p.cliente.cognome||''}`.trim() : p.cliente?.ragioneSociale || '—';
                    const prezzo = parseFloat(p.prezzo?.prezzoConcordato || p.prezzo?.prezzoListino || 0);
                    const stato = p.stato || 'bozza';
                    const statoColor = stato === 'convertito' ? '#10b981' : stato === 'approvato' ? '#3b82f6' : '#f59e0b';
                    return (
                      <div key={p.id || i} style={{ background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontSize: '10px', fontWeight: '900', color: statoColor, background: `${statoColor}20`, padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>{stato}</span>
                              <span style={{ fontSize: '10px', color: T.textMuted }}>{data ? new Date(data).toLocaleDateString('it-IT') : '—'}</span>
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: '800', color: T.text }}>{cliente}</div>
                            <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>
                              Venditore: {p.venditoreNome || '—'}
                            </div>
                            {p.servizi && Object.entries(p.servizi).filter(([k,v]) => v && k !== 'costoMawdy36' && !k.startsWith('costo')).map(([k]) => k).length > 0 && (
                              <div style={{ fontSize: '10px', color: '#6366f1', marginTop: '4px' }}>
                                🔧 {Object.entries(p.servizi).filter(([k,v]) => v && !k.startsWith('costo')).map(([k]) => k).join(', ')}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '16px', fontWeight: '900', color: '#10b981' }}>
                              {prezzo > 0 ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(prezzo) : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'movimenti' && (
            <div>
              {movimentiVeicolo.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: T.textMuted }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>🚚</div>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>Nessuna movimentazione registrata</div>
                  <div style={{ fontSize: '11px', marginTop: '4px' }}>Lo storico si popola quando questo veicolo viene spostato tramite un DDT</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {movimentiVeicolo.map((d, i) => (
                    <div key={d.id || i} style={{ background: T.bg, borderRadius: '12px', border: `1px solid ${T.border}`, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '900', color: '#0ea5e9' }}>{d.numero}</span>
                            <span style={{ fontSize: '10px', color: T.textMuted }}>{new Date(d.data).toLocaleDateString('it-IT')}</span>
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: T.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            📍 {d.sedeOrigine} <span style={{ color: T.textMuted }}>→</span> {d.sedeDestinazione}
                          </div>
                          <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>{d.causale} {d.creatoDaNome ? `· da ${d.creatoDaNome}` : ''}</div>
                        </div>
                        <button onClick={() => generaStampaDDT(d)}
                          style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #0ea5e9', background: 'rgba(14,165,233,0.1)', color: '#0ea5e9', cursor: 'pointer', fontWeight: '800', fontSize: '10px', flexShrink: 0 }}>
                          🖨️ DDT
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          {(() => {
            if (user?.ruolo === 'osservatore') return (
              <div style={{ width: '100%', padding: '13px', background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: '12px', color: '#64748b', fontWeight: '900', fontSize: '13px', textAlign: 'center' }}>
                👁 Sola consultazione
              </div>
            );
            const stato = (car.status || '').toUpperCase();
            if (stato === 'VENDUTA' || stato === 'SOLD') return (
              <div style={{ width: '100%', padding: '13px', background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: '12px', color: '#64748b', fontWeight: '900', fontSize: '13px', textAlign: 'center' }}>
                🚫 VENDUTA — Non preventivabile
              </div>
            );
            if (stato === 'CONTRATTUALIZZATA') {
              const puoPreventivareContrattualizzata = ['admin','super_admin'].includes((user?.ruolo||'').toLowerCase().trim());
              return (
                <div>
                  {puoPreventivareContrattualizzata ? (
                    <button onClick={() => onPreventivo(car)} style={{ width: '100%', padding: '13px', background: 'rgba(139,92,246,0.12)', border: '1px solid #8b5cf6', borderRadius: '12px', color: '#8b5cf6', cursor: 'pointer', fontWeight: '900', fontSize: '13px' }}>
                      📝 CONTRATTUALIZZATA — Preventiva Ugualmente
                    </button>
                  ) : (
                    <div style={{ width: '100%', padding: '13px', background: 'rgba(139,92,246,0.12)', border: '1px solid #8b5cf6', borderRadius: '12px', color: '#8b5cf6', fontWeight: '900', fontSize: '13px', textAlign: 'center' }}>
                      📝 CONTRATTUALIZZATA
                    </div>
                  )}
                  {(car.consulenteNome || car.clienteNome) && (
                    <div style={{ fontSize: '11px', color: '#8b5cf6', textAlign: 'center', marginTop: '5px', fontWeight: '700' }}>
                      👤 {car.clienteNome || ''}{car.consulenteNome ? ` — ${car.consulenteNome}` : ''}
                    </div>
                  )}
                </div>
              );
            }
            if (car.bloccoAdmin) return (
              <div>
                <button onClick={() => onPreventivo(car)} style={{ width: '100%', padding: '13px', background: 'rgba(220,38,38,0.12)', border: '1px solid #dc2626', borderRadius: '12px', color: '#dc2626', cursor: 'pointer', fontWeight: '900', fontSize: '13px' }}>
                  🔒 BLOCCATA DA {String(car.bloccoAdmin.nome || 'ADMIN').toUpperCase()} — Preventiva Ugualmente
                </button>
                <div style={{ fontSize: '11px', color: '#dc2626', textAlign: 'center', marginTop: '5px', fontWeight: '700' }}>
                  {car.bloccoAdmin.ruolo === 'super_admin' ? 'Super admin' : 'Admin'}
                  {car.bloccoAdmin.at ? ` · dal ${new Date(car.bloccoAdmin.at).toLocaleDateString('it-IT')}` : ''}
                  {car.bloccoAdmin.nota ? ` · ${car.bloccoAdmin.nota}` : ''}
                </div>
              </div>
            );
            if (inTrattativa) return (
              <div>
                <button onClick={() => onPreventivo(car)} style={{ width: '100%', padding: '13px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '12px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '13px' }}>
                  🔒 IN TRATTATIVA — Preventiva Ugualmente
                </button>
                {cognomeTrattativa && <div style={{ fontSize: '11px', color: '#ef4444', textAlign: 'center', marginTop: '5px', fontWeight: '700' }}>👤 {cognomeTrattativa}</div>}
              </div>
            );
            if (stato === 'PRENOTATA' || stato === 'RESERVED' || car._prenotata) return (
              <button onClick={() => onPreventivo(car)} style={{ width: '100%', padding: '13px', background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', borderRadius: '12px', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '13px' }}>
                🔒 PRENOTATA — Preventiva Ugualmente
              </button>
            );
            return (
              <button onClick={() => onPreventivo(car)} style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '12px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '13px', letterSpacing: '0.5px' }}>
                📋 CREA PREVENTIVO PER QUESTA VETTURA
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── COMPONENTI CONDIVISI ─────────────────────────────────────────────────────
// ─── FASCE GIACENZA ───────────────────────────────────────────────────────────
function FasceGiacenza({ filtered }) {
  const theme = useTheme(); const T = THEMES[theme];
  const tot = filtered.length || 1;
  const fasce = [
    ['0 – 90 giorni',    filtered.filter(v => v.giorni <= 90).length,                    '#10b981'],
    ['91 – 150 giorni',  filtered.filter(v => v.giorni > 90  && v.giorni <= 150).length, '#8b5cf6'],
    ['151 – 200 giorni', filtered.filter(v => v.giorni > 150 && v.giorni <= 200).length, '#f59e0b'],
    ['Oltre 200 giorni', filtered.filter(v => v.giorni > 200).length,                    '#ef4444'],
  ];
  return (
    <div style={{ background: T.surface, padding: '20px', borderRadius: '25px', border: `1px solid ${T.border}` }}>
      <h4 style={{ margin: '0 0 15px 0', fontSize: '11px', color: '#f59e0b', fontWeight: '900' }}>FASCE GIACENZA</h4>
      {fasce.map(([label, val, color]) => (
        <div key={label} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '5px' }}>
            <span style={{ color: T.textSub }}>{label}</span>
            <b style={{ color }}>{val} unità</b>
          </div>
          <div style={{ height: '6px', background: T.chartBg, borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(val/tot)*100}%`, background: color, borderRadius: '10px', transition: 'width 0.6s ease' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const RadarStat = ({ label, value, sub, color, compact }) => {
  const theme = useTheme(); const T = THEMES[theme];
  return (
    <div style={{ background: T.surface, padding: compact ? '14px 12px' : '20px', borderRadius: '20px', border: `1px solid ${T.border}`, borderTop: `4px solid ${color}` }}>
      <div style={{ color: T.textMuted, fontSize: '8px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: compact ? '18px' : '22px', fontWeight: '900', color: T.text, letterSpacing: '-0.5px' }}>{value}</div>
      <div style={{ color: color, fontSize: '9px', marginTop: '4px', fontWeight: '700' }}>{sub}</div>
    </div>
  );
};

const RadarChart = ({ title, data, color }) => {
  const theme = useTheme(); const T = THEMES[theme];
  return (
    <div style={{ background: T.surface, padding: '20px', borderRadius: '25px', border: `1px solid ${T.border}` }}>
      <h4 style={{ margin: '0 0 15px 0', fontSize: '11px', color: color, fontWeight: '900' }}>{title.toUpperCase()}</h4>
      {data.map(([label, val]) => (
        <div key={label} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '5px' }}>
            <span style={{ color: T.textSub }}>{String(label || 'N.D.').toUpperCase()}</span>
            <b style={{ color: T.text }}>{fmtN(val)}</b>
          </div>
          <div style={{ height: '4px', background: T.chartBg, borderRadius: '10px' }}>
            <div style={{ height: '100%', width: `${(val/(data[0][1]||1))*100}%`, background: color, borderRadius: '10px' }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB ANALISI — grafici riservati ad admin e super admin
// Usa solo dati già scaricati (contratti globali + stock in cache):
// nessuna lettura in più su Firebase, salvo lo stock se non è ancora in cache.
// ════════════════════════════════════════════════════════════════════════════
const MESI_BREVI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const normTipoVeicolo = t => {
  const x = String(t || '').toLowerCase();
  if (/km\s*0|km0|zero/.test(x)) return 'Km0';
  if (/nuov|new/.test(x)) return 'Nuovo';
  if (/usat|used/.test(x)) return 'Usato';
  return t ? String(t) : 'N.D.';
};
const annoDa = v => { const m = String(v || '').match(/(19|20)\d{2}/); return m ? m[0] : ''; };
// Marca unificata per l'analisi: "FIAT", "fiat" e "Fiat Panda" → "Fiat".
// Prima si riconoscono le marche note (anche di più parole: Land Rover,
// Alfa Romeo, DS Automobiles…), altrimenti si prende la prima parola.
const MARCHE_NOTE_ANALISI = ['DR','Iveco','Alfa Romeo','Abarth','Audi','BMW','BYD','Citroen','Citroën','Cupra','Dacia','DR Automobiles','DS Automobiles','DS','EVO','Ferrari','Fiat','Ford','Honda','Hyundai','ICH-X','Ineos','Isuzu','Jaecoo','Jaguar','Jeep','Kia','Lamborghini','Lancia','Land Rover','Leapmotor','Lexus','Lynk & Co','Maserati','Mazda','Mercedes-Benz','Mercedes','MG','Mini','Mitsubishi','Nissan','Omoda','Opel','Peugeot','Polestar','Porsche','Renault','Seat','Skoda','Smart','Sportequipe','Subaru','Suzuki','Tesla','Tiger','Toyota','Volkswagen','Volvo','BMW Motorrad','Piaggio','Yamaha','Ducati','KTM','Kymco','Benelli'];
const normMarcaAnalisi = (() => {
  const strip = x => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const note = [...MARCHE_NOTE_ANALISI].sort((a, b) => b.length - a.length).map(m => [strip(m), m.replace('Citroën', 'Citroen').replace(/^Mercedes$/, 'Mercedes-Benz').replace(/^DS$/, 'DS Automobiles').replace(/^DR$/, 'DR Automobiles')]);
  return raw => {
    const x = strip(raw);
    if (!x || x === 'n.d.') return 'N.D.';
    const trovata = note.find(([k]) => x === k || x.startsWith(k + ' ') || x.startsWith(k + '-'));
    if (trovata) return trovata[1];
    const prima = x.split(' ')[0];
    return prima.charAt(0).toUpperCase() + prima.slice(1);
  };
})();

// Stato condiviso della pagina ANALISI: vista mobile e "espandi tutto"
const AnalisiUiCtx = React.createContext({ isMobile: false, tuttiAperti: null });
function useLarghezzaFinestra() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => { const f = () => setW(window.innerWidth); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return w;
}

// Variazione rispetto al periodo di confronto: "▲ +12,3% (+5)" in piccolo.
// meglio = 'su' (aumento positivo, verde) o 'giu' (diminuzione positiva)
function Variazione({ cur, prev, fmt = v => String(Math.round(v)), meglio = 'su', T }) {
  if (prev === null || prev === undefined || !isFinite(prev)) return null;
  const diff = (cur || 0) - (prev || 0);
  const perc = prev ? (diff / Math.abs(prev)) * 100 : null;
  const buono = diff === 0 ? null : ((diff > 0) === (meglio === 'su'));
  const col = buono === null ? (T?.textMuted || '#9ca3af') : buono ? '#10b981' : '#ef4444';
  const freccia = diff > 0 ? '▲' : diff < 0 ? '▼' : '=';
  const segno = diff > 0 ? '+' : diff < 0 ? '−' : '';
  return (
    <div style={{ fontSize: '10px', fontWeight: '800', color: col, marginTop: '3px' }}>
      {freccia} {perc === null ? 'n.d.' : `${segno}${Math.abs(perc).toFixed(1).replace('.', ',')}%`} ({segno}{fmt(Math.abs(diff))})
      <span style={{ color: T?.textMuted || '#9ca3af', fontWeight: '600' }}> vs {fmt(prev || 0)}</span>
    </div>
  );
}

// Riquadro compatto: titolo + valore riassuntivo; il contenuto si apre con la freccia
function SezioneEstendibile({ T, titolo, riepilogo, coloreRiepilogo, children }) {
  const ui = useContext(AnalisiUiCtx);
  const [aperto, setAperto] = useState(!!ui.tuttiAperti);
  useEffect(() => { if (ui.tuttiAperti !== null) setAperto(ui.tuttiAperti); }, [ui.tuttiAperti]);
  return (
    <div style={{ background: T.surface || T.card, border: `1px solid ${T.border}`, borderRadius: '14px' }}>
      <button onClick={() => setAperto(a => !a)}
        aria-expanded={aperto}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: ui.isMobile ? '14px 14px' : '14px 18px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', minHeight: '48px' }}>
        <span style={{ color: T.textMuted, fontSize: '13px', width: '14px', transition: 'transform 0.15s', transform: aperto ? 'rotate(90deg)' : 'none' }}>▸</span>
        <span style={{ flex: 1, fontSize: ui.isMobile ? '13px' : '14px', fontWeight: '800', color: T.text, lineHeight: 1.3 }}>{titolo}</span>
        {riepilogo !== undefined && <span style={{ fontSize: ui.isMobile ? '14px' : '16px', fontWeight: '900', color: coloreRiepilogo || T.text, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{riepilogo}</span>}
      </button>
      {aperto && <div style={{ padding: '0 16px 14px' }}>{children}</div>}
    </div>
  );
}

function GraficoBarre({ T, titolo, sottotitolo, dati, formato, colore = '#3b82f6', serie2, colore2 = '#ef4444', legenda, riepilogo, coloreRiepilogo }) {
  const ui = useContext(AnalisiUiCtx);
  // Su mobile il disegno è più stretto: testi a dimensione reale, non rimpiccioliti
  const W = ui.isMobile ? 360 : 720, H = ui.isMobile ? 220 : 240, padL = 8, padR = 8, padT = 28, padB = 28;
  const fs = ui.isMobile ? 10 : 11;
  const mostraValori = !ui.isMobile || dati.length <= 8;
  const valori = dati.flatMap(d => [d.v, serie2 ? d.v2 : 0]).filter(v => isFinite(v));
  const max = Math.max(1, ...valori);
  const n = dati.length || 1;
  const slot = (W - padL - padR) / n;
  const bw = serie2 ? Math.min(18, slot * 0.36) : Math.min(30, slot * 0.6);
  const y = v => padT + (H - padT - padB) * (1 - (v || 0) / max);
  return (
    <SezioneEstendibile T={T} titolo={titolo} riepilogo={riepilogo} coloreRiepilogo={coloreRiepilogo}>
      {sottotitolo && <div style={{ fontSize: '11px', color: T.textMuted }}>{sottotitolo}</div>}
      {legenda && (
        <div style={{ display: 'flex', gap: '14px', fontSize: '10px', color: T.textMuted, marginTop: '6px' }}>
          {legenda.map(([c, l]) => <span key={l}><span style={{ display: 'inline-block', width: '9px', height: '9px', background: c, borderRadius: '2px', marginRight: '5px' }} />{l}</span>)}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxHeight: '280px', marginTop: '6px', display: 'block' }} role="img" aria-label={titolo}>
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1={padL} x2={W - padR} y1={padT + (H - padT - padB) * f} y2={padT + (H - padT - padB) * f} stroke={T.border} strokeOpacity="0.5" strokeDasharray="2 4" />
        ))}
        <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} stroke={T.border} />
        {dati.map((d, i) => {
          const cx = padL + slot * i + slot / 2;
          const b1x = serie2 ? cx - bw - 1 : cx - bw / 2;
          return (
            <g key={i}>
              {d.v > 0 && <rect x={b1x} y={y(d.v)} width={bw} height={H - padB - y(d.v)} rx="3" fill={colore} />}
              {d.v > 0 && !serie2 && mostraValori && <text x={cx} y={y(d.v) - 6} textAnchor="middle" fontSize={fs} fontWeight="700" fill={T.text} style={{ fontVariantNumeric: 'tabular-nums' }}>{formato(d.v)}</text>}
              {serie2 && d.v2 > 0 && <rect x={cx + 1} y={y(d.v2)} width={bw} height={H - padB - y(d.v2)} rx="3" fill={colore2} />}
              {(!ui.isMobile || dati.length <= 12 || i % 2 === 0) && <text x={cx} y={H - 9} textAnchor="middle" fontSize={fs} fill={T.textMuted}>{d.l}</text>}
            </g>
          );
        })}
      </svg>
    </SezioneEstendibile>
  );
}

// Mini grafico a barre (usato nelle righe espanse delle tabelle venditori)
function MiniBarre({ T, dati, colore = '#3b82f6' }) {
  const ui = useContext(AnalisiUiCtx);
  const W = ui.isMobile ? 340 : 720, H = 150, padT = 22, padB = 22;
  const max = Math.max(1, ...dati.map(d => d.v));
  const slot = W / Math.max(1, dati.length);
  const bw = Math.min(28, slot * 0.6);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxHeight: '170px', display: 'block' }} role="img">
      <line x1="0" x2={W} y1={H - padB} y2={H - padB} stroke={T.border} />
      {dati.map((d, i) => {
        const cx = slot * i + slot / 2; const h = (H - padT - padB) * (d.v / max);
        return (
          <g key={i}>
            {d.v > 0 && <rect x={cx - bw / 2} y={H - padB - h} width={bw} height={h} rx="3" fill={colore} />}
            {d.v > 0 && <text x={cx} y={H - padB - h - 5} textAnchor="middle" fontSize="11" fontWeight="700" fill={T.text}>{d.v}</text>}
            <text x={cx} y={H - 6} textAnchor="middle" fontSize="10" fill={T.textMuted}>{d.l}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Tabella per persona (venditore o rivenditore): contratti per mese, totale,
// peso sul totale, preventivi e conversione; clic sul nome = grafico mensile
function TabellaPerPersona({ T, titolo, etichetta, plurale, righe, mesi, colore }) {
  const ui = useContext(AnalisiUiCtx);
  const [aperti, setAperti] = useState({});
  const totGen = righe.reduce((t, r) => t + r.totale, 0);
  const th = { padding: '7px 8px', color: T.textMuted, fontWeight: '800', fontSize: '11px', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', textAlign: 'right' };
  const td = { padding: '7px 8px', fontSize: '12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' };
  const pct = v => `${v.toFixed(1).replace('.', ',')}%`;
  return (
    <SezioneEstendibile T={T} titolo={titolo} riepilogo={`${totGen} contratti · ${righe.length} ${plurale}`} coloreRiepilogo={colore}>
      <div style={{ fontSize: '11px', color: T.textMuted, marginBottom: '8px' }}>Tocca un nome per vedere il suo grafico mese per mese.</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${260 + mesi.length * 70}px` }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: T.surface || T.card, zIndex: 1 }}>{etichetta}</th>
            {mesi.map(m => <th key={m.k} style={th}>{m.l}</th>)}
            <th style={{ ...th, color: T.text }}>Totale</th>
            <th style={th}>Peso</th>
            <th style={th}>Preventivi</th>
            <th style={th}>Conversione</th>
          </tr></thead>
          <tbody>
            {righe.map(r => (
              <React.Fragment key={r.nome}>
                <tr onClick={() => setAperti(a => ({ ...a, [r.nome]: !a[r.nome] }))} style={{ cursor: 'pointer' }}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: '700', color: T.text, position: 'sticky', left: 0, background: T.surface || T.card, zIndex: 1 }}>
                    <span style={{ color: T.textMuted, marginRight: '6px', display: 'inline-block', transform: aperti[r.nome] ? 'rotate(90deg)' : 'none' }}>▸</span>{r.nome}
                  </td>
                  {mesi.map(m => <td key={m.k} style={{ ...td, color: r.perMese[m.k] ? T.text : T.textMuted }}>{r.perMese[m.k] || ''}</td>)}
                  <td style={{ ...td, fontWeight: '900', color: colore }}>{r.totale}</td>
                  <td style={{ ...td, color: T.textSub || T.textMuted }}>{totGen ? pct((r.totale / totGen) * 100) : '—'}</td>
                  <td style={{ ...td, color: T.textSub || T.textMuted }}>{r.preventivi || '—'}</td>
                  <td style={{ ...td, fontWeight: '700', color: '#8b5cf6' }}>{r.preventivi ? pct((r.totale / r.preventivi) * 100) : '—'}</td>
                </tr>
                {aperti[r.nome] && (
                  <tr><td colSpan={mesi.length + 5} style={{ padding: '8px 4px 14px', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ position: ui.isMobile ? 'sticky' : 'static', left: 0, maxWidth: ui.isMobile ? 'calc(100vw - 60px)' : 'none' }}>
                      <div style={{ fontSize: '12px', fontWeight: '800', color: T.text, marginBottom: '4px' }}>{r.nome} · contratti per mese</div>
                      <MiniBarre T={T} colore={colore} dati={mesi.map(m => ({ l: m.lb, v: r.perMese[m.k] || 0 }))} />
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {!righe.length && <tr><td colSpan={mesi.length + 5} style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>Nessun contratto con questi filtri</td></tr>}
            {righe.length > 0 && (
              <tr>
                <td style={{ ...td, textAlign: 'left', fontWeight: '900', color: T.text, position: 'sticky', left: 0, background: T.surface || T.card }}>Totale</td>
                {mesi.map(m => <td key={m.k} style={{ ...td, fontWeight: '800', color: T.text }}>{righe.reduce((t, r) => t + (r.perMese[m.k] || 0), 0) || ''}</td>)}
                <td style={{ ...td, fontWeight: '900', color: colore }}>{totGen}</td>
                <td style={td}>100%</td>
                <td style={td}>{righe.reduce((t, r) => t + (r.preventivi || 0), 0) || '—'}</td>
                <td style={{ ...td, fontWeight: '700', color: '#8b5cf6' }}>{(() => { const p = righe.reduce((t, r) => t + (r.preventivi || 0), 0); return p ? pct((totGen / p) * 100) : '—'; })()}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '8px' }}>Peso: quota sul totale dei contratti in tabella. Conversione: contratti ÷ preventivi fatti nello stesso periodo.</div>
    </SezioneEstendibile>
  );
}

function AnalisiEconomiciView({ user, contratti = [], preventivi = [] }) {
  const theme = useTheme(); const T = THEMES[theme];
  const [stock, setStock] = useState([]);
  useEffect(() => { veicoliStockPerExport().then(setStock).catch(() => setStock([])); }, []);
  const annoCorrente = new Date().getFullYear();
  // Periodo principale e periodo di confronto: date precise (dal / al)
  const oggiIso = new Date().toISOString().slice(0, 10);
  const [periodoDal, setPeriodoDal] = useState(`${annoCorrente}-01-01`);
  const [periodoAl, setPeriodoAl] = useState(oggiIso);
  const fPeriodo = `${periodoDal}|${periodoAl}`;   // usato come dipendenza dei calcoli
  const iniP = new Date(`${periodoDal}T00:00:00`), finP = new Date(`${periodoAl}T23:59:59`);
  const inPeriodo = d => d && !isNaN(d) && d >= iniP && d <= finP;
  const [fMarca, setFMarca] = useState([]);
  const [fModello, setFModello] = useState([]);
  const [fTipo, setFTipo] = useState([]);
  const [fCambio, setFCambio] = useState([]);
  const [fAnnoImm, setFAnnoImm] = useState([]);
  const [fMese, setFMese] = useState([]);
  const [fConsulente, setFConsulente] = useState([]);
  const [fSede, setFSede] = useState([]);
  const [fCanale, setFCanale] = useState([]);     // B2C / B2B
  // Confronto con un secondo periodo (anno + mesi). Vuoto = nessun confronto
  const [filtriAperti, setFiltriAperti] = useState(false);
  const [confDal, setConfDal] = useState('');
  const [confAl, setConfAl] = useState('');
  const confAnno = confDal && confAl ? `${confDal}|${confAl}` : '';   // '' = nessun confronto
  const confMesi = [];
  const [fTesto, setFTesto] = useState('');
  const testo = useDeferredValue(fTesto);
  const fmtE = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
  const normVT = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Indici dello stock per ritrovare l'auto di ogni contratto
  const idx = useMemo(() => {
    const perId = new Map(), perVin = new Map(), perTarga = new Map();
    stock.forEach(v => { if (v.id) perId.set(String(v.id), v); if (v.vin) perVin.set(normVT(v.vin), v); if (v.targa) perTarga.set(normVT(v.targa), v); });
    return { perId, perVin, perTarga };
  }, [stock]);

  // Nomi consulente unificati: "LUCA AMBROSINO", "Luca  Ambrosino" e
  // "Ambrosino Luca" diventano un solo consulente
  const chiaveNome = x => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z\s']/g, ' ').split(/\s+/).filter(Boolean).sort().join(' ');
  const nomiConsulenti = useMemo(() => {
    const conteggi = {};
    contratti.forEach(c => {
      const orig = String(c.venditoreNome || '').replace(/\s+/g, ' ').trim();
      if (!orig) return;
      const k = chiaveNome(orig);
      const titolo = orig.toLowerCase().replace(/(^|[\s'])(\p{L})/gu, (m, a, b) => a + b.toUpperCase());
      (conteggi[k] = conteggi[k] || {})[titolo] = ((conteggi[k] || {})[titolo] || 0) + 1;
    });
    // Per ogni consulente la forma più usata
    return Object.fromEntries(Object.entries(conteggi).map(([k, forme]) => [k, Object.entries(forme).sort((a, b) => b[1] - a[1])[0][0]]));
  }, [contratti]);

  // Righe di analisi: una per contratto venduto/approvato
  // Tutti i contratti (serve al tasso di annullamento); le vendite sono
  // approvati + venduti
  const righeTutte = useMemo(() => contratti
    .filter(c => ['bozza', 'approvato', 'venduto', 'annullato'].includes(c.stato))
    .map(c => {
      const ve = c.veicolo || {}, de = c.datiExtra || {}, pr = c.prezzo || {}, sv = c.servizi || {};
      const s = (ve.id && idx.perId.get(String(ve.id))) || idx.perVin.get(normVT(de.vin || ve.vin)) || idx.perTarga.get(normVT(ve.targa)) || {};
      const data = new Date(de.dataFirma || c.dataCreazione || '');
      const ingresso = new Date(s.enteredInStock || ve.enteredInStock || '');
      const giacenza = !isNaN(data) && !isNaN(ingresso) && data >= ingresso ? Math.round((data - ingresso) / 864e5) : null;
      const spesaServizi = (sv.lojack ? 476 : 0) + (sv.blockshaft ? 182 : 0) + (/metasystem/i.test(sv.altroAntifurto || '') ? 85 : 0) + (sv.serviceCare ? 100 : 0)
        + (calcolaCostoGaranziaMawdy(
            s.cc || ve.cc || estraiCilindrataDaVersione(s.versione || ve.versione) || estraiCilindrataDaVersione(s.modello || ve.modello) || s.registrazione || ve.registrazione,
            s.km || ve.km, s.marca || ve.marca, rilevaGaranziaMawdy(c).mesi, c.dataCreazione) || 0);
      return {
        c, data,
        marca: normMarcaAnalisi(ve.marca || s.marca), modello: (ve.modello || s.modello || 'N.D.').trim(),
        tipo: normTipoVeicolo(ve.tipo || s.tipo), cambio: (s.gearType || ve.gearType || 'N.D.'),
        annoImm: annoDa(de.immatricolazione || ve.registrazione || s.registrazione) || 'N.D.',
        mese: isNaN(data) ? 'N.D.' : MESI_BREVI[data.getMonth()],
        consulente: nomiConsulenti[chiaveNome(c.venditoreNome)] || 'N.D.',
        // Sede della vendita = sede del consulente (stesse liste di tutto RadaR)
        sede: ({ agnano: 'Agnano', nola: 'Nola', salerno: 'Salerno' })[getSede(String(c.venditoreNome || '').toLowerCase().replace(/\s+/g, ' ').trim())] || 'Agnano',
        giacenza,
        serviziVenduti: sommaCostiServiziNoEstinzione(sv),
        spesaServizi,
        finanziata: !!pr.hasFinanziamento && parseFloat(pr.importoFinanziato || 0) > 0,
        b2b: !!c.cliente?.b2b,   // i B2B non si finanziano: esclusi dalla penetrazione finanziaria
        canale: c.cliente?.b2b ? 'B2B' : 'B2C',
        // Provvigione con le stesse regole di RADAR → Provvigioni
        provvigione: (() => { try { return calcolaProvvigione(c).totale || 0; } catch (e) { return 0; } })(),
        dataProvv: dataProvvigionale(c),
        ferro: (parseFloat(pr.prezzoListino || ve.prezzo || 0) - parseFloat(pr.sconto || 0)),
        testo: `${ve.marca} ${ve.modello} ${ve.versione || ''} ${ve.targa || ''} ${c.cliente?.cognome || ''} ${c.cliente?.ragioneSociale || ''} ${c.venditoreNome || ''}`.toLowerCase(),
      };
    }), [contratti, idx, nomiConsulenti]);
  const righe = useMemo(() => righeTutte.filter(r => ['approvato', 'venduto'].includes(r.c.stato)), [righeTutte]);

  const passa = (r, escludi) => {
    if (escludi !== 'periodo' && !inPeriodo(r.data)) return false;
    if (escludi !== 'marca' && fMarca.length && !fMarca.includes(r.marca)) return false;
    if (escludi !== 'modello' && fModello.length && !fModello.includes(r.modello)) return false;
    if (escludi !== 'tipo' && fTipo.length && !fTipo.includes(r.tipo)) return false;
    if (escludi !== 'cambio' && fCambio.length && !fCambio.includes(r.cambio)) return false;
    if (escludi !== 'annoImm' && fAnnoImm.length && !fAnnoImm.includes(r.annoImm)) return false;
    if (escludi !== 'mese' && escludi !== 'periodo' && fMese.length && !fMese.includes(r.mese)) return false;
    if (escludi !== 'consulente' && fConsulente.length && !fConsulente.includes(r.consulente)) return false;
    if (escludi !== 'sede' && fSede.length && !fSede.includes(r.sede)) return false;
    if (escludi !== 'canale' && fCanale.length && !fCanale.includes(r.canale)) return false;
    if (testo.trim() && !r.testo.includes(testo.trim().toLowerCase())) return false;
    return true;
  };
  const filtrate = useMemo(() => righe.filter(r => passa(r)), [righe, fPeriodo, fMarca, fModello, fTipo, fCambio, fAnnoImm, fMese, fConsulente, fSede, fCanale, testo]);
  const opz = campo => {
    const v = [...new Set(righe.filter(r => passa(r, campo)).map(r => r[campo]))];
    return campo === 'mese' ? MESI_BREVI.filter(m => v.includes(m)) : v.sort();
  };
  const anniPeriodo = [...new Set(righe.map(r => !isNaN(r.data) ? r.data.getFullYear() : null).filter(Boolean))].sort((a, b) => b - a);

  // Giacenza media dello stock ATTUALE con gli stessi filtri (escluso il periodo)
  const giacenzaStock = useMemo(() => {
    const att = stock.filter(v => v && !['VENDUTA', 'SOLD', 'CONTRATTUALIZZATA'].includes(String(v.status || '').toUpperCase())).filter(v => {
      const r = { marca: normMarcaAnalisi(v.marca), modello: (v.modello || 'N.D.').trim(), tipo: normTipoVeicolo(v.tipo), cambio: v.gearType || 'N.D.', annoImm: annoDa(v.registrazione) || 'N.D.' };
      if (fMarca.length && !fMarca.includes(r.marca)) return false;
      if (fModello.length && !fModello.includes(r.modello)) return false;
      if (fTipo.length && !fTipo.includes(r.tipo)) return false;
      if (fCambio.length && !fCambio.includes(r.cambio)) return false;
      if (fAnnoImm.length && !fAnnoImm.includes(r.annoImm)) return false;
      if (testo.trim() && !`${v.marca} ${v.modello} ${v.versione || ''} ${v.targa || ''}`.toLowerCase().includes(testo.trim().toLowerCase())) return false;
      return true;
    });
    const g = att.map(v => parseFloat(v.giorni)).filter(x => isFinite(x));
    return { n: att.length, media: g.length ? Math.round(g.reduce((a, b) => a + b, 0) / g.length) : 0 };
  }, [stock, fMarca, fModello, fTipo, fCambio, fAnnoImm, testo]);

  const calcolaKpi = lista => {
    const filtrate = lista;
    const n = filtrate.length;
    const conG = filtrate.filter(r => r.giacenza !== null);
    const sv = filtrate.reduce((t, r) => t + r.serviziVenduti, 0);
    const sp = filtrate.reduce((t, r) => t + r.spesaServizi, 0);
    return {
      n,
      giacenza: conG.length ? Math.round(conG.reduce((t, r) => t + r.giacenza, 0) / conG.length) : 0, nGiacenza: conG.length,
      serviziVenduti: sv, spesaServizi: sp,
      // Penetrazione finanziaria: SOLO contratti B2C
      penetrazione: (() => { const b2c = filtrate.filter(r => !r.b2b); return b2c.length ? (b2c.filter(r => r.finanziata).length / b2c.length) * 100 : 0; })(),
      nB2c: filtrate.filter(r => !r.b2b).length,
      ferroMedio: n ? filtrate.reduce((t, r) => t + r.ferro, 0) / n : 0,
    };
  };
  const kpi = useMemo(() => calcolaKpi(filtrate), [filtrate]);

  // ── Periodo di confronto ──
  const iniC = new Date(`${confDal}T00:00:00`), finC = new Date(`${confAl}T23:59:59`);
  const inConf = d => d && !isNaN(d) && confAnno !== '' && d >= iniC && d <= finC
    && (!fMese.length || fMese.includes(MESI_BREVI[d.getMonth()]));
  const filtrateConf = useMemo(() => (confAnno === '' ? null : righe.filter(r => passa(r, 'periodo') && inConf(r.data))),
    [righe, confAnno, confMesi, fMarca, fModello, fTipo, fCambio, fAnnoImm, fConsulente, fSede, fCanale, testo]);
  const kpiConf = useMemo(() => (filtrateConf ? calcolaKpi(filtrateConf) : null), [filtrateConf]);

  // Tasso di annullamento: contratti annullati / contratti totali del periodo
  // (bozze, approvati, venduti e annullati, per data di firma), stessi filtri
  const annullamento = useMemo(() => {
    const tutti = righeTutte.filter(r => passa(r));
    const annullati = tutti.filter(r => r.c.stato === 'annullato').length;
    return { tot: tutti.length, annullati, tasso: tutti.length ? (annullati / tutti.length) * 100 : 0 };
  }, [righeTutte, fPeriodo, fMarca, fModello, fTipo, fCambio, fAnnoImm, fMese, fConsulente, fSede, fCanale, testo]);
  const annullamentoConf = useMemo(() => {
    if (confAnno === '') return null;
    const tutti = righeTutte.filter(r => passa(r, 'periodo') && inConf(r.data));
    const annullati = tutti.filter(r => r.c.stato === 'annullato').length;
    return { tot: tutti.length, annullati, tasso: tutti.length ? (annullati / tutti.length) * 100 : 0 };
  }, [righeTutte, confAnno, confMesi, fMarca, fModello, fTipo, fCambio, fAnnoImm, fConsulente, fSede, fCanale, testo]);

  // Totale provvigioni identico a RADAR → Provvigioni: contratti venduti o
  // consegnati per DATA DI VENDITA, venditori consulenti, meno i ristorni
  // registrati nel periodo. Gli altri filtri (sede, marca, consulente…) valgono.
  const inPeriodoProvv = d => inPeriodo(d) && (!fMese.length || fMese.includes(MESI_BREVI[d.getMonth()]));
  const provv = useMemo(() => {
    let lordo = 0, ristorni = 0, n = 0;
    righe.forEach(r => {
      if (!passa(r, 'periodo')) return;
      if (r.dataProvv && inPeriodoProvv(r.dataProvv)) { lordo += r.provvigione; n++; }
      const rs = r.c.ristornoFinanziamento;
      if (rs?.at && inPeriodoProvv(new Date(rs.at))) ristorni += parseFloat(rs.importo) || 0;
    });
    return { lordo, ristorni, netto: lordo - ristorni, n };
  }, [righe, fPeriodo, fMarca, fModello, fTipo, fCambio, fAnnoImm, fMese, fConsulente, fSede, fCanale, testo]);
  const provvConf = useMemo(() => {
    if (confAnno === '') return null;
    let lordo = 0, ristorni = 0;
    righe.forEach(r => {
      if (!passa(r, 'periodo')) return;
      if (r.dataProvv && inConf(r.dataProvv)) lordo += r.provvigione;
      const rs = r.c.ristornoFinanziamento;
      if (rs?.at && inConf(new Date(rs.at))) ristorni += parseFloat(rs.importo) || 0;
    });
    return { netto: lordo - ristorni };
  }, [righe, confAnno, confMesi, fMarca, fModello, fTipo, fCambio, fAnnoImm, fConsulente, fSede, fCanale, testo]);

  // Serie mensili (anno scelto) o annuali (tutti gli anni)
  const serie = useMemo(() => {
    const mesiNelPeriodo = [];
    for (let d = new Date(iniP.getFullYear(), iniP.getMonth(), 1); d <= finP; d.setMonth(d.getMonth() + 1)) mesiNelPeriodo.push(new Date(d));
    const perAnno = mesiNelPeriodo.length > 24;
    const pluriennale = new Set(mesiNelPeriodo.map(d => d.getFullYear())).size > 1;
    const chiavi = perAnno ? [...new Set(mesiNelPeriodo.map(d => String(d.getFullYear())))] : mesiNelPeriodo.map(d => `${d.getFullYear()}-${d.getMonth()}`);
    const gruppi = Object.fromEntries(chiavi.map(k => [k, []]));
    filtrate.forEach(r => { if (isNaN(r.data)) return; const k = perAnno ? String(r.data.getFullYear()) : `${r.data.getFullYear()}-${r.data.getMonth()}`; if (gruppi[k]) gruppi[k].push(r); });
    return chiavi.map(k => {
      const g = gruppi[k];
      const conG = g.filter(r => r.giacenza !== null);
      return {
        l: perAnno ? k : (() => { const [yy, mm] = k.split('-'); return pluriennale ? `${MESI_BREVI[parseInt(mm)]} ${yy.slice(2)}` : MESI_BREVI[parseInt(mm)]; })(),
        giacenza: conG.length ? conG.reduce((t, r) => t + r.giacenza, 0) / conG.length : 0,
        servizi: g.reduce((t, r) => t + r.serviziVenduti, 0),
        spesa: g.reduce((t, r) => t + r.spesaServizi, 0),
        penetrazione: (() => { const b2c = g.filter(r => !r.b2b); return b2c.length ? (b2c.filter(r => r.finanziata).length / b2c.length) * 100 : 0; })(),
        ferro: g.length ? g.reduce((t, r) => t + r.ferro, 0) / g.length : 0,
      };
    });
  }, [filtrate, fPeriodo]);

  // Dettaglio per marca
  const perMarca = useMemo(() => {
    const m = {};
    filtrate.forEach(r => {
      const x = (m[r.marca] = m[r.marca] || { marca: r.marca, n: 0, g: [], sv: 0, sp: 0, fin: 0, nB2c: 0, ferro: 0, provv: 0 });
      x.n++; if (r.giacenza !== null) x.g.push(r.giacenza); x.sv += r.serviziVenduti; x.sp += r.spesaServizi; x.ferro += r.ferro;
      if (!r.b2b) { x.nB2c++; if (r.finanziata) x.fin++; }
    });
    righe.forEach(r => {
      if (!passa(r, 'periodo') || !r.dataProvv || !inPeriodoProvv(r.dataProvv)) return;
      const x = (m[r.marca] = m[r.marca] || { marca: r.marca, n: 0, g: [], sv: 0, sp: 0, fin: 0, nB2c: 0, ferro: 0, provv: 0, nProvv: 0 });
      x.provv += r.provvigione; x.nProvv = (x.nProvv || 0) + 1;
    });
    return Object.values(m).filter(x => x.n || x.provv).sort((a, b) => b.n - a.n);
  }, [filtrate, righe, fPeriodo, fMese]);

  const nFiltri = fMarca.length + fModello.length + fTipo.length + fCambio.length + fAnnoImm.length + fMese.length + fConsulente.length + fSede.length + fCanale.length + (fTesto.trim() ? 1 : 0);
  const card = (t, v, sub, col, varz) => (
    <div style={{ background: T.surface || T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${col === T.text ? T.border : col}`, borderRadius: '14px', padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: T.textSub || T.textMuted }}>{t}</div>
      <div style={{ fontSize: 'clamp(19px, 4.8vw, 26px)', fontWeight: '900', color: col, marginTop: '4px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
      {sub && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{sub}</div>}
      {varz}
    </div>
  );
  const V = (cur, prev, fmt, meglio) => (kpiConf ? <Variazione T={T} cur={cur} prev={prev} fmt={fmt} meglio={meglio} /> : null);
  const fmtPct = v => `${v.toFixed(1).replace('.', ',')} pt`;
  const fmtGg = v => `${Math.round(v)} gg`;

  // ── Venditori B2C e rivenditori B2B: contratti per mese, peso, conversione ──
  const MESI_NOMI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
  const mesiTab = (() => {
    const out = []; const pluri = iniP.getFullYear() !== finP.getFullYear();
    for (let d = new Date(iniP.getFullYear(), iniP.getMonth(), 1); d <= finP && out.length < 36; d.setMonth(d.getMonth() + 1)) {
      const m = d.getMonth();
      out.push({ k: `${d.getFullYear()}-${m}`, l: `${String(m + 1).padStart(2, '0')} - ${MESI_NOMI[m]}${pluri ? ` ${String(d.getFullYear()).slice(2)}` : ''}`, lb: `${MESI_BREVI[m]}${pluri ? ` ${String(d.getFullYear()).slice(2)}` : ''}` });
    }
    return out;
  })();
  // Rivenditori: "Overcar", "OVERCAR SRL" e "Overcar S.r.l." sono lo stesso.
  // Chiave senza forma societaria, punteggiatura, accenti e maiuscole; come
  // nome si mostra la forma più usata nei contratti.
  const FORME_SOCIETARIE = /\b(s\.?\s?r\.?\s?l\.?\s?s?|s\.?\s?p\.?\s?a|s\.?\s?n\.?\s?c|s\.?\s?a\.?\s?s|s\.?\s?a\.?\s?p\.?\s?a|soc\.?\s*coop\.?|societ[aà]\s+cooperativa|unipersonale|in\s+liquidazione|ditta\s+individuale|d\.?\s?i)\b\.?/gi;
  const testoCliente = c => (c?.ragioneSociale || `${c?.cognome || ''} ${c?.nome || ''}`).replace(/\s+/g, ' ').trim();
  // Raggruppamenti manuali: chiave (senza spazi) → nome da mostrare
  const ALIAS_RIVENDITORI = {
    autoscala: 'Auto Scala',
    quattrotempi: 'Quattro Tempi',
    emmerre: 'Emmerre Auto', emmerreauto: 'Emmerre Auto',
  };
  const chiaveCliente = t => {
    const k = String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(FORME_SOCIETARIE, ' ')
      .replace(/\bkm\s*\d+\b/g, ' ')          // "Quattro Tempi km 197" → "Quattro Tempi"
      .replace(/[^a-z0-9]+/g, '');             // senza spazi: "Auto Scala" = "Autoscala"
    return ALIAS_RIVENDITORI[k] ? `alias:${ALIAS_RIVENDITORI[k]}` : k;
  };
  const nomiRivenditori = useMemo(() => {
    const conteggi = {};
    [...contratti, ...(preventivi || [])].forEach(x => {
      if (!x?.cliente?.b2b) return;
      const orig = testoCliente(x.cliente); if (!orig) return;
      const k = chiaveCliente(orig); if (!k) return;
      // forma da mostrare: senza forma societaria, con le iniziali maiuscole
      const pulito = orig.replace(FORME_SOCIETARIE, ' ').replace(/\s+/g, ' ').replace(/[\s,.\-]+$/, '').trim();
      const forma = pulito.toLowerCase().replace(/(^|[\s'&.\-])(\p{L})/gu, (m, a, b) => a + b.toUpperCase());
      (conteggi[k] = conteggi[k] || {})[forma] = (conteggi[k][forma] || 0) + 1;
    });
    return Object.fromEntries(Object.entries(conteggi).map(([k, f]) => [k, Object.entries(f).sort((a, b) => b[1] - a[1])[0][0]]));
  }, [contratti, preventivi]);
  const nomeCliente = c => { const k = chiaveCliente(testoCliente(c)); if (k.startsWith('alias:')) return k.slice(6); return (k && nomiRivenditori[k]) || testoCliente(c).toUpperCase() || 'N.D.'; };
  const tabellePersone = useMemo(() => {
    // preventivi del periodo con gli stessi filtri di sede e consulente
    const prevPeriodo = (preventivi || []).filter(p => {
      const d = new Date(p.dataCreazione || p.data || '');
      if (!inPeriodo(d)) return false;
      const cons = nomiConsulenti[chiaveNome(p.venditoreNome)] || 'N.D.';
      if (fConsulente.length && !fConsulente.includes(cons)) return false;
      const sede = ({ agnano: 'Agnano', nola: 'Nola', salerno: 'Salerno' })[getSede(String(p.venditoreNome || '').toLowerCase().replace(/\s+/g, ' ').trim())] || 'Agnano';
      if (fSede.length && !fSede.includes(sede)) return false;
      return true;
    });
    const costruisci = (lista, chiave, prevChiave, prevLista) => {
      const m = {};
      lista.forEach(r => {
        const k = chiave(r); if (isNaN(r.data)) return;
        const x = (m[k] = m[k] || { nome: k, perMese: {}, totale: 0, preventivi: 0 });
        const mk = `${r.data.getFullYear()}-${r.data.getMonth()}`;
        x.perMese[mk] = (x.perMese[mk] || 0) + 1; x.totale++;
      });
      prevLista.forEach(p => { const k = prevChiave(p); if (m[k]) m[k].preventivi++; });
      return Object.values(m).sort((a, b) => b.totale - a.totale);
    };
    return {
      b2c: costruisci(filtrate.filter(r => !r.b2b), r => r.consulente,
        p => nomiConsulenti[chiaveNome(p.venditoreNome)] || 'N.D.', prevPeriodo.filter(p => !p.cliente?.b2b)),
      b2b: costruisci(filtrate.filter(r => r.b2b), r => nomeCliente(r.c.cliente),
        p => nomeCliente(p.cliente), prevPeriodo.filter(p => p.cliente?.b2b)),
    };
  }, [filtrate, preventivi, fPeriodo, fConsulente, fSede, nomiConsulenti, nomiRivenditori]);

  const ui = useContext(AnalisiUiCtx);
  const fmtData = iso => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('it-IT') : '');
  const pannello = { background: T.surface || T.card, border: `1px solid ${T.border}`, borderRadius: '16px', padding: ui.isMobile ? '12px' : '16px 18px' };
  const titoloGruppo = t => <div style={{ fontSize: '13px', fontWeight: '800', color: T.textSub || T.textMuted, margin: '18px 2px 8px' }}>{t}</div>;
  const gridKpi = { display: 'grid', gridTemplateColumns: ui.isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(210px, 1fr))', gap: ui.isMobile ? '8px' : '10px' };
  return (
    <div>
      {/* Periodo e confronto: sempre visibili */}
      <div className="analisi-no-stampa" style={{ ...pannello, marginBottom: '10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: T.textMuted, fontWeight: '700' }}>Dal <input type="date" value={periodoDal} onChange={e => setPeriodoDal(e.target.value)} style={{ padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light', flex: 1, minWidth: 0 }} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: T.textMuted, fontWeight: '700' }}>Al <input type="date" value={periodoAl} onChange={e => setPeriodoAl(e.target.value)} style={{ padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light', flex: 1, minWidth: 0 }} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8b5cf6', fontWeight: '700' }}>Confronto dal <input type="date" value={confDal} onChange={e => setConfDal(e.target.value)} style={{ ...{ padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light', flex: 1, minWidth: 0 }, borderColor: confDal ? '#8b5cf6' : undefined }} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8b5cf6', fontWeight: '700' }}>Al <input type="date" value={confAl} onChange={e => setConfAl(e.target.value)} style={{ ...{ padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light', flex: 1, minWidth: 0 }, borderColor: confAl ? '#8b5cf6' : undefined }} /></label>
        <button onClick={() => {
            // Stesso periodo dell'anno precedente
            const sposta = iso => { const d = new Date(`${iso}T12:00:00`); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); };
            setConfDal(sposta(periodoDal)); setConfAl(sposta(periodoAl));
          }}
          style={{ padding: '9px 10px', borderRadius: '8px', border: '1px dashed #8b5cf6', background: 'transparent', color: '#8b5cf6', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>
          ↺ Stesso periodo anno prima
        </button>
        {confAnno !== '' && (
          <button onClick={() => { setConfDal(''); setConfAl(''); }}
            style={{ padding: '9px 10px', borderRadius: '8px', border: '1px solid #8b5cf6', background: 'transparent', color: '#8b5cf6', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>
            ✕ Togli confronto
          </button>
        )}
        </div>
      </div>

      {/* Altri filtri: a scomparsa */}
      <div className="analisi-no-stampa" style={{ ...pannello, padding: 0, marginBottom: '6px' }}>
        <button onClick={() => setFiltriAperti(v => !v)} aria-expanded={filtriAperti}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 16px', background: 'transparent', border: 'none', cursor: 'pointer', color: T.text, fontWeight: '800', fontSize: '13px', minHeight: '46px' }}>
          <span style={{ color: T.textMuted, transition: 'transform 0.15s', transform: filtriAperti ? 'rotate(90deg)' : 'none' }}>▸</span>
          <span style={{ flex: 1, textAlign: 'left' }}>Filtri</span>
          {nFiltri > 0 && <span style={{ fontSize: '11px', fontWeight: '800', color: '#3b82f6', background: 'rgba(59,130,246,0.12)', padding: '3px 9px', borderRadius: '999px' }}>{nFiltri} attivi</span>}
        </button>
        {filtriAperti && (
          <div style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? '1fr' : 'repeat(auto-fill, minmax(190px, 1fr))', gap: '8px', padding: '0 16px 16px' }}>
        <RadarMultiSelect label="MESE" options={opz('mese')} selected={fMese} onChange={setFMese} onClear={() => setFMese([])} />
        <RadarMultiSelect label="SEDE" options={opz('sede')} selected={fSede} onChange={setFSede} onClear={() => setFSede([])} />
        <RadarMultiSelect label="B2C / B2B" options={opz('canale')} selected={fCanale} onChange={setFCanale} onClear={() => setFCanale([])} />
        <RadarMultiSelect label="CONSULENTE" options={opz('consulente')} selected={fConsulente} onChange={setFConsulente} onClear={() => setFConsulente([])} />
        <RadarMultiSelect label="MARCA" options={opz('marca')} selected={fMarca} onChange={setFMarca} onClear={() => setFMarca([])} />
        <RadarMultiSelect label="MODELLO" options={opz('modello')} selected={fModello} onChange={setFModello} onClear={() => setFModello([])} />
        <RadarMultiSelect label="TIPOLOGIA" options={opz('tipo')} selected={fTipo} onChange={setFTipo} onClear={() => setFTipo([])} />
        <RadarMultiSelect label="CAMBIO" options={opz('cambio')} selected={fCambio} onChange={setFCambio} onClear={() => setFCambio([])} />
        <RadarMultiSelect label="ANNO IMMATR." options={opz('annoImm')} selected={fAnnoImm} onChange={setFAnnoImm} onClear={() => setFAnnoImm([])} />
        <input value={fTesto} onChange={e => setFTesto(e.target.value)} placeholder="Cerca (versione, targa, cliente, consulente…)"
          style={{ padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${fTesto.trim() ? '#3b82f6' : T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', outline: 'none' }} />
        {nFiltri > 0 && (
          <button onClick={() => { setFMarca([]); setFModello([]); setFTipo([]); setFCambio([]); setFAnnoImm([]); setFMese([]); setFConsulente([]); setFSede([]); setFCanale([]); setFTesto(''); }}
            style={{ padding: '9px 10px', borderRadius: '8px', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>
            ✕ Pulisci filtri ({nFiltri})
          </button>
        )}
          </div>
        )}
      </div>

      {/* Riepilogo del periodo (anche in stampa) */}
      <div style={{ fontSize: '12px', color: T.textMuted, margin: '12px 2px 0' }}>
        Periodo <b style={{ color: T.text }}>{fmtData(periodoDal)} – {fmtData(periodoAl)}</b>
        {confAnno !== '' && <> · confronto con <b style={{ color: '#8b5cf6' }}>{fmtData(confDal)} – {fmtData(confAl)}</b></>}
        {nFiltri > 0 && <> · {nFiltri} filtri attivi</>}
      </div>

      {titoloGruppo('Vendite')}
      <div style={gridKpi}>
        {card('Auto vendute', kpi.n, 'contratti approvati e venduti', T.text, V(kpi.n, kpiConf?.n, v => String(Math.round(v)), 'su'))}
        {card('Prezzo medio ferro', fmtE(kpi.ferroMedio), 'listino meno sconto', '#3b82f6', V(kpi.ferroMedio, kpiConf?.ferroMedio, fmtE, 'su'))}
        {card('Totale provvigioni', fmtE(provv.netto), `${provv.n} auto consegnate${provv.ristorni ? `, ristorni −${fmtE(provv.ristorni)}` : ''}`, '#ec4899', provvConf ? <Variazione T={T} cur={provv.netto} prev={provvConf.netto} fmt={fmtE} meglio="su" /> : null)}
        {card('Tasso di annullamento', `${annullamento.tasso.toFixed(1).replace('.', ',')}%`, `${annullamento.annullati} su ${annullamento.tot} contratti`, '#ef4444', annullamentoConf ? <Variazione T={T} cur={annullamento.tasso} prev={annullamentoConf.tasso} fmt={fmtPct} meglio="giu" /> : null)}
      </div>

      {titoloGruppo('Servizi e finanziamenti')}
      <div style={gridKpi}>
        {card('Servizi venduti', fmtE(kpi.serviziVenduti), kpi.n ? `${fmtE(kpi.serviziVenduti / kpi.n)} per auto` : null, '#10b981', V(kpi.serviziVenduti, kpiConf?.serviziVenduti, fmtE, 'su'))}
        {card('Spesa su servizi', fmtE(kpi.spesaServizi), `margine ${fmtE(kpi.serviziVenduti - kpi.spesaServizi)}`, '#ef4444', V(kpi.spesaServizi, kpiConf?.spesaServizi, fmtE, 'giu'))}
        {card('Penetrazione finanziaria', `${kpi.penetrazione.toFixed(1).replace('.', ',')}%`, `solo B2C, ${kpi.nB2c} contratti`, '#8b5cf6', V(kpi.penetrazione, kpiConf?.penetrazione, fmtPct, 'su'))}
      </div>

      {titoloGruppo('Stock')}
      <div style={gridKpi}>
        {card('Giacenza media vendute', `${kpi.giacenza} gg`, `su ${kpi.nGiacenza} auto`, '#f59e0b', V(kpi.giacenza, kpiConf?.giacenza, fmtGg, 'giu'))}
        {card('Giacenza stock attuale', `${giacenzaStock.media} gg`, `${giacenzaStock.n} auto in stock oggi`, '#f59e0b')}
      </div>

      {titoloGruppo('Venditori e rivenditori')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <TabellaPerPersona T={T} titolo="Venditori · contratti B2C per mese, peso e conversione" etichetta="Venditore" plurale="venditori" righe={tabellePersone.b2c} mesi={mesiTab} colore="#10b981" />
        <TabellaPerPersona T={T} titolo="Rivenditori B2B · contratti per mese, peso e conversione" etichetta="Rivenditore" plurale="rivenditori" righe={tabellePersone.b2b} mesi={mesiTab} colore="#3b82f6" />
      </div>

      {titoloGruppo('Andamento')}
      {/* Grafici */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        <GraficoBarre T={T} titolo="Giacenza media vetture vendute" riepilogo={`${kpi.giacenza} gg`} coloreRiepilogo="#f59e0b" sottotitolo="Giorni dall'ingresso in stock alla firma del contratto"
          dati={serie.map(d => ({ l: d.l, v: Math.round(d.giacenza) }))} formato={v => `${v}`} colore="#f59e0b" />
        <GraficoBarre T={T} titolo="Servizi venduti e spesa sui servizi" riepilogo={`${fmtE(kpi.serviziVenduti)} / ${fmtE(kpi.spesaServizi)}`} coloreRiepilogo="#10b981" sottotitolo="Importi in euro"
          dati={serie.map(d => ({ l: d.l, v: d.servizi, v2: d.spesa }))} formato={fmtE} colore="#10b981" serie2 colore2="#ef4444"
          legenda={[['#10b981', 'Servizi venduti'], ['#ef4444', 'Spesa sui servizi']]} />
        <GraficoBarre T={T} titolo="Penetrazione finanziaria" riepilogo={`${kpi.penetrazione.toFixed(1).replace('.', ',')}%`} coloreRiepilogo="#8b5cf6" sottotitolo="% di auto vendute con finanziamento — solo clienti B2C (i B2B sono esclusi)"
          dati={serie.map(d => ({ l: d.l, v: Math.round(d.penetrazione) }))} formato={v => `${v}%`} colore="#8b5cf6" />
        <GraficoBarre T={T} titolo="Prezzo medio del ferro venduto" riepilogo={fmtE(kpi.ferroMedio)} coloreRiepilogo="#3b82f6" sottotitolo="Listino − sconto"
          dati={serie.map(d => ({ l: d.l, v: Math.round(d.ferro) }))} formato={v => `${Math.round(v / 1000)}k`} colore="#3b82f6" />
      </div>

      {/* Dettaglio per marca */}
      <SezioneEstendibile T={T} titolo="Dettaglio per marca" riepilogo={`${perMarca.length} marche`}>
        {ui.isMobile ? (
          // Mobile: una scheda per marca invece della tabella larga
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {perMarca.map(x => (
              <div key={x.marca} style={{ border: `1px solid ${T.border}`, borderRadius: '12px', padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '900', color: T.text }}>{x.marca}</span>
                  <span style={{ fontSize: '12px', color: T.textMuted }}>{x.n} firmate</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '12px' }}>
                  {[['Giacenza', x.g.length ? `${Math.round(x.g.reduce((a, b) => a + b, 0) / x.g.length)} gg` : '—', '#f59e0b'],
                    ['Ferro medio', x.n ? fmtE(x.ferro / x.n) : '—', '#3b82f6'],
                    ['Servizi', fmtE(x.sv), '#10b981'],
                    ['Spesa servizi', fmtE(x.sp), '#ef4444'],
                    ['Penetrazione', x.nB2c ? `${Math.round((x.fin / x.nB2c) * 100)}%` : '—', '#8b5cf6'],
                    ['Provvigioni', x.nProvv ? `${fmtE(x.provv)} (${x.nProvv})` : '—', '#ec4899']].map(([l, v, c]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                      <span style={{ color: T.textMuted }}>{l}</span><b style={{ color: c, fontVariantNumeric: 'tabular-nums' }}>{v}</b>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {!perMarca.length && <div style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>Nessun contratto con questi filtri</div>}
          </div>
        ) : (
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '720px' }}>
          <thead>
            <tr>{['Marca', 'Auto firmate', 'Giacenza media', 'Servizi venduti', 'Spesa servizi', 'Penetrazione fin.', 'Prezzo medio ferro', 'Provvigioni (auto consegnate)'].map(h => (
              <th key={h} style={{ textAlign: h === 'Marca' ? 'left' : 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '800', borderBottom: `1px solid ${T.border}` }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {perMarca.map(x => (
              <tr key={x.marca}>
                <td style={{ padding: '6px 8px', color: T.text, fontWeight: '700' }}>{x.marca}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: T.text }}>{x.n}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#f59e0b' }}>{x.g.length ? `${Math.round(x.g.reduce((a, b) => a + b, 0) / x.g.length)} gg` : '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#10b981' }}>{fmtE(x.sv)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ef4444' }}>{fmtE(x.sp)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#8b5cf6' }}>{x.nB2c ? `${Math.round((x.fin / x.nB2c) * 100)}%` : '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#3b82f6' }}>{x.n ? fmtE(x.ferro / x.n) : '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ec4899' }}>{x.nProvv ? `${fmtE(x.provv)} (${x.nProvv})` : '—'}</td>
              </tr>
            ))}
            {!perMarca.length && <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>Nessun contratto con questi filtri</td></tr>}
          </tbody>
        </table>
        </div>
        )}
        <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '8px' }}>
          «Auto firmate» conta i contratti firmati nel periodo; le provvigioni seguono RADAR → Provvigioni e contano le auto <b>consegnate</b> nel periodo (tra parentesi), anche se firmate prima.
        </div>
      </SezioneEstendibile>
    </div>
  );
}

// ─── ANALISI · PERFORMANCE BACK OFFICE ──────────────────────────────────────
// Dati: log del Back Office salvato su ogni contratto (chi ha fatto cosa e
// quando). Nessuna lettura in più: i contratti sono già in memoria.
const RUOLI_BO_ANALISI = ['back_office', 'admin', 'super_admin'];
function PerformanceBOView({ contratti = [] }) {
  const theme = useTheme(); const T = THEMES[theme];
  const oggi = new Date();
  const iso = d => d.toISOString().slice(0, 10);
  const [dal, setDal] = useState(`${oggi.getFullYear()}-01-01`);
  const [al, setAl] = useState(iso(oggi));
  const [fUtente, setFUtente] = useState([]);
  // Periodo di confronto (vuoto = nessun confronto)
  const [confDal, setConfDal] = useState('');
  const [confAl, setConfAl] = useState('');
  const inizio = new Date(`${dal}T00:00:00`), fine = new Date(`${al}T23:59:59`);
  const nelPeriodo = d => d && !isNaN(d) && d >= inizio && d <= fine;
  const normN = x => String(x || 'N.D.').replace(/\s+/g, ' ').trim();

  // Eventi del log di tutti i contratti
  const eventi = useMemo(() => {
    const out = [];
    contratti.forEach(c => Object.values(c.backoffice_log || {}).forEach(e => {
      if (!e || !e.timestamp) return;
      out.push({ c, campo: e.campo, valore: e.valore, utente: normN(e.utente), ruolo: e.ruolo || '', data: new Date(e.timestamp) });
    }));
    return out;
  }, [contratti]);
  const utentiBO = useMemo(() => [...new Set(eventi.filter(e => RUOLI_BO_ANALISI.includes(e.ruolo)).map(e => e.utente))].sort(), [eventi]);
  const okUtente = u => !fUtente.length || fUtente.includes(u);

  // Liquidazioni: l'ultima spunta "Liquidato" di ogni contratto ancora liquidato
  const liquidazioni = useMemo(() => {
    const ultima = new Map();
    eventi.filter(e => e.campo === 'liquidato' && e.valore === true && e.c.backoffice?.liquidato).forEach(e => {
      const prev = ultima.get(e.c.id); if (!prev || e.data > prev.data) ultima.set(e.c.id, e);
    });
    return [...ultima.values()].filter(e => nelPeriodo(e.data) && okUtente(e.utente));
  }, [eventi, dal, al, fUtente]);

  // Consegne: giorni dall'approvazione (ingresso in Back Office) alla consegna
  const consegne = useMemo(() => {
    const out = [];
    contratti.forEach(c => {
      if (c.backoffice?.statoConsegna !== 'consegnato') return;
      const logC = Object.values(c.backoffice_log || {}).filter(e => e && e.campo === 'statoConsegna' && e.valore === 'consegnato' && e.timestamp)
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0];
      const dataCons = new Date(logC?.timestamp || c.vendutoAt || c.dataVendita || '');
      const dataIn = new Date(c.approvazioneData || '');
      if (!nelPeriodo(dataCons) || isNaN(dataIn) || dataCons < dataIn) return;
      const utente = normN(logC?.utente);
      if (!okUtente(utente)) return;
      out.push({ c, utente, giorni: Math.round((dataCons - dataIn) / 864e5), data: dataCons });
    });
    return out;
  }, [contratti, dal, al, fUtente]);

  // Giorni lavorativi del periodo (lun–sab) per la media giornaliera
  const giorniLav = useMemo(() => {
    let n = 0; const d = new Date(inizio); const f = fine > oggi ? oggi : fine;
    while (d <= f) { if (d.getDay() !== 0) n++; d.setDate(d.getDate() + 1); }
    return Math.max(1, n);
  }, [dal, al]);

  const giornaliero = (fine - inizio) / 864e5 <= 45;
  const serieLiquidati = useMemo(() => {
    const m = new Map();
    if (giornaliero) {
      const d = new Date(inizio); const f = fine > oggi ? oggi : fine;
      while (d <= f) { if (d.getDay() !== 0) m.set(iso(d), { l: `${d.getDate()}/${d.getMonth() + 1}`, v: 0 }); d.setDate(d.getDate() + 1); }
      liquidazioni.forEach(e => { const k = iso(e.data); if (m.has(k)) m.get(k).v++; });
    } else {
      const d = new Date(inizio.getFullYear(), inizio.getMonth(), 1);
      while (d <= fine) { m.set(`${d.getFullYear()}-${d.getMonth()}`, { l: MESI_BREVI[d.getMonth()], v: 0 }); d.setMonth(d.getMonth() + 1); }
      liquidazioni.forEach(e => { const k = `${e.data.getFullYear()}-${e.data.getMonth()}`; if (m.has(k)) m.get(k).v++; });
    }
    return [...m.values()];
  }, [liquidazioni, dal, al]);
  const serieLavorazione = useMemo(() => {
    const m = new Map(); const d = new Date(inizio.getFullYear(), inizio.getMonth(), 1);
    while (d <= fine) { m.set(`${d.getFullYear()}-${d.getMonth()}`, { l: MESI_BREVI[d.getMonth()], s: 0, n: 0 }); d.setMonth(d.getMonth() + 1); }
    consegne.forEach(x => { const k = `${x.data.getFullYear()}-${x.data.getMonth()}`; if (m.has(k)) { m.get(k).s += x.giorni; m.get(k).n++; } });
    return [...m.values()].map(x => ({ l: x.l, v: x.n ? Math.round(x.s / x.n) : 0 }));
  }, [consegne, dal, al]);

  const perUtente = useMemo(() => {
    const m = {};
    liquidazioni.forEach(e => { (m[e.utente] = m[e.utente] || { utente: e.utente, liq: 0, cons: [], }).liq++; });
    consegne.forEach(x => { (m[x.utente] = m[x.utente] || { utente: x.utente, liq: 0, cons: [] }).cons.push(x.giorni); });
    return Object.values(m).sort((a, b) => (b.liq + b.cons.length) - (a.liq + a.cons.length));
  }, [liquidazioni, consegne]);

  const mediaLav = consegne.length ? Math.round(consegne.reduce((t, x) => t + x.giorni, 0) / consegne.length) : 0;

  // ── Confronto: stesse metriche sul secondo periodo ──
  const confronto = useMemo(() => {
    if (!confDal || !confAl) return null;
    const ini = new Date(`${confDal}T00:00:00`), fin = new Date(`${confAl}T23:59:59`);
    const dentro = d => d && !isNaN(d) && d >= ini && d <= fin;
    const ultima = new Map();
    eventi.filter(e => e.campo === 'liquidato' && e.valore === true && e.c.backoffice?.liquidato).forEach(e => {
      const prev = ultima.get(e.c.id); if (!prev || e.data > prev.data) ultima.set(e.c.id, e);
    });
    const liq = [...ultima.values()].filter(e => dentro(e.data) && okUtente(e.utente)).length;
    const cons = [];
    contratti.forEach(c => {
      if (c.backoffice?.statoConsegna !== 'consegnato') return;
      const logC = Object.values(c.backoffice_log || {}).filter(e => e && e.campo === 'statoConsegna' && e.valore === 'consegnato' && e.timestamp)
        .sort((x, y) => y.timestamp.localeCompare(x.timestamp))[0];
      const dataCons = new Date(logC?.timestamp || c.vendutoAt || c.dataVendita || '');
      const dataIn = new Date(c.approvazioneData || '');
      if (!dentro(dataCons) || isNaN(dataIn) || dataCons < dataIn) return;
      if (!okUtente(normN(logC?.utente))) return;
      cons.push(Math.round((dataCons - dataIn) / 864e5));
    });
    let gl = 0; const d = new Date(ini); const f = fin > oggi ? oggi : fin;
    while (d <= f) { if (d.getDay() !== 0) gl++; d.setDate(d.getDate() + 1); }
    gl = Math.max(1, gl);
    return { liq, mediaLiq: liq / gl, cons: cons.length, mediaLav: cons.length ? Math.round(cons.reduce((a, b) => a + b, 0) / cons.length) : 0 };
  }, [eventi, contratti, confDal, confAl, fUtente]);
  const ui = useContext(AnalisiUiCtx);
  const card = (t, v, sub, col, varz) => (
    <div style={{ background: T.surface || T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${col === T.text ? T.border : col}`, borderRadius: '14px', padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: T.textSub || T.textMuted }}>{t}</div>
      <div style={{ fontSize: 'clamp(19px, 4.8vw, 26px)', fontWeight: '900', color: col, marginTop: '4px', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
      {sub && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{sub}</div>}
      {varz}
    </div>
  );
  const inS = { padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light' };

  return (
    <div>
      <div className="analisi-no-stampa" style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', marginBottom: '14px', alignItems: 'center', background: T.surface || T.card, border: `1px solid ${T.border}`, borderRadius: '16px', padding: ui.isMobile ? '12px' : '16px 18px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: T.textMuted, fontWeight: '800' }}>DAL <input type="date" value={dal} onChange={e => setDal(e.target.value)} style={{ ...inS, flex: 1 }} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: T.textMuted, fontWeight: '800' }}>AL <input type="date" value={al} onChange={e => setAl(e.target.value)} style={{ ...inS, flex: 1 }} /></label>
        <RadarMultiSelect label="UTENTE BO / ADMIN" options={utentiBO} selected={fUtente} onChange={setFUtente} onClear={() => setFUtente([])} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: '#8b5cf6', fontWeight: '800' }}>CONFRONTO DAL <input type="date" value={confDal} onChange={e => setConfDal(e.target.value)} style={{ ...inS, flex: 1, borderColor: confDal ? '#8b5cf6' : undefined }} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: '#8b5cf6', fontWeight: '800' }}>AL <input type="date" value={confAl} onChange={e => setConfAl(e.target.value)} style={{ ...inS, flex: 1, borderColor: confAl ? '#8b5cf6' : undefined }} /></label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(210px, 1fr))', gap: ui.isMobile ? '8px' : '10px', marginBottom: '16px' }}>
        {card('Finanziamenti liquidati', liquidazioni.length, 'nel periodo', '#10b981', confronto && <Variazione T={T} cur={liquidazioni.length} prev={confronto.liq} meglio="su" />)}
        {card('Media liquidati al giorno', (liquidazioni.length / giorniLav).toFixed(2).replace('.', ','), `su ${giorniLav} giorni lavorativi (lun–sab)`, '#10b981', confronto && <Variazione T={T} cur={liquidazioni.length / giorniLav} prev={confronto.mediaLiq} fmt={v => v.toFixed(2).replace('.', ',')} meglio="su" />)}
        {card('Auto consegnate', consegne.length, 'nel periodo', T.text, confronto && <Variazione T={T} cur={consegne.length} prev={confronto.cons} meglio="su" />)}
        {card('Giorni medi in lavorazione', `${mediaLav} gg`, "dall'approvazione alla consegna", '#f59e0b', confronto && <Variazione T={T} cur={mediaLav} prev={confronto.mediaLav} fmt={v => `${Math.round(v)} gg`} meglio="giu" />)}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <GraficoBarre T={T} titolo={`Finanziamenti liquidati ${giornaliero ? 'al giorno' : 'al mese'}`} riepilogo={`${liquidazioni.length}`} coloreRiepilogo="#10b981"
          sottotitolo={giornaliero ? 'Periodo fino a 45 giorni: un valore per giorno lavorativo' : 'Periodo lungo: totale per mese (restringi le date per il dettaglio giornaliero)'}
          dati={serieLiquidati} formato={v => `${v}`} colore="#10b981" />
        <GraficoBarre T={T} titolo="Giorni medi in lavorazione prima della consegna" riepilogo={`${mediaLav} gg`} coloreRiepilogo="#f59e0b"
          sottotitolo="Per mese di consegna: dall'approvazione del contratto alla consegna"
          dati={serieLavorazione} formato={v => `${v}`} colore="#f59e0b" />
        <SezioneEstendibile T={T} titolo="Dettaglio per utente" riepilogo={`${perUtente.length} utenti`}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '520px' }}>
              <thead><tr>{['Utente', 'Liquidati', 'Media al giorno', 'Consegne', 'Media gg lavorazione'].map(h => (
                <th key={h} style={{ textAlign: h === 'Utente' ? 'left' : 'right', padding: '6px 8px', color: T.textMuted, fontWeight: '800', borderBottom: `1px solid ${T.border}` }}>{h}</th>))}</tr></thead>
              <tbody>
                {perUtente.map(x => (
                  <tr key={x.utente}>
                    <td style={{ padding: '6px 8px', color: T.text, fontWeight: '700' }}>{x.utente}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#10b981' }}>{x.liq}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#10b981' }}>{(x.liq / giorniLav).toFixed(2).replace('.', ',')}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: T.text }}>{x.cons.length}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#f59e0b' }}>{x.cons.length ? `${Math.round(x.cons.reduce((a, b) => a + b, 0) / x.cons.length)} gg` : '—'}</td>
                  </tr>
                ))}
                {!perUtente.length && <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>Nessuna attività nel periodo</td></tr>}
              </tbody>
            </table>
          </div>
        </SezioneEstendibile>
      </div>
      <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '10px' }}>
        I dati vengono dal log del Back Office: liquidazioni e consegne registrate prima dell'attivazione del log non hanno l'utente e non compaiono.
      </div>
    </div>
  );
}

// ─── ANALISI · STORICO BLOCCHI ──────────────────────────────────────────────
// Auto che erano bloccate (contratto, trattativa, blocco admin) e poi sono
// state liberate: chi le bloccava, per quanto tempo, chi le ha liberate.
// Fonti: contratti annullati, cambi veicolo sui contratti, azioni sul telaio
// registrate da RadaR (rimetti disponibile, libera trattativa, sblocco admin).
function StoricoBlocchiView({ contratti = [] }) {
  const theme = useTheme(); const T = THEMES[theme];
  const ui = useContext(AnalisiUiCtx);
  const oggi = new Date();
  const [dal, setDal] = useState(`${oggi.getFullYear()}-01-01`);
  const [al, setAl] = useState(oggi.toISOString().slice(0, 10));
  const [fTipo, setFTipo] = useState([]);
  const [fChi, setFChi] = useState([]);
  const [testo, setTesto] = useState('');
  const [azioni, setAzioni] = useState({});
  const [stockS, setStockS] = useState([]);
  useEffect(() => {
    get(ref(db, 'azioni_telaio')).then(snap => setAzioni(snap.val() || {})).catch(() => {});
    veicoliStockPerExport().then(setStockS).catch(() => {});
  }, []);
  const norm = x => String(x || '').replace(/\s+/g, ' ').trim();
  const cliente = c => c?.ragioneSociale || norm(`${c?.cognome || ''} ${c?.nome || ''}`);

  const eventi = useMemo(() => {
    const out = [];
    contratti.forEach(c => {
      const v = c.veicolo || {};
      const dal0 = c.approvazioneData || c.dataCreazione;
      if (c.stato === 'annullato' && c.annullamentoData) {
        out.push({ tipo: 'Contratto annullato', auto: `${v.marca || ''} ${v.modello || ''}`.trim(), targa: v.targa || v.vin || '',
          bloccatoDa: norm(c.venditoreNome), cliente: cliente(c.cliente), dal: dal0, liberata: c.annullamentoData,
          liberataDa: norm(c.annullamentoNome), motivo: c.annullamentoMotivazione || '', contratto: c.datiExtra?.numeroContratto || c.id });
      }
      const sost = c.veicoliSostituiti ? Object.values(c.veicoliSostituiti) : (c.veicoloSostituito ? [c.veicoloSostituito] : []);
      sost.forEach(x => {
        if (!x?.timestamp) return;
        out.push({ tipo: 'Cambio veicolo', auto: `${x.da?.marca || ''} ${x.da?.modello || ''}`.trim(), targa: x.da?.targa || x.da?.vin || '',
          bloccatoDa: norm(c.venditoreNome), cliente: cliente(c.cliente), dal: dal0, liberata: x.timestamp,
          liberataDa: norm(x.utente), motivo: `Sostituita con ${x.a?.marca || ''} ${x.a?.modello || ''} ${x.a?.targa || ''}`.trim(), contratto: c.datiExtra?.numeroContratto || c.id });
      });
    });
    Object.values(azioni).forEach(lista => (Array.isArray(lista) ? lista : Object.values(lista || {})).forEach(a => {
      if (!a || a.tipo !== 'liberazione') return;
      out.push({ tipo: /trattativa/i.test(a.motivo) ? 'Trattativa liberata' : /blocco admin/i.test(a.motivo) ? 'Sblocco blocco admin' : 'Rimessa disponibile',
        auto: `${a.marca || ''} ${a.modello || ''}`.trim(), targa: a.targa || a.vin || '', bloccatoDa: norm(a.bloccatoDa), cliente: a.cliente || '',
        dal: null, liberata: a.data, liberataDa: norm(a.autore), motivo: a.motivo || '', contratto: '' });
    }));
    // Storico precedente al registro: auto rimesse disponibili a mano che
    // hanno la data di ripristino salvata nello stock. Chi la bloccava si
    // ricava dall'ultimo contratto su quell'auto; chi l'ha liberata non è noto.
    const nVT = x => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const giaRegistrate = new Set(out.filter(e => e.tipo !== 'Contratto annullato' && e.tipo !== 'Cambio veicolo').map(e => `${nVT(e.targa)}|${String(e.liberata).slice(0, 10)}`));
    stockS.forEach(v => {
      if (!v?.ripristinatoAt || v.ripristinatoPer !== 'ripristino manuale admin') return;
      const chiave = `${nVT(v.targa || v.vin)}|${String(v.ripristinatoAt).slice(0, 10)}`;
      if (giaRegistrate.has(chiave)) return;
      const ultimo = contratti.filter(c => (v.vin && nVT(c.veicolo?.vin || c.datiExtra?.vin) === nVT(v.vin)) || (v.targa && nVT(c.veicolo?.targa) === nVT(v.targa)))
        .sort((a, b) => String(b.dataCreazione).localeCompare(String(a.dataCreazione)))[0];
      out.push({ tipo: 'Rimessa disponibile', auto: `${v.marca || ''} ${v.modello || ''}`.trim(), targa: v.targa || v.vin || '',
        bloccatoDa: norm(ultimo?.venditoreNome), cliente: ultimo ? cliente(ultimo.cliente) : '', dal: ultimo ? (ultimo.approvazioneData || ultimo.dataCreazione) : null,
        liberata: v.ripristinatoAt, liberataDa: 'N.D. (prima del registro)', motivo: 'Ripristino manuale admin (dato ricostruito)', contratto: ultimo?.datiExtra?.numeroContratto || '' });
    });
    return out.map(e => {
      const d1 = new Date(e.liberata), d0 = e.dal ? new Date(e.dal) : null;
      return { ...e, dLib: d1, giorni: d0 && !isNaN(d0) && d1 >= d0 ? Math.round((d1 - d0) / 864e5) : null };
    }).filter(e => !isNaN(e.dLib)).sort((a, b) => b.dLib - a.dLib);
  }, [contratti, azioni, stockS]);

  const ini = new Date(`${dal}T00:00:00`), fin = new Date(`${al}T23:59:59`);
  const filtrati = eventi.filter(e => e.dLib >= ini && e.dLib <= fin
    && (!fTipo.length || fTipo.includes(e.tipo))
    && (!fChi.length || fChi.includes(e.bloccatoDa || 'N.D.'))
    && (!testo.trim() || `${e.auto} ${e.targa} ${e.bloccatoDa} ${e.cliente} ${e.liberataDa} ${e.motivo} ${e.contratto}`.toLowerCase().includes(testo.trim().toLowerCase())));
  const tipi = [...new Set(eventi.map(e => e.tipo))].sort();
  const chi = [...new Set(eventi.map(e => e.bloccatoDa || 'N.D.'))].sort();
  const conG = filtrati.filter(e => e.giorni !== null);
  const perChi = Object.entries(filtrati.reduce((m, e) => { const k = e.bloccatoDa || 'N.D.'; m[k] = (m[k] || 0) + 1; return m; }, {})).sort((a, b) => b[1] - a[1]);
  const fmtD = d => (d ? new Date(d).toLocaleDateString('it-IT') : '—');
  const inS = { padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light', flex: 1, minWidth: 0 };
  const card = (t, v, sub, col) => (
    <div style={{ background: T.surface || T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${col}`, borderRadius: '14px', padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: T.textSub || T.textMuted }}>{t}</div>
      <div style={{ fontSize: 'clamp(19px, 4.8vw, 26px)', fontWeight: '900', color: col, marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
      {sub && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{sub}</div>}
    </div>
  );
  const coloreTipo = t => ({ 'Contratto annullato': '#ef4444', 'Cambio veicolo': '#3b82f6', 'Trattativa liberata': '#f59e0b', 'Sblocco blocco admin': '#8b5cf6' })[t] || '#10b981';

  return (
    <div>
      <div className="analisi-no-stampa" style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', marginBottom: '14px', background: T.surface || T.card, border: `1px solid ${T.border}`, borderRadius: '16px', padding: ui.isMobile ? '12px' : '16px 18px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: T.textMuted, fontWeight: '700' }}>Liberata dal <input type="date" value={dal} onChange={e => setDal(e.target.value)} style={inS} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: T.textMuted, fontWeight: '700' }}>al <input type="date" value={al} onChange={e => setAl(e.target.value)} style={inS} /></label>
        <RadarMultiSelect label="BLOCCATA DA" options={chi} selected={fChi} onChange={setFChi} onClear={() => setFChi([])} />
        <RadarMultiSelect label="COME È STATA LIBERATA" options={tipi} selected={fTipo} onChange={setFTipo} onClear={() => setFTipo([])} />
        <input value={testo} onChange={e => setTesto(e.target.value)} placeholder="Cerca auto, targa, cliente, contratto…" style={{ ...inS, fontWeight: '600' }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(210px, 1fr))', gap: ui.isMobile ? '8px' : '10px', marginBottom: '16px' }}>
        {card('Auto liberate', filtrati.length, 'nel periodo', T.text)}
        {card('Giorni medi bloccate', conG.length ? `${Math.round(conG.reduce((t, e) => t + e.giorni, 0) / conG.length)} gg` : '—', `su ${conG.length} con data di blocco`, '#f59e0b')}
        {card('Chi ha bloccato di più', perChi[0] ? perChi[0][0] : '—', perChi[0] ? `${perChi[0][1]} auto poi liberate` : null, '#ef4444')}
        {card('Annullamenti', filtrati.filter(e => e.tipo === 'Contratto annullato').length, 'contratti annullati', '#ef4444')}
      </div>

      <SezioneEstendibile T={T} titolo="Elenco auto liberate" riepilogo={`${filtrati.length}`}>
        {ui.isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filtrati.map((e, i) => (
              <div key={i} style={{ border: `1px solid ${T.border}`, borderLeft: `3px solid ${coloreTipo(e.tipo)}`, borderRadius: '12px', padding: '10px 12px', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <b style={{ color: T.text }}>{e.auto || 'Auto'} {e.targa && <span style={{ color: T.textMuted, fontWeight: '600' }}>{e.targa}</span>}</b>
                  <span style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{fmtD(e.liberata)}</span>
                </div>
                <div style={{ color: coloreTipo(e.tipo), fontWeight: '800', marginTop: '3px' }}>{e.tipo}</div>
                <div style={{ color: T.textSub || T.textMuted, marginTop: '4px', lineHeight: 1.45 }}>
                  Bloccata da <b style={{ color: T.text }}>{e.bloccatoDa || 'N.D.'}</b>{e.cliente ? ` per ${e.cliente}` : ''}{e.dal ? ` dal ${fmtD(e.dal)}` : ''}{e.giorni !== null ? ` (${e.giorni} gg)` : ''}.
                  Liberata da <b style={{ color: T.text }}>{e.liberataDa || 'N.D.'}</b>{e.motivo ? `: ${e.motivo}` : ''}
                </div>
              </div>
            ))}
            {!filtrati.length && <div style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>Nessuna auto liberata nel periodo</div>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '960px' }}>
              <thead><tr>{['Liberata il', 'Auto', 'Come', 'Bloccata da', 'Cliente', 'Bloccata dal', 'Giorni', 'Liberata da', 'Motivo / contratto'].map(h => (
                <th key={h} style={{ textAlign: h === 'Giorni' ? 'right' : 'left', padding: '7px 8px', color: T.textMuted, fontWeight: '800', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>))}</tr></thead>
              <tbody>
                {filtrati.map((e, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '7px 8px', color: T.text, whiteSpace: 'nowrap' }}>{fmtD(e.liberata)}</td>
                    <td style={{ padding: '7px 8px', color: T.text, fontWeight: '700' }}>{e.auto}<div style={{ color: T.textMuted, fontWeight: '500' }}>{e.targa}</div></td>
                    <td style={{ padding: '7px 8px', color: coloreTipo(e.tipo), fontWeight: '800', whiteSpace: 'nowrap' }}>{e.tipo}</td>
                    <td style={{ padding: '7px 8px', color: T.text, fontWeight: '700' }}>{e.bloccatoDa || 'N.D.'}</td>
                    <td style={{ padding: '7px 8px', color: T.textSub || T.textMuted }}>{e.cliente || '—'}</td>
                    <td style={{ padding: '7px 8px', color: T.textSub || T.textMuted, whiteSpace: 'nowrap' }}>{fmtD(e.dal)}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', color: '#f59e0b', fontWeight: '700' }}>{e.giorni !== null ? e.giorni : '—'}</td>
                    <td style={{ padding: '7px 8px', color: T.text }}>{e.liberataDa || 'N.D.'}</td>
                    <td style={{ padding: '7px 8px', color: T.textSub || T.textMuted, maxWidth: '260px' }}>{e.motivo}{e.contratto ? <div style={{ color: T.textMuted }}>Contratto {e.contratto}</div> : null}</td>
                  </tr>
                ))}
                {!filtrati.length && <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>Nessuna auto liberata nel periodo</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </SezioneEstendibile>
      <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '10px', lineHeight: 1.5 }}>
        Annullamenti e cambi veicolo sono ricostruiti dai contratti, anche per il passato. Le liberazioni manuali (rimetti disponibile, libera trattativa, sblocco admin) sono registrate da oggi in poi.
      </div>
    </div>
  );
}

// ─── ANALISI · LOG ACCESSI ───────────────────────────────────────────────────
// Login riusciti registrati da RadaR (log_accessi): chi, quando, metodo e
// dispositivo. Letto solo quando si apre questo sotto-tab.
function LogAccessiView() {
  const theme = useTheme(); const T = THEMES[theme];
  const ui = useContext(AnalisiUiCtx);
  const oggi = new Date();
  const meseFa = new Date(oggi.getTime() - 30 * 864e5);
  const [dal, setDal] = useState(meseFa.toISOString().slice(0, 10));
  const [al, setAl] = useState(oggi.toISOString().slice(0, 10));
  const [fUtente, setFUtente] = useState([]);
  const [testo, setTesto] = useState('');
  const [righe, setRighe] = useState([]);
  const [utenti, setUtenti] = useState({});
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState('');
  useEffect(() => {
    setCaricando(true); setErrore('');
    Promise.all([
      get(query(ref(db, 'log_accessi'), orderByChild('at'), startAt(`${dal}T00:00:00`))),
      Object.keys(utenti).length ? Promise.resolve(null) : get(ref(db, 'utenti')),
    ]).then(([snapLog, snapUt]) => {
      if (snapUt) setUtenti(snapUt.val() || {});
      const fine = `${al}T23:59:59.999Z`;
      setRighe(Object.values(snapLog.val() || {}).filter(r => r && r.at && r.at <= fine).sort((a, b) => b.at.localeCompare(a.at)));
    }).catch(e => setErrore(/permission/i.test(String(e?.message)) ? 'Permesso negato: aggiungi alle regole del database il nodo log_accessi (vedi istruzioni).' : String(e?.message || e)))
      .finally(() => setCaricando(false));
  }, [dal, al]);
  const perUid = useMemo(() => Object.fromEntries(Object.entries(utenti).map(([uid, u]) => [uid, u])), [utenti]);
  const nome = r => { const u = perUid[r.uid]; return u ? `${u.nome || u.Nome || ''} ${u.cognome || u.Cognome || ''}`.trim() || r.email : r.email; };
  const ruolo = r => (perUid[r.uid]?.ruolo || perUid[r.uid]?.Ruolo || '');
  const nomiUtenti = [...new Set(righe.map(nome))].sort();
  const filtrate = righe.filter(r => (!fUtente.length || fUtente.includes(nome(r)))
    && (!testo.trim() || `${nome(r)} ${r.email} ${ruolo(r)} ${r.tipo} ${r.so} ${r.browser} ${r.metodo}`.toLowerCase().includes(testo.trim().toLowerCase())));
  const conMfa = filtrate.filter(r => /authenticator/i.test(r.metodo || '')).length;
  // Ultimo accesso di ogni utente attivo (anche chi non è entrato nel periodo)
  const ultimi = useMemo(() => {
    const m = {};
    righe.forEach(r => { if (!m[r.uid] || r.at > m[r.uid].at) m[r.uid] = r; });
    return Object.entries(perUid).filter(([, u]) => String(u?.ruolo || u?.Ruolo || '').toLowerCase() !== 'disattivato')
      .map(([uid, u]) => ({ uid, nome: `${u.nome || u.Nome || ''} ${u.cognome || u.Cognome || ''}`.trim() || u.email || uid, ruolo: u.ruolo || u.Ruolo || '', ultimo: m[uid]?.at || null }))
      .sort((a, b) => String(b.ultimo || '').localeCompare(String(a.ultimo || '')));
  }, [righe, perUid]);
  const fmtDT = iso => new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const inS = { padding: '9px 10px', background: T.inputBg || T.bg, border: `1px solid ${T.border}`, borderRadius: '8px', color: T.text, fontSize: '11px', fontWeight: '700', colorScheme: theme === 'dark' ? 'dark' : 'light', flex: 1, minWidth: 0 };
  const card = (t, v, sub, col) => (
    <div style={{ background: T.surface || T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${col}`, borderRadius: '14px', padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: T.textSub || T.textMuted }}>{t}</div>
      <div style={{ fontSize: 'clamp(19px, 4.8vw, 26px)', fontWeight: '900', color: col, marginTop: '4px' }}>{v}</div>
      {sub && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{sub}</div>}
    </div>
  );
  return (
    <div>
      <div className="analisi-no-stampa" style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', marginBottom: '14px', background: T.surface || T.card, border: `1px solid ${T.border}`, borderRadius: '16px', padding: ui.isMobile ? '12px' : '16px 18px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: T.textMuted, fontWeight: '700' }}>Dal <input type="date" value={dal} onChange={e => setDal(e.target.value)} style={inS} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: T.textMuted, fontWeight: '700' }}>Al <input type="date" value={al} onChange={e => setAl(e.target.value)} style={inS} /></label>
        <RadarMultiSelect label="UTENTE" options={nomiUtenti} selected={fUtente} onChange={setFUtente} onClear={() => setFUtente([])} />
        <input value={testo} onChange={e => setTesto(e.target.value)} placeholder="Cerca utente, ruolo, dispositivo…" style={{ ...inS, fontWeight: '600' }} />
      </div>
      {errore && <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '700', marginBottom: '12px' }}>{errore}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(210px, 1fr))', gap: ui.isMobile ? '8px' : '10px', marginBottom: '16px' }}>
        {card('Accessi', caricando ? '…' : filtrate.length, 'login riusciti nel periodo', T.text)}
        {card('Utenti diversi', new Set(filtrate.map(r => r.uid)).size, 'hanno fatto almeno un login', '#3b82f6')}
        {card('Con Authenticator', filtrate.length ? `${Math.round((conMfa / filtrate.length) * 100)}%` : '—', `${conMfa} accessi con codice`, '#10b981')}
        {card('Da smartphone', filtrate.filter(r => r.tipo === 'Smartphone').length, 'accessi da telefono', '#8b5cf6')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <SezioneEstendibile T={T} titolo="Elenco accessi" riepilogo={`${filtrate.length}`}>
          {ui.isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {filtrate.map((r, i) => (
                <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: '10px', padding: '8px 10px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}><b style={{ color: T.text }}>{nome(r)}</b><span style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{fmtDT(r.at)}</span></div>
                  <div style={{ color: T.textMuted, marginTop: '2px' }}>{r.tipo} · {r.so} · {r.browser} · {r.metodo}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '760px' }}>
                <thead><tr>{['Data e ora', 'Utente', 'Ruolo', 'Metodo', 'Dispositivo', 'Sistema', 'Browser'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 8px', color: T.textMuted, fontWeight: '800', borderBottom: `1px solid ${T.border}` }}>{h}</th>))}</tr></thead>
                <tbody>
                  {filtrate.map((r, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '6px 8px', color: T.text, whiteSpace: 'nowrap' }}>{fmtDT(r.at)}</td>
                      <td style={{ padding: '6px 8px', color: T.text, fontWeight: '700' }}>{nome(r)}</td>
                      <td style={{ padding: '6px 8px', color: T.textMuted }}>{ruolo(r)}</td>
                      <td style={{ padding: '6px 8px', color: /authenticator/i.test(r.metodo || '') ? '#10b981' : T.textSub || T.textMuted }}>{r.metodo}</td>
                      <td style={{ padding: '6px 8px', color: T.textSub || T.textMuted }}>{r.tipo}</td>
                      <td style={{ padding: '6px 8px', color: T.textSub || T.textMuted }}>{r.so}</td>
                      <td style={{ padding: '6px 8px', color: T.textSub || T.textMuted }}>{r.browser}</td>
                    </tr>
                  ))}
                  {!filtrate.length && <tr><td colSpan={7} style={{ padding: '14px', textAlign: 'center', color: T.textMuted }}>{caricando ? 'Caricamento…' : 'Nessun accesso nel periodo'}</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </SezioneEstendibile>
        <SezioneEstendibile T={T} titolo="Ultimo accesso per utente" riepilogo={`${ultimi.filter(u => !u.ultimo).length} senza accessi nel periodo`}>
          <div style={{ display: 'grid', gridTemplateColumns: ui.isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '6px' }}>
            {ultimi.map(u => (
              <div key={u.uid} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '12px', padding: '6px 8px', border: `1px solid ${T.border}`, borderRadius: '8px' }}>
                <span style={{ color: T.text, fontWeight: '700' }}>{u.nome} <span style={{ color: T.textMuted, fontWeight: '500' }}>{u.ruolo}</span></span>
                <span style={{ color: u.ultimo ? T.textSub || T.textMuted : '#ef4444', whiteSpace: 'nowrap' }}>{u.ultimo ? fmtDT(u.ultimo) : 'nessuno'}</span>
              </div>
            ))}
          </div>
        </SezioneEstendibile>
      </div>
      <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '10px' }}>Sono registrati i login riusciti da quando è attivo il log; i ricaricamenti di pagina con sessione già aperta non contano come accesso.</div>
    </div>
  );
}

// Contenitore del tab ANALISI con i sotto-tab
function AnalisiView(props) {
  const theme = useTheme(); const T = THEMES[theme];
  const [sub, setSub] = useState('economici');
  const larghezza = useLarghezzaFinestra();
  const isMobile = larghezza < 768;
  const [tuttiAperti, setTuttiAperti] = useState(null);
  const stampa = () => { setTuttiAperti(true); setTimeout(() => window.print(), 350); };
  const btn = attivo => ({ padding: isMobile ? '10px 12px' : '9px 16px', borderRadius: '10px', border: `1px solid ${attivo ? '#f59e0b' : T.border}`, background: attivo ? 'rgba(245,158,11,0.12)' : 'transparent', color: attivo ? '#f59e0b' : T.textSub || T.textMuted, fontWeight: '800', fontSize: '12px', cursor: 'pointer', flex: isMobile ? 1 : 'none' });
  return (
    <AnalisiUiCtx.Provider value={{ isMobile, tuttiAperti }}>
      {/* Stampa: solo il contenuto dell'analisi, sezioni aperte, sfondo chiaro */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .analisi-stampa, .analisi-stampa * { visibility: visible !important; }
          .analisi-stampa { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
          .analisi-no-stampa { display: none !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
      <div className="analisi-stampa" style={{ padding: isMobile ? '14px 12px 90px' : '24px 28px', maxWidth: '1320px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', fontWeight: '900', color: T.text, letterSpacing: '-0.01em' }}>Analisi</h1>
            <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '2px' }}>Gruppo Auto Scala · dati RadaR aggiornati in tempo reale</div>
          </div>
          <div className="analisi-no-stampa" style={{ display: 'flex', gap: '6px', width: isMobile ? '100%' : 'auto' }}>
            <button onClick={() => setTuttiAperti(v => !v)} style={btn(false)}>{tuttiAperti ? 'Comprimi tutto' : 'Espandi tutto'}</button>
            <button onClick={stampa} style={btn(false)}>Stampa / PDF</button>
          </div>
        </div>
        <div className="analisi-no-stampa" style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap' }}>
          {[['economici', 'Risultati economici'], ['bo', 'Performance BO'], ['blocchi', 'Storico blocchi'], ...(puoVedereLogAccessi() ? [['accessi', 'Log accessi']] : [])].map(([k, l]) => (
            <button key={k} onClick={() => setSub(k)} style={btn(sub === k)}>{l}</button>
          ))}
        </div>
        {sub === 'economici' ? <AnalisiEconomiciView {...props} /> : sub === 'bo' ? <PerformanceBOView contratti={props.contratti} /> : sub === 'blocchi' ? <StoricoBlocchiView contratti={props.contratti} /> : puoVedereLogAccessi() ? <LogAccessiView /> : null}
      </div>
    </AnalisiUiCtx.Provider>
  );
}

const RadarMultiSelect = ({ label, options, selected, onChange, onClear, fullWidth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme(); const T = THEMES[theme];
  const validOptions = (options || []).filter(opt => opt && opt !== '');
  return (
    <div style={{ position: 'relative', minWidth: fullWidth ? '100%' : '140px', flex: fullWidth ? 'none' : '1 1 140px', display: 'flex', width: fullWidth ? '100%' : undefined }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <div onClick={() => setIsOpen(!isOpen)} style={{ background: T.inputBg, color: T.text, border: `1px solid ${T.border}`, padding: fullWidth ? '11px 12px' : '10px 12px', borderRadius: '10px 0 0 10px', fontSize: fullWidth ? '11px' : '9px', fontWeight: '900', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected.length > 0 ? `${label} (${selected.length})` : label}</span>
          <span style={{ marginLeft: '6px' }}>{isOpen ? '▲' : '▼'}</span>
        </div>
        {isOpen && (
          <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, background: T.dropBg, border: `1px solid ${T.border2}`, borderRadius: '12px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', padding: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}>
            {validOptions.map(opt => (
              <div key={opt} onClick={() => onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt])} style={{ padding: fullWidth ? '10px 8px' : '8px', cursor: 'pointer', fontSize: '11px', color: selected.includes(opt) ? '#3b82f6' : T.textSub, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '14px', height: '14px', border: '1px solid #3b82f6', background: selected.includes(opt) ? '#3b82f6' : 'transparent', borderRadius: '3px', flexShrink: 0 }} />
                {String(opt || 'N.D.').toUpperCase()}
              </div>
            ))}
          </div>
        )}
      </div>
      <button onClick={onClear} style={{ background: T.inputBg, color: '#ef4444', border: `1px solid ${T.border}`, borderLeft: 'none', borderRadius: '0 10px 10px 0', width: fullWidth ? '36px' : '25px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>×</button>
    </div>
  );
};

// ================================================================================
// NOLEGGIO LUNGO TERMINE
// ================================================================================
// ─── LOGISTICA — DDT e movimentazione veicoli tra sedi ─────────────────────
// Info aggiuntive (città per l'intestazione DDT, indirizzo) solo per le sedi
// che conosciamo in anticipo. L'elenco delle sedi selezionabili nel modulo
// Logistica NON è limitato a queste: viene sempre preso dai valori reali
// presenti nello stock (vedi sediDisponibili in LogisticaView), qualunque
// sede compaia lì — comprese quelle non elencate qui sotto.
// Firebase Realtime Database serializza un array come OGGETTO (chiavi
// numeriche) invece che come vero array, se ha degli elementi mancanti (es.
// un elemento cancellato singolarmente lasciando un "buco"). Anche un campo
// come "permessi" può capitare così se impostato a mano su Firebase.
// Chiamare .some()/.map() direttamente su un oggetto lancia "X.some is not
// a function" — da qui questo helper, usato ovunque leggiamo array che
// potrebbero provenire da Firebase in questa forma "a rischio".

const SEDI_CONOSCIUTE = [
  { nome: 'Agnano', citta: 'POZZUOLI (NA)', indirizzo: 'Via Antiniana, 2/B - 80078 Agnano (Pozzuoli, NA)' },
  { nome: 'Nola', citta: 'NOLA (NA)', indirizzo: 'Nola (NA)' },
  { nome: 'Salerno', citta: 'SALERNO (SA)', indirizzo: 'Salerno (SA)' },
];
// Restituisce le info note per una sede, o un fallback generico (solo il
// nome, nessun indirizzo) per qualsiasi sede non presente nell'elenco sopra.
function getInfoSede(nome) {
  const trovata = SEDI_CONOSCIUTE.find(s => s.nome.toLowerCase().trim() === (nome || '').toLowerCase().trim());
  return trovata || { nome, citta: (nome || '').toUpperCase(), indirizzo: '' };
}

const CAUSALI_DDT = ['Trasferimento tra sedi', 'Conto visione', 'Conto vendita', 'Reso', 'Riparazione/Officina', 'Altro'];

// ─── Scheda Veicolo (vetrina) — stampa in stile "cartellino" da esposizione ─
async function generaSchedaVeicolo(car) {
  // Finestra aperta SUBITO, in modo sincrono nel click — se aspettassimo
  // il recupero del testo completo (asincrono) prima di aprirla, il
  // browser la bloccherebbe silenziosamente (stesso principio già visto
  // per il preventivo officina).
  const win = window.open('', '_blank');
  if (!win) return;

  // Il campo "optionals" nello stock è tagliato a 300 caratteri (risparmio
  // banda su ogni download quotidiano di Stock/Radar). Il testo completo,
  // quando esiste, sta in un nodo separato — richiesta mirata per questo
  // singolo veicolo, solo ora che stiamo davvero stampando.
  let optionalsCompleti = car.optionals || '';
  try {
    const snap = await get(ref(db, `stock_optionals_completi/${car.id}`));
    if (snap.exists()) optionalsCompleti = snap.val();
  } catch (e) { /* fallback silenzioso al testo tagliato, meglio di niente */ }

  const anno = car.registrazione ? (() => {
    const parts = car.registrazione.split('/');
    return parts.length === 2 ? `${parts[0].padStart(2,'0')}/${parts[1]}` : car.registrazione;
  })() : '';

  const optionalsList = optionalsCompleti ? optionalsCompleti.split(',').map(s => s.trim()).filter(Boolean) : [];
  const optionalsTesto = optionalsList.length ? optionalsList.join(', ') : 'Nessun optional rilevato';

  // Rata = 1,1% del prezzo di listino, arrotondata all'euro
  const prezzoListino = parseFloat(car.prezzo || 0);
  const rata = Math.round(prezzoListino * 0.011);

  // QR — link WhatsApp click-to-chat con messaggio precompilato specifico
  // per questo veicolo: chi scansiona apre subito una chat con il numero
  // del Gruppo, messaggio già pronto, senza dover scrivere nulla.
  const NUMERO_WHATSAPP = '393317049394';
  const messaggioWA = `Ciao! Sono interessato a ${car.marca} ${car.modello}${car.targa ? ` (targa ${car.targa})` : ''} — potete darmi maggiori informazioni?`;
  const waUrl = `https://wa.me/${NUMERO_WHATSAPP}?text=${encodeURIComponent(messaggioWA)}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(waUrl)}`;

  win.document.write(`<!DOCTYPE html><html><head><title>Scheda ${car.marca} ${car.modello}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700;800&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; background: #ccc; }
  body { font-family: 'Open Sans', Arial, Helvetica, sans-serif; font-weight: 700; display: flex; justify-content: center; }
  /* Pagina A4 a dimensione fissa (210x297mm) — il contenuto reale viene
     misurato e scalato via JS per riempirla "giusto giusto", qualunque sia
     la lunghezza della lista optional. */
  #pagina { width: 210mm; height: 297mm; background: #fff; position: relative; overflow: hidden; }
  #scheda { position: absolute; top: 12mm; left: 0; right: 0; margin: 0 auto; width: 198mm; }
  .logo { text-align: center; margin-bottom: 16px; }
  .logo img { height: 46px; }
  .box { border: 2px solid #1a5fb4; border-radius: 11px; margin-bottom: 12px; }
  .box-label { background: #1a5fb4; color: #fff; text-align: center; font-weight: 800; font-size: 13px; padding: 7px; letter-spacing: 0.5px; border-radius: 9px 9px 0 0; }
  .box-valore { text-align: center; font-weight: 800; font-size: 20px; padding: 11px; color: #111; }
  .box-valore.piccolo { font-size: 13px; padding: 14px 22px; text-align: left; line-height: 1.55; column-count: 3; column-gap: 24px; }
  .riga-doppia { display: flex; gap: 12px; margin-bottom: 12px; }
  .riga-doppia .box { flex: 1; margin-bottom: 0; display: flex; align-items: stretch; overflow: hidden; }
  .riga-doppia .box-label { background: #1a5fb4; color: #fff; font-weight: 800; font-size: 13px; display: flex; align-items: center; justify-content: center; padding: 0 14px; border-radius: 0; white-space: nowrap; }
  .riga-doppia .box-valore { flex: 1; font-size: 17px; padding: 9px; }
  .rata-riga { display: flex; align-items: flex-end; gap: 14px; }
  .rata-box { flex: 1; }
  .rata-box .box-valore { font-size: 32px; padding: 9px; }
  .qr-box { width: 108px; text-align: center; }
  .qr-box img { width: 90px; height: 90px; border: 2px solid #1a5fb4; border-radius: 8px; }
  @media print { html, body { background: #fff; } #pagina { box-shadow: none; } }
  @media screen { #pagina { box-shadow: 0 0 20px rgba(0,0,0,0.25); margin: 20px 0; } }
</style>
</head><body>
<div id="pagina">
  <div id="scheda">
    <div class="logo">
      <img src="${window.location.origin}/logo-blue.png" alt="Gruppo Autoscala" />
    </div>

    <div class="box">
      <div class="box-label">MARCA | MODELLO</div>
      <div class="box-valore">${(car.marca || '').toUpperCase()} ${(car.modello || '').toUpperCase()}</div>
    </div>

    <div class="box">
      <div class="box-label">ALLESTIMENTO</div>
      <div class="box-valore" style="font-size:18px;">${car.versione || '—'}</div>
    </div>

    <div class="riga-doppia">
      <div class="box">
        <div class="box-label">KM</div>
        <div class="box-valore">${car.km ? new Intl.NumberFormat('it-IT').format(car.km) : '—'}</div>
      </div>
      <div class="box">
        <div class="box-label">ANNO</div>
        <div class="box-valore">${anno || '—'}</div>
      </div>
    </div>

    <div class="box">
      <div class="box-label">OPTIONAL</div>
      <div class="box-valore piccolo">${optionalsTesto}</div>
    </div>

    <div class="rata-riga">
      <div class="box rata-box">
        <div class="box-label">RATA</div>
        <div class="box-valore">${rata}€</div>
      </div>
      <div class="qr-box">
        <img src="${qrUrl}" alt="QR" />
      </div>
    </div>

    <div style="text-align:center; font-size:6px; font-weight:400; color:#999; margin-top:8px;">
      Optional soggetti a caricamento automatico da sistema. Controllare sempre la vettura per sicurezza.
    </div>
  </div>
</div>
<script>
  // Misura l'altezza reale e riduce SOLO il font del testo optional finché
  // non entra in una pagina A4 — la larghezza della scheda resta SEMPRE
  // piena, non viene mai ristretta. Niente soglie indovinate in base al
  // numero di optional: si adatta sempre, che siano 3 o 150.
  function adattaEStampa() {
    const pagina = document.getElementById('pagina');
    const scheda = document.getElementById('scheda');
    const optionalEl = document.querySelector('.box-valore.piccolo');
    const alturaDisponibilePx = pagina.clientHeight - (12 * 3.7795 * 2); // 297mm - margini top/bottom equivalenti a 12mm ciascuno, mm->px

    if (optionalEl) {
      let fontSize = 13;
      const fontMinimo = 6.5;
      optionalEl.style.fontSize = fontSize + 'px';
      while (scheda.scrollHeight > alturaDisponibilePx && fontSize > fontMinimo) {
        fontSize -= 0.3;
        optionalEl.style.fontSize = fontSize.toFixed(1) + 'px';
      }
    }
    setTimeout(() => window.print(), 150);
  }
  window.onload = () => setTimeout(adattaEStampa, 350);
</script>
</body></html>`);
  win.document.close();
}

function generaStampaDDT(ddt) {
  const win = window.open('', '_blank');
  if (!win) return;
  const dataStr = new Date(ddt.data).toLocaleDateString('it-IT');
  const sedeOr = getInfoSede(ddt.sedeOrigine);
  const dataConsegna = ddt.dataConsegna ? new Date(ddt.dataConsegna).toLocaleDateString('it-IT') : dataStr;
  const oraConsegna = ddt.oraConsegna || '';

  const righeHTML = toArraySafe(ddt.veicoli).map(v => `
    <tr>
      <td style="padding:8px 10px;border:1px solid #000;font-size:12px;">${v.marca || ''}</td>
      <td style="padding:8px 10px;border:1px solid #000;font-size:12px;">${v.modello || ''}</td>
      <td style="padding:8px 10px;border:1px solid #000;font-size:12px;">${v.targa || ''}</td>
      <td style="padding:8px 10px;border:1px solid #000;font-size:12px;">${v.vin || ''}</td>
    </tr>`).join('') || `<tr><td style="padding:8px 10px;border:1px solid #000;">&nbsp;</td><td style="padding:8px 10px;border:1px solid #000;"></td><td style="padding:8px 10px;border:1px solid #000;"></td><td style="padding:8px 10px;border:1px solid #000;"></td></tr>`;

  win.document.write(`<!DOCTYPE html><html><head><title>DDT ${ddt.numero}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Times New Roman', Georgia, serif; font-size: 13px; color: #000; background: #fff; padding: 50px 60px; }
  .page { max-width: 800px; margin: 0 auto; }
  .logo { text-align: center; margin-bottom: 40px; }
  .logo .g { font-size: 10px; letter-spacing: 8px; color: #2563eb; font-weight: 400; }
  .logo .g { font-size: 10px; letter-spacing: 8px; color: #2563eb; font-weight: 400; }
  .riga-testata { display: flex; justify-content: space-between; margin-bottom: 26px; font-size: 13px; }
  .dest { text-align: right; margin-bottom: 22px; font-size: 13px; line-height: 1.5; }
  .dest a { color: #2563eb; text-decoration: underline; }
  .consegna-label { margin-bottom: 14px; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 26px; }
  th { border: 1px solid #000; padding: 8px 10px; font-size: 12px; font-weight: 700; text-align: center; }
  .riga-info { margin-bottom: 18px; font-size: 13px; }
  .firme { display: flex; justify-content: space-between; margin-top: 60px; }
  .firma-box { width: 42%; }
  .firma-label { font-size: 13px; margin-bottom: 40px; }
  .firma-line { border-bottom: 1px solid #000; }
  .footer { margin-top: 90px; display: flex; justify-content: space-between; font-size: 9px; color: #333; }
  .footer .az { font-weight: 700; }
  .footer div { line-height: 1.6; }
  @media print { body { padding: 30px 50px; } }
</style>
</head><body>
<div class="page">
  <div class="logo">
    <img src="${window.location.origin}/logo-blue.png" alt="Gruppo Autoscala" style="height:52px;" />
  </div>

  <div class="riga-testata">
    <div>${sedeOr.citta || 'POZZUOLI (NA)'}, ${dataStr}.</div>
    <div>DDT N. ${ddt.numero}</div>
  </div>

  <div class="dest">
    <div>: <a href="#">Spet.le</a>: ${ddt.destinatarioNome || ''}</div>
    ${(ddt.destinatarioIndirizzo || '').split(',').map(r => `<div>${r.trim()}</div>`).join('')}
  </div>

  <div class="consegna-label">${ddt.tipo === 'documenti' ? `SI CONSEGNA AL SUDDETTO ${ddt.destinatarioCliente === false ? 'DESTINATARIO' : 'CLIENTE'} QUANTO SEGUE:` : `SI CONSEGNANO AL SUDDETTO ${ddt.destinatarioCliente === false ? 'DESTINATARIO' : 'CLIENTE'} I SEGUENTI VEICOLI:`}</div>

  ${ddt.tipo === 'documenti'
    ? `<div style="border:1px solid #000;padding:14px 16px;margin-bottom:26px;min-height:80px;font-size:13px;white-space:pre-wrap;line-height:1.6;">${(ddt.descrizioneDocumenti || '').replace(/</g, '&lt;')}</div>`
    : `<table>
    <thead><tr><th>MARCA</th><th>MODELLO</th><th>TARGA</th><th>TELAIO</th></tr></thead>
    <tbody>${righeHTML}</tbody>
  </table>`
  }

  <div class="riga-info">DATA E ORA DI CONSEGNA ${dataConsegna}${oraConsegna ? ` ORA ${oraConsegna}` : ''}</div>
  <div class="riga-info">TRASPORTO AFFIDATO A: ${ddt.conducente || ''}</div>
  ${ddt.targaProva ? `<div class="riga-info">TARGA PROVA ${ddt.targaProva}</div>` : ''}

  <div class="firme">
    <div class="firma-box">
      <div class="firma-label">FIRMA CONDUCENTE</div>
      <div class="firma-line"></div>
    </div>
    <div class="firma-box">
      <div class="firma-label">FIRMA DESTINATARIO</div>
      <div class="firma-line"></div>
    </div>
  </div>

  <div class="footer">
    <div>
      <div class="az">Gruppo Autoscala srl.</div>
      <div>Via Antiniana, 2/B</div>
      <div>Ex. Via Scarfoglio</div>
      <div>80078 Agnano (Pozzuoli)</div>
    </div>
    <div style="text-align:right">
      <div>P.IVA e CF 07821241218</div>
      <div>info@gruppoautoscala.com</div>
      <div>Tel. 08118658721</div>
    </div>
  </div>
</div>
<script>window.onload = () => window.print();</script>
</body></html>`);
  win.document.close();
}

// ─── Export Ubicazioni — solo targa, telaio, marca, modello, sede ─────────
function esportaNoleggioExcel(preventivi) {
  if (bloccaExportExcel()) return;
  const sv = v => (v === undefined || v === null) ? '' : v.toString();
  const fd = iso => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d; };

  const headers = [
    'Stato', 'Data', 'Data Consegna', 'Consulente', 'Fonte', 'Tipologia Cliente', 'Dealer/Partner',
    'Cliente Nome', 'Indirizzo', 'P.IVA', 'Codice Fiscale', 'Telefono', 'Email',
    'Marca', 'Modello', 'Versione', 'Colore', 'Alimentazione',
    'Canone (€)', 'Durata (mesi)', 'Anticipo (€)', 'Km Annui',
  ];

  const righe = (preventivi || []).map(p => {
    const cl = p.cliente || {};
    const v = p.veicolo || {};
    // Un preventivo può avere più offerte — qui si esporta la prima
    // (l'offerta principale), coerente con lo stesso criterio già usato
    // per la modalità caparra nei contratti.
    const off = (p.offerte && p.offerte[0]) || {};
    return [
      sv(p.stato), fd(p.data), fd(p.dataConsegna), sv(p.venditoreNome), sv(p.fonte), sv(p.tipologiaCliente), sv(p.dealer),
      sv(cl.nome), sv(cl.indirizzo), sv(cl.piva), sv(cl.cf), sv(cl.telefono), sv(cl.email),
      sv(v.marca), sv(v.modello), sv(v.versione), sv(v.colore), sv(v.alimentazione),
      parseFloat(off.canone) || 0, parseFloat(off.durata) || 0, parseFloat(off.anticipo) || 0, parseFloat(off.kmAnnui) || 0,
    ];
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...righe], { cellDates: true });
  ws['!cols'] = headers.map(() => ({ wch: 18 }));
  applicaFormatiExcel(ws, headers, righe.length, ['Canone (€)', 'Anticipo (€)'], ['Data', 'Data Consegna']);
  XLSX.utils.book_append_sheet(wb, ws, 'Noleggio NLT');
  const oggi = new Date();
  const nomeFile = `RadaR_Noleggio_${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}.xlsx`;
  scriviExcel(wb, nomeFile);
}

function esportaUbicazioniExcel(vehicles) {
  if (bloccaExportExcel()) return;
  const sv = v => (v === undefined || v === null) ? '' : v.toString();
  const headers = ['Targa', 'Telaio (VIN)', 'Marca', 'Modello', 'Ubicazione'];
  const righe = (vehicles || [])
    .filter(v => (v.status || '').toUpperCase() !== 'VENDUTA')
    .map(v => [sv(v.targa), sv(v.vin), sv(v.marca), sv(v.modello), sv(v.sede)]);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...righe]);
  ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 16 }, { wch: 18 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Ubicazioni');
  const oggi = new Date();
  const tag = `${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}`;
  scriviExcel(wb, `Ubicazioni_RadaR_${tag}.xlsx`);
}

function LogisticaView({ user, db }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [ddtList, setDdtList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [cambioSedeOpen, setCambioSedeOpen] = useState(false);
  const [normalizzaOpen, setNormalizzaOpen] = useState(false);
  const [modificaDDT, setModificaDDT] = useState(null);
  const [search, setSearch] = useState('');
  const [fMese, setFMese] = useState('');
  const [fAnno, setFAnno] = useState('');
  const [logList, setLogList] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [sedeLogList, setSedeLogList] = useState([]);
  const [searchLogSedi, setSearchLogSedi] = useState('');
  const [sedeLogOpen, setSedeLogOpen] = useState(false);
  const [bloccandoSedi, setBloccandoSedi] = useState(false);
  const [importandoSedi, setImportandoSedi] = useState(false);
  const inputSediFileRef = useRef(null);

  // Aggiorna SOLO il campo sede, matchando per telaio (VIN) — nessun altro
  // campo del veicolo viene toccato, anche se il file ne contiene altri.
  // Nessun veicolo nuovo viene mai creato: i telai non trovati vengono
  // segnalati, non inseriti.
  const importaSediDaFile = async (file) => {
    setImportandoSedi(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

      if (rows.length === 0) { alert('Il file sembra vuoto.'); setImportandoSedi(false); return; }

      // Trova le colonne giuste tra le intestazioni reali del file, senza
      // pretendere un nome esatto — cerca alias comuni, case-insensitive.
      const headers = Object.keys(rows[0]);
      const findCol = (alias) => headers.find(h => alias.some(a => h.toLowerCase().trim() === a.toLowerCase().trim()));
      const colVin = findCol(['Vin', 'Telaio', 'Telaio (VIN)', 'VIN']);
      const colSede = findCol(['Sede', 'Ubicazione', 'Sede / Ubicazione', 'Concessionario']);

      if (!colVin || !colSede) {
        alert(`Non trovo le colonne necessarie nel file.\nColonne trovate: ${headers.join(', ')}\n\nServe una colonna Telaio/VIN e una colonna Sede/Ubicazione.`);
        setImportandoSedi(false);
        return;
      }

      const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (!stockSnap.exists()) { alert('Stock non trovato.'); setImportandoSedi(false); return; }
      const raw = stockSnap.val();
      const arr = Array.isArray(raw) ? raw : Object.values(raw);
      const idxByVin = {};
      arr.forEach((v, i) => { if (v.vin) idxByVin[norm(v.vin)] = i; });

      let aggiornati = 0, ignorati = 0, nonTrovati = 0;
      const nonTrovatiList = [];
      const merged = [...arr];

      rows.forEach(row => {
        const vin = (row[colVin] || '').toString().trim();
        const sede = (row[colSede] || '').toString().trim();
        if (!vin || !sede) { ignorati++; return; }

        const idx = idxByVin[norm(vin)];
        if (idx === undefined) { nonTrovati++; nonTrovatiList.push(vin); return; }

        merged[idx] = { ...merged[idx], sede, sedeLocked: true };
        aggiornati++;
      });

      await set(ref(db, 'stock_data/vehicles'), merged);
      window._radarStockCache = null; window._radarStockTs = 0;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}

      alert(`✅ Aggiornamento sedi completato!\n📍 Aggiornati: ${aggiornati}\n⏭️ Righe senza VIN/sede valida (ignorate): ${ignorati}${nonTrovati > 0 ? `\n⚠️ Telai non trovati in stock (${nonTrovati}): ${nonTrovatiList.slice(0, 15).join(', ')}${nonTrovatiList.length > 15 ? '...' : ''}` : ''}`);
    } catch (e) {
      alert('❌ Errore durante l\'aggiornamento: ' + e.message);
    }
    setImportandoSedi(false);
  };

  useEffect(() => {
    // Stesso principio applicato oggi ovunque: log storici da consultare,
    // non serve il live — cache TTL 4 ore + IndexedDB.
    let annullato = false;
    const now = Date.now();
    if (window._radarDdtLogTs && (now - window._radarDdtLogTs) < 4*60*60*1000) {
      setLogList(window._radarDdtLogCache || []);
      return;
    }
    idbLeggi('ddt_log_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarDdtLogCache = cache.list; window._radarDdtLogTs = cache.ts;
        setLogList(cache.list);
        return;
      }
      get(ref(db, 'ddt_log')).then(snap => {
        if (annullato) return;
        const data = snap.val() || {};
        const list = Object.values(data).sort((a, b) => new Date(b.data) - new Date(a.data));
        const ts = Date.now();
        window._radarDdtLogCache = list; window._radarDdtLogTs = ts;
        idbScrivi('ddt_log_cache', { list, ts });
        setLogList(list);
      });
    });
    return () => { annullato = true; };
  }, []);

  useEffect(() => {
    let annullato = false;
    const now = Date.now();
    if (window._radarCambioSedeLogTs && (now - window._radarCambioSedeLogTs) < 4*60*60*1000) {
      setSedeLogList(window._radarCambioSedeLogCache || []);
      return;
    }
    idbLeggi('cambio_sede_log_cache').then(cache => {
      if (annullato) return;
      if (cache && (now - cache.ts) < 4*60*60*1000) {
        window._radarCambioSedeLogCache = cache.list; window._radarCambioSedeLogTs = cache.ts;
        setSedeLogList(cache.list);
        return;
      }
      get(ref(db, 'cambio_sede_log')).then(snap => {
        if (annullato) return;
        const data = snap.val() || {};
        const list = Object.values(data).sort((a, b) => new Date(b.data) - new Date(a.data));
        const ts = Date.now();
        window._radarCambioSedeLogCache = list; window._radarCambioSedeLogTs = ts;
        idbScrivi('cambio_sede_log_cache', { list, ts });
        setSedeLogList(list);
      });
    });
    return () => { annullato = true; };
  }, []);

  useEffect(() => {
    let annullato = false;
    const caricaDdt = () => {
      get(ref(db, 'ddt')).then(snap => {
        if (annullato) return;
        const data = snap.val() || {};
        const list = Object.values(data).sort((a, b) => new Date(b.data) - new Date(a.data));
        const ts = Date.now();
        window._radarDdtCache2 = list; window._radarDdtTs2 = ts;
        idbScrivi('ddt_list_cache', { list, ts });
        setDdtList(list);
        setLoading(false);
      });
    };
    const now = Date.now();
    if (window._radarDdtTs2 && (now - window._radarDdtTs2) < 4*60*60*1000) {
      setDdtList(window._radarDdtCache2 || []);
      setLoading(false);
    } else {
      idbLeggi('ddt_list_cache').then(cache => {
        if (annullato) return;
        if (cache && (now - cache.ts) < 4*60*60*1000) {
          window._radarDdtCache2 = cache.list; window._radarDdtTs2 = cache.ts;
          setDdtList(cache.list);
          setLoading(false);
        } else {
          caricaDdt();
        }
      });
    }
    // Refresh immediato reale (non solo invalidazione per "la prossima
    // volta") — usato subito dopo aver creato/modificato un DDT, così
    // compare a schermo istantaneamente invece di richiedere di
    // chiudere e riaprire la schermata.
    window._forzaRefreshDdt = () => { window._radarDdtTs = 0; caricaDdt(); };
    return () => { annullato = true; };
  }, []);

  useEffect(() => {
    if (window._radarStockCache?.vehicles) {
      setVehicles(window._radarStockCache.vehicles);
    } else {
      get(ref(db, 'stock_data')).then(snap => {
        if (snap.val()?.vehicles) {
          window._radarStockCache = snap.val();
          window._radarStockTs = Date.now();
          setVehicles(Array.isArray(snap.val().vehicles) ? snap.val().vehicles : Object.values(snap.val().vehicles));
        }
      });
    }
  }, []);

  // Sedi realmente disponibili: prese dai valori distinti del campo "sede"
  // presenti nello stock — non limitate alle 3 di Gruppo Autoscala, così
  // funziona anche per qualsiasi altra sede/tenant presente nei dati reali.
  const sediDisponibili = useMemo(() => {
    const norm = s => (s || '').toString().trim().toUpperCase();
    const canonici = new Map();
    vehicles.forEach(v => {
      if (!v.sede) return;
      const k = norm(v.sede);
      if (!canonici.has(k)) canonici.set(k, v.sede.toString().trim());
    });
    return [...canonici.values()].sort();
  }, [vehicles]);

  const filtered = ddtList.filter(d => {
    if (fAnno && new Date(d.data).getFullYear() !== parseInt(fAnno)) return false;
    if (fMese && (new Date(d.data).getMonth() + 1) !== parseInt(fMese)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return d.numero?.toLowerCase().includes(s) ||
      d.sedeOrigine?.toLowerCase().includes(s) ||
      d.sedeDestinazione?.toLowerCase().includes(s) ||
      (toArraySafe(d.veicoli)).some(v => `${v.marca} ${v.modello} ${v.targa} ${v.vin}`.toLowerCase().includes(s));
  });

  // Anni realmente presenti tra i DDT, per popolare il filtro anno
  const anniDisponibili = useMemo(() => {
    const anni = new Set(ddtList.map(d => new Date(d.data).getFullYear()));
    anni.add(new Date().getFullYear());
    return [...anni].sort((a, b) => b - a);
  }, [ddtList]);
  const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '19px' : '22px', fontWeight: '900', color: T.text }}>🚚 Logistica</h2>
          <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>{filtered.length} document{filtered.length===1?'o':'i'} di trasporto</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
          <input placeholder="🔍 Cerca DDT, sede, veicolo, targa..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '10px 12px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', flex: isMobile ? '1 1 100%' : 'none', minWidth: isMobile ? '0' : '200px' }} />
          <select value={fMese} onChange={e => setFMese(e.target.value)}
            style={{ padding: '10px 10px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
            <option value="">Tutti i mesi</option>
            {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={fAnno} onChange={e => setFAnno(e.target.value)}
            style={{ padding: '10px 10px', background: T.inputBg, border: `1px solid ${T.border2}`, borderRadius: '10px', color: T.text, fontSize: '12px', outline: 'none', cursor: 'pointer' }}>
            <option value="">Tutti gli anni</option>
            {anniDisponibili.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {puoEsportareExcel(user) && (
          <button onClick={() => esportaUbicazioniExcel(vehicles)}
            style={{ padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: '10px', color: '#10b981', cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
            📊 Esporta Ubicazioni
          </button>
          )}
          {(['admin', 'super_admin', 'logistica', 'osservatore'].includes(user?.ruolo) || user?.permessi?.includes('logistica')) && (
            <button onClick={() => setCambioSedeOpen(true)}
              style={{ padding: '10px 14px', background: 'rgba(139,92,246,0.1)', border: '1px solid #8b5cf6', borderRadius: '10px', color: '#8b5cf6', cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
              📍 Cambio Sede Manuale
            </button>
          )}
          {user?.ruolo === 'super_admin' && (
            <button onClick={() => setNormalizzaOpen(true)}
              style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '10px', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
              🔧 Normalizza Sedi
            </button>
          )}
          {user?.ruolo === 'super_admin' && (
            <button onClick={async () => {
              if (!window.confirm('Bloccare TUTTE le sedi attualmente presenti in stock?\n\nDa questo momento nessun import automatico (StockSpark/CarSpark) potrà più modificare la sede di NESSUN veicolo — resterà quella di adesso finché non la cambi tu manualmente (DDT o Cambio Sede Manuale).\n\nProcedere?')) return;
              setBloccandoSedi(true);
              try {
                const stockSnap = await get(ref(db, 'stock_data/vehicles'));
                if (!stockSnap.exists()) { alert('Stock non trovato.'); setBloccandoSedi(false); return; }
                const raw = stockSnap.val();
                const arr = Array.isArray(raw) ? raw : Object.values(raw);
                let bloccati = 0;
                const aggiornati = arr.map(v => {
                  if (v.sedeLocked || !v.sede) return v;
                  bloccati++;
                  return { ...v, sedeLocked: true };
                });
                await set(ref(db, 'stock_data/vehicles'), aggiornati);
                window._radarStockCache = null; window._radarStockTs = 0;
                try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}
                alert(`✅ Bloccate ${bloccati} sedi — da ora resteranno quelle attuali finché non le cambi tu manualmente.`);
              } catch (e) {
                alert('Errore: ' + e.message);
              }
              setBloccandoSedi(false);
            }} disabled={bloccandoSedi}
              style={{ padding: '10px 14px', background: 'rgba(139,92,246,0.1)', border: '1px solid #8b5cf6', borderRadius: '10px', color: '#8b5cf6', cursor: bloccandoSedi ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap', opacity: bloccandoSedi ? 0.6 : 1 }}>
              {bloccandoSedi ? '⏳ Bloccando...' : '🔒 Blocca Sedi Attuali'}
            </button>
          )}
          {user?.ruolo === 'super_admin' && (
            <>
              <input ref={inputSediFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) importaSediDaFile(e.target.files[0]); e.target.value = ''; }} />
              <button onClick={() => inputSediFileRef.current?.click()} disabled={importandoSedi}
                title="Aggiorna solo la sede dei telai presenti nel file — non tocca nessun altro campo"
                style={{ padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: '10px', color: '#10b981', cursor: importandoSedi ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap', opacity: importandoSedi ? 0.6 : 1 }}>
                {importandoSedi ? '⏳ Aggiornando...' : '📍 Aggiorna Sedi da File'}
              </button>
            </>
          )}
          {user?.ruolo === 'super_admin' && (
            <button onClick={() => setLogOpen(o => !o)}
              style={{ padding: '10px 14px', background: logOpen ? 'rgba(100,116,139,0.15)' : T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
              📋 Log {logList.length > 0 ? `(${logList.length})` : ''}
            </button>
          )}
          {user?.ruolo === 'super_admin' && (
            <button onClick={() => setSedeLogOpen(o => !o)}
              style={{ padding: '10px 14px', background: sedeLogOpen ? 'rgba(100,116,139,0.15)' : T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.textSub, cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
              📍 Log Sedi {sedeLogList.length > 0 ? `(${sedeLogList.length})` : ''}
            </button>
          )}
          <button onClick={() => setNuovoOpen(true)}
            style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap', flex: isMobile ? '1 1 auto' : 'none' }}>
            + Nuovo DDT
          </button>
        </div>
      </div>

      {logOpen && user?.ruolo === 'super_admin' && (
        <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px 20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '900', color: T.textMuted, letterSpacing: '0.5px' }}>📋 REGISTRO MODIFICHE/ELIMINAZIONI DDT</div>
            {user?.ruolo === 'super_admin' && logList.length > 0 && (
              <button onClick={async () => {
                if (!window.confirm('Eliminare TUTTO il registro? Questa azione non è reversibile.')) return;
                await set(ref(db, 'ddt_log'), null);
              }}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontWeight: '800', fontSize: '10px' }}>
                🗑️ Svuota registro
              </button>
            )}
          </div>
          {logList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px', color: T.textMuted, fontSize: '12px' }}>Nessuna modifica o eliminazione registrata.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
              {logList.map(l => (
                <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: T.bg, borderRadius: '9px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '11px', color: T.textSub }}>
                    <b style={{ color: l.azione === 'eliminato' ? '#ef4444' : '#f59e0b' }}>{l.azione === 'eliminato' ? '🗑️ Eliminato' : '✏️ Modificato'}</b>
                    {' '}DDT <b>{l.ddtNumero}</b> da <b>{l.utente}</b> ({l.ruolo}) il {new Date(l.data).toLocaleString('it-IT')}
                    {l.dettagli && <span style={{ color: T.textMuted }}> · {l.dettagli}</span>}
                  </div>
                  {user?.ruolo === 'super_admin' && (
                    <button onClick={async () => { if (window.confirm('Eliminare questa voce di log?')) await set(ref(db, `ddt_log/${l.id}`), null); }}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', padding: '0 4px', flexShrink: 0 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sedeLogOpen && user?.ruolo === 'super_admin' && (
        <div style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px 20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: '900', color: T.textMuted, letterSpacing: '0.5px' }}>📍 REGISTRO CAMBI SEDE MANUALI</div>
            {sedeLogList.length > 0 && (
              <button onClick={async () => {
                if (!window.confirm('Eliminare TUTTO il registro cambi sede? Questa azione non è reversibile.')) return;
                await set(ref(db, 'cambio_sede_log'), null);
              }}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontWeight: '800', fontSize: '10px' }}>
                🗑️ Svuota registro
              </button>
            )}
          </div>
          {sedeLogList.length > 0 && (
            <input value={searchLogSedi} onChange={e => setSearchLogSedi(e.target.value)} placeholder="🔍 Cerca per telaio, targa o modello..."
              style={{ width: '100%', padding: '9px 12px', marginBottom: '10px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
          )}
          {(() => {
            const s = searchLogSedi.trim().toLowerCase();
            const sedeLogFiltrato = s ? sedeLogList.filter(l =>
              (l.vin || '').toLowerCase().includes(s) ||
              (l.targa || '').toLowerCase().includes(s) ||
              (l.veicolo || '').toLowerCase().includes(s)
            ) : sedeLogList;
            if (sedeLogList.length === 0) return (
              <div style={{ textAlign: 'center', padding: '16px', color: T.textMuted, fontSize: '12px' }}>Nessun cambio sede manuale registrato.</div>
            );
            if (sedeLogFiltrato.length === 0) return (
              <div style={{ textAlign: 'center', padding: '16px', color: T.textMuted, fontSize: '12px' }}>Nessun risultato per "{searchLogSedi}".</div>
            );
            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
              {sedeLogFiltrato.map(l => (
                <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: T.bg, borderRadius: '9px', border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: '11px', color: T.textSub }}>
                    <b style={{ color: '#0ea5e9' }}>{l.veicolo}</b> {l.targa && `(${l.targa})`}
                    {l.vin && <span style={{ color: T.textMuted, fontSize: '10px' }}> · telaio {l.vin}</span>}
                    {' — '}<span style={{ color: '#f59e0b' }}>{l.sedeVecchia}</span> → <span style={{ color: '#10b981' }}>{l.sedeNuova}</span>
                    {' · '}da <b>{l.utente}</b> ({l.ruolo}) il {new Date(l.data).toLocaleString('it-IT')}
                  </div>
                  <button onClick={async () => { if (window.confirm('Eliminare questa voce di log?')) await set(ref(db, `cambio_sede_log/${l.id}`), null); }}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', padding: '0 4px', flexShrink: 0 }}>×</button>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted, background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}` }}>
          Nessun documento di trasporto {search ? 'trovato' : 'ancora creato'}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(d => (
            <div key={d.id} style={{ background: T.surface, borderRadius: '16px', border: `1px solid ${T.border}`, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: '900', color: '#0ea5e9' }}>{d.numero}</span>
                  <span style={{ fontSize: '11px', color: T.textMuted }}>{new Date(d.data).toLocaleDateString('it-IT')}</span>
                  {d.tipo === 'documenti' && (
                    <span style={{ fontSize: '9px', fontWeight: '900', color: '#8b5cf6', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.35)', borderRadius: '20px', padding: '2px 8px' }}>📄 DOCUMENTI/TARGHE</span>
                  )}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: T.text, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📍 {d.sedeOrigine} <span style={{ color: T.textMuted }}>→</span> {d.sedeDestinazione}
                </div>
                <div style={{ fontSize: '11px', color: T.textSub, marginTop: '4px' }}>
                  {d.tipo === 'documenti'
                    ? (d.descrizioneDocumenti || '').slice(0, 70) + ((d.descrizioneDocumenti || '').length > 70 ? '...' : '')
                    : `${toArraySafe(d.veicoli).length} veicol${toArraySafe(d.veicoli).length === 1 ? 'o' : 'i'} · ${d.causale}`
                  } {d.creatoDaNome ? `· da ${d.creatoDaNome}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => generaStampaDDT(d)}
                  style={{ padding: '9px 16px', borderRadius: '9px', border: '1px solid #0ea5e9', background: 'rgba(14,165,233,0.1)', color: '#0ea5e9', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
                  🖨️ Ristampa
                </button>
                {['admin', 'super_admin'].includes(user?.ruolo) && (
                  <button onClick={() => setModificaDDT(d)}
                    style={{ padding: '9px 14px', borderRadius: '9px', border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    ✏️ Modifica
                  </button>
                )}
                {['admin', 'super_admin'].includes(user?.ruolo) && (
                  <button onClick={async () => {
                    if (!window.confirm(`Eliminare il DDT ${d.numero}?\n\nI veicoli che non sono stati spostati di nuovo da allora torneranno alla sede di origine (${d.sedeOrigine}). Questa azione non è reversibile.`)) return;
                    try {
                      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
                      if (stockSnap.exists()) {
                        const raw = stockSnap.val();
                        const arr = Array.isArray(raw) ? raw : Object.values(raw);
                        const idSet = new Set(toArraySafe(d.veicoli).map(v => v.id));
                        // Riporta alla sede di origine solo i veicoli che sono
                        // ancora nella sede di destinazione di QUESTO DDT — se
                        // nel frattempo sono stati spostati da un DDT più
                        // recente, non li tocchiamo per non vanificare quello.
                        const aggiornati = arr.map(v => {
                          if (!idSet.has(v.id)) return v;
                          if ((v.sede || '').toLowerCase().trim() !== (d.sedeDestinazione || '').toLowerCase().trim()) return v;
                          return { ...v, sede: d.sedeOrigine };
                        });
                        await set(ref(db, 'stock_data/vehicles'), aggiornati);
                        window._radarStockCache = null; window._radarStockTs = 0;
                        try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}
                      }
                      // Registra l'eliminazione in un log separato prima di
                      // cancellare il DDT — così resta traccia di chi e
                      // quando lo ha eliminato, anche se il documento stesso
                      // non esiste più. Solo super_admin può poi ripulire
                      // questo registro.
                      const logId = `LOG-${Date.now()}`;
                      await set(ref(db, `ddt_log/${logId}`), {
                        id: logId, ddtId: d.id, ddtNumero: d.numero, azione: 'eliminato',
                        utente: `${user?.nome || ''} ${user?.cognome || ''}`.trim(), ruolo: user?.ruolo,
                        data: new Date().toISOString(),
                        dettagli: `${d.sedeOrigine} → ${d.sedeDestinazione}, ${toArraySafe(d.veicoli).length} veicoli`,
                      });
                      await set(ref(db, `ddt/${d.id}`), null);
                      window._radarDdtTs = 0;
                      setDdtList(prev => prev.filter(x => x.id !== d.id));
                      if (window._radarDdtCache2) window._radarDdtCache2 = window._radarDdtCache2.filter(x => x.id !== d.id);
                      alert('✅ DDT eliminato.');
                    } catch (e) {
                      alert('Errore: ' + e.message);
                    }
                  }}
                    style={{ padding: '9px 14px', borderRadius: '9px', border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    🗑️ Elimina
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {nuovoOpen && (
        <NuovoDDTModal user={user} db={db} vehicles={vehicles} ddtList={ddtList} sediDisponibili={sediDisponibili} onClose={() => setNuovoOpen(false)}
          onSaved={() => { setNuovoOpen(false); window._forzaRefreshDdt?.(); }} />
      )}

      {cambioSedeOpen && (
        <CambioSedeManualeModal db={db} user={user} vehicles={vehicles} sediDisponibili={sediDisponibili} onClose={() => setCambioSedeOpen(false)} />
      )}

      {normalizzaOpen && (
        <NormalizzaSediModal db={db} vehicles={vehicles} onClose={() => setNormalizzaOpen(false)} />
      )}

      {modificaDDT && (
        <ModificaDDTModal db={db} user={user} ddt={modificaDDT} onClose={() => setModificaDDT(null)} />
      )}
    </div>
  );
}

function NuovoDDTModal({ user, db, vehicles, ddtList, sediDisponibili, onClose, onSaved }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [tipoDDT, setTipoDDT] = useState('veicoli'); // 'veicoli' | 'documenti'
  const [descrizioneDocumenti, setDescrizioneDocumenti] = useState('');
  const [sedeOrigine, setSedeOrigine] = useState('');
  const [sedeDestinazione, setSedeDestinazione] = useState('');
  const [sedeDestModoLibero, setSedeDestModoLibero] = useState(false);
  const [causale, setCausale] = useState(CAUSALI_DDT[0]);
  const [destinatarioNome, setDestinatarioNome] = useState('');
  const [destinatarioIndirizzo, setDestinatarioIndirizzo] = useState('');
  const [dataConsegna, setDataConsegna] = useState(new Date().toISOString().slice(0, 10));
  const [oraConsegna, setOraConsegna] = useState(new Date().toTimeString().slice(0, 5));
  const [conducente, setConducente] = useState('');
  const [targaProva, setTargaProva] = useState('');
  const [selezionati, setSelezionati] = useState({});
  const [searchV, setSearchV] = useState('');
  const [saving, setSaving] = useState(false);
  const [veicoliOffStock, setVeicoliOffStock] = useState([]);
  const [offMarca, setOffMarca] = useState('');
  const [offModello, setOffModello] = useState('');
  const [offTarga, setOffTarga] = useState('');
  const [offTelaio, setOffTelaio] = useState('');

  // Se è un trasferimento tra sedi, precompila destinatario con i dati
  // della sede di destinazione — resta comunque modificabile (es. se il
  // DDT è in realtà per la consegna a un cliente finale).
  useEffect(() => {
    if (sedeDestinazione && !destinatarioNome) {
      const sd = getInfoSede(sedeDestinazione);
      setDestinatarioNome(sedeDestinazione);
      setDestinatarioIndirizzo(sd?.indirizzo || '');
    }
  }, [sedeDestinazione]);

  const disponibili = vehicles.filter(v => {
    if ((v.status || '').toUpperCase() === 'VENDUTA') return false;
    if (sedeOrigine && (v.sede || '').toLowerCase().trim() !== sedeOrigine.toLowerCase().trim()) return false;
    if (searchV.trim()) {
      const s = searchV.toLowerCase();
      if (!`${v.marca} ${v.modello} ${v.targa} ${v.vin}`.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const nSelezionati = Object.values(selezionati).filter(Boolean).length + veicoliOffStock.length;

  const handleAggiungiOffStock = () => {
    if (!offMarca.trim() || !offModello.trim()) { alert('Indica almeno marca e modello del veicolo fuori stock.'); return; }
    if (!offTarga.trim() && !offTelaio.trim()) { alert('Indica almeno la targa o il telaio del veicolo fuori stock.'); return; }
    setVeicoliOffStock(prev => [...prev, {
      id: `offstock_${Date.now()}_${prev.length}`,
      marca: offMarca.trim(), modello: offModello.trim(), targa: offTarga.trim(), vin: offTelaio.trim(),
      offStock: true,
    }]);
    setOffMarca(''); setOffModello(''); setOffTarga(''); setOffTelaio('');
  };

  const handleSalva = async () => {
    if (!sedeOrigine || !sedeDestinazione) { alert('Seleziona sede di origine e destinazione.'); return; }
    if (sedeOrigine === sedeDestinazione) { alert('Sede di origine e destinazione devono essere diverse.'); return; }
    if (!conducente.trim()) { alert('Indica il nome del conducente incaricato del trasporto.'); return; }
    const veicoliScelti = tipoDDT === 'veicoli' ? vehicles.filter(v => selezionati[v.id]) : [];
    if (tipoDDT === 'veicoli' && veicoliScelti.length === 0 && veicoliOffStock.length === 0) { alert('Seleziona o aggiungi almeno un veicolo da trasportare.'); return; }
    if (tipoDDT === 'documenti' && !descrizioneDocumenti.trim()) { alert('Descrivi cosa viene trasportato (documenti, targhe, ecc.).'); return; }

    setSaving(true);
    try {
      const id = `DDT-${Date.now()}`;
      const anno = new Date().getFullYear();
      // Numerazione progressiva per anno, stile "N. 340/2026" come da modello reale.
      // DDT_OFFSET_INIZIALE allinea il contatore ai DDT già emessi fuori da
      // RadaR prima che questo modulo esistesse — solo per l'anno indicato,
      // negli anni successivi (non presenti qui sotto) riparte da 0/1.
      const DDT_OFFSET_INIZIALE = { 2026: 412 };
      // Numero progressivo derivato dal MASSIMO numero già usato quest'anno,
      // non dal conteggio dei DDT esistenti: contare si rompe se un DDT
      // viene eliminato (il conteggio scende e il prossimo creato riusa un
      // numero già emesso). Inoltre la lista ddtList passata come prop può
      // arrivare da una cache vecchia fino a 4 ore — qui invece leggiamo
      // SEMPRE fresco da Firebase proprio nel momento di assegnare il
      // numero, così anche se un collega ha creato DDT nel frattempo il
      // rischio di duplicati resta minimo (solo la finestra di pochi
      // istanti tra due salvataggi quasi simultanei, non più ore).
      const snapDdtFresco = await get(ref(db, 'ddt'));
      const ddtListFresca = snapDdtFresco.exists() ? Object.values(snapDdtFresco.val()) : [];
      const massimoEsistente = ddtListFresca
        .filter(d => new Date(d.data).getFullYear() === anno)
        .reduce((max, d) => {
          const n = parseInt(String(d.numero || '').split('/')[0], 10);
          return Number.isFinite(n) && n > max ? n : max;
        }, DDT_OFFSET_INIZIALE[anno] || 0);
      const progressivo = massimoEsistente + 1;
      const numero = `${progressivo}/${anno}`;
      const nomeUtente = `${user?.nome || ''} ${user?.cognome || ''}`.trim();

      const ddt = {
        id, numero,
        tipo: tipoDDT, // 'veicoli' | 'documenti' — i DDT documenti/targhe non toccano mai lo stock
        data: new Date().toISOString(),
        sedeOrigine, sedeDestinazione, causale,
        destinatarioNome, destinatarioIndirizzo,
        dataConsegna: new Date(dataConsegna).toISOString(),
        oraConsegna, conducente, targaProva,
        // I veicoli fuori stock vengono inclusi nel documento stampato e
        // nello storico, ma non hanno un record in stock_data — non
        // subiscono quindi nessuno spostamento automatico di sede.
        veicoli: tipoDDT === 'veicoli' ? [
          ...veicoliScelti.map(v => ({ id: v.id, vin: v.vin || '', targa: v.targa || '', marca: v.marca || '', modello: v.modello || '' })),
          ...veicoliOffStock.map(v => ({ id: v.id, vin: v.vin || '', targa: v.targa || '', marca: v.marca || '', modello: v.modello || '', offStock: true })),
        ] : [],
        ...(tipoDDT === 'documenti' ? { descrizioneDocumenti: descrizioneDocumenti.trim() } : {}),
        creatoDa: user?.uid || '', creatoDaNome: nomeUtente,
        creatoAt: new Date().toISOString(),
      };

      // 1. Salva il DDT
      await set(ref(db, `ddt/${id}`), ddt);
      window._radarDdtTs = 0; window._radarDdtTs2 = 0; // invalida sia la cache per-veicolo che quella lista completa

      // 2. Sposta automaticamente i veicoli alla nuova sede — SOLO per i DDT
      // di tipo "veicoli" con veicoli realmente presenti in stock. I DDT
      // "documenti/targhe" non toccano mai stock_data, per design: qui si
      // trasportano carte/targhe, non le auto stesse, quindi la loro
      // ubicazione fisica in stock non deve cambiare.
      if (tipoDDT === 'veicoli') {
        const stockSnap = await get(ref(db, 'stock_data/vehicles'));
        if (stockSnap.exists() && veicoliScelti.length > 0) {
          const raw = stockSnap.val();
          const arr = Array.isArray(raw) ? raw : Object.values(raw);
          // Un veicolo aggiunto come "fuori stock" (id temporaneo
          // offstock_...) potrebbe nel frattempo corrispondere a un
          // veicolo VERO già presente in stock (stesso telaio/targa,
          // arrivato via sincronizzazione StockSpark dopo che l'utente lo
          // aveva digitato a mano). Senza questo controllo, l'aggiornamento
          // sede finiva scritto sull'id temporaneo — una chiave fantasma,
          // mai letta da nessuna vista, lasciando la card mostrare ancora
          // la vecchia sede anche col DDT salvato correttamente.
          const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
          const veicoliRisolti = veicoliScelti.map(v => {
            if (!v.offStock && !String(v.id || '').startsWith('offstock_')) return v;
            const match = arr.find(sv =>
              (v.vin && norm(sv.vin) === norm(v.vin)) ||
              (v.targa && norm(sv.targa) === norm(v.targa))
            );
            return match ? { ...v, id: match.id } : v;
          });
          const idSet = new Set(veicoliRisolti.map(v => v.id));
          // sedeLocked: true — da qui in poi RadaR (i DDT) è l'autorità sulla
          // sede di questo veicolo. L'import CSV StockSpark non la tocca più,
          // altrimenti al prossimo import tornerebbe quella riportata da SSK,
          // annullando lo spostamento appena fatto.
          const aggiornati = arr.map(v => idSet.has(v.id) ? { ...v, sede: sedeDestinazione, sedeLocked: true } : v);
          await set(ref(db, 'stock_data/vehicles'), aggiornati);
          window._radarStockCache = null; window._radarStockTs = 0;
          try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}
          // Anche in stati_veicoli — canale "live" che ha PRIORITÀ sul
          // valore in stock_data/vehicles nella visualizzazione delle
          // card. Senza questo, un valore vecchio rimasto lì (da un
          // precedente cambio sede manuale, che invece lo scrive)
          // continuerebbe a "vincere" sulla sede corretta appena
          // impostata dal DDT — mostrando la sede sbagliata a schermo
          // pur avendo il dato giusto in stock_data.
          for (const v of veicoliRisolti) {
            if (v.id && !String(v.id).startsWith('offstock_')) await set(ref(db, `stati_veicoli/${v.id}/sede`), sedeDestinazione);
          }
        }
      }

      generaStampaDDT(ddt);
      onSaved();
    } catch (e) {
      alert('Errore nel salvataggio: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '620px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>🚚 Nuovo Documento di Trasporto</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          {/* TIPO DDT — veicoli (sposta la sede) oppure documenti/targhe (non tocca mai lo stock) */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', background: T.bg, borderRadius: '10px', padding: '4px' }}>
            <button type="button" onClick={() => setTipoDDT('veicoli')}
              style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '900', fontSize: '11px', background: tipoDDT === 'veicoli' ? '#0ea5e9' : 'transparent', color: tipoDDT === 'veicoli' ? '#fff' : T.textSub }}>
              🚗 Trasporto Veicoli
            </button>
            <button type="button" onClick={() => setTipoDDT('documenti')}
              style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '900', fontSize: '11px', background: tipoDDT === 'documenti' ? '#0ea5e9' : 'transparent', color: tipoDDT === 'documenti' ? '#fff' : T.textSub }}>
              📄 Documenti / Targhe
            </button>
          </div>
          {tipoDDT === 'documenti' && (
            <div style={{ fontSize: '10px', color: '#0ea5e9', background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)', borderRadius: '8px', padding: '8px 12px', marginBottom: '14px', lineHeight: 1.5 }}>
              ℹ️ Questo tipo di DDT non sposta nessun veicolo in Stock — usalo per il trasporto di carte di circolazione, targhe, duplicati chiavi o altra documentazione, senza toccare l'ubicazione delle auto.
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>SEDE ORIGINE</label>
              <select value={sedeOrigine} onChange={e => { setSedeOrigine(e.target.value); setSelezionati({}); }}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none' }}>
                <option value="">Seleziona...</option>
                {sediDisponibili.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>SEDE DESTINAZIONE</label>
              <select value={sedeDestModoLibero ? '__altra__' : sedeDestinazione}
                onChange={e => {
                  if (e.target.value === '__altra__') { setSedeDestModoLibero(true); setSedeDestinazione(''); }
                  else { setSedeDestModoLibero(false); setSedeDestinazione(e.target.value); }
                }}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none' }}>
                <option value="">Seleziona...</option>
                {sediDisponibili.filter(s => s !== sedeOrigine).map(s => <option key={s} value={s}>{s}</option>)}
                <option value="__altra__">Altra sede (specifica)...</option>
              </select>
              {sedeDestModoLibero && (
                <input type="text" value={sedeDestinazione} onChange={e => setSedeDestinazione(e.target.value)}
                  placeholder="Nome nuova sede" style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginTop: '8px' }} />
              )}
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>CAUSALE (uso interno, non stampata)</label>
            <select value={causale} onChange={e => setCausale(e.target.value)}
              style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none' }}>
              {CAUSALI_DDT.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>SPETT.LE (destinatario)</label>
            <input type="text" value={destinatarioNome} onChange={e => setDestinatarioNome(e.target.value)} placeholder="Nome cliente o sede destinazione"
              style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' }} />
            <input type="text" value={destinatarioIndirizzo} onChange={e => setDestinatarioIndirizzo(e.target.value)} placeholder="Indirizzo destinatario"
              style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>DATA CONSEGNA</label>
              <input type="date" value={dataConsegna} onChange={e => setDataConsegna(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>ORA CONSEGNA</label>
              <input type="time" value={oraConsegna} onChange={e => setOraConsegna(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>TRASPORTO AFFIDATO A (conducente)</label>
              <input type="text" value={conducente} onChange={e => setConducente(e.target.value)} placeholder="Nome e cognome"
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>TARGA PROVA (opzionale)</label>
              <input type="text" value={targaProva} onChange={e => setTargaProva(e.target.value)} placeholder="Es. R1 PSCALA"
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          {tipoDDT === 'veicoli' ? (
            <>
              <div style={{ fontSize: '11px', fontWeight: '900', color: '#f59e0b', letterSpacing: '0.5px', marginBottom: '8px' }}>
                🚗 VEICOLO FUORI STOCK <span style={{ fontWeight: '700', color: T.textMuted, textTransform: 'none', letterSpacing: 0 }}>(non presente in RadaR)</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <input type="text" value={offMarca} onChange={e => setOffMarca(e.target.value)} placeholder="Marca"
                  style={{ flex: '1 1 100px', padding: '9px 10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, fontSize: '12px', outline: 'none' }} />
                <input type="text" value={offModello} onChange={e => setOffModello(e.target.value)} placeholder="Modello"
                  style={{ flex: '1 1 100px', padding: '9px 10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, fontSize: '12px', outline: 'none' }} />
                <input type="text" value={offTarga} onChange={e => setOffTarga(e.target.value)} placeholder="Targa"
                  style={{ flex: '1 1 90px', padding: '9px 10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, fontSize: '12px', outline: 'none' }} />
                <input type="text" value={offTelaio} onChange={e => setOffTelaio(e.target.value)} placeholder="Telaio (VIN)"
                  style={{ flex: '1 1 140px', padding: '9px 10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, fontSize: '12px', outline: 'none' }} />
                <button onClick={handleAggiungiOffStock} type="button"
                  style={{ padding: '9px 16px', background: '#f59e0b', border: 'none', borderRadius: '9px', color: '#0f172a', cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
                  + Aggiungi
                </button>
              </div>
              {veicoliOffStock.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                  {veicoliOffStock.map(v => (
                    <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '9px' }}>
                      <div>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{v.marca} {v.modello}</span>
                        <span style={{ fontSize: '10px', color: T.textMuted, marginLeft: '8px' }}>{v.targa || '—'} · {v.vin || '—'}</span>
                      </div>
                      <button onClick={() => setVeicoliOffStock(prev => prev.filter(x => x.id !== v.id))} type="button"
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>×</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: '11px', fontWeight: '900', color: T.textMuted, letterSpacing: '0.5px', marginBottom: '8px' }}>
                VEICOLI DA STOCK {nSelezionati > 0 && <span style={{ color: '#0ea5e9' }}>({nSelezionati} totali selezionati)</span>}
              </div>
              {!sedeOrigine ? (
                <div style={{ textAlign: 'center', padding: '20px', color: T.textMuted, fontSize: '12px', background: T.bg, borderRadius: '10px' }}>Seleziona prima la sede di origine.</div>
              ) : (
                <>
                  <input placeholder="🔍 Cerca veicolo..." value={searchV} onChange={e => setSearchV(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '9px', color: T.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' }} />
                  {disponibili.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '16px', color: T.textMuted, fontSize: '12px' }}>Nessun veicolo disponibile in questa sede.</div>
                  ) : (
                    <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {disponibili.slice(0, 100).map(v => (
                        <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: selezionati[v.id] ? 'rgba(14,165,233,0.1)' : T.bg, border: `1px solid ${selezionati[v.id] ? '#0ea5e9' : T.border}`, borderRadius: '9px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!selezionati[v.id]} onChange={e => setSelezionati(p => ({ ...p, [v.id]: e.target.checked }))}
                            style={{ width: '16px', height: '16px', accentColor: '#0ea5e9', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{v.marca} {v.modello}</div>
                            <div style={{ fontSize: '10px', color: T.textMuted }}>{v.targa || 'N.D.'} · {v.vin || '—'}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <label style={{ fontSize: '11px', fontWeight: '900', color: T.textMuted, letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                DESCRIZIONE DOCUMENTI / TARGHE TRASPORTATI
              </label>
              <textarea value={descrizioneDocumenti} onChange={e => setDescrizioneDocumenti(e.target.value)} rows={5}
                placeholder="Es. Carta di circolazione veicolo AB123CD, 2 targhe prova, duplicato chiavi veicolo XY456ZZ..."
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Annulla</button>
          <button onClick={handleSalva} disabled={saving}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', border: 'none', borderRadius: '10px', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', opacity: saving ? 0.6 : 1 }}>
            {saving ? '⏳ Salvataggio...' : (tipoDDT === 'veicoli' ? '📄 Genera DDT e Sposta Veicoli' : '📄 Genera DDT')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cambio Sede Manuale — admin/super_admin, senza generare un DDT ────────
function CambioSedeManualeModal({ db, user, vehicles, sediDisponibili, onClose }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [search, setSearch] = useState('');
  const [selezionato, setSelezionato] = useState(null);
  const [nuovaSede, setNuovaSede] = useState('');
  const [nuovaSedeLibera, setNuovaSedeLibera] = useState('');
  const [saving, setSaving] = useState(false);

  const risultati = useMemo(() => {
    if (!search.trim() || selezionato) return [];
    const s = search.toLowerCase();
    return vehicles.filter(v => (v.status || '').toUpperCase() !== 'VENDUTA' &&
      `${v.marca} ${v.modello} ${v.targa} ${v.vin}`.toLowerCase().includes(s)
    ).slice(0, 30);
  }, [search, vehicles, selezionato]);

  const handleSalva = async () => {
    const sedeFinale = (nuovaSede === '__altra__' ? nuovaSedeLibera : nuovaSede).trim();
    if (!selezionato) { alert('Seleziona un veicolo.'); return; }
    if (!sedeFinale) { alert('Indica la nuova sede.'); return; }
    if (sedeFinale.toLowerCase().trim() === (selezionato.sede || '').toLowerCase().trim()) { alert('Il veicolo è già in questa sede.'); return; }

    setSaving(true);
    try {
      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (!stockSnap.exists()) { alert('Stock non trovato.'); setSaving(false); return; }
      const raw = stockSnap.val();
      const arr = Array.isArray(raw) ? raw : Object.values(raw);
      const sedeVecchia = selezionato.sede || 'N.D.';
      // Scrittura mirata al singolo veicolo (stesso pattern già usato per
      // lo stato VENDUTA altrove) invece di riscrivere l'intero array —
      // prima questa operazione forzava un resync completo di stock_data
      // (3.6MB) a tutti i client con un listener attivo, per cambiare la
      // sede di un solo veicolo.
      const idx = arr.findIndex(v => v.id === selezionato.id);
      if (idx === -1) { alert('Veicolo non trovato nello stock.'); setSaving(false); return; }
      await set(ref(db, `stock_data/vehicles/${idx}/sede`), sedeFinale);
      await set(ref(db, `stock_data/vehicles/${idx}/sedeLocked`), true);
      await set(ref(db, `stati_veicoli/${selezionato.id}/sede`), sedeFinale);
      window._radarStockCache = null; window._radarStockTs = 0;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}
      // Registro le modifiche manuali di sede — visibile solo a super_admin,
      // per tracciare chi ha spostato cosa e quando.
      try {
        const logId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await set(ref(db, `cambio_sede_log/${logId}`), {
          id: logId,
          veicolo: `${selezionato.marca || ''} ${selezionato.modello || ''}`.trim(),
          targa: selezionato.targa || '',
          vin: selezionato.vin || '',
          sedeVecchia,
          sedeNuova: sedeFinale,
          utente: `${user?.nome || ''} ${user?.cognome || ''}`.trim() || 'N.D.',
          ruolo: user?.ruolo || 'N.D.',
          data: new Date().toISOString(),
        });
      } catch (e2) { /* il log non deve mai bloccare l'operazione principale */ }
      alert('✅ Sede aggiornata.');
      onClose();
    } catch (e) {
      alert('Errore: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '520px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>📍 Cambio Sede Manuale</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          <div style={{ fontSize: '10px', color: T.textMuted, marginBottom: '14px', lineHeight: 1.5 }}>
            Cambia direttamente la sede di un veicolo senza generare un documento di trasporto — utile per correzioni rapide. Il veicolo resta comunque tracciato: da questo momento la sua ubicazione è gestita da RadaR.
          </div>

          {!selezionato ? (
            <>
              <input placeholder="🔍 Cerca veicolo per marca, modello, targa, telaio..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }} />
              {risultati.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto' }}>
                  {risultati.map(v => (
                    <button key={v.id} onClick={() => setSelezionato(v)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '10px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', cursor: 'pointer', textAlign: 'left' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '800', color: T.text }}>{v.marca} {v.modello}</div>
                        <div style={{ fontSize: '10px', color: T.textMuted }}>{v.targa || 'N.D.'} · {v.vin || '—'}</div>
                      </div>
                      <div style={{ fontSize: '10px', fontWeight: '800', color: '#0ea5e9', whiteSpace: 'nowrap' }}>📍 {v.sede || 'N.D.'}</div>
                    </button>
                  ))}
                </div>
              )}
              {search.trim() && risultati.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: T.textMuted, fontSize: '12px' }}>Nessun veicolo trovato.</div>
              )}
            </>
          ) : (
            <>
              <div style={{ background: T.bg, borderRadius: '12px', padding: '14px', border: `1px solid ${T.border}`, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '900', color: T.text }}>{selezionato.marca} {selezionato.modello}</div>
                  <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{selezionato.targa || 'N.D.'} · {selezionato.vin || '—'}</div>
                  <div style={{ fontSize: '11px', color: '#0ea5e9', fontWeight: '800', marginTop: '4px' }}>Sede attuale: {selezionato.sede || 'N.D.'}</div>
                </div>
                <button onClick={() => { setSelezionato(null); setNuovaSede(''); setNuovaSedeLibera(''); }}
                  style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: '8px', padding: '6px 10px', color: T.textSub, cursor: 'pointer', fontSize: '10px', fontWeight: '800' }}>
                  Cambia
                </button>
              </div>

              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '6px' }}>NUOVA SEDE</label>
              <select value={nuovaSede} onChange={e => setNuovaSede(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}>
                <option value="">Seleziona...</option>
                {sediDisponibili.filter(s => s.toLowerCase().trim() !== (selezionato.sede || '').toLowerCase().trim()).map(s => <option key={s} value={s}>{s}</option>)}
                <option value="__altra__">Altra sede (specifica)...</option>
              </select>
              {nuovaSede === '__altra__' && (
                <input type="text" value={nuovaSedeLibera} onChange={e => setNuovaSedeLibera(e.target.value)} placeholder="Nome nuova sede"
                  style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              )}
            </>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Annulla</button>
          {selezionato && (
            <button onClick={handleSalva} disabled={saving}
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: '10px', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', opacity: saving ? 0.6 : 1 }}>
              {saving ? '⏳ Salvataggio...' : '✅ Conferma Cambio Sede'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Normalizza Sedi — unisce varianti diverse della stessa sede reale ─────
function NormalizzaSediModal({ db, vehicles, onClose }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [selezionate, setSelezionate] = useState({});
  const [nomeCorretto, setNomeCorretto] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Raggruppa per valore GREZZO (non normalizzato) — è proprio questo che
  // serve vedere per scovare i doppioni: "Agnano", "AGNANO ", "agnano"
  // risultano righe separate, ognuna con il proprio conteggio.
  const gruppi = useMemo(() => {
    const map = new Map();
    vehicles.forEach(v => {
      if ((v.status || '').toUpperCase() === 'VENDUTA') return;
      const raw = (v.sede || '').toString();
      if (!raw.trim()) return;
      if (!map.has(raw)) map.set(raw, 0);
      map.set(raw, map.get(raw) + 1);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [vehicles, refreshTick]);

  const nSelezionate = Object.values(selezionate).filter(Boolean).length;

  const handleUnifica = async () => {
    const scelte = Object.entries(selezionate).filter(([, v]) => v).map(([k]) => k);
    if (scelte.length < 2) { alert('Seleziona almeno 2 varianti da unificare.'); return; }
    if (!nomeCorretto.trim()) { alert('Indica il nome corretto della sede.'); return; }

    setSaving(true);
    try {
      const stockSnap = await get(ref(db, 'stock_data/vehicles'));
      if (!stockSnap.exists()) { alert('Stock non trovato.'); setSaving(false); return; }
      const raw = stockSnap.val();
      const arr = Array.isArray(raw) ? raw : Object.values(raw);
      const scelteSet = new Set(scelte);
      let modificati = 0;
      const aggiornati = arr.map(v => {
        if (!scelteSet.has((v.sede || '').toString())) return v;
        modificati++;
        // sedeLocked: true — evita che un futuro import CSV/sync riporti
        // indietro la vecchia scrittura sbagliata, vanificando la pulizia.
        return { ...v, sede: nomeCorretto.trim(), sedeLocked: true };
      });
      await set(ref(db, 'stock_data/vehicles'), aggiornati);
      window._radarStockCache = null; window._radarStockTs = 0;
      try { localStorage.removeItem('radar_stock_cache'); localStorage.removeItem('radar_stock_ts'); } catch (e) {}

      alert(`✅ Unificate ${scelte.length} varianti in "${nomeCorretto.trim()}" — ${modificati} veicoli aggiornati.`);
      setSelezionate({});
      setNomeCorretto('');
      setRefreshTick(t => t + 1);
      // Aggiorna anche l'array locale così la lista si ricalcola subito
      // senza dover riaprire il modale.
      vehicles.forEach(v => {
        if (scelteSet.has((v.sede || '').toString())) { v.sede = nomeCorretto.trim(); v.sedeLocked = true; }
      });
    } catch (e) {
      alert('Errore: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '560px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>🔧 Normalizza Sedi</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          <div style={{ fontSize: '10px', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '8px 12px', marginBottom: '16px', lineHeight: 1.5 }}>
            ℹ️ Seleziona le scritture che rappresentano la <b>stessa sede reale</b>, indica il nome corretto e unifica. I veicoli coinvolti vengono bloccati (come per i DDT): un futuro import non potrà più riportarli alla scrittura vecchia.
          </div>

          <div style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, letterSpacing: '0.5px', marginBottom: '8px' }}>
            SCRITTURE TROVATE IN STOCK ({gruppi.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '260px', overflowY: 'auto', marginBottom: '16px' }}>
            {gruppi.map(([nome, count]) => (
              <label key={nome} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: selezionate[nome] ? 'rgba(239,68,68,0.1)' : T.bg, border: `1px solid ${selezionate[nome] ? '#ef4444' : T.border}`, borderRadius: '9px', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!selezionate[nome]} onChange={e => setSelezionate(p => ({ ...p, [nome]: e.target.checked }))}
                  style={{ width: '16px', height: '16px', accentColor: '#ef4444', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', fontWeight: '700', color: T.text, flex: 1, wordBreak: 'break-all' }}>"{nome}"</span>
                <span style={{ fontSize: '10px', color: T.textMuted, whiteSpace: 'nowrap' }}>{count} veicol{count === 1 ? 'o' : 'i'}</span>
              </label>
            ))}
          </div>

          <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '6px' }}>
            NOME CORRETTO {nSelezionate > 0 && <span style={{ color: '#ef4444' }}>({nSelezionate} varianti selezionate)</span>}
          </label>
          <input type="text" value={nomeCorretto} onChange={e => setNomeCorretto(e.target.value)} placeholder="Es. Agnano"
            style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Chiudi</button>
          <button onClick={handleUnifica} disabled={saving || nSelezionate < 2}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', borderRadius: '10px', color: '#fff', cursor: (saving || nSelezionate < 2) ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', opacity: (saving || nSelezionate < 2) ? 0.5 : 1 }}>
            {saving ? '⏳ Unificando...' : `🔧 Unifica ${nSelezionate > 0 ? nSelezionate : ''} varianti`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modifica DDT — admin/super_admin, registra sempre un log ─────────────
function ModificaDDTModal({ db, user, ddt, onClose }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const [destinatarioNome, setDestinatarioNome] = useState(ddt.destinatarioNome || '');
  const [destinatarioIndirizzo, setDestinatarioIndirizzo] = useState(ddt.destinatarioIndirizzo || '');
  const [causale, setCausale] = useState(ddt.causale || CAUSALI_DDT[0]);
  const [conducente, setConducente] = useState(ddt.conducente || '');
  const [targaProva, setTargaProva] = useState(ddt.targaProva || '');
  const [dataConsegna, setDataConsegna] = useState(ddt.dataConsegna ? new Date(ddt.dataConsegna).toISOString().slice(0, 10) : '');
  const [oraConsegna, setOraConsegna] = useState(ddt.oraConsegna || '');
  const [descrizioneDocumenti, setDescrizioneDocumenti] = useState(ddt.descrizioneDocumenti || '');
  const [saving, setSaving] = useState(false);

  const handleSalva = async () => {
    if (!conducente.trim()) { alert('Indica il conducente.'); return; }
    setSaving(true);
    try {
      const aggiornato = {
        ...ddt,
        destinatarioNome, destinatarioIndirizzo, causale, conducente, targaProva,
        dataConsegna: dataConsegna ? new Date(dataConsegna).toISOString() : ddt.dataConsegna,
        oraConsegna,
        ...(ddt.tipo === 'documenti' ? { descrizioneDocumenti } : {}),
      };
      // Pulizia prima di scrivere: Firebase rifiuta l'intero salvataggio se un
      // campo è undefined/NaN o se ci sono campi tecnici aggiunti in lista (_…)
      const pulito = JSON.parse(JSON.stringify(aggiornato, (k, v) => (k.startsWith('_') ? undefined : (typeof v === 'number' && !isFinite(v) ? 0 : v))));
      if (!ddt.id) throw new Error('DDT senza identificativo: ricarica la pagina e riprova.');
      await update(ref(db, `ddt/${ddt.id}`), pulito);
      window._radarDdtTs = 0; window._radarDdtTs2 = 0;

      // Registra sempre un log della modifica — chi, quando. Solo
      // super_admin potrà poi eliminare questo registro.
      const logId = `LOG-${Date.now()}`;
      await set(ref(db, `ddt_log/${logId}`), {
        id: logId, ddtId: ddt.id, ddtNumero: ddt.numero, azione: 'modificato',
        utente: `${user?.nome || ''} ${user?.cognome || ''}`.trim(), ruolo: user?.ruolo,
        data: new Date().toISOString(),
      });

      alert('✅ DDT aggiornato.');
      window._forzaRefreshDdt?.();
      onClose();
    } catch (e) {
      console.error('[RadaR] Modifica DDT non riuscita:', e);
      alert(/permission/i.test(String(e?.message)) ? 'DDT non salvato: permesso negato da Firebase (ruolo o regole del database).' : 'DDT non salvato.\n\nErrore: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '560px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: T.text }}>✏️ Modifica DDT {ddt.numero}</div>
          <button onClick={onClose} style={{ background: T.bg, border: 'none', color: T.text, borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          <div style={{ fontSize: '10px', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', padding: '8px 12px', marginBottom: '16px', lineHeight: 1.5 }}>
            ℹ️ Sede, veicoli e numero DDT non sono modificabili qui — se sono sbagliati, elimina il documento e creane uno nuovo. Ogni modifica salvata viene registrata in un log.
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>SPETT.LE (destinatario)</label>
            <input type="text" value={destinatarioNome} onChange={e => setDestinatarioNome(e.target.value)}
              style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' }} />
            <input type="text" value={destinatarioIndirizzo} onChange={e => setDestinatarioIndirizzo(e.target.value)} placeholder="Indirizzo destinatario"
              style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
          </div>

          {ddt.tipo === 'documenti' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>DESCRIZIONE DOCUMENTI / TARGHE</label>
              <textarea value={descrizioneDocumenti} onChange={e => setDescrizioneDocumenti(e.target.value)} rows={4}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>CAUSALE</label>
              <select value={causale} onChange={e => setCausale(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none' }}>
                {CAUSALI_DDT.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>DATA CONSEGNA</label>
              <input type="date" value={dataConsegna} onChange={e => setDataConsegna(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>ORA CONSEGNA</label>
              <input type="time" value={oraConsegna} onChange={e => setOraConsegna(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>TRASPORTO AFFIDATO A (conducente)</label>
              <input type="text" value={conducente} onChange={e => setConducente(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', fontWeight: '900', color: T.textMuted, display: 'block', marginBottom: '5px' }}>TARGA PROVA</label>
              <input type="text" value={targaProva} onChange={e => setTargaProva(e.target.value)}
                style={{ width: '100%', padding: '10px', background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: '10px', color: T.text, cursor: 'pointer', fontWeight: '800', fontSize: '12px' }}>Annulla</button>
          <button onClick={handleSalva} disabled={saving}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '10px', color: '#0f172a', cursor: saving ? 'default' : 'pointer', fontWeight: '900', fontSize: '12px', opacity: saving ? 0.6 : 1 }}>
            {saving ? '⏳ Salvataggio...' : '✅ Salva Modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoleggioView({ user, db }) {
  const theme = useTheme(); const T = THEMES[theme];
  const { isMobile } = useBreakpoint();
  const fmtNLT = v => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v);
  const ruoloBaseNLT = (user?.ruolo||user?.Ruolo||'').toLowerCase().trim();
  // Un consulente normale può avere 'consulente_nlt' tra i permessi
  // aggiuntivi (non nel ruolo base) — es. un consulente che fa anche
  // noleggio lungo termine, senza smettere di essere un consulente
  // normale a tutti gli altri effetti. Qui trattiamo quel caso come se
  // il ruolo fosse consulente_nlt, così viene filtrato correttamente sui
  // soli propri preventivi, come per chi ha davvero quel ruolo.
  const permessiNLT = toArraySafe(user?.permessi).map(p => (p||'').toLowerCase().trim());
  const ruoloNLT = ruoloBaseNLT === 'consulente' && permessiNLT.includes('consulente_nlt') ? 'consulente_nlt' : ruoloBaseNLT;
  const isAdminNLT = ['admin_nlt','super_admin','admin'].includes(ruoloNLT);
  const [view, setView] = useState('lista');
  const [preventivi, setPreventivi] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroMese, setFiltroMese] = useState([]);   // array di 'MM/YYYY'
  const [filtroConsulente, setFiltroConsulente] = useState([]);

  useEffect(() => {
    get(ref(db,'nlt_preventivi')).then(snap => {
      if (snap.exists()) {
        let list = Object.values(snap.val()).sort((a,b)=>new Date(b.data||0)-new Date(a.data||0));
        // Normalizza venditoreNome a Title Case per uniformità
        list = list.map(p=>({...p, venditoreNome: (p.venditoreNome||'').replace(/\b\w/g, c=>c.toUpperCase()).trim()}));
        if (ruoloNLT==='consulente_nlt') {
          const mioUid   = (user.uid||'').toLowerCase();
          const miaEmail = (user.email||'').toLowerCase();
          const mioNome  = `${user.nome||''} ${user.cognome||''}`.trim().toLowerCase();
          list = list.filter(p => {
            const pUid  = (p.venditoreUid||'').toLowerCase();
            const pNome = (p.venditoreNome||'').replace(/\b\w/g,c=>c.toUpperCase()).trim().toLowerCase();
            return pUid === mioUid || pUid === miaEmail || pNome === mioNome;
          });
        }
        setPreventivi(list);
      }
      setLoading(false);
    });
  },[]);

  // Lista consulenti unici (solo per admin) — deduplicati per nome normalizzato
  const consulenti = useMemo(()=>{
    const map={};
    preventivi.forEach(p=>{
      if(p.venditoreNome){
        const nome = p.venditoreNome.replace(/\b\w/g,c=>c.toUpperCase()).trim();
        map[nome] = p.venditoreUid||nome;
      }
    });
    return Object.entries(map).map(([nome,uid])=>({uid,nome})).sort((a,b)=>a.nome.localeCompare(b.nome));
  },[preventivi]);

  // Mesi disponibili (2025 + 2026)
  const mesiDisponibili = useMemo(()=>{
    const set=new Set();
    preventivi.forEach(p=>{ const d=new Date(p.data||0); if(!isNaN(d)) set.add(`${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`); });
    return [...set].sort((a,b)=>{ const [ma,ya]=a.split('/'); const [mb,yb]=b.split('/'); return ya!==yb?yb-ya:mb-ma; });
  },[preventivi]);

  // Anni disponibili
  const anniDisponibili = useMemo(()=>{
    const set=new Set();
    preventivi.forEach(p=>{ const d=new Date(p.data||0); if(!isNaN(d)) set.add(d.getFullYear()); });
    return [...set].sort((a,b)=>b-a);
  },[preventivi]);

  const [filtroAnno, setFiltroAnno] = useState([]);   // array di anni
  const [filtroPartner, setFiltroPartner] = useState([]);
  const [filtroDaLead, setFiltroDaLead] = useState('');   // '' | 'si' | 'no'
  const [filtroFonte, setFiltroFonte] = useState([]);     // array di fonti

  // Partner/dealer disponibili
  const partners = useMemo(()=>{
    const set=new Set();
    preventivi.forEach(p=>{ if(p.dealer&&p.dealer.trim()) set.add(p.dealer.trim()); });
    return [...set].sort();
  },[preventivi]);

  // Fonti disponibili (per il filtro "Fonte pratica")
  const fontiDisponibili = useMemo(()=>{
    const set=new Set();
    preventivi.forEach(p=>{ if(p.fonte&&p.fonte.trim()) set.add(p.fonte.trim()); });
    return [...set].sort();
  },[preventivi]);

  // Fonti considerate "provenienti da lead"
  const FONTI_LEAD = ['gruppoautoscala.com','meta','google ads','telefonata'];
  const isFonteLead = (fonte) => FONTI_LEAD.includes((fonte||'').toLowerCase().trim());

  // Filtra per mese/anno/consulente/partner (per KPI e grafici)
  const prevFiltrati = useMemo(()=>{
    let list=preventivi;
    if(filtroAnno.length) list=list.filter(p=>{ const d=new Date(p.data||0); return filtroAnno.includes(String(d.getFullYear())); });
    if(filtroMese.length){ list=list.filter(p=>{ const d=new Date(p.data||0); return filtroMese.includes(`${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`); }); }
    if(filtroConsulente.length) list=list.filter(p=>filtroConsulente.includes((p.venditoreNome||'').replace(/\b\w/g,c=>c.toUpperCase()).trim()));
    if(filtroPartner.length) list=list.filter(p=>filtroPartner.includes(p.dealer));
    if(filtroDaLead==='si') list=list.filter(p=>isFonteLead(p.fonte));
    if(filtroDaLead==='no') list=list.filter(p=>!isFonteLead(p.fonte));
    if(filtroFonte.length) list=list.filter(p=>filtroFonte.includes((p.fonte||'').trim()));
    return list;
  },[preventivi,filtroMese,filtroAnno,filtroConsulente,filtroPartner,filtroDaLead,filtroFonte]);

  // KPI — deduplicati per cliente SOLO tra le richieste non ancora concluse
  // (rifiutate, in attesa, bozze): se la stessa email o lo stesso telefono
  // compare su più preventivi ripetuti, contano come UNO SOLO, per non
  // gonfiare i numeri con richieste ripetute della stessa persona che non
  // sono mai diventate affari veri. Gli "accettato" invece contano SEMPRE
  // singolarmente — se un cliente ha 2 preventivi diventati contratti,
  // sono 2 affari reali distinti, non vanno mai deduplicati.
  const dedupPerCliente = (lista) => {
    const gruppi = new Map();
    const senzaChiave = [];
    lista.forEach(p => {
      const email = (p.cliente?.email || '').trim().toLowerCase();
      const tel = (p.cliente?.telefono || '').replace(/\D/g, '');
      const chiave = email || tel;
      if (!chiave) { senzaChiave.push(p); return; }
      const esistente = gruppi.get(chiave);
      const dataP = new Date(p.data || 0).getTime();
      const dataEsistente = esistente ? new Date(esistente.data || 0).getTime() : -1;
      if (!esistente || dataP > dataEsistente) gruppi.set(chiave, p);
    });
    return [...gruppi.values(), ...senzaChiave];
  };

  const prevAccettatiKpi = useMemo(() => prevFiltrati.filter(p => p.stato === 'accettato'), [prevFiltrati]);
  const prevNonAccettatiDedupKpi = useMemo(() => dedupPerCliente(prevFiltrati.filter(p => p.stato !== 'accettato')), [prevFiltrati]);
  // Lista completa per i grafici che mostrano insieme accettato+altri stati
  // (es. bozza/inviato) — accettato sempre per intero, il resto deduplicato.
  const prevDedupKpi = useMemo(() => [...prevAccettatiKpi, ...prevNonAccettatiDedupKpi], [prevAccettatiKpi, prevNonAccettatiDedupKpi]);

  const kpi = useMemo(()=>{
    const potenziali = prevAccettatiKpi.length + prevNonAccettatiDedupKpi.filter(p=>STATI_NLT_IN_CORSO.includes(p.stato)).length;
    const rifiutate  = prevNonAccettatiDedupKpi.filter(p=>p.stato==='rifiutato').length;
    const approvate  = prevAccettatiKpi.length;
    const totProvv   = prevAccettatiKpi.reduce((s,p)=>s+(parseFloat(p.provvigione)||0),0);
    const tot        = prevAccettatiKpi.length + prevNonAccettatiDedupKpi.length;
    return {potenziali,rifiutate,approvate,totProvv,tot};
  },[prevAccettatiKpi,prevNonAccettatiDedupKpi]);

  // Lista filtrata per la tabella (include filtroStato e search)
  const filteredNLT = useMemo(()=>{
    let list=prevFiltrati;
    if(filtroStato!=='tutti') list=list.filter(p=>p.stato===filtroStato);
    if(search){const s=search.toLowerCase();list=list.filter(p=>
      (p.cliente?.nome||'').toLowerCase().includes(s)||
      (p.cliente?.ragioneSociale||'').toLowerCase().includes(s)||
      (p.veicolo?.marca||'').toLowerCase().includes(s)||
      (p.veicolo?.modello||'').toLowerCase().includes(s)||
      (p.venditoreNome||'').toLowerCase().includes(s)||
      (p.tipologiaCliente||'').toLowerCase().includes(s)
    );}
    return list;
  },[prevFiltrati,filtroStato,search]);

  if (view==='nuovo'||view==='modifica') return <NLTForm user={user} T={T} isMobile={isMobile} db={db} isAdminNLT={isAdminNLT} preventivo={view==='modifica'?selected:null}
    onSaved={p=>{setPreventivi(prev=>{const i=prev.findIndex(x=>x.id===p.id);if(i>-1){const n=[...prev];n[i]=p;return n;}return[p,...prev];});setView('lista');}} onClose={()=>setView('lista')} />;
  if (view==='duplica'&&selected) return <NLTForm user={user} T={T} isMobile={isMobile} db={db} isAdminNLT={isAdminNLT}
    preventivo={{...selected, id:null, veicolo:{marca:'',modello:'',versione:'',colore:'',alimentazione:''}, optional:'', offerte:[{canone:'',durata:'',anticipo:'',kmAnnui:'',scorporaCanone:false,canoneLocazione:'',canoneServizi:'',mostraScorporoInPdf:false}], canone:'', durata:'', anticipo:'', kmAnnui:'', stato:'bozza', note:'', data:new Date().toISOString().split('T')[0], dataConsegna:''}}
    onSaved={p=>{setPreventivi(prev=>[p,...prev]);setView('lista');}} onClose={()=>setView('lista')} />;
  if (view==='dettaglio'&&selected) return <NLTDettaglio p={selected} T={T} isMobile={isMobile} isAdminNLT={isAdminNLT} fmt={fmtNLT} user={user}
    onModifica={()=>setView('modifica')} onClose={()=>{setSelected(null);setView('lista');}}
    onDuplica={()=>setView('duplica')}
    onElimina={async()=>{
      await set(ref(db,`nlt_preventivi/${selected.id}`),null);
      setPreventivi(prev=>prev.filter(x=>x.id!==selected.id));
      setSelected(null); setView('lista');
    }} />;

  const statoC=Object.fromEntries(STATI_NLT.map(x=>[x.v,x.c]));

  // Torta SVG helper
  const PieChart = ({slices,size=90})=>{
    let cum=0; const tot=slices.reduce((s,x)=>s+x.v,0);
    if(tot===0) return <div style={{width:size,height:size,borderRadius:'50%',background:T.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',color:T.textMuted}}>N/D</div>;
    const parts=slices.map(s=>{const pct=s.v/tot;const start=cum;cum+=pct;return{...s,start,pct};});
    const arc=(cx,cy,r,startA,endA)=>{
      if(endA-startA>=0.9999) return `M ${cx-r} ${cy} A ${r} ${r} 0 1 1 ${cx+r} ${cy} A ${r} ${r} 0 1 1 ${cx-r} ${cy}`;
      const s=startA*Math.PI*2-Math.PI/2, e=endA*Math.PI*2-Math.PI/2;
      const x1=cx+r*Math.cos(s),y1=cy+r*Math.sin(s),x2=cx+r*Math.cos(e),y2=cy+r*Math.sin(e);
      return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${endA-startA>0.5?1:0} 1 ${x2} ${y2} Z`;
    };
    const c=size/2,r=c-4;
    return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {parts.map((s,i)=><path key={i} d={arc(c,c,r,s.start,s.start+s.pct)} fill={s.color} opacity={0.9}/>)}
      <text x={c} y={c+1} textAnchor="middle" dominantBaseline="middle" fontSize="13" fontWeight="900" fill={T.text}>{tot}</text>
    </svg>;
  };

  return (
    <div style={{padding:isMobile?'12px':'20px'}}>
      {/* HEADER */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <div style={{fontSize:'18px',fontWeight:'900',color:T.text}}>Noleggio Lungo Termine</div>
          <div style={{fontSize:'11px',color:'#f59e0b',fontWeight:'700',marginTop:'2px'}}>Tutte le cifre IVA ESCLUSA</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {puoEsportareExcel(user) && (
          <button onClick={()=>esportaNoleggioExcel(prevFiltrati)} style={{padding:'10px 18px',background:'transparent',border:'1px solid #10b981',borderRadius:'12px',color:'#10b981',cursor:'pointer',fontWeight:'900',fontSize:'12px',whiteSpace:'nowrap'}}>
            📊 Esporta Excel {prevFiltrati.length ? `(${prevFiltrati.length})` : ''}
          </button>
          )}
          <button onClick={()=>setView('nuovo')} style={{padding:'10px 20px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:'12px',color:'#fff',cursor:'pointer',fontWeight:'900',fontSize:'12px'}}>
            + Nuovo Preventivo NLT
          </button>
        </div>
      </div>

      {/* FILTRI TEMPORALE + CONSULENTE */}
      <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'16px',alignItems:'center',padding:'12px 16px',background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`}}>
        <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px'}}>FILTRI</div>
        <RadarMultiSelect label="ANNO" options={anniDisponibili.map(String)} selected={filtroAnno} onChange={v=>{setFiltroAnno(v); setFiltroMese([]);}} onClear={()=>{setFiltroAnno([]); setFiltroMese([]);}} />
        <RadarMultiSelect label="MESE" options={mesiDisponibili.filter(m=>!filtroAnno.length||filtroAnno.some(a=>m.endsWith(a)))} selected={filtroMese} onChange={setFiltroMese} onClear={()=>setFiltroMese([])} />
        {isAdminNLT && (
          <RadarMultiSelect label="CONSULENTE" options={consulenti.map(c=>c.nome)} selected={filtroConsulente} onChange={setFiltroConsulente} onClear={()=>setFiltroConsulente([])} />
        )}
        <select value={filtroDaLead} onChange={e=>setFiltroDaLead(e.target.value)}
          style={{background:T.inputBg,border:`1px solid ${filtroDaLead?'#f59e0b':T.border2}`,borderRadius:'8px',padding:'6px 10px',color:T.text,fontSize:'11px',outline:'none'}}>
          <option value="">Da lead: tutti</option>
          <option value="si">Da lead: Sì</option>
          <option value="no">Da lead: No</option>
        </select>
        {fontiDisponibili.length > 0 && (
          <RadarMultiSelect label="FONTE" options={fontiDisponibili} selected={filtroFonte} onChange={setFiltroFonte} onClear={()=>setFiltroFonte([])} />
        )}
        {(filtroMese.length||filtroConsulente.length||filtroAnno.length||filtroPartner.length||filtroDaLead||filtroFonte.length) && (
          <button onClick={()=>{setFiltroMese([]);setFiltroConsulente([]);setFiltroAnno([]);setFiltroPartner([]);setFiltroDaLead('');setFiltroFonte([]);}}
            style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'8px',padding:'6px 10px',color:'#ef4444',cursor:'pointer',fontSize:'10px',fontWeight:'700'}}>
            ✕ Reset
          </button>
        )}
        {partners.length > 0 && (
          <RadarMultiSelect label="PARTNER" options={partners} selected={filtroPartner} onChange={setFiltroPartner} onClear={()=>setFiltroPartner([])} />
        )}
      </div>

      {/* GRAFICI CONFRONTO — solo se non filtro mese singolo */}
      {!loading && !filtroMese.length && isAdminNLT && (() => {
        const MESI_LABEL=['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
        // Dati per confronto mese vs mese (2025 vs 2026)
        const byMese = (anno) => Array.from({length:12},(_,i)=>{
          const m=`${String(i+1).padStart(2,'0')}/${anno}`;
          let list=preventivi.filter(p=>{ const d=new Date(p.data||0); return d.getFullYear()===anno && d.getMonth()===i; });
          if(filtroConsulente.length) list=list.filter(p=>filtroConsulente.includes(p.venditoreNome));
          return {label:MESI_LABEL[i], acc:list.filter(p=>p.stato==='accettato').length, tot:list.length, provv:list.filter(p=>p.stato==='accettato').reduce((s,p)=>s+(parseFloat(p.provvigione)||0),0)};
        });
        const d2025 = byMese(2025);
        const d2026 = byMese(2026);
        const maxTot = Math.max(...d2025.map(d=>d.tot),...d2026.map(d=>d.tot),1);
        // YTD corrente anno
        const annoYTD = filtroAnno.length === 1 ? parseInt(filtroAnno[0]) : new Date().getFullYear();
        const meseCorr = filtroAnno.length === 1 ? 12 : new Date().getMonth();
        const ytd2025 = d2025.slice(0,meseCorr+1).reduce((s,d)=>({acc:s.acc+d.acc,tot:s.tot+d.tot,provv:s.provv+d.provv}),{acc:0,tot:0,provv:0});
        const ytd2026 = d2026.slice(0,meseCorr+1).reduce((s,d)=>({acc:s.acc+d.acc,tot:s.tot+d.tot,provv:s.provv+d.provv}),{acc:0,tot:0,provv:0});
        const fmtK = v => v>=1000?`€${(v/1000).toFixed(1)}k`:`€${v.toFixed(0)}`;
        return (
          <div style={{background:T.surface,borderRadius:'16px',border:`1px solid ${T.border}`,padding:'16px',marginBottom:'16px'}}>
            <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'12px'}}>📊 CONFRONTO ANNUALE</div>
            {/* YTD summary */}
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr 1fr',gap:'10px',marginBottom:'14px'}}>
              {[['2025 — Pratiche totali',ytd2025.tot,'#6366f1'],['2025 — Approvate',ytd2025.acc,'#10b981'],
                ['2026 — Pratiche totali',ytd2026.tot,'#8b5cf6'],['2026 — Approvate',ytd2026.acc,'#34d399']].map(([l,v,c])=>(
                <div key={l} style={{background:T.bg,borderRadius:'10px',padding:'10px 12px'}}>
                  <div style={{fontSize:'9px',color:T.textMuted}}>{l}</div>
                  <div style={{fontSize:'20px',fontWeight:'900',color:c}}>{v}</div>
                </div>
              ))}
            </div>
            {/* Bar chart confronto mensile */}
            <div style={{overflowX:'auto'}}>
              <div style={{display:'flex',gap:'6px',alignItems:'flex-end',minWidth:'600px',height:'100px',padding:'0 4px'}}>
                {MESI_LABEL.map((m,i)=>{
                  const t25=d2025[i].tot, t26=d2026[i].tot;
                  const h25=Math.round((t25/maxTot)*80), h26=Math.round((t26/maxTot)*80);
                  return (
                    <div key={m} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'2px'}}>
                      <div style={{display:'flex',gap:'2px',alignItems:'flex-end',height:'84px'}}>
                        <div title={`2025: ${t25} pratiche`} style={{width:'14px',height:`${h25||2}px`,background:'#6366f1',borderRadius:'3px 3px 0 0',opacity:0.8}}/>
                        <div title={`2026: ${t26} pratiche`} style={{width:'14px',height:`${h26||2}px`,background:'#f59e0b',borderRadius:'3px 3px 0 0',opacity:0.8}}/>
                      </div>
                      <div style={{fontSize:'8px',color:T.textMuted}}>{m}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{display:'flex',gap:'14px',marginTop:'6px',justifyContent:'center'}}>
                <span style={{fontSize:'9px',color:'#6366f1',fontWeight:'700'}}>■ 2025</span>
                <span style={{fontSize:'9px',color:'#f59e0b',fontWeight:'700'}}>■ 2026</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* GRAFICO PARTNER + PERFORMANCE — solo admin, senza filtro mese */}
      {!loading && !filtroMese.length && isAdminNLT && (() => {
        // Partner histogram
        const partnerData = partners.map(partner => {
          const list = prevFiltrati.filter(p=>p.dealer===partner||(!filtroPartner&&p.dealer===partner));
          const allList = prevFiltrati.filter(p=>p.dealer===partner);
          return { partner, tot: allList.length, acc: allList.filter(p=>p.stato==='accettato').length, provv: allList.filter(p=>p.stato==='accettato').reduce((s,p)=>s+(parseFloat(p.provvigione)||0),0) };
        }).filter(d=>d.tot>0).sort((a,b)=>b.tot-a.tot);

        // Performance per consulente
        const perfData = consulenti.map(c=>{
          const list = prevFiltrati.filter(p=>(p.venditoreNome||'').replace(/\b\w/g,ch=>ch.toUpperCase()).trim()===c.nome);
          return { nome: c.nome.split(' ')[0], tot: list.length, acc: list.filter(p=>p.stato==='accettato').length, rif: list.filter(p=>p.stato==='rifiutato').length, provv: list.filter(p=>p.stato==='accettato').reduce((s,p)=>s+(parseFloat(p.provvigione)||0),0) };
        }).filter(d=>d.tot>0);

        const maxP = Math.max(...partnerData.map(d=>d.tot),1);
        const maxC = Math.max(...perfData.map(d=>d.tot),1);
        const fmtK = v => v>=1000?`€${(v/1000).toFixed(1)}k`:`€${Math.round(v)}`;

        return (<>
          {/* Partner histogram */}
          {partnerData.length > 0 && (
            <div style={{background:T.surface,borderRadius:'16px',border:`1px solid ${T.border}`,padding:'16px',marginBottom:'16px'}}>
              <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'12px'}}>🏢 PRATICHE PER PARTNER / DEALER</div>
              <div style={{overflowX:'auto'}}>
                <div style={{display:'flex',gap:'8px',alignItems:'flex-end',minWidth:`${partnerData.length*70}px`,height:'120px',padding:'0 4px'}}>
                  {partnerData.map(d=>{
                    const hTot=Math.round((d.tot/maxP)*90), hAcc=Math.round((d.acc/maxP)*90);
                    return (
                      <div key={d.partner} style={{flex:1,minWidth:'60px',display:'flex',flexDirection:'column',alignItems:'center',gap:'2px'}}>
                        <div style={{fontSize:'9px',color:'#10b981',fontWeight:'700'}}>{d.acc}/{d.tot}</div>
                        <div style={{display:'flex',gap:'3px',alignItems:'flex-end',height:'90px'}}>
                          <div title={'Totale: '+d.tot} style={{width:'18px',height:`${hTot}px`,background:'#6366f1',borderRadius:'3px 3px 0 0',opacity:0.7}}/>
                          <div title={'Rent firmati: '+d.acc} style={{width:'18px',height:`${hAcc}px`,background:'#10b981',borderRadius:'3px 3px 0 0'}}/>
                        </div>
                        <div style={{fontSize:'8px',color:T.textMuted,textAlign:'center',maxWidth:'60px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.partner}</div>
                        {d.provv>0&&<div style={{fontSize:'8px',color:'#6366f1',fontWeight:'700'}}>{fmtK(d.provv)}</div>}
                      </div>
                    );
                  })}
                </div>
                <div style={{display:'flex',gap:'14px',marginTop:'6px',justifyContent:'center'}}>
                  <span style={{fontSize:'9px',color:'#6366f1',fontWeight:'700'}}>■ Totale</span>
                  <span style={{fontSize:'9px',color:'#10b981',fontWeight:'700'}}>■ Rent firmati</span>
                </div>
              </div>
            </div>
          )}

          {/* Performance consulenti */}
          {perfData.length > 0 && (
            <div style={{background:T.surface,borderRadius:'16px',border:`1px solid ${T.border}`,padding:'16px',marginBottom:'16px'}}>
              <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'12px'}}>👤 PERFORMANCE CONSULENTI NLT</div>
              <div style={{display:'flex',gap:'12px',alignItems:'flex-end',height:'140px',padding:'0 4px'}}>
                {perfData.map(d=>{
                  const hTot=Math.round((d.tot/maxC)*100), hAcc=Math.round((d.acc/maxC)*100), hRif=Math.round((d.rif/maxC)*100);
                  const pct = d.tot>0?Math.round(d.acc/d.tot*100):0;
                  return (
                    <div key={d.nome} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'3px'}}>
                      <div style={{fontSize:'9px',fontWeight:'900',color:pct>=40?'#10b981':'#f59e0b'}}>{pct}%</div>
                      <div style={{display:'flex',gap:'3px',alignItems:'flex-end',height:'100px'}}>
                        <div title={'Totale: '+d.tot} style={{width:'20px',height:`${hTot}px`,background:'#6366f1',borderRadius:'3px 3px 0 0',opacity:0.6}}/>
                        <div title={'Rent firmati: '+d.acc} style={{width:'20px',height:`${hAcc}px`,background:'#10b981',borderRadius:'3px 3px 0 0'}}/>
                        <div title={'PRT respinte: '+d.rif} style={{width:'20px',height:`${hRif}px`,background:'#ef4444',borderRadius:'3px 3px 0 0',opacity:0.7}}/>
                      </div>
                      <div style={{fontSize:'9px',color:T.text,fontWeight:'700',textAlign:'center'}}>{d.nome}</div>
                      {d.provv>0&&<div style={{fontSize:'8px',color:'#6366f1',fontWeight:'700'}}>{fmtK(d.provv)}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{display:'flex',gap:'14px',marginTop:'6px',justifyContent:'center'}}>
                <span style={{fontSize:'9px',color:'#6366f1',fontWeight:'700'}}>■ Totale</span>
                <span style={{fontSize:'9px',color:'#10b981',fontWeight:'700'}}>■ Rent firmati</span>
                <span style={{fontSize:'9px',color:'#ef4444',fontWeight:'700'}}>■ PRT respinte</span>
              </div>
            </div>
          )}
        </>);
      })()}
      {!loading && (
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr 1fr',gap:'12px',marginBottom:'20px'}}>
          {/* Torta Potenziali */}
          <div style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',gap:'8px'}}>
            <div style={{fontSize:'9px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px'}}>POTENZIALI</div>
            <PieChart size={80} slices={[
              {v:prevDedupKpi.filter(p=>p.stato==='accettato').length,color:'#10b981'},
              {v:prevDedupKpi.filter(p=>STATI_NLT_IN_CORSO.includes(p.stato)).length,color:'#3b82f6'},
              {v:prevDedupKpi.filter(p=>p.stato==='bozza'||!p.stato).length,color:'#f59e0b'},
            ]}/>
            <div style={{fontSize:'20px',fontWeight:'900',color:'#3b82f6'}}>{kpi.potenziali}</div>
            <div style={{fontSize:'9px',color:T.textMuted,textAlign:'center'}}>Rent firmati + trattative in corso</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap',justifyContent:'center'}}>
              <span style={{fontSize:'9px',color:'#10b981'}}>■ {prevDedupKpi.filter(p=>p.stato==='accettato').length} firmati</span>
              <span style={{fontSize:'9px',color:'#3b82f6'}}>■ {prevDedupKpi.filter(p=>STATI_NLT_IN_CORSO.includes(p.stato)).length} in corso</span>
            </div>
          </div>
          {/* Torta Rifiutate */}
          <div style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',gap:'8px'}}>
            <div style={{fontSize:'9px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px'}}>PRT RESPINTE</div>
            <PieChart size={80} slices={[
              {v:kpi.rifiutate,color:'#ef4444'},
              {v:kpi.tot-kpi.rifiutate,color:T.bg},
            ]}/>
            <div style={{fontSize:'20px',fontWeight:'900',color:'#ef4444'}}>{kpi.rifiutate}</div>
            <div style={{fontSize:'9px',color:T.textMuted}}>{kpi.tot>0?Math.round(kpi.rifiutate/kpi.tot*100):0}% del totale</div>
          </div>
          {/* Torta Approvate */}
          <div style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',gap:'8px'}}>
            <div style={{fontSize:'9px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px'}}>RENT FIRMATI</div>
            <PieChart size={80} slices={[
              {v:kpi.approvate,color:'#10b981'},
              {v:kpi.tot-kpi.approvate,color:T.bg},
            ]}/>
            <div style={{fontSize:'20px',fontWeight:'900',color:'#10b981'}}>{kpi.approvate}</div>
            <div style={{fontSize:'9px',color:T.textMuted}}>{kpi.tot>0?Math.round(kpi.approvate/kpi.tot*100):0}% del totale</div>
          </div>
          {/* Provvigioni */}
          <div style={{background:T.surface,borderRadius:'14px',border:`2px solid #6366f1`,padding:'14px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'8px'}}>
            <div style={{fontSize:'9px',fontWeight:'900',color:'#6366f1',letterSpacing:'1px'}}>PROVVIGIONI</div>
            <div style={{fontSize:'10px',color:T.textMuted,textAlign:'center'}}>Su pratiche approvate</div>
            <div style={{fontSize:'22px',fontWeight:'900',color:'#6366f1',textAlign:'center'}}>{fmtNLT(kpi.totProvv)}</div>
            <div style={{fontSize:'9px',color:T.textMuted}}>{kpi.approvate} rent firmati</div>
          </div>
        </div>
      )}

      {/* FILTRI LISTA */}
      <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'14px',alignItems:'center'}}>
        <input placeholder="🔍 Cerca per nome, veicolo, consulente..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{background:T.inputBg,border:`1px solid ${T.border2}`,borderRadius:'10px',padding:'8px 12px',color:T.text,fontSize:'12px',outline:'none',flex:1,minWidth:'180px'}} />
        {[['tutti','Tutti'], ...STATI_NLT.map(x=>[x.v,x.l])].map(([v,l])=>(
          <button key={v} onClick={()=>setFiltroStato(v)}
            style={{padding:'7px 12px',fontSize:'10px',fontWeight:'700',border:'none',borderRadius:'8px',cursor:'pointer',background:filtroStato===v?'#6366f1':T.bg,color:filtroStato===v?'#fff':T.textSub}}>{l}</button>
        ))}
      </div>

      {/* LISTA */}
      {loading ? <div style={{textAlign:'center',padding:'60px',color:T.textMuted}}>Caricamento...</div>
      : filteredNLT.length===0 ? <div style={{textAlign:'center',padding:'60px',color:T.textMuted,fontSize:'13px',fontWeight:'700'}}>Nessun preventivo NLT trovato</div>
      : filteredNLT.map(p=>{
        const sc=statoC[p.stato]||'#64748b';
        return (
          <div key={p.id} onClick={()=>{setSelected(p);setView('dettaglio');}}
            style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'14px 18px',marginBottom:'10px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px'}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'3px'}}>
                <span style={{fontSize:'10px',fontWeight:'900',color:sc,background:sc+'20',padding:'2px 7px',borderRadius:'6px'}}>{etichettaStatoNlt(p.stato)}</span>
                <span style={{fontSize:'10px',color:T.textMuted}}>{p.data?new Date(p.data).toLocaleDateString('it-IT'):'---'}</span>
                {p.tipologiaCliente&&<span style={{fontSize:'10px',color:'#8b5cf6',fontWeight:'700'}}>{p.tipologiaCliente}</span>}
                {p.fonte&&<span style={{fontSize:'9px',color:T.textMuted,background:T.bg,padding:'1px 6px',borderRadius:'4px'}}>{p.fonte}</span>}
                {p.dealer&&<span style={{fontSize:'9px',color:'#8b5cf6',background:'rgba(139,92,246,0.1)',padding:'1px 6px',borderRadius:'4px',fontWeight:'700'}}>🏢 {p.dealer}</span>}
              </div>
              <div style={{fontSize:'14px',fontWeight:'800',color:T.text}}>{p.cliente?.nome||'---'}</div>
              <div style={{fontSize:'11px',color:T.textMuted}}>{p.veicolo?.marca} {p.veicolo?.modello} {p.veicolo?.versione}</div>
              {isAdminNLT&&<div style={{fontSize:'10px',color:T.textMuted}}>Consulente: {p.venditoreNome||'---'}</div>}
            </div>
            <div style={{textAlign:'right',flexShrink:0}}>
              <div style={{fontSize:'16px',fontWeight:'900',color:'#6366f1'}}>{p.canone?fmtNLT(p.canone)+'/mese':'---'}</div>
              <div style={{fontSize:'10px',color:T.textMuted}}>{p.durata?p.durata+' mesi':''}{p.kmAnnui?' - '+(p.kmAnnui==='illimitati'?'Illimitati':Number(p.kmAnnui).toLocaleString('it-IT')+' km/a'):''}</div>
              {p.dataConsegna&&<div style={{fontSize:'9px',color:'#10b981'}}>Consegna: {new Date(p.dataConsegna).toLocaleDateString('it-IT')}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NLTForm({ user, T, isMobile, db, onSaved, onClose, preventivo, isAdminNLT }) {
  const isEdit=!!preventivo;
  const ruoloBaseNLT = (user?.ruolo||user?.Ruolo||'').toLowerCase().trim();
  const permessiNLT = toArraySafe(user?.permessi).map(p => (p||'').toLowerCase().trim());
  const ruoloNLT = ruoloBaseNLT === 'consulente' && permessiNLT.includes('consulente_nlt') ? 'consulente_nlt' : ruoloBaseNLT;
  const [saving,setSaving]=useState(false);
  const today=new Date().toISOString().split('T')[0];
  const iS={width:'100%',padding:'9px 12px',background:T.inputBg,border:`1px solid ${T.border2}`,borderRadius:'10px',color:T.text,fontSize:'12px',outline:'none',boxSizing:'border-box'};
  const sS={background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'16px',marginBottom:'14px'};
  const lS={fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'10px'};
  const fSt={fontSize:'10px',color:T.textMuted,marginBottom:'4px'};
  const [form,setForm]=useState({
    data:preventivo?.data?.split('T')[0]||today, stato:preventivo?.stato||'bozza',
    fonte:preventivo?.fonte||'',
    tipologiaCliente:preventivo?.tipologiaCliente||'',
    dealer:preventivo?.dealer||'',
    dataConsegna:preventivo?.dataConsegna?.split('T')[0]||preventivo?.dataConsegna||'',
    cliente:preventivo?.cliente||{nome:'',indirizzo:'',piva:'',cf:'',telefono:'',email:''},
    veicolo:preventivo?.veicolo||{marca:'',modello:'',versione:'',colore:'',alimentazione:''},
    optional:preventivo?.optional||'',
    canone:preventivo?.canone||'', durata:preventivo?.durata||'',
    anticipo:preventivo?.anticipo||'', kmAnnui:preventivo?.kmAnnui||'',
    scorporaCanone:preventivo?.scorporaCanone||false,
    canoneLocazione:preventivo?.canoneLocazione||'',
    canoneServizi:preventivo?.canoneServizi||'',
    mostraScorporoInPdf:preventivo?.mostraScorporoInPdf||false,
    // Offerte multiple — migra dai vecchi campi se necessario
    offerte: preventivo?.offerte || (preventivo?.canone ? [{
      canone: preventivo.canone||'', durata: preventivo.durata||'',
      anticipo: preventivo.anticipo||'', kmAnnui: preventivo.kmAnnui||'',
      scorporaCanone: preventivo.scorporaCanone||false,
      canoneLocazione: preventivo.canoneLocazione||'',
      canoneServizi: preventivo.canoneServizi||'',
      mostraScorporoInPdf: preventivo.mostraScorporoInPdf||false,
    }] : [{canone:'',durata:'',anticipo:'',kmAnnui:'',scorporaCanone:false,canoneLocazione:'',canoneServizi:'',mostraScorporoInPdf:false}]),
    polizze:preventivo?.polizze||{rca:false,kasko:false,furtoIncendio:false,cristalli:false,kv:false,altrePolizze:'',franchigie:''},
    servizi:preventivo?.servizi||{manutenzione:false,manutenzioneStr:false,soccorsoStradale:false,autoSostitutiva:false,gommeSummer:false,gommeWinter:false,altriServizi:''},
    note:preventivo?.note||'',
    provvigione:preventivo?.provvigione||'',
    venditoreNome:preventivo?.venditoreNome||'',
    venditoreUid:preventivo?.venditoreUid||'',
  });
  const upF=(k,v)=>setForm(p=>({...p,[k]:v}));
  const upC=(k,v)=>setForm(p=>({...p,cliente:{...p.cliente,[k]:v}}));
  const upV=(k,v)=>setForm(p=>({...p,veicolo:{...p.veicolo,[k]:v}}));
  const upP=(k,v)=>setForm(p=>{
    const pol={...p.polizze,[k]:v};
    // Polizza tolta → via anche la sua franchigia
    if(FRANCHIGIE_NLT[k] && !v) pol[campoFranchigia(k)]='';
    // Se si usano i menu franchigia, il testo si rigenera da solo
    const usaMenu=Object.keys(FRANCHIGIE_NLT).some(x=>pol[campoFranchigia(x)]);
    if(usaMenu || k.startsWith('franchigia')) pol.franchigie=testoFranchigieNlt(pol);
    return {...p,polizze:pol};
  });
  const upSv=(k,v)=>setForm(p=>({...p,servizi:{...p.servizi,[k]:v}}));
  const CB=({label,checked,onChange})=>(<label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',padding:'5px 0'}}><input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} style={{accentColor:'#6366f1',width:'14px',height:'14px'}}/><span style={{fontSize:'12px',color:T.text}}>{label}</span></label>);
  const handleSave=async()=>{
    if(!form.cliente.nome){alert('Inserisci il nome del cliente');return;}
    setSaving(true);
    try {
      const id=preventivo?.id||`nlt_${Date.now()}`;
      const offerte=(form.offerte||[]).map(o=>({...o,canone:parseFloat(o.canone)||0,durata:parseInt(o.durata)||0,anticipo:parseFloat(o.anticipo)||0,kmAnnui:o.kmAnnui==='illimitati'?'illimitati':(parseInt(o.kmAnnui)||0),canoneLocazione:parseFloat(o.canoneLocazione)||0,canoneServizi:parseFloat(o.canoneServizi)||0}));
      const primaOfferta=offerte[0]||{};
      const record={...form,id,data:new Date(form.data).toISOString(),
        offerte,
        // Mantieni campi legacy per compatibilità con lista/KPI
        canone:primaOfferta.canone||0, durata:primaOfferta.durata||0,
        anticipo:primaOfferta.anticipo||0, kmAnnui:primaOfferta.kmAnnui,
        provvigione:parseFloat(form.provvigione)||0,
        venditoreNome: isAdminNLT && ['admin','super_admin'].includes(ruoloNLT) && form.venditoreNome
          ? form.venditoreNome
          : preventivo?.venditoreNome || `${user.nome} ${user.cognome}`,
        venditoreUid: isAdminNLT && ['admin','super_admin'].includes(ruoloNLT) && form.venditoreUid
          ? form.venditoreUid
          : preventivo?.venditoreUid || user.uid||user.email,
        createdAt:preventivo?.createdAt||new Date().toISOString(),updatedAt:new Date().toISOString()};
      await set(ref(db,`nlt_preventivi/${id}`),record);
      onSaved(record);
    }catch(e){alert('Errore: '+e.message);}
    setSaving(false);
  };
  const totC=(parseFloat(form.canone)||0)*(parseInt(form.durata)||0);
  return (
    <div style={{padding:isMobile?'12px':'20px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <div style={{fontSize:'18px',fontWeight:'900',color:T.text}}>{isEdit?'Modifica':'Nuovo'} Preventivo NLT</div>
          <div style={{fontSize:'11px',color:'#f59e0b',fontWeight:'700',marginTop:'2px'}}>TUTTE LE CIFRE SONO IVA ESCLUSA</div>
        </div>
        <button onClick={onClose} style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:'10px',padding:'8px 16px',color:T.text,cursor:'pointer',fontWeight:'700'}}>Annulla</button>
      </div>
      <div style={sS}><div style={lS}>INFORMAZIONI GENERALI</div>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:'10px'}}>
          <div><div style={fSt}>Data Preventivo</div><input type="date" value={form.data} onChange={e=>upF('data',e.target.value)} style={iS}/></div>
          <div><div style={fSt}>Data Consegna Prevista</div><input type="date" value={form.dataConsegna} onChange={e=>upF('dataConsegna',e.target.value)} style={iS}/></div>
          <div><div style={fSt}>Stato</div><select value={form.stato} onChange={e=>upF('stato',e.target.value)} style={{...iS,cursor:'pointer'}}>
            {STATI_NLT.map(x=><option key={x.v} value={x.v}>{x.l}</option>)}</select></div>
          <div><div style={fSt}>Fonte</div><select value={form.fonte} onChange={e=>upF('fonte',e.target.value)} style={{...iS,cursor:'pointer'}}>
            <option value="">Seleziona fonte...</option>
            {['Meta','Google Ads','gruppoautoscala.com','Ingresso','Rete Esterna','Telefonata','Cliente Personale','Contatto Direzione'].map(v=><option key={v}>{v}</option>)}
          </select></div>
          <div><div style={{...fSt,color:'#8b5cf6'}}>🏢 Società Noleggio <span style={{fontSize:'9px',fontWeight:'400',opacity:0.7}}>(uso interno)</span></div>
            <select value={form.dealer||''} onChange={e=>upF('dealer',e.target.value)} style={{...iS,cursor:'pointer',border:`1px solid ${form.dealer?'#8b5cf6':''}` }}>
              <option value="">Seleziona società...</option>
              {['AGOS RENT','ALPHABET','ARVAL','AYVENS','AYVENS/TITUS','COMPASS RENT','DRIVALIA','FIRST','LEASYS'].map(v=><option key={v}>{v}</option>)}
            </select>
          </div>
          <div><div style={fSt}>Tipologia Cliente</div><select value={form.tipologiaCliente} onChange={e=>upF('tipologiaCliente',e.target.value)} style={{...iS,cursor:'pointer'}}>
            <option value="">Seleziona...</option>{['Privato','Azienda','Libero Professionista','Partita IVA','Ente Pubblico'].map(v=><option key={v}>{v}</option>)}</select></div>
          {/* Consulente — solo admin/super_admin possono cambiarlo */}
          {['admin','super_admin'].includes(ruoloNLT) ? (
            <div><div style={{...fSt,color:'#f59e0b'}}>Consulente ⚙️</div>
              <select value={form.venditoreNome||''} onChange={e=>{
                upF('venditoreNome',e.target.value);
                upF('venditoreUid',e.target.value); // usa il nome come uid nel dropdown
              }} style={{...iS,cursor:'pointer',border:'1px solid #f59e0b'}}>
                <option value="">Seleziona consulente...</option>
                {[
                  {nome:'Riccardo Meregalli',uid:'riccardo.meregalli@radargas.it'},
                  {nome:'Raffaele Crispo',uid:'raffaele.crispo@radargas.it'},
                  {nome:'Francesco Sementa',uid:'francesco.sementa@radargas.it'},
                ].map(c=><option key={c.uid} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
          ) : (
            <div><div style={fSt}>Consulente</div>
              <div style={{...iS,background:T.bg,opacity:0.7,display:'flex',alignItems:'center'}}>{form.venditoreNome||`${user.nome} ${user.cognome}`}</div>
            </div>
          )}
        </div>
      </div>
      <div style={sS}><div style={lS}>DATI CLIENTE</div>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'10px'}}>
          {[['Nome / Ragione Sociale','nome','text'],['Indirizzo','indirizzo','text'],['Partita IVA','piva','text'],['Codice Fiscale','cf','text'],['Telefono','telefono','tel'],['Email','email','email']].map(([l,k,t])=>(
            <div key={k}><div style={fSt}>{l}</div><input type={t} value={form.cliente[k]} onChange={e=>upC(k,e.target.value)} style={iS}/></div>
          ))}
        </div>
      </div>
      <div style={sS}><div style={lS}>VEICOLO</div>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:'10px'}}>
          {[['Marca','marca'],['Modello','modello'],['Versione','versione'],['Colore','colore']].map(([l,k])=>(
            <div key={k}><div style={fSt}>{l}</div><input value={form.veicolo[k]} onChange={e=>upV(k,e.target.value)} style={iS}/></div>
          ))}
          <div><div style={fSt}>Alimentazione</div><select value={form.veicolo.alimentazione} onChange={e=>upV('alimentazione',e.target.value)} style={{...iS,cursor:'pointer'}}>
            <option value="">Seleziona...</option>{['Benzina','Diesel','Ibrido','Ibrido Plug-in','Elettrico','GPL','Metano'].map(a=><option key={a}>{a}</option>)}</select></div>
        </div>
        <div style={{marginTop:'10px'}}><div style={fSt}>Optional / Accessori</div>
          <textarea value={form.optional} onChange={e=>upF('optional',e.target.value)} rows={2} style={{...iS,resize:'vertical'}} placeholder="Elenca gli optional inclusi..."/></div>
      </div>
      <div style={{...sS,border:'2px solid #f59e0b'}}><div style={{...lS,color:'#f59e0b'}}>OFFERTE — IVA ESCLUSA
        <span style={{fontSize:'9px',fontWeight:'400',color:T.textMuted,letterSpacing:'0',marginLeft:'8px'}}>puoi aggiungere più varianti di offerta</span>
      </div>

        {/* Lista offerte */}
        {(form.offerte||[{canone:'',durata:'',anticipo:'',kmAnnui:'',scorporaCanone:false,canoneLocazione:'',canoneServizi:'',mostraScorporoInPdf:false}]).map((offerta, idx) => {
          const upO=(k,v)=>setForm(p=>{const o=[...(p.offerte||[])];o[idx]={...o[idx],[k]:v};return{...p,offerte:o};});
          return (
            <div key={idx} style={{background:'rgba(245,158,11,0.04)',borderRadius:'10px',border:'1px solid rgba(245,158,11,0.2)',padding:'12px',marginBottom:'10px',position:'relative'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px'}}>
                <span style={{fontSize:'10px',fontWeight:'900',color:'#f59e0b'}}>OFFERTA {idx+1}</span>
                {(form.offerte||[]).length>1&&<button onClick={()=>setForm(p=>({...p,offerte:(p.offerte||[]).filter((_,i)=>i!==idx)}))} style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'6px',padding:'3px 8px',color:'#ef4444',cursor:'pointer',fontSize:'10px',fontWeight:'700'}}>✕ Rimuovi</button>}
              </div>
              <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr 1fr',gap:'10px'}}>
                <div><div style={fSt}>Canone Mensile (IVA es.)</div><input type="number" value={offerta.canone||''} onChange={e=>upO('canone',e.target.value)} onWheel={e=>e.preventDefault()} style={iS} placeholder="0.00"/></div>
                <div><div style={fSt}>Durata (mesi)</div><input type="number" value={offerta.durata||''} onChange={e=>upO('durata',e.target.value)} onWheel={e=>e.preventDefault()} style={iS} placeholder="es. 36"/></div>
                <div><div style={fSt}>Anticipo (IVA es.)</div><input type="number" value={offerta.anticipo||''} onChange={e=>upO('anticipo',e.target.value)} onWheel={e=>e.preventDefault()} style={iS} placeholder="0.00"/></div>
                <div><div style={fSt}>KM Annui</div><select value={offerta.kmAnnui||''} onChange={e=>upO('kmAnnui',e.target.value)} style={{...iS,cursor:'pointer'}}>
                  <option value="">Seleziona...</option>{[5000,10000,15000,20000,25000,30000,35000,40000,45000,50000,55000,60000,65000,70000,75000,80000,85000,90000,95000,100000].map(k=><option key={k} value={k}>{Number(k).toLocaleString('it-IT')} km</option>)}<option value="illimitati">Illimitati</option></select></div>
              </div>
              {/* Scorporo */}
              <div style={{marginTop:'8px'}}>
                <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',marginBottom:'6px'}}>
                  <input type="checkbox" checked={offerta.scorporaCanone||false} onChange={e=>upO('scorporaCanone',e.target.checked)} style={{accentColor:'#f59e0b',width:'13px',height:'13px'}}/>
                  <span style={{fontSize:'10px',fontWeight:'700',color:'#f59e0b'}}>Scorpora canone in Locazione + Servizi</span>
                </label>
                {offerta.scorporaCanone&&(
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',paddingLeft:'20px'}}>
                    <div><div style={fSt}>Canone Locazione</div><input type="number" value={offerta.canoneLocazione||''} onChange={e=>upO('canoneLocazione',e.target.value)} onWheel={e=>e.preventDefault()} style={iS} placeholder="0.00"/></div>
                    <div><div style={fSt}>Canone Servizi</div><input type="number" value={offerta.canoneServizi||''} onChange={e=>upO('canoneServizi',e.target.value)} onWheel={e=>e.preventDefault()} style={iS} placeholder="0.00"/></div>
                    <div style={{gridColumn:'1/-1'}}>
                      <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer'}}>
                        <input type="checkbox" checked={offerta.mostraScorporoInPdf||false} onChange={e=>upO('mostraScorporoInPdf',e.target.checked)} style={{accentColor:'#6366f1',width:'13px',height:'13px'}}/>
                        <span style={{fontSize:'10px',color:T.text}}>Mostra scorporo nel preventivo stampato</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              {/* Riepilogo IVA */}
              {parseFloat(offerta.canone)>0&&(
                <div style={{display:'flex',gap:'12px',flexWrap:'wrap',marginTop:'8px',padding:'6px 10px',background:'rgba(245,158,11,0.08)',borderRadius:'8px'}}>
                  <div><div style={{fontSize:'9px',color:'#f59e0b'}}>Canone IVA incl.</div><div style={{fontSize:'13px',fontWeight:'900',color:'#f59e0b'}}>{new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(parseFloat(offerta.canone)*1.22)}/mese</div></div>
                  {parseFloat(offerta.anticipo)>0&&<div><div style={{fontSize:'9px',color:'#f59e0b'}}>Anticipo IVA incl.</div><div style={{fontSize:'13px',fontWeight:'900',color:'#f59e0b'}}>{new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(parseFloat(offerta.anticipo)*1.22)}</div></div>}
                </div>
              )}
            </div>
          );
        })}
        <button onClick={()=>setForm(p=>({...p,offerte:[...(p.offerte||[]),{canone:'',durata:'',anticipo:'',kmAnnui:'',scorporaCanone:false,canoneLocazione:'',canoneServizi:'',mostraScorporoInPdf:false}]}))}
          style={{background:'rgba(245,158,11,0.1)',border:'1px dashed rgba(245,158,11,0.5)',borderRadius:'10px',padding:'8px 16px',color:'#f59e0b',cursor:'pointer',fontSize:'11px',fontWeight:'700',width:'100%',marginTop:'4px'}}>
          + Aggiungi offerta
        </button>
      </div>
      <div style={sS}><div style={lS}>POLIZZE ASSICURATIVE</div>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'16px'}}>
          <div>
            {Object.entries(FRANCHIGIE_NLT).map(([k,d])=>(
              <div key={k} style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
                <div style={{minWidth:'150px'}}><CB label={d.label} checked={form.polizze[k]} onChange={v=>upP(k,v)}/></div>
                {form.polizze[k]&&(
                  <select value={form.polizze[campoFranchigia(k)]||''} onChange={e=>upP(campoFranchigia(k),e.target.value)}
                    style={{...iS,width:'auto',minWidth:'170px',padding:'5px 8px',cursor:'pointer',borderColor:form.polizze[campoFranchigia(k)]?undefined:'#f59e0b'}}>
                    <option value="">Franchigia…</option>
                    {d.valori.map(x=><option key={x} value={x}>{x}</option>)}
                  </select>
                )}
              </div>
            ))}
            <CB label="Cristalli" checked={form.polizze.cristalli} onChange={v=>upP('cristalli',v)}/>
            <CB label="KV (Kasko Valore)" checked={form.polizze.kv} onChange={v=>upP('kv',v)}/>
            <div style={{marginTop:'8px'}}><div style={fSt}>Altre polizze</div><input value={form.polizze.altrePolizze} onChange={e=>upP('altrePolizze',e.target.value)} style={iS}/></div>
          </div>
          <div>
            <div style={fSt}>Franchigie (da stampare)</div>
            <div style={{...iS,minHeight:'90px',whiteSpace:'pre-line',color:form.polizze.franchigie?undefined:'#94a3b8'}}>
              {form.polizze.franchigie||'Flagga RCA, Kasko o Furto e Incendio e scegli la franchigia dal menu.'}
            </div>
            {form.polizze.franchigie&&!Object.keys(FRANCHIGIE_NLT).some(x=>form.polizze[campoFranchigia(x)])&&(
              <div style={{fontSize:'9px',color:'#f59e0b',marginTop:'4px'}}>Testo di un preventivo precedente: scegliendo una franchigia dai menu viene sostituito.</div>
            )}
          </div>
        </div>
      </div>
      <div style={sS}><div style={lS}>SERVIZI INCLUSI</div>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:'0'}}>
          <CB label="Manutenzione ordinaria" checked={form.servizi.manutenzione} onChange={v=>upSv('manutenzione',v)}/>
          <CB label="Manutenzione straordinaria" checked={form.servizi.manutenzioneStr||false} onChange={v=>upSv('manutenzioneStr',v)}/>
          <CB label="Soccorso stradale" checked={form.servizi.soccorsoStradale} onChange={v=>upSv('soccorsoStradale',v)}/>
          <CB label="Auto sostitutiva" checked={form.servizi.autoSostitutiva} onChange={v=>upSv('autoSostitutiva',v)}/>
          <CB label="Pneumatici estivi" checked={form.servizi.gommeSummer} onChange={v=>upSv('gommeSummer',v)}/>
          <CB label="Pneumatici invernali" checked={form.servizi.gommeWinter} onChange={v=>upSv('gommeWinter',v)}/>
        </div>
        <div style={{marginTop:'8px'}}><div style={fSt}>Altri servizi</div><input value={form.servizi.altriServizi} onChange={e=>upSv('altriServizi',e.target.value)} style={iS}/></div>
      </div>
      <div style={{...sS,marginBottom:'14px'}}><div style={lS}>NOTE</div>
        <textarea value={form.note} onChange={e=>upF('note',e.target.value)} rows={3} style={{...iS,resize:'vertical'}} placeholder="Note aggiuntive..."/></div>
      {/* PROVVIGIONE — non visibile nella stampa, visibile a tutti */}
      <div style={{...sS,marginBottom:'20px',border:`1px dashed ${T.border}`,background:'rgba(99,102,241,0.04)'}}>
        <div style={{...lS,color:'#6366f1'}}>PROVVIGIONE <span style={{fontSize:'9px',fontWeight:'400',color:T.textMuted,letterSpacing:'0'}}>— non visibile nella stampa</span></div>
        <input type="number" value={form.provvigione} onChange={e=>upF('provvigione',e.target.value)} onWheel={e=>e.preventDefault()}
          style={{...iS,border:'1px solid #6366f140'}} placeholder="0.00"/>
      </div>
      <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',paddingBottom:'20px'}}>
        <button onClick={onClose} style={{padding:'12px 24px',background:T.bg,border:`1px solid ${T.border}`,borderRadius:'12px',color:T.text,cursor:'pointer',fontWeight:'700'}}>Annulla</button>
        <button onClick={handleSave} disabled={saving} style={{padding:'12px 28px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:'12px',color:'#fff',cursor:'pointer',fontWeight:'900',fontSize:'13px'}}>
          {saving?'Salvataggio...':isEdit?'Aggiorna':'Salva Preventivo'}
        </button>
      </div>
    </div>
  );
}

function generateNLTPdf(p, logoSrc){
  const win=window.open('','_blank');
  if(!win)return;
  const fmtC=v=>new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(v);
  const polizzeL=[p.polizze?.rca&&'RCA',p.polizze?.kasko&&'Kasko',p.polizze?.furtoIncendio&&'Furto e Incendio',p.polizze?.cristalli&&'Cristalli',p.polizze?.kv&&'KV',p.polizze?.altrePolizze].filter(Boolean).join(', ');
  const serviziL=[p.servizi?.manutenzione&&'Manutenzione ordinaria',p.servizi?.manutenzioneStr&&'Manutenzione straordinaria',p.servizi?.soccorsoStradale&&'Soccorso stradale',p.servizi?.autoSostitutiva&&'Auto sostitutiva',p.servizi?.gommeSummer&&'Pneumatici estivi',p.servizi?.gommeWinter&&'Pneumatici invernali',p.servizi?.altriServizi].filter(Boolean).join(', ');
  const offerte=p.offerte||[{canone:p.canone,durata:p.durata,anticipo:p.anticipo,kmAnnui:p.kmAnnui,scorporaCanone:p.scorporaCanone,canoneLocazione:p.canoneLocazione,canoneServizi:p.canoneServizi,mostraScorporoInPdf:p.mostraScorporoInPdf}];
  const D=(tag,attrs,content)=>attrs?'<'+tag+' '+attrs+'>'+content+'</'+tag+'>':'<'+tag+'>'+content+'</'+tag+'>';
  const offerteHtml=offerte.map((o,i)=>{
    const label=offerte.length>1?D('div','style="font-size:9px;font-weight:900;color:#1a5c38;letter-spacing:1px;margin-bottom:4px;margin-top:'+(i>0?'12px':'0')+';"','OFFERTA '+(i+1)):'';
    const scorporo=(o.scorporaCanone&&o.mostraScorporoInPdf)?D('div','class="row" style="padding-left:16px;font-size:10px;"','<span style="color:#888;">di cui locazione</span><b style="color:#555;">'+fmtC(o.canoneLocazione||0)+' / mese</b>')+D('div','class="row" style="padding-left:16px;font-size:10px;"','<span style="color:#888;">di cui servizi</span><b style="color:#555;">'+fmtC(o.canoneServizi||0)+' / mese</b>'):'';
    const kmStr=o.kmAnnui==='illimitati'?'Illimitati':Number(o.kmAnnui||0).toLocaleString('it-IT')+' km';
    return label+D('div','class="box" style="'+(i>0?'margin-top:8px;':'')+'"',
      D('div','class="row"','<span>Canone mensile (IVA es.)</span><b style="font-size:13px;">'+fmtC(o.canone||0)+' / mese</b>')+scorporo+
      D('div','class="row highlight"','<span>Canone mensile IVA inclusa (22%)</span><b>'+fmtC((o.canone||0)*1.22)+' / mese</b>')+
      D('div','class="row" style="margin-top:8px;"','<span>Durata</span><b>'+(o.durata||'---')+' mesi</b>')+
      D('div','class="row"','<span>KM Annui</span><b>'+kmStr+'</b>')+
      D('div','class="row"','<span>Anticipo (IVA es.)</span><b>'+fmtC(o.anticipo||0)+'</b>')+
      D('div','class="row highlight"','<span>Anticipo IVA inclusa (22%)</span><b>'+fmtC((o.anticipo||0)*1.22)+'</b>')
    );
  }).join('');
  const consegna=p.dataConsegna?D('div','','Consegna prevista: <b>'+new Date(p.dataConsegna).toLocaleDateString('it-IT')+'</b>'):'';
  const tipologia=p.tipologiaCliente?' <span style="font-size:10px;color:#555;">— '+p.tipologiaCliente+'</span>':'';
  const clienteExtra=(p.cliente?.piva?D('div','','<span style="color:#555;">P.IVA:</span> <b>'+p.cliente.piva+'</b>'):'')+
    (p.cliente?.cf?D('div','','<span style="color:#555;">C.F.:</span> <b>'+p.cliente.cf+'</b>'):'')+
    (p.cliente?.telefono?D('div','','<span style="color:#555;">Tel.:</span> <b>'+p.cliente.telefono+'</b>'):'')+
    (p.cliente?.email?D('div','','<span style="color:#555;">Email:</span> <b>'+p.cliente.email+'</b>'):'');
  const optHtml=p.optional?D('div','style="margin-top:8px;padding:8px 10px;background:#f9fcfa;border-left:3px solid #c8e0d0;font-size:10px;color:#555;border-radius:3px;"','<b style="color:#1a5c38;">Optional inclusi:</b> '+p.optional):'';
  const polHtml=polizzeL?D('div','class="sec"',D('div','class="sec-t"','Polizze Assicurative')+polizzeL.split(', ').map(s=>D('span','class="tag"',s)).join('')+(p.polizze?.franchigie?D('div','style="margin-top:6px;white-space:pre-line;font-size:10px;border-left:3px solid #c8e0d0;padding:6px 10px;"','<b style="color:#1a5c38;">Franchigie:</b><br>'+p.polizze.franchigie):'')):'';
  const servHtml=serviziL?D('div','class="sec"',D('div','class="sec-t"','Servizi Inclusi')+serviziL.split(', ').map(s=>D('span','class="tag tag-blue"',s)).join('')):'';
  const noteHtml=p.note?D('div','class="sec"',D('div','class="sec-t"','Note')+'<div style="color:#555;line-height:1.6;">'+p.note+'</div>'):'';
  const css='*{box-sizing:border-box;margin:0;padding:0;}body{font-family:"Helvetica Neue",Arial,sans-serif;font-size:11px;color:#1a1a1a;padding:32px;background:#fff;}.hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;margin-bottom:6px;border-bottom:3px solid #1a5c38;}.hdr-info{font-size:10px;color:#555;margin-top:8px;line-height:1.6;}.badge{display:inline-block;background:#1a5c38;color:#fff;padding:6px 18px;font-weight:900;font-size:14px;letter-spacing:1px;border-radius:4px;margin-bottom:8px;}.meta{font-size:11px;color:#333;line-height:1.8;text-align:right;}.meta b{color:#1a5c38;}.sec{margin-bottom:18px;}.sec-t{font-size:9px;font-weight:900;letter-spacing:2px;text-transform:uppercase;color:#1a5c38;border-bottom:2px solid #1a5c38;padding-bottom:5px;margin-bottom:10px;}.row{display:flex;justify-content:space-between;align-items:center;padding:5px 0;border-bottom:1px solid #e8f0eb;}.row span{color:#555;}.row b{color:#1a1a1a;}.row.highlight{background:#f0f7f2;padding:6px 8px;border-radius:4px;border-bottom:none;margin-top:6px;}.row.highlight span{color:#1a5c38;font-weight:700;}.row.highlight b{color:#1a5c38;font-size:13px;}.box{background:#f9fcfa;border:1px solid #c8e0d0;border-left:3px solid #1a5c38;padding:12px 16px;border-radius:4px;}.grid2{display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;}.tag{display:inline-block;background:#f0f7f2;color:#1a5c38;font-size:10px;font-weight:700;padding:3px 8px;border-radius:3px;margin:2px 3px 2px 0;}.tag-blue{background:#eef2ff;color:#3730a3;}.firma-row{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:48px;}.firma-box{border-top:2px solid #1a5c38;padding-top:8px;text-align:center;font-size:10px;color:#555;}.footer{margin-top:24px;padding-top:10px;border-top:1px solid #e8f0eb;font-size:9px;color:#999;text-align:center;}@media print{button{display:none!important;}body{padding:20px;}}';
  win.document.write('<!DOCTYPE html><html><head><title>Preventivo NLT</title><style>'+css+'</style></head><body>'+
    '<button onclick="window.print()" style="background:#1a5c38;color:#fff;border:none;padding:9px 24px;border-radius:6px;cursor:pointer;margin-bottom:20px;font-size:12px;font-weight:700;">Stampa / Salva PDF</button>'+
    D('div','class="hdr"',
      D('div','','<img src="'+logoSrc+'" style="height:200px;width:auto;display:block;" alt="Gruppo Autoscala Rent"/>'+D('div','class="hdr-info"','Gruppo Autoscala S.r.l. &nbsp;&middot;&nbsp; Via Antiniana, 2/B - 80078 Pozzuoli (NA)<br>Tel. 0811865872 &nbsp;&middot;&nbsp; P.IVA IT07821241218'))+
      D('div','class="badge-wrap"',D('div','class="badge"','PREVENTIVO NLT')+D('div','class="meta"','<div>Data: <b>'+(p.data?new Date(p.data).toLocaleDateString('it-IT'):'---')+'</b></div>'+consegna+'<div>Consulente: <b>'+(p.venditoreNome||'---')+'</b></div>'+((()=>{const r=recapitiConsulente(p.venditoreNome,RECAPITI_CONSULENTI_NLT);return r?'<div>Cell.: <b>'+r.cell+'</b></div><div>Email: <b>'+r.email+'</b></div>':'';})())))
    )+
    D('div','class="sec"',D('div','class="sec-t"','Cliente')+D('div','class="grid2"','<div><b style="font-size:13px;">'+(p.cliente?.nome||'---')+'</b>'+tipologia+'</div><div>'+(p.cliente?.indirizzo||'')+'</div>'+clienteExtra))+
    D('div','class="sec"',D('div','class="sec-t"','Veicolo')+D('div','class="grid2"','<div><b style="font-size:13px;">'+(p.veicolo?.marca||'')+' '+(p.veicolo?.modello||'')+'</b></div><div>'+(p.veicolo?.versione||'')+'</div>'+(p.veicolo?.alimentazione?'<div><span style="color:#555;">Alimentazione:</span> '+p.veicolo.alimentazione+'</div>':'')+( p.veicolo?.colore?'<div><span style="color:#555;">Colore:</span> '+p.veicolo.colore+'</div>':''))+optHtml)+
    D('div','class="sec"',D('div','class="sec-t"','Condizioni Economiche')+offerteHtml)+
    polHtml+servHtml+noteHtml+
    D('div','class="firma-row"',D('div','class="firma-box"','Firma Consulente')+D('div','class="firma-box"','Firma Cliente / Timbro'))+
    D('div','class="footer"','Documento generato da RadaR GAS Intelligence Cloud &nbsp;&middot;&nbsp; Gruppo Autoscala S.r.l.')+
    '</body></html>');
  win.document.close();
}


function NLTDettaglio({ p, T, isMobile, isAdminNLT, fmt, onModifica, onClose, onElimina, onDuplica, user }) {
  const [confermaElimina, setConfermaElimina] = useState(false);
  const sc=(STATO_NLT[p.stato]||{}).c||'#64748b';
  const totC=(p.canone||0)*(p.durata||0);
  const fmtC=v=>new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(v);
  const stampaPDF=()=>generateNLTPdf(p, GAS_RENT_LOGO);
;
  return (
    <div style={{padding:isMobile?'12px':'20px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <span style={{fontSize:'10px',fontWeight:'900',color:sc,background:sc+'20',padding:'3px 10px',borderRadius:'8px'}}>{etichettaStatoNlt(p.stato)}</span>
            <span style={{fontSize:'11px',color:T.textMuted}}>{p.data?new Date(p.data).toLocaleDateString('it-IT'):'---'}</span>
            {p.fonte&&<span style={{fontSize:'10px',color:'#8b5cf6',fontWeight:'700',background:'rgba(139,92,246,0.1)',padding:'2px 7px',borderRadius:'6px'}}>{p.fonte}</span>}
          {p.dealer&&<span style={{fontSize:'10px',color:'#8b5cf6',fontWeight:'700',background:'rgba(139,92,246,0.15)',padding:'2px 7px',borderRadius:'6px'}}>🏢 {p.dealer}</span>}
          </div>
          <div style={{fontSize:'20px',fontWeight:'900',color:T.text,marginTop:'4px'}}>{p.cliente?.nome||'---'}</div>
          <div style={{fontSize:'12px',color:T.textMuted}}>{p.veicolo?.marca} {p.veicolo?.modello} - {p.tipologiaCliente||'---'}</div>
          {p.dataConsegna&&<div style={{fontSize:'11px',color:'#10b981',fontWeight:'700',marginTop:'2px'}}>Consegna prevista: {new Date(p.dataConsegna).toLocaleDateString('it-IT')}</div>}
        </div>
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          <button onClick={stampaPDF} style={{padding:'9px 16px',background:T.bg,border:`1px solid ${T.border}`,borderRadius:'10px',color:T.text,cursor:'pointer',fontWeight:'700',fontSize:'11px'}}>Stampa PDF</button>
          <button onClick={onDuplica} style={{padding:'9px 16px',background:'rgba(16,185,129,0.1)',border:'1px solid #10b981',borderRadius:'10px',color:'#10b981',cursor:'pointer',fontWeight:'700',fontSize:'11px'}}>⊕ Duplica cliente</button>
          {(isAdminNLT || p.venditoreUid===(user.uid||user.email) || p.venditoreNome===`${user.nome} ${user.cognome}`)&&<button onClick={onModifica} style={{padding:'9px 16px',background:'rgba(99,102,241,0.1)',border:'1px solid #6366f1',borderRadius:'10px',color:'#6366f1',cursor:'pointer',fontWeight:'700',fontSize:'11px'}}>Modifica</button>}
          {isAdminNLT&&<button onClick={()=>setConfermaElimina(true)} style={{padding:'9px 16px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.4)',borderRadius:'10px',color:'#ef4444',cursor:'pointer',fontWeight:'700',fontSize:'11px'}}>Elimina</button>}
          <button onClick={onClose} style={{padding:'9px 16px',background:T.bg,border:`1px solid ${T.border}`,borderRadius:'10px',color:T.text,cursor:'pointer',fontWeight:'700',fontSize:'11px'}}>Indietro</button>
        </div>
      </div>

      {/* CONFERMA ELIMINA */}
      {confermaElimina && (
        <div style={{background:'rgba(239,68,68,0.08)',border:'2px solid rgba(239,68,68,0.4)',borderRadius:'14px',padding:'16px 20px',marginBottom:'16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
          <div>
            <div style={{fontSize:'13px',fontWeight:'900',color:'#ef4444'}}>Eliminare questo preventivo NLT?</div>
            <div style={{fontSize:'11px',color:T.textMuted,marginTop:'2px'}}>Azione irreversibile — il preventivo verrà rimosso definitivamente</div>
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={()=>setConfermaElimina(false)} style={{padding:'8px 16px',background:T.bg,border:`1px solid ${T.border}`,borderRadius:'10px',color:T.text,cursor:'pointer',fontWeight:'700',fontSize:'11px'}}>Annulla</button>
            <button onClick={onElimina} style={{padding:'8px 16px',background:'#ef4444',border:'none',borderRadius:'10px',color:'#fff',cursor:'pointer',fontWeight:'900',fontSize:'11px'}}>Sì, elimina</button>
          </div>
        </div>
      )}
      <div style={{background:'#fff3cd',border:'2px solid #f59e0b',borderRadius:'12px',padding:'10px 16px',marginBottom:'16px',fontWeight:'700',fontSize:'12px',color:'#856404'}}>
        TUTTE LE CIFRE SONO IVA ESCLUSA
      </div>
      {p.provvigione > 0 && (
        <div style={{background:'rgba(99,102,241,0.08)',border:'1px dashed rgba(99,102,241,0.4)',borderRadius:'10px',padding:'10px 14px',marginBottom:'14px'}}>
          <div style={{fontSize:'9px',fontWeight:'900',color:'#6366f1',letterSpacing:'1px',marginBottom:'4px'}}>PROVVIGIONE (non in stampa)</div>
          <div style={{fontSize:'18px',fontWeight:'900',color:'#6366f1'}}>{fmtC(p.provvigione)}</div>
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'14px'}}>
        <div style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'16px'}}>
          <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'10px'}}>CLIENTE</div>
          {[['Nome',p.cliente?.nome],['Tipologia',p.tipologiaCliente],['P.IVA',p.cliente?.piva],['C.F.',p.cliente?.cf],['Telefono',p.cliente?.telefono],['Email',p.cliente?.email],['Indirizzo',p.cliente?.indirizzo]].filter(([,v])=>v).map(([l,v])=>(
            <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}><span style={{color:T.textMuted}}>{l}</span><span style={{color:T.text,fontWeight:'600'}}>{v}</span></div>
          ))}
        </div>
        <div style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'16px'}}>
          <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'10px'}}>VEICOLO</div>
          {[['Marca',p.veicolo?.marca],['Modello',p.veicolo?.modello],['Versione',p.veicolo?.versione],['Alimentazione',p.veicolo?.alimentazione],['Colore',p.veicolo?.colore]].filter(([,v])=>v).map(([l,v])=>(
            <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}><span style={{color:T.textMuted}}>{l}</span><span style={{color:T.text,fontWeight:'600'}}>{v}</span></div>
          ))}
          {p.optional&&<div style={{fontSize:'11px',color:T.textMuted,marginTop:'8px'}}>Optional: {p.optional}</div>}
        </div>
        <div style={{background:T.surface,borderRadius:'14px',border:'2px solid #f59e0b',padding:'16px'}}>
          <div style={{fontSize:'10px',fontWeight:'900',color:'#f59e0b',letterSpacing:'1px',marginBottom:'10px'}}>CONDIZIONI ECONOMICHE - IVA ESCLUSA</div>
          {(p.offerte||[{canone:p.canone,durata:p.durata,anticipo:p.anticipo,kmAnnui:p.kmAnnui,scorporaCanone:p.scorporaCanone,canoneLocazione:p.canoneLocazione,canoneServizi:p.canoneServizi}]).map((o,i,arr)=>(
            <div key={i} style={{marginBottom:i<arr.length-1?'10px':0,paddingBottom:i<arr.length-1?'10px':0,borderBottom:i<arr.length-1?`1px dashed ${T.border}`:'none'}}>
              {arr.length>1&&<div style={{fontSize:'9px',fontWeight:'900',color:'#f59e0b',marginBottom:'4px'}}>OFFERTA {i+1}</div>}
              {[['Canone mensile (IVA es.)',fmtC(o.canone||0)+'/mese'],
                ...(o.scorporaCanone?[['  di cui locazione',fmtC(o.canoneLocazione||0)+'/mese'],['  di cui servizi',fmtC(o.canoneServizi||0)+'/mese']]:[]),
                ['Canone mensile IVA incl.',fmtC((o.canone||0)*1.22)+'/mese'],
                ['Durata',(o.durata||'---')+' mesi'],
                ['Anticipo (IVA es.)',fmtC(o.anticipo||0)],
                ['Anticipo IVA inclusa',fmtC((o.anticipo||0)*1.22)],
                ['KM Annui', o.kmAnnui==='illimitati'?'Illimitati':Number(o.kmAnnui||0).toLocaleString('it-IT')+' km'],
              ].map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${T.border}`,fontSize:'11px'}}>
                  <span style={{color:T.textMuted,paddingLeft:l.startsWith('  ')?'12px':'0',fontSize:l.startsWith('  ')?'10px':'11px'}}>{l.trim()}</span>
                  <span style={{color:l.includes('IVA incl')?'#f59e0b':T.text,fontWeight:l.includes('IVA')?'900':'600'}}>{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{background:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,padding:'16px'}}>
          <div style={{fontSize:'10px',fontWeight:'900',color:T.textMuted,letterSpacing:'1px',marginBottom:'8px'}}>POLIZZE E SERVIZI</div>
          {[p.polizze?.rca&&'RCA',p.polizze?.kasko&&'Kasko',p.polizze?.furtoIncendio&&'Furto e Incendio',p.polizze?.cristalli&&'Cristalli',p.polizze?.kv&&'KV',p.polizze?.altrePolizze].filter(Boolean).map(s=><div key={s} style={{fontSize:'11px',padding:'2px 0',color:'#3b82f6'}}>{s}</div>)}
          {p.polizze?.franchigie&&<div style={{fontSize:'11px',color:T.textMuted,marginTop:'8px',whiteSpace:'pre-line',borderTop:`1px solid ${T.border}`,paddingTop:'6px'}}><span style={{fontWeight:'700',color:T.text}}>Franchigie:</span>{'\n'}{p.polizze.franchigie}</div>}
          {[p.servizi?.manutenzione&&'Manutenzione ordinaria',p.servizi?.manutenzioneStr&&'Manutenzione straordinaria',p.servizi?.soccorsoStradale&&'Soccorso stradale',p.servizi?.autoSostitutiva&&'Auto sostitutiva',p.servizi?.gommeSummer&&'Pneumatici estivi',p.servizi?.gommeWinter&&'Pneumatici invernali',p.servizi?.altriServizi].filter(Boolean).map(s=><div key={s} style={{fontSize:'11px',padding:'2px 0',color:'#10b981'}}>{s}</div>)}
          {p.note&&<div style={{fontSize:'11px',color:T.textMuted,marginTop:'8px',fontStyle:'italic'}}>{p.note}</div>}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}
